import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { useUser, useAuth, SignInButton } from '@clerk/clerk-react';
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
// @ts-ignore
import Globe from 'globe.gl';
import * as THREE from 'three';
// @ts-ignore
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import './App.css';

const API_BASE  = 'https://pqc-api-worker.tychon-endpoint.workers.dev';
const PROBE_URL = 'https://pqc-probe-worker.tychon-endpoint.workers.dev';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Domain {
  hostname: string;
  pqc_status: 'secure' | 'legacy' | 'insecure' | 'unknown';
  overall_grade: string;
  last_updated: string;
  // Extended fields from /v1/list join
  key_exchange:   string | null;
  pqc_group_name: string | null;
  sig_alg:        string | null;
  pubkey_alg:     string | null;
  pubkey_size:    number | null;
  tls_1_3:        number | null;
  hsts_max_age:   number | null;
  asn_country:    string | null;
  scan_violations: number | null;
}

interface SiteData {
  hostname: string;
  pqc_status: string;
  overall_grade: string;
  last_updated: string;
  // Change tracking
  prev_overall_grade: string | null;
  prev_tls_version:   string | null;
  prev_kx_group:      string | null;
  prev_cert_sig:      string | null;
  changed_at:         string | null;
  tls_1_3: boolean;
  tls_1_2: boolean;
  hsts_policy: string | null;
  cipher_suite: string;
  key_exchange: string;
  pqc_group_name: string;
  pqc_group_id: string;
  forward_secrecy: boolean;
  aead_support: boolean;
  subject: string;
  issuer_name: string;
  sig_alg: string;
  pubkey_alg: string;
  pubkey_size: number;
  not_after: string;
  is_expired: boolean;
  // Security headers
  hsts_max_age: number | null;
  hsts_include_subdomains: boolean;
  hsts_preload: boolean;
  has_csp: boolean;
  x_frame_options: string;
  x_content_type_nosniff: boolean;
  referrer_policy: string;
  has_permissions_policy: boolean;
  server: string;
}

interface EnrichData {
  ip:           string | null;
  cname:        string | null;
  dnssec:       boolean;
  caa:          string[];
  spf:          string | null;
  dmarc:        string | null;
  geo: {
    city:         string | null;
    region:       string | null;
    country_name: string | null;
    country_code: string | null;
    org:          string | null;
    asn:          string | null;
  } | null;
  registration: {
    registrar: string | null;
    country:   string | null;
    created:   string | null;
    expires:   string | null;
    updated:   string | null;
  } | null;
  radar: {
    rank_bucket:    string | null;
    rank_exact:     number | null;
    categories:     string[];
    asn_name:       string | null;
    asn_country:    string | null;
    asn_location:   string | null;
    scan_malicious:  boolean | null;
    scan_phishing:   boolean | null;
    scan_date:       string | null;
    scan_queued:     boolean;
    scan_violations: number | null;
    otx_pulse_count: number | null;
    otx_malicious:   boolean | null;
    otx_tags:        string[];
    otx_last_seen:   string | null;
  } | null;
  ai: {
    urgency_score:   number;
    narrative:       string;
    recommendations: string[];
    anomalies:       string[];
    confidence:      number;
  } | null;
}

interface SubdomainResult {
  hostname: string;
  pqc_status: string;
  overall_grade: string;
  last_updated?: string;
  pqc_group_name?: string;
  cipher_suite?: string;
  _state: 'known' | 'probing' | 'done' | 'error';
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Accepts a raw URL or bare domain, extracts the hostname, validates it.
// Returns the clean hostname (no www, lowercase) or null if invalid.
const VALID_DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*\.[a-z]{2,63}$/
function parseDomain(raw: string): string | null {
  const s = raw.trim()
  if (!s) return null
  let host: string
  if (s.includes('://')) {
    try { host = new URL(s).hostname }
    catch { return null }
  } else {
    // Bare domain or domain/path — strip path, query, fragment, port
    host = s.split('/')[0].split('?')[0].split('#')[0].split(':')[0]
  }
  host = host.toLowerCase().replace(/^www\./, '').trim()
  return VALID_DOMAIN_RE.test(host) ? host : null
}

// ─── Auth / Rank Helpers ──────────────────────────────────────────────────────

const CONSUMER_DOMAINS = new Set([
  'gmail.com','yahoo.com','hotmail.com','outlook.com','live.com',
  'icloud.com','me.com','aol.com','protonmail.com','proton.me',
  'mail.com','ymail.com','googlemail.com','msn.com','comcast.net',
])
function canBeCorporate(email: string): boolean {
  const domain = email.split('@')[1]?.toLowerCase()
  return !!domain && !CONSUMER_DOMAINS.has(domain)
}

const RANKS = [
  { id: 1,  name: 'Recruit',                      minPoints: 0,           tier: 'enlisted' },
  { id: 2,  name: 'Specialist 1',                 minPoints: 500,         tier: 'enlisted' },
  { id: 3,  name: 'Specialist 2',                 minPoints: 2_500,       tier: 'enlisted' },
  { id: 4,  name: 'Specialist 3',                 minPoints: 10_000,      tier: 'enlisted' },
  { id: 5,  name: 'Specialist 4',                 minPoints: 50_000,      tier: 'enlisted' },
  { id: 6,  name: 'Sergeant',                     minPoints: 150_000,     tier: 'nco' },
  { id: 7,  name: 'Technical Sergeant',           minPoints: 500_000,     tier: 'nco' },
  { id: 8,  name: 'Master Sergeant',              minPoints: 1_500_000,   tier: 'nco' },
  { id: 9,  name: 'Senior Master Sergeant',       minPoints: 5_000_000,   tier: 'nco' },
  { id: 10, name: 'Chief Master Sergeant',        minPoints: 15_000_000,  tier: 'nco' },
  { id: 11, name: 'Lieutenant Guardian',          minPoints: 50_000_000,  tier: 'officer' },
  { id: 12, name: 'Captain Guardian',             minPoints: 150_000_000, tier: 'officer' },
  { id: 13, name: 'Major Guardian',               minPoints: 500_000_000, tier: 'officer' },
  { id: 14, name: 'Colonel Guardian',             minPoints: 1_000_000_000, tier: 'officer' },
  { id: 15, name: 'General of the Quantum Force', minPoints: Infinity,    tier: 'general' },
]
function getRankForPoints(points: number) {
  let rank = RANKS[0]
  for (const r of RANKS) {
    if (r.minPoints === Infinity) break
    if (points >= r.minPoints) rank = r
  }
  return rank
}
function rankColor(tier: string): string {
  if (tier === 'general') return '#ffd700'
  if (tier === 'officer') return '#f59e0b'
  if (tier === 'nco')     return '#f97316'
  return 'var(--accent-cyan)'
}

// ─── maybeAwardPoints ─────────────────────────────────────────────────────────

async function maybeAwardPoints(
  hostname: string,
  pqcStatus: string,
  getToken: () => Promise<string | null | undefined>
) {
  if (pqcStatus === 'unknown') return
  try {
    const token = await getToken()
    if (!token) return
    await fetch(`${API_BASE}/v1/discovery/award`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ hostname }),
    })
  } catch { /* non-fatal */ }
}

function parseCipher(suite: string): { encryption: string; hash: string } {
  if (!suite) return { encryption: '—', hash: '—' };
  const encryption =
    suite.includes('AES_256_GCM')       ? 'AES-256-GCM'       :
    suite.includes('AES_128_GCM')       ? 'AES-128-GCM'       :
    suite.includes('CHACHA20_POLY1305') ? 'ChaCha20-Poly1305' : '—';
  const hash =
    suite.includes('SHA384') ? 'SHA-384' :
    suite.includes('SHA256') ? 'SHA-256' :
    suite.includes('SHA512') ? 'SHA-512' : '—';
  return { encryption, hash };
}

interface GroupInfo { group: string; mechanism: string; type: string; isPQC: boolean; }
function getGroupInfo(name: string, cipherSuite?: string): GroupInfo {
  // Known key_share group from TLS 1.3 ServerHello
  if (name?.includes('MLKEM1024')) return { group: name, mechanism: 'ML-KEM-1024 Hybrid', type: 'PQC Hybrid', isPQC: true };
  if (name?.includes('MLKEM768'))  return { group: name, mechanism: 'ML-KEM-768 Hybrid',  type: 'PQC Hybrid', isPQC: true };
  if (name?.includes('X25519'))            return { group: name, mechanism: 'X25519',                type: 'Classical',  isPQC: false };
  if (name?.includes('secp256r1') || name?.includes('P-256')) return { group: name, mechanism: 'ECDHE (P-256)', type: 'Classical', isPQC: false };
  if (name?.includes('secp384r1') || name?.includes('P-384')) return { group: name, mechanism: 'ECDHE (P-384)', type: 'Classical', isPQC: false };
  if (name && name !== 'Unknown' && name !== '—') return { group: name, mechanism: name, type: 'Classical', isPQC: false };

  // No key_share group (TLS 1.2) — infer from cipher suite name
  if (cipherSuite) {
    const cs = cipherSuite.toUpperCase();
    if (cs.includes('ECDHE_ECDSA') || cs.includes('ECDHE-ECDSA'))
      return { group: 'ECDHE (P-256)', mechanism: 'ECDHE', type: 'Classical', isPQC: false };
    if (cs.includes('ECDHE_RSA') || cs.includes('ECDHE-RSA'))
      return { group: 'ECDHE', mechanism: 'ECDHE', type: 'Classical', isPQC: false };
    if (cs.includes('DHE_RSA') || cs.includes('DHE-RSA'))
      return { group: 'DHE', mechanism: 'DHE', type: 'Classical', isPQC: false };
    if (cs.includes('RSA_WITH') || cs.includes('RSA-WITH') || cs.startsWith('TLS_RSA'))
      return { group: 'RSA', mechanism: 'RSA (no FS)', type: 'Classical', isPQC: false };
  }
  return { group: '—', mechanism: '—', type: 'N/A', isPQC: false };
}

function isSigPQC(sig: string): boolean {
  return ['ML-DSA', 'Dilithium', 'SLH-DSA', 'SPHINCS', 'FALCON'].some(s => sig?.includes(s));
}

function daysLeft(dateStr: string): number {
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
}

function countryFlag(code: string | null | undefined): string {
  if (!code || code.length !== 2) return '';
  return Array.from(code.toUpperCase())
    .map(c => String.fromCodePoint(c.charCodeAt(0) + 0x1F1A5))
    .join('');
}

// ─── UI Primitives ────────────────────────────────────────────────────────────

function Row({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  return (
    <div className="detail-row">
      <span className="detail-label">{label}</span>
      <span className={`detail-value${mono ? ' mono' : ''}`}>{value ?? '—'}</span>
    </div>
  );
}

function TypeTag({ isPQC, label }: { isPQC: boolean; label: string }) {
  return <span className={`type-tag ${isPQC ? 'pqc' : 'classical'}`}>{label}</span>;
}

// ─── ChangeChip ───────────────────────────────────────────────────────────────
// Shows "was X" badge when a field changed. Direction is upgrade (green arrow up)
// or downgrade (red arrow down) based on simple ordering heuristics.
const GRADE_RANK: Record<string, number> = { 'A+':6, 'A':5, 'B':4, 'C':3, 'D':2, 'F':1 }
const TLS_RANK:   Record<string, number> = { 'TLS 1.3':3, 'TLS 1.2':2, 'TLS 1.1-':1 }
const KX_RANK:    Record<string, number> = {
  'X25519MLKEM768':3, 'X25519MLKEM1024':3, 'SecP256r1MLKEM768':3, 'SecP256r1MLKEM1024':3,
  'X25519 + ML-KEM-768':3, 'X25519 + ML-KEM-1024':3, 'P-256 + ML-KEM-768':3, 'P-256 + ML-KEM-1024':3,
  'Classical (X25519)':2, 'Classical (secp256r1)':2, 'Classical (secp384r1)':2,
  'Classical (Unknown)':1, 'Unknown':0,
}

function changeDir(prev: string, cur: string, rank: Record<string, number>): 'up' | 'down' | 'lateral' {
  const p = rank[prev] ?? -1, c = rank[cur] ?? -1
  if (p === -1 || c === -1) return 'lateral'
  return c > p ? 'up' : c < p ? 'down' : 'lateral'
}

function ChangeChip({ prev, cur, changedAt, rankMap }: {
  prev: string; cur: string; changedAt: string | null; rankMap?: Record<string, number>
}) {
  const dir = rankMap ? changeDir(prev, cur, rankMap) : 'lateral'
  const arrow  = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '↔'
  const color  = dir === 'up' ? 'var(--success)' : dir === 'down' ? 'var(--error)' : 'var(--text-dim)'
  const date   = changedAt ? new Date(changedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null
  return (
    <span className="change-chip" style={{ borderColor: color, color }}>
      {arrow} was <strong>{prev}</strong>
      {date && <span style={{ opacity: 0.65, marginLeft: 4 }}>{date}</span>}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'secure' ? 'secure' : status === 'insecure' ? 'insecure' : 'legacy';
  const label = status === 'secure' ? 'HARDENED' : status === 'insecure' ? 'FRACTURED' : status.toUpperCase();
  return <span className={`badge ${cls}`}>{label}</span>;
}

// ─── Data Quality Banner ──────────────────────────────────────────────────────

interface DQNotice { level: 'warn' | 'info' | 'error'; title: string; body: string }

function getDataQualityNotices(data: SiteData): DQNotice[] {
  const notices: DQNotice[] = []
  const hasCert   = !!(data.issuer_name || data.subject)
  const hasTLS    = data.tls_1_3 !== null || data.tls_1_2 !== null
  const isUnknown = data.pqc_status === 'unknown'

  // CT log fallback — cert recovered but no live connection
  if (!hasTLS && hasCert && isUnknown) {
    notices.push({
      level: 'warn',
      title: 'No live TLS connection — certificate from CT logs only',
      body:  'The probe could not establish a TCP/TLS connection to port 443. This host is likely an intranet resource, VPN endpoint, or firewall-restricted service not reachable from the public internet. Certificate data shown was recovered from public Certificate Transparency logs (crt.sh) — it reflects what the domain once published, not a live handshake. TLS version, key exchange, and cipher data are unavailable.',
    })
  }

  // Previously probed but now unreachable
  if (!hasTLS && !hasCert && isUnknown) {
    notices.push({
      level: 'warn',
      title: 'Host unreachable — no data available',
      body:  'The probe could not connect to this host and no certificate was found in Certificate Transparency logs. The domain may not serve HTTPS, may be decommissioned, or may be a DNS placeholder.',
    })
  }

  // Has TLS data but currently unknown (went offline since last probe)
  if (hasTLS && isUnknown) {
    notices.push({
      level: 'info',
      title: 'Host offline — showing last known data',
      body:  `This host was previously reachable but did not respond during the most recent probe. TLS and certificate data shown is from the last successful scan on ${new Date(data.last_updated).toLocaleDateString()}.`,
    })
  }

  // Expired certificate
  if (data.is_expired) {
    const expiry = data.not_after ? new Date(data.not_after).toLocaleDateString() : 'unknown date'
    notices.push({
      level: 'error',
      title: `Certificate expired ${expiry}`,
      body:  'The TLS certificate for this domain has passed its expiry date. Browsers will show a security warning. The domain may be decommissioned, the renewal workflow may have failed, or this may be an internal host that uses a separately-managed PKI.',
    })
  }

  return notices
}

function DataQualityBanner({ data }: { data: SiteData }) {
  const notices = getDataQualityNotices(data)
  if (notices.length === 0) return null

  const levelCfg = {
    error: { border: 'rgba(255,77,77,0.35)',   bg: 'rgba(255,77,77,0.06)',   icon: '⚠', color: '#ff6b6b' },
    warn:  { border: 'rgba(255,180,0,0.35)',   bg: 'rgba(255,180,0,0.06)',   icon: '⚠', color: '#ffcc00' },
    info:  { border: 'rgba(96,165,250,0.35)',  bg: 'rgba(96,165,250,0.06)',  icon: 'ℹ', color: '#60a5fa' },
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '1.25rem 0 0' }}>
      {notices.map((n, i) => {
        const cfg = levelCfg[n.level]
        return (
          <div key={i} style={{
            background: cfg.bg, border: `1px solid ${cfg.border}`,
            borderRadius: 8, padding: '0.9rem 1.1rem',
            display: 'flex', gap: '0.75rem', alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: '1rem', color: cfg.color, flexShrink: 0, marginTop: 1 }}>{cfg.icon}</span>
            <div>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: cfg.color, marginBottom: 4, letterSpacing: '0.02em' }}>
                {n.title}
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', lineHeight: 1.6 }}>
                {n.body}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Upgrade Ladder ───────────────────────────────────────────────────────────

const TIER_CFG = {
  broken:    { fg: '#ef4444', bg: 'rgba(239,68,68,0.07)',    badge: 'Broken'    },
  legacy:    { fg: '#9ca3af', bg: 'rgba(156,163,175,0.05)',  badge: 'Legacy'    },
  classical: { fg: '#f59e0b', bg: 'rgba(245,158,11,0.07)',   badge: 'Classical' },
  modern:    { fg: '#60a5fa', bg: 'rgba(96,165,250,0.07)',   badge: 'Modern'    },
  pqc:       { fg: '#00ff88', bg: 'rgba(0,255,136,0.07)',    badge: 'PQC Ready' },
} as const;
type TierLevel = keyof typeof TIER_CFG;

interface TierDef {
  label:        string;   // short monospace identifier
  sublabel:     string;   // one-line description
  level:        TierLevel;
  detectedLabel?: string; // what to show when this is the current tier (actual negotiated value)
  future?:      boolean;  // coming soon — not yet deployed industry-wide
}

function UpgradeLadder({ title, description, tiers, currentIdx }: {
  title:        string;
  description:  string;
  tiers:        TierDef[];   // index 0 = worst/oldest, last = best
  currentIdx:   number;      // -1 = no data, nothing highlighted
}) {
  const reversed = [...tiers].map((t, i) => ({ ...t, idx: i })).reverse();
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  return (
    <div className="detail-card" style={{ display: 'flex', flexDirection: 'column', padding: '1.25rem' }}>
      <h3 className="card-title" style={{ marginBottom: '0.25rem' }}>{title}</h3>
      <p style={{ fontSize: '0.7rem', color: 'var(--text-dim)', margin: '0 0 1rem', lineHeight: 1.5, opacity: 0.8 }}>{description}</p>

      <div style={{ flex: 1 }}>
        {reversed.map(({ label, sublabel, level, detectedLabel, future, idx }) => {
          const isCurrent = idx === currentIdx;
          const isBelow   = currentIdx >= 0 && idx < currentIdx;
          const isAbove   = currentIdx < 0 || idx > currentIdx;
          const isHovered = hoveredIdx === idx;
          const cfg       = TIER_CFG[level];
          const isBottom  = idx === 0;
          const active    = isCurrent || isHovered;

          return (
            <div key={label}
              onMouseEnter={() => setHoveredIdx(idx)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* ── Tier block ── */}
              <div style={{
                padding:      active ? '10px 12px' : '6px 12px',
                borderRadius: '8px',
                border:       active
                  ? `1px solid ${cfg.fg}99`
                  : isAbove && currentIdx >= 0
                  ? '1px dashed rgba(255,255,255,0.1)'
                  : '1px solid rgba(255,255,255,0.05)',
                background:   active ? cfg.bg : 'transparent',
                opacity:      isHovered ? 1 : isAbove && currentIdx >= 0 ? 0.38 : isBelow ? 0.48 : currentIdx < 0 ? 0.45 : 1,
                boxShadow:    active ? `0 0 18px ${cfg.fg}18` : 'none',
                display:      'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
                transition:   'all 0.15s',
                cursor:       'default',
              }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{
                    fontSize:   active ? '0.88rem' : '0.74rem',
                    fontWeight: active ? 800 : 500,
                    color:      active ? cfg.fg : 'var(--text-dim)',
                    fontFamily: 'monospace',
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {isCurrent && detectedLabel ? detectedLabel : label}
                  </div>
                  <div style={{ fontSize: '0.63rem', color: 'var(--text-dim)', marginTop: '2px', opacity: active ? 0.85 : 0.55, lineHeight: 1.4 }}>
                    {sublabel}{future && !isCurrent ? ' · not yet widely issued' : ''}
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', flexShrink: 0 }}>
                  {isCurrent && (
                    <span style={{
                      fontSize: '0.5rem', fontWeight: 900, letterSpacing: '0.16em',
                      padding: '1px 6px', borderRadius: '3px',
                      background: `${cfg.fg}22`, color: cfg.fg, border: `1px solid ${cfg.fg}55`,
                    }}>CURRENT</span>
                  )}
                  <span style={{
                    fontSize: '0.5rem', letterSpacing: '0.13em', textTransform: 'uppercase',
                    fontWeight: 700, color: active ? cfg.fg : 'rgba(255,255,255,0.18)',
                  }}>{cfg.badge}</span>
                  {isBelow && <span style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.18)', lineHeight: 1 }}>✓</span>}
                </div>
              </div>

              {/* ── Connector ── (not below the bottom tier) */}
              {!isBottom && (
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'column', height: '16px' }}>
                  <div style={{
                    width: '1px', flex: 1,
                    background: idx < currentIdx
                      ? `${TIER_CFG[tiers[Math.min(idx + 1, tiers.length - 1)].level].fg}35`
                      : 'rgba(255,255,255,0.07)',
                  }} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Bottom label ── */}
      <div style={{
        marginTop: '0.75rem', paddingTop: '0.6rem',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        fontSize: '0.62rem', color: 'var(--text-dim)', textAlign: 'center', opacity: 0.6,
      }}>
        {currentIdx === tiers.length - 1
          ? `✓ At the highest tier`
          : `${tiers.length - 1 - currentIdx} tier${tiers.length - 1 - currentIdx > 1 ? 's' : ''} above current`}
      </div>
    </div>
  );
}

function gradeColor(g: string | undefined): string {
  if (!g) return 'var(--text-dim)';
  if (g.startsWith('A'))  return 'var(--success)';
  if (g === 'B' || g === 'C') return 'var(--warning)';
  return 'var(--error)'; // F or ?
}

// ─── Score Breakdown ──────────────────────────────────────────────────────────

interface ScoreResult {
  protocol:    number;
  keyExchange: number;
  certificate: number;
  headers:     number;
  total:       number;
}

function computeScore(data: SiteData, group: GroupInfo): ScoreResult {
  const protocolRaw = data.tls_1_3 ? 100 : data.tls_1_2 ? 70 : 0;
  const keRaw       = group.isPQC ? 100 : (group.group !== '—' ? 60 : 0);
  const sig         = (data.sig_alg   ?? '').toLowerCase();
  const pubkey      = (data.pubkey_alg ?? '').toLowerCase();
  let certRaw = 100;
  if (sig.includes('sha1') || sig.includes('md5')) {
    certRaw = 0;
  } else if (pubkey.includes('rsa') && data.pubkey_size <= 2048) {
    certRaw = 80;
  }
  const headersRaw = (data.hsts_max_age ?? 0) > 0 ? 100 : 0;
  const total = protocolRaw * 0.25 + keRaw * 0.40 + certRaw * 0.25 + headersRaw * 0.10;
  return { protocol: protocolRaw, keyExchange: keRaw, certificate: certRaw, headers: headersRaw, total };
}

function ScoreBreakdown({ data, group }: { data: SiteData; group: GroupInfo }) {
  const scores = computeScore(data, group);

  const categories = [
    {
      label:    'Key Exchange',
      weight:   40,
      raw:      scores.keyExchange,
      weighted: scores.keyExchange * 0.40,
      detail:   scores.keyExchange === 100 ? 'PQC hybrid — quantum safe' : scores.keyExchange === 60 ? 'Classical only — HNDL risk' : 'None detected',
    },
    {
      label:    'TLS Protocol',
      weight:   25,
      raw:      scores.protocol,
      weighted: scores.protocol * 0.25,
      detail:   data.tls_1_3 ? 'TLS 1.3 — PQC capable' : data.tls_1_2 ? 'TLS 1.2 — no PQC key exchange' : 'TLS 1.1 or below — deprecated',
    },
    {
      label:    'Certificate',
      weight:   25,
      raw:      scores.certificate,
      weighted: scores.certificate * 0.25,
      detail:   scores.certificate === 100 ? 'Strong key & signature' : scores.certificate === 80 ? 'RSA-2048 — acceptable, not optimal' : 'Weak key or SHA-1 signature',
    },
    {
      label:    'Security Headers',
      weight:   10,
      raw:      scores.headers,
      weighted: scores.headers * 0.10,
      detail:   scores.headers === 100 ? 'HSTS enforced' : 'HSTS not detected',
    },
  ];

  const scoreColor = (raw: number) =>
    raw >= 90 ? 'var(--success)' : raw >= 60 ? 'var(--warning)' : 'var(--error)';

  return (
    <div style={{
      background: 'var(--bg-card)',
      border: '1px solid var(--border-bright)',
      borderRadius: '16px',
      padding: '1.25rem 1.5rem',
      marginTop: '1.25rem',
      boxShadow: 'var(--card-shadow)',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1rem' }}>
        <div>
          <span style={{ fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-dim)', fontWeight: 700 }}>
            Score Breakdown
          </span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', opacity: 0.55, marginLeft: '0.75rem' }}>
            How the grade is calculated
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
          <span style={{ fontSize: '1.4rem', fontWeight: 900, color: gradeColor(data.overall_grade), fontVariantNumeric: 'tabular-nums' }}>
            {scores.total.toFixed(1)}
          </span>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}> / 100</span>
        </div>
      </div>

      {/* Category bars */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {categories.map(cat => (
          <div key={cat.label}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-main)', minWidth: '130px' }}>{cat.label}</span>
                <span style={{ fontSize: '0.62rem', color: 'var(--text-dim)', opacity: 0.6 }}>{cat.detail}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexShrink: 0, marginLeft: '8px' }}>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>{cat.weight}% weight</span>
                <span style={{
                  fontSize: '0.8rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                  color: scoreColor(cat.raw), minWidth: '36px', textAlign: 'right',
                }}>+{cat.weighted.toFixed(1)}</span>
              </div>
            </div>
            {/* Bar */}
            <div style={{ height: '6px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${cat.raw}%`,
                background: cat.raw >= 90
                  ? 'linear-gradient(90deg, var(--success), #00cc6a)'
                  : cat.raw >= 60
                  ? 'linear-gradient(90deg, var(--warning), #e6b800)'
                  : 'linear-gradient(90deg, var(--error), #cc3d3d)',
                borderRadius: '3px',
                transition: 'width 0.8s ease',
              }} />
            </div>
            {/* Sub-labels */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
              <span style={{ fontSize: '0.6rem', color: 'var(--text-dim)', opacity: 0.4 }}>0</span>
              <span style={{ fontSize: '0.6rem', color: scoreColor(cat.raw), opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>{cat.raw} / 100</span>
            </div>
          </div>
        ))}
      </div>

      {/* Grade threshold guide */}
      <div style={{
        marginTop: '1rem', paddingTop: '0.75rem',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap',
      }}>
        {[
          { g: 'A+', min: 95, color: 'var(--success)'  },
          { g: 'A',  min: 90, color: 'var(--success)'  },
          { g: 'B',  min: 80, color: 'var(--warning)'  },
          { g: 'C',  min: 70, color: 'var(--warning)'  },
          { g: 'F',  min: 0,  color: 'var(--error)'    },
        ].map(({ g, min, color }) => {
          const isCurrent = data.overall_grade === g || (g === 'F' && data.overall_grade === 'F');
          return (
            <div key={g} style={{
              padding: '2px 10px', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 700,
              background: isCurrent ? `${color}22` : 'transparent',
              border: isCurrent ? `1px solid ${color}66` : '1px solid rgba(255,255,255,0.08)',
              color: isCurrent ? color : 'rgba(255,255,255,0.25)',
            }}>
              {g} {min === 95 ? '≥95' : min === 90 ? '90–94' : min === 80 ? '80–89' : min === 70 ? '70–79' : '<70'}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Q-Day Countdown ──────────────────────────────────────────────────────────

const Q_DAY = new Date('2030-04-14T00:00:00Z');

function QDayCountdown({ compact }: { compact?: boolean } = {}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = Math.max(0, Q_DAY.getTime() - now);
  const days    = Math.floor(diff / 86_400_000);
  const hours   = Math.floor((diff % 86_400_000) / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000)  /    60_000);
  const seconds = Math.floor((diff % 60_000)      /     1_000);
  const pad = (n: number) => String(n).padStart(2, '0');

  if (compact) {
    return (
      <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.75rem', fontWeight: 800, color: '#f87171', letterSpacing: '-0.01em' }}>
        ⚠ {days}d {pad(hours)}h {pad(minutes)}m
      </span>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, rgba(220,38,38,0.07) 0%, rgba(0,0,0,0) 70%)',
      border: '1px solid rgba(220,38,38,0.22)',
      borderRadius: '12px',
      padding: '1.1rem 1.4rem',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '0.5rem',
      minWidth: '220px',
    }}>
      <div style={{ fontSize: '0.6rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(248,113,113,0.75)', fontWeight: 700 }}>
        &#9888;&nbsp; Estimated Q-Day
      </div>
      <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'baseline' }}>
        {[
          { value: days,    label: 'd' },
          { value: hours,   label: 'h' },
          { value: minutes, label: 'm' },
          { value: seconds, label: 's' },
        ].map(({ value, label }) => (
          <span key={label} style={{ fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
            <span style={{ fontSize: label === 'd' ? '1.7rem' : '1.3rem', fontWeight: 800, color: '#f87171', letterSpacing: '-0.02em' }}>
              {label === 'd' ? days : pad(value)}
            </span>
            <span style={{ fontSize: '0.6rem', color: 'var(--text-dim)', marginLeft: '1px' }}>{label}</span>
          </span>
        ))}
      </div>
      <div style={{ fontSize: '0.62rem', color: 'rgba(248,113,113,0.45)', letterSpacing: '0.03em', lineHeight: 1.4 }}>
        When quantum breaks RSA &amp; ECC
      </div>
    </div>
  );
}

// ─── Registry helpers ─────────────────────────────────────────────────────────

function registryScore(d: Domain): number | null {
  if (d.key_exchange === null || d.key_exchange === undefined) return null;
  const tls13 = (d.tls_1_3 ?? 0) > 0;
  const group = (d.pqc_group_name ?? d.key_exchange ?? '').toLowerCase();
  const isPQC = group.includes('mlkem') || group.includes('kyber');
  const hasKE = (d.key_exchange ?? '').length > 0;
  const keScore = isPQC ? 100 : hasKE ? 60 : 0;
  const protocolScore = tls13 ? 100 : 70;
  const sig = (d.sig_alg ?? '').toLowerCase();
  const pubkey = (d.pubkey_alg ?? '').toLowerCase();
  let certScore = 100;
  if (sig.includes('sha1') || sig.includes('md5')) certScore = 0;
  else if (pubkey.includes('rsa') && (d.pubkey_size ?? 0) <= 2048) certScore = 80;
  const headersScore = (d.hsts_max_age ?? 0) > 0 ? 100 : 0;
  return Math.round(protocolScore * 0.25 + keScore * 0.40 + certScore * 0.25 + headersScore * 0.10);
}

// ─── Registry ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

const SELECT_STYLE: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border)',
  borderRadius: '6px',
  padding: '5px 8px',
  color: 'var(--text-main)',
  fontSize: '0.78rem',
  cursor: 'pointer',
};

function Registry() {
  const [stats, setStats]               = useState<any>(null);
  const [domains, setDomains]           = useState<Domain[]>([]);
  const [total, setTotal]               = useState(0);
  const [page, setPage]                 = useState(0);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterGrade, setFilterGrade]   = useState('');
  const [filterCipher, setFilterCipher] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [tableSearch, setTableSearch]   = useState('');
  const [tableSearchInput, setTableSearchInput] = useState('');
  const [search, setSearch]             = useState('');
  const [cipherOptions, setCipherOptions]   = useState<string[]>([]);
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE}/v1/stats`).then(r => r.json()).then(setStats);
    fetch(`${API_BASE}/v1/filter-options`)
      .then(r => r.json())
      .then((d: { ciphers: string[]; countries: string[] }) => {
        setCipherOptions(d.ciphers ?? []);
        setCountryOptions(d.countries ?? []);
      });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) });
    if (filterStatus)  params.set('status',  filterStatus);
    if (filterGrade)   params.set('grade',   filterGrade);
    if (filterCipher)  params.set('cipher',  filterCipher);
    if (filterCountry) params.set('country', filterCountry);
    if (tableSearch)   params.set('search',  tableSearch);
    fetch(`${API_BASE}/v1/list?${params}`)
      .then(r => r.json())
      .then(data => { setDomains(data.results || []); setTotal(data.total || 0); });
  }, [page, filterStatus, filterGrade, filterCipher, filterCountry, tableSearch]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const isValidDomain = (value: string) => {
    const stripped = value.replace(/^\*\./, '').replace(/^www\./, '');
    return /^[a-zA-Z0-9][a-zA-Z0-9\-\.]*\.[a-zA-Z]{2,}$/.test(stripped);
  };

  const handleSearch = () => {
    if (!search || !isValidDomain(search)) return;
    const isCompany = search.startsWith('*.') || search.endsWith('.*');
    if (isCompany) {
      navigate(`/company/${search.replace(/^\*\./, '')}`);
    } else {
      navigate(`/report/${search.replace(/^www\./, '')}`);
    }
  };

  const applyTableSearch = () => { setPage(0); setTableSearch(tableSearchInput); };

  const resetFilters = () => {
    setFilterStatus(''); setFilterGrade(''); setFilterCipher(''); setFilterCountry('');
    setTableSearch(''); setTableSearchInput(''); setPage(0);
  };

  const hasFilters = filterStatus || filterGrade || filterCipher || filterCountry || tableSearch;

  return (
    <>
      {/* ── Hero ── */}
      <header style={{
        padding: '1.5rem 0 1rem',
        borderBottom: '1px solid var(--border)',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 280 }}>
            <h1 style={{ margin: '0 0 0.2rem', fontSize: '1.5rem', fontWeight: 800, lineHeight: 1.2 }}>
              Sentry Manifest
            </h1>
            <p style={{ color: 'var(--text-dim)', margin: '0 0 0.75rem', fontSize: '0.82rem' }}>
              Global inventory of Post-Quantum Cryptography adoption across the Sentry Array.
            </p>
            <div className="search-box" style={{ margin: 0, maxWidth: '460px' }}>
              <input
                placeholder="domain.com or *.domain.com for company report…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                style={search && !isValidDomain(search) ? { borderColor: 'var(--error)', outline: '1px solid var(--error)' } : undefined}
              />
              <button onClick={handleSearch} disabled={!!search && !isValidDomain(search)}>Analyze</button>
            </div>
          </div>

          {/* Stats strip */}
          {stats && (
            <div style={{ display: 'flex', gap: '1px', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border-bright)', flexShrink: 0 }}>
              {[
                { label: 'Total',      value: stats.total,          color: 'var(--text-main)' },
                { label: 'Hardened',   value: stats.secure  || 0,   color: 'var(--success)'   },
                { label: 'Legacy',     value: stats.legacy  || 0,   color: 'var(--warning)'   },
                { label: 'At Risk',    value: stats.insecure || 0,  color: 'var(--error)'     },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: 'var(--bg-card)', padding: '0.5rem 0.9rem', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{ fontSize: '1rem', fontWeight: 800, color, fontVariantNumeric: 'tabular-nums' }}>{value?.toLocaleString()}</span>
                  <span style={{ fontSize: '0.62rem', color: 'var(--text-dim)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        <input
          placeholder="Filter hostnames…"
          value={tableSearchInput}
          onChange={e => setTableSearchInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && applyTableSearch()}
          style={{ ...SELECT_STYLE, flex: '1', minWidth: '160px', padding: '5px 10px' }}
        />
        <button onClick={applyTableSearch} className="recheck-btn" style={{ padding: '5px 12px', fontSize: '0.78rem' }}>Search</button>

        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(0); }} style={SELECT_STYLE}>
          <option value="">All statuses</option>
          <option value="secure">PQC Secure</option>
          <option value="legacy">Legacy</option>
          <option value="insecure">Insecure</option>
          <option value="unknown">Unknown</option>
        </select>

        <select value={filterGrade} onChange={e => { setFilterGrade(e.target.value); setPage(0); }} style={SELECT_STYLE}>
          <option value="">All scores</option>
          <option value="A+">A+ (95–100)</option>
          <option value="A">A (90–94)</option>
          <option value="B">B (80–89)</option>
          <option value="C">C (70–79)</option>
          <option value="F">F (&lt;70)</option>
        </select>

        <select value={filterCipher} onChange={e => { setFilterCipher(e.target.value); setPage(0); }} style={SELECT_STYLE}>
          <option value="">All ciphers</option>
          {cipherOptions.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setPage(0); }} style={SELECT_STYLE}>
          <option value="">All countries</option>
          {countryOptions.map(c => (
            <option key={c} value={c}>{countryFlag(c)} {c}</option>
          ))}
        </select>

        {hasFilters && (
          <button onClick={resetFilters} className="recheck-btn" style={{ padding: '5px 10px', fontSize: '0.78rem', opacity: 0.7 }}>✕ Clear</button>
        )}

        <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem', marginLeft: 'auto' }}>
          {total > 0 ? `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total.toLocaleString()}` : ''}
        </span>
      </div>

      {/* ── Table ── */}
      <div style={{ overflowX: 'auto' }}>
        <table className="data-table registry-table">
          <thead>
            <tr>
              <th>Hostname</th>
              <th>Country</th>
              <th>Quantum Safe</th>
              <th>Key Exchange</th>
              <th>Cert Signature</th>
              <th>Grade</th>
              <th>Score</th>
              <th>Violations</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {domains.length === 0 && (
              <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-dim)', padding: '2rem' }}>No results</td></tr>
            )}
            {domains.map(d => {
              const score = registryScore(d);
              // Show pqc_group_name when available (e.g. "X25519MLKEM768"), else key_exchange
              const ke = d.pqc_group_name ?? d.key_exchange ?? '—';
              const isPQC = ke.toLowerCase().includes('mlkem') || ke.toLowerCase().includes('kyber');
              // Use ip.guide geo_country (via asn_country alias from COALESCE in API)
              const countryCode = d.asn_country ?? null;
              const flag = countryFlag(countryCode);
              return (
                <tr key={d.hostname}>
                  <td style={{ fontWeight: 600, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {d.hostname}
                  </td>
                  <td style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {countryCode ? (
                      <span title={countryCode} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <span style={{ fontSize: '1rem' }}>{flag}</span>
                        <span style={{ color: 'var(--text-dim)' }}>{countryCode}</span>
                      </span>
                    ) : <span style={{ color: 'var(--text-dim)' }}>—</span>}
                  </td>
                  <td style={{ fontSize: '0.78rem' }}>
                    {d.pqc_status === 'secure'
                      ? <span style={{ color: 'var(--success)', fontWeight: 700 }}>✓ Yes</span>
                      : d.pqc_status === 'unknown'
                        ? <span style={{ color: 'var(--text-dim)' }}>—</span>
                        : <span style={{ color: 'var(--error)', fontWeight: 700 }}>✗ No</span>}
                  </td>
                  <td style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', fontFamily: 'monospace', color: isPQC ? 'var(--success)' : 'var(--text-main)' }}>
                    {ke}
                  </td>
                  <td style={{ fontSize: '0.75rem', whiteSpace: 'nowrap', fontFamily: 'monospace', color: 'var(--text-dim)' }}>
                    {d.sig_alg ?? '—'}
                  </td>
                  <td>
                    <div className="grade-circle" style={{ color: gradeColor(d.overall_grade), width: 28, height: 28, fontSize: '0.72rem' }}>
                      {d.overall_grade ?? '?'}
                    </div>
                  </td>
                  <td style={{ fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', color: score !== null ? gradeColor(d.overall_grade) : 'var(--text-dim)', fontWeight: 600 }}>
                    {score !== null ? score : '—'}
                  </td>
                  <td style={{ fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}>
                    {d.scan_violations === null || d.scan_violations === undefined
                      ? <span style={{ color: 'var(--text-dim)' }}>—</span>
                      : d.scan_violations === 0
                        ? <span style={{ color: 'var(--success)' }}>0</span>
                        : <span style={{ color: 'var(--warning)' }}>⚠ {d.scan_violations}</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <Link to={`/report/${d.hostname}`} className="recheck-btn" style={{ padding: '3px 9px', fontSize: '0.72rem', marginRight: 4 }}>Report</Link>
                    <Link to={`/company/${d.hostname.split('.').slice(-2).join('.')}`} className="recheck-btn" style={{ padding: '3px 9px', fontSize: '0.72rem', borderColor: 'var(--accent-purple)', color: 'var(--accent-purple)' }}>Co.</Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', margin: '1.25rem 0', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
          <button onClick={() => setPage(0)}           disabled={page === 0}              className="recheck-btn" style={{ padding: '4px 9px', fontSize: '0.75rem' }}>«</button>
          <button onClick={() => setPage(p => p - 1)}  disabled={page === 0}              className="recheck-btn" style={{ padding: '4px 9px', fontSize: '0.75rem' }}>‹ Prev</button>
          <span>Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(p => p + 1)}  disabled={page >= totalPages - 1}  className="recheck-btn" style={{ padding: '4px 9px', fontSize: '0.75rem' }}>Next ›</button>
          <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} className="recheck-btn" style={{ padding: '4px 9px', fontSize: '0.75rem' }}>»</button>
        </div>
      )}
    </>
  );
}

// ─── Company Report ───────────────────────────────────────────────────────────

const PROBE_CONCURRENCY = 5;

function CompanyReport() {
  const { domain } = useParams<{ domain: string }>();
  const [phase, setPhase]       = useState<'discovering' | 'probing' | 'done'>('discovering');
  const [results, setResults]   = useState<Record<string, SubdomainResult>>({});
  const [totalCT, setTotalCT]   = useState(0);
  const [probeQueue, setProbeQueue] = useState<string[]>([]);
  const [probed, setProbed]     = useState(0);
  const [totalToProbe, setTotalToProbe] = useState(0);
  const abortRef = useRef(false);

  useEffect(() => {
    abortRef.current = false;
    runReport();
    return () => { abortRef.current = true; };
  }, [domain]);

  async function runReport() {
    setPhase('discovering');
    setResults({});
    setProbed(0);

    // Step 1: discover via api-worker (CT + D1)
    const res  = await fetch(`${API_BASE}/v1/company/${domain}`);
    const data = await res.json();
    if (data.error) return setPhase('done');

    setTotalCT(data.total_discovered || 0);

    // Seed results with already-known domains
    const initial: Record<string, SubdomainResult> = {};
    for (const d of (data.known || [])) {
      initial[d.hostname] = { ...d, _state: 'known' };
    }
    setResults(initial);

    // Step 2: probe unprobed in batches
    const queue: string[] = data.unprobed || [];
    setTotalToProbe(queue.length);
    setPhase('probing');

    for (let i = 0; i < queue.length; i += PROBE_CONCURRENCY) {
      if (abortRef.current) break;
      const batch = queue.slice(i, i + PROBE_CONCURRENCY);

      // Mark as probing
      setResults(prev => {
        const next = { ...prev };
        for (const h of batch) next[h] = { hostname: h, pqc_status: 'probing', overall_grade: '…', _state: 'probing' };
        return next;
      });

      await Promise.all(batch.map(async (hostname) => {
        try {
          // Use the probe response directly — avoids D1 replication lag that
          // causes /v1/details to return 404 immediately after a fresh write.
          const r    = await fetch(`${PROBE_URL}/v1/check/${hostname}`);
          const json = await r.json();
          setResults(prev => ({
            ...prev,
            [hostname]: {
              hostname,
              pqc_status:    json.pqc_status    || 'unknown',
              overall_grade: json.overall_grade  || '?',
              pqc_group_name: json.pqc_group     || json.pqc_group_name || '',
              _state: (json.pqc_status && json.pqc_status !== 'unreachable') ? 'done' : 'error',
            }
          }));
        } catch {
          setResults(prev => ({ ...prev, [hostname]: { hostname, pqc_status: 'error', overall_grade: '?', _state: 'error' } }));
        }
        setProbed(p => p + 1);
      }));
    }

    setPhase('done');
  }

  const rows     = Object.values(results);
  const secure   = rows.filter(r => r.pqc_status === 'secure').length;
  const legacy   = rows.filter(r => r.pqc_status === 'legacy').length;
  const insecure = rows.filter(r => r.pqc_status === 'insecure').length;
  const total    = rows.filter(r => r._state !== 'probing').length;
  const pct      = total > 0 ? Math.round((secure / total) * 100) : 0;

  const sorted = [...rows].sort((a, b) => {
    const order: Record<string, number> = { insecure: 0, legacy: 1, error: 2, secure: 3, probing: 4 };
    return (order[a.pqc_status] ?? 5) - (order[b.pqc_status] ?? 5);
  });

  return (
    <div style={{animation:'fadeIn 0.4s'}}>
      {/* Header */}
      <div className="report-header">
        <div>
          <div style={{fontSize:'0.75rem', letterSpacing:'2px', color:'var(--text-dim)', marginBottom:'4px', textTransform:'uppercase'}}>Company Quantum Report</div>
          <h1 style={{margin:0, fontSize:'2.2rem'}}>*.{domain}</h1>
          <p style={{color:'var(--text-dim)', margin:'6px 0 0'}}>
            {phase === 'discovering' && 'Discovering subdomains via Certificate Transparency…'}
            {phase === 'probing'     && `Probing ${probed} / ${totalToProbe} discovered subdomains…`}
            {phase === 'done'        && `${totalCT} subdomains discovered · ${rows.length} probed`}
          </p>
        </div>
        <div style={{textAlign:'right'}}>
          <div className="grade-large" style={{color: pct >= 80 ? 'var(--success)' : pct >= 40 ? 'var(--warning)' : 'var(--error)'}}>
            {phase === 'discovering' ? '…' : `${pct}%`}
          </div>
          <div style={{fontSize:'0.75rem', color:'var(--text-dim)', marginTop:'4px'}}>PQC Coverage</div>
        </div>
      </div>

      {/* Progress bar */}
      {phase === 'probing' && (
        <div className="probe-progress">
          <div className="probe-progress-bar" style={{width: `${totalToProbe ? (probed / totalToProbe) * 100 : 0}%`}} />
        </div>
      )}

      {/* Summary */}
      <section className="stats-grid" style={{marginBottom:'2rem'}}>
        <div className="stat-card">
          <div className="stat-value">{rows.length}</div>
          <div className="stat-label">Probed</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{color:'var(--success)'}}>{secure}</div>
          <div className="stat-label">PQC Secure</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{color:'var(--warning)'}}>{legacy}</div>
          <div className="stat-label">Legacy TLS</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{color:'var(--error)'}}>{insecure}</div>
          <div className="stat-label">At Risk</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{color: pct >= 80 ? 'var(--success)' : pct >= 40 ? 'var(--warning)' : 'var(--error)'}}>
            {phase === 'discovering' ? '…' : `${pct}%`}
          </div>
          <div className="stat-label">PQC Coverage</div>
        </div>
      </section>

      {/* Coverage bar */}
      {total > 0 && (
        <div className="coverage-bar-wrap">
          <div className="coverage-bar">
            <div className="coverage-seg secure"  style={{width:`${(secure   / total) * 100}%`}} title={`Secure: ${secure}`} />
            <div className="coverage-seg legacy"  style={{width:`${(legacy   / total) * 100}%`}} title={`Legacy: ${legacy}`} />
            <div className="coverage-seg insecure" style={{width:`${(insecure / total) * 100}%`}} title={`At Risk: ${insecure}`} />
          </div>
          <div className="coverage-legend">
            <span className="cleg secure">■ PQC Secure</span>
            <span className="cleg legacy">■ Legacy</span>
            <span className="cleg insecure">■ At Risk</span>
          </div>
        </div>
      )}

      {/* Results table */}
      <table className="data-table" style={{marginTop:'1.5rem'}}>
        <thead>
          <tr>
            <th>Subdomain</th>
            <th>Status</th>
            <th>Grade</th>
            <th>Key Exchange</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(r => (
            <tr key={r.hostname} style={{opacity: r._state === 'probing' ? 0.5 : 1}}>
              <td style={{fontWeight:'bold', fontFamily:'monospace', fontSize:'0.875rem'}}>{r.hostname}</td>
              <td>
                {r._state === 'probing'
                  ? <span style={{color:'var(--accent-cyan)', fontSize:'0.8rem'}}>⟳ probing…</span>
                  : r._state === 'error'
                  ? <span style={{color:'var(--text-dim)', fontSize:'0.8rem'}}>unreachable</span>
                  : <StatusBadge status={r.pqc_status} />
                }
              </td>
              <td>
                {r.overall_grade && r._state !== 'probing' && (
                  <div className="grade-circle" style={{color: gradeColor(r.overall_grade)}}>
                    {r.overall_grade}
                  </div>
                )}
              </td>
              <td style={{fontSize:'0.8rem', color:'var(--text-dim)', fontFamily:'monospace'}}>
                {r.pqc_group_name || (r._state === 'done' || r._state === 'known' ? '—' : '')}
              </td>
              <td>
                {(r._state === 'done' || r._state === 'known') && (
                  <Link to={`/report/${r.hostname}`} className="recheck-btn" style={{padding:'4px 12px', fontSize:'0.75rem'}}>
                    Details →
                  </Link>
                )}
              </td>
            </tr>
          ))}
          {phase === 'discovering' && (
            <tr><td colSpan={5} style={{textAlign:'center', color:'var(--text-dim)', padding:'2rem'}}>
              Querying Certificate Transparency logs…
            </td></tr>
          )}
        </tbody>
      </table>

      {phase === 'done' && totalCT > rows.length && (
        <p style={{textAlign:'center', color:'var(--text-dim)', fontSize:'0.8rem', marginTop:'1rem'}}>
          {totalCT - rows.length} additional subdomains discovered but not probed (capped at 150 per report).
        </p>
      )}
    </div>
  );
}

// ─── Security Headers Card ────────────────────────────────────────────────────

function HeaderRow({ label, desc, pass, value }: { label: string; desc: string; pass: boolean | null; value: React.ReactNode }) {
  const icon = pass === null ? null : pass
    ? <span style={{color:'var(--success)', fontWeight:'bold', marginRight:'6px'}}>✓</span>
    : <span style={{color:'var(--error)',   fontWeight:'bold', marginRight:'6px'}}>✗</span>;
  return (
    <div style={{display:'flex', alignItems:'flex-start', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid rgba(255,255,255,0.05)', gap:'12px'}}>
      <div style={{minWidth:'220px'}}>
        <div style={{color:'var(--text-secondary)', fontSize:'0.82rem', fontWeight:600, display:'flex', alignItems:'center'}}>{icon}{label}</div>
        <div style={{color:'var(--text-dim)', fontSize:'0.72rem', marginTop:'2px', paddingLeft: pass !== null ? '18px' : '0'}}>{desc}</div>
      </div>
      <span style={{fontSize:'0.85rem', color:'var(--text-secondary)', fontFamily:'monospace', textAlign:'right', paddingTop:'1px'}}>{value}</span>
    </div>
  );
}

function AiAssessmentCard({ ai }: { ai: NonNullable<EnrichData['ai']> }) {
  const urgency   = ai.urgency_score ?? 0;
  const barColor  = urgency >= 76 ? 'var(--error)'
    : urgency >= 51 ? '#e06820'
    : urgency >= 26 ? 'var(--warning)'
    : 'var(--text-dim)';

  return (
    <div className="detail-card" style={{ gridColumn: '1 / -1' }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
        <h3 className="card-title" style={{ margin: 0, fontFamily: 'monospace', letterSpacing: '0.1em' }}>
          POLARIS-1 // COMMAND SITREP
        </h3>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ flex: 1, height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${urgency}%`, height: '100%', background: barColor, borderRadius: '3px', transition: 'width 0.5s ease' }} />
          </div>
          <span style={{ fontSize: '0.75rem', color: barColor, fontWeight: 700, minWidth: '2.8rem', textAlign: 'right' }}>
            {urgency}/100
          </span>
        </div>
      </div>

      {/* Narrative */}
      <div style={{ fontSize: '0.82rem', lineHeight: 1.7, color: 'var(--text-main)', marginBottom: '1rem', padding: '0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', borderLeft: `3px solid ${barColor}`, fontFamily: 'monospace' }}>
        {ai.narrative}
      </div>

      {/* Recommendations */}
      {ai.recommendations.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: '0.5rem' }}>Migration Path</div>
          <ol style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {ai.recommendations.map((rec, i) => {
              const spaceIdx = rec.indexOf(' ');
              const first = spaceIdx > -1 ? rec.slice(0, spaceIdx) : rec;
              const rest  = spaceIdx > -1 ? rec.slice(spaceIdx) : '';
              return (
                <li key={i} style={{ fontSize: '0.8rem', lineHeight: 1.6, color: 'var(--text-main)', padding: '0.25rem 0', display: 'flex', gap: '0.5rem' }}>
                  <span style={{ color: 'var(--accent-cyan)', flexShrink: 0 }}>→</span>
                  <span><strong>{first}</strong>{rest}</span>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {/* Anomalies */}
      {ai.anomalies.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          {ai.anomalies.map((a, i) => (
            <div key={i} style={{ fontSize: '0.78rem', color: 'var(--warning)', padding: '0.3rem 0.6rem', background: 'rgba(255,170,0,0.07)', borderRadius: '4px', marginBottom: '4px' }}>
              ⚠ {a}
            </div>
          ))}
        </div>
      )}

      {/* Footer */}
      <div style={{ fontSize: '0.65rem', color: 'var(--text-dim)', opacity: 0.5, marginTop: '0.5rem' }}>
        POLARIS-1 · Cloudflare Workers AI · llama-3.1-8b-instruct-fast · {new Date().toLocaleDateString()}
      </div>
    </div>
  );
}

function formatHstsAge(seconds: number | null): string {
  if (!seconds) return '—';
  const days = Math.round(seconds / 86400);
  if (days >= 365) { const y = Math.round(days / 365); return `${y} yr`; }
  if (days >= 30)  { const m = Math.round(days / 30);  return `${m} mo`; }
  return days === 1 ? '1 day' : `${days} days`;
}

function SecurityHeadersCard({ data, enrich }: { data: SiteData; enrich: EnrichData | null }) {
  const hasHsts = (data.hsts_max_age ?? 0) > 0;
  return (
    <div className="detail-card" style={{gridColumn: '1 / -1'}}>
      <h3 className="card-title">Security Headers</h3>
      <HeaderRow
        label="HSTS"
        desc="Forces browsers to connect only via HTTPS for the specified duration, preventing protocol downgrade attacks."
        pass={hasHsts}
        value={hasHsts
          ? <span>
              max-age={formatHstsAge(data.hsts_max_age)}
              {!!data.hsts_include_subdomains && <span style={{marginLeft:'8px', color:'var(--accent-cyan)', fontSize:'0.75rem'}}>includeSubDomains</span>}
              {!!data.hsts_preload            && <span style={{marginLeft:'8px', color:'var(--accent-cyan)', fontSize:'0.75rem'}}>preload</span>}
            </span>
          : 'Not set'}
      />
      <HeaderRow
        label="Content-Security-Policy"
        desc="Restricts which scripts, styles, and resources the browser may load, mitigating XSS and data injection attacks."
        pass={!!data.has_csp}
        value={data.has_csp ? 'Present' : 'Not set'}
      />
      <HeaderRow
        label="X-Frame-Options"
        desc="Prevents the page from being embedded in an iframe on another origin, blocking clickjacking attacks."
        pass={!!data.x_frame_options}
        value={data.x_frame_options || 'Not set'}
      />
      <HeaderRow
        label="X-Content-Type-Options"
        desc="Tells the browser not to guess the content type (MIME sniffing), preventing attacks that exploit type confusion."
        pass={!!data.x_content_type_nosniff}
        value={data.x_content_type_nosniff ? 'nosniff' : 'Not set'}
      />
      <HeaderRow
        label="Referrer-Policy"
        desc="Controls how much referrer information is included in requests, limiting data leakage to third parties."
        pass={!!data.referrer_policy}
        value={data.referrer_policy || 'Not set'}
      />
      <HeaderRow
        label="Permissions-Policy"
        desc="Restricts access to browser APIs like camera, microphone, and geolocation, reducing the attack surface."
        pass={!!data.has_permissions_policy}
        value={data.has_permissions_policy ? 'Present' : 'Not set'}
      />
      <HeaderRow
        label="Server"
        desc="Identifies the web server software. Exposing this can aid fingerprinting; consider suppressing or obscuring it."
        pass={null}
        value={(() => {
          const srv = data.server || '';
          const isCfHeader = /^cloudflare/i.test(srv);
          const geoOrg = enrich?.geo?.org;
          // When probe returns "cloudflare" but the actual host is different,
          // the CF Worker's outbound fetch went through CF's own edge layer.
          // Show the real hosting provider from IP geo alongside the CDN label.
          if (isCfHeader && geoOrg && !/cloudflare/i.test(geoOrg)) {
            return (
              <span>
                <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                  {geoOrg}
                </span>
                <span style={{
                  marginLeft: '8px', fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)',
                  fontFamily: 'monospace',
                }}>
                  via Cloudflare CDN
                </span>
              </span>
            );
          }
          return srv || '—';
        })()}
      />
    </div>
  );
}

// ─── Site Report ──────────────────────────────────────────────────────────────

function SiteReportBody({ hostname: rawDomain, onBack, inline = false, refreshKey = 0, onViewCompanyReport }: {
  hostname: string;
  onBack: () => void;
  inline?: boolean;
  refreshKey?: number;
  onViewCompanyReport?: (domain: string) => void;
}) {
  // Normalize: strip leading www. so www.google.com and google.com share one record
  const domain = rawDomain?.replace(/^www\./, '') ?? '';
  const [data, setData]       = useState<SiteData | null>(null);
  const [enrich, setEnrich]   = useState<EnrichData | null>(null);
  const [loading, setLoading] = useState(true);

  const { isSignedIn, getToken } = useAuth();

  // readOnly = true → only read from DB, never trigger a probe (used by Refresh button)
  // force    = true → run a fresh probe first, then read (used by Recheck button)
  const loadDetails = async (force = false, readOnly = false) => {
    setLoading(true);
    try {
      if (force) {
        await fetch(`${PROBE_URL}/v1/check/${domain}?force=true`);
      }

      // After a forced probe, D1 replication can lag. Retry up to 4 times
      // with increasing delays before giving up.
      const delays = force ? [1500, 2000, 2500, 3000] : [0];
      let json: any = { error: 'not tried' };
      for (const delay of delays) {
        if (delay > 0) await new Promise(r => setTimeout(r, delay));
        const res = await fetch(`${API_BASE}/v1/details/${domain}`);
        json = await res.json();
        if (!json.error) break;
      }

      // Auto-promote to a probe only on the initial load, never on a manual Refresh
      if (json.error && !force && !readOnly) { loadDetails(true); return; }

      // If domain exists but was submitted by the plugin (no prior enrich),
      // force=true after a normal load so enrichment runs immediately rather than lazily.
      // Detected by: record found but last_updated is recent yet key_exchange is missing.
      const pluginOnlyRecord = !json.error && !force && !readOnly && !json.key_exchange
      if (pluginOnlyRecord) {
        // fire a fresh probe in the background — don't block the UI
        fetch(`${PROBE_URL}/v1/check/${domain}?force=true`).catch(() => {})
      }

      const enrichRes  = await fetch(`${API_BASE}/v1/enrich/${domain}${force ? '?force=true' : ''}`);
      const enrichJson = await enrichRes.json();
      setData(json.error ? null : json);
      setEnrich(enrichJson.error ? null : enrichJson);

      // Award points for discovering a non-quantum-safe domain.
      // Backend UNIQUE(clerk_user_id, hostname) ensures each domain only awards once.
      if (!json.error && isSignedIn) {
        maybeAwardPoints(domain, json.pqc_status, getToken);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => {
    // refreshKey > 0 means the user clicked the Refresh button — read-only, no probe
    loadDetails(false, refreshKey > 0);
  }, [domain, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return (
    <div className="loading-screen">
      <div className="loading-spinner" />
      <h2>Dispatching Sentry…</h2>
      <p style={{color:'var(--text-dim)'}}>Interrogating TLS handshake and certificate chain</p>
    </div>
  );
  if (!data) return (
    <div style={{textAlign:'center', padding:'5rem'}}>
      <h2>Domain Not Found</h2>
      <button className="recheck-btn" onClick={onBack}>Back</button>
    </div>
  );

  const cipher     = parseCipher(data.cipher_suite);
  const group      = getGroupInfo(data.pqc_group_name, data.cipher_suite);
  const sigIsPQC   = isSigPQC(data.sig_alg);
  const tlsVer     = data.tls_1_3 ? 'TLS 1.3' : data.tls_1_2 ? 'TLS 1.2' : 'Unknown';
  const days       = daysLeft(data.not_after);
  const gradeColorVal = gradeColor(data.overall_grade);
  const baseDomain = domain?.split('.').slice(-2).join('.');

  // ── Upgrade ladder indices ──
  const keTiers: TierDef[] = [
    { label: 'RSA Key Exchange',    sublabel: 'Static key · no forward secrecy · broken by Shor\'s algorithm',                                         level: 'broken'    },
    { label: 'DHE / DH',            sublabel: 'Discrete log · forward secrecy · quantum vulnerable',                                                    level: 'legacy'    },
    { label: 'ECDHE (P-256/P-384)', sublabel: 'Elliptic curve · forward secrecy · quantum vulnerable',                                                  level: 'classical' },
    { label: 'X25519',              sublabel: 'Modern elliptic curve · faster than P-256 · still quantum vulnerable',                                    level: 'modern'    },
    { label: 'PQC Hybrid (ML-KEM-768)',  sublabel: 'X25519MLKEM768 or SecP256r1MLKEM768 · ~192-bit quantum security · NIST FIPS 203',                   level: 'pqc'       },
    { label: 'PQC Hybrid (ML-KEM-1024)', sublabel: 'X25519MLKEM1024 or SecP256r1MLKEM1024 · ~256-bit quantum security · NIST FIPS 203 · highest tier',  level: 'pqc'       },
  ];
  const hasTLS = data.tls_1_3 !== null || data.tls_1_2 !== null
  // -1 = no data, nothing highlighted
  const keCurrentIdx = !hasTLS ? -1
    : group.group.includes('MLKEM1024') ? 5
    : group.group.includes('MLKEM768')  ? 4
    : (group.mechanism ?? '').toLowerCase().includes('x25519') ? 3
    : (group.mechanism ?? '').toLowerCase().match(/ecdhe|p-256|p-384|secp/) ? 2
    : (group.mechanism ?? '').toLowerCase().includes('dhe') ? 1
    : 0;

  const tlsTiers: TierDef[] = [
    { label: 'TLS 1.1 or below', sublabel: 'Deprecated by RFC 8996 · known protocol vulnerabilities',              level: 'broken'    },
    { label: 'TLS 1.2',          sublabel: 'Acceptable fallback · no key_share extension · PQC not possible',      level: 'classical' },
    { label: 'TLS 1.3',          sublabel: 'Required for PQC key exchange · encrypted handshake · fastest',        level: 'pqc'       },
  ];
  const tlsCurrentIdx = !hasTLS ? -1 : data.tls_1_3 ? 2 : data.tls_1_2 ? 1 : 0;

  const sigTiers: TierDef[] = [
    { label: 'MD5 / SHA-1 RSA',       sublabel: 'Cryptographically broken · collision attacks demonstrated',                      level: 'broken'    },
    { label: 'RSA-SHA256 / SHA384',   sublabel: 'Industry standard today · vulnerable to Shor\'s algorithm',                      level: 'classical' },
    { label: 'ECDSA-SHA256 / SHA384', sublabel: 'Smaller keys · efficient · still quantum vulnerable',                            level: 'modern'    },
    { label: 'ML-DSA / SLH-DSA',     sublabel: 'NIST FIPS 204/205 · quantum safe · CAs not yet issuing at scale',   level: 'pqc', future: true },
  ];
  const sigCurrentIdx = sigIsPQC ? 3
    : (data.sig_alg ?? '').toLowerCase().includes('ecdsa') ? 2
    : (data.sig_alg ?? '').toLowerCase().match(/sha1|md5/) ? 0
    : 1;

  const SectionHeader = ({ title, subtitle }: { title: string; subtitle: string }) => (
    <div style={{ margin: '2rem 0 1rem', display: 'flex', alignItems: 'baseline', gap: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.6rem' }}>
      <h2 style={{ margin: 0, fontSize: '0.8rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-dim)', fontWeight: 700 }}>{title}</h2>
      <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)', opacity: 0.6 }}>{subtitle}</span>
    </div>
  );

  return (
    <div style={{ animation: 'fadeIn 0.4s' }}>

      {/* ── Header ── */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr auto',
        gap: '1.5rem', alignItems: 'start',
        padding: '1.5rem 0 1.25rem',
        borderBottom: '1px solid var(--border)',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0, fontSize: '1.8rem', fontWeight: 800 }}>{data.hostname}</h1>
            <StatusBadge status={data.pqc_status} />
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center', marginTop: '6px' }}>
            {enrich?.geo && (
              <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
                {countryFlag(enrich.geo.country_code) && (
                  <span style={{ marginRight: '5px', fontSize: '1rem', lineHeight: 1 }}>
                    {countryFlag(enrich.geo.country_code)}
                  </span>
                )}
                {[enrich.geo.org, enrich.geo.country_name].filter(Boolean).join(' · ')}
                {enrich.geo.asn && <span style={{ marginLeft: '6px', fontFamily: 'monospace', fontSize: '0.72rem', color: 'rgba(160,160,160,0.6)' }}>{enrich.geo.asn}</span>}
              </span>
            )}
            <span style={{ fontSize: '0.78rem', color: 'var(--text-dim)' }}>
              Checked {new Date(data.last_updated).toLocaleString()}
            </span>
          </div>
          <div style={{ marginTop: '10px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              onClick={() => onViewCompanyReport ? onViewCompanyReport(baseDomain) : undefined}
              style={{ fontSize: '0.78rem', color: 'var(--accent-purple)', background: 'none', border: '1px solid var(--accent-purple)', padding: '3px 10px', borderRadius: '20px', cursor: 'pointer' }}
            >
              View Company Report →
            </button>
            <button className="recheck-btn" onClick={() => loadDetails(true)} style={{ fontSize: '0.78rem', padding: '3px 12px' }}>
              Recheck
            </button>
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '0.55rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: '4px' }}>Overall Grade</div>
          <div style={{ fontSize: '3.5rem', fontWeight: 900, lineHeight: 1, color: gradeColorVal }}>{data.overall_grade}</div>
          {data.prev_overall_grade && data.prev_overall_grade !== data.overall_grade && (
            <ChangeChip prev={data.prev_overall_grade} cur={data.overall_grade} changedAt={data.changed_at} rankMap={GRADE_RANK} />
          )}
        </div>
      </div>

      {/* ── Data quality notices ── */}
      <DataQualityBanner data={data} />

      {/* ── Score Breakdown ── */}
      <ScoreBreakdown data={data} group={group} />

      {/* ── AI Assessment ── */}
      {enrich?.ai && (
        <>
          <SectionHeader
            title="POLARIS-1 // Command Sitrep"
            subtitle="AI-generated intelligence briefing — quantum risk context and migration guidance"
          />
          <AiAssessmentCard ai={enrich.ai} />
        </>
      )}

      {/* ── PQC Upgrade Ladders ── */}
      <SectionHeader
        title="Post-Quantum Cryptography Readiness"
        subtitle="How far has this server advanced toward quantum-safe encryption? Read each ladder bottom-up."
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', alignItems: 'stretch' }}>
        <UpgradeLadder
          title="Key Exchange"
          description="Protects the session encryption key during the TLS handshake. The most critical vector for the Harvest Now, Decrypt Later attack — intercepted traffic can be retroactively decrypted if this is classical."
          tiers={keTiers.map((t, i) => i === keCurrentIdx ? {
            ...t,
            detectedLabel: group.mechanism !== '—' ? group.mechanism : t.label,
          } : t)}
          currentIdx={keCurrentIdx}
        />
        <UpgradeLadder
          title="TLS Protocol Version"
          description="The version of the TLS protocol negotiated. TLS 1.3 is required for post-quantum key exchange — it introduced the key_share extension that carries ML-KEM encapsulation keys."
          tiers={tlsTiers.map((t, i) => i === tlsCurrentIdx ? { ...t, detectedLabel: data.tls_1_3 ? 'TLS 1.3' : data.tls_1_2 ? 'TLS 1.2' : 'TLS 1.1 or below' } : t)}
          currentIdx={tlsCurrentIdx}
        />
        <UpgradeLadder
          title="Certificate Signature"
          description="Authenticates the server's identity. A quantum computer running Shor's algorithm could forge classical signatures. ML-DSA (FIPS 204) is the target — not yet issued by public CAs at scale."
          tiers={sigTiers.map((t, i) => i === sigCurrentIdx ? { ...t, detectedLabel: data.sig_alg || t.label } : t)}
          currentIdx={sigCurrentIdx}
        />
      </div>

      {/* ── Change indicators — only when a change was actually detected ── */}
      {(() => {
        const curTls = data.tls_1_3 ? 'TLS 1.3' : data.tls_1_2 ? 'TLS 1.2' : 'TLS 1.1-'
        const curKx  = data.pqc_group_name || data.key_exchange || ''
        const curSig = data.sig_alg || ''
        const kxChanged  = data.prev_kx_group  && data.prev_kx_group  !== curKx
        const tlsChanged = data.prev_tls_version && data.prev_tls_version !== curTls
        const sigChanged = data.prev_cert_sig  && data.prev_cert_sig  !== curSig
        if (!kxChanged && !tlsChanged && !sigChanged) return null
        return (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginTop: 8 }}>
            <div style={{ minHeight: 24 }}>
              {kxChanged && <ChangeChip prev={data.prev_kx_group!} cur={curKx} changedAt={data.changed_at} rankMap={KX_RANK} />}
            </div>
            <div style={{ minHeight: 24 }}>
              {tlsChanged && <ChangeChip prev={data.prev_tls_version!} cur={curTls} changedAt={data.changed_at} rankMap={TLS_RANK} />}
            </div>
            <div style={{ minHeight: 24 }}>
              {sigChanged && <ChangeChip prev={data.prev_cert_sig!} cur={curSig} changedAt={data.changed_at} />}
            </div>
          </div>
        )
      })()}


      {/* ── TLS & Connection Details ── */}
      <SectionHeader
        title="TLS Connection Details"
        subtitle="Raw cryptographic parameters negotiated during the TLS handshake"
      />
      <div className="detail-sections">
        <div className="detail-card">
          <h3 className="card-title">Session Encryption</h3>
          <Row label="Cipher Suite"    value={data.cipher_suite} mono />
          <Row label="Key Exchange"    value={
            group.mechanism !== '—'
              ? <><span style={{ fontFamily:'monospace' }}>{group.mechanism}</span>{' '}<TypeTag isPQC={group.isPQC} label={group.isPQC ? 'PQC Hybrid' : 'Classical'} /></>
              : <span style={{ color:'var(--text-dim)' }}>—</span>
          } />
          <Row label="Encryption"      value={cipher.encryption} />
          <Row label="MAC / Hash"      value={cipher.hash} />
          <Row label="Forward Secrecy" value={
            <span style={{ color: data.forward_secrecy ? 'var(--success)' : 'var(--error)' }}>
              {data.forward_secrecy ? '✓ Yes' : '✗ No'}
            </span>
          } />
          <Row label="AEAD"            value={
            <span style={{ color: data.aead_support ? 'var(--success)' : 'var(--text-dim)' }}>
              {data.aead_support ? '✓ Yes' : 'No'}
            </span>
          } />
          <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.75rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', fontSize: '0.72rem', color: 'var(--text-dim)', lineHeight: 1.5 }}>
            Forward secrecy ensures past sessions cannot be decrypted even if the server's private key is later compromised — a key protection against the Harvest Now, Decrypt Later attack.
          </div>
        </div>
        <div className="detail-card">
          <h3 className="card-title">TLS Certificate</h3>
          {(!data.tls_1_3 && !data.tls_1_2 && (data.issuer_name || data.subject)) && (
            <div style={{
              fontSize: '0.68rem', color: '#ffcc00', background: 'rgba(255,180,0,0.07)',
              border: '1px solid rgba(255,180,0,0.25)', borderRadius: 5,
              padding: '5px 9px', marginBottom: '0.6rem', lineHeight: 1.5,
            }}>
              ⚠ Source: Certificate Transparency logs (crt.sh) — not from a live TLS handshake
            </div>
          )}
          <Row label="Subject"   value={data.subject || '—'} />
          <Row label="Issuer"    value={data.issuer_name || '—'} />
          <Row label="Public Key" value={data.pubkey_alg && data.pubkey_size ? `${data.pubkey_alg} (${data.pubkey_size}-bit)` : '—'} />
          <Row label="Signature" value={
            data.sig_alg
              ? <><span>{data.sig_alg}</span> <TypeTag isPQC={sigIsPQC} label={sigIsPQC ? 'PQC' : 'Classical'} /></>
              : '—'
          } />
          <Row label="Expires"   value={data.not_after ? new Date(data.not_after).toLocaleDateString() : '—'} />
          <Row label="Days Left" value={
            data.not_after
              ? <span style={{ color: days < 30 ? 'var(--error)' : days < 90 ? 'var(--warning)' : 'var(--success)' }}>
                  {days > 0 ? `${days} days` : 'Expired'}
                </span>
              : '—'
          } />
          <Row label="Status" value={
            <span style={{ color: data.is_expired ? 'var(--error)' : 'var(--success)' }}>
              {data.is_expired ? '✗ Expired' : '✓ Valid'}
            </span>
          } />
        </div>
      </div>

      {/* ── Infrastructure & DNS ── */}
      {enrich && <>
        <SectionHeader
          title="Infrastructure & DNS"
          subtitle="Hosting provider, DNS configuration, and domain registration details"
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', alignItems: 'start' }}>

          <div className="detail-card">
            <h3 className="card-title">Network &amp; Hosting</h3>
            <Row label="IP Address" value={
              enrich.ip
                ? enrich.ip
                : enrich.cname
                  ? <span style={{ fontSize: '0.75rem' }}><span style={{ color: 'var(--text-dim)', fontFamily: 'monospace' }}>CNAME → </span>{enrich.cname}</span>
                  : '—'
            } mono />
            <Row label="Provider"   value={(() => {
              const org = enrich.geo?.org
              if (!org) return '—'
              // Strip trailing geo suffixes baked into ASN names (e.g. "Citibank N.A. - ISP Peering, Hong Kong, China")
              // Remove everything after " - " that looks like an ISP type + location, or trailing ", City, Country"
              return org
                .replace(/\s*-\s*(ISP Peering|Hosting|Colocation|Transit|Peering|Broadband|Wireless|Cable|DSL|Fiber)[^$]*/i, '')
                .replace(/,\s*[^,]+,\s*[A-Z][a-z].*$/, '') // strip trailing ", City, Country"
                .trim() || org
            })()} />
            <Row label="ASN"        value={enrich.geo?.asn || '—'} mono />
            <Row label="Location"   value={(() => {
              const geo = enrich.geo
              if (!geo) return '—'
              const flag    = countryFlag(geo.country_code)
              const country = geo.country_name ?? geo.country_code  // fallback to ISO code
              const parts   = [geo.city, country].filter(Boolean)
              return parts.length ? <span>{flag && <>{flag} </>}{parts.join(', ')}</span> : '—'
            })()} />
            {enrich.radar?.rank_bucket && (
              <Row label="Radar Rank" value={
                <span>
                  {enrich.radar.rank_exact
                    ? `#${enrich.radar.rank_exact.toLocaleString()} globally`
                    : `${enrich.radar.rank_bucket} globally`}
                  <a href={`https://radar.cloudflare.com/domains/domain/${data.hostname}`}
                     target="_blank" rel="noopener noreferrer"
                     style={{ marginLeft: 6, fontSize: '0.7rem', color: 'var(--accent-cyan)', opacity: 0.7 }}>↗</a>
                </span>
              } />
            )}
          </div>

          <div className="detail-card">
            <h3 className="card-title">DNS Security</h3>
            <div style={{ marginBottom: '0.5rem', fontSize: '0.72rem', color: 'var(--text-dim)', lineHeight: 1.5 }}>
              DNSSEC prevents DNS spoofing. CAA records restrict which Certificate Authorities may issue certs. SPF and DMARC protect against email spoofing.
            </div>
            <Row label="DNSSEC" value={
              <span style={{ color: enrich.dnssec ? 'var(--success)' : 'var(--warning)' }}>
                {enrich.dnssec ? '✓ Signed' : '⚠ Not signed'}
              </span>
            } />
            <Row label="CAA Records" value={
              enrich.caa.length > 0
                ? <span style={{ fontSize: '0.75rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>{enrich.caa.slice(0, 2).join(', ')}</span>
                : <span style={{ color: 'var(--warning)' }}>⚠ None set</span>
            } />
            <Row label="SPF"   value={enrich.spf   ? <span style={{ color: 'var(--success)' }}>✓ Present</span> : <span style={{ color: 'var(--warning)' }}>⚠ None</span>} />
            <Row label="DMARC" value={enrich.dmarc ? <span style={{ color: 'var(--success)' }}>✓ Present</span> : <span style={{ color: 'var(--warning)' }}>⚠ None</span>} />
            {enrich.dmarc && (
              <div style={{ marginTop: '6px', fontSize: '0.7rem', fontFamily: 'monospace', color: 'var(--text-dim)', wordBreak: 'break-all', padding: '4px 6px', background: 'rgba(255,255,255,0.03)', borderRadius: '4px' }}>{enrich.dmarc}</div>
            )}
          </div>

          <div className="detail-card">
            <h3 className="card-title">Domain Registration</h3>
            <Row label="Registrar" value={enrich.registration?.registrar || '—'} />
            {enrich.registration?.country && (
              <Row label="Registrant Country" value={
                <span>{countryFlag(enrich.registration.country)} {enrich.registration.country}</span>
              } />
            )}
            <Row label="Created"   value={enrich.registration?.created ? new Date(enrich.registration.created).toLocaleDateString() : '—'} />
            <Row label="Expires"   value={(() => {
              const exp = enrich.registration?.expires;
              if (!exp) return '—';
              const d = daysLeft(exp);
              return <span style={{ color: d < 30 ? 'var(--error)' : d < 90 ? 'var(--warning)' : 'var(--text-main)' }}>
                {new Date(exp).toLocaleDateString()} <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem' }}>({d > 0 ? `${d}d` : 'expired'})</span>
              </span>;
            })()} />
            <Row label="Updated"   value={enrich.registration?.updated ? new Date(enrich.registration.updated).toLocaleDateString() : '—'} />
          </div>

        </div>

        {enrich.radar && (
          <div className="detail-card" style={{ marginTop: '20px' }}>
            <h3 className="card-title">Threat Intelligence</h3>
            <Row label="CF Radar Verdict" value={
              <span style={{
                color: enrich.radar.scan_malicious === null
                  ? 'var(--text-dim)'
                  : enrich.radar.scan_malicious ? 'var(--error)' : 'var(--success)'
              }}>
                {enrich.radar.scan_malicious === null
                  ? enrich.radar.scan_queued
                    ? '⟳ Scan submitted — check back in ~2 min'
                    : 'No scan on record'
                  : enrich.radar.scan_malicious ? '⚠ Malicious' : '✓ Clean'}
              </span>
            } />
            {enrich.radar.scan_malicious !== null && (
              <Row label="Phishing" value={
                <span style={{ color: enrich.radar.scan_phishing ? 'var(--error)' : 'var(--success)' }}>
                  {enrich.radar.scan_phishing ? '⚠ Detected' : '✓ None detected'}
                </span>
              } />
            )}
            {enrich.radar.scan_violations !== null && (
              <Row label="Security Violations" value={
                <span style={{ color: enrich.radar.scan_violations > 0 ? 'var(--warning)' : 'var(--success)' }}>
                  {enrich.radar.scan_violations > 0
                    ? `⚠ ${enrich.radar.scan_violations} found (CORS / blocked resources)`
                    : '✓ None'}
                </span>
              } />
            )}
            {enrich.radar.scan_date && (
              <Row label="Last Scanned" value={new Date(enrich.radar.scan_date).toLocaleDateString()} mono />
            )}
            {enrich.radar.categories.length > 0 && (
              <Row label="Categories" value={
                <span style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                  {enrich.radar.categories.map((cat: string) => (
                    <span key={cat} style={{ fontSize:'0.68rem', padding:'1px 7px', borderRadius:20, border:'1px solid rgba(0,209,255,0.25)', color:'var(--accent-cyan)', background:'rgba(0,209,255,0.06)' }}>{cat}</span>
                  ))}
                </span>
              } />
            )}
            {enrich.radar.categories.length === 0 && (
              <Row label="Categories" value={<span style={{ color:'var(--text-dim)', fontSize:'0.78rem' }}>Not indexed by Cloudflare Radar</span>} />
            )}

            {/* ── AlienVault OTX ── */}
            {enrich.radar.otx_pulse_count !== null && (
              <>
                <Row label="OTX Pulses" value={
                  <span style={{ color: enrich.radar.otx_malicious ? 'var(--error)' : 'var(--success)' }}>
                    {enrich.radar.otx_malicious
                      ? `⚠ ${enrich.radar.otx_pulse_count} threat pulse${enrich.radar.otx_pulse_count !== 1 ? 's' : ''}`
                      : '✓ No threat pulses'}
                  </span>
                } />
                {enrich.radar.otx_tags.length > 0 && (
                  <Row label="OTX Tags" value={
                    <span style={{ fontSize: '0.8rem' }}>{enrich.radar.otx_tags.join(', ')}</span>
                  } />
                )}
                {enrich.radar.otx_last_seen && (
                  <Row label="OTX Last Seen" value={new Date(enrich.radar.otx_last_seen).toLocaleDateString()} mono />
                )}
              </>
            )}

            <div className="detail-row" style={{ borderBottom: 'none', paddingTop: 8, display: 'flex', gap: '16px' }}>
              <a href={`https://radar.cloudflare.com/domains/domain/${data.hostname}`}
                 target="_blank" rel="noopener noreferrer"
                 style={{ fontSize: '0.75rem', color: 'var(--accent-cyan)' }}>
                View on Cloudflare Radar ↗
              </a>
              <a href={`https://otx.alienvault.com/indicator/domain/${data.hostname}`}
                 target="_blank" rel="noopener noreferrer"
                 style={{ fontSize: '0.75rem', color: 'var(--accent-cyan)' }}>
                View on AlienVault OTX ↗
              </a>
            </div>
          </div>
        )}
      </>}

      {/* ── Security Headers ── */}
      {data.hsts_max_age !== undefined && <>
        <SectionHeader
          title="Security Headers"
          subtitle="HTTP response headers that instruct browsers on security behavior"
        />
        <SecurityHeadersCard data={data} enrich={enrich} />
      </>}

    </div>
  );
}

function SiteReport() {
  const { domain: rawDomain } = useParams();
  const navigate = useNavigate();
  const domain = parseDomain(rawDomain ?? '')
  if (!domain) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'60vh', gap:'1rem' }}>
        <div style={{ fontSize:'2rem' }}>⚠</div>
        <h2 style={{ margin:0, fontSize:'1rem', letterSpacing:'2px', textTransform:'uppercase', color:'var(--error)' }}>Invalid Domain</h2>
        <div style={{ fontSize:'0.78rem', color:'var(--text-dim)', fontFamily:'monospace' }}>
          "{rawDomain}" is not a valid hostname or URL.
        </div>
        <button className="recheck-btn" onClick={() => navigate('/')}>← Back to Globe</button>
      </div>
    )
  }
  return <SiteReportBody hostname={domain} onBack={() => navigate(-1 as any)} />;
}

// ─── Blog ─────────────────────────────────────────────────────────────────────

const POSTS = [
  {
    slug: 'harvest-now-decrypt-later',
    date: 'March 2025',
    tag: 'Threat Intelligence',
    tagColor: 'var(--error)',
    title: 'Harvest Now, Decrypt Later: The Silent Attack Already Underway',
    summary: 'Nation-state actors are collecting encrypted traffic today with one goal — decrypt it the moment a capable quantum computer exists. You may already be a victim.',
    body: [
      {
        heading: "The attack doesn't need a quantum computer yet",
        text: `The most dangerous aspect of the quantum threat isn't some future catastrophic event — it's an attack that's already happening silently, right now. Intelligence agencies and well-resourced adversaries are intercepting and storing encrypted internet traffic at massive scale. They don't need to read it today. They just need to keep it until quantum computing matures enough to break the encryption protecting it.

This strategy is known as "Harvest Now, Decrypt Later" (HNDL), and multiple intelligence disclosures have confirmed it is active. The NSA PRISM program, the GCHQ TEMPORA operation, and various leaks have all revealed bulk internet traffic collection at the fiber backbone level. State actors with long-term patience — China, Russia, Iran, and others — have every incentive to store petabytes of today's encrypted communications.`,
      },
      {
        heading: 'What data is at risk',
        text: `Any communication encrypted with RSA or elliptic-curve cryptography (ECC) is theoretically vulnerable once a cryptographically relevant quantum computer (CRQC) exists. That includes:

• Financial transactions and wire transfers
• Medical records and health data
• Government and military communications
• Corporate intellectual property and trade secrets
• Legal and attorney-client privileged communications
• Authentication tokens and long-lived credentials

If any of this data needs to remain confidential for more than 5–15 years, it is at risk today. A government secret classified for 30 years. A company's R&D pipeline. A long-term contract. All of it.`,
      },
      {
        heading: 'The timeline is closer than most expect',
        text: `In 2024, IBM announced a 1,000+ qubit processor. Google's Willow chip demonstrated exponential error reduction. DARPA launched a dedicated program to build a fault-tolerant quantum computer within the decade. The US government's own CISA assessment puts a "cryptographically relevant quantum computer" potentially within 10–15 years — and the intelligence community estimates could be shorter.

The migration to post-quantum cryptography takes years. NIST spent 8 years standardizing ML-KEM and ML-DSA. Large enterprises typically require 3–5 years to complete a cryptographic migration. The window to act is now — not when a quantum computer arrives.`,
      },
      {
        heading: 'What protection looks like',
        text: `The solution is to upgrade to post-quantum cryptography before the threat materializes. ML-KEM (CRYSTALS-Kyber, NIST FIPS 203) is now the standard for key exchange. ML-DSA (CRYSTALS-Dilithium, NIST FIPS 204) is the standard for digital signatures. Both are already deployed in Chrome, Firefox, Cloudflare, and major cloud providers.

The PQC Quantum Guard tool you're using right now shows you which websites have already made the transition. A site showing "X25519MLKEM768" is using a hybrid classical+PQC key exchange — protecting today's traffic against future quantum decryption. Sites still using classical-only TLS are contributing to the harvest-now problem.`,
      },
    ],
  },
  {
    slug: 'how-quantum-breaks-encryption',
    date: 'February 2025',
    tag: 'Cryptography',
    tagColor: 'var(--accent-blue)',
    title: "Shor's Algorithm: How a Quantum Computer Will Break the Internet's Security",
    summary: "RSA and elliptic-curve cryptography protect nearly everything online. A sufficiently large quantum computer running Shor's algorithm will render both obsolete in hours.",
    body: [
      {
        heading: 'The math that secures the internet today',
        text: `Modern public-key cryptography is built on mathematical problems that are easy to compute in one direction but practically impossible to reverse. RSA relies on the difficulty of factoring large numbers — given a 2048-bit number that is the product of two large primes, no classical computer can find those primes in any reasonable timeframe. Elliptic-curve cryptography (ECC) relies on the elliptic-curve discrete logarithm problem — given a point on an elliptic curve, you cannot efficiently determine the scalar that generated it.

These assumptions have held for decades. RSA was published in 1977 and has never been practically broken. ECC, introduced in the 1980s, provides equivalent security with smaller keys. Together they protect TLS connections, SSH sessions, VPNs, code signing, email encryption, and nearly every other security protocol in use.`,
      },
      {
        heading: "Shor's algorithm changes everything",
        text: `In 1994, mathematician Peter Shor published an algorithm that can factor integers and solve discrete logarithm problems in polynomial time — on a quantum computer. For RSA-2048, a classical computer would require approximately 300 trillion years to factor the key. A quantum computer running Shor's algorithm on a sufficient number of stable, error-corrected qubits could do it in hours or days.

The same algorithm applies to elliptic-curve discrete logarithm problems, breaking ECC equally efficiently. Every TLS connection using RSA key exchange or ECDH, every SSH key, every code-signing certificate — all broken. Shor's algorithm doesn't weaken RSA or ECC; it completely eliminates their security properties.`,
      },
      {
        heading: 'What about symmetric encryption?',
        text: `AES and ChaCha20 — the symmetric algorithms that encrypt the actual data in TLS — are much more resistant to quantum attacks. Grover's algorithm provides a quadratic speedup for searching, which effectively halves the key length security (AES-256 would have ~128-bit post-quantum security). This is manageable by using AES-256 instead of AES-128.

The critical problem is the key exchange. RSA and ECDH are used to establish shared secrets and exchange keys. Break those, and attackers gain the keys needed to decrypt the AES-encrypted data. The symmetric encryption itself is the easy part — the key exchange is where quantum computing poses an existential threat.`,
      },
      {
        heading: 'The new cryptographic primitives',
        text: `NIST's post-quantum cryptography standardization, completed in 2024, produced three primary standards:

**FIPS 203 — ML-KEM (Module-Lattice Key Encapsulation Mechanism):** Replaces RSA and ECDH for key exchange. Based on the Module Learning With Errors (MLWE) problem — believed to be hard for both classical and quantum computers. Already deployed in TLS 1.3 as X25519MLKEM768 (a hybrid with classical X25519).

**FIPS 204 — ML-DSA (Module-Lattice Digital Signature Algorithm):** Replaces RSA-PSS and ECDSA for digital signatures. Also lattice-based.

**FIPS 205 — SLH-DSA (Stateless Hash-Based Digital Signature Algorithm):** An alternative signature scheme based on hash functions rather than lattices, providing defense-in-depth if lattice problems are later broken.

The transition to these algorithms is already underway. This registry tracks which sites have deployed ML-KEM for TLS key exchange.`,
      },
    ],
  },
  {
    slug: 'tls-quantum-readiness-guide',
    date: 'January 2025',
    tag: 'How-To',
    tagColor: 'var(--success)',
    title: 'Is Your Website Quantum-Ready? A Practical TLS Checklist',
    summary: 'PQC deployment in TLS is not theoretical — Chrome already negotiates ML-KEM with Cloudflare, Fastly, and Google. Here is exactly what to check and what to do.',
    body: [
      {
        heading: 'What "quantum-ready TLS" actually means',
        text: `A TLS connection has two distinct cryptographic operations that need to be upgraded for post-quantum security:

1. **Key exchange** — how client and server establish a shared secret. Classical ECDH (X25519, P-256) is vulnerable to Shor's algorithm. The replacement is ML-KEM, standardized as FIPS 203.

2. **Certificate signature** — how the server proves its identity. Classical RSA and ECDSA signatures are vulnerable. The replacement is ML-DSA (FIPS 204) or SLH-DSA (FIPS 205). Certificate authorities have not yet widely issued PQC certificates.

Of these two, key exchange is more urgent for the harvest-now-decrypt-later threat. An attacker storing your traffic today can decrypt it later if they capture the classical key exchange. The hybrid X25519MLKEM768 scheme addresses this — it combines classical X25519 with ML-KEM-768 so the connection is secure against both classical and quantum adversaries.`,
      },
      {
        heading: 'The deployment landscape right now',
        text: `**Already deployed (key exchange):**
• Cloudflare — all properties globally support X25519MLKEM768 since mid-2024
• Google — all Google services support X25519MLKEM768
• Fastly — PQC hybrid key exchange deployed across CDN
• Chrome — negotiates X25519MLKEM768 by default since Chrome 131
• Firefox — enabled by default since Firefox 132
• AWS — partial rollout on CloudFront

**Not yet deployed:**
• Certificate authorities have not issued ML-DSA or SLH-DSA certificates at scale
• Most on-premises and self-hosted servers (Apache, Nginx, IIS) require manual configuration
• Load balancers from F5, Citrix, and others need firmware updates

If your site is behind Cloudflare with default settings, you almost certainly already have PQC key exchange — check it here to confirm.`,
      },
      {
        heading: 'How to enable ML-KEM on your server',
        text: `**OpenSSL 3.5+** (released April 2025) includes ML-KEM support natively. Upgrade your OpenSSL version and it is enabled by default for TLS 1.3.

**Nginx:** Once built against OpenSSL 3.5+, no additional configuration is needed — ML-KEM groups are included in the default TLS 1.3 group list.

**Apache httpd:** Same — update OpenSSL and rebuild or replace the shared library.

**HAProxy:** Version 3.1+ with OpenSSL 3.5+ supports ML-KEM.

**The simplest path:** Route your traffic through Cloudflare (free tier works) and you get PQC key exchange immediately, for any origin server, with zero configuration.

**Test your result:** Use this registry to check any domain. A grade of A or higher with "X25519MLKEM768" confirms quantum-safe key exchange.`,
      },
      {
        heading: 'Grading criteria used by this registry',
        text: `This tool grades each domain on four dimensions:

• **Protocol (25%)** — TLS 1.3 scores full marks. TLS 1.2 is penalized. TLS 1.1 and below fails.

• **Key Exchange (40%)** — X25519MLKEM768, X25519MLKEM1024, or SecP256r1MLKEM768 score full marks. Classical-only ECDH scores partial marks. No forward secrecy fails.

• **Certificate (20%)** — RSA key size, ECDSA key size, signature algorithm, and expiry all factor in. PQC signatures (not yet common) would score maximum.

• **Security Headers (15%)** — HSTS with long max-age, CSP presence, X-Frame-Options, X-Content-Type-Options, and Referrer-Policy.

A site can achieve an A+ only with TLS 1.3, PQC hybrid key exchange, a modern certificate, and strong security headers. Grade F is assigned when a site is unreachable, uses broken protocols, or provides no usable TLS data.`,
      },
    ],
  },
  {
    slug: 'post-quantum-standards-explained',
    date: 'December 2024',
    tag: 'Standards',
    tagColor: 'var(--accent-purple)',
    title: "NIST's Post-Quantum Standards: ML-KEM, ML-DSA, and What They Replace",
    summary: 'After eight years and four rounds of evaluation, NIST finalized three post-quantum cryptography standards in 2024. Here is what they are, what they replace, and why the choices were made.',
    body: [
      {
        heading: 'Eight years of public cryptanalysis',
        text: `In 2016, NIST launched a public competition to standardize post-quantum cryptographic algorithms. 82 initial submissions were received from research teams worldwide. Over four evaluation rounds spanning eight years, submissions were subjected to intense cryptanalysis by the global cryptographic community. Several prominent candidates were broken or significantly weakened during the process — including SIKE, which was completely broken in a 2022 paper using a single laptop in under an hour.

The survivors of this process were standardized in August 2024:
• FIPS 203: ML-KEM (key encapsulation)
• FIPS 204: ML-DSA (digital signatures)
• FIPS 205: SLH-DSA (hash-based signatures)

A fourth standard, FN-DSA (FALCON), is expected as FIPS 206.`,
      },
      {
        heading: 'ML-KEM: replacing RSA and ECDH',
        text: `ML-KEM (Module-Lattice Key Encapsulation Mechanism, formerly CRYSTALS-Kyber) is the primary replacement for RSA key exchange and ECDH in TLS. It is a Key Encapsulation Mechanism (KEM) — the sender encapsulates a random key using the recipient's public key, and only the recipient can decapsulate it using their private key.

Security is based on the Module Learning With Errors (MLWE) problem: given a matrix A and vector b = As + e (where s is a small secret and e is a small error), find s. No known quantum or classical algorithm solves this efficiently. Three security levels are defined:
• ML-KEM-512: ~128-bit classical, ~128-bit quantum security
• ML-KEM-768: ~192-bit classical (used in X25519MLKEM768 hybrid)
• ML-KEM-1024: ~256-bit classical

The hybrid X25519MLKEM768 used in TLS combines ML-KEM-768 with classical X25519. If either component is broken, the other still provides security. This transitional approach maintains compatibility and provides defense-in-depth.`,
      },
      {
        heading: 'ML-DSA: replacing RSA-PSS and ECDSA',
        text: `ML-DSA (Module-Lattice Digital Signature Algorithm, formerly CRYSTALS-Dilithium) replaces RSA-PSS and ECDSA for digital signatures. It is also lattice-based, derived from the same algebraic structure as ML-KEM.

Signature sizes are larger than classical schemes — an ML-DSA-65 signature is 3,293 bytes compared to 71 bytes for ECDSA P-256. Public keys are also larger: 1,952 bytes vs. 64 bytes for ECDSA. This is an acceptable trade-off for long-term security, though it has implications for certificate sizes and TLS handshake overhead.

Certificate authorities will need to issue ML-DSA certificates before websites can use PQC signatures. Google, Let's Encrypt, and DigiCert have announced plans but widespread deployment is expected to lag key exchange upgrades by 2–3 years.`,
      },
      {
        heading: 'SLH-DSA: the conservative backup',
        text: `SLH-DSA (Stateless Hash-Based Digital Signature Algorithm, formerly SPHINCS+) takes a fundamentally different approach — it relies only on the security of hash functions, not on structured algebraic problems. Hash function security is well-understood and has withstood decades of cryptanalysis with no quantum speedup beyond Grover's algorithm.

The trade-off is performance and size: SLH-DSA signatures range from 7,856 to 49,856 bytes depending on the parameter set, making them impractical for TLS where certificate size matters. They are better suited for code signing, firmware updates, and long-term archive use where signature size is not a concern.

The existence of SLH-DSA provides an important hedge: if lattice-based assumptions are ever found to be weaker than expected, an entirely different mathematical foundation is already standardized and ready.`,
      },
    ],
  },
  {
    slug: 'cryptographic-inventory',
    date: 'March 2025',
    tag: 'Enterprise Security',
    tagColor: '#fb923c',
    title: "You Can't Migrate What You Haven't Mapped: The Case for Cryptographic Inventory",
    summary: "Before your organization can become quantum-safe, you need to know exactly where cryptography lives in your environment. Most enterprises have no idea — and that's the real problem.",
    body: [
      {
        heading: "The migration problem nobody talks about",
        text: `Every CISO has now heard that post-quantum cryptography is coming. NIST published FIPS 203, 204, 205, and 206 in August 2024. NSA's CNSA 2.0 mandates PQC for national security systems by 2025. The White House issued a memo (M-23-02) directing federal agencies to inventory and migrate cryptographic dependencies.

But here's the problem nobody talks about: you cannot migrate what you haven't mapped. And most enterprises — including large ones — have no coherent picture of where cryptography lives in their environment. They know they have TLS. They know they have VPNs. They don't know that their internal LDAP authentication, their CI/CD pipeline secrets management, their database-to-database mTLS, their IoT sensor telemetry, their backup encryption, their HSM key derivation chains, and their legacy mainframe batch jobs all use RSA-2048 in ways that will require individual migration efforts.`,
      },
      {
        heading: "What a cryptographic inventory actually captures",
        text: `A complete cryptographic inventory isn't just a list of SSL/TLS certificates. It captures every location where cryptography is used — and the specific algorithms, key sizes, and protocols in play. This includes:

• TLS endpoint certificates (hostname, expiry, key type, cipher suites)
• Internal service-to-service mTLS (microservices, service mesh, API gateways)
• VPN and IPSec tunnels (IKEv2 algorithm configurations)
• SSH host keys and authorized_keys entries (developer access, CI/CD)
• Code signing certificates (Authenticode, GPG package signing keys)
• Encrypted storage (database encryption, backup encryption, disk encryption)
• Hardware Security Modules (HSM key types and wrapped key algorithms)
• Authentication infrastructure (SAML assertions, JWT signing keys, OIDC)
• Email security (S/MIME certificates, PGP keys, DKIM signing keys)
• IoT and OT device certificates (firmware signing, telemetry TLS)

Each of these is a migration unit. Each has a different owner, a different migration path, and a different risk profile.`,
      },
      {
        heading: "Why most organizations are unprepared",
        text: `A 2024 Deloitte survey found that only 9% of enterprises had completed a formal cryptographic inventory. IBM's 2024 Cost of a Data Breach report noted that organizations with undocumented cryptographic assets took an average of 287 days longer to detect breaches involving cryptographic vulnerabilities.

The reasons for this gap are structural:

Certificate management tools track public-facing TLS certificates but miss internal PKI, mTLS, SSH keys, and application-layer cryptography. Most organizations don't even know how many applications they have, let alone what cryptographic libraries they're using and at what versions. Cryptography is typically invisible — it's a library call inside an application, not a service that shows up in an asset inventory. DevOps practices have accelerated deployment of new services without corresponding cryptographic governance. Legacy systems (mainframes, SCADA, embedded systems) may use undocumented or proprietary cryptographic implementations.`,
      },
      {
        heading: "The harvest-now-decrypt-later dimension",
        text: `The inventory problem is made more urgent by harvest-now-decrypt-later (HNDL) attacks. An adversary recording your encrypted traffic today doesn't need you to inventory your cryptography — they'll decrypt it all once a CRQC exists. From their perspective, your entire cryptographic estate is already exposed.

This means the window for action is not "before quantum computers arrive." The window is now. Every month of delay is another month of traffic that will be retroactively decryptable. Communications that need to remain confidential for 10+ years — government, legal, medical, financial — are already at risk.

The practical implication: your inventory should be prioritized by data sensitivity and retention period. A TLS session for a marketing website matters less than the mTLS tunnel carrying your EHR data or your financial clearing traffic.`,
      },
      {
        heading: "Building your inventory: where to start",
        text: `A pragmatic approach to cryptographic inventory works in layers:

Layer 1 — External attack surface. Scan all public-facing TLS endpoints. This is achievable quickly using tools like SSL Labs, Qualys, or the PQC Quantum Guard extension. Understand which cipher suites and key exchange algorithms are in use.

Layer 2 — Internal network. Deploy passive TLS/SSH inspection on internal network segments. Tools like Zeek (Bro), Wireshark, or commercial network detection tools can identify cryptographic protocol usage without breaking encryption.

Layer 3 — Application dependency scanning. Use software composition analysis (SCA) tools to identify cryptographic library dependencies (OpenSSL, BouncyCastle, Java Cryptography Extension, NSS) and their versions. NIST's CBOM (Cryptographic Bill of Materials) standard defines a machine-readable format for this.

Layer 4 — Certificate and key stores. Enumerate all certificate stores, HSMs, and key management systems. CertCentral, Venafi, HashiCorp Vault, AWS KMS, and Azure Key Vault all have APIs to export cryptographic asset inventories.

Layer 5 — Code scanning. Static analysis for cryptographic API calls in source code (CryptoGuard, CodeSonar, or SAST tools with cryptographic rules).

The NIST NCCoE SP 1800-38 series provides a detailed migration playbook, including CBOM tooling recommendations.`,
      },
      {
        heading: "The cost of inaction",
        text: `Organizations that delay cryptographic inventory face compounding costs. The longer the delay, the larger the migration effort — new applications are deployed daily, each adding new cryptographic dependencies. The regulatory cost of non-compliance increases as deadlines approach (NSA CNSA 2.0, CISA federal mandates, PCI DSS, HIPAA). And the reputational cost of a post-quantum breach — once quantum computers exist — will fall hardest on organizations that had years of warning and chose not to act.

The organizations that will navigate the quantum transition successfully are those that start their inventory today. Not when the standards are "finalized enough." Not when their vendor ships a patch. Today. The PQC standards are final. The threat is real. The inventory is the first step.`,
      },
    ],
  },
  {
    slug: 'nist-fips-203-204-205-206-deep-dive',
    date: 'April 2025',
    tag: 'Standards',
    tagColor: '#34d399',
    title: 'NIST FIPS 203–206: What the Final PQC Standards Actually Mean for Your Stack',
    summary: 'NIST published four final post-quantum cryptographic standards in August 2024. Here is what each standard does, where it deploys, and what you need to change in your infrastructure.',
    body: [
      {
        heading: 'Why August 2024 matters',
        text: `On August 13, 2024, NIST published four final Federal Information Processing Standards for post-quantum cryptography: FIPS 203 (ML-KEM), FIPS 204 (ML-DSA), FIPS 205 (SLH-DSA), and FIPS 206 (FN-DSA). This was not a draft, a preview, or a research proposal. These are finalized federal standards that US government agencies are now required to adopt, and that the global technology industry is implementing.

The publication followed an eight-year competition involving 82 initial submissions from cryptographers across 25 countries. The algorithms were subjected to public cryptanalysis, side-channel attack research, and performance profiling across a decade of scrutiny. They are as thoroughly vetted as any cryptographic standard in history.`,
      },
      {
        heading: 'FIPS 203: ML-KEM — replacing key exchange',
        text: `ML-KEM (Module Lattice Key Encapsulation Mechanism) is the replacement for ECDH and RSA key exchange in TLS, SSH, and any protocol that establishes a shared secret. It is based on the Module Learning With Errors (MLWE) problem — a lattice problem for which no efficient quantum algorithm is known.

Three parameter sets: ML-KEM-512 (128-bit security, ~800 byte public key), ML-KEM-768 (192-bit security, ~1100 bytes), ML-KEM-1024 (256-bit security, ~1568 bytes). ML-KEM-768 is the recommended baseline for TLS.

Deployment today: Chrome 131 uses X25519MLKEM768 hybrid (X25519 + ML-KEM-768) for TLS 1.3 key exchange. Cloudflare serves X25519MLKEM768 to compatible clients. AWS KMS supports ML-KEM. Signal has implemented ML-KEM in its PQXDH protocol. The hybrid approach — running classical ECDH alongside ML-KEM — provides protection against both classical and quantum attacks simultaneously.`,
      },
      {
        heading: 'FIPS 204: ML-DSA — replacing digital signatures',
        text: `ML-DSA (Module Lattice Digital Signature Algorithm, formerly CRYSTALS-Dilithium) replaces ECDSA and RSA-PSS for digital signatures. It signs data — certificates, code, documents, tokens — using lattice mathematics that resists quantum attack.

Three parameter sets: ML-DSA-44 (128-bit), ML-DSA-65 (192-bit), ML-DSA-87 (256-bit). Signatures are larger than ECDSA (2420–4595 bytes vs. ~64 bytes for ECDSA P-256) but verification is fast.

Deployment: OpenSSL 3.5 includes ML-DSA. X.509 certificate profiles for ML-DSA are defined in IETF draft-ietf-lamps-dilithium-certificates. CA/Browser Forum has an active working group on PQC certificate issuance. Expect ML-DSA TLS certificates from major CAs in 2025–2026.`,
      },
      {
        heading: 'FIPS 205 and 206: Conservative and compact alternatives',
        text: `FIPS 205 (SLH-DSA, formerly SPHINCS+) provides hash-based signatures whose security relies solely on SHA-256 or SHAKE-256 — no lattice assumptions required. This is a critical hedge: if lattice problems turn out to have unexpected weaknesses, SLH-DSA remains secure. The cost is signature size (7–50KB) and slower signing. Best suited for code signing, firmware updates, and long-term document archiving.

FIPS 206 (FN-DSA, formerly FALCON) provides compact lattice-based signatures over NTRU lattices. Signatures are ~5–10× smaller than ML-DSA, making it attractive for bandwidth-constrained environments. However, FALCON's implementation requires careful Gaussian sampling that is susceptible to timing side-channels — it requires constant-time implementation on all target platforms.

For most TLS deployments, ML-DSA is the preferred migration target. FN-DSA is for specialized environments. SLH-DSA is for high-assurance archive and code signing.`,
      },
      {
        heading: 'What you need to change',
        text: `The migration from classical cryptography to FIPS 203–206 is not a single update — it is a multi-layer, multi-year effort. The practical checklist:

1. TLS key exchange: Enable X25519MLKEM768 in your TLS 1.3 stack. Available in OpenSSL 3.2+, BoringSSL, Rustls, Go (experimental). Configure your load balancer or CDN to prefer this.

2. TLS certificates: Monitor CA announcements for hybrid ML-DSA + ECDSA certificate issuance. Update trust store handling to accept hybrid certificates when available.

3. SSH: Upgrade to OpenSSH 9.0+ and enable sntrup761x25519-sha512 KEX. This is a single line in sshd_config and provides immediate hybrid PQC protection.

4. Code signing: Evaluate dual-signing (RSA/ECDSA + ML-DSA) for software packages. sigstore/cosign has ML-DSA support in development.

5. VPN/IPSec: Implement RFC 9370 multiple key exchange in IKEv2. StrongSwan 6.0+ supports ML-KEM hybrid.

6. Certificate management: Begin CBOM (Cryptographic Bill of Materials) generation for all services. You cannot migrate without an inventory.`,
      },
    ],
  },
  {
    slug: 'zero-trust-pqc',
    date: 'May 2025',
    tag: 'Architecture',
    tagColor: '#a78bfa',
    title: 'Zero Trust in the Quantum Era: Why Your Architecture Needs a Cryptographic Rethink',
    summary: "Zero Trust assumes every connection must be authenticated and encrypted. But if the cryptography underpinning that authentication is quantum-broken, Zero Trust becomes Zero Assurance. Here's what needs to change.",
    body: [
      {
        heading: 'The promise and the problem',
        text: `Zero Trust Architecture (ZTA) is now the dominant security framework for enterprise networks. The core principle — never trust, always verify — is sound. Every connection is authenticated. Every request is authorized. Every channel is encrypted. CISA, NIST (SP 800-207), and the DoD have all mandated Zero Trust adoption.

But Zero Trust rests on a cryptographic foundation that is quantum-vulnerable. The mTLS sessions securing service-to-service communication, the OIDC tokens authenticating users, the X.509 certificates identifying services, the SAML assertions flowing between identity providers — all of these use ECDSA, RSA, or ECDH cryptography that will be broken by Shor's algorithm.

A Zero Trust architecture that does not account for quantum cryptography is providing a false sense of security. The perimeter is gone — but the cryptography holding everything together is built on sand.`,
      },
      {
        heading: 'The specific attack surfaces',
        text: `In a Zero Trust architecture, the quantum-vulnerable attack surfaces include:

Identity and authentication: OIDC/OAuth 2.0 JWT tokens are signed with RS256 (RSA-2048) or ES256 (ECDSA P-256). A quantum adversary could forge tokens for any identity. SAML assertions use RSA or ECDSA signatures — same problem. X.509 client certificates for mTLS use RSA or ECDSA.

Service-to-service communication: Every mTLS connection uses ECDH for key exchange. Service mesh (Istio, Linkerd, Consul Connect) issues short-lived mTLS certificates from internal CAs — those CA signing keys are ECDSA or RSA.

API gateways: API key validation, JWT verification, OAuth token introspection — all cryptographically signed with quantum-vulnerable algorithms.

Secrets management: HashiCorp Vault uses RSA or ECDSA for its transit secrets engine and for audit log signing. AWS KMS, Azure Key Vault, and GCP Cloud KMS are migrating to PQC but deployments are still predominantly classical.

Device attestation: Zero Trust endpoint verification (device certificates, TPM attestation) uses RSA or ECDSA. A quantum adversary could forge device certificates, bypassing endpoint posture verification.`,
      },
      {
        heading: 'The migration order for ZTA',
        text: `Migrating a Zero Trust architecture to quantum safety requires a prioritized approach. The order matters:

Priority 1 — Identity Provider (IdP): The highest-value target. If your OIDC/SAML IdP's signing keys are compromised, every authentication decision across your entire environment is forgeable. Migrate your IdP signing keys to ML-DSA first. Monitor Okta, Entra ID, and Ping Identity for PQC JWT/SAML signing support.

Priority 2 — Internal PKI: Your service mesh CA signs every mTLS certificate. Migrate your internal CA to issue hybrid (ECDSA + ML-DSA) certificates. Tools: EJBCA, HashiCorp Vault PKI secrets engine, AWS Private CA — all have PQC roadmaps.

Priority 3 — External TLS: Enable X25519MLKEM768 on all external endpoints. This protects traffic confidentiality against harvest-now-decrypt-later.

Priority 4 — Secrets and key management: Migrate HSM-protected keys to ML-KEM wrapped key formats. Rekey any long-lived symmetric keys derived from RSA/ECDH-based mechanisms.

Priority 5 — Device attestation: This is the longest-lead-time item — it requires hardware (TPM) and firmware updates. Begin planning now.`,
      },
      {
        heading: 'Crypto-agility: the capability you need now',
        text: `The organizations that will navigate the quantum transition most smoothly are those that have built crypto-agility into their architecture — the ability to swap cryptographic algorithms without redesigning systems.

Crypto-agility means: algorithm identifiers in key material (not hardcoded), configurable cipher suite negotiation, certificate lifecycle management that supports multiple key types simultaneously, and abstraction layers in cryptographic code that don't couple business logic to specific algorithm implementations.

If your service mesh, IdP, and secrets manager are not crypto-agile today, the quantum migration will require architectural reconstruction rather than configuration updates. The time to build this capability is now, before the migration pressure arrives.`,
      },
    ],
  },
];

function BlogIndex() {
  const navigate = useNavigate();
  return (
    <div style={{animation:'fadeIn 0.4s'}}>
      <div style={{padding:'3rem 0 2rem', textAlign:'center'}}>
        <h1 style={{fontSize:'2.4rem', margin:'0 0 0.5rem'}}>Quantum Security Insights</h1>
        <p style={{color:'var(--text-dim)', fontSize:'1.05rem'}}>Research, analysis, and practical guidance on post-quantum cryptography.</p>
      </div>
      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(340px, 1fr))', gap:'1.5rem', padding:'0 0 3rem'}}>
        {POSTS.map(post => (
          <div key={post.slug}
            onClick={() => navigate(`/blog/${post.slug}`)}
            style={{
              background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:'12px',
              padding:'1.8rem', cursor:'pointer', transition:'border-color 0.2s, transform 0.15s',
              display:'flex', flexDirection:'column', gap:'0.75rem',
            }}
            onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--accent-blue)'; (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--border)'; (e.currentTarget as HTMLDivElement).style.transform = ''; }}
          >
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
              <span style={{fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color: post.tagColor, border:`1px solid ${post.tagColor}`, padding:'2px 8px', borderRadius:'20px'}}>
                {post.tag}
              </span>
              <span style={{fontSize:'0.78rem', color:'var(--text-dim)'}}>{post.date}</span>
            </div>
            <h2 style={{margin:0, fontSize:'1.15rem', lineHeight:1.4, color:'var(--text-primary)'}}>{post.title}</h2>
            <p style={{margin:0, fontSize:'0.875rem', color:'var(--text-secondary)', lineHeight:1.6}}>{post.summary}</p>
            <span style={{fontSize:'0.82rem', color:'var(--accent-blue)', marginTop:'auto', paddingTop:'0.5rem'}}>Read article →</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = POSTS.find(p => p.slug === slug);

  if (!post) return (
    <div style={{textAlign:'center', padding:'5rem'}}>
      <h2>Article not found</h2>
      <button className="recheck-btn" onClick={() => navigate('/blog')}>Back to Blog</button>
    </div>
  );

  return (
    <div style={{animation:'fadeIn 0.4s', maxWidth:'780px', margin:'0 auto', padding:'2.5rem 0 4rem'}}>
      <button onClick={() => navigate('/blog')}
        style={{background:'none', border:'none', color:'var(--text-dim)', cursor:'pointer', padding:'0 0 1.5rem', fontSize:'0.875rem', display:'flex', alignItems:'center', gap:'6px'}}>
        ← All articles
      </button>
      <div style={{display:'flex', gap:'12px', alignItems:'center', marginBottom:'1rem'}}>
        <span style={{fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.08em', textTransform:'uppercase', color: post.tagColor, border:`1px solid ${post.tagColor}`, padding:'2px 8px', borderRadius:'20px'}}>
          {post.tag}
        </span>
        <span style={{fontSize:'0.8rem', color:'var(--text-dim)'}}>{post.date}</span>
      </div>
      <h1 style={{fontSize:'2rem', lineHeight:1.3, margin:'0 0 1rem'}}>{post.title}</h1>
      <p style={{fontSize:'1.05rem', color:'var(--text-secondary)', lineHeight:1.7, borderLeft:'3px solid var(--accent-blue)', paddingLeft:'1rem', margin:'0 0 2.5rem'}}>
        {post.summary}
      </p>
      {post.body.map((section, i) => (
        <div key={i} style={{marginBottom:'2rem'}}>
          <h2 style={{fontSize:'1.2rem', color:'var(--accent-blue)', margin:'0 0 0.75rem'}}>{section.heading}</h2>
          {section.text.split('\n\n').map((para, j) => (
            <p key={j} style={{margin:'0 0 1rem', lineHeight:1.8, color:'var(--text-secondary)', whiteSpace:'pre-line'}}>
              {para}
            </p>
          ))}
        </div>
      ))}
      <div style={{marginTop:'3rem', padding:'1.5rem', background:'var(--card-bg)', border:'1px solid var(--border)', borderRadius:'12px', textAlign:'center'}}>
        <p style={{margin:'0 0 0.75rem', color:'var(--text-secondary)'}}>Check your site's quantum readiness now</p>
        <button className="recheck-btn" onClick={() => navigate('/registry')}>Open the Registry →</button>
      </div>
    </div>
  );
}

// ─── GlobalView ───────────────────────────────────────────────────────────────

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json'

// ISO numeric → alpha-2 mapping for the most common country codes
const ISO_NUM_TO_A2: Record<string, string> = {
  '004':'AF','008':'AL','012':'DZ','024':'AO','032':'AR','036':'AU','040':'AT',
  '050':'BD','056':'BE','068':'BO','076':'BR','100':'BG','124':'CA','152':'CL',
  '156':'CN','170':'CO','191':'HR','203':'CZ','208':'DK','218':'EC','818':'EG',
  '233':'EE','246':'FI','250':'FR','276':'DE','288':'GH','300':'GR','344':'HK',
  '348':'HU','356':'IN','360':'ID','364':'IR','372':'IE','376':'IL','380':'IT',
  '388':'JM','392':'JP','400':'JO','404':'KE','410':'KR','414':'KW','422':'LB',
  '458':'MY','484':'MX','504':'MA','528':'NL','554':'NZ','566':'NG','578':'NO',
  '586':'PK','604':'PE','616':'PL','620':'PT','630':'PR','634':'QA','642':'RO',
  '643':'RU','682':'SA','703':'SK','705':'SI','710':'ZA','724':'ES','752':'SE',
  '756':'CH','764':'TH','792':'TR','804':'UA','784':'AE','826':'GB','840':'US',
  '704':'VN','887':'YE','716':'ZW','862':'VE','858':'UY',
}

// eTLD-1 suffix → alpha-2.
// Checked from longest suffix first so "co.uk" beats "uk".
// US generic TLDs (.gov, .edu, .mil) are very reliable country signals.
const SUFFIX_TO_A2: [string, string][] = [
  // ── Second-level ccTLDs ──────────────────────────────────────────────────
  ['co.uk','GB'],['org.uk','GB'],['me.uk','GB'],['net.uk','GB'],['ac.uk','GB'],
  ['gov.uk','GB'],['ltd.uk','GB'],['plc.uk','GB'],
  ['com.au','AU'],['net.au','AU'],['org.au','AU'],['edu.au','AU'],['gov.au','AU'],['asn.au','AU'],
  ['com.br','BR'],['net.br','BR'],['org.br','BR'],['gov.br','BR'],['edu.br','BR'],
  ['com.ar','AR'],['net.ar','AR'],['org.ar','AR'],['gob.ar','AR'],
  ['com.mx','MX'],['net.mx','MX'],['org.mx','MX'],['gob.mx','MX'],
  ['co.jp','JP'],['ne.jp','JP'],['or.jp','JP'],['go.jp','JP'],['ac.jp','JP'],['gr.jp','JP'],
  ['co.nz','NZ'],['net.nz','NZ'],['org.nz','NZ'],['govt.nz','NZ'],['ac.nz','NZ'],
  ['co.za','ZA'],['net.za','ZA'],['org.za','ZA'],['gov.za','ZA'],['ac.za','ZA'],
  ['co.in','IN'],['net.in','IN'],['org.in','IN'],['gov.in','IN'],['ac.in','IN'],
  ['co.kr','KR'],['or.kr','KR'],['go.kr','KR'],['ac.kr','KR'],
  ['com.cn','CN'],['net.cn','CN'],['org.cn','CN'],['gov.cn','CN'],['edu.cn','CN'],
  ['com.hk','HK'],['net.hk','HK'],['org.hk','HK'],['gov.hk','HK'],
  ['com.sg','SG'],['net.sg','SG'],['org.sg','SG'],['gov.sg','SG'],['edu.sg','SG'],
  ['com.tw','TW'],['net.tw','TW'],['org.tw','TW'],['gov.tw','TW'],['edu.tw','TW'],
  ['com.tr','TR'],['net.tr','TR'],['org.tr','TR'],['gov.tr','TR'],
  ['com.sa','SA'],['net.sa','SA'],['org.sa','SA'],['gov.sa','SA'],['edu.sa','SA'],
  ['com.ae','AE'],['net.ae','AE'],['org.ae','AE'],['gov.ae','AE'],['ac.ae','AE'],
  ['com.eg','EG'],['net.eg','EG'],['org.eg','EG'],['gov.eg','EG'],
  ['com.my','MY'],['net.my','MY'],['org.my','MY'],['gov.my','MY'],['edu.my','MY'],
  ['com.ph','PH'],['net.ph','PH'],['org.ph','PH'],['gov.ph','PH'],['edu.ph','PH'],
  ['com.pk','PK'],['net.pk','PK'],['org.pk','PK'],['gov.pk','PK'],['edu.pk','PK'],
  ['com.ng','NG'],['org.ng','NG'],['gov.ng','NG'],['edu.ng','NG'],
  ['com.gh','GH'],['org.gh','GH'],['gov.gh','GH'],['edu.gh','GH'],
  ['com.ke','KE'],['co.ke','KE'],['org.ke','KE'],['go.ke','KE'],
  ['com.ma','MA'],['net.ma','MA'],['org.ma','MA'],['gov.ma','MA'],
  ['com.pl','PL'],['net.pl','PL'],['org.pl','PL'],['gov.pl','PL'],
  // ── US generic TLDs — highly reliable country signal ──────────────────────
  ['gov','US'],['mil','US'],['edu','US'],
  // ── Single ccTLDs ─────────────────────────────────────────────────────────
  ['us','US'],['uk','GB'],['de','DE'],['fr','FR'],['jp','JP'],['cn','CN'],['au','AU'],
  ['ca','CA'],['br','BR'],['in','IN'],['ru','RU'],['it','IT'],['es','ES'],['nl','NL'],
  ['se','SE'],['no','NO'],['fi','FI'],['dk','DK'],['pl','PL'],['ch','CH'],['at','AT'],
  ['be','BE'],['cz','CZ'],['hu','HU'],['ro','RO'],['sk','SK'],['si','SI'],['hr','HR'],
  ['ua','UA'],['pt','PT'],['ie','IE'],['nz','NZ'],['za','ZA'],['mx','MX'],['ar','AR'],
  ['cl','CL'],['sg','SG'],['hk','HK'],['kr','KR'],['tw','TW'],['id','ID'],['my','MY'],
  ['th','TH'],['vn','VN'],['ph','PH'],['tr','TR'],['il','IL'],['sa','SA'],['ae','AE'],
  ['eg','EG'],['ng','NG'],['ke','KE'],['gh','GH'],['ma','MA'],['pk','PK'],['bd','BD'],
  ['gr','GR'],['bg','BG'],['lv','LV'],['lt','LT'],['ee','EE'],['is','IS'],['lu','LU'],
  ['cy','CY'],['mt','MT'],['rs','RS'],['pe','PE'],
  // .co intentionally excluded: widely used as a brand TLD (elastic.co, t.co, etc.)
  // and incorrectly maps non-Colombian entities to CO.
]

function hostnameToCountry(hostname: string): string | null {
  const h = hostname.toLowerCase()
  for (const [suffix, a2] of SUFFIX_TO_A2) {
    if (h === suffix || h.endsWith('.' + suffix)) return a2
  }
  return null
}

interface AnalyticsData {
  pqc_status:   { secure: number; legacy: number; insecure: number; total: number }
  grades:       { grade: string; count: number }[]
  tls:          { tls13: number; tls12: number; tls11_below: number; total: number }
  key_exchange: { name: string; count: number }[]
  cipher_suites:{ name: string; count: number }[]
  sig_algs:     { name: string; count: number }[]
  pubkey_algs:  { name: string; count: number }[]
  hsts:         { with_hsts: number; preloaded: number; include_subdomains: number; total: number }
  forward_secrecy: { with_fs: number; total: number }
  aead:         { with_aead: number; total: number }
}

const CHART_COLORS = ['#00d1ff','#7000ff','#00ff88','#ffcc00','#ff4d4d','#a78bfa','#34d399','#fb923c','#60a5fa','#f472b6']
const GRADE_COLORS: Record<string, string> = {
  'A+': '#00ff88', 'A': '#34d399', 'B': '#60a5fa', 'C': '#ffcc00', 'F': '#ff4d4d',
}

function pct(num: number, denom: number) {
  if (!denom) return 0
  return Math.round((num / denom) * 100)
}

function MiniPie({ data, colors }: { data: { name: string; value: number }[]; colors: string[] }) {
  return (
    <ResponsiveContainer width="100%" height={180}>
      <PieChart>
        <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} dataKey="value">
          {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
        </Pie>
        <Tooltip
          contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: '8px', fontSize: '0.78rem' }}
          itemStyle={{ color: 'var(--text-main)' }}
          formatter={(v: any, name: any) => [`${v}`, name]}
        />
      </PieChart>
    </ResponsiveContainer>
  )
}

function HBar({ data, colors }: { data: { name: string; count: number }[]; colors: string[] }) {
  const clean = data.map(d => ({
    ...d,
    label: d.name.replace('TLS_', '').replace('TLS13_', '').replace(/_/g, ' '),
  }))
  return (
    <ResponsiveContainer width="100%" height={Math.max(200, clean.length * 38)}>
      <BarChart data={clean} layout="vertical" margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" horizontal={false} />
        <XAxis type="number" tick={{ fill: 'var(--text-dim)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis type="category" dataKey="label" width={190} tick={{ fill: 'var(--text-dim)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip
          contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: '8px', fontSize: '0.78rem' }}
          itemStyle={{ color: 'var(--text-main)' }}
          cursor={{ fill: 'rgba(0,209,255,0.04)' }}
        />
        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
          {clean.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

function GradeBar({ data }: { data: { grade: string; count: number }[] }) {
  const order = ['A+', 'A', 'B', 'C', 'F']
  const sorted = order.map(g => ({ grade: g, count: data.find(d => d.grade === g)?.count ?? 0 })).filter(d => d.count > 0)
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={sorted} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" vertical={false} />
        <XAxis dataKey="grade" tick={{ fill: 'var(--text-dim)', fontSize: 12, fontWeight: 700 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: 'var(--text-dim)', fontSize: 11 }} axisLine={false} tickLine={false} />
        <Tooltip
          contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: '8px', fontSize: '0.78rem' }}
          itemStyle={{ color: 'var(--text-main)' }}
          cursor={{ fill: 'rgba(0,209,255,0.04)' }}
        />
        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
          {sorted.map((d) => <Cell key={d.grade} fill={GRADE_COLORS[d.grade] ?? '#a0a0a0'} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

type CountryData = Record<string, {
  secure: number; legacy: number; insecure: number; total: number;
  ke: Record<string, number>; grades: Record<string, number>;
}>

interface SelectedCountry {
  name: string; a2: string;
  secure: number; legacy: number; insecure: number; total: number;
  ke: { name: string; count: number }[];
  grades: { grade: string; count: number }[];
  rank: number; totalCountries: number;
}

function brightenHex(hex: string, factor: number): string {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = Math.min(255, Math.round(((n >> 16) & 0xff) * factor))
  const g = Math.min(255, Math.round(((n >>  8) & 0xff) * factor))
  const b = Math.min(255, Math.round(( n        & 0xff) * factor))
  return `#${r.toString(16).padStart(2,'0')}${g.toString(16).padStart(2,'0')}${b.toString(16).padStart(2,'0')}`
}

function readinessColor(ratio: number): string {
  if (ratio >= 0.6) return '#4a7c59'   // muted olive-green — PQC secure
  if (ratio >= 0.35) return '#3a6080'  // steel-blue — partial/legacy
  if (ratio >= 0.15) return '#8a6a2a'  // dark amber — at risk
  return '#7a3030'                      // deep brick-red — insecure
}

const NIGHT_SKY_URL = '//cdn.jsdelivr.net/npm/three-globe/example/img/night-sky.png'
const SAT_LAT = 28       // fixed orbital inclination (degrees)
const SAT_ALT = 0.38     // orbital altitude (fraction of globe radius above surface)
const SAT_SPD = 0.35     // degrees per frame at 60fps

function QuantumGlobe({
  countryData,
  countryRanks,
  onSelect,
  onGlobeReady,
  selectedA2,
  starsEnabled,
  orbiterPhase,
}: {
  countryData: CountryData
  countryRanks: Record<string, number>
  onSelect: (c: SelectedCountry | null) => void
  onGlobeReady?: (globe: any) => void
  selectedA2?: string | null
  starsEnabled: boolean
  orbiterPhase: 'orbiting' | 'landing' | 'hidden'
}) {
  const containerRef          = useRef<HTMLDivElement>(null)
  const globeRef              = useRef<any>(null)
  const hoveredRef            = useRef<any>(null)
  const selectedA2Ref         = useRef<string | null | undefined>(selectedA2)
  const visibilityCleanupRef  = useRef<(() => void) | null>(null)
  // 3D sentry satellite
  const satMeshRef            = useRef<THREE.Group | null>(null)
  const orbitRingRef          = useRef<THREE.Line | null>(null)
  const satLngRef             = useRef(0)
  const satAltRef             = useRef(SAT_ALT)
  const satRafRef             = useRef(0)
  // Mirror props into refs so async init callbacks always see the latest values
  const countryDataRef   = useRef<CountryData>(countryData)
  const countryRanksRef  = useRef<Record<string, number>>(countryRanks)
  const onSelectRef      = useRef(onSelect)
  useEffect(() => { countryDataRef.current  = countryData  }, [countryData])
  useEffect(() => { countryRanksRef.current = countryRanks }, [countryRanks])
  useEffect(() => { onSelectRef.current     = onSelect     }, [onSelect])

  useEffect(() => {
    if (!containerRef.current) return

    fetch(GEO_URL)
      .then(r => r.json())
      .then((world: any) => {
        // topojson → GeoJSON features
        const { feature } = require('topojson-client')
        const countries = feature(world, world.objects.countries)

        const globe = (Globe as any)({
          rendererConfig: { antialias: false, powerPreference: 'high-performance' },
        })(containerRef.current!)
        globeRef.current = globe
        onGlobeReady?.(globe)

        // Cap pixel ratio — Retina is 2×, which means 4× pixels. Capping at 1.5 cuts
        // WebGL fill-rate cost by ~44% with minimal visual difference.
        globe.renderer().setPixelRatio(Math.min(window.devicePixelRatio, 1.5))

        globe
          .globeImageUrl('')
          .backgroundColor('#000005')
          .backgroundImageUrl(starsEnabled ? NIGHT_SKY_URL : '')
          .showAtmosphere(true)
          .atmosphereColor('#0d2a50')
          .atmosphereAltitude(0.12)
          .polygonsData((countries as any).features)
          .polygonsTransitionDuration(200)
          .polygonAltitude((d: any) => {
            const a2 = ISO_NUM_TO_A2[String(d.id).padStart(3, '0')]
            if (hoveredRef.current === d || selectedA2Ref.current === a2) return 0.08
            return (countryDataRef.current[a2]?.total ?? 0) > 0 ? 0.015 : 0.006
          })
          .polygonCapColor((d: any) => {
            const a2 = ISO_NUM_TO_A2[String(d.id).padStart(3, '0')]
            const c = countryDataRef.current[a2]
            const base = (!c || !c.total) ? '#1e2318' : readinessColor(c.secure / c.total)
            return (hoveredRef.current === d || selectedA2Ref.current === a2) ? brightenHex(base, 2.2) : base
          })
          .polygonSideColor((d: any) => {
            const a2 = ISO_NUM_TO_A2[String(d.id).padStart(3, '0')]
            if (hoveredRef.current === d || selectedA2Ref.current === a2) return 'rgba(255,255,255,0.15)'
            return 'rgba(0,0,0,0.4)'
          })
          .polygonStrokeColor((d: any) => {
            const a2 = ISO_NUM_TO_A2[String(d.id).padStart(3, '0')]
            return (hoveredRef.current === d || selectedA2Ref.current === a2) ? '#aabbaa' : '#06070d'
          })
          .onPolygonHover((d: any) => {
            hoveredRef.current = d ?? null
            const g = globeRef.current
            if (!g) return
            // Poke globe to re-evaluate per-polygon callbacks
            const data = g.polygonsData()
            if (data?.length) g.polygonsData([...data])
          })
          .polygonLabel((d: any) => {
            const a2  = ISO_NUM_TO_A2[String(d.id).padStart(3, '0')]
            const c   = countryDataRef.current[a2]
            const name = d.properties.name
            if (!c || !c.total) return `<div style="font-family:sans-serif;background:#0d0f18;border:1px solid #252840;border-radius:8px;padding:8px 12px;color:#a0a0a0;font-size:12px">${name}<br/><span style="opacity:0.5">No data</span></div>`
            const ratio = c.secure / c.total
            const col   = readinessColor(ratio)
            return `
              <div style="font-family:sans-serif;background:#0d0f18;border:1px solid ${col}55;border-radius:10px;padding:10px 14px;min-width:160px;box-shadow:0 4px 20px rgba(0,0,0,0.6)">
                <div style="font-size:13px;font-weight:700;color:#fff;margin-bottom:6px">${countryFlag(a2)} ${name}</div>
                <div style="display:flex;gap:6px;margin-bottom:4px">
                  <div style="flex:1;text-align:center;background:rgba(74,124,89,0.15);border-radius:6px;padding:4px">
                    <div style="font-size:16px;font-weight:800;color:#6aaa7a">${pct(c.secure, c.total)}%</div>
                    <div style="font-size:10px;color:#a0a0a0">PQC Secure</div>
                  </div>
                  <div style="flex:1;text-align:center;background:rgba(138,106,42,0.15);border-radius:6px;padding:4px">
                    <div style="font-size:16px;font-weight:800;color:#b8923a">${pct(c.legacy, c.total)}%</div>
                    <div style="font-size:10px;color:#a0a0a0">Legacy</div>
                  </div>
                  <div style="flex:1;text-align:center;background:rgba(122,48,48,0.2);border-radius:6px;padding:4px">
                    <div style="font-size:16px;font-weight:800;color:#b85050">${pct(c.insecure, c.total)}%</div>
                    <div style="font-size:10px;color:#a0a0a0">Insecure</div>
                  </div>
                </div>
                <div style="margin-top:6px;padding-top:6px;border-top:1px solid #252840;display:flex;justify-content:space-between;align-items:center">
                  <span style="font-size:10px;color:#a0a0a0">${c.total.toLocaleString()} domains</span>
                  <span style="font-size:11px;font-weight:700;color:${col}">${countryRanksRef.current[a2] ? `Rank #${countryRanksRef.current[a2]}` : ''}</span>
                </div>
                <div style="font-size:10px;color:#a0a0a0;text-align:center;margin-top:4px;opacity:0.6">click for full breakdown</div>
              </div>`
          })
          .onPolygonClick((d: any) => {
            const a2 = ISO_NUM_TO_A2[String(d.id).padStart(3, '0')]
            const c  = countryDataRef.current[a2]
            if (!c || !c.total) return
            // Update the selected ref synchronously before onPolygonHover(null) fires.
            // This ensures polygon callbacks see the new selection when the globe
            // re-evaluates immediately after the click clears the hover state.
            selectedA2Ref.current = a2
            const g = globeRef.current
            if (g) { const ex = g.polygonsData(); if (ex?.length) g.polygonsData([...ex]) }
            const totalCountries = Object.keys(countryRanksRef.current).length
            onSelectRef.current({
              name:     d.properties.name,
              a2,
              secure:   c.secure,
              legacy:   c.legacy,
              insecure: c.insecure,
              total:    c.total,
              ke:     Object.entries(c.ke).map(([name, count]) => ({ name, count })).sort((a, b) => b.count - a.count).slice(0, 6),
              grades: Object.entries(c.grades).map(([grade, count]) => ({ grade, count })).sort((a, b) => a.grade.localeCompare(b.grade)),
              rank: countryRanksRef.current[a2] ?? totalCountries,
              totalCountries,
            })
          })

        // Globe surface: deep blue ocean
        globe.renderer().setClearColor(0x000005, 1)

        const mesh = globe.scene().children.find((c: any) => c.type === 'Mesh' && c.geometry?.type === 'SphereGeometry')
        if (mesh) mesh.material.color.set(0x030e1c)

        // Auto-rotate slowly; mouse drag works automatically via OrbitControls
        globe.controls().autoRotate      = true
        globe.controls().autoRotateSpeed = 0.25
        globe.controls().enableZoom      = true
        globe.controls().minDistance     = 200
        globe.controls().maxDistance     = 600

        // Resize observer
        const ro = new ResizeObserver(() => {
          const el = containerRef.current
          if (el) globe.width(el.clientWidth).height(el.clientHeight)
        })
        ro.observe(containerRef.current!)

        const el = containerRef.current!
        globe.width(el.clientWidth).height(el.clientHeight)

        // ── 3D sentry satellite ────────────────────────────────────────────────
        // Build satellite mesh and add directly to globe scene.
        // Position is updated via globe.getCoords() in the rAF loop — no reactive
        // overhead from globe.gl's data layer.
        const satGroup = new THREE.Group()

        // Core bright sphere
        satGroup.add(new THREE.Mesh(
          new THREE.SphereGeometry(2.8, 14, 14),
          new THREE.MeshBasicMaterial({ color: 0x00d1ff }),
        ))
        // Inner glow shell
        satGroup.add(new THREE.Mesh(
          new THREE.SphereGeometry(5.5, 14, 14),
          new THREE.MeshBasicMaterial({ color: 0x00d1ff, transparent: true, opacity: 0.18 }),
        ))
        // Outer very-transparent corona
        satGroup.add(new THREE.Mesh(
          new THREE.SphereGeometry(9, 14, 14),
          new THREE.MeshBasicMaterial({ color: 0x0066ff, transparent: true, opacity: 0.07 }),
        ))
        // Sensor ring (flat ring around equator of the satellite)
        satGroup.add(new THREE.Mesh(
          new THREE.RingGeometry(4.5, 6.5, 32),
          new THREE.MeshBasicMaterial({ color: 0x00d1ff, transparent: true, opacity: 0.5, side: THREE.DoubleSide }),
        ))

        satGroup.visible = false
        globe.scene().add(satGroup)
        satMeshRef.current = satGroup

        // Orbit ring — a LineLoop tracing the satellite's orbital path
        const globeRadius = globe.getGlobeRadius()
        const ringPts: THREE.Vector3[] = []
        for (let i = 0; i <= 128; i++) {
          const c = globe.getCoords(SAT_LAT, (i / 128) * 360, SAT_ALT)
          ringPts.push(new THREE.Vector3(c.x, c.y, c.z))
        }
        const orbitRing = new THREE.LineLoop(
          new THREE.BufferGeometry().setFromPoints(ringPts),
          new THREE.LineBasicMaterial({ color: 0x00d1ff, transparent: true, opacity: 0.18 }),
        )
        orbitRing.visible = false
        globe.scene().add(orbitRing)
        orbitRingRef.current = orbitRing
        // suppress unused var warning
        void globeRadius

        // Pause rendering when the tab is hidden — single biggest idle-CPU win
        const onVisibility = () => {
          if (!globeRef.current) return
          const ctrl = globeRef.current.controls()
          if (document.hidden) {
            ctrl._wasAutoRotating = ctrl.autoRotate
            ctrl.autoRotate = false
            globeRef.current.pauseAnimation?.()
          } else {
            ctrl.autoRotate = ctrl._wasAutoRotating ?? true
            globeRef.current.resumeAnimation?.()
          }
        }
        document.addEventListener('visibilitychange', onVisibility)
        visibilityCleanupRef.current = () =>
          document.removeEventListener('visibilitychange', onVisibility)
      })
      .catch(console.error)

    return () => {
      globeRef.current?._destructor?.()
      visibilityCleanupRef.current?.()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Re-render globe polygons whenever countryData changes.
  // Callbacks already read from refs so we just need to poke globe.gl
  // into re-evaluating colors by re-setting polygonsData.
  useEffect(() => {
    const globe = globeRef.current
    if (!globe || !Object.keys(countryData).length) return
    // Re-set the same features array reference to trigger a color/altitude refresh
    const existing = globe.polygonsData()
    if (existing?.length) globe.polygonsData([...existing])
  }, [countryData])

  // Keep selectedA2Ref in sync and poke globe to re-evaluate polygon colors/altitude.
  useEffect(() => {
    selectedA2Ref.current = selectedA2 ?? null
    const globe = globeRef.current
    if (!globe) return
    const existing = globe.polygonsData()
    if (existing?.length) globe.polygonsData([...existing])
  }, [selectedA2])

  // Toggle star background inside Three.js when preference changes
  useEffect(() => {
    const globe = globeRef.current
    if (!globe) return
    globe.backgroundImageUrl(starsEnabled ? NIGHT_SKY_URL : '')
  }, [starsEnabled])

  // Drive the 3D sentry satellite via globe.getCoords() + direct mesh position update
  useEffect(() => {
    const sat  = satMeshRef.current
    const ring = orbitRingRef.current
    const globe = globeRef.current
    cancelAnimationFrame(satRafRef.current)

    if (orbiterPhase === 'hidden' || !sat || !ring || !globe) {
      if (sat)  sat.visible  = false
      if (ring) ring.visible = false
      return
    }

    sat.visible  = true
    ring.visible = true
    satAltRef.current = SAT_ALT

    if (orbiterPhase === 'orbiting') {
      const tick = () => {
        satLngRef.current = (satLngRef.current + SAT_SPD) % 360
        const { x, y, z } = globe.getCoords(SAT_LAT, satLngRef.current, satAltRef.current)
        sat.position.set(x, y, z)
        // Rotate ring face toward globe centre so it stays visible from any angle
        sat.lookAt(0, 0, 0)
        satRafRef.current = requestAnimationFrame(tick)
      }
      satRafRef.current = requestAnimationFrame(tick)
      return () => cancelAnimationFrame(satRafRef.current)
    }

    if (orbiterPhase === 'landing') {
      const ringRef = ring  // captured non-null for use inside tick closure
      // Descend from SAT_ALT to 0 then vanish
      const tick = () => {
        satAltRef.current = Math.max(0, satAltRef.current - 0.012)
        const { x, y, z } = globe.getCoords(SAT_LAT, satLngRef.current, satAltRef.current)
        sat.position.set(x, y, z)
        sat.lookAt(0, 0, 0)
        // Fade out core sphere as it descends
        const progress = 1 - satAltRef.current / SAT_ALT
        ;(sat.children[0] as THREE.Mesh).material = new THREE.MeshBasicMaterial({
          color: 0x00d1ff, transparent: true, opacity: 1 - progress,
        })
        if (satAltRef.current > 0) {
          satRafRef.current = requestAnimationFrame(tick)
        } else {
          sat.visible = false
          if (ringRef) ringRef.visible = false
        }
      }
      satRafRef.current = requestAnimationFrame(tick)
      return () => cancelAnimationFrame(satRafRef.current)
    }
  }, [orbiterPhase]) // eslint-disable-line

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: '520px', cursor: 'grab' }} />
      {/* Legend */}
      <div style={{ position: 'absolute', bottom: '16px', left: '16px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {[
          { color: '#00ff88', label: '≥60% PQC' },
          { color: '#60a5fa', label: '35–60%'   },
          { color: '#ffcc00', label: '15–35%'   },
          { color: '#ff4d4d', label: '<15%'     },
          { color: '#1c2030', label: 'No data'  },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.68rem', color: 'rgba(160,160,160,0.8)', background: 'rgba(6,7,13,0.75)', padding: '3px 8px', borderRadius: '20px', backdropFilter: 'blur(4px)' }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
            {label}
          </div>
        ))}
      </div>
      <div style={{ position: 'absolute', top: '12px', right: '16px', fontSize: '0.65rem', color: 'rgba(160,160,160,0.5)', background: 'rgba(6,7,13,0.7)', padding: '4px 10px', borderRadius: '20px' }}>
        Drag to rotate · scroll to zoom · click country for details
      </div>
    </div>
  )
}

function rankMedal(rank: number): string {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return `#${rank}`
}

function CountryPanel({ country, onClose }: { country: SelectedCountry; onClose: () => void }) {
  const ratio      = country.total > 0 ? country.secure / country.total : 0
  const barColor   = readinessColor(ratio)
  const gradeOrder = ['A+', 'A', 'B', 'C', 'F']
  const topHalf    = country.rank <= Math.ceil(country.totalCountries / 2)
  const rankColor  = country.rank <= 3 ? '#ffcc00' : country.rank <= 10 ? '#00ff88' : topHalf ? '#60a5fa' : 'var(--text-dim)'

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: '360px', zIndex: 500,
      background: 'var(--bg-card)', borderLeft: '1px solid var(--border-bright)',
      boxShadow: '-8px 0 40px rgba(0,0,0,0.6)', overflowY: 'auto',
      animation: 'slideIn 0.25s ease',
    }}>
      <div style={{ padding: '1.5rem' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
          <div>
            <div style={{ fontSize: '1.6rem', lineHeight: 1 }}>{countryFlag(country.a2)}</div>
            <h2 style={{ margin: '6px 0 2px', fontSize: '1.3rem', fontWeight: 800 }}>{country.name}</h2>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>{country.total.toLocaleString()} domains scanned</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid var(--border-bright)', color: 'var(--text-dim)', cursor: 'pointer', borderRadius: '6px', padding: '4px 10px', fontSize: '1rem' }}>✕</button>
        </div>

        {/* ── World Rank — most prominent element ── */}
        <div style={{
          background: `linear-gradient(135deg, ${rankColor}12, ${rankColor}06)`,
          border: `1px solid ${rankColor}44`,
          borderRadius: '12px', padding: '1rem 1.25rem',
          marginBottom: '1.25rem', textAlign: 'center',
        }}>
          <div style={{ fontSize: '0.6rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: '6px' }}>Global Quantum Rank</div>
          <div style={{ fontSize: '2.8rem', fontWeight: 900, color: rankColor, lineHeight: 1, fontFamily: 'monospace' }}>
            {rankMedal(country.rank)}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '6px' }}>
            out of {country.totalCountries} tracked countries
          </div>
          <div style={{ marginTop: '8px', fontSize: '0.7rem', color: rankColor, fontWeight: 600 }}>
            {country.rank <= 3   ? 'World leader in quantum readiness'
            : country.rank <= 10 ? 'Top 10 globally'
            : topHalf            ? 'Above global average'
            :                      'Below global average — improvement needed'}
          </div>
        </div>

        {/* Readiness score */}
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.75rem' }}>
            <span style={{ color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>Quantum Readiness</span>
            <span style={{ color: barColor, fontWeight: 700, fontFamily: 'monospace' }}>{pct(country.secure, country.total)}%</span>
          </div>
          <div style={{ height: '8px', background: 'var(--border-bright)', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct(country.secure, country.total)}%`, background: barColor, borderRadius: '4px', transition: 'width 0.6s ease' }} />
          </div>
        </div>

        {/* Status pills */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '1.5rem' }}>
          {[
            { label: 'Secure',   val: country.secure,   color: '#00ff88', bg: 'rgba(0,255,136,0.08)' },
            { label: 'Legacy',   val: country.legacy,   color: '#ffcc00', bg: 'rgba(255,204,0,0.08)' },
            { label: 'Insecure', val: country.insecure, color: '#ff4d4d', bg: 'rgba(255,77,77,0.08)' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: '8px', padding: '10px 8px', textAlign: 'center', border: `1px solid ${s.color}33` }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, fontFamily: 'monospace' }}>{s.val}</div>
              <div style={{ fontSize: '0.65rem', color: 'var(--text-dim)', marginTop: '2px' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Key Exchange breakdown */}
        {country.ke.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 10px', fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-dim)', paddingBottom: '6px', borderBottom: '1px solid var(--border-bright)' }}>Key Exchange Groups</h4>
            {country.ke.map((k, i) => {
              const isPQC = /mlkem|kyber/i.test(k.name)
              const max   = country.ke[0]?.count ?? 1
              return (
                <div key={k.name} style={{ marginBottom: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginBottom: '3px' }}>
                    <span style={{ color: isPQC ? '#00ff88' : 'var(--text-dim)', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {k.name.length > 28 ? k.name.slice(0, 28) + '…' : k.name}
                    </span>
                    <span style={{ color: 'var(--text-dim)' }}>{k.count}</span>
                  </div>
                  <div style={{ height: '4px', background: 'var(--border-bright)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(k.count / max) * 100}%`, background: CHART_COLORS[i % CHART_COLORS.length], borderRadius: '2px' }} />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Grade distribution */}
        {country.grades.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <h4 style={{ margin: '0 0 10px', fontSize: '0.65rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-dim)', paddingBottom: '6px', borderBottom: '1px solid var(--border-bright)' }}>Grade Distribution</h4>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {gradeOrder.map(g => {
                const entry = country.grades.find(e => e.grade === g)
                if (!entry) return null
                return (
                  <div key={g} style={{ textAlign: 'center', background: 'var(--bg-raised)', borderRadius: '8px', padding: '8px 14px', border: `1px solid ${GRADE_COLORS[g] ?? '#a0a0a0'}44` }}>
                    <div style={{ fontSize: '1.3rem', fontWeight: 900, color: GRADE_COLORS[g] ?? '#a0a0a0', fontFamily: 'monospace' }}>{g}</div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)' }}>{entry.count}</div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

interface MetricDef { label: string; value: string | number; color?: string; sub?: string; border?: string }
function MetricsStrip({ metrics }: { metrics: MetricDef[] }) {
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap',
      background: 'var(--bg-card)', border: '1px solid var(--border-bright)',
      borderRadius: '12px', overflow: 'hidden', boxShadow: 'var(--card-shadow-sm)',
      marginBottom: '2rem',
    }}>
      {metrics.map((m, i) => (
        <div key={m.label} style={{
          flex: '1 1 120px', padding: '0.9rem 1.2rem',
          borderLeft: i === 0 ? 'none' : '1px solid var(--border-bright)',
          borderTop: '3px solid ' + (m.border ?? 'transparent'),
          display: 'flex', flexDirection: 'column', gap: '2px',
        }}>
          <div style={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>{m.label}</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 800, color: m.color ?? 'var(--text-main)', fontFamily: 'monospace', lineHeight: 1.1 }}>{m.value}</div>
          {m.sub && <div style={{ fontSize: '0.62rem', color: 'var(--text-dim)', opacity: 0.55 }}>{m.sub}</div>}
        </div>
      ))}
    </div>
  )
}

function PieLegend({ data, colors }: { data: { name: string; value: number }[]; colors: string[] }) {
  const total = data.reduce((s, d) => s + d.value, 0)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
      {data.map((d, i) => (
        <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.78rem' }}>
          <div style={{ width: 10, height: 10, borderRadius: 2, background: colors[i % colors.length], flexShrink: 0 }} />
          <span style={{ color: 'var(--text-dim)', flex: 1 }}>{d.name}</span>
          <span style={{ color: 'var(--text-main)', fontFamily: 'monospace', fontSize: '0.75rem' }}>{pct(d.value, total)}%</span>
        </div>
      ))}
    </div>
  )
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: '16px', padding: '1.5rem', boxShadow: 'var(--card-shadow)' }}>
      <h3 style={{ margin: '0 0 1.25rem', fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', color: 'var(--text-dim)', paddingBottom: '0.75rem', borderBottom: '1px solid var(--border-bright)' }}>{title}</h3>
      {children}
    </div>
  )
}

// ─── Inline Registry overlay (slides down from top) ───────────────────────────

// ─── Filter state for InlineRegistry ─────────────────────────────────────────
interface RegistryFilters {
  search:    string
  status:    string
  grade:     string
  tls:       string
  kx_type:   string
  hsts:      string
  changed:   string
}
const DEFAULT_FILTERS: RegistryFilters = {
  search: '', status: '', grade: '', tls: '', kx_type: '', hsts: '', changed: '',
}
const GRADE_OPTIONS = ['A+', 'A', 'B', 'C', 'D', 'F']

function FilterPill({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="ir-filter-pill">
      {label}
      <button onClick={onRemove} className="ir-filter-pill-x">✕</button>
    </span>
  )
}

function InlineRegistry({ open, countryFilter, title, onClose, onDomainSelect }: {
  open: boolean;
  countryFilter: string | null;
  title: string;
  onClose: () => void;
  onDomainSelect: (hostname: string) => void;
}) {
  const [domains, setDomains]   = useState<Domain[]>([])
  const [total, setTotal]       = useState(0)
  const [page, setPage]         = useState(0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [filters, setFilters]   = useState<RegistryFilters>(DEFAULT_FILTERS)
  const [applied, setApplied]   = useState<RegistryFilters>(DEFAULT_FILTERS)

  const setF = (key: keyof RegistryFilters, val: string) =>
    setFilters(f => ({ ...f, [key]: val }))

  const applyFilters = () => { setApplied({ ...filters }); setPage(0) }
  const clearFilters = () => { const f = DEFAULT_FILTERS; setFilters(f); setApplied(f); setSearchInput(''); setPage(0) }

  useEffect(() => {
    if (!open) return
    setPage(0); setDomains([]); setTotal(0); setFilters(DEFAULT_FILTERS); setApplied(DEFAULT_FILTERS); setSearchInput('')
  }, [open, countryFilter])

  useEffect(() => {
    if (!open) return
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) })
    if (countryFilter)    params.set('country',  countryFilter)
    if (applied.search)   params.set('search',   applied.search)
    if (applied.status)   params.set('status',   applied.status)
    if (applied.grade)    params.set('grade',    applied.grade)
    if (applied.tls)      params.set('tls',      applied.tls)
    if (applied.kx_type)  params.set('kx_type',  applied.kx_type)
    if (applied.hsts)     params.set('hsts',     applied.hsts)
    if (applied.changed)  params.set('changed',  applied.changed)
    fetch(`${API_BASE}/v1/list?${params}`)
      .then(r => r.json())
      .then(data => { setDomains(data.results || []); setTotal(data.total || 0) })
  }, [open, page, countryFilter, applied])

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const dotClass = (s: string | null) =>
    s === 'secure' ? 'secure' : s === 'legacy' ? 'legacy' : s === 'insecure' ? 'insecure' : 'unknown'

  // Active filter pills
  const activePills: { label: string; clear: () => void }[] = []
  if (applied.search)  activePills.push({ label: `hostname: ${applied.search}`, clear: () => { setF('search',''); setApplied(p => ({ ...p, search:'' })); setSearchInput('') } })
  if (applied.status)  activePills.push({ label: `status: ${applied.status}`, clear: () => { setF('status',''); setApplied(p => ({ ...p, status:'' })) } })
  if (applied.grade)   activePills.push({ label: `grade: ${applied.grade}`, clear: () => { setF('grade',''); setApplied(p => ({ ...p, grade:'' })) } })
  if (applied.tls)     activePills.push({ label: `TLS: ${applied.tls}`, clear: () => { setF('tls',''); setApplied(p => ({ ...p, tls:'' })) } })
  if (applied.kx_type) activePills.push({ label: `KX: ${applied.kx_type}`, clear: () => { setF('kx_type',''); setApplied(p => ({ ...p, kx_type:'' })) } })
  if (applied.hsts)    activePills.push({ label: `HSTS: ${applied.hsts}`, clear: () => { setF('hsts',''); setApplied(p => ({ ...p, hsts:'' })) } })
  if (applied.changed) activePills.push({ label: `changed: ${applied.changed}`, clear: () => { setF('changed',''); setApplied(p => ({ ...p, changed:'' })) } })

  return (
    <div className={`ir-overlay${open ? ' open' : ''}`}>
      {/* ── Header bar ── */}
      <div className="ir-header">
        <div className="ir-title">{title}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, justifyContent: 'flex-end' }}>
          {/* Hostname search */}
          <input
            className="ir-search-input"
            placeholder="Search hostname…"
            value={searchInput}
            onChange={e => { setSearchInput(e.target.value); setF('search', e.target.value) }}
            onKeyDown={e => { if (e.key === 'Enter') applyFilters() }}
          />
          {/* Filters toggle */}
          <button
            className={`ir-filter-toggle${filtersOpen ? ' active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
            title="Advanced filters">
            ⚙ Filters{activePills.length > 0 ? ` (${activePills.length})` : ''}
          </button>
          {activePills.length > 0 && (
            <button className="ir-clear-btn" onClick={clearFilters}>Clear All</button>
          )}
          <span className="ir-node-count">{total > 0 ? `${total.toLocaleString()} nodes` : ''}</span>
          <button className="gd-close-btn" onClick={onClose}>✕ Close</button>
        </div>
      </div>

      {/* ── Active filter pills ── */}
      {activePills.length > 0 && (
        <div className="ir-pills-bar">
          {activePills.map((p, i) => <FilterPill key={i} label={p.label} onRemove={p.clear} />)}
        </div>
      )}

      {/* ── Advanced filter panel ── */}
      {filtersOpen && (
        <div className="ir-filter-panel">
          {/* Row 1: Status + Grade */}
          <div className="ir-filter-group">
            <span className="ir-filter-label">STATUS</span>
            {['', 'secure', 'legacy', 'insecure'].map(v => (
              <button key={v} className={`ir-chip${filters.status === v ? ' active' : ''}`}
                onClick={() => setF('status', v)}>
                {v === '' ? 'Any' : v === 'secure' ? 'PQC Secure' : v === 'legacy' ? 'Legacy' : 'Insecure'}
              </button>
            ))}
          </div>
          <div className="ir-filter-group">
            <span className="ir-filter-label">GRADE</span>
            <button className={`ir-chip${filters.grade === '' ? ' active' : ''}`} onClick={() => setF('grade', '')}>Any</button>
            {GRADE_OPTIONS.map(g => (
              <button key={g} className={`ir-chip${filters.grade === g ? ' active' : ''}`}
                onClick={() => setF('grade', g)} style={{ color: gradeColor(g) }}>{g}</button>
            ))}
          </div>
          {/* Row 2: TLS + KX */}
          <div className="ir-filter-group">
            <span className="ir-filter-label">TLS VERSION</span>
            {[['', 'Any'], ['1.3', 'TLS 1.3'], ['1.2', 'TLS 1.2'], ['1.1-', 'TLS 1.1-']].map(([v, l]) => (
              <button key={v} className={`ir-chip${filters.tls === v ? ' active' : ''}`}
                onClick={() => setF('tls', v)}>{l}</button>
            ))}
          </div>
          <div className="ir-filter-group">
            <span className="ir-filter-label">KEY EXCHANGE</span>
            {[['', 'Any'], ['pqc', 'PQC Hybrid'], ['classical', 'Classical']].map(([v, l]) => (
              <button key={v} className={`ir-chip${filters.kx_type === v ? ' active' : ''}`}
                onClick={() => setF('kx_type', v)}>{l}</button>
            ))}
          </div>
          {/* Row 3: HSTS + Changed */}
          <div className="ir-filter-group">
            <span className="ir-filter-label">HSTS</span>
            {[['', 'Any'], ['yes', 'Has HSTS'], ['no', 'No HSTS']].map(([v, l]) => (
              <button key={v} className={`ir-chip${filters.hsts === v ? ' active' : ''}`}
                onClick={() => setF('hsts', v)}>{l}</button>
            ))}
          </div>
          <div className="ir-filter-group">
            <span className="ir-filter-label">CHANGES</span>
            {[['', 'Any'], ['yes', 'Changed'], ['upgraded', '↑ Upgraded'], ['downgraded', '↓ Downgraded']].map(([v, l]) => (
              <button key={v} className={`ir-chip${filters.changed === v ? ' active' : ''}`}
                onClick={() => setF('changed', v)}>{l}</button>
            ))}
          </div>
          {/* Apply button */}
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button className="ir-apply-btn" onClick={applyFilters}>Apply Filters</button>
            <button className="ir-clear-btn" onClick={clearFilters}>Reset</button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      <div className="ir-body">
        <table className="qr-table">
          <thead>
            <tr>
              <th style={{ width: 20 }}></th>
              <th>NODE / HOSTNAME</th>
              <th>GEO</th>
              <th>KEY EXCHANGE</th>
              <th>CERT SIG</th>
              <th>GRADE</th>
              <th>SCORE</th>
            </tr>
          </thead>
          <tbody>
            {domains.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-dim)', fontSize: '0.7rem', letterSpacing: '2px' }}>
                {open ? 'NO NODES FOUND' : ''}
              </td></tr>
            ) : domains.map((d, i) => {
              const score = registryScore(d)
              const ke = d.pqc_group_name ?? d.key_exchange ?? '—'
              const isPQC = ke.toLowerCase().includes('mlkem') || ke.toLowerCase().includes('kyber')
              const cc = d.asn_country ?? null
              return (
                <tr
                  key={d.hostname}
                  className={`qr-row status-${d.pqc_status ?? 'unknown'}`}
                  style={{ animationDelay: `${Math.min(i * 12, 300)}ms`, cursor: 'pointer' }}
                  onClick={() => onDomainSelect(d.hostname)}>
                  <td><span className={`qr-dot ${dotClass(d.pqc_status)}`} /></td>
                  <td className="qr-hostname">{d.hostname}</td>
                  <td className="qr-geo">{cc ? <span>{countryFlag(cc)} {cc}</span> : <span style={{ color: 'var(--text-dim)' }}>—</span>}</td>
                  <td>
                    <span className="qr-ke" style={{ color: isPQC ? 'var(--success)' : 'var(--text-dim)' }}>
                      {isPQC && <span className="qr-pqc-badge">PQC</span>}
                      {ke}
                    </span>
                  </td>
                  <td className="qr-sig">{d.sig_alg ?? '—'}</td>
                  <td><span className="qr-grade" style={{ color: gradeColor(d.overall_grade), textShadow: `0 0 10px ${gradeColor(d.overall_grade)}66` }}>{d.overall_grade ?? '?'}</span></td>
                  <td>
                    <span style={{ color: score !== null ? gradeColor(d.overall_grade) : 'var(--text-dim)', fontWeight: 700 }}>{score ?? '—'}</span>
                    {score !== null && (
                      <span className="qr-score-bar">
                        <span className="qr-score-fill" style={{ width: `${score}%`, background: gradeColor(d.overall_grade) }} />
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="qr-pagination">
            <button className="qr-page-btn" onClick={() => setPage(0)} disabled={page === 0}>«</button>
            <button className="qr-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹ Prev</button>
            <span className="qr-page-info">PAGE <span style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>{page + 1}</span> / {totalPages}</span>
            <button className="qr-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next ›</button>
            <button className="qr-page-btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Inline Report overlay (slides in from right) ─────────────────────────────

function InlineReport({ hostname, onClose, onViewCompanyReport }: { hostname: string | null; onClose: () => void; onViewCompanyReport?: (domain: string) => void }) {
  const [refreshKey, setRefreshKey] = useState(0)
  // Reset refresh key when domain changes
  useEffect(() => { setRefreshKey(0) }, [hostname])
  return (
    <div className={`irp-overlay${hostname ? ' open' : ''}`}>
      <div className="irp-panel-header">
        <span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', fontFamily: 'monospace', letterSpacing: '1px' }}>
          {hostname ?? ''}
        </span>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button
            className="gd-close-btn"
            style={{ borderColor: 'rgba(0,209,255,0.3)', color: 'var(--accent-cyan)' }}
            onClick={() => setRefreshKey(k => k + 1)}
            title="Refresh data">
            ↺ Refresh
          </button>
          <button className="gd-close-btn" onClick={onClose}>✕ Close</button>
        </div>
      </div>
      <div className="irp-panel-body">
        {hostname && <SiteReportBody hostname={hostname} onBack={onClose} inline refreshKey={refreshKey} onViewCompanyReport={onViewCompanyReport} />}
      </div>
    </div>
  )
}

// ─── Quantum Registry — Futuristic inventory view ─────────────────────────────

function QuantumRegistry() {
  const [stats, setStats]               = useState<any>(null)
  const [domains, setDomains]           = useState<Domain[]>([])
  const [total, setTotal]               = useState(0)
  const [page, setPage]                 = useState(0)
  const [filterStatus, setFilterStatus] = useState('')
  const [filterGrade, setFilterGrade]   = useState('')
  const [filterCipher, setFilterCipher] = useState('')
  const [filterCountry, setFilterCountry] = useState('')
  const [tableSearch, setTableSearch]   = useState('')
  const [tableSearchInput, setTableSearchInput] = useState('')
  const [search, setSearch]             = useState('');
  const [cipherOptions, setCipherOptions]   = useState<string[]>([])
  const [countryOptions, setCountryOptions] = useState<string[]>([])
  const [filtersOpen, setFiltersOpen]   = useState(false)
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    fetch(`${API_BASE}/v1/stats`).then(r => r.json()).then(setStats)
    fetch(`${API_BASE}/v1/filter-options`)
      .then(r => r.json())
      .then((d: { ciphers: string[]; countries: string[] }) => {
        setCipherOptions(d.ciphers ?? [])
        setCountryOptions(d.countries ?? [])
      })
  }, [])

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(page * PAGE_SIZE) })
    if (filterStatus)  params.set('status',  filterStatus)
    if (filterGrade)   params.set('grade',   filterGrade)
    if (filterCipher)  params.set('cipher',  filterCipher)
    if (filterCountry) params.set('country', filterCountry)
    if (tableSearch)   params.set('search',  tableSearch)
    fetch(`${API_BASE}/v1/list?${params}`)
      .then(r => r.json())
      .then(data => { setDomains(data.results || []); setTotal(data.total || 0) })
  }, [page, filterStatus, filterGrade, filterCipher, filterCountry, tableSearch])

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const hasFilters = filterStatus || filterGrade || filterCipher || filterCountry || tableSearch
  const pqcPct = stats ? Math.round(((stats.secure ?? 0) / (stats.total || 1)) * 100) : null

  const isValidDomain = (v: string) => {
    const s = v.replace(/^\*\./, '').replace(/^www\./, '')
    return /^[a-zA-Z0-9][a-zA-Z0-9\-.]*\.[a-zA-Z]{2,}$/.test(s)
  }
  const handleSearch = () => {
    if (!search || !isValidDomain(search)) return
    if (search.startsWith('*.')) navigate(`/company/${search.replace(/^\*\./, '')}`)
    else navigate(`/report/${search.replace(/^www\./, '')}`)
  }
  const resetFilters = () => {
    setFilterStatus(''); setFilterGrade(''); setFilterCipher(''); setFilterCountry('')
    setTableSearch(''); setTableSearchInput(''); setPage(0)
  }

  const dotClass = (s: string | null) =>
    s === 'secure' ? 'secure' : s === 'legacy' ? 'legacy' : s === 'insecure' ? 'insecure' : 'unknown'

  const statusMeta = (s: string | null) => {
    if (s === 'secure')   return { label: 'PQC SECURE', color: 'var(--success)' }
    if (s === 'legacy')   return { label: 'LEGACY TLS', color: 'var(--warning)' }
    if (s === 'insecure') return { label: 'INSECURE',   color: 'var(--error)'   }
    return { label: 'UNKNOWN', color: 'var(--text-dim)' }
  }

  return (
    <div className="qr-desktop">
      <div className="qr-bg" />

      {/* ── Top Bar ── */}
      <div className="gd-topbar">
        <div className="gd-topbar-left">
          <Link to="/" className="gd-logo">QUANTUM SENTRY</Link>
        </div>
        <div className="gd-chips">
          {stats && [
            { label: 'TOTAL',   val: stats.total?.toLocaleString(),    color: 'var(--text-main)' },
            { label: 'SECURE',  val: stats.secure?.toLocaleString(),   color: 'var(--success)'   },
            { label: 'LEGACY',  val: stats.legacy?.toLocaleString(),   color: 'var(--warning)'   },
            { label: 'AT RISK', val: stats.insecure?.toLocaleString(), color: 'var(--error)'     },
            { label: 'PQC %',   val: pqcPct !== null ? `${pqcPct}%` : '…', color: pqcPct !== null && pqcPct > 50 ? 'var(--success)' : 'var(--warning)' },
          ].map(c => (
            <div key={c.label} className="gd-chip">
              <span className="gd-chip-val" style={{ color: c.color }}>{c.val ?? '…'}</span>
              <span className="gd-chip-label">{c.label}</span>
            </div>
          ))}
        </div>
        <div className="gd-topbar-right">
          <button
            className="gd-nav-btn"
            style={filtersOpen ? { borderColor: 'var(--accent-cyan)', color: 'var(--accent-cyan)' } : {}}
            onClick={() => setFiltersOpen(o => !o)}>
            ⚙ Filters{hasFilters ? ' ●' : ''}
          </button>
          <button className="gd-nav-btn" onClick={() => navigate('/')}>Globe</button>
          <button className="gd-nav-btn" onClick={() => navigate('/blog')}>Blog</button>
        </div>
      </div>

      {/* ── Left Filter Panel ── */}
      <div className={`gd-left ${filtersOpen ? 'open' : ''}`}>
        <div className="gd-panel-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>FILTERS</span>
          {hasFilters && (
            <button onClick={resetFilters} style={{ background: 'none', border: 'none', color: 'var(--error)', cursor: 'pointer', fontSize: '0.62rem', fontWeight: 700 }}>
              CLEAR ALL
            </button>
          )}
        </div>
        <div className="gd-panel-body">
          <div className="qr-filter-section">
            <div className="qr-filter-label">Search Hostname</div>
            <input
              className="qr-filter-input"
              placeholder="e.g. cloudflare.com"
              value={tableSearchInput}
              onChange={e => setTableSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { setPage(0); setTableSearch(tableSearchInput) } }}
            />
            <button className="qr-filter-apply-btn" onClick={() => { setPage(0); setTableSearch(tableSearchInput) }}>Apply</button>
          </div>
          <div className="qr-filter-section">
            <div className="qr-filter-label">Quantum Status</div>
            {(['', 'secure', 'legacy', 'insecure', 'unknown'] as const).map(s => {
              const labels: Record<string, string> = { '': 'All Statuses', secure: 'PQC Secure', legacy: 'Legacy', insecure: 'Insecure', unknown: 'Unknown' }
              const colors: Record<string, string> = { secure: 'var(--success)', legacy: 'var(--warning)', insecure: 'var(--error)' }
              return (
                <button
                  key={s}
                  className={`qr-filter-btn ${filterStatus === s ? 'active' : ''}`}
                  style={(filterStatus === s && s) ? { borderColor: colors[s], color: colors[s] } : {}}
                  onClick={() => { setFilterStatus(s); setPage(0) }}>
                  {s && <span className={`qr-dot ${dotClass(s)}`} style={{ flexShrink: 0 }} />}
                  {labels[s]}
                </button>
              )
            })}
          </div>
          <div className="qr-filter-section">
            <div className="qr-filter-label">Grade</div>
            <select className="qr-filter-select" value={filterGrade} onChange={e => { setFilterGrade(e.target.value); setPage(0) }}>
              <option value="">All grades</option>
              {['A+', 'A', 'B', 'C', 'F'].map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div className="qr-filter-section">
            <div className="qr-filter-label">Key Exchange</div>
            <select className="qr-filter-select" value={filterCipher} onChange={e => { setFilterCipher(e.target.value); setPage(0) }}>
              <option value="">All ciphers</option>
              {cipherOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="qr-filter-section">
            <div className="qr-filter-label">Country</div>
            <select className="qr-filter-select" value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setPage(0) }}>
              <option value="">All countries</option>
              {countryOptions.map(c => <option key={c} value={c}>{countryFlag(c)} {c}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* ── Main Table Area ── */}
      <div className={`qr-main ${filtersOpen ? 'filters-open' : ''} ${selectedDomain ? 'right-open' : ''}`}>
        {/* Count bar */}
        <div className="qr-count-bar">
          <span className="qr-count-text">
            {total > 0 ? (
              <>
                <span className="qr-count-range">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)}</span>
                {' of '}
                <span className="qr-count-total">{total.toLocaleString()}</span>
                <span className="qr-count-label"> nodes indexed</span>
              </>
            ) : <span className="qr-count-label">Loading…</span>}
          </span>
          {hasFilters && <span className="qr-filtered-badge">⚠ Filtered</span>}
        </div>

        {/* Table */}
        <table className="qr-table">
          <thead>
            <tr>
              <th style={{ width: 20 }}></th>
              <th>NODE / HOSTNAME</th>
              <th>GEO</th>
              <th>KEY EXCHANGE</th>
              <th>CERT SIG</th>
              <th>GRADE</th>
              <th>SCORE</th>
            </tr>
          </thead>
          <tbody>
            {domains.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-dim)' }}>
                  <div style={{ fontSize: '0.7rem', letterSpacing: '2px', opacity: 0.4 }}>NO NODES FOUND</div>
                </td>
              </tr>
            ) : domains.map((d, i) => {
              const score = registryScore(d)
              const ke = d.pqc_group_name ?? d.key_exchange ?? '—'
              const isPQC = ke.toLowerCase().includes('mlkem') || ke.toLowerCase().includes('kyber')
              const cc = d.asn_country ?? null
              const isSelected = selectedDomain?.hostname === d.hostname
              return (
                <tr
                  key={d.hostname}
                  className={`qr-row status-${d.pqc_status ?? 'unknown'}${isSelected ? ' selected' : ''}`}
                  style={{ animationDelay: `${Math.min(i * 15, 400)}ms` }}
                  onClick={() => setSelectedDomain(isSelected ? null : d)}>
                  <td><span className={`qr-dot ${dotClass(d.pqc_status)}`} /></td>
                  <td className="qr-hostname">{d.hostname}</td>
                  <td className="qr-geo">
                    {cc ? <span title={cc}>{countryFlag(cc)} {cc}</span> : <span style={{ color: 'var(--text-dim)' }}>—</span>}
                  </td>
                  <td>
                    <span className="qr-ke" style={{ color: isPQC ? 'var(--success)' : 'var(--text-dim)' }}>
                      {isPQC && <span className="qr-pqc-badge">PQC</span>}
                      {ke}
                    </span>
                  </td>
                  <td className="qr-sig">{d.sig_alg ?? '—'}</td>
                  <td>
                    <span className="qr-grade" style={{ color: gradeColor(d.overall_grade), textShadow: `0 0 10px ${gradeColor(d.overall_grade)}66` }}>
                      {d.overall_grade ?? '?'}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: score !== null ? gradeColor(d.overall_grade) : 'var(--text-dim)', fontWeight: 700 }}>
                      {score !== null ? score : '—'}
                    </span>
                    {score !== null && (
                      <span className="qr-score-bar">
                        <span className="qr-score-fill" style={{ width: `${score}%`, background: gradeColor(d.overall_grade) }} />
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="qr-pagination">
            <button className="qr-page-btn" onClick={() => setPage(0)}            disabled={page === 0}>«</button>
            <button className="qr-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹ Prev</button>
            <span className="qr-page-info">
              PAGE <span style={{ color: 'var(--accent-cyan)', fontWeight: 700 }}>{page + 1}</span> / {totalPages}
            </span>
            <button className="qr-page-btn" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next ›</button>
            <button className="qr-page-btn" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
          </div>
        )}
      </div>

      {/* ── Right Panel — Domain Detail ── */}
      <div className={`gd-right ${selectedDomain ? 'open' : ''}`}>
        {selectedDomain && (() => {
          const d = selectedDomain
          const score = registryScore(d)
          const ke = d.pqc_group_name ?? d.key_exchange ?? '—'
          const isPQC = ke.toLowerCase().includes('mlkem') || ke.toLowerCase().includes('kyber')
          const sm = statusMeta(d.pqc_status)
          const cc = d.asn_country ?? null
          return (
            <>
              <div className="gd-right-header">
                <div style={{ flex: 1, overflow: 'hidden' }}>
                  <div className="qr-domain-host">{d.hostname}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 3 }}>
                    <span className={`qr-dot ${dotClass(d.pqc_status)}`} />
                    <span style={{ fontSize: '0.62rem', color: sm.color, fontWeight: 700, letterSpacing: '1.2px' }}>{sm.label}</span>
                  </div>
                </div>
                <button className="gd-close-btn" onClick={() => setSelectedDomain(null)}>✕</button>
              </div>

              {/* Big grade */}
              <div className="qr-grade-display">
                <div className="qr-grade-big" style={{ color: gradeColor(d.overall_grade) }}>
                  {d.overall_grade ?? '?'}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '1.6rem', color: score !== null ? gradeColor(d.overall_grade) : 'var(--text-dim)' }}>
                    {score ?? '—'}<span style={{ fontSize: '0.7rem', color: 'var(--text-dim)', fontWeight: 400 }}>/100</span>
                  </div>
                  <div style={{ fontSize: '0.6rem', color: 'var(--text-dim)', letterSpacing: '1px', textTransform: 'uppercase', marginTop: 2 }}>Quantum Security Score</div>
                  {score !== null && (
                    <div style={{ marginTop: 8, height: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${score}%`, background: gradeColor(d.overall_grade), borderRadius: 2, boxShadow: `0 0 8px ${gradeColor(d.overall_grade)}` }} />
                    </div>
                  )}
                </div>
              </div>

              <div className="qr-detail-section">
                <div className="qr-detail-label">Key Exchange</div>
                <div className="qr-detail-value" style={{ color: isPQC ? 'var(--success)' : 'var(--text-main)' }}>
                  {isPQC && <span className="qr-pqc-badge" style={{ marginRight: 7 }}>PQC</span>}
                  {ke}
                </div>
              </div>

              {d.sig_alg && (
                <div className="qr-detail-section">
                  <div className="qr-detail-label">Certificate Signature</div>
                  <div className="qr-detail-value" style={{ color: 'var(--text-dim)' }}>{d.sig_alg}</div>
                </div>
              )}

              {d.pubkey_alg && (
                <div className="qr-detail-section">
                  <div className="qr-detail-label">Public Key</div>
                  <div className="qr-detail-value" style={{ color: 'var(--text-dim)' }}>
                    {d.pubkey_alg}{d.pubkey_size ? ` (${d.pubkey_size}-bit)` : ''}
                  </div>
                </div>
              )}

              {cc && (
                <div className="qr-detail-section">
                  <div className="qr-detail-label">Origin Country</div>
                  <div className="qr-detail-value">{countryFlag(cc)} {cc}</div>
                </div>
              )}

              <div className="qr-detail-section">
                <div className="qr-detail-label">TLS 1.3</div>
                <div className="qr-detail-value" style={{ color: d.tls_1_3 ? 'var(--success)' : 'var(--warning)' }}>
                  {d.tls_1_3 ? '✓ Supported' : '✗ Not detected'}
                </div>
              </div>

              {d.scan_violations !== null && d.scan_violations !== undefined && (
                <div className="qr-detail-section">
                  <div className="qr-detail-label">Security Violations</div>
                  <div className="qr-detail-value" style={{ color: d.scan_violations > 0 ? 'var(--warning)' : 'var(--success)' }}>
                    {d.scan_violations === 0 ? '✓ None detected' : `⚠ ${d.scan_violations} violation${d.scan_violations !== 1 ? 's' : ''}`}
                  </div>
                </div>
              )}

              <div style={{ padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Link to={`/report/${d.hostname}`} className="gd-explore-btn" style={{ textDecoration: 'none', textAlign: 'center', display: 'block' }}>
                  Full Report →
                </Link>
                <Link
                  to={`/company/${d.hostname.split('.').slice(-2).join('.')}`}
                  className="gd-explore-btn"
                  style={{ textDecoration: 'none', textAlign: 'center', display: 'block', borderColor: 'var(--accent-purple)', color: 'var(--accent-purple)' }}>
                  Company View →
                </Link>
              </div>
            </>
          )
        })()}
      </div>

      {/* ── Footer ── */}
      <div className="gd-footer">
        <QDayCountdown compact />
        <div className="gd-search">
          <input
            className="gd-search-input"
            placeholder="domain.com — probe & analyze"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
          />
          <button className="gd-search-btn" onClick={handleSearch}>SCAN</button>
        </div>
        <div className="gd-footer-links">
          <button className="gd-footer-nav" onClick={() => navigate('/')}>Globe</button>
          <button className="gd-footer-nav" onClick={() => navigate('/blog')}>Blog</button>
        </div>
      </div>
    </div>
  )
}

// ─── Globe Desktop — OS-style home experience ──────────────────────────────────

// Globe camera targets per country: [lat, lng, altitude, mapScale]
const COUNTRY_POV: Record<string, [number, number, number, number]> = {
  US:[37,-96,2.5,600],  CA:[57,-96,2.5,500],  MX:[24,-102,3.0,1100],
  GB:[54,-2,3.5,3800],  FR:[46,2,3.5,2800],   DE:[51,10,3.5,3000],
  JP:[36,138,3.5,2200], CN:[36,104,2.5,700],   AU:[-25,134,2.5,550],
  IN:[21,79,2.8,800],   BR:[-10,-53,2.5,500],  RU:[62,90,2.0,350],
  ZA:[-29,25,3.0,1200], KR:[36,128,4.0,3000],  SG:[1.4,103.8,5.5,40000],
  NL:[52,5,4.0,5000],   CH:[47,8,4.5,5500],    SE:[62,16,3.5,1800],
  NO:[65,18,3.0,1800],  FI:[65,26,3.5,2000],   DK:[56,10,4.5,5000],
  BE:[51,4,5.0,7000],   IT:[42,12,3.0,2000],   ES:[40,-4,3.5,2400],
  PL:[52,19,3.5,2500],  UA:[49,31,3.5,2000],   TR:[39,35,3.0,1500],
  SA:[24,45,3.0,1300],  AE:[24,54,4.5,6000],   IL:[32,35,5.0,6000],
  TW:[24,121,5.0,6000], HK:[22,114,6.0,20000], ID:[-1,114,3.0,900],
  MY:[4,108,4.0,2500],  TH:[16,101,3.5,2000],  VN:[14,108,3.5,2000],
  PH:[13,122,3.5,2200], PK:[30,70,3.0,1100],   NG:[9,8,3.5,1600],
  EG:[27,30,3.5,2000],  AR:[-34,-64,2.8,900],  GR:[39,22,4.0,3500],
  PT:[39,-8,4.5,4000],  IE:[53,-8,4.5,4500],   AT:[47,14,4.5,5000],
  CZ:[50,16,4.5,5000],  HU:[47,19,4.5,5000],   RO:[46,25,4.0,3500],
  BG:[43,25,4.5,4000],  RS:[44,21,5.0,5000],
}

// US state centroids: abbr → { name, lat, lng }
const US_STATES: Record<string, { name: string; lat: number; lng: number }> = {
  AL:{name:'Alabama',lat:32.8,lng:-86.8},        AK:{name:'Alaska',lat:64.2,lng:-153.4},
  AZ:{name:'Arizona',lat:34.3,lng:-111.9},       AR:{name:'Arkansas',lat:35.2,lng:-91.8},
  CA:{name:'California',lat:36.8,lng:-119.4},    CO:{name:'Colorado',lat:39.0,lng:-105.5},
  CT:{name:'Connecticut',lat:41.6,lng:-72.7},    DE:{name:'Delaware',lat:38.9,lng:-75.5},
  FL:{name:'Florida',lat:28.6,lng:-82.4},        GA:{name:'Georgia',lat:32.7,lng:-83.4},
  HI:{name:'Hawaii',lat:20.8,lng:-156.3},        ID:{name:'Idaho',lat:44.4,lng:-114.0},
  IL:{name:'Illinois',lat:40.0,lng:-89.2},       IN:{name:'Indiana',lat:40.3,lng:-86.1},
  IA:{name:'Iowa',lat:42.0,lng:-93.2},           KS:{name:'Kansas',lat:38.5,lng:-98.3},
  KY:{name:'Kentucky',lat:37.7,lng:-84.9},       LA:{name:'Louisiana',lat:31.2,lng:-91.8},
  ME:{name:'Maine',lat:45.3,lng:-69.4},          MD:{name:'Maryland',lat:39.1,lng:-76.7},
  MA:{name:'Massachusetts',lat:42.4,lng:-71.7},  MI:{name:'Michigan',lat:43.3,lng:-84.7},
  MN:{name:'Minnesota',lat:46.4,lng:-93.1},      MS:{name:'Mississippi',lat:32.7,lng:-89.7},
  MO:{name:'Missouri',lat:38.5,lng:-92.4},       MT:{name:'Montana',lat:46.9,lng:-110.4},
  NE:{name:'Nebraska',lat:41.5,lng:-99.9},       NV:{name:'Nevada',lat:39.3,lng:-116.6},
  NH:{name:'New Hampshire',lat:43.7,lng:-71.6},  NJ:{name:'New Jersey',lat:40.2,lng:-74.7},
  NM:{name:'New Mexico',lat:34.5,lng:-106.1},    NY:{name:'New York',lat:42.9,lng:-75.6},
  NC:{name:'North Carolina',lat:35.6,lng:-79.4}, ND:{name:'North Dakota',lat:47.4,lng:-100.5},
  OH:{name:'Ohio',lat:40.4,lng:-82.8},           OK:{name:'Oklahoma',lat:35.6,lng:-96.9},
  OR:{name:'Oregon',lat:43.9,lng:-120.6},        PA:{name:'Pennsylvania',lat:41.0,lng:-77.7},
  RI:{name:'Rhode Island',lat:41.7,lng:-71.5},   SC:{name:'South Carolina',lat:33.9,lng:-80.9},
  SD:{name:'South Dakota',lat:44.4,lng:-100.2},  TN:{name:'Tennessee',lat:35.9,lng:-86.7},
  TX:{name:'Texas',lat:31.1,lng:-100.0},         UT:{name:'Utah',lat:39.3,lng:-111.1},
  VT:{name:'Vermont',lat:44.1,lng:-72.6},        VA:{name:'Virginia',lat:37.8,lng:-78.2},
  WA:{name:'Washington',lat:47.4,lng:-120.3},    WV:{name:'West Virginia',lat:38.6,lng:-80.6},
  WI:{name:'Wisconsin',lat:44.2,lng:-89.8},      WY:{name:'Wyoming',lat:43.0,lng:-107.5},
}

// FIPS code → state abbreviation
const FIPS_TO_ABBR: Record<string, string> = {
  '01':'AL','02':'AK','04':'AZ','05':'AR','06':'CA','08':'CO','09':'CT','10':'DE',
  '12':'FL','13':'GA','15':'HI','16':'ID','17':'IL','18':'IN','19':'IA','20':'KS',
  '21':'KY','22':'LA','23':'ME','24':'MD','25':'MA','26':'MI','27':'MN','28':'MS',
  '29':'MO','30':'MT','31':'NE','32':'NV','33':'NH','34':'NJ','35':'NM','36':'NY',
  '37':'NC','38':'ND','39':'OH','40':'OK','41':'OR','42':'PA','44':'RI','45':'SC',
  '46':'SD','47':'TN','48':'TX','49':'UT','50':'VT','51':'VA','53':'WA','54':'WV',
  '55':'WI','56':'WY',
}

// Deterministic domain → US state hash
function domainStateHash(hostname: string): string {
  let h = 0
  for (let i = 0; i < hostname.length; i++) h = (Math.imul(31, h) + hostname.charCodeAt(i)) | 0
  const keys = Object.keys(US_STATES)
  return keys[Math.abs(h) % keys.length]
}

function stateStatusColors(domains: Domain[]) {
  if (!domains.length) return { fill: '#0c1220', stroke: '#1c2030' }
  const s = domains.filter(d => d.pqc_status === 'secure').length
  const l = domains.filter(d => d.pqc_status === 'legacy').length
  const ins = domains.filter(d => d.pqc_status === 'insecure').length
  if (s > l && s > ins) return { fill: 'rgba(0,255,136,0.18)',  stroke: 'rgba(0,255,136,0.55)' }
  if (l > ins)           return { fill: 'rgba(255,204,0,0.18)',  stroke: 'rgba(255,204,0,0.55)'  }
  if (ins > 0)           return { fill: 'rgba(255,77,77,0.18)',   stroke: 'rgba(255,77,77,0.55)'  }
  return { fill: '#0c1220', stroke: '#1c2030' }
}

// SVG isometric 3D bar at a map marker position
function IsoBar({ count, maxCount, color, onClick, delay = 0 }: {
  count: number; maxCount: number; color: string; onClick: () => void; delay?: number
}) {
  const maxH = 55
  const barH = maxCount > 0 ? Math.max(5, Math.round((count / maxCount) * maxH)) : 5
  const w = 14, ox = 7, oy = 4
  return (
    <g onClick={onClick} className="iso-bar" style={{ cursor: 'pointer', animationDelay: `${delay}ms` }}>
      {/* Front face */}
      <rect x={-w/2} y={-barH} width={w} height={barH} fill={color} opacity={0.88} />
      {/* Top face */}
      <polygon points={`${-w/2},${-barH} ${-w/2+ox},${-barH-oy} ${w/2+ox},${-barH-oy} ${w/2},${-barH}`} fill={color} opacity={1} />
      {/* Right side */}
      <polygon points={`${w/2},${-barH} ${w/2+ox},${-barH-oy} ${w/2+ox},${-oy} ${w/2},0`} fill={color} opacity={0.5} />
      {count > 0 && (
        <text y={-barH - oy - 4} textAnchor="middle" style={{ fontSize: '7px', fill: color, fontWeight: 700, fontFamily: 'monospace', pointerEvents: 'none' }}>{count}</text>
      )}
    </g>
  )
}

// ── Top Menu Bar ──────────────────────────────────────────────────────────────
// ── Blog Slideover (left panel) ───────────────────────────────────────────────

function BlogSlideover({ open, onClose, onSelectPost }: {
  open: boolean; onClose: () => void; onSelectPost: (slug: string) => void
}) {
  return (
    <div className={`blog-slideover${open ? ' open' : ''}`}>
      <div className="gd-panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px' }}>
        <span style={{ letterSpacing: '1px', fontSize: '0.6rem', color: 'var(--accent-cyan)' }}>QUANTUM INSIGHTS</span>
        <button className="gd-close-btn" onClick={onClose} style={{ fontSize: '0.65rem', padding: '2px 7px' }}>✕</button>
      </div>
      <div className="gd-panel-body" style={{ overflowY: 'auto', flex: 1 }}>
        {POSTS.map(post => (
          <div key={post.slug} className="blog-slide-item" onClick={() => onSelectPost(post.slug)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: '0.58rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: post.tagColor }}>{post.tag}</span>
              <span style={{ fontSize: '0.6rem', color: 'var(--text-dim)' }}>{post.date}</span>
            </div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-primary)', lineHeight: 1.4, marginBottom: 4 }}>{post.title}</div>
            <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)', lineHeight: 1.5 }}>{post.summary.slice(0, 90)}…</div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Blog Article Window (centered overlay) ────────────────────────────────────

function BlogArticleWindow({ slug, onClose }: { slug: string | null; onClose: () => void }) {
  const post = POSTS.find(p => p.slug === slug)
  if (!slug) return null
  return (
    <div className={`blog-article-backdrop${slug ? ' open' : ''}`} onClick={onClose}>
      <div className="blog-article-window" onClick={e => e.stopPropagation()}>
        <div className="blog-article-header">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span style={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: post?.tagColor, border: `1px solid ${post?.tagColor}`, padding: '2px 8px', borderRadius: 20 }}>{post?.tag}</span>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>{post?.date}</span>
          </div>
          <button className="gd-close-btn" onClick={onClose}>✕ Close</button>
        </div>
        <div className="blog-article-body">
          {!post ? (
            <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-dim)' }}>Article not found.</div>
          ) : (
            <>
              <h1 style={{ fontSize: '1.6rem', lineHeight: 1.3, margin: '0 0 1rem', color: 'var(--text-primary)' }}>{post.title}</h1>
              <p style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', lineHeight: 1.7, borderLeft: '3px solid var(--accent-blue)', paddingLeft: '1rem', margin: '0 0 2rem' }}>{post.summary}</p>
              {post.body.map((section, i) => (
                <div key={i} style={{ marginBottom: '1.75rem' }}>
                  <h2 style={{ fontSize: '1.05rem', color: 'var(--accent-blue)', margin: '0 0 0.6rem' }}>{section.heading}</h2>
                  {section.text.split('\n\n').map((para, j) => (
                    <p key={j} style={{ margin: '0 0 0.85rem', lineHeight: 1.8, color: 'var(--text-secondary)', whiteSpace: 'pre-line', fontSize: '0.88rem' }}>{para}</p>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Sector Reports ────────────────────────────────────────────────────────────

interface TldGroup { label: string; tld: string; emoji: string; extraOrgs?: string[] }
interface OrgGroup { label: string; apex: string; extra?: string[]; emoji?: string }
interface SectorPack {
  label: string; emoji: string; description: string
  type?: 'domains' | 'tld-groups' | 'org-groups'
  domains?: string[]
  tldGroups?: TldGroup[]
  orgGroups?: OrgGroup[]
}

const SECTOR_PACKS: Record<string, SectorPack> = {
  universities: {
    label: 'US Universities', emoji: '🎓',
    description: 'Top research universities — all subdomains included. Who\'s quantum-ready?',
    type: 'org-groups',
    orgGroups: [
      { label: 'Harvard',        apex: 'harvard.edu' },
      { label: 'MIT',            apex: 'mit.edu' },
      { label: 'Stanford',       apex: 'stanford.edu' },
      { label: 'Yale',           apex: 'yale.edu' },
      { label: 'Princeton',      apex: 'princeton.edu' },
      { label: 'Columbia',       apex: 'columbia.edu' },
      { label: 'UPenn',          apex: 'upenn.edu' },
      { label: 'Cornell',        apex: 'cornell.edu' },
      { label: 'Dartmouth',      apex: 'dartmouth.edu' },
      { label: 'Brown',          apex: 'brown.edu' },
      { label: 'Caltech',        apex: 'caltech.edu' },
      { label: 'U Chicago',      apex: 'uchicago.edu' },
      { label: 'Duke',           apex: 'duke.edu' },
      { label: 'Northwestern',   apex: 'northwestern.edu' },
      { label: 'Vanderbilt',     apex: 'vanderbilt.edu' },
      { label: 'Rice',           apex: 'rice.edu' },
      { label: 'Notre Dame',     apex: 'nd.edu' },
      { label: 'Georgetown',     apex: 'georgetown.edu' },
      { label: 'Emory',          apex: 'emory.edu' },
      { label: 'CMU',            apex: 'cmu.edu' },
      { label: 'U Michigan',     apex: 'umich.edu' },
      { label: 'UC Berkeley',    apex: 'berkeley.edu' },
      { label: 'UCLA',           apex: 'ucla.edu' },
      { label: 'USC',            apex: 'usc.edu' },
      { label: 'NYU',            apex: 'nyu.edu' },
      { label: 'Boston U',       apex: 'bu.edu' },
      { label: 'Tufts',          apex: 'tufts.edu' },
      { label: 'Purdue',         apex: 'purdue.edu' },
      { label: 'Georgia Tech',   apex: 'gatech.edu' },
      { label: 'U Virginia',     apex: 'virginia.edu' },
    ],
  },
  cybersecurity: {
    label: 'Cybersecurity Companies', emoji: '🛡️',
    description: 'Are the companies protecting the web protecting themselves?',
    type: 'org-groups',
    orgGroups: [
      { label: 'CrowdStrike',         apex: 'crowdstrike.com' },
      { label: 'Palo Alto Networks',  apex: 'paloaltonetworks.com' },
      { label: 'Fortinet',            apex: 'fortinet.com' },
      { label: 'Check Point',         apex: 'checkpoint.com' },
      { label: 'Zscaler',             apex: 'zscaler.com' },
      { label: 'SentinelOne',         apex: 'sentinelone.com' },
      { label: 'Darktrace',           apex: 'darktrace.com' },
      { label: 'Qualys',              apex: 'qualys.com' },
      { label: 'Tenable',             apex: 'tenable.com' },
      { label: 'Rapid7',              apex: 'rapid7.com' },
      { label: 'SailPoint',           apex: 'sailpoint.com' },
      { label: 'CyberArk',            apex: 'cyberark.com' },
      { label: 'Proofpoint',          apex: 'proofpoint.com' },
      { label: 'Mimecast',            apex: 'mimecast.com' },
      { label: 'Varonis',             apex: 'varonis.com' },
      { label: 'Illumio',             apex: 'illumio.com' },
      { label: 'Wiz',                 apex: 'wiz.io' },
      { label: 'Snyk',                apex: 'snyk.io' },
      { label: 'Lacework',            apex: 'lacework.com' },
      { label: 'Vectra AI',           apex: 'vectra.ai' },
      { label: 'Rubrik',              apex: 'rubrik.com' },
      { label: 'Abnormal Security',   apex: 'abnormalsecurity.com' },
      { label: 'Exabeam',             apex: 'exabeam.com' },
      { label: 'Secureworks',         apex: 'secureworks.com' },
    ],
  },
  dod: {
    label: 'Dept. of Defense', emoji: '⚔️',
    description: 'Quantum posture across every scanned subdomain — broken down by military branch and agency',
    type: 'tld-groups',
    tldGroups: [
      { label: 'Air Force',     tld: '.af.mil',        emoji: '✈️' },
      { label: 'Army',          tld: '.army.mil',       emoji: '🪖' },
      { label: 'Navy',          tld: '.navy.mil',       emoji: '⚓' },
      { label: 'Marine Corps',  tld: '.marines.mil',    emoji: '🦅', extraOrgs: ['usmc.mil', 'marines.mil'] },
      { label: 'Coast Guard',   tld: '.uscg.mil',       emoji: '🛟' },
      { label: 'Space Force',   tld: '.spaceforce.mil', emoji: '🚀' },
      { label: 'Cyber Command', tld: '.cybercom.mil',   emoji: '💻' },
      { label: 'DARPA',         tld: '.darpa.mil',      emoji: '🔬' },
      { label: 'DIA',           tld: '.dia.mil',        emoji: '🔍' },
      { label: 'DISA',          tld: '.disa.mil',       emoji: '🛰️' },
      { label: 'NGA',           tld: '.nga.mil',        emoji: '🗺️' },
      { label: 'NSA / CSS',     tld: '.nsa.gov',        emoji: '🕵️' },
      { label: 'OSD / DOD',     tld: '.defense.gov',    emoji: '🏛️' },
    ],
  },
  usgov: {
    label: 'US Government', emoji: '🏛️',
    description: 'Federal agencies and departments — every scanned subdomain counts',
    type: 'org-groups',
    orgGroups: [
      { label: 'White House',         apex: 'whitehouse.gov' },
      { label: 'State Dept.',         apex: 'state.gov' },
      { label: 'Treasury',            apex: 'treasury.gov' },
      { label: 'DOJ',                 apex: 'justice.gov' },
      { label: 'DHS',                 apex: 'dhs.gov' },
      { label: 'HHS',                 apex: 'hhs.gov' },
      { label: 'Commerce',            apex: 'commerce.gov' },
      { label: 'Dept. of Energy',     apex: 'energy.gov' },
      { label: 'Transportation',      apex: 'transportation.gov' },
      { label: 'Labor',               apex: 'labor.gov' },
      { label: 'USDA',                apex: 'usda.gov' },
      { label: 'Interior',            apex: 'interior.gov' },
      { label: 'Education',           apex: 'ed.gov' },
      { label: 'VA',                  apex: 'va.gov' },
      { label: 'CIA',                 apex: 'cia.gov' },
      { label: 'FBI',                 apex: 'fbi.gov' },
      { label: 'CISA',                apex: 'cisa.gov' },
      { label: 'NIST',                apex: 'nist.gov' },
      { label: 'Federal Reserve',     apex: 'federalreserve.gov' },
      { label: 'SEC',                 apex: 'sec.gov' },
      { label: 'NASA',                apex: 'nasa.gov' },
      { label: 'EPA',                 apex: 'epa.gov' },
      { label: 'FCC',                 apex: 'fcc.gov' },
      { label: 'FTC',                 apex: 'ftc.gov' },
      { label: 'IRS',                 apex: 'irs.gov' },
    ],
  },
  bigtech: {
    label: 'Big Tech', emoji: '💻',
    description: 'The platforms running the internet — all their properties graded',
    type: 'org-groups',
    orgGroups: [
      { label: 'Google',      apex: 'google.com',      extra: ['googleapis.com','googlevideo.com'] },
      { label: 'Microsoft',   apex: 'microsoft.com',   extra: ['azure.com','live.com'] },
      { label: 'Apple',       apex: 'apple.com',       extra: ['icloud.com'] },
      { label: 'Amazon',      apex: 'amazon.com',      extra: ['aws.amazon.com'] },
      { label: 'Meta',        apex: 'meta.com',        extra: ['facebook.com','instagram.com'] },
      { label: 'Netflix',     apex: 'netflix.com' },
      { label: 'X / Twitter', apex: 'x.com',           extra: ['t.co'] },
      { label: 'LinkedIn',    apex: 'linkedin.com' },
      { label: 'Salesforce',  apex: 'salesforce.com',  extra: ['force.com'] },
      { label: 'Oracle',      apex: 'oracle.com' },
      { label: 'IBM',         apex: 'ibm.com' },
      { label: 'Intel',       apex: 'intel.com' },
      { label: 'NVIDIA',      apex: 'nvidia.com' },
      { label: 'AMD',         apex: 'amd.com' },
      { label: 'Cloudflare',  apex: 'cloudflare.com' },
      { label: 'Fastly',      apex: 'fastly.com' },
      { label: 'Akamai',      apex: 'akamai.com' },
      { label: 'GitHub',      apex: 'github.com' },
      { label: 'GitLab',      apex: 'gitlab.com' },
      { label: 'Stripe',      apex: 'stripe.com' },
      { label: 'Twilio',      apex: 'twilio.com' },
      { label: 'Shopify',     apex: 'shopify.com' },
      { label: 'Atlassian',   apex: 'atlassian.com' },
      { label: 'Slack',       apex: 'slack.com' },
    ],
  },
  finance: {
    label: 'Financial Sector', emoji: '🏦',
    description: 'Banks, exchanges, and financial infrastructure — every domain they touch',
    type: 'org-groups',
    orgGroups: [
      { label: 'JPMorgan Chase',    apex: 'jpmorgan.com',          extra: ['jpmorganchase.com','chase.com'] },
      { label: 'Bank of America',   apex: 'bankofamerica.com' },
      { label: 'Wells Fargo',       apex: 'wellsfargo.com' },
      { label: 'Citigroup',         apex: 'citi.com',              extra: ['citibank.com'] },
      { label: 'Goldman Sachs',     apex: 'goldmansachs.com',      extra: ['gs.com'] },
      { label: 'Morgan Stanley',    apex: 'morganstanley.com' },
      { label: 'BlackRock',         apex: 'blackrock.com' },
      { label: 'Vanguard',          apex: 'vanguard.com' },
      { label: 'Fidelity',          apex: 'fidelity.com' },
      { label: 'Schwab',            apex: 'schwab.com' },
      { label: 'Visa',              apex: 'visa.com' },
      { label: 'Mastercard',        apex: 'mastercard.com' },
      { label: 'American Express',  apex: 'americanexpress.com',   extra: ['amex.com'] },
      { label: 'PayPal',            apex: 'paypal.com',            extra: ['venmo.com'] },
      { label: 'Coinbase',          apex: 'coinbase.com' },
      { label: 'Bloomberg',         apex: 'bloomberg.com' },
      { label: 'Nasdaq',            apex: 'nasdaq.com' },
      { label: 'NYSE',              apex: 'nyse.com' },
      { label: 'SWIFT',             apex: 'swift.com' },
      { label: 'Federal Reserve',   apex: 'federalreserve.gov' },
    ],
  },
}

const GRADE_ORDER: Record<string, number> = { 'A+':0,'A':1,'A-':2,'B+':3,'B':4,'B-':5,'C':6,'D':7,'F':8,'':9 }

function DomainRow({ hostname, db, onOpenReport }: { hostname: string; db: any; onOpenReport: (h: string) => void }) {
  const statusColor = db?.pqc_status === 'secure' ? 'var(--success)'
    : db?.pqc_status === 'legacy' ? 'var(--warning)' : db ? 'var(--error)' : 'var(--text-dim)'
  const grade = db?.overall_grade ?? null
  const gradeColor = grade?.startsWith('A') ? 'var(--success)'
    : grade?.startsWith('B') ? 'var(--warning)' : grade ? 'var(--error)' : 'var(--text-dim)'
  return (
    <tr className="sector-row" onClick={() => db && onOpenReport(hostname)}>
      <td className="sector-hostname">
        <span className="sector-dot" style={{ background: statusColor }} />
        {hostname}
        {!db && <span className="sector-unscanned">not scanned</span>}
      </td>
      <td>
        {grade
          ? <span className="sector-grade" style={{ color: gradeColor, borderColor: gradeColor + '55' }}>{grade}</span>
          : <span style={{ color:'var(--text-dim)', fontSize:'0.7rem' }}>—</span>}
      </td>
      <td style={{ color: statusColor, fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.06em' }}>
        {db?.pqc_status?.toUpperCase() ?? '—'}
      </td>
      <td className="sector-mono">{db?.pqc_group_name ?? db?.key_exchange ?? '—'}</td>
      <td style={{ fontSize:'0.7rem', color: db?.tls_1_3 ? 'var(--success)' : 'var(--text-dim)' }}>
        {db ? (db.tls_1_3 ? 'TLS 1.3' : 'TLS 1.2') : '—'}
      </td>
      <td className="sector-mono" style={{ color:'var(--text-dim)' }}>
        {db?.last_updated ? new Date(db.last_updated).toLocaleDateString() : '—'}
      </td>
    </tr>
  )
}

function SectorReports({ open, onClose, onOpenReport }: {
  open: boolean; onClose: () => void; onOpenReport: (hostname: string) => void
}) {
  const [activePack, setActivePack]       = useState<string>('universities')
  const [dbRows, setDbRows]               = useState<Record<string, any>>({})
  // tld-groups mode: map tld → array of domain rows
  const [tldData, setTldData]             = useState<Record<string, any[]>>({})
  const [expandedBranch, setExpandedBranch] = useState<string | null>(null)
  const [loading, setLoading]             = useState(false)

  // org-groups mode: map apex → array of domain rows
  const [orgData, setOrgData] = useState<Record<string, any[]>>({})

  const pack = SECTOR_PACKS[activePack]
  const isTldMode = pack.type === 'tld-groups'
  const isOrgMode = pack.type === 'org-groups'

  // Reset expanded branch when switching packs
  useEffect(() => { setExpandedBranch(null) }, [activePack])

  // ── domains mode ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || isTldMode || isOrgMode) return
    setLoading(true)
    const domainList = (pack.domains ?? []).join(',')
    fetch(`${API_BASE}/v1/list?domains=${encodeURIComponent(domainList)}&limit=100`)
      .then(r => r.json())
      .then((d: any) => {
        const map: Record<string, any> = {}
        for (const row of d.results ?? []) map[row.hostname] = row
        setDbRows(map)
      })
      .catch(() => setDbRows({}))
      .finally(() => setLoading(false))
  }, [activePack, open, isTldMode, isOrgMode]) // eslint-disable-line

  // ── tld-groups mode ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !isTldMode) return
    setLoading(true)
    setTldData({})
    const groups = pack.tldGroups ?? []
    Promise.all(
      groups.map(g => {
        const fetches: Promise<any[]>[] = [
          fetch(`${API_BASE}/v1/list?tld=${encodeURIComponent(g.tld)}&limit=200`)
            .then(r => r.json()).then((d: any) => d.results ?? []).catch(() => []),
          ...(g.extraOrgs ?? []).map(org =>
            fetch(`${API_BASE}/v1/list?org=${encodeURIComponent(org)}&limit=200`)
              .then(r => r.json()).then((d: any) => d.results ?? []).catch(() => [])
          ),
        ]
        return Promise.all(fetches).then(allRows => {
          const seen = new Map<string, any>()
          for (const rows of allRows) for (const row of rows) seen.set(row.hostname, row)
          return { tld: g.tld, rows: Array.from(seen.values()) }
        })
      })
    ).then(results => {
      const map: Record<string, any[]> = {}
      for (const { tld, rows } of results) map[tld] = rows
      setTldData(map)
    }).finally(() => setLoading(false))
  }, [activePack, open, isTldMode]) // eslint-disable-line

  // ── org-groups mode ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!open || !isOrgMode) return
    setLoading(true)
    setOrgData({})
    const groups = pack.orgGroups ?? []
    Promise.all(
      groups.map(g => {
        const apexes = [g.apex, ...(g.extra ?? [])]
        return Promise.all(
          apexes.map(apex =>
            fetch(`${API_BASE}/v1/list?org=${encodeURIComponent(apex)}&limit=200`)
              .then(r => r.json())
              .then((d: any) => d.results ?? [])
              .catch(() => [])
          )
        ).then(results => {
          // Merge + deduplicate across all apex domains
          const seen = new Map<string, any>()
          for (const rows of results) for (const row of rows) seen.set(row.hostname, row)
          return { apex: g.apex, rows: Array.from(seen.values()) }
        })
      })
    ).then(results => {
      const map: Record<string, any[]> = {}
      for (const { apex, rows } of results) map[apex] = rows
      setOrgData(map)
    }).finally(() => setLoading(false))
  }, [activePack, open, isOrgMode]) // eslint-disable-line

  // ── domains mode derived data ──────────────────────────────────────────────
  const domainRows = (pack.domains ?? [])
    .map(hostname => ({ hostname, db: dbRows[hostname] ?? null }))
    .sort((a, b) => {
      const ga = GRADE_ORDER[a.db?.overall_grade ?? ''] ?? 9
      const gb = GRADE_ORDER[b.db?.overall_grade ?? ''] ?? 9
      if (ga !== gb) return ga - gb
      return a.hostname.localeCompare(b.hostname)
    })
  const scanned = domainRows.filter(r => r.db).length
  const secure  = domainRows.filter(r => r.db?.pqc_status === 'secure').length
  const pct     = scanned > 0 ? Math.round((secure / scanned) * 100) : 0

  // ── tld-groups mode derived data ───────────────────────────────────────────
  // Aggregate stats per branch
  const branchStats = (pack.tldGroups ?? []).map(g => {
    const rows: any[] = tldData[g.tld] ?? []
    const total    = rows.length
    const nsecure  = rows.filter(r => r.pqc_status === 'secure').length
    const nlegacy  = rows.filter(r => r.pqc_status === 'legacy').length
    const ninsec   = rows.filter(r => r.pqc_status === 'insecure').length
    const grades: Record<string, number> = {}
    for (const r of rows) { const g = r.overall_grade || '?'; grades[g] = (grades[g] ?? 0) + 1 }
    const bestGrade = rows.length > 0
      ? rows.reduce((best, r) => {
          const go = GRADE_ORDER[r.overall_grade ?? ''] ?? 9
          const bo = GRADE_ORDER[best ?? ''] ?? 9
          return go < bo ? r.overall_grade : best
        }, null as string | null)
      : null
    return { ...g, total, secure: nsecure, legacy: nlegacy, insecure: ninsec, grades, bestGrade,
      pct: total > 0 ? Math.round((nsecure / total) * 100) : 0 }
  }).sort((a, b) => b.total - a.total)

  const totalScannedBranches = branchStats.reduce((s, b) => s + b.total, 0)
  const totalSecureBranches  = branchStats.reduce((s, b) => s + b.secure, 0)
  const overallPct = totalScannedBranches > 0 ? Math.round((totalSecureBranches / totalScannedBranches) * 100) : 0

  // ── org-groups mode derived data ──────────────────────────────────────────
  function orgGroupStats(g: OrgGroup) {
    const rows: any[] = orgData[g.apex] ?? []
    const total    = rows.length
    const nsecure  = rows.filter(r => r.pqc_status === 'secure').length
    const nlegacy  = rows.filter(r => r.pqc_status === 'legacy').length
    const ninsec   = rows.filter(r => r.pqc_status === 'insecure').length
    const bestGrade = rows.length > 0
      ? rows.reduce((best, r) => {
          const go = GRADE_ORDER[r.overall_grade ?? ''] ?? 9
          const bo = GRADE_ORDER[best ?? ''] ?? 9
          return go < bo ? r.overall_grade : best
        }, null as string | null)
      : null
    return { ...g, total, secure: nsecure, legacy: nlegacy, insecure: ninsec, bestGrade,
      pct: total > 0 ? Math.round((nsecure / total) * 100) : 0 }
  }
  const orgStats = (pack.orgGroups ?? []).map(orgGroupStats).sort((a, b) => b.total - a.total)
  const totalScannedOrgs = orgStats.reduce((s, o) => s + o.total, 0)
  const totalSecureOrgs  = orgStats.reduce((s, o) => s + o.secure, 0)
  const orgOverallPct    = totalScannedOrgs > 0 ? Math.round((totalSecureOrgs / totalScannedOrgs) * 100) : 0

  return (
    <div className={`sector-overlay${open ? ' open' : ''}`}>
      {/* Header */}
      <div className="sector-header">
        <div className="sector-tabs">
          {Object.entries(SECTOR_PACKS).map(([key, p]) => (
            <button key={key}
              className={`sector-tab${activePack === key ? ' active' : ''}`}
              onClick={() => setActivePack(key)}>
              {p.emoji} {p.label}
            </button>
          ))}
        </div>
        <button className="gd-close-btn" onClick={onClose}>✕ Close</button>
      </div>

      {/* Sub-header */}
      <div className="sector-subheader">
        <div>
          <span className="sector-desc">{pack.description}</span>
          {isTldMode ? (
            <>
              <span className="sector-stat">{totalScannedBranches} sites across {branchStats.filter(b => b.total > 0).length} branches</span>
              {totalScannedBranches > 0 && <span className="sector-stat" style={{ color:'var(--success)' }}>{overallPct}% quantum-ready</span>}
            </>
          ) : isOrgMode ? (
            <>
              <span className="sector-stat">{totalScannedOrgs} sites across {orgStats.filter(o => o.total > 0).length} orgs</span>
              {totalScannedOrgs > 0 && <span className="sector-stat" style={{ color:'var(--success)' }}>{orgOverallPct}% quantum-ready</span>}
            </>
          ) : (
            <>
              <span className="sector-stat">{scanned}/{(pack.domains ?? []).length} scanned</span>
              {scanned > 0 && <span className="sector-stat" style={{ color:'var(--success)' }}>{pct}% quantum-ready</span>}
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="sector-body">
        {loading ? (
          <div style={{ textAlign:'center', padding:'3rem', color:'var(--text-dim)', fontFamily:'monospace', fontSize:'0.8rem' }}>
            LOADING SECTOR DATA...
          </div>
        ) : isTldMode ? (
          // ── TLD groups view ──────────────────────────────────────────────
          <table className="sector-table">
            <thead>
              <tr>
                <th>Branch / Agency</th>
                <th style={{ textAlign:'right' }}>Sites</th>
                <th>Quantum-Ready</th>
                <th>Best Grade</th>
                <th>Distribution</th>
              </tr>
            </thead>
            <tbody>
              {branchStats.map(branch => {
                const isExpanded = expandedBranch === branch.tld
                const bestColor = branch.bestGrade?.startsWith('A') ? 'var(--success)'
                  : branch.bestGrade?.startsWith('B') ? 'var(--warning)'
                  : branch.bestGrade ? 'var(--error)' : 'var(--text-dim)'
                const expandedRows = (tldData[branch.tld] ?? []).sort((a: any, b: any) => {
                  const ga = GRADE_ORDER[a.overall_grade ?? ''] ?? 9
                  const gb = GRADE_ORDER[b.overall_grade ?? ''] ?? 9
                  if (ga !== gb) return ga - gb
                  return a.hostname.localeCompare(b.hostname)
                })
                return (
                  <React.Fragment key={branch.tld}>
                    <tr
                      className={`sector-row sector-branch-row${branch.total === 0 ? ' sector-empty' : ''}`}
                      onClick={() => branch.total > 0 && setExpandedBranch(isExpanded ? null : branch.tld)}
                    >
                      <td className="sector-hostname" style={{ fontWeight: 600 }}>
                        <span style={{ marginRight: 6, fontSize:'0.85rem' }}>{branch.emoji}</span>
                        {branch.label}
                        <span style={{ marginLeft: 8, fontSize:'0.68rem', color:'var(--text-dim)', fontWeight:400 }}>{branch.tld}</span>
                        {branch.total > 0 && (
                          <span style={{ marginLeft: 8, fontSize:'0.72rem', color:'var(--accent-cyan)', opacity: 0.7 }}>
                            {isExpanded ? '▲' : '▼'}
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:'0.8rem' }}>
                        {branch.total === 0
                          ? <span style={{ color:'var(--text-dim)' }}>0</span>
                          : branch.total}
                      </td>
                      <td>
                        {branch.total > 0 ? (
                          <span style={{
                            fontSize:'0.78rem', fontWeight:700,
                            color: branch.pct >= 50 ? 'var(--success)' : branch.pct > 0 ? 'var(--warning)' : 'var(--text-dim)'
                          }}>{branch.pct}%</span>
                        ) : <span style={{ color:'var(--text-dim)', fontSize:'0.7rem' }}>—</span>}
                      </td>
                      <td>
                        {branch.bestGrade
                          ? <span className="sector-grade" style={{ color: bestColor, borderColor: bestColor + '55' }}>{branch.bestGrade}</span>
                          : <span style={{ color:'var(--text-dim)', fontSize:'0.7rem' }}>—</span>}
                      </td>
                      <td>
                        {branch.total > 0 && (
                          <div style={{ display:'flex', gap: 2, alignItems:'center', height: 14 }}>
                            {branch.secure  > 0 && <div title={`${branch.secure} secure`}  style={{ height:10, width: Math.max(4, (branch.secure/branch.total)*80), background:'var(--success)', borderRadius:2, opacity:0.85 }} />}
                            {branch.legacy  > 0 && <div title={`${branch.legacy} legacy`}  style={{ height:10, width: Math.max(4, (branch.legacy/branch.total)*80), background:'var(--warning)', borderRadius:2, opacity:0.85 }} />}
                            {branch.insecure> 0 && <div title={`${branch.insecure} insecure`} style={{ height:10, width: Math.max(4, (branch.insecure/branch.total)*80), background:'var(--error)', borderRadius:2, opacity:0.85 }} />}
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExpanded && expandedRows.map((row: any) => (
                      <tr key={row.hostname} className="sector-row sector-sub-row" onClick={() => onOpenReport(row.hostname)}>
                        <td className="sector-hostname" style={{ paddingLeft: 32 }}>
                          <span className="sector-dot" style={{
                            background: row.pqc_status === 'secure' ? 'var(--success)'
                              : row.pqc_status === 'legacy' ? 'var(--warning)' : 'var(--error)'
                          }} />
                          {row.hostname}
                        </td>
                        <td />
                        <td style={{ color: row.pqc_status === 'secure' ? 'var(--success)' : row.pqc_status === 'legacy' ? 'var(--warning)' : 'var(--error)', fontSize:'0.72rem', fontWeight:700 }}>
                          {row.pqc_status?.toUpperCase()}
                        </td>
                        <td>
                          {row.overall_grade && (
                            <span className="sector-grade" style={{
                              color: row.overall_grade.startsWith('A') ? 'var(--success)' : row.overall_grade.startsWith('B') ? 'var(--warning)' : 'var(--error)',
                              borderColor: (row.overall_grade.startsWith('A') ? 'var(--success)' : row.overall_grade.startsWith('B') ? 'var(--warning)' : 'var(--error)') + '55'
                            }}>{row.overall_grade}</span>
                          )}
                        </td>
                        <td className="sector-mono" style={{ fontSize:'0.7rem' }}>{row.pqc_group_name ?? row.key_exchange ?? '—'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        ) : isOrgMode ? (
          // ── Org groups view (same structure as tld-groups) ────────────────
          <table className="sector-table">
            <thead>
              <tr>
                <th>Organization</th>
                <th style={{ textAlign:'right' }}>Sites</th>
                <th>Quantum-Ready</th>
                <th>Best Grade</th>
                <th>Distribution</th>
              </tr>
            </thead>
            <tbody>
              {orgStats.map(org => {
                const isExpanded = expandedBranch === org.apex
                const bestColor = org.bestGrade?.startsWith('A') ? 'var(--success)'
                  : org.bestGrade?.startsWith('B') ? 'var(--warning)'
                  : org.bestGrade ? 'var(--error)' : 'var(--text-dim)'
                const expandedRows = (orgData[org.apex] ?? []).sort((a: any, b: any) => {
                  const ga = GRADE_ORDER[a.overall_grade ?? ''] ?? 9
                  const gb = GRADE_ORDER[b.overall_grade ?? ''] ?? 9
                  if (ga !== gb) return ga - gb
                  return a.hostname.localeCompare(b.hostname)
                })
                return (
                  <React.Fragment key={org.apex}>
                    <tr
                      className={`sector-row sector-branch-row${org.total === 0 ? ' sector-empty' : ''}`}
                      onClick={() => org.total > 0 && setExpandedBranch(isExpanded ? null : org.apex)}
                    >
                      <td className="sector-hostname" style={{ fontWeight: 600 }}>
                        {org.emoji && <span style={{ marginRight: 6 }}>{org.emoji}</span>}
                        {org.label}
                        <span style={{ marginLeft: 8, fontSize:'0.68rem', color:'var(--text-dim)', fontWeight:400 }}>{org.apex}{(org.extra ?? []).length > 0 ? ` +${org.extra!.length}` : ''}</span>
                        {org.total > 0 && (
                          <span style={{ marginLeft: 8, fontSize:'0.72rem', color:'var(--accent-cyan)', opacity: 0.7 }}>
                            {isExpanded ? '▲' : '▼'}
                          </span>
                        )}
                      </td>
                      <td style={{ textAlign:'right', fontFamily:'monospace', fontSize:'0.8rem' }}>
                        {org.total === 0 ? <span style={{ color:'var(--text-dim)' }}>0</span> : org.total}
                      </td>
                      <td>
                        {org.total > 0 ? (
                          <span style={{ fontSize:'0.78rem', fontWeight:700, color: org.pct >= 50 ? 'var(--success)' : org.pct > 0 ? 'var(--warning)' : 'var(--text-dim)' }}>{org.pct}%</span>
                        ) : <span style={{ color:'var(--text-dim)', fontSize:'0.7rem' }}>—</span>}
                      </td>
                      <td>
                        {org.bestGrade
                          ? <span className="sector-grade" style={{ color: bestColor, borderColor: bestColor + '55' }}>{org.bestGrade}</span>
                          : <span style={{ color:'var(--text-dim)', fontSize:'0.7rem' }}>—</span>}
                      </td>
                      <td>
                        {org.total > 0 && (
                          <div style={{ display:'flex', gap:2, alignItems:'center', height:14 }}>
                            {org.secure   > 0 && <div title={`${org.secure} secure`}   style={{ height:10, width: Math.max(4,(org.secure/org.total)*80),   background:'var(--success)', borderRadius:2, opacity:0.85 }} />}
                            {org.legacy   > 0 && <div title={`${org.legacy} legacy`}   style={{ height:10, width: Math.max(4,(org.legacy/org.total)*80),   background:'var(--warning)', borderRadius:2, opacity:0.85 }} />}
                            {org.insecure > 0 && <div title={`${org.insecure} insecure`} style={{ height:10, width: Math.max(4,(org.insecure/org.total)*80), background:'var(--error)',   borderRadius:2, opacity:0.85 }} />}
                          </div>
                        )}
                      </td>
                    </tr>
                    {isExpanded && expandedRows.map((row: any) => (
                      <tr key={row.hostname} className="sector-row sector-sub-row" onClick={() => onOpenReport(row.hostname)}>
                        <td className="sector-hostname" style={{ paddingLeft: 32 }}>
                          <span className="sector-dot" style={{ background: row.pqc_status === 'secure' ? 'var(--success)' : row.pqc_status === 'legacy' ? 'var(--warning)' : 'var(--error)' }} />
                          {row.hostname}
                        </td>
                        <td />
                        <td style={{ color: row.pqc_status === 'secure' ? 'var(--success)' : row.pqc_status === 'legacy' ? 'var(--warning)' : 'var(--error)', fontSize:'0.72rem', fontWeight:700 }}>
                          {row.pqc_status?.toUpperCase()}
                        </td>
                        <td>
                          {row.overall_grade && (
                            <span className="sector-grade" style={{ color: row.overall_grade.startsWith('A') ? 'var(--success)' : row.overall_grade.startsWith('B') ? 'var(--warning)' : 'var(--error)', borderColor: (row.overall_grade.startsWith('A') ? 'var(--success)' : row.overall_grade.startsWith('B') ? 'var(--warning)' : 'var(--error)') + '55' }}>{row.overall_grade}</span>
                          )}
                        </td>
                        <td className="sector-mono" style={{ fontSize:'0.7rem' }}>{row.pqc_group_name ?? row.key_exchange ?? '—'}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        ) : (
          // ── domains list view ────────────────────────────────────────────
          <table className="sector-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Grade</th>
                <th>Status</th>
                <th>Key Exchange</th>
                <th>TLS</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {domainRows.map(({ hostname, db }) => (
                <DomainRow key={hostname} hostname={hostname} db={db} onOpenReport={onOpenReport} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

// ─── Report Builder Modal ─────────────────────────────────────────────────────

interface CompanyConfig { name: string; roots: string[] }

function ReportBuilder({ open, onClose, onLaunch }: {
  open: boolean; onClose: () => void; onLaunch: (cfg: CompanyConfig) => void
}) {
  const [name, setName]     = useState('')
  const [roots, setRoots]   = useState([''])
  const [touched, setTouched] = useState<boolean[]>([false])

  function addRoot() {
    setRoots(r => [...r, ''])
    setTouched(t => [...t, false])
  }
  function removeRoot(i: number) {
    setRoots(r => r.filter((_, idx) => idx !== i))
    setTouched(t => t.filter((_, idx) => idx !== i))
  }
  function setRoot(i: number, v: string) {
    setRoots(r => r.map((x, idx) => idx === i ? v : x))
  }
  function touchRoot(i: number) {
    setTouched(t => t.map((x, idx) => idx === i ? true : x))
  }

  const parsedRoots = roots.map(r => parseDomain(r))
  const validRoots  = parsedRoots.filter((p): p is string => p !== null)
  const canLaunch   = validRoots.length > 0

  function handleLaunch() {
    // Touch all fields to show errors
    setTouched(roots.map(() => true))
    if (!canLaunch) return
    onLaunch({ name: name.trim() || validRoots[0], roots: validRoots })
    onClose()
    // Reset for next use
    setName(''); setRoots(['']); setTouched([false])
  }

  if (!open) return null
  return (
    <div className="report-builder-backdrop" onClick={onClose}>
      <div className="report-builder-modal" onClick={e => e.stopPropagation()}>
        <div className="report-builder-title">Build Company Report</div>
        <div className="report-builder-field">
          <label>Company Name</label>
          <input className="report-builder-input" placeholder="e.g. American Express"
            value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="report-builder-field">
          <label>Root Domains <span style={{ color:'var(--text-dim)', fontWeight:400 }}>(apex domains — all subdomains will be discovered)</span></label>
          {roots.map((r, i) => {
            const parsed  = parsedRoots[i]
            const isUrl   = r.includes('://')
            const isEmpty = r.trim() === ''
            const isInvalid = touched[i] && !isEmpty && parsed === null
            return (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ display:'flex', gap:6 }}>
                  <input
                    className={`report-builder-input${isInvalid ? ' report-builder-input-error' : ''}`}
                    style={{ flex:1 }}
                    placeholder={i === 0 ? 'e.g. americanexpress.com or https://amex.com' : 'e.g. amex.com'}
                    value={r}
                    onChange={e => setRoot(i, e.target.value)}
                    onBlur={() => touchRoot(i)}
                  />
                  {roots.length > 1 && (
                    <button className="report-builder-remove" onClick={() => removeRoot(i)}>✕</button>
                  )}
                </div>
                {isInvalid && (
                  <div className="report-builder-field-error">⚠ Not a valid domain or URL</div>
                )}
                {!isInvalid && parsed && isUrl && (
                  <div className="report-builder-field-hint">→ {parsed}</div>
                )}
              </div>
            )
          })}
          {roots.length < 5 && (
            <button className="report-builder-add" onClick={addRoot}>+ Add domain</button>
          )}
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', alignItems:'center', marginTop:20 }}>
          {validRoots.length > 0 && (
            <span style={{ fontSize:'0.7rem', color:'var(--text-dim)', marginRight:'auto' }}>
              {validRoots.length} valid domain{validRoots.length !== 1 ? 's' : ''}
            </span>
          )}
          <button className="gd-close-btn" onClick={onClose}>Cancel</button>
          <button
            className="recheck-btn"
            style={{ padding:'8px 20px', fontSize:'0.8rem', opacity: canLaunch ? 1 : 0.4, cursor: canLaunch ? 'pointer' : 'not-allowed' }}
            onClick={handleLaunch}
          >
            Launch Report →
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Company Report Panel (globe-integrated side panel) ───────────────────────

const COMPANY_CONCURRENCY = 6
const CACHE_FRESH_DAYS = 30

function CompanyReportPanel({ config, open, onClose, onOpenReport, onGlobeRefresh, initialTab = 'current' }: {
  config: CompanyConfig | null; open: boolean; onClose: () => void
  onOpenReport: (h: string) => void; onGlobeRefresh: () => void
  initialTab?: 'current' | 'history'
}) {
  const [tab, setTab]           = useState<'current' | 'history'>(initialTab)
  const [log, setLog]           = useState<string[]>([])
  const [phase, setPhase]       = useState<'idle' | 'discovering' | 'probing' | 'done'>('idle')
  const [results, setResults]   = useState<Record<string, any>>({})
  const [probed, setProbed]     = useState(0)
  const [totalToProbe, setTotalToProbe] = useState(0)
  const [totalDiscovered, setTotalDiscovered] = useState(0)
  const [filter, setFilter]     = useState('')
  const [expandedCountry, setExpandedCountry] = useState<string | null>(null)
  const [pastReports, setPastReports] = useState<any[]>([])
  const abortRef  = useRef(false)
  const logEndRef = useRef<HTMLDivElement>(null)

  const { isSignedIn, getToken } = useAuth()

  const addLog = (msg: string) => setLog(prev => [...prev.slice(-199), msg])

  useEffect(() => {
    if (logEndRef.current) logEndRef.current.scrollIntoView({ behavior:'smooth' })
  }, [log])

  // Load past reports whenever the panel opens or user switches to history tab
  useEffect(() => {
    if (!open) return
    fetch(`${API_BASE}/v1/company/reports`)
      .then(r => r.json())
      .then((d: any) => setPastReports(d.results || []))
      .catch(() => {})
  }, [open, tab])

  useEffect(() => {
    if (!open || !config) return
    abortRef.current = false
    setTab(initialTab)
    // Only run a new probe on 'current' tab; 'history' just shows saved reports
    if (initialTab === 'current') {
      setLog([]); setResults({}); setProbed(0); setTotalToProbe(0); setFilter('')
      runReport(config)
    }
    return () => { abortRef.current = true }
  }, [open, config?.name, initialTab]) // eslint-disable-line

  async function runReport(cfg: CompanyConfig) {
    setPhase('discovering')
    const [primary, ...extras] = cfg.roots
    const extraParam = extras.length > 0 ? `?extra=${encodeURIComponent(extras.join(','))}` : ''
    addLog(`> COMPANY REPORT: ${cfg.name}`)
    addLog(`> ROOTS: ${cfg.roots.join(', ')}`)
    addLog(`> QUERYING CT LOGS + HACKERTARGET + RAPIDDNS + URLSCAN + JLDC...`)

    let data: any
    try {
      const res = await fetch(`${API_BASE}/v1/company/${primary}${extraParam}`)
      data = await res.json()
      if (data.error) { addLog(`> ERROR: ${data.error}`); setPhase('done'); return }
    } catch { addLog(`> ERROR: Failed to reach API`); setPhase('done'); return }

    const known: any[]    = data.known || []
    const unprobed: string[] = data.unprobed || []
    setTotalDiscovered(data.total_discovered || 0)
    // Local tracking map — reliable snapshot for report saving (React state batching)
    const localResults: Record<string, any> = {}
    addLog(`> DISCOVERED: ${data.total_discovered} subdomains (CT + HackerTarget + URLScan)`)

    // Seed results with known domains
    const initial: Record<string, any> = {}
    const cutoff = Date.now() - CACHE_FRESH_DAYS * 24 * 60 * 60 * 1000
    const stale: string[] = []
    for (const d of known) {
      const age = d.last_updated ? new Date(d.last_updated).getTime() : 0
      const entry = { ...d, _state: age > cutoff ? 'cached' : 'stale' }
      initial[d.hostname] = entry
      localResults[d.hostname] = entry
      if (age <= cutoff) stale.push(d.hostname)
    }
    setResults(initial)
    addLog(`> ${known.length} cached | ${known.length - stale.length} fresh | ${stale.length} stale`)
    addLog(`> ${unprobed.length} new subdomains to probe`)

    // Queue = unprobed + stale (re-probe stale ones)
    const queue = [...unprobed, ...stale]
    setTotalToProbe(queue.length)
    if (queue.length === 0) { addLog(`> ALL DATA FRESH — REPORT COMPLETE`); setPhase('done'); onGlobeRefresh(); return }

    // Immediately show all queued domains as "pending" so the full list is visible
    setResults(prev => {
      const next = { ...prev }
      for (const h of queue) if (!next[h]) next[h] = { hostname: h, pqc_status: 'probing', overall_grade: '…', _state: 'probing' }
      return next
    })

    setPhase('probing')
    addLog(`> LAUNCHING ${queue.length} PROBES...`)

    for (let i = 0; i < queue.length; i += COMPANY_CONCURRENCY) {
      if (abortRef.current) { addLog(`> ABORTED`); break }
      const batch = queue.slice(i, i + COMPANY_CONCURRENCY)

      await Promise.all(batch.map(async (hostname) => {
        try {
          const r    = await fetch(`${PROBE_URL}/v1/check/${hostname}`, {
            signal: AbortSignal.timeout(30_000),
          })
          const json = r.ok ? await r.json() : {}
          const entry = {
            hostname,
            pqc_status:     json.pqc_status    || 'unknown',
            overall_grade:  json.overall_grade  || '?',
            pqc_group_name: json.pqc_group      || json.pqc_group_name || '',
            last_updated:   new Date().toISOString(),
            _state: (json.pqc_status && json.pqc_status !== 'unknown') ? 'done' : 'error',
          }
          localResults[hostname] = entry
          setResults(prev => ({ ...prev, [hostname]: entry }))
        } catch {
          const errEntry = { hostname, pqc_status: 'unknown', overall_grade: '?', _state: 'error' }
          localResults[hostname] = errEntry
          setResults(prev => ({ ...prev, [hostname]: errEntry }))
        }
        setProbed(p => p + 1)
      }))

      // Reconcile grades from D1 — covers cases where the probe worker returned
      // a non-ok response (CPU timeout, 524) but still wrote data before dying.
      try {
        const d1res = await fetch(`${API_BASE}/v1/list?domains=${encodeURIComponent(batch.join(','))}&limit=${batch.length}`)
        const d1data = d1res.ok ? await d1res.json() as any : { results: [] }
        for (const row of d1data.results ?? []) {
          if (!row.hostname || !row.pqc_status || row.pqc_status === 'unknown') continue
          const reconciled = {
            ...(localResults[row.hostname] ?? { hostname: row.hostname }),
            pqc_status:    row.pqc_status,
            overall_grade: row.overall_grade || localResults[row.hostname]?.overall_grade || '?',
            pqc_group_name: row.pqc_group_name || localResults[row.hostname]?.pqc_group_name || '',
            _state: 'done' as const,
          }
          localResults[row.hostname] = reconciled
          setResults(prev => ({ ...prev, [row.hostname]: reconciled }))
        }
      } catch { /* non-fatal */ }

      if (i % (COMPANY_CONCURRENCY * 4) === 0) onGlobeRefresh()
    }

    addLog(`> SCAN COMPLETE — ${queue.length} domains processed`)
    setPhase('done')
    onGlobeRefresh()

    // Persist report summary to D1 using localResults (reliable — no state-batching issues)
    try {
      const snap = Object.values(localResults)
      const s   = snap.filter((r:any) => r.pqc_status === 'secure').length
      const l   = snap.filter((r:any) => r.pqc_status === 'legacy').length
      const ins = snap.filter((r:any) => r.pqc_status === 'insecure').length
      const token = isSignedIn ? await getToken() : null
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (token) headers['Authorization'] = `Bearer ${token}`
      await fetch(`${API_BASE}/v1/company/report/save`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: cfg.name, roots: cfg.roots,
          total: snap.length, secure: s, legacy: l, insecure: ins,
          pct_secure: snap.length > 0 ? Math.round((s / snap.length) * 100) : 0,
          discovered: data?.total_discovered || 0,
        }),
      })
      addLog(`> REPORT SAVED TO DATABASE`)
    } catch { /* non-fatal */ }
  }

  const rows = Object.values(results)
  const done  = rows.filter(r => r._state !== 'probing')
  const secure  = done.filter(r => r.pqc_status === 'secure').length
  const legacy  = done.filter(r => r.pqc_status === 'legacy').length
  const insecure= done.filter(r => r.pqc_status === 'insecure').length
  const pct     = done.length > 0 ? Math.round((secure / done.length) * 100) : 0

  // Countries from results
  const countryMap: Record<string, { total:number; secure:number; name:string }> = {}
  for (const r of done) {
    const cc = r.asn_country || r.country
    if (!cc || cc.length !== 2) continue
    if (!countryMap[cc]) countryMap[cc] = { total:0, secure:0, name: cc }
    countryMap[cc].total++
    if (r.pqc_status === 'secure') countryMap[cc].secure++
  }
  const countries = Object.entries(countryMap).sort((a,b) => b[1].total - a[1].total)

  const filterLower = filter.toLowerCase()
  const filteredRows = rows
    .filter(r => !filter || r.hostname.includes(filterLower))
    .sort((a: any, b: any) => {
      const stateOrder: Record<string, number> = { insecure:0, legacy:1, error:2, stale:3, probing:4, cached:5, done:6 }
      const sa = stateOrder[a._state] ?? 5, sb = stateOrder[b._state] ?? 5
      if (sa !== sb) return sa - sb
      const ga = GRADE_ORDER[a.overall_grade ?? ''] ?? 9
      const gb = GRADE_ORDER[b.overall_grade ?? ''] ?? 9
      return ga - gb
    })

  const progress = totalToProbe > 0 ? Math.round((probed / totalToProbe) * 100) : 0

  return (
    <div className={`company-panel${open ? ' open' : ''}`}>
      {/* Header */}
      <div className="company-panel-header">
        <div>
          <div className="company-panel-title">{config?.name ?? 'Company Report'}</div>
          <div className="company-panel-subtitle">{config?.roots.join(' · ')}</div>
        </div>
        <button className="gd-close-btn" onClick={onClose}>✕</button>
      </div>

      {/* Tab bar */}
      <div className="company-tab-bar">
        <button
          className={`company-tab-btn${tab === 'current' ? ' active' : ''}`}
          onClick={() => setTab('current')}
        >CURRENT REPORT</button>
        <button
          className={`company-tab-btn${tab === 'history' ? ' active' : ''}`}
          onClick={() => setTab('history')}
        >PAST REPORTS</button>
      </div>

      {tab === 'history' ? (
        /* ── History tab ── */
        <div className="company-history-list">
          {pastReports.length === 0 ? (
            <div style={{ padding:'2rem', textAlign:'center', color:'var(--text-dim)', fontSize:'0.75rem' }}>
              No saved reports yet
            </div>
          ) : pastReports.map((r: any) => {
            const pctR = r.total > 0 ? Math.round((r.secure / r.total) * 100) : 0
            return (
              <div key={r.id} className="company-history-row">
                <div className="company-history-name">{r.name}</div>
                <div className="company-history-roots">{JSON.parse(r.roots || '[]').join(' · ')}</div>
                <div className="company-history-meta">
                  <span style={{ color:'var(--success)' }}>{r.secure} secure</span>
                  <span style={{ color:'var(--warning)' }}>{r.legacy} legacy</span>
                  <span style={{ color:'var(--error)' }}>{r.insecure} at risk</span>
                  <span style={{ color: pctR >= 50 ? 'var(--success)' : 'var(--warning)', fontWeight:700 }}>{pctR}% ready</span>
                  <span style={{ color:'var(--text-dim)', marginLeft:'auto' }}>{r.total} domains</span>
                </div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:6 }}>
                  <span style={{ fontSize:'0.65rem', color:'var(--text-dim)' }}>
                    {new Date(r.created_at).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                  </span>
                  <a href={`/cr/${r.id}`} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize:'0.65rem', color:'var(--accent-cyan)', textDecoration:'none', border:'1px solid rgba(0,209,255,0.25)', padding:'2px 8px', borderRadius:4 }}>
                    ↗ Full Report
                  </a>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        /* ── Current report tab ── */
        <>
          {/* Console */}
          <div className="company-console">
            <div className="company-console-label">PROBE CONSOLE</div>
            <div className="company-console-body">
              {log.map((l, i) => <div key={i} className="company-log-line">{l}</div>)}
              <div ref={logEndRef} />
            </div>
            {phase === 'probing' && (
              <div className="company-progress-wrap">
                <div className="company-progress-bar" style={{ width: `${progress}%` }} />
                <span className="company-progress-label">{probed}/{totalToProbe}</span>
              </div>
            )}
          </div>

          {/* Stats strip */}
          {done.length > 0 && (
            <div className="company-stats-strip">
              <span style={{ color:'var(--text-dim)', fontSize:'0.72rem' }}>{totalDiscovered} discovered</span>
              <span style={{ color:'var(--success)' }}>{secure} secure</span>
              <span style={{ color:'var(--warning)' }}>{legacy} legacy</span>
              <span style={{ color:'var(--error)' }}>{insecure} at risk</span>
              <span style={{ color: pct >= 50 ? 'var(--success)' : 'var(--warning)', fontWeight:700 }}>{pct}% ready</span>
            </div>
          )}

          {/* Countries */}
          {countries.length > 0 && (
            <div className="company-countries">
              <div className="company-section-label">COUNTRIES DISCOVERED — {countries.length}</div>
              <div className="company-country-list">
                {countries.map(([cc, d]) => (
                  <div key={cc} className="company-country-row"
                    onClick={() => setExpandedCountry(expandedCountry === cc ? null : cc)}>
                    <span className="company-country-flag">{countryFlag(cc)}</span>
                    <span className="company-country-name">{cc}</span>
                    <span className="company-country-count">{d.total}</span>
                    <div className="company-country-bar">
                      {d.secure > 0 && <div style={{ height:6, width:`${(d.secure/d.total)*100}%`, background:'var(--success)', borderRadius:2 }} />}
                      {(d.total-d.secure) > 0 && <div style={{ height:6, width:`${((d.total-d.secure)/d.total)*100}%`, background:'var(--warning)', borderRadius:2 }} />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Domain list */}
          <div className="company-domain-section">
            <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderBottom:'1px solid rgba(255,255,255,0.06)' }}>
              <div className="company-section-label" style={{ margin:0 }}>SITES — {done.length}</div>
              <input className="company-filter-input" placeholder="filter domains..."
                value={filter} onChange={e => setFilter(e.target.value)} />
            </div>
            <div className="company-domain-list">
              {filteredRows.map((r: any) => {
                const isProbing = r._state === 'probing'
                const statusColor = isProbing ? 'var(--accent-cyan)'
                  : r.pqc_status === 'secure'   ? 'var(--success)'
                  : r.pqc_status === 'legacy'   ? 'var(--warning)'
                  : r.pqc_status === 'insecure' ? 'var(--error)'
                  : 'var(--text-dim)'  // unknown / error / unreachable → gray
                const grade = r.overall_grade
                const gradeColor = !grade || grade === '?' || grade === '…' ? 'var(--text-dim)'
                  : grade.startsWith('A') ? 'var(--success)'
                  : grade.startsWith('B') ? 'var(--warning)'
                  : 'var(--error)'
                return (
                  <div key={r.hostname} className="company-domain-row"
                    onClick={() => !isProbing && onOpenReport(r.hostname)}>
                    <span className="company-domain-dot" style={{ background: statusColor }} />
                    <span className="company-domain-name">{r.hostname}</span>
                    {isProbing
                      ? <span style={{ fontSize:'0.68rem', color:'var(--accent-cyan)', marginLeft:'auto' }}>probing…</span>
                      : <>
                          {grade && grade !== '?' && grade !== '…' && (
                            <span className="company-domain-grade" style={{ color: gradeColor, borderColor: gradeColor + '44' }}>{grade}</span>
                          )}
                          {r._state === 'cached' && <span className="company-domain-tag">cached</span>}
                        </>
                    }
                  </div>
                )
              })}
              {filteredRows.length === 0 && phase !== 'discovering' && (
                <div style={{ padding:'2rem', textAlign:'center', color:'var(--text-dim)', fontSize:'0.75rem' }}>
                  {filter ? 'No matches' : 'No domains yet'}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// ─── RankBadge ────────────────────────────────────────────────────────────────

function RankBadge({ rankId, size = 'md' }: { rankId: number; size?: 'sm' | 'md' | 'lg' }) {
  const rank = RANKS.find(r => r.id === rankId) ?? RANKS[0]
  const color = rankColor(rank.tier)
  const dim = size === 'sm' ? 20 : size === 'lg' ? 48 : 32
  const pips = rank.id <= 5 ? rank.id - 1 : 0  // Specialist pips (0-4)
  const chevrons = rank.tier === 'nco' ? Math.min(rank.id - 5, 3) : 0

  return (
    <svg
      width={dim} height={dim} viewBox="0 0 32 32"
      style={{ flexShrink: 0 }}
      aria-label={rank.name}
    >
      {/* Delta (triangle) outline — Space Force inspired */}
      <polygon
        points="16,3 29,27 3,27"
        fill="none"
        stroke={color}
        strokeWidth={rank.id === 1 ? 1.5 : 2}
        opacity={rank.id === 1 ? 0.5 : 1}
      />
      {/* Chevrons (NCO tier) */}
      {chevrons >= 1 && <polyline points="10,22 16,18 22,22" fill="none" stroke={color} strokeWidth={1.8} />}
      {chevrons >= 2 && <polyline points="10,19 16,15 22,19" fill="none" stroke={color} strokeWidth={1.8} />}
      {chevrons >= 3 && <polyline points="10,16 16,12 22,16" fill="none" stroke={color} strokeWidth={1.8} />}
      {/* Pips (Specialist tier) */}
      {Array.from({ length: pips }).map((_, i) => (
        <circle
          key={i}
          cx={16 - (pips - 1) * 3 + i * 6}
          cy={20}
          r={2}
          fill={color}
        />
      ))}
      {/* Officer bar(s) */}
      {rank.tier === 'officer' && rank.id === 11 && (
        <rect x="10" y="19" width="12" height="2.5" fill={color} rx="1" />
      )}
      {rank.tier === 'officer' && rank.id === 12 && (<>
        <rect x="10" y="17" width="12" height="2" fill={color} rx="1" />
        <rect x="10" y="21" width="12" height="2" fill={color} rx="1" />
      </>)}
      {rank.tier === 'officer' && rank.id >= 13 && (
        <text x="16" y="22" textAnchor="middle" fontSize="9" fill={color} fontWeight="bold">
          {rank.id === 13 ? '♦' : rank.id === 14 ? '⊕' : '★'}
        </text>
      )}
      {/* General — 5 stars */}
      {rank.tier === 'general' && (
        <text x="16" y="21" textAnchor="middle" fontSize="7" fill={color}>★★★★★</text>
      )}
    </svg>
  )
}

// ─── HomePlanet (/me) ─────────────────────────────────────────────────────────

function HomePlanet() {
  const { user, isSignedIn, isLoaded } = useUser()
  const { getToken, signOut }          = useAuth()
  const navigate                       = useNavigate()
  const [profile, setProfile]          = useState<any>(null)
  const [loading, setLoading]          = useState(true)
  const [upgradeOpen, setUpgradeOpen]  = useState(false)

  useEffect(() => {
    if (!isLoaded) return
    if (!isSignedIn) { navigate('/'); return }
    getToken().then((token: string | null) => {
      if (!token) return
      fetch(`${API_BASE}/v1/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => { setProfile(d); setLoading(false) })
        .catch(() => setLoading(false))
    })
  }, [isLoaded, isSignedIn]) // eslint-disable-line react-hooks/exhaustive-deps

  const rank     = profile?.rank     ?? RANKS[0]
  const nextRank = profile?.next_rank ?? null
  const points   = profile?.points   ?? 0
  const pct      = nextRank && nextRank.minPoints !== Infinity
    ? Math.min(100, Math.round(((points - rank.minPoints) / (nextRank.minPoints - rank.minPoints)) * 100))
    : 100
  const color    = rankColor(rank.tier)

  const email = user?.primaryEmailAddress?.emailAddress ?? ''

  return (
    <div style={{ minHeight: '100vh', background: '#060a16', color: '#e2e8f0', fontFamily: 'inherit' }}>

      {/* Top nav */}
      <div style={{ borderBottom: '1px solid rgba(0,209,255,0.12)', padding: '0.8rem 2rem', display: 'flex', alignItems: 'center', gap: '1.5rem', background: 'rgba(6,10,22,0.9)', backdropFilter: 'blur(12px)', position: 'sticky', top: 0, zIndex: 100 }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: 'var(--accent-cyan, #00d1ff)', cursor: 'pointer', fontSize: '0.78rem', letterSpacing: '0.12em', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
        >
          ← QUANTUM SENTRY
        </button>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => signOut(() => navigate('/'))}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '0.72rem', letterSpacing: '0.1em', padding: '4px 12px' }}
        >
          SIGN OUT
        </button>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '3rem 2rem 6rem' }}>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '8rem 0', color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem', letterSpacing: '0.1em' }}>
            LOADING DOSSIER…
          </div>
        ) : (
          <>
            {/* ── Hero ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3rem', alignItems: 'center', marginBottom: '3rem', padding: '2.5rem 3rem', background: 'linear-gradient(135deg, rgba(6,10,22,0.9), rgba(0,209,255,0.04))', border: '1px solid rgba(0,209,255,0.14)', borderRadius: 16, position: 'relative', overflow: 'hidden' }}>
              {/* Glow */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${color}88, transparent)` }} />
              <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 10% 50%, ${color}08, transparent 60%)`, pointerEvents: 'none' }} />

              {/* Big rank badge */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.8rem' }}>
                <svg width={96} height={96} viewBox="0 0 32 32" aria-label={rank.name} style={{ filter: `drop-shadow(0 0 12px ${color}66)` }}>
                  <polygon points="16,3 29,27 3,27" fill="none" stroke={color} strokeWidth={rank.id === 1 ? 1.5 : 2.2} opacity={rank.id === 1 ? 0.5 : 1} />
                  {rank.tier === 'nco' && (() => { const n = Math.min(rank.id - 5, 3); return (<>
                    {n >= 1 && <polyline points="10,22 16,18 22,22" fill="none" stroke={color} strokeWidth={1.8} />}
                    {n >= 2 && <polyline points="10,19 16,15 22,19" fill="none" stroke={color} strokeWidth={1.8} />}
                    {n >= 3 && <polyline points="10,16 16,12 22,16" fill="none" stroke={color} strokeWidth={1.8} />}
                  </>) })()}
                  {rank.tier === 'enlisted' && (() => { const pips = rank.id - 1; return Array.from({ length: pips }).map((_, i) => (
                    <circle key={i} cx={16 - (pips - 1) * 3 + i * 6} cy={20} r={2} fill={color} />
                  )) })()}
                  {rank.tier === 'officer' && rank.id === 11 && <rect x="10" y="19" width="12" height="2.5" fill={color} rx="1" />}
                  {rank.tier === 'officer' && rank.id === 12 && <><rect x="10" y="17" width="12" height="2" fill={color} rx="1" /><rect x="10" y="21" width="12" height="2" fill={color} rx="1" /></>}
                  {rank.tier === 'officer' && rank.id >= 13 && <text x="16" y="22" textAnchor="middle" fontSize="9" fill={color} fontWeight="bold">{rank.id === 13 ? '♦' : rank.id === 14 ? '⊕' : '★'}</text>}
                  {rank.tier === 'general' && <text x="16" y="21" textAnchor="middle" fontSize="7" fill={color}>★★★★★</text>}
                </svg>
                <div style={{ fontSize: '0.65rem', letterSpacing: '0.15em', color, textAlign: 'center', lineHeight: 1.4, maxWidth: 100, textTransform: 'uppercase', fontWeight: 600 }}>{rank.name}</div>
              </div>

              {/* Identity */}
              <div>
                <div style={{ fontSize: '0.6rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.3)', marginBottom: '0.5rem' }}>QUANTUM FORCE DOSSIER</div>
                <div style={{ fontSize: '2.4rem', fontWeight: 900, letterSpacing: '-0.5px', lineHeight: 1, marginBottom: '0.3rem' }}>
                  {user?.firstName && user?.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : user?.username || user?.firstName || 'Agent Unknown'}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)', marginBottom: '1.8rem' }}>{email}</div>

                {/* Points + progress bar */}
                <div style={{ marginBottom: '0.6rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                  <span style={{ color, fontWeight: 700 }}>{points.toLocaleString()} pts</span>
                  {nextRank && <span style={{ color: 'rgba(255,255,255,0.3)' }}>{nextRank.minPoints.toLocaleString()} → {nextRank.name}</span>}
                </div>
                <div style={{ height: 12, background: 'rgba(255,255,255,0.06)', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.08)', position: 'relative' }}>
                  <div style={{ height: '100%', width: `${Math.max(pct, 0.5)}%`, background: `linear-gradient(90deg, ${color}66, ${color})`, borderRadius: 6, boxShadow: `0 0 12px ${color}88`, transition: 'width 1s ease', position: 'relative', overflow: 'hidden' }}>
                    {/* Scanning shimmer */}
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%)', animation: 'shimmer 2s infinite', backgroundSize: '200% 100%' }} />
                  </div>
                </div>
                {nextRank && (
                  <div style={{ marginTop: '0.4rem', display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: 'rgba(255,255,255,0.25)' }}>
                    <span>{(nextRank.minPoints - points).toLocaleString()} pts to next rank</span>
                    <span>{Math.ceil((nextRank.minPoints - points) / 10).toLocaleString()} domains needed</span>
                  </div>
                )}
              </div>
            </div>

            {/* ── Stats row ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2.5rem' }}>
              {[
                { label: 'DISCOVERIES',    val: (profile?.discoveries ?? 0).toLocaleString(), color: '#00d1ff', icon: '◈' },
                { label: 'TOTAL POINTS',   val: points.toLocaleString(),                      color,            icon: '⬡' },
                { label: 'RANK',           val: `#${rank.id}`,                                color,            icon: '△' },
                { label: 'ACCOUNT',        val: (profile?.account_type ?? 'personal').replace('_pending', '…').toUpperCase(), color: profile?.account_type === 'corporate' ? '#00ff88' : 'rgba(255,255,255,0.5)', icon: '◉' },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(6,10,22,0.8)', border: `1px solid ${s.color}22`, borderRadius: 10, padding: '1.4rem 1.2rem', textAlign: 'center', backdropFilter: 'blur(8px)', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 50% 0%, ${s.color}08, transparent 70%)` }} />
                  <div style={{ fontSize: '1rem', color: `${s.color}55`, marginBottom: '0.3rem' }}>{s.icon}</div>
                  <div style={{ fontSize: '1.8rem', fontWeight: 900, color: s.color, lineHeight: 1, textShadow: `0 0 16px ${s.color}55` }}>{s.val}</div>
                  <div style={{ fontSize: '0.55rem', letterSpacing: '1.8px', color: 'rgba(255,255,255,0.3)', marginTop: '0.5rem' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* ── Two-col layout: rank ladder + recent discoveries ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>

              {/* Rank ladder */}
              <div style={{ background: 'rgba(6,10,22,0.8)', border: '1px solid rgba(0,209,255,0.1)', borderRadius: 12, padding: '1.5rem', backdropFilter: 'blur(8px)' }}>
                <div style={{ fontSize: '0.6rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.3)', marginBottom: '1.2rem' }}>QUANTUM FORCE RANK LADDER</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  {RANKS.slice().reverse().map(r => {
                    const isCurrent = r.id === rank.id
                    const isAchieved = r.id <= rank.id
                    const rc = rankColor(r.tier)
                    return (
                      <div key={r.id} style={{
                        display: 'flex', alignItems: 'center', gap: '0.7rem',
                        padding: '0.45rem 0.7rem', borderRadius: 6,
                        background: isCurrent ? `${rc}18` : 'transparent',
                        border: isCurrent ? `1px solid ${rc}44` : '1px solid transparent',
                        boxShadow: isCurrent ? `0 0 14px ${rc}22` : 'none',
                        opacity: isAchieved ? 1 : 0.3,
                        transition: 'all 0.2s',
                      }}>
                        <RankBadge rankId={r.id} size="sm" />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.75rem', fontWeight: isCurrent ? 700 : 400, color: isCurrent ? rc : 'rgba(255,255,255,0.7)' }}>{r.name}</div>
                          <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.25)' }}>
                            {r.minPoints === Infinity ? 'Leaderboard #1' : r.minPoints.toLocaleString() + ' pts'}
                          </div>
                        </div>
                        {isCurrent && <div style={{ fontSize: '0.55rem', letterSpacing: '0.1em', color: rc, fontWeight: 700 }}>YOU</div>}
                        {isAchieved && !isCurrent && <div style={{ fontSize: '0.65rem', color: rc }}>✓</div>}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* Recent discoveries */}
              <div style={{ background: 'rgba(6,10,22,0.8)', border: '1px solid rgba(0,209,255,0.1)', borderRadius: 12, padding: '1.5rem', backdropFilter: 'blur(8px)' }}>
                <div style={{ fontSize: '0.6rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.3)', marginBottom: '1.2rem' }}>RECENT DISCOVERIES</div>
                {(!profile?.recent_discoveries || profile.recent_discoveries.length === 0) ? (
                  <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.8rem', textAlign: 'center', padding: '3rem 0' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '0.8rem', opacity: 0.4 }}>◈</div>
                    No discoveries yet.<br />
                    <span style={{ fontSize: '0.72rem' }}>Scan a domain to earn points.</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                    {profile.recent_discoveries.map((d: any, i: number) => (
                      <div key={d.hostname} style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', padding: '0.6rem 0.7rem', borderRadius: 6, background: i % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent', border: '1px solid transparent' }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: d.pqc_status === 'legacy' ? '#ffcc00' : '#ff4d4d', boxShadow: `0 0 6px ${d.pqc_status === 'legacy' ? '#ffcc00' : '#ff4d4d'}88` }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.8rem', color: 'rgba(0,209,255,0.85)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.hostname}</div>
                          <div style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)' }}>{new Date(d.awarded_at).toLocaleDateString()}</div>
                        </div>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#00ff88', flexShrink: 0 }}>+{d.points_awarded}pts</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* ── Corporate upgrade CTA ── */}
            {profile?.account_type === 'personal' && canBeCorporate(email) && (
              <div style={{ padding: '1.5rem 2rem', background: 'rgba(0,255,136,0.04)', border: '1px solid rgba(0,255,136,0.18)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2rem' }}>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#00ff88', marginBottom: '0.3rem' }}>Upgrade to Corporate Account</div>
                  <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.4)' }}>Your email qualifies. Claim your organization's domains, keep reports private, and appear on the corporate leaderboard.</div>
                </div>
                <button className="gd-nav-btn" onClick={() => setUpgradeOpen(true)}
                  style={{ flexShrink: 0, background: 'rgba(0,255,136,0.1)', borderColor: 'rgba(0,255,136,0.3)', color: '#00ff88', whiteSpace: 'nowrap' }}>
                  REGISTER ORG
                </button>
              </div>
            )}

            {upgradeOpen && (
              <CorporateSignupFlow onClose={() => setUpgradeOpen(false)} onSuccess={() => { setUpgradeOpen(false); setProfile((p: any) => p ? { ...p, account_type: 'corporate_pending' } : p) }} />
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── CorporateSignupFlow ──────────────────────────────────────────────────────

function CorporateSignupFlow({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const { getToken } = useAuth()
  const [step, setStep]         = useState<1 | 2 | 3>(1)
  const [orgName, setOrgName]   = useState('')
  const [domains, setDomains]   = useState('')
  const [loading, setLoading]   = useState(false)
  const [result, setResult]     = useState<any>(null)
  const [error, setError]       = useState('')

  const submit = async () => {
    const claimedDomains = domains.split(/[\n,]+/).map(d => d.trim().toLowerCase()).filter(Boolean)
    if (!orgName || claimedDomains.length === 0) { setError('Organization name and at least one domain are required.'); return }
    setLoading(true); setError('')
    try {
      const token = await getToken()
      const res = await fetch(`${API_BASE}/v1/corporate/register`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ org_name: orgName, claimed_domains: claimedDomains }),
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Registration failed'); setLoading(false); return }
      setResult(data)
      setStep(3)
    } catch (e: any) { setError(e.message) }
    setLoading(false)
  }

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }
  const modal: React.CSSProperties = {
    background: 'var(--panel-bg, #0d1117)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 480,
    boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
  }

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={modal}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', letterSpacing: '0.1em' }}>
            {step === 1 ? 'CORPORATE ACCOUNT' : step === 2 ? 'REGISTER ORGANIZATION' : 'REGISTRATION SUBMITTED'}
          </h2>
          <button className="gd-close-btn" onClick={onClose}>✕</button>
        </div>

        {step === 1 && (
          <>
            <p style={{ color: 'var(--text-dim)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
              Your email qualifies for a Corporate account. Corporate accounts can claim ownership of your organization's domains and keep reports private.
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="gd-nav-btn" onClick={onClose} style={{ flex: 1 }}>STAY PERSONAL</button>
              <button className="gd-nav-btn" onClick={() => setStep(2)} style={{ flex: 1, background: 'rgba(0,255,136,0.1)', borderColor: 'rgba(0,255,136,0.3)', color: '#00ff88' }}>
                GO CORPORATE
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', letterSpacing: '0.1em', display: 'block', marginBottom: '0.4rem' }}>ORGANIZATION NAME</label>
              <input
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.6rem 0.8rem', color: '#fff', fontSize: '0.9rem' }}
                placeholder="Acme Corp"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
              />
            </div>
            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)', letterSpacing: '0.1em', display: 'block', marginBottom: '0.4rem' }}>CLAIMED DOMAINS (one per line)</label>
              <textarea
                style={{ width: '100%', boxSizing: 'border-box', background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border)', borderRadius: 6, padding: '0.6rem 0.8rem', color: '#fff', fontSize: '0.85rem', minHeight: 100, resize: 'vertical' }}
                placeholder="acme.com&#10;acme.io"
                value={domains}
                onChange={e => setDomains(e.target.value)}
              />
            </div>
            {error && <div style={{ color: '#ff4d4d', fontSize: '0.8rem', marginBottom: '0.8rem' }}>{error}</div>}
            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="gd-nav-btn" onClick={() => setStep(1)} style={{ flex: 1 }}>BACK</button>
              <button className="gd-nav-btn" onClick={submit} disabled={loading}
                style={{ flex: 2, background: 'rgba(0,255,136,0.1)', borderColor: 'rgba(0,255,136,0.3)', color: '#00ff88' }}>
                {loading ? 'SUBMITTING…' : 'SUBMIT'}
              </button>
            </div>
          </>
        )}

        {step === 3 && result && (
          <>
            <div style={{ textAlign: 'center', padding: '1rem 0', marginBottom: '1rem' }}>
              {result.approved ? (
                <>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✓</div>
                  <div style={{ color: '#00ff88', fontWeight: 700, marginBottom: '0.5rem' }}>Auto-Approved!</div>
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Your email domain matches your claimed domain. Corporate account active.</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>⌛</div>
                  <div style={{ color: '#ffcc00', fontWeight: 700, marginBottom: '0.5rem' }}>Pending Review</div>
                  <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Your registration is under admin review. You'll be notified once approved.</div>
                </>
              )}
            </div>
            <button className="gd-nav-btn" onClick={onSuccess} style={{ width: '100%' }}>CLOSE</button>
          </>
        )}
      </div>
    </div>
  )
}

// ─── GlobeTopBar ──────────────────────────────────────────────────────────────

function GlobeTopBar({ stats, countryData, leftOpen, onToggleLeft, navigate, onResetGlobe, onOpenSectors, onOpenCompany, starsEnabled, onToggleStars }: {
  stats: AnalyticsData | null; countryData: CountryData
  leftOpen: boolean; onToggleLeft: () => void
  navigate: ReturnType<typeof useNavigate>; onResetGlobe: () => void; onOpenSectors: () => void; onOpenCompany: () => void
  starsEnabled: boolean; onToggleStars: () => void
}) {
  const n = stats?.pqc_status?.total ?? 0
  const chips = stats ? [
    { label: 'SECURE',    val: stats.pqc_status.secure,        color: '#00ff88' },
    { label: 'LEGACY',    val: stats.pqc_status.legacy,        color: '#ffcc00' },
    { label: 'INSECURE',  val: stats.pqc_status.insecure,      color: '#ff4d4d' },
    { label: 'DOMAINS',   val: n,                               color: 'var(--accent-cyan)' },
    { label: 'COUNTRIES', val: Object.keys(countryData).length, color: '#a78bfa' },
    { label: 'TLS 1.3',   val: `${pct(stats.tls?.tls13, stats.tls?.total)}%`, color: '#60a5fa' },
  ] : []

  const { user, isSignedIn } = useUser()
  const { getToken }         = useAuth()
  const [userRankId, setUserRankId] = useState(1)

  useEffect(() => {
    if (!isSignedIn) return
    getToken().then((token: string | null) => {
      if (!token) return
      fetch(`${API_BASE}/v1/me/rank`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => { if (d.rank?.id) setUserRankId(d.rank.id) })
        .catch(() => {})
    })
  }, [isSignedIn]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="gd-topbar">
        <div className="gd-topbar-left">
          <button className="gd-icon-btn" onClick={onToggleLeft} title="Recent Scans">
            {leftOpen ? '◀' : '▶'}
          </button>
          <div className="gd-logo-wrap" onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <img src="/tychon-logo.png" alt="Tychon" style={{ height: 28, width: 28, objectFit: 'contain', flexShrink: 0 }} />
            <span className="gd-logo">QUANTUM SENTRY</span>
          </div>
        </div>
        <div className="gd-chips">
          {chips.map(c => (
            <div key={c.label} className="gd-chip" style={{ borderColor: typeof c.color === 'string' && c.color.startsWith('#') ? c.color + '38' : 'rgba(255,255,255,0.08)' }}>
              <span className="gd-chip-val" style={{ color: c.color }}>{typeof c.val === 'number' ? c.val.toLocaleString() : c.val}</span>
              <span className="gd-chip-label">{c.label}</span>
            </div>
          ))}
        </div>
        <div className="gd-topbar-right">
          <button className="gd-nav-btn" onClick={onResetGlobe}>Globe</button>
          <button className="gd-nav-btn" onClick={onOpenSectors}>Sectors</button>
          <button className="gd-nav-btn" onClick={onOpenCompany}>Company</button>
          <button className="gd-nav-btn" onClick={() => navigate('/knowledge/laws')}>Laws</button>
          <button
            className="gd-nav-btn"
            onClick={onToggleStars}
            title={starsEnabled ? 'Hide stars' : 'Show stars'}
            style={{ opacity: starsEnabled ? 1 : 0.4, fontSize: '0.85rem', padding: '2px 8px' }}
          >✦</button>
          {/* Auth pill */}
          {isSignedIn ? (
            <button
              className="gd-nav-btn"
              onClick={() => navigate('/me')}
              style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '2px 8px' }}
              title="Your profile & rank"
            >
              <RankBadge rankId={userRankId} size="sm" />
              <span style={{ maxWidth: 80, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.firstName || user?.username || 'CREW'}
              </span>
            </button>
          ) : (
            <SignInButton mode="modal">
              <button className="gd-nav-btn">SIGN IN</button>
            </SignInButton>
          )}
        </div>
      </div>
    </>
  )
}

// ── Left Panel — Recent Scans ─────────────────────────────────────────────────
function GlobeLeftPanel({ domains, open, onOpenReport }: {
  domains: Domain[]; open: boolean; onOpenReport: (hostname: string) => void
}) {
  const [tab, setTab] = useState<'scans' | 'changes'>('scans')
  const [changes, setChanges] = useState<any[]>([])

  useEffect(() => {
    if (tab !== 'changes') return
    fetch(`${API_BASE}/v1/changes?limit=50`)
      .then(r => r.json())
      .then((d: any) => setChanges(d.results || []))
      .catch(() => {})
  }, [tab])

  return (
    <div className={`gd-left${open ? ' open' : ''}`}>
      {/* Tab bar */}
      <div className="gd-left-tabs">
        <button className={`gd-left-tab${tab === 'scans' ? ' active' : ''}`} onClick={() => setTab('scans')}>RECENT SCANS</button>
        <button className={`gd-left-tab${tab === 'changes' ? ' active' : ''}`} onClick={() => setTab('changes')}>CHANGES</button>
      </div>

      {tab === 'scans' ? (
        <div className="gd-panel-body">
          {domains.length === 0 && <div className="gd-empty">No scans yet</div>}
          {domains.map(d => {
            const color = d.pqc_status === 'secure' ? '#00ff88' : d.pqc_status === 'legacy' ? '#ffcc00' : '#ff4d4d'
            const cc = (d as any).asn_country ?? null
            return (
              <div key={d.hostname} className="gd-recent-item" onClick={() => onOpenReport(d.hostname)}>
                <div className="gd-recent-flag">{countryFlag(cc) || '🌐'}</div>
                <div className="gd-recent-info">
                  <div className="gd-recent-host">{d.hostname.length > 25 ? d.hostname.slice(0,25)+'…' : d.hostname}</div>
                  <div className="gd-recent-meta" style={{ color }}>
                    {d.pqc_status?.toUpperCase() ?? '—'}
                    {d.overall_grade && <span className="gd-recent-grade" style={{ borderColor: color+'60', color }}>{d.overall_grade}</span>}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <div className="gd-panel-body">
          {changes.length === 0 && <div className="gd-empty">No changes detected yet</div>}
          {changes.map((c: any) => {
            const gradeDir = c.prev_overall_grade && c.prev_overall_grade !== c.overall_grade
              ? changeDir(c.prev_overall_grade, c.overall_grade, GRADE_RANK) : null
            const kxDir = c.prev_kx_group && c.prev_kx_group !== c.cur_kx_group
              ? changeDir(c.prev_kx_group, c.cur_kx_group, KX_RANK) : null
            const tlsDir = c.prev_tls_version && c.prev_tls_version !== c.cur_tls_version
              ? changeDir(c.prev_tls_version, c.cur_tls_version, TLS_RANK) : null
            const hasUpgrade = [gradeDir, kxDir, tlsDir].some(d => d === 'up')
            const hasDowngrade = [gradeDir, kxDir, tlsDir].some(d => d === 'down')
            const overallDir = hasDowngrade ? 'down' : hasUpgrade ? 'up' : 'lateral'
            const arrow = overallDir === 'up' ? '↑' : overallDir === 'down' ? '↓' : '↔'
            const arrowColor = overallDir === 'up' ? 'var(--success)' : overallDir === 'down' ? 'var(--error)' : 'var(--text-dim)'
            const changedFields = [
              c.prev_overall_grade !== c.overall_grade && `Grade: ${c.prev_overall_grade}→${c.overall_grade}`,
              c.prev_kx_group !== c.cur_kx_group && c.prev_kx_group && `KX: ${c.prev_kx_group}→${c.cur_kx_group}`,
              c.prev_tls_version !== c.cur_tls_version && c.prev_tls_version && `TLS: ${c.prev_tls_version}→${c.cur_tls_version}`,
              c.prev_cert_sig !== c.cur_cert_sig && c.prev_cert_sig && `Sig: ${c.prev_cert_sig}→${c.cur_cert_sig}`,
            ].filter(Boolean).join(' · ')
            const date = c.changed_at ? new Date(c.changed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''
            return (
              <div key={c.hostname} className="gd-recent-item" onClick={() => onOpenReport(c.hostname)}>
                <div className="gd-recent-flag" style={{ fontSize: '1rem', color: arrowColor, fontWeight: 700 }}>{arrow}</div>
                <div className="gd-recent-info">
                  <div className="gd-recent-host">{c.hostname.length > 24 ? c.hostname.slice(0,24)+'…' : c.hostname}
                    <span style={{ fontSize: '0.62rem', color: 'var(--text-dim)', marginLeft: 6 }}>{date}</span>
                  </div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-dim)', marginTop: 1, lineHeight: 1.4 }}>{changedFields}</div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Right Slideout ────────────────────────────────────────────────────────────
function GlobeRightSlideout({ open, country, stateName, domains, countryRanks, onClose, onExploreRegion, onOpenRegistry, onOpenReport, viewMode }: {
  open: boolean; country: SelectedCountry | null; stateName: string | null
  domains: Domain[]; countryRanks: Record<string, number>
  onClose: () => void; onExploreRegion: (key: string) => void; onOpenRegistry: () => void; onOpenReport: (hostname: string) => void; viewMode: 'globe' | 'region'
}) {
  if (!country) return null
  const ratio      = country.total > 0 ? country.secure / country.total : 0
  const barColor   = readinessColor(ratio)
  const totalCountries = Object.keys(countryRanks).length
  const rank       = countryRanks[country.a2] ?? totalCountries
  const rankColor  = rank <= 3 ? '#ffcc00' : rank <= 10 ? '#00ff88' : rank <= totalCountries / 2 ? '#60a5fa' : 'var(--text-dim)'

  // When in state view, filter domains to the selected state
  const stateAbbr = stateName ? Object.keys(US_STATES).find(k => US_STATES[k].name === stateName) : null
  const scopedDomains = stateAbbr
    ? domains.filter(d => domainStateHash(d.hostname) === stateAbbr)
    : domains

  const displayDomains = scopedDomains.slice(0, 20)

  return (
    <div className={`gd-right${open ? ' open' : ''}`}>
      <div className="gd-right-header">
        <div>
          <div style={{ fontSize: '1.6rem', lineHeight: 1 }}>{countryFlag(country.a2)}</div>
          <h2 style={{ margin: '5px 0 2px', fontSize: '1.15rem', fontWeight: 800 }}>
            {stateName ? `${stateName}` : country.name}
          </h2>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-dim)' }}>
            {stateName ? `${country.name} · ${scopedDomains.length} domains` : `${country.total.toLocaleString()} domains tracked`}
          </div>
        </div>
        <button onClick={onClose} className="gd-close-btn">✕</button>
      </div>

      <div style={{ padding: '0 1.1rem 1.25rem' }}>
        {/* World rank */}
        {!stateName && (
          <div className="gd-rank-card" style={{ borderColor: rankColor+'44', background: rankColor+'0a', marginTop: '0.9rem' }}>
            <div className="gd-rank-label">Global Quantum Rank</div>
            <div className="gd-rank-value" style={{ color: rankColor }}>{rankMedal(rank)}</div>
            <div className="gd-rank-sub">of {totalCountries} tracked countries</div>
            <div className="gd-rank-desc" style={{ color: rankColor }}>
              {rank <= 3 ? 'World leader in quantum readiness' : rank <= 10 ? 'Top 10 globally' : rank <= totalCountries/2 ? 'Above global average' : 'Below average — action needed'}
            </div>
          </div>
        )}
        {stateName && <div style={{ marginTop: '0.9rem' }} />}

        {/* Readiness bar */}
        <div style={{ marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', marginBottom: '4px' }}>
            <span style={{ color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '1px' }}>Quantum Readiness</span>
            <span style={{ color: barColor, fontWeight: 700, fontFamily: 'monospace' }}>{pct(country.secure, country.total)}%</span>
          </div>
          <div style={{ height: '6px', background: 'var(--border-bright)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct(country.secure, country.total)}%`, background: `linear-gradient(90deg, ${barColor}, ${barColor}99)`, borderRadius: '3px', transition: 'width 0.8s ease' }} />
          </div>
        </div>

        {/* Status pills */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginBottom: '0.9rem' }}>
          {[
            { l:'Secure',   v:country.secure,   c:'#00ff88' },
            { l:'Legacy',   v:country.legacy,   c:'#ffcc00' },
            { l:'Insecure', v:country.insecure, c:'#ff4d4d' },
          ].map(s => (
            <div key={s.l} style={{ background: s.c+'0d', borderRadius: '8px', padding: '7px 4px', textAlign: 'center', border: `1px solid ${s.c}33` }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 800, color: s.c, fontFamily: 'monospace' }}>{s.v}</div>
              <div style={{ fontSize: '0.6rem', color: 'var(--text-dim)' }}>{s.l}</div>
            </div>
          ))}
        </div>

        {/* Key exchange breakdown */}
        {country.ke.length > 0 && !stateName && (
          <div style={{ marginBottom: '0.9rem' }}>
            <div className="gd-section-label" style={{ marginBottom: '7px' }}>Key Exchange</div>
            {country.ke.slice(0, 5).map((k, i) => {
              const isPQC = /mlkem|kyber/i.test(k.name)
              const max = country.ke[0]?.count ?? 1
              return (
                <div key={k.name} style={{ marginBottom: '5px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', marginBottom: '2px' }}>
                    <span style={{ color: isPQC ? '#00ff88' : 'var(--text-dim)', fontFamily: 'monospace', fontSize: '0.65rem' }}>{k.name.length > 26 ? k.name.slice(0,26)+'…' : k.name}</span>
                    <span style={{ color: 'var(--text-dim)' }}>{k.count}</span>
                  </div>
                  <div style={{ height: '3px', background: 'var(--border-bright)', borderRadius: '2px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(k.count/max)*100}%`, background: CHART_COLORS[i%CHART_COLORS.length] }} />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Grade distribution */}
        {country.grades.length > 0 && !stateName && (
          <div style={{ marginBottom: '0.9rem' }}>
            <div className="gd-section-label" style={{ marginBottom: '7px' }}>Grade Distribution</div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {['A+','A','B','C','F'].map(g => {
                const entry = country.grades.find(e => e.grade === g)
                if (!entry) return null
                return (
                  <div key={g} style={{ textAlign: 'center', background: 'var(--bg-raised)', borderRadius: '7px', padding: '6px 10px', border: `1px solid ${(GRADE_COLORS[g]??'#a0a0a0')}44` }}>
                    <div style={{ fontSize: '1.1rem', fontWeight: 900, color: GRADE_COLORS[g]??'#a0a0a0', fontFamily: 'monospace' }}>{g}</div>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-dim)' }}>{entry.count}</div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Action buttons */}
        {(() => {
          // Determine explore button: direct sub-regions take priority, then virtual continent
          const directKey = !stateName && viewMode === 'globe' && REGION_SUBS[country.a2] ? country.a2 : null
          const continentKey = !stateName && viewMode === 'globe' && !directKey ? (COUNTRY_TO_REGION[country.a2] ?? null) : null
          const exploreKey = directKey ?? continentKey
          const exploreLabel = exploreKey ? `Explore ${REGION_LABEL[exploreKey] ?? 'Regions'} →` : null
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: '0.9rem' }}>
              <button className="gd-explore-btn" onClick={onOpenRegistry}
                style={{ borderColor: 'var(--accent-cyan)', color: 'var(--accent-cyan)' }}>
                ⬇ View Manifest{stateName ? ` — ${stateName}` : ` — ${country.name}`}
              </button>
              {exploreLabel && (
                <button className="gd-explore-btn" onClick={() => onExploreRegion(exploreKey!)}>
                  {exploreLabel}
                </button>
              )}
            </div>
          )
        })()}

        {/* Domain list */}
        <div>
          <div className="gd-section-label" style={{ marginBottom: '8px' }}>
            {stateName ? `Domains assigned to ${stateName}` : 'Tracked Domains'} ({scopedDomains.length})
          </div>
          {displayDomains.map(d => {
            const col = d.pqc_status === 'secure' ? '#00ff88' : d.pqc_status === 'legacy' ? '#ffcc00' : '#ff4d4d'
            return (
              <div key={d.hostname}
                onClick={() => onOpenReport(d.hostname)}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer' }}>
                <span style={{ fontSize:'0.72rem', color:'var(--text-main)', fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'240px' }}>{d.hostname}</span>
                <span style={{ fontSize:'0.62rem', fontWeight:700, color:col, flexShrink:0, marginLeft:'6px' }}>{d.overall_grade || d.pqc_status?.slice(0,3).toUpperCase()}</span>
              </div>
            )
          })}
          {scopedDomains.length > 20 && (
            <div style={{ fontSize:'0.68rem', color:'var(--text-dim)', marginTop:'8px', textAlign:'center' }}>
              +{scopedDomains.length - 20} more ·{' '}
              <span onClick={onOpenRegistry} style={{ color:'var(--accent-cyan)', cursor:'pointer' }}>view registry</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Probe Orbiter (animated dot that circles the globe) ───────────────────────
// Uses rAF instead of CSS keyframes so we always know the exact position
// and can land smoothly without snapping.
function ProbeOrbiter({ phase }: { phase: 'orbiting' | 'landing' | 'hidden' }) {
  const dotRef   = useRef<HTMLDivElement>(null)
  const rafRef   = useRef<number>(0)
  const angleRef = useRef(0)          // current orbit angle in radians
  const RX = 195, RY = 75            // orbit ellipse radii (px)

  useEffect(() => {
    if (phase === 'hidden') {
      cancelAnimationFrame(rafRef.current)
      return
    }

    if (phase === 'orbiting') {
      const tick = () => {
        angleRef.current = (angleRef.current + 0.022) % (2 * Math.PI)
        const a = angleRef.current
        const x = Math.cos(a) * RX
        const y = Math.sin(a) * RY
        // Depth cue: dimmer when "behind" globe (y < 0)
        const depth = 0.35 + 0.65 * (Math.sin(a) * 0.5 + 0.5)
        if (dotRef.current) {
          dotRef.current.style.transform = `translate(${x}px, ${y}px)`
          dotRef.current.style.opacity   = String(depth)
        }
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
      return () => cancelAnimationFrame(rafRef.current)
    }

    if (phase === 'landing') {
      // Stop orbit at current position, pulse bright then shrink to nothing
      cancelAnimationFrame(rafRef.current)
      const dot = dotRef.current
      if (!dot) return
      dot.style.opacity    = '1'
      dot.style.transition = 'none'
      // Frame 1: stay in place, burst bright
      dot.style.boxShadow = '0 0 0 0 rgba(0,209,255,0.9), 0 0 40px 16px rgba(0,209,255,0.7)'
      requestAnimationFrame(() => {
        // Frame 2: expand glow ring outward
        dot.style.transition = 'box-shadow 0.25s ease-out'
        dot.style.boxShadow  = '0 0 0 28px rgba(0,209,255,0), 0 0 60px 20px rgba(0,209,255,0)'
        setTimeout(() => {
          // Shrink the dot itself to zero
          dot.style.transition = 'transform 0.35s ease-in, opacity 0.35s ease-in'
          dot.style.transform  = dot.style.transform + ' scale(0)'
          dot.style.opacity    = '0'
        }, 200)
      })
    }
  }, [phase]) // eslint-disable-line

  if (phase === 'hidden') return null
  return (
    <div className="probe-orbiter-wrap" aria-hidden>
      <div ref={dotRef} className="probe-orbiter-dot" />
    </div>
  )
}

// ── Probe Console (upper-right terminal) ──────────────────────────────────────
type ProbePhase = 'idle' | 'opening' | 'active' | 'closing'

interface ProbeLandInfo {
  hostname: string
  a2: string | null
  countryName: string | null
  statusLabel: string
  gradeLabel: string
}

function ProbeConsole({ domain, onLand }: {
  domain: string | null
  onLand: (info: ProbeLandInfo) => void
}) {
  const [phase, setPhase]     = useState<ProbePhase>('idle')
  const [lines, setLines]     = useState<string[]>([])
  const [dots, setDots]       = useState('.')
  const [waiting, setWaiting] = useState(false)
  const linesRef = useRef<HTMLDivElement>(null)
  const abortRef = useRef(false)

  useEffect(() => {
    if (!waiting) return
    const id = setInterval(() => setDots(d => d.length >= 3 ? '.' : d + '.'), 400)
    return () => clearInterval(id)
  }, [waiting])

  useEffect(() => {
    if (linesRef.current) linesRef.current.scrollTop = linesRef.current.scrollHeight
  }, [lines])

  useEffect(() => {
    if (!domain) return
    abortRef.current = false
    setLines([])
    setPhase('opening')
    const t = setTimeout(() => {
      if (!abortRef.current) { setPhase('active'); runProbe() }
    }, 500)
    return () => { abortRef.current = true; clearTimeout(t) }
  }, [domain]) // eslint-disable-line

  function addLine(text: string) { setLines(prev => [...prev, text]) }

  async function runProbe() {
    const d = (domain ?? '').replace(/^www\./, '')
    addLine(`> LAUNCHING PROBE: ${d}`)
    setWaiting(true)
    addLine(`> SEARCHING DATABASE`)

    let exists = false, isStale = false
    let statusLabel = '', gradeLabel = ''
    try {
      const res  = await fetch(`${API_BASE}/v1/details/${d}`)
      const json = await res.json()
      const staleDate   = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      const lastUpdated = json.last_updated ? new Date(json.last_updated) : null
      exists      = !json.error
      isStale     = exists && lastUpdated ? lastUpdated < staleDate : false
      statusLabel = (json.pqc_status ?? '').toUpperCase()
      gradeLabel  = json.overall_grade ?? ''
      if (exists && isStale && lastUpdated)
        addLine(`> RECORD STALE — LAST SEEN ${lastUpdated.toLocaleDateString()}`)
    } catch { /* treat as not-found */ }
    setWaiting(false)

    if (!exists) addLine(`> NO RECORD FOUND`)

    if (!exists || isStale) {
      addLine(`> ACQUIRING FRESH SCAN`)
      setWaiting(true)
      try { await fetch(`${PROBE_URL}/v1/check/${d}?force=true`) } catch { /* SiteReportBody retries */ }
      setWaiting(false)
      addLine(`> SCAN COMPLETE`)
    } else {
      addLine(`> RECORD LOCATED`)
      if (statusLabel) addLine(`> STATUS: ${statusLabel}`)
      if (gradeLabel)  addLine(`> GRADE:  ${gradeLabel}`)
    }

    // Resolve geo country via enrich
    addLine(`> LOCATING SIGNAL`)
    setWaiting(true)
    let a2: string | null = null
    let countryName: string | null = null
    try {
      const er = await fetch(`${API_BASE}/v1/enrich/${d}`)
      const ej = await er.json()
      a2 = ej?.geo?.country_code ?? null
      countryName = ej?.geo?.country_name ?? null
      if (a2) addLine(`> SIGNAL ORIGIN: ${countryFlag(a2)} ${countryName ?? a2}`)
    } catch { /* no geo */ }
    setWaiting(false)

    addLine(`> PROBE RETURNING`)
    // Close console and fire land callback
    setPhase('closing')
    setTimeout(() => {
      setPhase('idle')
      setLines([])
      onLand({ hostname: d, a2, countryName, statusLabel, gradeLabel })
    }, 500)
  }

  if (phase === 'idle') return null
  return (
    <div className="probe-console-box-wrap">
      <div className={`probe-console-box ${phase}`}>
        <div className="probe-console-bar">
          <span className="probe-dot red" /><span className="probe-dot yellow" /><span className="probe-dot green" />
          <span className="probe-console-title">PQC PROBE TERMINAL</span>
        </div>
        <div className="probe-console-log" ref={linesRef}>
          {lines.map((l, i) => <div key={i} className="probe-log-line">{l}</div>)}
          {waiting && <div className="probe-log-line probe-log-waiting">&gt; {dots}</div>}
        </div>
      </div>
    </div>
  )
}

// ── POLARIS-1 Robot SVG ───────────────────────────────────────────────────────
function Polaris1Robot({ size = 130 }: { size?: number }) {
  return (
    <svg viewBox="0 0 130 200" xmlns="http://www.w3.org/2000/svg"
      style={{ width: size, height: size * 1.54, overflow: 'visible', filter: 'drop-shadow(0 0 12px rgba(0,209,255,0.25))' }}>
      <defs>
        <radialGradient id="p1-eye" cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#00d1ff" stopOpacity="1" />
          <stop offset="70%" stopColor="#0080cc" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#003355" stopOpacity="0.3" />
        </radialGradient>
        <radialGradient id="p1-pupil" cx="40%" cy="35%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="100%" stopColor="#00d1ff" stopOpacity="0.2" />
        </radialGradient>
        <linearGradient id="p1-body" x1="0%" y1="0%" x2="10%" y2="100%">
          <stop offset="0%" stopColor="#1e2d4a" />
          <stop offset="100%" stopColor="#0b1422" />
        </linearGradient>
        <linearGradient id="p1-head" x1="0%" y1="0%" x2="5%" y2="100%">
          <stop offset="0%" stopColor="#243358" />
          <stop offset="100%" stopColor="#111d30" />
        </linearGradient>
        <filter id="p1-glow">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="b" />
          <feComposite in="b" in2="SourceGraphic" operator="over" />
        </filter>
      </defs>

      {/* ── Antenna ── */}
      <line x1="65" y1="4" x2="65" y2="18" stroke="#00d1ff" strokeWidth="1.8" opacity="0.7" />
      <circle cx="65" cy="4" r="4.5" fill="#00d1ff" style={{ animation: 'p1-blink 2.8s ease-in-out infinite' }} />
      <circle cx="65" cy="4" r="2" fill="white" opacity="0.9" />

      {/* ── Head ── */}
      <rect x="22" y="18" width="86" height="60" rx="14" fill="url(#p1-head)"
        stroke="#00d1ff" strokeWidth="1.6" strokeOpacity="0.7" />
      {/* Visor band */}
      <rect x="22" y="30" width="86" height="26" rx="3" fill="rgba(0,209,255,0.07)" />
      <rect x="24" y="31" width="82" height="24" rx="2" fill="none"
        stroke="rgba(0,209,255,0.15)" strokeWidth="0.6" />

      {/* Eyes */}
      <circle cx="47" cy="43" r="10" fill="#051828" stroke="#00d1ff" strokeWidth="1.4" strokeOpacity="0.8" />
      <circle cx="83" cy="43" r="10" fill="#051828" stroke="#00d1ff" strokeWidth="1.4" strokeOpacity="0.8" />
      <circle cx="47" cy="43" r="7" fill="url(#p1-eye)" style={{ animation: 'p1-eyeglow 2.4s ease-in-out infinite' }} />
      <circle cx="83" cy="43" r="7" fill="url(#p1-eye)" style={{ animation: 'p1-eyeglow 2.4s ease-in-out infinite' }} />
      <circle cx="47" cy="43" r="3" fill="url(#p1-pupil)" />
      <circle cx="83" cy="43" r="3" fill="url(#p1-pupil)" />
      {/* Eye shine */}
      <circle cx="50" cy="40" r="1.2" fill="white" opacity="0.8" />
      <circle cx="86" cy="40" r="1.2" fill="white" opacity="0.8" />

      {/* Speaker grille mouth */}
      <rect x="42" y="62" width="46" height="11" rx="4" fill="rgba(0,209,255,0.06)"
        stroke="rgba(0,209,255,0.25)" strokeWidth="0.7" />
      {[65, 69, 73].map(y => (
        <line key={y} x1="45" y1={y} x2="85" y2={y}
          stroke="#00d1ff" strokeWidth="0.7" opacity="0.35" />
      ))}
      {/* Ear panels */}
      <rect x="10" y="34" width="12" height="18" rx="5" fill="url(#p1-head)"
        stroke="rgba(0,209,255,0.3)" strokeWidth="1" />
      <rect x="108" y="34" width="12" height="18" rx="5" fill="url(#p1-head)"
        stroke="rgba(0,209,255,0.3)" strokeWidth="1" />
      {/* Rank pip on ears */}
      <circle cx="16" cy="43" r="2.5" fill="#00d1ff" opacity="0.7" />
      <circle cx="114" cy="43" r="2.5" fill="#00d1ff" opacity="0.7" />

      {/* ── Neck ── */}
      <rect x="52" y="78" width="26" height="10" rx="4" fill="#0d1525"
        stroke="rgba(0,209,255,0.2)" strokeWidth="1" />
      {[80,83,86].map(y => (
        <line key={y} x1="55" y1={y} x2="75" y2={y}
          stroke="#00d1ff" strokeWidth="0.6" opacity="0.25" />
      ))}

      {/* ── Shoulders / Epaulettes ── */}
      {/* Left shoulder */}
      <rect x="6" y="88" width="24" height="14" rx="5" fill="url(#p1-body)"
        stroke="rgba(0,209,255,0.35)" strokeWidth="1" />
      {[91,95,99].map(y => (
        <line key={y} x1="9" y1={y} x2="27" y2={y}
          stroke="#00d1ff" strokeWidth="0.9" opacity="0.5" />
      ))}
      {/* Right shoulder */}
      <rect x="100" y="88" width="24" height="14" rx="5" fill="url(#p1-body)"
        stroke="rgba(0,209,255,0.35)" strokeWidth="1" />
      {[91,95,99].map(y => (
        <line key={y} x1="103" y1={y} x2="121" y2={y}
          stroke="#00d1ff" strokeWidth="0.9" opacity="0.5" />
      ))}

      {/* ── Arms ── */}
      <rect x="6" y="100" width="24" height="50" rx="8" fill="url(#p1-body)"
        stroke="rgba(0,209,255,0.2)" strokeWidth="1" />
      <rect x="8" y="116" width="20" height="5" rx="2" fill="rgba(0,209,255,0.18)" />
      <rect x="8" y="132" width="20" height="5" rx="2" fill="rgba(0,209,255,0.12)" />

      <rect x="100" y="100" width="24" height="50" rx="8" fill="url(#p1-body)"
        stroke="rgba(0,209,255,0.2)" strokeWidth="1" />
      <rect x="102" y="116" width="20" height="5" rx="2" fill="rgba(0,209,255,0.18)" />
      <rect x="102" y="132" width="20" height="5" rx="2" fill="rgba(0,209,255,0.12)" />

      {/* ── Body ── */}
      <rect x="28" y="88" width="74" height="80" rx="12" fill="url(#p1-body)"
        stroke="rgba(0,209,255,0.25)" strokeWidth="1.5" />
      {/* Chest panel */}
      <rect x="36" y="96" width="58" height="64" rx="7" fill="rgba(0,209,255,0.04)"
        stroke="rgba(0,209,255,0.18)" strokeWidth="0.8" />

      {/* Star insignia */}
      <text x="65" y="124" textAnchor="middle" fontSize="20"
        fill="#00d1ff" opacity="0.85"
        style={{ filter: 'drop-shadow(0 0 4px #00d1ff)' }}>★</text>

      {/* Nameplate */}
      <rect x="38" y="133" width="54" height="14" rx="3.5"
        fill="rgba(0,209,255,0.1)" stroke="rgba(0,209,255,0.35)" strokeWidth="0.7" />
      <text x="65" y="143.5" textAnchor="middle" fontSize="6.5"
        fill="#00d1ff" fontFamily="'Courier New', monospace" letterSpacing="2" opacity="0.95">POLARIS-1</text>

      {/* Chest indicator lights — single shared animation, staggered via delay */}
      <circle cx="42" cy="112" r="2.5" fill="#00ff88" opacity="0.8"
        style={{ animation: 'p1-blink 4s ease-in-out infinite' }} />
      <circle cx="49" cy="112" r="2.5" fill="#00d1ff" opacity="0.6"
        style={{ animation: 'p1-blink 4s 1.3s ease-in-out infinite' }} />
      <circle cx="56" cy="112" r="2.5" fill="#ffcc00" opacity="0.5"
        style={{ animation: 'p1-blink 4s 2.6s ease-in-out infinite' }} />

      {/* ── Legs ── */}
      <rect x="34" y="168" width="26" height="26" rx="7" fill="url(#p1-body)"
        stroke="rgba(0,209,255,0.2)" strokeWidth="1" />
      <rect x="70" y="168" width="26" height="26" rx="7" fill="url(#p1-body)"
        stroke="rgba(0,209,255,0.2)" strokeWidth="1" />
      {/* Boot toe plates */}
      <rect x="35" y="188" width="24" height="5" rx="3" fill="rgba(0,209,255,0.2)"
        stroke="rgba(0,209,255,0.3)" strokeWidth="0.6" />
      <rect x="71" y="188" width="24" height="5" rx="3" fill="rgba(0,209,255,0.2)"
        stroke="rgba(0,209,255,0.3)" strokeWidth="0.6" />
    </svg>
  )
}

// ── POLARIS-1 Command Sitrep Panel ────────────────────────────────────────────
const TOUR_STEPS = [
  {
    id: 'left-console',
    icon: '◀',
    label: 'Left Console',
    desc: 'Your most recent sentry deployments and global country rankings. Toggle the left panel with the arrow in the top-left corner.',
    actionLabel: 'Open Console',
  },
  {
    id: 'globe',
    icon: '◎',
    label: 'The Globe',
    desc: 'Nations glow by their quantum readiness. Click any country to see its global rank, PQC adoption rate, and every domain we track inside it.',
    actionLabel: null,
  },
  {
    id: 'deploy',
    icon: '⬡',
    label: 'Deploy a Sentry',
    desc: 'Enter any domain in the search bar below and hit Deploy. The Sentry Array interrogates its full TLS posture and indexes it into the Manifest in real time.',
    actionLabel: 'Focus Search',
  },
  {
    id: 'company',
    icon: '◈',
    label: 'Company Sitrep',
    desc: 'Run a multi-domain company-level sweep. Enter a root domain to enumerate and grade an entire organization\'s cryptographic attack surface in one report.',
    actionLabel: 'Open Builder',
  },
  {
    id: 'manifest',
    icon: '≡',
    label: 'Sentry Manifest',
    desc: 'The full global database of every tracked site. Filter by status, grade, country, or cipher. Click any entry to read its complete quantum readiness report.',
    actionLabel: 'Open Manifest',
  },
] as const

const POLARIS_MSGS = [
  "POLARIS-1 ONLINE. QUANTUM SENTRY COMMAND SYSTEM ACTIVE.",
  "A new class of computer is coming — one powerful enough to break all of today's encryption.",
  "We call the day it arrives Q-DAY. Projected: 2035.",
  "Every website you visit, every encrypted message you send — all of it becomes readable on Q-Day.",
  "Worse: adversaries are already collecting your encrypted data TODAY, waiting to decrypt it when Q-Day arrives.",
  "This attack is called 'Harvest Now, Decrypt Later.' It is happening RIGHT NOW.",
  "The entire internet must upgrade to quantum-safe encryption before Q-Day — or billions are exposed.",
  "My mission: find every vulnerable site and generate a fix report before it is too late.",
  "I cannot be everywhere. I need you to command me.",
  "Enter any domain below. I will dispatch a sentry to scan its defenses and grade its quantum readiness.",
  "If the site is already safe: HARDENED. Move to the next target.",
  "If it is vulnerable: I generate a full report with exact steps to fix it. Share it with whoever runs the site.",
  "Every site we find and fix brings us one step closer to surviving Q-Day.",
  "AWAITING YOUR COMMAND, OPERATOR.",
]

function Polaris1Panel({
  open,
  onOpenLeft,
  onOpenRegistry,
  onOpenCompany,
  onFocusSearch,
}: {
  open: boolean
  onOpenLeft: () => void
  onOpenRegistry: () => void
  onOpenCompany: () => void
  onFocusSearch: () => void
}) {
  const [expanded, setExpanded] = useState<string | null>(null)
  const [display, setDisplay]   = useState('')
  const [msgIdx,  setMsgIdx]    = useState(0)
  const twRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    const msg = POLARIS_MSGS[msgIdx % POLARIS_MSGS.length]

    // type out
    let charIdx = 0
    const typeNext = () => {
      if (cancelled) return
      if (charIdx <= msg.length) {
        setDisplay(msg.slice(0, charIdx))
        charIdx++
        twRef.current = setTimeout(typeNext, 32)
      } else {
        // hold: ~320ms per word for 8th-grade read speed
        const wordCount = msg.split(' ').length
        const hold = Math.max(1400, wordCount * 320)
        twRef.current = setTimeout(eraseNext, hold)
      }
    }

    // erase
    let eraseLen = msg.length
    const eraseNext = () => {
      if (cancelled) return
      if (eraseLen >= 0) {
        setDisplay(msg.slice(0, eraseLen))
        eraseLen--
        twRef.current = setTimeout(eraseNext, 14)
      } else {
        twRef.current = setTimeout(() => {
          if (!cancelled) setMsgIdx(i => i + 1)
        }, 380)
      }
    }

    twRef.current = setTimeout(typeNext, msgIdx === 0 ? 600 : 120)

    return () => {
      cancelled = true
      if (twRef.current) clearTimeout(twRef.current)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msgIdx, open])

  const handleAction = (id: string) => {
    if (id === 'left-console') onOpenLeft()
    if (id === 'company')      onOpenCompany()
    if (id === 'manifest')     onOpenRegistry()
    if (id === 'deploy')       onFocusSearch()
  }

  return (
    <div className={`gd-right${open ? ' open' : ''}`}
      style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{`
        @keyframes p1-float {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-7px); }
        }
        @keyframes p1-blink {
          0%, 88%, 100% { opacity: 1; }
          93%            { opacity: 0.05; }
        }
        @keyframes p1-eyeglow {
          0%, 100% { opacity: 0.95; }
          50%       { opacity: 0.55; }
        }
        @keyframes p1-cursor-blink {
          0%, 100% { opacity: 0.9; }
          50%       { opacity: 0; }
        }
        .p1-float { animation: p1-float 4.2s ease-in-out infinite; }
      `}</style>

      {/* Header */}
      <div style={{
        padding: '1.1rem 1.25rem 0.8rem',
        borderBottom: '1px solid rgba(0,209,255,0.12)',
        background: 'rgba(0,209,255,0.03)',
        flexShrink: 0,
      }}>
        <div style={{ fontSize: '0.58rem', letterSpacing: '0.22em', color: 'var(--accent-cyan)', textTransform: 'uppercase', marginBottom: '0.2rem', opacity: 0.8 }}>
          POLARIS-1 // COMMAND SITREP
        </div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', lineHeight: 1.4 }}>
          Sentinel AI · Quantum Sentry Intelligence System
        </div>
      </div>

      {/* Robot + greeting */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.75rem 0 0.75rem', gap: '0.75rem', flexShrink: 0 }}>
        <video
          src="/polaris1.mp4"
          autoPlay loop muted playsInline
          style={{
            width: '100%',
            display: 'block',
            borderRadius: '0',
            border: 'none',
          }}
        />
        <div style={{
          background: 'rgba(0,0,0,0.35)',
          border: '1px solid rgba(0,209,255,0.22)',
          borderRadius: '8px',
          padding: '0.7rem 1rem',
          margin: '0 1rem',
          position: 'relative',
          height: '5.5rem',
          overflow: 'hidden',
        }}>
          {/* Speech bubble notch */}
          <div style={{
            position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '7px solid transparent',
            borderRight: '7px solid transparent',
            borderBottom: '8px solid rgba(0,209,255,0.22)',
          }} />
          <div style={{
            fontFamily: "'Courier New', monospace",
            fontSize: '0.72rem',
            color: 'var(--accent-cyan)',
            lineHeight: 1.6,
            textAlign: 'left',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}>
            {display}<span style={{ animation: 'p1-cursor-blink 0.8s step-end infinite', opacity: 0.9 }}>▍</span>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ margin: '0 1rem', borderTop: '1px solid rgba(0,209,255,0.08)', flexShrink: 0 }} />

      {/* Tour step cards */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 0.75rem 1.5rem', scrollbarWidth: 'thin' }}>
        <div style={{ fontSize: '0.58rem', letterSpacing: '0.18em', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: '0.6rem', paddingLeft: '0.25rem', opacity: 0.6 }}>
          Briefing Modules
        </div>
        {TOUR_STEPS.map((step, idx) => {
          const isOpen = expanded === step.id
          return (
            <div key={step.id}
              onClick={() => setExpanded(isOpen ? null : step.id)}
              style={{
                background: isOpen ? 'rgba(0,209,255,0.07)' : 'rgba(255,255,255,0.02)',
                border: `1px solid ${isOpen ? 'rgba(0,209,255,0.35)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: '8px',
                marginBottom: '6px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                overflow: 'hidden',
              }}>
              {/* Step header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.7rem 0.85rem' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '6px', flexShrink: 0,
                  background: isOpen ? 'rgba(0,209,255,0.15)' : 'rgba(0,209,255,0.06)',
                  border: `1px solid ${isOpen ? 'rgba(0,209,255,0.5)' : 'rgba(0,209,255,0.15)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.85rem',
                  color: isOpen ? 'var(--accent-cyan)' : 'var(--text-dim)',
                  transition: 'all 0.2s',
                }}>
                  {step.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <span style={{
                      fontSize: '0.6rem', color: 'var(--text-dim)', opacity: 0.5,
                      fontFamily: 'monospace',
                    }}>
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    <span style={{
                      fontSize: '0.8rem', fontWeight: 700,
                      color: isOpen ? 'var(--accent-cyan)' : 'var(--text-main)',
                      transition: 'color 0.2s',
                    }}>
                      {step.label}
                    </span>
                  </div>
                </div>
                <span style={{
                  fontSize: '0.65rem', color: 'var(--text-dim)',
                  transform: isOpen ? 'rotate(90deg)' : 'none',
                  transition: 'transform 0.2s',
                  opacity: 0.5,
                }}>▶</span>
              </div>

              {/* Expanded body */}
              {isOpen && (
                <div style={{ padding: '0 0.85rem 0.85rem', borderTop: '1px solid rgba(0,209,255,0.1)' }}>
                  <p style={{
                    fontSize: '0.75rem', color: 'var(--text-dim)', lineHeight: 1.65,
                    margin: '0.6rem 0 0.75rem', fontFamily: "'Courier New', monospace",
                  }}>
                    {step.desc}
                  </p>
                  {step.actionLabel && (
                    <button
                      onClick={e => { e.stopPropagation(); handleAction(step.id) }}
                      style={{
                        background: 'rgba(0,209,255,0.1)',
                        border: '1px solid rgba(0,209,255,0.4)',
                        color: 'var(--accent-cyan)',
                        borderRadius: '6px',
                        padding: '0.35rem 0.85rem',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        letterSpacing: '0.05em',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(0,209,255,0.18)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'rgba(0,209,255,0.1)')}
                    >
                      {step.actionLabel} →
                    </button>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {/* Footer status */}
        <div style={{
          marginTop: '1rem',
          padding: '0.65rem 0.75rem',
          background: 'rgba(0,255,136,0.04)',
          border: '1px solid rgba(0,255,136,0.15)',
          borderRadius: '7px',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#00ff88', flexShrink: 0, boxShadow: '0 0 6px #00ff88' }} />
          <span style={{ fontSize: '0.65rem', color: 'var(--text-dim)', fontFamily: 'monospace', lineHeight: 1.4 }}>
            POLARIS-1 online · Sentry Array active · All systems nominal
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Scan Modal (command-palette style) ────────────────────────────────────────
function ScanModal({ open, onClose, onProbe }: {
  open: boolean; onClose: () => void; onProbe: (domain: string) => void
}) {
  const [val, setVal]           = useState('')
  const [error, setError]       = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) { setVal(''); setError(null); setSubmitted(false) }
  }, [open])

  useEffect(() => {
    if (open && inputRef.current) {
      // small delay so CSS transition doesn't fight focus
      setTimeout(() => inputRef.current?.focus(), 80)
    }
  }, [open])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    if (open) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const parsed   = parseDomain(val)
  const showError = submitted && val.trim().length > 0 && !parsed

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitted(true)
    const domain = parseDomain(val)
    if (!domain) {
      setError(val.trim() ? 'Not a valid domain or URL' : 'Enter a domain or URL')
      return
    }
    onProbe(domain)
    onClose()
  }

  if (!open) return null
  return (
    <div className="scan-modal-backdrop" onClick={onClose}>
      <div className="scan-modal" onClick={e => e.stopPropagation()}>
        <div className="scan-modal-label">SCAN DOMAIN</div>
        <form className="scan-modal-form" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            className={`scan-modal-input${showError ? ' scan-modal-input-error' : ''}`}
            value={val}
            onChange={e => { setVal(e.target.value); if (submitted && parseDomain(e.target.value)) setError(null) }}
            placeholder="e.g. google.com or https://example.com"
            autoComplete="off"
            spellCheck={false}
          />
          <button type="submit" className="scan-modal-btn">Scan →</button>
        </form>
        {showError && <div className="scan-modal-error">⚠ {error}</div>}
        {!showError && parsed && val.includes('/') && (
          <div className="scan-modal-hint">→ {parsed}</div>
        )}
        <div className="scan-modal-footer">Press <kbd>Esc</kbd> to close · <kbd>/</kbd> to open from anywhere</div>
      </div>
    </div>
  )
}

// ── Footer Taskbar ────────────────────────────────────────────────────────────
function GlobeFooter({ onOpenScan }: {
  onOpenScan: () => void
}) {
  return (
    <div className="gd-footer">
      {/* Q-Day timer */}
      <div style={{ display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
        <span style={{ fontSize:'0.55rem', letterSpacing:'1.5px', textTransform:'uppercase', color:'var(--text-dim)' }}>Q-Day</span>
        <QDayCountdown compact />
      </div>
      {/* Scan trigger — centered absolutely */}
      <button className="gd-scan-trigger" onClick={onOpenScan}
        style={{ position:'absolute', left:'50%', transform:'translateX(-50%)' }}>
        <span className="gd-scan-trigger-icon">⬡</span>
        Scan a domain
        <span className="gd-scan-trigger-shortcut">/</span>
      </button>
    </div>
  )
}

// ── Country / State Map View ──────────────────────────────────────────────────
const US_GEO_URL   = 'https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json'
const WORLD_GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json'

// Generic hash: distribute a hostname deterministically across any set of keys
function domainSubHash(hostname: string, keys: string[]): string {
  let h = 0
  for (let i = 0; i < hostname.length; i++) h = (Math.imul(31, h) + hostname.charCodeAt(i)) | 0
  return keys[Math.abs(h) % keys.length]
}

// Sub-regions per region key: { abbr → { name, lat, lng } }
const REGION_SUBS: Record<string, Record<string, { name: string; lat: number; lng: number }>> = {
  US: Object.fromEntries(Object.entries(US_STATES).map(([k,v]) => [k, { name: v.name, lat: v.lat, lng: v.lng }])),
  AU: {
    NSW:{ name:'New South Wales',        lat:-32.1, lng:147.5 },
    VIC:{ name:'Victoria',              lat:-37.0, lng:144.7 },
    QLD:{ name:'Queensland',            lat:-22.5, lng:144.4 },
    SA: { name:'South Australia',       lat:-30.0, lng:136.0 },
    WA: { name:'Western Australia',     lat:-25.7, lng:121.6 },
    TAS:{ name:'Tasmania',              lat:-42.0, lng:146.5 },
    NT: { name:'Northern Territory',    lat:-19.5, lng:133.5 },
    ACT:{ name:'ACT',                   lat:-35.5, lng:149.0 },
  },
  IN: {
    MH: { name:'Maharashtra',     lat: 19.7, lng: 75.7 },
    UP: { name:'Uttar Pradesh',   lat: 26.9, lng: 80.9 },
    KA: { name:'Karnataka',       lat: 15.3, lng: 75.7 },
    TN: { name:'Tamil Nadu',      lat: 11.1, lng: 78.7 },
    WB: { name:'West Bengal',     lat: 22.9, lng: 87.8 },
    GJ: { name:'Gujarat',         lat: 22.3, lng: 72.0 },
    RJ: { name:'Rajasthan',       lat: 27.0, lng: 74.2 },
    MP: { name:'Madhya Pradesh',  lat: 23.5, lng: 77.7 },
    AP: { name:'Andhra Pradesh',  lat: 15.9, lng: 79.7 },
    TS: { name:'Telangana',       lat: 17.5, lng: 79.0 },
    KL: { name:'Kerala',          lat: 10.9, lng: 76.3 },
    HR: { name:'Haryana',         lat: 29.1, lng: 76.1 },
    PB: { name:'Punjab',          lat: 31.0, lng: 75.4 },
    DL: { name:'Delhi',           lat: 28.7, lng: 77.1 },
    BR: { name:'Bihar',           lat: 25.1, lng: 85.3 },
    OR: { name:'Odisha',          lat: 20.9, lng: 84.9 },
  },
  CN: {
    GD: { name:'Guangdong',  lat: 23.4, lng:113.4 },
    ZJ: { name:'Zhejiang',   lat: 29.1, lng:119.7 },
    JS: { name:'Jiangsu',    lat: 32.9, lng:119.5 },
    SH: { name:'Shanghai',   lat: 31.2, lng:121.5 },
    BJ: { name:'Beijing',    lat: 40.0, lng:116.4 },
    SD: { name:'Shandong',   lat: 36.3, lng:117.0 },
    HUB:{ name:'Hubei',      lat: 30.6, lng:114.3 },
    SC: { name:'Sichuan',    lat: 30.1, lng:103.0 },
    FJ: { name:'Fujian',     lat: 26.1, lng:117.3 },
    HUN:{ name:'Hunan',      lat: 27.6, lng:111.7 },
    AH: { name:'Anhui',      lat: 31.8, lng:117.3 },
    HEB:{ name:'Hebei',      lat: 38.0, lng:114.5 },
    SAX:{ name:'Shaanxi',    lat: 35.2, lng:108.9 },
    LN: { name:'Liaoning',   lat: 41.8, lng:123.4 },
    GX: { name:'Guangxi',    lat: 23.8, lng:108.8 },
    CQ: { name:'Chongqing',  lat: 29.6, lng:106.6 },
  },
  RU: {
    CFD: { name:'Central',       lat: 56.0, lng: 40.0 },
    NWFD:{ name:'Northwest',     lat: 61.0, lng: 40.0 },
    SFD: { name:'South',         lat: 45.5, lng: 44.0 },
    NCFD:{ name:'N. Caucasus',   lat: 43.7, lng: 43.5 },
    PFD: { name:'Volga',         lat: 53.0, lng: 51.0 },
    UFD: { name:'Ural',          lat: 59.0, lng: 61.5 },
    SIFD:{ name:'Siberia',       lat: 55.0, lng: 82.0 },
    FEFD:{ name:'Far East',      lat: 56.0, lng:130.0 },
  },
  // Virtual regions — sub-items ARE countries with real DB data
  EU: {
    DE:{ name:'Germany',     lat: 51.2, lng: 10.5 },
    FR:{ name:'France',      lat: 46.2, lng:  2.2 },
    GB:{ name:'UK',          lat: 54.0, lng: -2.0 },
    IT:{ name:'Italy',       lat: 41.9, lng: 12.6 },
    ES:{ name:'Spain',       lat: 40.4, lng: -3.7 },
    PL:{ name:'Poland',      lat: 51.9, lng: 19.1 },
    NL:{ name:'Netherlands', lat: 52.4, lng:  4.9 },
    BE:{ name:'Belgium',     lat: 50.5, lng:  4.5 },
    SE:{ name:'Sweden',      lat: 62.0, lng: 16.5 },
    NO:{ name:'Norway',      lat: 64.0, lng: 16.0 },
    FI:{ name:'Finland',     lat: 63.0, lng: 26.0 },
    DK:{ name:'Denmark',     lat: 56.3, lng:  9.5 },
    CH:{ name:'Switzerland', lat: 46.8, lng:  8.2 },
    AT:{ name:'Austria',     lat: 47.5, lng: 14.6 },
    CZ:{ name:'Czechia',     lat: 49.8, lng: 15.5 },
    HU:{ name:'Hungary',     lat: 47.2, lng: 19.5 },
    RO:{ name:'Romania',     lat: 45.9, lng: 24.9 },
    PT:{ name:'Portugal',    lat: 39.4, lng: -8.2 },
    GR:{ name:'Greece',      lat: 39.1, lng: 21.8 },
    UA:{ name:'Ukraine',     lat: 49.0, lng: 31.5 },
    IE:{ name:'Ireland',     lat: 53.4, lng: -7.9 },
    BG:{ name:'Bulgaria',    lat: 42.7, lng: 25.5 },
    RS:{ name:'Serbia',      lat: 44.0, lng: 21.0 },
  },
  SA_REG: {
    BR:{ name:'Brazil',     lat:-14.2, lng:-51.9 },
    AR:{ name:'Argentina',  lat:-38.4, lng:-63.6 },
    CO:{ name:'Colombia',   lat:  4.6, lng:-74.1 },
    CL:{ name:'Chile',      lat:-35.7, lng:-71.5 },
    PE:{ name:'Peru',       lat: -9.2, lng:-75.0 },
    VE:{ name:'Venezuela',  lat:  6.4, lng:-66.6 },
    EC:{ name:'Ecuador',    lat: -1.8, lng:-78.2 },
    BO:{ name:'Bolivia',    lat:-16.3, lng:-63.6 },
    PY:{ name:'Paraguay',   lat:-23.4, lng:-58.4 },
    UY:{ name:'Uruguay',    lat:-32.5, lng:-55.8 },
  },
  CA_REG: {
    MX:{ name:'Mexico',      lat: 23.6, lng:-102.6 },
    GT:{ name:'Guatemala',   lat: 15.8, lng: -90.2 },
    HN:{ name:'Honduras',    lat: 15.2, lng: -86.2 },
    SV:{ name:'El Salvador', lat: 13.8, lng: -88.9 },
    NI:{ name:'Nicaragua',   lat: 12.9, lng: -85.2 },
    CR:{ name:'Costa Rica',  lat:  9.7, lng: -83.8 },
    PA:{ name:'Panama',      lat:  8.5, lng: -80.8 },
    CU:{ name:'Cuba',        lat: 21.5, lng: -80.0 },
    DO:{ name:'Dom. Rep.',   lat: 18.7, lng: -70.2 },
  },
}

// Which region key is "virtual" (sub-items are actual A2 country codes with DB data)
const VIRTUAL_REGIONS = new Set(['EU', 'SA_REG', 'CA_REG'])

// Country A2 → region key (for virtual continent views)
const COUNTRY_TO_REGION: Record<string, string> = {
  DE:'EU', FR:'EU', GB:'EU', IT:'EU', ES:'EU', PL:'EU', NL:'EU', BE:'EU',
  SE:'EU', NO:'EU', FI:'EU', DK:'EU', CH:'EU', AT:'EU', CZ:'EU', HU:'EU',
  RO:'EU', PT:'EU', GR:'EU', UA:'EU', IE:'EU', BG:'EU', RS:'EU',
  BR:'SA_REG', AR:'SA_REG', CO:'SA_REG', CL:'SA_REG', PE:'SA_REG',
  VE:'SA_REG', EC:'SA_REG', BO:'SA_REG', PY:'SA_REG', UY:'SA_REG',
  MX:'CA_REG', GT:'CA_REG', HN:'CA_REG', SV:'CA_REG', NI:'CA_REG',
  CR:'CA_REG', PA:'CA_REG', CU:'CA_REG', DO:'CA_REG',
}

// Human-readable labels
const REGION_LABEL: Record<string, string> = {
  US:'US States', AU:'Australian States', IN:'Indian States',
  CN:'Chinese Provinces', RU:'Russian Districts',
  EU:'Europe', SA_REG:'South America', CA_REG:'Central America',
}

// Map projection center and scale for each region
const REGION_MAP_POV: Record<string, { center: [number,number]; scale: number }> = {
  US:     { center: [-96, 37],  scale: 700 },
  AU:     { center: [134,-27],  scale: 700 },
  IN:     { center: [80, 23],   scale: 1100 },
  CN:     { center: [105, 35],  scale: 700 },
  RU:     { center: [95, 62],   scale: 280 },
  EU:     { center: [15, 52],   scale: 700 },
  SA_REG: { center: [-58,-15],  scale: 420 },
  CA_REG: { center: [-87, 15],  scale: 1800 },
}


function CountryStateMap({ country, domains, countryData, regionKey, onStateSelect, onSubCountrySelect, onBack }: {
  country: SelectedCountry; domains: Domain[]; countryData: CountryData
  regionKey: string
  onStateSelect: (stateName: string) => void
  onSubCountrySelect: (a2: string, name: string) => void
  onBack: () => void
}) {
  const isUS      = regionKey === 'US'
  const isVirtual = VIRTUAL_REGIONS.has(regionKey)
  const subs      = REGION_SUBS[regionKey] ?? {}
  const subKeys   = Object.keys(subs)
  const mapPov    = REGION_MAP_POV[regionKey]

  // Hash-distribute domains across non-virtual sub-regions
  const subMap = React.useMemo(() => {
    if (isVirtual || subKeys.length === 0) return {} as Record<string, Domain[]>
    const m: Record<string, Domain[]> = {}
    for (const k of subKeys) m[k] = []
    for (const d of domains) {
      const k = isUS ? domainStateHash(d.hostname) : domainSubHash(d.hostname, subKeys)
      if (m[k] !== undefined) m[k].push(d)
    }
    return m
  }, [domains, subKeys, isUS, isVirtual])

  // For virtual regions, pull actual counts from countryData
  const virtualData = React.useMemo(() => {
    if (!isVirtual) return {} as Record<string, { total:number; secure:number; legacy:number }>
    const out: Record<string, { total:number; secure:number; legacy:number }> = {}
    for (const a2 of subKeys) {
      const c = countryData[a2]
      out[a2] = c ? { total: c.total, secure: c.secure, legacy: c.legacy } : { total: 0, secure: 0, legacy: 0 }
    }
    return out
  }, [countryData, subKeys, isVirtual])

  const maxCount = isVirtual
    ? Math.max(...subKeys.map(a2 => virtualData[a2]?.total ?? 0), 1)
    : Math.max(...subKeys.map(k => subMap[k]?.length ?? 0), 1)

  const barColor = (key: string) => {
    const total  = isVirtual ? virtualData[key]?.total  : subMap[key]?.length
    const secure = isVirtual ? virtualData[key]?.secure : subMap[key]?.filter(d => d.pqc_status === 'secure').length
    const legacy = isVirtual ? virtualData[key]?.legacy : subMap[key]?.filter(d => d.pqc_status === 'legacy').length
    if (!total || total === 0) return 'rgba(160,160,160,0.25)'
    const s = secure ?? 0, l = legacy ?? 0, rest = (total ?? 0) - s - l
    return s > l && s > rest ? '#00ff88' : l > rest ? '#ffcc00' : '#ff4d4d'
  }

  const handleSubClick = (key: string) => {
    const sub = subs[key]
    if (!sub) return
    if (isVirtual) onSubCountrySelect(key, sub.name)
    else onStateSelect(sub.name)
  }

  const regionTitle = isVirtual
    ? (REGION_LABEL[regionKey] ?? regionKey)
    : `${countryFlag(country.a2)} ${country.name} — ${REGION_LABEL[regionKey] ?? 'Regions'}`

  return (
    <div className="country-stage-inner">
      <button className="country-back-btn" onClick={onBack}>← Globe</button>

      <div className="country-map-title">
        {!isVirtual && <span>{countryFlag(country.a2)}</span>}
        <span>{regionTitle}</span>
        <span className="country-map-sub">· {isVirtual
          ? `${subKeys.filter(k => (virtualData[k]?.total ?? 0) > 0).length} active regions`
          : `${domains.length} domains · click a region`}
        </span>
      </div>

      {isUS ? (
        /* US: AlbersUSA projection with state fills */
        <ComposableMap projection="geoAlbersUsa" width={960} height={600}
          style={{ width:'100%', height:'100%' }}>
          <Geographies geography={US_GEO_URL}>
            {({ geographies }: { geographies: any[] }) => (
              <>
                {geographies.map((geo: any) => {
                  const fips = geo.id?.toString()?.padStart(2, '0')
                  const abbr = FIPS_TO_ABBR[fips]
                  const doms = abbr ? (subMap[abbr] ?? []) : []
                  const { fill, stroke } = stateStatusColors(doms)
                  return (
                    <Geography key={geo.rsmKey} geography={geo}
                      onClick={() => { if (abbr) onStateSelect(US_STATES[abbr].name) }}
                      style={{
                        default: { fill, stroke, strokeWidth: 0.5, outline: 'none' },
                        hover:   { fill: 'rgba(0,209,255,0.25)', stroke: 'var(--accent-cyan)', strokeWidth: 0.8, outline: 'none', cursor: 'pointer' },
                        pressed: { fill: 'rgba(0,209,255,0.3)', outline: 'none' },
                      }} />
                  )
                })}
                {subKeys.filter(k => (subMap[k]?.length ?? 0) > 0).map((abbr, i) => {
                  const st = US_STATES[abbr]
                  return (
                    <Marker key={abbr} coordinates={[st.lng, st.lat]}>
                      <IsoBar count={subMap[abbr].length} maxCount={maxCount} color={barColor(abbr)}
                        onClick={() => onStateSelect(st.name)} delay={i * 20} />
                    </Marker>
                  )
                })}
              </>
            )}
          </Geographies>
        </ComposableMap>
      ) : (
        /* All other regions: Mercator projection with marker bars */
        <ComposableMap
          projection="geoMercator"
          projectionConfig={mapPov ? { center: mapPov.center, scale: mapPov.scale } : undefined}
          width={960} height={600}
          style={{ width:'100%', height:'100%' }}>
          <Geographies geography={WORLD_GEO_URL}>
            {({ geographies }: { geographies: any[] }) =>
              geographies.map((geo: any) => {
                const geoA2 = ISO_NUM_TO_A2[String(geo.id).padStart(3,'0')]
                const isHighlighted = isVirtual
                  ? !!(geoA2 && subs[geoA2])
                  : geoA2 === country.a2
                const virtColor = isHighlighted && isVirtual && geoA2
                  ? readinessColor((virtualData[geoA2]?.secure ?? 0) / Math.max(virtualData[geoA2]?.total ?? 1, 1))
                  : null
                return (
                  <Geography key={geo.rsmKey} geography={geo}
                    onClick={() => { if (isVirtual && geoA2 && subs[geoA2]) handleSubClick(geoA2) }}
                    style={{
                      default: {
                        fill: isHighlighted ? (virtColor ? virtColor + '22' : 'rgba(0,209,255,0.14)') : '#0c1220',
                        stroke: isHighlighted ? (virtColor ?? 'var(--accent-cyan)') : '#1c2030',
                        strokeWidth: isHighlighted ? 0.7 : 0.3, outline: 'none',
                      },
                      hover: {
                        fill: isHighlighted ? 'rgba(0,209,255,0.3)' : '#111825',
                        cursor: isHighlighted ? 'pointer' : 'default', outline: 'none',
                      },
                      pressed: { outline: 'none' },
                    }} />
                )
              })
            }
          </Geographies>
          {/* IsoBar markers at sub-region centroids */}
          {subKeys.map((key, i) => {
            const sub   = subs[key]
            const count = isVirtual ? (virtualData[key]?.total ?? 0) : (subMap[key]?.length ?? 0)
            return (
              <Marker key={key} coordinates={[sub.lng, sub.lat]}>
                <IsoBar count={count} maxCount={maxCount} color={barColor(key)}
                  onClick={() => handleSubClick(key)} delay={i * 18} />
              </Marker>
            )
          })}
        </ComposableMap>
      )}

      <div className="country-legend">
        <div className="country-legend-title">PQC Status</div>
        {[['#00ff88','PQC Secure'],['#ffcc00','Legacy'],['#ff4d4d','Insecure']].map(([c,l]) => (
          <div key={l} className="country-legend-item">
            <div style={{ width:9, height:9, borderRadius:2, background:c, flexShrink:0 }} /><span>{l}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── GlobeHUD — floating console panels around the globe ─────────────────────

function GlobeHUD({ stats, visible, leftOpen }: {
  stats: AnalyticsData | null
  visible: boolean
  leftOpen: boolean
}) {
  const total  = stats?.pqc_status.total  ?? 0
  const secure = stats?.pqc_status.secure ?? 0
  const legacy = stats?.pqc_status.legacy ?? 0
  const insecure = stats?.pqc_status.insecure ?? 0
  const atRisk = legacy + insecure

  const pqcPct = total ? Math.round((secure / total) * 100) : 0
  const tls13Pct = stats?.tls.total ? Math.round((stats.tls.tls13 / stats.tls.total) * 100) : 0
  const hstsPct  = stats?.hsts.total  ? Math.round((stats.hsts.with_hsts / stats.hsts.total) * 100) : 0

  // Grade bar segments
  const grades = stats?.grades ?? []
  const gradeTotal = grades.reduce((s, g) => s + g.count, 0)
  const GRADE_ORDER = ['A+','A','B','C','F']
  const GRADE_COL: Record<string,string> = { 'A+':'#00ff88','A':'#34d399','B':'#60a5fa','C':'#ffcc00','F':'#ff4d4d' }

  // KX top 3
  const kxList = (stats?.key_exchange ?? []).slice(0, 4)
  const kxMax  = kxList[0]?.count ?? 1
  const isPqcKx = (name: string) => /mlkem|kyber|x25519mlkem/i.test(name)

  // Threat level
  let threatLabel = 'MODERATE'
  let threatColor = '#ffcc00'
  if (pqcPct >= 30) { threatLabel = 'GUARDED'; threatColor = '#60a5fa' }
  if (pqcPct >= 50) { threatLabel = 'LOW';     threatColor = '#34d399' }
  if (pqcPct < 10)  { threatLabel = 'CRITICAL'; threatColor = '#ff4d4d' }

  // Harvest-now-decrypt-later exposure = non-PQC-secure domains
  const harvestExposure = total - secure

  return (
    <div className={`globe-hud${visible ? ' globe-hud-on' : ''}`}>
      {/* ── Top-right: Quantum Readiness ── */}
      <div className="globe-hud-panel globe-hud-tr">
        <div className="globe-hud-hdr">
          <span className="globe-hud-bracket">[</span>
          QUANTUM READINESS
          <span className="globe-hud-bracket">]</span>
          <div className="globe-hud-live" />
        </div>
        {!stats ? (
          <div className="globe-hud-loading">ACQUIRING SIGNAL…</div>
        ) : (
          <>
            <div className="globe-hud-big">
              <span className="globe-hud-big-n" style={{ color: pqcPct >= 30 ? '#34d399' : '#ffcc00' }}>
                {pqcPct}%
              </span>
              <div className="globe-hud-big-lbl">PQC PROTECTED DOMAINS</div>
            </div>
            <div className="globe-hud-grade-bar">
              {GRADE_ORDER.map(g => {
                const c = grades.find(x => x.grade === g)?.count ?? 0
                const w = gradeTotal ? (c / gradeTotal) * 100 : 0
                return <div key={g} style={{ width: `${w}%`, background: GRADE_COL[g] ?? '#475569', minWidth: w > 0 ? 2 : 0 }} />
              })}
            </div>
            <div className="globe-hud-grade-lbls">
              {GRADE_ORDER.map(g => (
                <span key={g} style={{ color: GRADE_COL[g] ?? '#475569', fontSize: '6px' }}>{g}</span>
              ))}
            </div>
            <div className="globe-hud-row">
              <span>DOMAINS INDEXED</span>
              <span className="globe-hud-val">{total.toLocaleString()}</span>
            </div>
            <div className="globe-hud-row">
              <span>PQC SECURE</span>
              <span className="globe-hud-val" style={{ color: '#34d399' }}>{secure.toLocaleString()}</span>
            </div>
            <div className="globe-hud-row">
              <span>AT RISK</span>
              <span className="globe-hud-val" style={{ color: '#ff4d4d' }}>{atRisk.toLocaleString()}</span>
            </div>
          </>
        )}
      </div>

      {/* ── Bottom-left: Key Exchange Intel ── */}
      <div className="globe-hud-panel globe-hud-bl" style={{ left: leftOpen ? 304 : 16 }}>
        <div className="globe-hud-hdr">
          <span className="globe-hud-bracket">[</span>
          KEY EXCHANGE INTEL
          <span className="globe-hud-bracket">]</span>
          <div className="globe-hud-live" />
        </div>
        {!stats ? (
          <div className="globe-hud-loading">ACQUIRING SIGNAL…</div>
        ) : (
          <>
            {kxList.map((kx, i) => {
              const isPqc = isPqcKx(kx.name)
              const fillW = kxMax ? Math.round((kx.count / kxMax) * 100) : 0
              const fillColor = isPqc ? '#34d399' : '#00d4ff'
              const shortName = kx.name.replace('Classical (','').replace(')','').replace('Hybrid (','').replace('PQC (','')
              return (
                <div key={kx.name} className="globe-hud-kx">
                  <span className="globe-hud-kx-rank">#{i + 1}</span>
                  <span className="globe-hud-kx-name" style={{ color: isPqc ? '#34d399' : '#94a3b8' }} title={kx.name}>{shortName}</span>
                  <div className="globe-hud-kx-track">
                    <div className="globe-hud-kx-fill" style={{ width: `${fillW}%`, background: fillColor }} />
                  </div>
                  <span className="globe-hud-kx-ct">{kx.count}</span>
                </div>
              )
            })}
            <div className="globe-hud-row" style={{ marginTop: 4 }}>
              <span>PQC KEY EXCHANGE</span>
              <span className="globe-hud-val" style={{ color: '#34d399' }}>
                {(() => {
                  const pqcKxTotal = kxList.filter(k => isPqcKx(k.name)).reduce((s, k) => s + k.count, 0)
                  const kxTotal    = kxList.reduce((s, k) => s + k.count, 0)
                  return kxTotal ? `${Math.round((pqcKxTotal / kxTotal) * 100)}%` : '—'
                })()}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── Bottom-right: Threat Signal ── */}
      <div className="globe-hud-panel globe-hud-br">
        <div className="globe-hud-hdr">
          <span className="globe-hud-bracket">[</span>
          THREAT SIGNAL
          <span className="globe-hud-bracket">]</span>
          <div className="globe-hud-live" />
        </div>
        {!stats ? (
          <div className="globe-hud-loading">ACQUIRING SIGNAL…</div>
        ) : (
          <>
            <div className="globe-hud-threat" style={{ color: threatColor, borderColor: `${threatColor}55`, background: `${threatColor}0f` }}>
              <span style={{ fontSize: 11 }}>◈</span>
              {threatLabel}
            </div>
            <div className="globe-hud-row">
              <span>TLS 1.3 ADOPTION</span>
              <span className="globe-hud-val">{tls13Pct}%</span>
            </div>
            <div className="globe-hud-row">
              <span>HSTS ENFORCED</span>
              <span className="globe-hud-val">{hstsPct}%</span>
            </div>
            <div className="globe-hud-row">
              <span>HARVEST EXPOSURE</span>
              <span className="globe-hud-val" style={{ color: harvestExposure > 3000 ? '#ff4d4d' : '#ffcc00' }}>
                {harvestExposure.toLocaleString()}
              </span>
            </div>
            <div className="globe-hud-row">
              <span>FORWARD SECRECY</span>
              <span className="globe-hud-val">
                {stats.forward_secrecy.total
                  ? `${Math.round((stats.forward_secrecy.with_fs / stats.forward_secrecy.total) * 100)}%`
                  : '—'}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function GlobalView({ initialReport, onConsoleHome }: { initialReport?: string; onConsoleHome?: () => void } = {}) {
  const navigate = useNavigate()
  const [stats,        setStats]        = useState<AnalyticsData | null>(null)
  const [countryData,  setCountryData]  = useState<CountryData>({})
  const [recentDomains,setRecentDomains]= useState<Domain[]>([])
  const [selectedCountry, setSelectedCountry] = useState<SelectedCountry | null>(null)
  const [viewMode,     setViewMode]     = useState<'globe' | 'region'>('globe')
  const [rightOpen,    setRightOpen]    = useState(false)
  const [leftOpen,     setLeftOpen]     = useState(true)
  const [selectedState,setSelectedState]= useState<string | null>(null)
  const [countryDomains,setCountryDomains] = useState<Domain[]>([])
  const [registryOpen,  setRegistryOpen]  = useState(false)
  const [reportDomain,  setReportDomain]  = useState<string | null>(initialReport ?? null)
  const [regionKey,     setRegionKey]     = useState<string>('US')
  const [blogOpen,      setBlogOpen]      = useState(false)
  const [blogSlug,      setBlogSlug]      = useState<string | null>(null)
  const [sectorsOpen,       setSectorsOpen]       = useState(false)
  const [companyOpen,       setCompanyOpen]       = useState(false)
  const [companyConfig,     setCompanyConfig]     = useState<CompanyConfig | null>(null)
  const [companyInitialTab, setCompanyInitialTab] = useState<'current' | 'history'>('current')
  const [reportBuilderOpen, setReportBuilderOpen] = useState(false)
  const [probeTarget,   setProbeTarget]   = useState<string | null>(null)
  const [orbiterPhase,  setOrbiterPhase]  = useState<'orbiting' | 'landing' | 'hidden'>('hidden')
  const [scanModalOpen, setScanModalOpen] = useState(false)
  const [starsEnabled,  setStarsEnabled]  = useState<boolean>(() => {
    try { return localStorage.getItem('qs-stars') !== 'off' } catch { return true }
  })
  const [showWelcome, setShowWelcome] = useState<boolean>(() => {
    try { return !localStorage.getItem('qs-welcomed') } catch { return true }
  })
  const globeInstanceRef  = useRef<any>(null)

  // Global "/" shortcut opens scan modal
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === '/' && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        e.preventDefault()
        setScanModalOpen(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const handleToggleStars = () => {
    setStarsEnabled(on => {
      const next = !on
      try { localStorage.setItem('qs-stars', next ? 'on' : 'off') } catch {}
      return next
    })
  }

  // Registry title reflects current country/state context
  const registryTitle = selectedState
    ? `${selectedState} — Quantum Inventory`
    : selectedCountry
      ? `${countryFlag(selectedCountry.a2)} ${selectedCountry.name} — Quantum Inventory`
      : 'Global Quantum Inventory'
  const registryCountryFilter = selectedCountry?.a2 ?? null

  function handleOpenRegistry() {
    setCompanyOpen(false)
    setRegistryOpen(o => !o)
  }
  function handleDomainSelect(hostname: string) {
    setReportDomain(hostname)
  }

  // Refresh all globe / sidebar data — called on mount and after each probe completes
  const refreshGlobeData = useCallback(async () => {
    const [statsRes, recentRes, countryRes] = await Promise.allSettled([
      fetch(`${API_BASE}/v1/analytics`).then(r => r.json()),
      fetch(`${API_BASE}/v1/list?limit=20`).then(r => r.json()),
      fetch(`${API_BASE}/v1/analytics/country`).then(r => r.json()),
    ])
    if (statsRes.status === 'fulfilled') setStats(statsRes.value)
    if (recentRes.status === 'fulfilled') {
      const recent: Domain[] = recentRes.value.results || []
      setRecentDomains(recent)
      // Fire background enrich for plugin-submitted domains missing geo/country data
      // (the probe worker never calls /v1/enrich, so asn_country stays null)
      const needsEnrich = recent.filter(d => !d.asn_country).slice(0, 6)
      for (const d of needsEnrich) {
        fetch(`${API_BASE}/v1/enrich/${d.hostname}`).catch(() => {})
      }
    }
    if (countryRes.status === 'fulfilled') {
      const map: CountryData = {}
      for (const row of countryRes.value) {
        map[row.country] = { total: row.total, secure: row.secure, legacy: row.legacy, insecure: row.insecure, ke: {}, grades: {} }
      }
      setCountryData(map)
    }
  }, []) // eslint-disable-line

  useEffect(() => { refreshGlobeData() }, []) // eslint-disable-line

  const countryRanks: Record<string,number> = {}
  Object.entries(countryData)
    .filter(([,c])=>c.total>0)
    .sort(([,a],[,b])=>(b.secure/b.total)-(a.secure/a.total))
    .forEach(([a2],i)=>{ countryRanks[a2]=i+1 })

  function handleCountrySelect(country: SelectedCountry | null) {
    if (!country) return;
    setCompanyOpen(false)
    setSelectedCountry(country)
    setSelectedState(null)
    setRightOpen(true)
    // Fetch domains for this country
    fetch(`${API_BASE}/v1/list?country=${country.a2}&limit=100`)
      .then(r=>r.json()).then((d:any)=>setCountryDomains(d.results||[])).catch(()=>{})
    // Zoom globe camera to country
    const pov = COUNTRY_POV[country.a2]
    if (pov && globeInstanceRef.current) {
      globeInstanceRef.current.controls().autoRotate = false
      globeInstanceRef.current.pointOfView({ lat:pov[0], lng:pov[1], altitude:pov[2] }, 1200)
    }
  }

  function handleExploreRegion(key: string) { setRegionKey(key); setViewMode('region') }

  function handleSubCountrySelect(a2: string, name: string) {
    // Treat virtual-region sub-country click as a full country selection
    const cd = countryData[a2]
    const country: SelectedCountry = {
      a2, name,
      total: cd?.total ?? 0, secure: cd?.secure ?? 0,
      legacy: cd?.legacy ?? 0, insecure: cd?.insecure ?? 0,
      ke: [], grades: [], rank: 0, totalCountries: 0,
    }
    setViewMode('globe')
    handleCountrySelect(country)
  }

  function handleBackToGlobe() {
    setViewMode('globe')
    setSelectedState(null)
    if (globeInstanceRef.current) {
      globeInstanceRef.current.controls().autoRotate = true
      globeInstanceRef.current.pointOfView({ lat:20, lng:0, altitude:3 }, 1200)
    }
  }

  function handleResetGlobe() {
    // Close everything and return to centered globe
    setViewMode('globe')
    setRightOpen(false)
    setSelectedCountry(null)
    setSelectedState(null)
    setCountryDomains([])
    setRegistryOpen(false)
    setReportDomain(null)
    setBlogOpen(false)
    setBlogSlug(null)
    setSectorsOpen(false)
    setCompanyOpen(false)
    if (globeInstanceRef.current) {
      globeInstanceRef.current.controls().autoRotate = true
      globeInstanceRef.current.pointOfView({ lat:20, lng:0, altitude:3 }, 1200)
    }
  }

  function handleOpenBlog() { setBlogOpen(o => !o) }
  function handleOpenCompany() {
    setSectorsOpen(false)
    setRegistryOpen(false)
    setReportDomain(null)
    setReportBuilderOpen(true)
  }

  function handleProbeLand(info: ProbeLandInfo) {
    setOrbiterPhase('landing')
    const a2 = info.a2

    // 650ms: landing flash finishes → refresh globe data then open panels
    setTimeout(async () => {
      setOrbiterPhase('hidden')

      // Refresh stats + recent + countryData — probe may have added a new country
      await refreshGlobeData()

      // Use functional updater to read the freshly-set countryData
      setCountryData(freshMap => {
        const cd = a2 ? freshMap[a2] : null
        if (a2 && cd) {
          const country: SelectedCountry = {
            a2, name: info.countryName ?? a2,
            total: cd.total, secure: cd.secure, legacy: cd.legacy, insecure: cd.insecure,
            ke: [], grades: [], rank: 0, totalCountries: Object.keys(freshMap).length,
          }
          const pov = COUNTRY_POV[a2]
          if (pov && globeInstanceRef.current) {
            globeInstanceRef.current.controls().autoRotate = false
            globeInstanceRef.current.pointOfView({ lat: pov[0], lng: pov[1], altitude: pov[2] }, 1000)
          }
          setSelectedCountry(country)
          setSelectedState(null)
          setRightOpen(true)
          fetch(`${API_BASE}/v1/list?country=${a2}&limit=50`)
            .then(r => r.json()).then((d: any) => setCountryDomains(d.results || [])).catch(() => {})
        }
        return freshMap // no change, we just read it
      })

      // Open report once globe has settled
      setTimeout(() => handleDomainSelect(info.hostname), 1100)
    }, 650)
  }

  // Show POLARIS-1 panel when no right-side content is occupying the pane
  const showPolaris = !rightOpen && !registryOpen && !reportDomain &&
                      !sectorsOpen && !companyOpen && !reportBuilderOpen && !blogOpen

  return (
    <div className="globe-desktop">
      {onConsoleHome && (
        <button className="console-home-btn" onClick={onConsoleHome}>◀ CONSOLE</button>
      )}
      {/* Globe canvas — always rendered, hidden when region view is active */}
      <div className={`globe-canvas-wrap${viewMode==='region' ? ' globe-hidden' : ''}`}>
        <QuantumGlobe
          countryData={countryData}
          countryRanks={countryRanks}
          onSelect={handleCountrySelect}
          onGlobeReady={(g:any) => { globeInstanceRef.current = g }}
          selectedA2={selectedCountry?.a2}
          starsEnabled={starsEnabled}
          orbiterPhase={orbiterPhase}
        />
        <GlobeHUD stats={stats} visible={showPolaris && viewMode === 'globe'} leftOpen={leftOpen} />
      </div>

      {/* Country / state map overlay */}
      <div className={`country-stage${viewMode==='region' ? ' visible' : ''}`}>
        {selectedCountry && viewMode==='region' && (
          <CountryStateMap
            country={selectedCountry}
            domains={countryDomains}
            countryData={countryData}
            regionKey={regionKey}
            onStateSelect={(name:string) => { setSelectedState(name); setRightOpen(true) }}
            onSubCountrySelect={handleSubCountrySelect}
            onBack={handleBackToGlobe}
          />
        )}
      </div>

      {/* OS Desktop Chrome */}
      <GlobeTopBar
        stats={stats} countryData={countryData}
        leftOpen={leftOpen} onToggleLeft={() => setLeftOpen(o=>!o)}
        navigate={navigate} onResetGlobe={handleResetGlobe} onOpenSectors={() => { setCompanyOpen(false); setSectorsOpen(o => !o) }} onOpenCompany={handleOpenCompany}
        starsEnabled={starsEnabled} onToggleStars={handleToggleStars}
      />
      <GlobeLeftPanel domains={recentDomains} open={leftOpen} onOpenReport={handleDomainSelect} />
      <GlobeRightSlideout
        open={rightOpen} country={selectedCountry} stateName={selectedState}
        domains={countryDomains} countryRanks={countryRanks}
        onClose={() => { setRightOpen(false); setSelectedCountry(null); setSelectedState(null); setCountryDomains([]); setViewMode('globe') }}
        onExploreRegion={handleExploreRegion}
        onOpenRegistry={handleOpenRegistry}
        onOpenReport={handleDomainSelect}
        viewMode={viewMode}
      />
      <Polaris1Panel
        open={showPolaris}
        onOpenLeft={() => setLeftOpen(o => !o)}
        onOpenRegistry={handleOpenRegistry}
        onOpenCompany={handleOpenCompany}
        onFocusSearch={() => setScanModalOpen(true)}
      />
      <GlobeFooter onOpenScan={() => setScanModalOpen(true)} />

      {/* ── Scan modal ── */}
      <ScanModal
        open={scanModalOpen}
        onClose={() => setScanModalOpen(false)}
        onProbe={d => { setScanModalOpen(false); setProbeTarget(d); setOrbiterPhase('orbiting') }}
      />

      {/* ── Overlay panels ── */}
      <InlineRegistry
        open={registryOpen}
        countryFilter={registryCountryFilter}
        title={registryTitle}
        onClose={() => setRegistryOpen(false)}
        onDomainSelect={handleDomainSelect}
      />
      <InlineReport hostname={reportDomain} onClose={() => setReportDomain(null)}
        onViewCompanyReport={async domain => {
          setReportDomain(null)
          // Check if a saved report already exists for this root domain
          try {
            const res = await fetch(`${API_BASE}/v1/company/reports`)
            const data = res.ok ? await res.json() as any : { results: [] }
            const existing = (data.results || []).find((r: any) => {
              const roots: string[] = JSON.parse(r.roots || '[]')
              return roots.some(root => domain === root || domain.endsWith(`.${root}`))
            })
            if (existing) {
              // Show existing report in history tab — no re-probe
              setCompanyConfig({ name: existing.name, roots: JSON.parse(existing.roots || '[]') })
              setCompanyInitialTab('history')
              setCompanyOpen(true)
              return
            }
          } catch { /* fall through to new report */ }
          // No existing report — open the builder form
          setCompanyInitialTab('current')
          setReportBuilderOpen(true)
        }}
      />
      <SectorReports
        open={sectorsOpen}
        onClose={() => setSectorsOpen(false)}
        onOpenReport={handleDomainSelect}
      />
      <BlogSlideover
        open={blogOpen}
        onClose={() => setBlogOpen(false)}
        onSelectPost={(slug) => { setBlogSlug(slug) }}
      />
      <BlogArticleWindow slug={blogSlug} onClose={() => setBlogSlug(null)} />
      <ReportBuilder
        open={reportBuilderOpen}
        onClose={() => setReportBuilderOpen(false)}
        onLaunch={cfg => {
          setSectorsOpen(false); setRegistryOpen(false); setReportDomain(null)
          setCompanyInitialTab('current')
          setCompanyConfig(cfg); setCompanyOpen(true)
        }}
      />
      <CompanyReportPanel
        config={companyConfig}
        open={companyOpen}
        onClose={() => { setCompanyOpen(false); setCompanyInitialTab('current') }}
        onOpenReport={handleDomainSelect}
        onGlobeRefresh={refreshGlobeData}
        initialTab={companyInitialTab}
      />
      <ProbeConsole
        domain={probeTarget}
        onLand={info => { setProbeTarget(null); handleProbeLand(info) }}
      />
      <WelcomeModal
        open={showWelcome}
        onClose={() => {
          setShowWelcome(false)
          try { localStorage.setItem('qs-welcomed', '1') } catch {}
        }}
      />
    </div>
  )
}

// ─── WelcomeModal ─────────────────────────────────────────────────────────────

function WelcomeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [phase, setPhase] = useState<'entering' | 'open' | 'closing' | 'closed'>('closed')

  useEffect(() => {
    if (open) {
      setPhase('entering')
      const t = setTimeout(() => setPhase('open'), 20)
      return () => clearTimeout(t)
    }
  }, [open])

  const handleClose = () => {
    setPhase('closing')
    setTimeout(() => { setPhase('closed'); onClose() }, 420)
  }

  if (phase === 'closed') return null

  const expanding = phase === 'entering'
  const collapsing = phase === 'closing'
  const animating = expanding || collapsing

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: animating ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,0.75)',
      backdropFilter: animating ? 'none' : 'blur(6px)',
      transition: 'background 0.25s, backdrop-filter 0.25s',
    }}>
      <style>{`
        @keyframes wm-scan {
          0%   { background-position: 0 0; }
          100% { background-position: 0 4px; }
        }
        @keyframes wm-glow-pulse {
          0%, 100% { box-shadow: 0 0 30px rgba(122,150,232,0.25), 0 0 60px rgba(122,150,232,0.1), inset 0 0 30px rgba(122,150,232,0.03); }
          50%       { box-shadow: 0 0 40px rgba(122,150,232,0.4),  0 0 80px rgba(122,150,232,0.15), inset 0 0 40px rgba(122,150,232,0.05); }
        }
        @keyframes wm-content-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wm-cursor {
          0%, 100% { opacity: 1; } 50% { opacity: 0; }
        }
        @keyframes wm-scanline {
          0%   { top: -10%; }
          100% { top: 110%; }
        }
      `}</style>

      {/* Terminal window */}
      <div style={{
        position: 'relative',
        width: 'min(640px, 94vw)',
        maxHeight: '90vh',
        background: 'rgba(4,6,14,0.97)',
        border: '1px solid rgba(122,150,232,0.45)',
        fontFamily: "'Courier New', Courier, monospace",
        overflowY: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(122,150,232,0.2) transparent',
        // The "terminal boot" animation: clip-path expands from a horizontal line
        clipPath: animating
          ? 'inset(50% 0 50% 0)'
          : 'inset(0% 0 0% 0)',
        transition: 'clip-path 0.38s cubic-bezier(0.4,0,0.2,1)',
        animation: phase === 'open' ? 'wm-glow-pulse 3s ease-in-out infinite' : 'none',
      }}>
        {/* Moving scanline */}
        {phase === 'open' && (
          <div style={{
            position: 'absolute', left: 0, right: 0, height: '2px',
            background: 'linear-gradient(90deg, transparent, rgba(122,150,232,0.15), transparent)',
            animation: 'wm-scanline 4s linear infinite',
            pointerEvents: 'none', zIndex: 2,
          }} />
        )}

        {/* Content — fades in after terminal expands */}
        <div style={{
          opacity: animating ? 0 : 1,
          transition: 'opacity 0.3s ease 0.1s',
        }}>
          {/* Title bar */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '0.6rem 1rem',
            borderBottom: '1px solid rgba(122,150,232,0.2)',
            background: 'rgba(122,150,232,0.04)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'rgba(122,150,232,0.7)', boxShadow: '0 0 6px rgba(122,150,232,0.9)' }} />
              <span style={{ fontSize: '0.6rem', letterSpacing: '0.25em', color: 'rgba(167,196,250,0.7)', textTransform: 'uppercase' }}>
                QUANTUM SENTRY // INITIAL CONTACT PROTOCOL
              </span>
            </div>
            <button onClick={handleClose} style={{
              background: 'none', border: '1px solid rgba(122,150,232,0.3)',
              color: 'rgba(167,196,250,0.6)', cursor: 'pointer',
              fontFamily: 'monospace', fontSize: '0.6rem', padding: '2px 8px',
              letterSpacing: '0.1em',
              transition: 'all 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(122,150,232,0.12)'; e.currentTarget.style.color = '#a7c4fa' }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'rgba(167,196,250,0.6)' }}
            >
              [×] CLOSE
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: '1.25rem 1.5rem 1.5rem' }}>

            {/* Logo + title row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.6rem' }}>
              <img
                src="/tychon-logo.png" alt="Tychon"
                style={{ width: 56, height: 56, objectFit: 'contain', flexShrink: 0, filter: 'drop-shadow(0 0 12px rgba(122,150,232,0.7))' }}
              />
              <div>
                <div style={{ marginBottom: '0.15rem', display: 'flex', alignItems: 'baseline', gap: '0.4rem' }}>
                  <span style={{
                    fontSize: '1.5rem', fontWeight: 900, letterSpacing: '0.06em',
                    background: 'linear-gradient(90deg, #a7c4fa 0%, #7a96e8 55%, #4a6ad8 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 0 10px rgba(122,150,232,0.5))',
                  }}>QUANTUM SENTRY</span>
                  <span style={{ animation: 'wm-cursor 1s step-end infinite', color: '#7a96e8', fontSize: '1.2rem' }}>█</span>
                </div>
                <div style={{ fontSize: '0.62rem', color: 'rgba(122,150,232,0.6)', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                  A TYCHON INTELLIGENCE PLATFORM
                </div>
              </div>
            </div>
            <div style={{ fontSize: '0.68rem', color: 'rgba(167,196,250,0.5)', letterSpacing: '0.1em', marginBottom: '1.25rem', textTransform: 'uppercase' }}>
              Global Post-Quantum Cryptography Readiness Inventory
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid rgba(122,150,232,0.15)', marginBottom: '1.25rem' }} />

            {/* Robot + briefing side by side */}
            <div style={{ display: 'flex', gap: '1.25rem', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              <img
                src="/polaris1-wave.png" alt="POLARIS-1"
                style={{ width: 170, flexShrink: 0, objectFit: 'contain', filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.6))' }}
              />
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: '0.62rem', letterSpacing: '0.2em', color: 'rgba(122,150,232,0.65)', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
                  POLARIS-1 // ASSIGNED ANALYST
                </div>
                <p style={{ fontSize: '0.78rem', color: '#c8d8e8', lineHeight: 1.7, margin: '0 0 0.75rem' }}>
                  Meet <strong style={{ color: '#a7c4fa' }}>POLARIS-1</strong> — your intelligence analyst for this mission. Named for the North Star: fixed, reliable, always pointing toward where you need to go.
                </p>
                <p style={{ fontSize: '0.78rem', color: '#c8d8e8', lineHeight: 1.7, margin: 0 }}>
                  POLARIS-1 will scan any domain, grade its quantum defenses, and generate a classified fix report with exact remediation steps.
                </p>
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid rgba(122,150,232,0.15)', marginBottom: '1.25rem' }} />

            {/* The Glass Era briefing */}
            <div style={{ fontSize: '0.62rem', letterSpacing: '0.2em', color: 'rgba(122,150,232,0.6)', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
              THREAT ASSESSMENT // THE GLASS ERA
            </div>
            <p style={{ fontSize: '0.78rem', color: '#c8d8e8', lineHeight: 1.75, margin: '0 0 0.7rem' }}>
              We are in the <strong style={{ color: '#a7c4fa' }}>Glass Era</strong> — a window where classical encryption (RSA, ECDSA, X25519) still holds, but is already being targeted. Nation-state adversaries are executing <strong style={{ color: '#ffaa00' }}>Harvest Now, Decrypt Later</strong> campaigns: capturing encrypted traffic today to decrypt it once cryptographically-relevant quantum computers arrive.
            </p>
            <p style={{ fontSize: '0.78rem', color: '#c8d8e8', lineHeight: 1.75, margin: '0 0 0.7rem' }}>
              NIST projects <strong style={{ color: '#ff5555' }}>Q-Day</strong> — the day quantum computers can break classical encryption — around <strong style={{ color: '#ff5555' }}>2030–2035</strong>. Every site still running classical-only key exchange is a glass wall. It looks solid. It isn't.
            </p>
            <p style={{ fontSize: '0.78rem', color: '#c8d8e8', lineHeight: 1.75, margin: 0 }}>
              Quantum Sentry tracks the global migration to post-quantum cryptography — one domain at a time. Command POLARIS-1 to deploy a sentry against any site and know exactly where it stands.
            </p>

            {/* Divider */}
            <div style={{ borderTop: '1px solid rgba(122,150,232,0.15)', margin: '1.25rem 0' }} />

            {/* CTA */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ fontSize: '0.62rem', color: 'rgba(122,150,232,0.45)', letterSpacing: '0.1em' }}>
                NIST FIPS 203 · FIPS 204 · FIPS 205 — FINALIZED AUG 2024
              </div>
              <button onClick={handleClose} style={{
                background: 'rgba(122,150,232,0.1)',
                border: '1px solid rgba(122,150,232,0.55)',
                color: '#a7c4fa',
                fontFamily: "'Courier New', monospace",
                fontSize: '0.72rem',
                fontWeight: 700,
                letterSpacing: '0.12em',
                padding: '0.55rem 1.4rem',
                cursor: 'pointer',
                textTransform: 'uppercase',
                transition: 'all 0.15s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(122,150,232,0.22)'; e.currentTarget.style.boxShadow = '0 0 20px rgba(122,150,232,0.35)' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(122,150,232,0.1)'; e.currentTarget.style.boxShadow = 'none' }}
              >
                ACKNOWLEDGE // ENTER COMMAND CENTER →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── NavLink ──────────────────────────────────────────────────────────────────

function NavLink({ to, label }: { to: string; label: string }) {
  const path = window.location.pathname
  const active = to === '/' ? path === '/' : path.startsWith(to)
  return (
    <Link to={to} style={{
      color: active ? 'var(--accent-cyan)' : 'var(--text-dim)',
      textDecoration: 'none',
      marginLeft: '20px',
      fontWeight: active ? 700 : 400,
      fontSize: '0.9rem',
    }}>
      {label}
    </Link>
  )
}

// ─── Console Shell ────────────────────────────────────────────────────────────

type ConsoleArea = 'overview' | 'globe' | 'analytics' | 'knowledge' | 'me'

const SOLAR_PLANET_DATA = [
  { id: 'globe'     as ConsoleArea, label: 'QUANTUM GLOBE',  desc: 'Live TLS & PQC signal',        color: '#00d4ff', hex: 0x00d4ff, radius: 80,  initAngle: 0.4,  size: 9,  speed: 0.00040, labelOffset: 24 },
  { id: 'analytics' as ConsoleArea, label: 'ANALYTICS',      desc: 'Quantum readiness metrics',    color: '#a78bfa', hex: 0xa78bfa, radius: 130, initAngle: 2.5,  size: 7,  speed: 0.00025, labelOffset: 20 },
  { id: 'knowledge' as ConsoleArea, label: 'KNOWLEDGE BASE', desc: 'PQC intelligence briefings',   color: '#34d399', hex: 0x34d399, radius: 175, initAngle: 4.8,  size: 8,  speed: 0.00016, labelOffset: 22 },
  { id: 'me'        as ConsoleArea, label: 'HOME PLANET',    desc: 'Your rank, score & discoveries', color: '#f59e0b', hex: 0xf59e0b, radius: 195, initAngle: 3.9,  size: 10, speed: 0.00010, labelOffset: 26 },
]

// ── THREE.js Solar System Overview ──────────────────────────────────────────

function SolarSystem3D({ onSelect }: { onSelect: (area: ConsoleArea) => void }) {
  const mountRef    = useRef<HTMLDivElement>(null)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const labelRefs   = useRef<Record<string, HTMLDivElement | null>>({})
  const [solarStats, setSolarStats] = useState<{ total: number; secure: number; legacy: number; insecure: number } | null>(null)

  useEffect(() => {
    fetch(`${API_BASE}/v1/analytics`)
      .then(r => r.json())
      .then((d: AnalyticsData) => setSolarStats(d.pqc_status))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!mountRef.current) return

    const scene  = new THREE.Scene()
    const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 5000)
    camera.position.set(0, 230, 390)
    camera.lookAt(0, 0, 0)

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    renderer.setSize(window.innerWidth, window.innerHeight)
    renderer.setClearColor(0x000000, 0)
    mountRef.current.appendChild(renderer.domElement)

    // Stars
    const starPos = new Float32Array(7000 * 3)
    for (let i = 0; i < starPos.length; i++) starPos[i] = (Math.random() - 0.5) * 4000
    const starGeo = new THREE.BufferGeometry()
    starGeo.setAttribute('position', new THREE.Float32BufferAttribute(starPos, 3))
    const stars = new THREE.Points(starGeo, new THREE.PointsMaterial({ color: 0xffffff, size: 1.1, transparent: true, opacity: 0.75, sizeAttenuation: true }))
    scene.add(stars)

    // Central hub
    const hub = new THREE.Mesh(
      new THREE.SphereGeometry(7, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0x00d4ff })
    )
    scene.add(hub)
    const hubGlow = new THREE.Mesh(
      new THREE.SphereGeometry(15, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0x00d4ff, transparent: true, opacity: 0.06, side: THREE.BackSide })
    )
    scene.add(hubGlow)

    // Lights
    scene.add(new THREE.AmbientLight(0x112244, 4))
    scene.add(new THREE.PointLight(0x0099ff, 4, 600))

    // Planets
    type PlanetObj = { mesh: THREE.Mesh; glow: THREE.Mesh; id: ConsoleArea; angle: number; radius: number; speed: number; color: string; labelOffset: number }
    const planets: PlanetObj[] = SOLAR_PLANET_DATA.map(p => {
      // Orbital ring
      const pts = Array.from({ length: 201 }, (_, i) => {
        const a = (i / 200) * Math.PI * 2
        return new THREE.Vector3(Math.cos(a) * p.radius, 0, Math.sin(a) * p.radius)
      })
      scene.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(pts),
        new THREE.LineBasicMaterial({ color: p.hex, transparent: true, opacity: 0.18 })
      ))

      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(p.size, 32, 32),
        new THREE.MeshStandardMaterial({ color: p.hex, emissive: p.hex, emissiveIntensity: 0.3, roughness: 0.45, metalness: 0.55 })
      )
      mesh.position.set(Math.cos(p.initAngle) * p.radius, 0, Math.sin(p.initAngle) * p.radius)
      mesh.userData.id = p.id
      scene.add(mesh)

      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(p.size * 2.4, 32, 32),
        new THREE.MeshBasicMaterial({ color: p.hex, transparent: true, opacity: 0.05, side: THREE.BackSide })
      )
      glow.position.copy(mesh.position)
      scene.add(glow)

      return { mesh, glow, id: p.id, angle: p.initAngle, radius: p.radius, speed: p.speed, color: p.color, labelOffset: p.labelOffset }
    })

    // Interaction
    const raycaster = new THREE.Raycaster()
    const mouse     = new THREE.Vector2()
    let hoveredId: ConsoleArea | null = null
    let zoomingId:  ConsoleArea | null = null
    let zoomT = 0
    const zoomStart = new THREE.Vector3()
    const zoomEnd   = new THREE.Vector3()

    function getMouseNDC(e: MouseEvent) {
      mouse.x =  (e.clientX / window.innerWidth)  * 2 - 1
      mouse.y = -(e.clientY / window.innerHeight) * 2 + 1
    }
    function toScreen(pos: THREE.Vector3): [number, number] {
      const v = pos.clone().project(camera)
      return [(v.x + 1) / 2 * window.innerWidth, -(v.y - 1) / 2 * window.innerHeight]
    }

    renderer.domElement.addEventListener('mousemove', (e: MouseEvent) => {
      getMouseNDC(e)
      raycaster.setFromCamera(mouse, camera)
      const hits = raycaster.intersectObjects(planets.map(p => p.mesh))
      hoveredId = hits.length > 0 ? (hits[0].object.userData.id as ConsoleArea) : null
      renderer.domElement.style.cursor = hoveredId ? 'pointer' : 'default'
    })

    renderer.domElement.addEventListener('click', (e: MouseEvent) => {
      if (zoomingId) return
      getMouseNDC(e)
      raycaster.setFromCamera(mouse, camera)
      const hits = raycaster.intersectObjects(planets.map(p => p.mesh))
      if (!hits.length) return
      const id     = hits[0].object.userData.id as ConsoleArea
      const planet = planets.find(p => p.id === id)!
      zoomingId = id
      zoomT = 0
      zoomStart.copy(camera.position)
      const dir = planet.mesh.position.clone().normalize()
      zoomEnd.copy(planet.mesh.position).addScaledVector(dir, -planet.mesh.position.length() * 0.38)
      zoomEnd.y = 28
      setTimeout(() => onSelectRef.current(id), 700)
    })

    let frameId: number
    let frame = 0
    function animate() {
      frameId = requestAnimationFrame(animate)
      frame++

      if (!zoomingId) {
        planets.forEach(p => {
          p.angle += p.speed
          const x = Math.cos(p.angle) * p.radius
          const z = Math.sin(p.angle) * p.radius
          p.mesh.position.set(x, 0, z)
          p.glow.position.set(x, 0, z)
          p.mesh.rotation.y += 0.006
          const isHov = hoveredId === p.id;
          (p.mesh.material as THREE.MeshStandardMaterial).emissiveIntensity = isHov ? 0.8 : 0.3;
          (p.glow.material as THREE.MeshBasicMaterial).opacity = isHov ? 0.16 : 0.05
        })
        stars.rotation.y += 0.00006
      } else {
        zoomT = Math.min(zoomT + 0.022, 1)
        const t = zoomT < 0.5 ? 2 * zoomT * zoomT : -1 + (4 - 2 * zoomT) * zoomT
        camera.position.lerpVectors(zoomStart, zoomEnd, t)
        const planet = planets.find(p => p.id === zoomingId)!
        camera.lookAt(planet.mesh.position)
      }

      // Update label positions via direct DOM (no React re-render)
      if (frame % 2 === 0) {
        planets.forEach(p => {
          const el = labelRefs.current[p.id]
          if (!el) return
          const [sx, sy] = toScreen(p.mesh.position)
          el.style.transform = `translate(-50%,0) translate(${sx}px,${sy + p.labelOffset + 16}px)`
          el.style.opacity   = hoveredId === p.id ? '1' : '0.5'
          el.style.color     = p.color;
          (el.querySelector('.sol-label-enter') as HTMLElement | null)?.style &&
            ((el.querySelector('.sol-label-enter') as HTMLElement).style.opacity = hoveredId === p.id ? '1' : '0')
        })
      }

      renderer.render(scene, camera)
    }
    animate()

    function onResize() {
      camera.aspect = window.innerWidth / window.innerHeight
      camera.updateProjectionMatrix()
      renderer.setSize(window.innerWidth, window.innerHeight)
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(frameId)
      window.removeEventListener('resize', onResize)
      renderer.domElement.remove()
      renderer.dispose()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh', background: '#02040a' }}>
      <div ref={mountRef} style={{ position: 'absolute', inset: 0 }} />

      {/* HUD corners */}
      <div className="solar-corner solar-corner-tl" />
      <div className="solar-corner solar-corner-tr" />
      <div className="solar-corner solar-corner-bl" />
      <div className="solar-corner solar-corner-br" />

      {/* Centered title */}
      <div className="solar-hud-header">
        <div className="solar-hud-title">QUANTUM SENTRY</div>
        <div className="solar-hud-sub">POST-QUANTUM INTELLIGENCE CONSOLE</div>
        <div className="solar-hud-hint">SELECT A DESTINATION</div>
      </div>

      {/* Planet labels — positioned by animation loop */}
      {SOLAR_PLANET_DATA.map(p => (
        <div
          key={p.id}
          ref={el => { labelRefs.current[p.id] = el }}
          className="sol-label"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
        >
          <div className="sol-label-name">{p.label}</div>
          <div className="sol-label-desc">{p.desc}</div>
          <div className="sol-label-enter">ENTER ›</div>
        </div>
      ))}

      {/* Stats strip */}
      {solarStats ? (
        <div className="solar-stats-strip">
          <div className="solar-stats-item">
            <span className="solar-stats-lbl">DOMAINS INDEXED</span>
            <span className="solar-stats-val">{solarStats.total.toLocaleString()}</span>
          </div>
          <div className="solar-stats-sep" />
          <div className="solar-stats-item">
            <span className="solar-stats-lbl">PQC PROTECTED</span>
            <span className="solar-stats-val" style={{ color: '#34d399' }}>
              {solarStats.total ? `${Math.round((solarStats.secure / solarStats.total) * 100)}%` : '—'}
            </span>
          </div>
          <div className="solar-stats-sep" />
          <div className="solar-stats-item">
            <span className="solar-stats-lbl">QUANTUM VULNERABLE</span>
            <span className="solar-stats-val" style={{ color: '#ff4d4d' }}>
              {(solarStats.legacy + solarStats.insecure).toLocaleString()}
            </span>
          </div>
          <div className="solar-stats-sep" />
          <div className="solar-stats-item solar-stats-live">
            <div className="solar-stats-dot" />
            <span className="solar-stats-live-lbl">LIVE</span>
          </div>
        </div>
      ) : (
        <div className="solar-stats-strip">
          <span className="solar-stats-loading">ACQUIRING SIGNAL…</span>
        </div>
      )}
    </div>
  )
}

// ── Analytics Console v3 ──────────────────────────────────────────────────────

type AnalyticsFilter = { type: string; value: string; label: string } | null
type PivotTab = 'status' | 'grades' | 'algorithms' | 'posture' | 'insights' | 'sectors' | 'domains'
type MultiFilter = {
  grade: string|null; status: string|null; kx_type: string|null
  tls: string|null; changed: string|null
  tld: string|null; country: string|null; org: string|null; hsts: string|null
}
type CrossRow    = { grade: string; secure: number; legacy: number; insecure: number; total: number }
type SectorRow   = { sector: string; total: number; secure: number; legacy: number; insecure: number; top_grades: number }
const EMPTY_MF: MultiFilter = { grade: null, status: null, kx_type: null, tls: null, changed: null, tld: null, country: null, org: null, hsts: null }

const SECTOR_META: Record<string, { label: string; icon: string; color: string; tld: string }> = {
  gov: { label: 'Government',  icon: '🏛', color: '#60a5fa', tld: '.gov' },
  edu: { label: 'Education',   icon: '🎓', color: '#a78bfa', tld: '.edu' },
  mil: { label: 'Military',    icon: '🛡', color: '#34d399', tld: '.mil' },
  org: { label: 'Nonprofit',   icon: '◎',  color: '#fb923c', tld: '.org' },
  com: { label: 'Commercial',  icon: '◈',  color: '#7a96e8', tld: '' },
}

function relTime(ts: string): string {
  const d = Date.now() - new Date(ts).getTime()
  if (d < 3600000)  return `${Math.floor(d / 60000)}m ago`
  if (d < 86400000) return `${Math.floor(d / 3600000)}h ago`
  if (d < 604800000) return `${Math.floor(d / 86400000)}d ago`
  return `${Math.floor(d / 604800000)}w ago`
}

// SVG ring chart
function PQCRing({ secure, legacy, insecure, onClick, size = 130 }: {
  secure: number; legacy: number; insecure: number
  onClick: (status: string) => void
  size?: number
}) {
  const total = secure + legacy + insecure
  if (!total) return null
  const sw = size >= 160 ? 16 : 13
  const r = 58, cx = 72, cy = 72
  const C = 2 * Math.PI * r
  const secLen = (secure  / total) * C
  const legLen = (legacy  / total) * C
  const insLen = (insecure / total) * C
  const GAP = C * 0.008
  return (
    <svg viewBox="0 0 144 144" style={{ width: size, height: size, flexShrink: 0, display: 'block' }}>
      <defs>
        <filter id="rg-g" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        <filter id="rg-y" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
        <filter id="rg-r" x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="2.5" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
      </defs>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth={sw} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#34d399" strokeWidth={sw}
        strokeDasharray={`${Math.max(secLen - GAP, 0)} ${C}`}
        strokeDashoffset={C / 4} strokeLinecap="butt"
        filter="url(#rg-g)" style={{ cursor:'pointer' }} onClick={() => onClick('secure')} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ffcc00" strokeWidth={sw}
        strokeDasharray={`${Math.max(legLen - GAP, 0)} ${C}`}
        strokeDashoffset={C / 4 - secLen} strokeLinecap="butt"
        filter="url(#rg-y)" style={{ cursor:'pointer' }} onClick={() => onClick('legacy')} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#ff4d4d" strokeWidth={sw}
        strokeDasharray={`${Math.max(insLen - GAP, 0)} ${C}`}
        strokeDashoffset={C / 4 - secLen - legLen} strokeLinecap="butt"
        filter="url(#rg-r)" style={{ cursor:'pointer' }} onClick={() => onClick('insecure')} />
      <text x={cx} y={cy - 8} textAnchor="middle" fill="#34d399" fontSize="22" fontWeight="900" fontFamily="JetBrains Mono,monospace">
        {pct(secure, total)}%
      </text>
      <text x={cx} y={cy + 8} textAnchor="middle" fill="rgba(122,150,232,0.55)" fontSize="7" letterSpacing="2" fontFamily="Space Mono,monospace">
        PQC READY
      </text>
      <text x={cx} y={cy + 21} textAnchor="middle" fill="rgba(122,150,232,0.32)" fontSize="6" letterSpacing="1" fontFamily="Space Mono,monospace">
        {total.toLocaleString()} DOMAINS
      </text>
    </svg>
  )
}

// Glowing horizontal bar row
function GlowBar({ value, max, color, height = 28, label, sublabel, count, pct: pctVal, active, onClick }: {
  value: number; max: number; color: string; height?: number
  label: string; sublabel?: string; count?: number | string; pct?: string | number
  active?: boolean; onClick?: () => void
}) {
  const w = max > 0 ? Math.max((value / max) * 100, 0.4) : 0
  return (
    <div className={`ac3-gbar-row${active ? ' ac3-gbar-active' : ''}${onClick ? ' ac3-gbar-clickable' : ''}`}
      onClick={onClick} style={{ '--gc': color } as React.CSSProperties}>
      <div className="ac3-gbar-label">
        <span className="ac3-gbar-name">{label}</span>
        {sublabel && <span className="ac3-gbar-sub">{sublabel}</span>}
      </div>
      <div className="ac3-gbar-track" style={{ height }}>
        <div className="ac3-gbar-fill" style={{
          width: `${w}%`,
          background: `linear-gradient(90deg, ${color}18 0%, ${color}bb 100%)`,
          boxShadow: `2px 0 14px ${color}55, inset 0 0 0 1px ${color}22`,
        }} />
        <div className="ac3-gbar-scan" />
      </div>
      <div className="ac3-gbar-meta">
        {count !== undefined && <span className="ac3-gbar-count">{typeof count === 'number' ? count.toLocaleString() : count}</span>}
        {pctVal !== undefined && <span className="ac3-gbar-pct" style={{ color }}>{pctVal}%</span>}
      </div>
    </div>
  )
}

function AnalyticsConsole() {
  const [stats,       setStats]       = useState<AnalyticsData | null>(null)
  const [loading,     setLoading]     = useState(true)
  const [pivot,       setPivot]       = useState<PivotTab>('status')
  const [filters,     setFilters]     = useState<MultiFilter>({ ...EMPTY_MF })
  const [crossData,   setCrossData]   = useState<CrossRow[]>([])
  const [crossLoad,   setCrossLoad]   = useState(false)
  const [sectorData,  setSectorData]  = useState<SectorRow[]>([])
  const [domains,     setDomains]     = useState<Domain[]>([])
  const [domTotal,    setDomTotal]    = useState(0)
  const [loadingDoms, setLoadingDoms] = useState(false)
  const [domPage,     setDomPage]     = useState(0)
  const [search,      setSearch]      = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [countryInput, setCountryInput] = useState('')
  const [orgInput,    setOrgInput]    = useState('')
  const [sortCol,     setSortCol]     = useState('last_updated')
  const [sortDir,     setSortDir]     = useState<'asc'|'desc'>('desc')
  const DRILL_PAGE = 40
  const DOM_PAGE = 100
  const PAGE_SIZE = pivot === 'domains' ? DOM_PAGE : DRILL_PAGE
  const now = new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  const activeCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0)
  const showDrill   = activeCount > 0 && pivot !== 'domains'

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/v1/analytics`).then(r => r.json()),
      fetch(`${API_BASE}/v1/analytics/sectors`).then(r => r.json()),
    ]).then(([d, s]) => {
      setStats(d as AnalyticsData)
      setSectorData(Array.isArray(s) ? s : [])
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  // Grade × PQC cross-analysis — 15 parallel lightweight API calls
  useEffect(() => {
    if (!stats) return
    setCrossLoad(true)
    const GRADES = ['A+','A','B','C','F']
    const STATUSES = ['secure','legacy','insecure']
    Promise.all(
      GRADES.flatMap(g => STATUSES.map(s =>
        fetch(`${API_BASE}/v1/list?grade=${encodeURIComponent(g)}&status=${s}&limit=1`)
          .then(r => r.json())
          .then((d: any) => ({ grade: g, status: s, n: d.total || 0 }))
          .catch(() => ({ grade: g, status: s, n: 0 }))
      ))
    ).then(results => {
      const map: Record<string, CrossRow> = {}
      for (const { grade, status, n } of results) {
        if (!map[grade]) map[grade] = { grade, secure: 0, legacy: 0, insecure: 0, total: 0 }
        ;(map[grade] as any)[status] = n
      }
      for (const g of (stats?.grades ?? [])) { if (map[g.grade]) map[g.grade].total = g.count }
      setCrossData(GRADES.map(g => map[g] ?? { grade: g, secure: 0, legacy: 0, insecure: 0, total: 0 }))
      setCrossLoad(false)
    })
  }, [stats]) // eslint-disable-line react-hooks/exhaustive-deps

  const isDomainSearch = pivot === 'domains'

  useEffect(() => {
    if (activeCount === 0 && !isDomainSearch) { setDomains([]); setDomTotal(0); return }
    setLoadingDoms(true)
    const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(domPage * PAGE_SIZE) })
    if (filters.grade)   p.set('grade',   filters.grade)
    if (filters.status)  p.set('status',  filters.status)
    if (filters.kx_type) p.set('kx_type', filters.kx_type)
    if (filters.tls)     p.set('tls',     filters.tls)
    if (filters.changed) p.set('changed', filters.changed)
    if (filters.tld)     p.set('tld',     filters.tld)
    if (filters.country) p.set('country', filters.country)
    if (filters.org)     p.set('org',     filters.org)
    if (filters.hsts)    p.set('hsts',    filters.hsts)
    if (search) p.set('search', search)
    if (isDomainSearch) { p.set('sort', sortCol); p.set('dir', sortDir) }
    fetch(`${API_BASE}/v1/list?${p}`)
      .then(r => r.json())
      .then((d: any) => { setDomains(d.results || []); setDomTotal(d.total || 0); setLoadingDoms(false) })
      .catch(() => setLoadingDoms(false))
  }, [filters, search, domPage, sortCol, sortDir, isDomainSearch]) // eslint-disable-line react-hooks/exhaustive-deps

  function setF(key: keyof MultiFilter, val: string | null) {
    setDomPage(0); setFilters(prev => ({ ...prev, [key]: val }))
  }
  function toggleF(key: keyof MultiFilter, val: string) {
    setDomPage(0); setFilters(prev => ({ ...prev, [key]: prev[key] === val ? null : val }))
  }
  function applyPreset(preset: Partial<MultiFilter>) {
    setDomPage(0); setFilters({ ...EMPTY_MF, ...preset })
  }
  function clearAll() {
    setFilters({ ...EMPTY_MF }); setSearch(''); setSearchInput('')
    setCountryInput(''); setOrgInput(''); setDomPage(0)
  }
  function commitSearch() { setDomPage(0); setSearch(searchInput) }
  function commitCountry() { setDomPage(0); setF('country', countryInput.trim() || null) }
  function commitOrg()     { setDomPage(0); setF('org', orgInput.trim() || null) }
  function toggleSort(col: string) {
    if (sortCol === col) { setSortDir(d => d === 'desc' ? 'asc' : 'desc') }
    else { setSortCol(col); setSortDir('asc') }
    setDomPage(0)
  }
  function goToSectorSearch(tld: string) {
    setDomPage(0)
    setFilters({ ...EMPTY_MF, tld: tld || null })
    setPivot('domains')
  }

  function filterSummary() {
    const parts: string[] = []
    if (filters.grade)   parts.push(`Grade ${filters.grade}`)
    if (filters.status)  parts.push(filters.status === 'secure' ? 'PQC READY' : filters.status === 'insecure' ? 'FRACTURED' : 'LEGACY')
    if (filters.kx_type) parts.push(filters.kx_type === 'pqc' ? 'PQC KX' : 'Classical KX')
    if (filters.tls)     parts.push(`TLS ${filters.tls}`)
    if (filters.changed) parts.push(filters.changed === 'upgraded' ? '↑ Upgraded' : '↓ Downgraded')
    if (filters.tld)     parts.push(SECTOR_META[filters.tld.replace('.','')]?.label ?? filters.tld)
    if (filters.country) parts.push(filters.country.toUpperCase())
    if (filters.org)     parts.push(`org:${filters.org}`)
    if (filters.hsts)    parts.push('HSTS')
    if (search)          parts.push(`"${search}"`)
    return parts.join(' + ')
  }

  const kxClean = (stats?.key_exchange ?? []).filter(kx => kx.name !== 'Unknown')

  const PIVOTS: { id: PivotTab; label: string }[] = [
    { id: 'status',     label: 'QUANTUM STATUS' },
    { id: 'grades',     label: 'GRADE MATRIX'   },
    { id: 'algorithms', label: 'ALGORITHMS'      },
    { id: 'posture',    label: 'POSTURE'         },
    { id: 'insights',   label: 'INSIGHTS'        },
    { id: 'sectors',    label: 'SECTORS'         },
    { id: 'domains',    label: 'DOMAIN SEARCH'   },
  ]

  return (
    <div className="ac3-shell">
      <div className="ac-scanlines" />

      {/* ── Top bar ────────────────────────────────────────────────────────── */}
      <div className="ac3-topbar">
        <div className="ac3-topbar-logo">
          <span className="ac3-logo-hex">⬡</span>QS // ANALYTICS
        </div>
        <div className="ac3-pivot-row">
          {PIVOTS.map(tab => (
            <button key={tab.id}
              className={`ac3-pivot-tab${pivot === tab.id ? ' ac3-pivot-active' : ''}`}
              onClick={() => setPivot(tab.id)}>
              {tab.label}
              {pivot === tab.id && <span className="ac3-pivot-bar" />}
            </button>
          ))}
        </div>
        <div className="ac3-topbar-right">
          {activeCount > 0 && (
            <span className="ac3-filter-badge">{activeCount} FILTER{activeCount > 1 ? 'S' : ''} ACTIVE</span>
          )}
          <span className="ac3-topbar-time">{now}</span>
        </div>
      </div>

      {/* ── Active filter chips row ──────────────────────────────────────── */}
      {stats && activeCount > 0 && (
        <div className="ac3-fbar">
          <span className="ac3-fbar-lbl">FILTERS:</span>
          {filters.grade   && <span className="ac3-fchip" style={{ '--fc': GRADE_COLORS[filters.grade] ?? '#7a96e8' } as React.CSSProperties}>
            Grade {filters.grade} <button onClick={() => setF('grade', null)}>✕</button></span>}
          {filters.status  && <span className="ac3-fchip" style={{ '--fc': filters.status==='secure'?'#34d399':filters.status==='insecure'?'#ff4d4d':'#ffcc00' } as React.CSSProperties}>
            {filters.status==='secure'?'PQC READY':filters.status==='insecure'?'FRACTURED':'LEGACY'} <button onClick={() => setF('status', null)}>✕</button></span>}
          {filters.kx_type && <span className="ac3-fchip" style={{ '--fc': '#34d399' } as React.CSSProperties}>
            {filters.kx_type==='pqc'?'PQC KX':'Classical KX'} <button onClick={() => setF('kx_type', null)}>✕</button></span>}
          {filters.tls     && <span className="ac3-fchip" style={{ '--fc': '#60a5fa' } as React.CSSProperties}>
            TLS {filters.tls} <button onClick={() => setF('tls', null)}>✕</button></span>}
          {filters.changed && <span className="ac3-fchip" style={{ '--fc': filters.changed==='upgraded'?'#34d399':'#ff4d4d' } as React.CSSProperties}>
            {filters.changed==='upgraded'?'↑ Upgraded':'↓ Downgraded'} <button onClick={() => setF('changed', null)}>✕</button></span>}
          {filters.tld     && <span className="ac3-fchip" style={{ '--fc': '#60a5fa' } as React.CSSProperties}>
            {SECTOR_META[filters.tld.replace('.','')]?.label ?? filters.tld} <button onClick={() => setF('tld', null)}>✕</button></span>}
          {filters.country && <span className="ac3-fchip" style={{ '--fc': '#a78bfa' } as React.CSSProperties}>
            {filters.country.toUpperCase()} <button onClick={() => { setF('country', null); setCountryInput('') }}>✕</button></span>}
          {filters.org     && <span className="ac3-fchip" style={{ '--fc': '#fb923c' } as React.CSSProperties}>
            org:{filters.org} <button onClick={() => { setF('org', null); setOrgInput('') }}>✕</button></span>}
          {filters.hsts    && <span className="ac3-fchip" style={{ '--fc': '#f472b6' } as React.CSSProperties}>
            HSTS <button onClick={() => setF('hsts', null)}>✕</button></span>}
          {search          && <span className="ac3-fchip" style={{ '--fc': '#7a96e8' } as React.CSSProperties}>
            "{search}" <button onClick={() => { setSearch(''); setSearchInput('') }}>✕</button></span>}
          <button className="ac3-fbar-clear" onClick={clearAll}>CLEAR ALL</button>
          {domTotal > 0 && <span className="ac3-fbar-count">{domTotal.toLocaleString()} MATCHES</span>}
        </div>
      )}

      {loading && (
        <div className="ac-loading">
          <div className="ac-loading-ring" /><span>LOADING TELEMETRY</span>
        </div>
      )}

      {stats && (
        <div className={`ac3-main${activeCount > 0 ? ' ac3-has-fbar' : ''}${pivot === 'domains' ? ' ac3-domain-mode' : ''}`}>

          {/* ── Left panel ─────────────────────────────────────────────────── */}
          {pivot !== 'domains' && <div className="ac3-left">
            <div className="ac3-left-ring">
              <PQCRing
                secure={stats.pqc_status.secure}
                legacy={stats.pqc_status.legacy}
                insecure={stats.pqc_status.insecure}
                size={176}
                onClick={v => toggleF('status', v)}
              />
            </div>
            <div className="ac3-left-divider" />

            <div className="ac3-left-rows">
              {[
                { l:'HARDENED',  v: stats.pqc_status.secure,   c:'#34d399', s:'secure'   },
                { l:'LEGACY',    v: stats.pqc_status.legacy,    c:'#ffcc00', s:'legacy'   },
                { l:'FRACTURED', v: stats.pqc_status.insecure,  c:'#ff4d4d', s:'insecure' },
              ].map(r => (
                <div key={r.s}
                  className={`ac3-left-row${filters.status===r.s?' ac3-row-on':''}`}
                  onClick={() => toggleF('status', r.s)}>
                  <span className="ac3-row-dot" style={{ background: r.c, boxShadow: `0 0 6px ${r.c}` }} />
                  <span className="ac3-row-lbl">{r.l}</span>
                  <span className="ac3-row-cnt" style={{ color: r.c }}>{r.v.toLocaleString()}</span>
                  <span className="ac3-row-pct">{pct(r.v, stats.pqc_status.total)}%</span>
                </div>
              ))}
            </div>
            <div className="ac3-left-divider" />

            <div className="ac3-left-section-hdr">GRADE FILTER</div>
            <div className="ac3-grade-pills">
              {(['A+','A','B','C','F'] as const).map(g => {
                const col = GRADE_COLORS[g] ?? '#7a96e8'
                return (
                  <button key={g}
                    className={`ac3-grade-pill${filters.grade===g?' ac3-gpill-on':''}`}
                    style={{ '--gc': col } as React.CSSProperties}
                    onClick={() => toggleF('grade', g)}>
                    {g}
                  </button>
                )
              })}
            </div>
            <div className="ac3-left-divider" />

            <div className="ac3-left-section-hdr">TLS VERSION</div>
            <div className="ac3-tls-pills">
              {[{v:'1.3',c:'#34d399'},{v:'1.2',c:'#60a5fa'},{v:'1.1-',c:'#ff4d4d',l:'≤1.1'}].map(t => (
                <button key={t.v}
                  className={`ac3-tls-pill${filters.tls===t.v?' ac3-tls-on':''}`}
                  style={{ '--tc': t.c } as React.CSSProperties}
                  onClick={() => toggleF('tls', t.v)}>
                  {(t as any).l ?? `TLS ${t.v}`}
                </button>
              ))}
            </div>
            <div className="ac3-left-divider" />

            <div className="ac3-left-metrics">
              {[
                { l:'FWD SECRECY',  n: stats.forward_secrecy.with_fs, d: stats.forward_secrecy.total, c:'#34d399' },
                { l:'AEAD CIPHERS', n: stats.aead.with_aead,          d: stats.aead.total,            c:'#60a5fa' },
                { l:'HSTS ENABLED', n: stats.hsts.with_hsts,          d: stats.hsts.total,            c:'#a78bfa' },
                { l:'TLS 1.3',      n: stats.tls.tls13,               d: stats.pqc_status.total,      c:'#00d4ff' },
              ].map(m => (
                <div key={m.l} className="ac3-metric-row">
                  <span className="ac3-metric-lbl">{m.l}</span>
                  <div className="ac3-metric-bar">
                    <div className="ac3-metric-fill" style={{ width: `${pct(m.n, m.d)}%`, background: m.c, boxShadow: `0 0 6px ${m.c}66` }} />
                  </div>
                  <span className="ac3-metric-val" style={{ color: m.c }}>{pct(m.n, m.d)}%</span>
                </div>
              ))}
            </div>
            <div className="ac3-left-divider" />

            <div className="ac3-search-wrap">
              <input
                className="ac3-search"
                placeholder="⌕  SEARCH DOMAIN…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && commitSearch()}
              />
            </div>
            {activeCount > 0 && (
              <button className="ac3-clear-btn" onClick={clearAll}>✕  CLEAR ALL FILTERS</button>
            )}
          </div>}

          {/* ── Center panel ───────────────────────────────────────────────── */}
          <div className={`ac3-center${showDrill ? ' ac3-center-narrow' : ''}${pivot === 'domains' ? ' ac3-center-full' : ''}`}>

            {/* ── QUANTUM STATUS ─────────────────────────────── */}
            {pivot === 'status' && (
              <div className="ac3-pivot-content">
                <div className="ac3-piv-header">
                  <span className="ac3-piv-title">QUANTUM READINESS BREAKDOWN</span>
                  <span className="ac3-piv-sub">Global PQC adoption signal across {stats.pqc_status.total.toLocaleString()} scanned domains</span>
                </div>
                <div className="ac3-hero-row">
                  {[
                    { v:`${pct(stats.pqc_status.secure,stats.pqc_status.total)}%`,  l:'PQC READY',    c:'#34d399' },
                    { v: stats.pqc_status.total.toLocaleString(),                   l:'TOTAL PROBED', c:'#7a96e8' },
                    { v:`${pct(stats.tls.tls13,stats.pqc_status.total)}%`,          l:'TLS 1.3',      c:'#00d4ff' },
                    { v: kxClean.filter(k=>k.name.includes('MLKEM')||k.name.includes('Kyber')).reduce((s,k)=>s+k.count,0).toLocaleString(), l:'HYBRID KX', c:'#34d399' },
                  ].map(h => (
                    <div key={h.l} className="ac3-hero-cell">
                      <div className="ac3-hero-val" style={{ color: h.c }}>{h.v}</div>
                      <div className="ac3-hero-lbl">{h.l}</div>
                    </div>
                  ))}
                </div>

                <div className="ac3-preset-strip">
                  <span className="ac3-preset-lbl">SMART QUERIES:</span>
                  {[
                    { l:'⚡ QUICK WINS',       f:{ status:'legacy',   kx_type:null, tls:null, grade:'A',  changed:null }, c:'#34d399' },
                    { l:'⚠ CRITICAL',          f:{ status:'insecure', kx_type:null, tls:null, grade:null, changed:null }, c:'#ff4d4d' },
                    { l:'◈ PQC LEADERS',       f:{ status:'secure',   kx_type:'pqc',tls:null, grade:null, changed:null }, c:'#00d4ff' },
                    { l:'⊙ UPGRADE PATH',      f:{ status:null, kx_type:'classical', tls:'1.3', grade:null, changed:null }, c:'#a78bfa' },
                    { l:'↑ RECENTLY HARDENED', f:{ status:null, kx_type:null, tls:null, grade:null, changed:'upgraded' }, c:'#60a5fa' },
                  ].map(p => (
                    <button key={p.l} className="ac3-preset-btn"
                      style={{ '--pc': p.c } as React.CSSProperties}
                      onClick={() => applyPreset(p.f)}>
                      {p.l}
                    </button>
                  ))}
                </div>

                <div className="ac3-section-hdr">
                  <span className="ac-panel-dot ac-dot-cyan" />KEY EXCHANGE ALGORITHMS
                  <span className="ac3-tag ac3-tag-pqc" style={{ cursor:'pointer', marginLeft: 8 }} onClick={() => toggleF('kx_type','pqc')}>PQC</span>
                  <span className="ac3-tag ac3-tag-clx" style={{ cursor:'pointer' }} onClick={() => toggleF('kx_type','classical')}>CLASSICAL</span>
                </div>
                <div className="ac3-bars">
                  {kxClean.slice(0, 12).map(kx => {
                    const isPQC = kx.name.includes('MLKEM') || kx.name.includes('Kyber')
                    const col   = isPQC ? '#34d399' : '#ffcc00'
                    return (
                      <GlowBar key={kx.name}
                        value={kx.count} max={kxClean[0]?.count || 1}
                        color={col} label={kx.name}
                        sublabel={isPQC ? 'POST-QUANTUM' : 'CLASSICAL'}
                        count={kx.count} pct={pct(kx.count, stats.pqc_status.total)}
                        active={filters.kx_type===(isPQC?'pqc':'classical')}
                        onClick={() => toggleF('kx_type', isPQC?'pqc':'classical')}
                      />
                    )
                  })}
                </div>
                <div className="ac3-section-hdr">
                  <span className="ac-panel-dot ac-dot-purple" />TLS VERSION DISTRIBUTION
                </div>
                <div className="ac3-bars">
                  {[
                    { l:'TLS 1.3',   n: stats.tls.tls13,       c:'#34d399', v:'1.3'  },
                    { l:'TLS 1.2',   n: stats.tls.tls12,       c:'#60a5fa', v:'1.2'  },
                    { l:'TLS ≤ 1.1', n: stats.tls.tls11_below, c:'#ff4d4d', v:'1.1-' },
                  ].map(t => (
                    <GlowBar key={t.l}
                      value={t.n} max={stats.tls.tls13 || 1}
                      color={t.c} label={t.l}
                      count={t.n} pct={pct(t.n, stats.pqc_status.total)}
                      active={filters.tls===t.v}
                      onClick={() => toggleF('tls', t.v)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ── GRADE MATRIX ───────────────────────────────── */}
            {pivot === 'grades' && (
              <div className="ac3-pivot-content">
                <div className="ac3-piv-header">
                  <span className="ac3-piv-title">CRYPTOGRAPHIC GRADE MATRIX</span>
                  <span className="ac3-piv-sub">SSL/TLS security grading — click any grade to drill down into matching domains</span>
                </div>
                <div className="ac3-hero-row">
                  {stats.grades.slice(0, 5).map(g => {
                    const col = GRADE_COLORS[g.grade] ?? '#7a96e8'
                    return (
                      <div key={g.grade}
                        className={`ac3-hero-cell ac3-grade-hero${filters.grade===g.grade?' ac3-row-on':''}`}
                        style={{ '--gc': col } as React.CSSProperties}
                        onClick={() => toggleF('grade', g.grade)}>
                        <div className="ac3-hero-val" style={{ color: col }}>{g.grade}</div>
                        <div className="ac3-hero-lbl">{g.count.toLocaleString()}</div>
                        <div className="ac3-grade-pct-lbl" style={{ color: col }}>{pct(g.count, stats.pqc_status.total)}%</div>
                      </div>
                    )
                  })}
                </div>
                <div className="ac3-section-hdr">
                  <span className="ac-panel-dot ac-dot-purple" />GRADE DISTRIBUTION
                </div>
                <div className="ac3-bars">
                  {stats.grades.map(g => {
                    const col = GRADE_COLORS[g.grade] ?? '#7a96e8'
                    return (
                      <GlowBar key={g.grade}
                        value={g.count} max={stats.grades[0]?.count || 1}
                        color={col} label={`Grade ${g.grade}`}
                        count={g.count} pct={pct(g.count, stats.pqc_status.total)}
                        active={filters.grade===g.grade}
                        onClick={() => toggleF('grade', g.grade)}
                      />
                    )
                  })}
                </div>
                {!crossLoad && crossData.some(r => r.total > 0) && (
                  <>
                    <div className="ac3-section-hdr">
                      <span className="ac-panel-dot ac-dot-cyan" />GRADE × PQC STATUS CROSS-ANALYSIS
                    </div>
                    <div className="ac3-cross-wrap">
                      <table className="ac3-cross-table">
                        <thead>
                          <tr>
                            <th className="ac3-th">GRADE</th>
                            <th className="ac3-th ac3-th-green">◈ HARDENED</th>
                            <th className="ac3-th ac3-th-yellow">◎ LEGACY</th>
                            <th className="ac3-th ac3-th-red">✕ FRACTURED</th>
                            <th className="ac3-th">TOTAL</th>
                            <th className="ac3-th">PQC%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {crossData.filter(r => r.total > 0).map(row => {
                            const col    = GRADE_COLORS[row.grade] ?? '#7a96e8'
                            const pqcPct = pct(row.secure, row.total)
                            return (
                              <tr key={row.grade} className="ac3-cross-row" onClick={() => toggleF('grade', row.grade)}>
                                <td className="ac3-td"><span className="ac3-cross-grade" style={{ color:col,borderColor:col }}>{row.grade}</span></td>
                                <td className="ac3-td"><span className="ac3-cross-val ac3-cv-green">{row.secure.toLocaleString()}</span><span className="ac3-cross-pct">{pct(row.secure,row.total)}%</span></td>
                                <td className="ac3-td"><span className="ac3-cross-val ac3-cv-yellow">{row.legacy.toLocaleString()}</span><span className="ac3-cross-pct">{pct(row.legacy,row.total)}%</span></td>
                                <td className="ac3-td"><span className="ac3-cross-val ac3-cv-red">{row.insecure.toLocaleString()}</span><span className="ac3-cross-pct">{pct(row.insecure,row.total)}%</span></td>
                                <td className="ac3-td"><span className="ac3-cross-total">{row.total.toLocaleString()}</span></td>
                                <td className="ac3-td">
                                  <div className="ac3-cross-bar-wrap">
                                    <div className="ac3-cross-bar" style={{ width:`${pqcPct}%`, background:col, boxShadow:`0 0 8px ${col}55` }} />
                                    <span className="ac3-cross-bar-pct" style={{ color:col }}>{pqcPct}%</span>
                                  </div>
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── ALGORITHMS ─────────────────────────────────── */}
            {pivot === 'algorithms' && (
              <div className="ac3-pivot-content">
                <div className="ac3-piv-header">
                  <span className="ac3-piv-title">CRYPTOGRAPHIC ALGORITHM INVENTORY</span>
                  <span className="ac3-piv-sub">Key exchange, cipher suites, and signature algorithms across all scanned hosts</span>
                </div>
                <div className="ac3-hero-row">
                  {[
                    { v: kxClean.filter(k=>k.name.includes('MLKEM')||k.name.includes('Kyber')).reduce((s,k)=>s+k.count,0).toLocaleString(), l:'HYBRID PQC KX',    c:'#34d399' },
                    { v:`${pct(stats.aead.with_aead,stats.aead.total)}%`,                                                                    l:'AEAD CIPHERS',   c:'#60a5fa' },
                    { v:`${pct(stats.forward_secrecy.with_fs,stats.forward_secrecy.total)}%`,                                                l:'FORWARD SECRECY',c:'#a78bfa' },
                    { v: kxClean.length.toString(),                                                                                          l:'KX ALGORITHMS',  c:'#7a96e8' },
                  ].map(h => (
                    <div key={h.l} className="ac3-hero-cell">
                      <div className="ac3-hero-val" style={{ color: h.c }}>{h.v}</div>
                      <div className="ac3-hero-lbl">{h.l}</div>
                    </div>
                  ))}
                </div>
                <div className="ac3-section-hdr">
                  <span className="ac-panel-dot ac-dot-green" />KEY EXCHANGE
                  <span className="ac3-tag ac3-tag-pqc" style={{ cursor:'pointer', marginLeft: 8 }} onClick={() => toggleF('kx_type','pqc')}>PQC</span>
                  <span className="ac3-tag ac3-tag-clx" style={{ cursor:'pointer' }} onClick={() => toggleF('kx_type','classical')}>CLASSICAL</span>
                </div>
                <div className="ac3-bars">
                  {kxClean.map(kx => {
                    const isPQC = kx.name.includes('MLKEM') || kx.name.includes('Kyber')
                    const col   = isPQC ? '#34d399' : '#ffcc00'
                    return (
                      <GlowBar key={kx.name}
                        value={kx.count} max={kxClean[0]?.count || 1}
                        color={col} label={kx.name}
                        sublabel={isPQC ? 'POST-QUANTUM (NIST APPROVED)' : 'CLASSICAL / LEGACY'}
                        count={kx.count} pct={pct(kx.count, stats.pqc_status.total)}
                        active={filters.kx_type===(isPQC?'pqc':'classical')}
                        onClick={() => toggleF('kx_type', isPQC?'pqc':'classical')}
                      />
                    )
                  })}
                </div>
                {stats.cipher_suites.length > 0 && (
                  <>
                    <div className="ac3-section-hdr"><span className="ac-panel-dot ac-dot-cyan" />CIPHER SUITES</div>
                    <div className="ac3-bars">
                      {stats.cipher_suites.slice(0, 8).map(cs => {
                        const isAEAD = cs.name.includes('GCM') || cs.name.includes('CCM') || cs.name.includes('POLY1305')
                        const col = isAEAD ? '#60a5fa' : '#a78bfa'
                        return (
                          <GlowBar key={cs.name}
                            value={cs.count} max={stats.cipher_suites[0]?.count || 1}
                            color={col} label={cs.name}
                            sublabel={isAEAD ? 'AEAD MODE' : 'NON-AEAD'}
                            count={cs.count} pct={pct(cs.count, stats.pqc_status.total)}
                          />
                        )
                      })}
                    </div>
                  </>
                )}
                {stats.sig_algs.length > 0 && (
                  <>
                    <div className="ac3-section-hdr"><span className="ac-panel-dot ac-dot-purple" />SIGNATURE ALGORITHMS</div>
                    <div className="ac3-bars">
                      {stats.sig_algs.slice(0, 8).map(sa => {
                        const isPQ    = sa.name.includes('ML-DSA') || sa.name.includes('FALCON') || sa.name.includes('SPHINCS')
                        const isECDSA = sa.name.includes('ECDSA') || sa.name.includes('Ed')
                        const col = isPQ ? '#34d399' : isECDSA ? '#a78bfa' : '#7a96e8'
                        return (
                          <GlowBar key={sa.name}
                            value={sa.count} max={stats.sig_algs[0]?.count || 1}
                            color={col} label={sa.name}
                            sublabel={isPQ ? 'PQC SIGNATURE' : isECDSA ? 'ELLIPTIC CURVE' : 'RSA / OTHER'}
                            count={sa.count} pct={pct(sa.count, stats.pqc_status.total)}
                          />
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── POSTURE ────────────────────────────────────── */}
            {pivot === 'posture' && (
              <div className="ac3-pivot-content">
                <div className="ac3-piv-header">
                  <span className="ac3-piv-title">CRYPTOGRAPHIC POSTURE ASSESSMENT</span>
                  <span className="ac3-piv-sub">Security hygiene — forward secrecy, AEAD, HSTS, and change signals</span>
                </div>
                <div className="ac3-hero-row">
                  {[
                    { v:`${pct(stats.forward_secrecy.with_fs,stats.forward_secrecy.total)}%`, l:'FWD SECRECY',    c:'#34d399' },
                    { v:`${pct(stats.aead.with_aead,stats.aead.total)}%`,                      l:'AEAD CIPHERS',  c:'#60a5fa' },
                    { v:`${pct(stats.hsts.with_hsts,stats.hsts.total)}%`,                      l:'HSTS ENABLED',  c:'#a78bfa' },
                    { v:`${pct(stats.hsts.preloaded,stats.hsts.total)}%`,                      l:'HSTS PRELOADED',c:'#f472b6' },
                  ].map(h => (
                    <div key={h.l} className="ac3-hero-cell">
                      <div className="ac3-hero-val" style={{ color: h.c }}>{h.v}</div>
                      <div className="ac3-hero-lbl">{h.l}</div>
                    </div>
                  ))}
                </div>
                <div className="ac3-section-hdr"><span className="ac-panel-dot ac-dot-green" />SECURITY HYGIENE METRICS</div>
                <div className="ac3-bars">
                  {[
                    { l:'Forward Secrecy',  n: stats.forward_secrecy.with_fs,  d: stats.forward_secrecy.total, c:'#34d399' },
                    { l:'AEAD Ciphers',     n: stats.aead.with_aead,           d: stats.aead.total,            c:'#60a5fa' },
                    { l:'HSTS Enabled',     n: stats.hsts.with_hsts,           d: stats.hsts.total,            c:'#a78bfa' },
                    { l:'HSTS Preloaded',   n: stats.hsts.preloaded,           d: stats.hsts.total,            c:'#f472b6' },
                    { l:'Incl. Subdomains', n: stats.hsts.include_subdomains,  d: stats.hsts.total,            c:'#fb923c' },
                  ].map(row => (
                    <GlowBar key={row.l} value={row.n} max={row.d||1} color={row.c}
                      label={row.l} count={row.n} pct={pct(row.n,row.d)} height={30} />
                  ))}
                </div>
                <div className="ac3-section-hdr"><span className="ac-panel-dot ac-dot-cyan" />CHANGE SIGNALS</div>
                <div className="ac3-bars">
                  {[
                    { l:'↑ RECENTLY UPGRADED',   c:'#34d399', v:'upgraded',   k:'changed' as keyof MultiFilter },
                    { l:'↓ RECENTLY DOWNGRADED', c:'#ff4d4d', v:'downgraded', k:'changed' as keyof MultiFilter },
                  ].map(row => (
                    <div key={row.l}
                      className={`ac3-gbar-row ac3-gbar-clickable${filters[row.k]===row.v?' ac3-gbar-active':''}`}
                      style={{ '--gc': row.c } as React.CSSProperties}
                      onClick={() => toggleF(row.k, row.v)}>
                      <div className="ac3-gbar-label">
                        <span className="ac3-gbar-name">{row.l}</span>
                        <span className="ac3-gbar-sub">CLICK TO VIEW DOMAINS →</span>
                      </div>
                      <div className="ac3-gbar-track" style={{ height: 28 }}>
                        <div className="ac3-gbar-fill" style={{ width:'100%', background:`linear-gradient(90deg,${row.c}08 0%,${row.c}33 100%)` }} />
                      </div>
                      <div className="ac3-gbar-meta">
                        <span className="ac3-gbar-pct" style={{ color: row.c }}>VIEW →</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── INSIGHTS ───────────────────────────────────── */}
            {pivot === 'insights' && (
              <div className="ac3-pivot-content">
                <div className="ac3-piv-header">
                  <span className="ac3-piv-title">INTELLIGENCE INSIGHTS</span>
                  <span className="ac3-piv-sub">Cross-dimensional queries and grade × PQC matrix — click any card to activate a smart filter</span>
                </div>

                <div className="ac3-section-hdr"><span className="ac-panel-dot ac-dot-cyan" />INTELLIGENCE QUERIES</div>
                <div className="ac3-preset-grid">
                  {[
                    { l:'⚡ QUICK WINS',         sub:'Grade A/A+ still on Classical KX',    f:{ grade:'A',  status:'legacy',   kx_type:null,        tls:null,  changed:null },       c:'#34d399' },
                    { l:'⚠ CRITICAL EXPOSURE',   sub:'Insecure F-grade domains',            f:{ grade:'F',  status:'insecure', kx_type:null,        tls:null,  changed:null },       c:'#ff4d4d' },
                    { l:'◈ PQC LEADERS',         sub:'Fully hardened with PQC KX',          f:{ grade:null, status:'secure',   kx_type:'pqc',       tls:null,  changed:null },       c:'#34d399' },
                    { l:'⊙ UPGRADE PATH',        sub:'TLS 1.3 ready, classical KX only',    f:{ grade:null, status:null,       kx_type:'classical', tls:'1.3', changed:null },       c:'#00d4ff' },
                    { l:'↑ RECENTLY HARDENED',   sub:'Domains that upgraded to PQC',        f:{ grade:null, status:null,       kx_type:null,        tls:null,  changed:'upgraded' },  c:'#a78bfa' },
                    { l:'↓ REGRESSION ALERT',    sub:'Domains that downgraded recently',    f:{ grade:null, status:null,       kx_type:null,        tls:null,  changed:'downgraded'}, c:'#ff4d4d' },
                    { l:'◫ LEGACY TLS EXPOSURE', sub:'Still running TLS 1.2',               f:{ grade:null, status:null,       kx_type:null,        tls:'1.2', changed:null },       c:'#ffcc00' },
                    { l:'★ TOP PERFORMERS',      sub:'Grade A+ across the board',           f:{ grade:'A+', status:null,       kx_type:null,        tls:null,  changed:null },       c:'#00ff88' },
                  ].map(p => (
                    <div key={p.l} className="ac3-preset-card"
                      style={{ '--pc': p.c } as React.CSSProperties}
                      onClick={() => applyPreset(p.f)}>
                      <div className="ac3-preset-card-title">{p.l}</div>
                      <div className="ac3-preset-card-sub">{p.sub}</div>
                      <div className="ac3-preset-card-arrow">APPLY FILTER →</div>
                    </div>
                  ))}
                </div>

                <div className="ac3-section-hdr">
                  <span className="ac-panel-dot ac-dot-purple" />GRADE × PQC STATUS CROSS-ANALYSIS
                  {crossLoad && <span style={{ marginLeft: 8, fontSize:'0.52rem', color:'rgba(0,209,255,0.35)' }}>COMPUTING…</span>}
                </div>
                {!crossLoad && crossData.some(r => r.total > 0) && (
                  <div className="ac3-cross-wrap">
                    <table className="ac3-cross-table">
                      <thead>
                        <tr>
                          <th className="ac3-th">GRADE</th>
                          <th className="ac3-th ac3-th-green">◈ HARDENED</th>
                          <th className="ac3-th ac3-th-yellow">◎ LEGACY</th>
                          <th className="ac3-th ac3-th-red">✕ FRACTURED</th>
                          <th className="ac3-th">TOTAL</th>
                          <th className="ac3-th">PQC%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crossData.filter(r => r.total > 0).map(row => {
                          const col    = GRADE_COLORS[row.grade] ?? '#7a96e8'
                          const pqcPct = pct(row.secure, row.total)
                          return (
                            <tr key={row.grade} className="ac3-cross-row" onClick={() => toggleF('grade', row.grade)}>
                              <td className="ac3-td"><span className="ac3-cross-grade" style={{ color:col, borderColor:col }}>{row.grade}</span></td>
                              <td className="ac3-td"><span className="ac3-cross-val ac3-cv-green">{row.secure.toLocaleString()}</span><span className="ac3-cross-pct">{pct(row.secure,row.total)}%</span></td>
                              <td className="ac3-td"><span className="ac3-cross-val ac3-cv-yellow">{row.legacy.toLocaleString()}</span><span className="ac3-cross-pct">{pct(row.legacy,row.total)}%</span></td>
                              <td className="ac3-td"><span className="ac3-cross-val ac3-cv-red">{row.insecure.toLocaleString()}</span><span className="ac3-cross-pct">{pct(row.insecure,row.total)}%</span></td>
                              <td className="ac3-td"><span className="ac3-cross-total">{row.total.toLocaleString()}</span></td>
                              <td className="ac3-td">
                                <div className="ac3-cross-bar-wrap">
                                  <div className="ac3-cross-bar" style={{ width:`${pqcPct}%`, background:col, boxShadow:`0 0 8px ${col}55` }} />
                                  <span className="ac3-cross-bar-pct" style={{ color:col }}>{pqcPct}%</span>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── SECTORS ────────────────────────────────────── */}
            {pivot === 'sectors' && (
              <div className="ac3-pivot-content">
                <div className="ac3-piv-header">
                  <span className="ac3-piv-title">SECTOR READINESS INTELLIGENCE</span>
                  <span className="ac3-piv-sub">PQC adoption broken down by TLD category — click any sector to explore its domains</span>
                </div>
                <div className="ac3-sector-grid">
                  {['gov','edu','mil','org','com'].map(s => {
                    const row  = sectorData.find(r => r.sector === s)
                    const meta = SECTOR_META[s]
                    const total    = row?.total ?? 0
                    const secure   = row?.secure ?? 0
                    const legacy   = row?.legacy ?? 0
                    const insecure = row?.insecure ?? 0
                    const pqcPct   = total > 0 ? pct(secure, total) : 0
                    const topPct   = total > 0 ? pct(row?.top_grades ?? 0, total) : 0
                    return (
                      <div key={s}
                        className={`ac3-sector-card${filters.tld===(meta.tld||s)?' ac3-sector-active':''}`}
                        style={{ '--sc': meta.color } as React.CSSProperties}
                        onClick={() => total > 0 ? goToSectorSearch(meta.tld || s) : undefined}>
                        <div className="ac3-sector-card-top">
                          <span className="ac3-sector-icon">{meta.icon}</span>
                          <div className="ac3-sector-info">
                            <div className="ac3-sector-name" style={{ color: meta.color }}>{meta.label}</div>
                            <div className="ac3-sector-tld">{meta.tld || '.com / .net / other'}</div>
                          </div>
                          <div className="ac3-sector-total">{total.toLocaleString()}<span>domains</span></div>
                        </div>
                        <div className="ac3-sector-bar-wrap">
                          <div className="ac3-sector-bar-bg">
                            <div className="ac3-sector-bar-seg" style={{ width:`${pct(secure,total)}%`, background:'#34d399', boxShadow:'0 0 8px #34d39966' }} />
                            <div className="ac3-sector-bar-seg" style={{ width:`${pct(legacy,total)}%`, background:'#ffcc00', boxShadow:'0 0 8px #ffcc0066' }} />
                            <div className="ac3-sector-bar-seg" style={{ width:`${pct(insecure,total)}%`, background:'#ff4d4d', boxShadow:'0 0 8px #ff4d4d66' }} />
                          </div>
                          <span className="ac3-sector-pqc-pct" style={{ color: meta.color }}>{pqcPct}% PQC READY</span>
                        </div>
                        <div className="ac3-sector-stats">
                          <span className="ac3-ss-green">◈ {secure.toLocaleString()} <em>secure</em></span>
                          <span className="ac3-ss-yellow">◎ {legacy.toLocaleString()} <em>legacy</em></span>
                          <span className="ac3-ss-red">✕ {insecure.toLocaleString()} <em>fractured</em></span>
                          <span style={{ color: meta.color }}>★ {topPct}% <em>A/A+</em></span>
                        </div>
                        {total > 0 && <div className="ac3-sector-cta">EXPLORE DOMAINS →</div>}
                      </div>
                    )
                  })}
                </div>
                {sectorData.length > 0 && (
                  <>
                    <div className="ac3-section-hdr"><span className="ac-panel-dot ac-dot-cyan" />PQC READINESS BY SECTOR</div>
                    <div className="ac3-bars">
                      {['gov','edu','mil','org','com'].map(s => {
                        const row  = sectorData.find(r => r.sector === s)
                        const meta = SECTOR_META[s]
                        const total  = row?.total ?? 0
                        const secure = row?.secure ?? 0
                        if (!total) return null
                        return (
                          <GlowBar key={s}
                            value={secure} max={total}
                            color={meta.color}
                            label={meta.label}
                            sublabel={`${total.toLocaleString()} domains`}
                            count={secure}
                            pct={pct(secure, total)}
                            active={filters.tld===(meta.tld||s)}
                            onClick={() => goToSectorSearch(meta.tld || s)}
                          />
                        )
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── DOMAIN SEARCH ──────────────────────────────── */}
            {pivot === 'domains' && (
              <div className="ac3-dom-search-shell">
                {/* Filter toolbar */}
                <div className="ac3-ds-toolbar">
                  <div className="ac3-ds-sector-row">
                    <span className="ac3-ds-lbl">SECTOR:</span>
                    {['gov','edu','mil','org','com'].map(s => {
                      const meta = SECTOR_META[s]
                      return (
                        <button key={s}
                          className={`ac3-ds-sector-btn${filters.tld===(meta.tld||s)?' ac3-ds-sector-on':''}`}
                          style={{ '--sc': meta.color } as React.CSSProperties}
                          onClick={() => toggleF('tld', meta.tld || s)}>
                          {meta.icon} {meta.label}
                        </button>
                      )
                    })}
                    <button className="ac3-ds-sector-btn ac3-ds-all-btn"
                      onClick={() => setF('tld', null)}
                      style={{ opacity: filters.tld ? 0.5 : 1 }}>
                      ALL
                    </button>
                  </div>
                  <div className="ac3-ds-input-row">
                    <input className="ac3-ds-input"
                      placeholder="⌕ HOSTNAME SEARCH…"
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && commitSearch()} />
                    <input className="ac3-ds-input ac3-ds-input-sm"
                      placeholder="COUNTRY (US, GB…)"
                      value={countryInput}
                      onChange={e => setCountryInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && commitCountry()} />
                    <input className="ac3-ds-input ac3-ds-input-sm"
                      placeholder="ORG DOMAIN…"
                      value={orgInput}
                      onChange={e => setOrgInput(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && commitOrg()} />
                    <div className="ac3-grade-pills" style={{ marginBottom: 0 }}>
                      {(['A+','A','B','C','F'] as const).map(g => (
                        <button key={g}
                          className={`ac3-grade-pill${filters.grade===g?' ac3-gpill-on':''}`}
                          style={{ '--gc': GRADE_COLORS[g] ?? '#7a96e8' } as React.CSSProperties}
                          onClick={() => toggleF('grade', g)}>
                          {g}
                        </button>
                      ))}
                    </div>
                    <div className="ac3-tls-pills" style={{ marginBottom: 0 }}>
                      {[{v:'secure',c:'#34d399',l:'PQC'},{v:'legacy',c:'#ffcc00',l:'LEGACY'},{v:'insecure',c:'#ff4d4d',l:'BROKEN'}].map(s => (
                        <button key={s.v}
                          className={`ac3-tls-pill${filters.status===s.v?' ac3-tls-on':''}`}
                          style={{ '--tc': s.c } as React.CSSProperties}
                          onClick={() => toggleF('status', s.v)}>
                          {s.l}
                        </button>
                      ))}
                    </div>
                    {activeCount > 0 && <button className="ac3-clear-btn" style={{ marginTop: 0, padding: '4px 10px', fontSize: '0.52rem' }} onClick={clearAll}>✕ CLEAR</button>}
                  </div>
                </div>

                {/* Result count + sort indicator */}
                <div className="ac3-ds-result-bar">
                  {loadingDoms ? (
                    <span style={{ color: 'rgba(0,209,255,0.5)', fontSize:'0.6rem' }}>QUERYING…</span>
                  ) : (
                    <span className="ac3-ds-count">
                      {domTotal > 0
                        ? `SHOWING ${(domPage * PAGE_SIZE + 1).toLocaleString()}–${Math.min((domPage + 1) * PAGE_SIZE, domTotal).toLocaleString()} OF ${domTotal.toLocaleString()} DOMAINS`
                        : activeCount > 0 ? 'NO RESULTS' : 'SHOWING ALL DOMAINS'}
                    </span>
                  )}
                  <span className="ac3-ds-sort-info">SORT: {sortCol.replace('_',' ').toUpperCase()} {sortDir === 'desc' ? '↓' : '↑'}</span>
                </div>

                {/* Sortable table */}
                <div className="ac3-ds-table-wrap">
                  <table className="ac3-ds-table">
                    <thead>
                      <tr>
                        {[
                          { col:'hostname',     label:'HOSTNAME'     },
                          { col:'grade',        label:'GRADE'        },
                          { col:'status',       label:'PQC STATUS'   },
                          { col:'kx',           label:'KEY EXCHANGE', noSort: true },
                          { col:'tls',          label:'TLS',          noSort: true },
                          { col:'country',      label:'COUNTRY'      },
                          { col:'last_updated', label:'UPDATED'      },
                        ].map(th => (
                          <th key={th.col}
                            className={`ac3-ds-th${th.noSort ? '' : ' ac3-ds-th-sort'}${sortCol===th.col ? ' ac3-ds-th-active' : ''}`}
                            onClick={th.noSort ? undefined : () => toggleSort(th.col)}>
                            {th.label}
                            {!th.noSort && <span className="ac3-ds-sort-arrow">{sortCol===th.col ? (sortDir==='desc'?'↓':'↑') : '⇅'}</span>}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {domains.map(d => {
                        const kx     = d.pqc_group_name || d.key_exchange || '—'
                        const isPQC  = kx.includes('MLKEM') || kx.includes('Kyber')
                        const gcolor = GRADE_COLORS[d.overall_grade] ?? '#7a96e8'
                        const scolor = d.pqc_status === 'secure' ? '#34d399' : d.pqc_status === 'insecure' ? '#ff4d4d' : '#ffcc00'
                        return (
                          <tr key={d.hostname} className="ac3-ds-row">
                            <td className="ac3-ds-td ac3-ds-td-host">
                              <span className="ac3-ds-hostname">{d.hostname}</span>
                              {d.scan_violations ? <span className="ac3-ds-violations" title={`${d.scan_violations} scan violations`}>⚠ {d.scan_violations}</span> : null}
                            </td>
                            <td className="ac3-ds-td">
                              <span className="ac3-ds-grade" style={{ color: gcolor, borderColor: gcolor }}>{d.overall_grade}</span>
                            </td>
                            <td className="ac3-ds-td">
                              <span className="ac3-ds-status" style={{ color: scolor }}>
                                {d.pqc_status === 'secure' ? '◈ HARDENED' : d.pqc_status === 'insecure' ? '✕ FRACTURED' : '◎ LEGACY'}
                              </span>
                            </td>
                            <td className="ac3-ds-td ac3-ds-td-kx">
                              <span className={`ac3-ds-kx${isPQC ? ' ac3-ds-kx-pqc' : ''}`}>{kx.length > 30 ? kx.slice(0,30)+'…' : kx}</span>
                            </td>
                            <td className="ac3-ds-td">
                              <span className={`ac3-ds-tls${d.tls_1_3 ? ' ac3-ds-tls13' : ' ac3-ds-tls12'}`}>
                                {d.tls_1_3 ? 'TLS 1.3' : 'TLS 1.2'}
                              </span>
                            </td>
                            <td className="ac3-ds-td">
                              {d.asn_country ? (
                                <span className="ac3-ds-country" title={d.asn_country}>
                                  {countryFlag(d.asn_country)} {d.asn_country}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="ac3-ds-td ac3-ds-td-time">
                              <span className="ac3-ds-time">{relTime(d.last_updated)}</span>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                  {!loadingDoms && domains.length === 0 && (
                    <div className="ac3-drill-empty" style={{ height: 200 }}>
                      <div className="ac3-drill-icon">⊕</div>
                      <p>{activeCount > 0 ? 'No matching domains.' : 'Use filters above to search domains, or browse all records.'}</p>
                    </div>
                  )}
                </div>

                {/* Pagination */}
                {domTotal > PAGE_SIZE && (
                  <div className="ac3-ds-pages">
                    <button className="ac3-page-btn" disabled={domPage === 0}
                      onClick={() => setDomPage(p => Math.max(0, p - 1))}>◀ PREV</button>
                    <span className="ac3-ds-page-info">
                      PAGE {domPage + 1} / {Math.ceil(domTotal / PAGE_SIZE)}
                    </span>
                    <button className="ac3-page-btn" disabled={(domPage + 1) * PAGE_SIZE >= domTotal}
                      onClick={() => setDomPage(p => p + 1)}>NEXT ▶</button>
                  </div>
                )}
              </div>
            )}

          </div>{/* /center */}

          {/* ── Right drill-down panel ──────────────────────────────────────── */}
          {pivot !== 'domains' && <div className={`ac3-right${showDrill ? ' ac3-right-open' : ''}`}>
            <div className="ac3-right-hdr">
              {showDrill ? (
                <>
                  <span className="ac-panel-dot ac-dot-cyan" />
                  <span className="ac3-right-title">
                    {filterSummary() || 'RESULTS'}
                    {domTotal > 0 && <span className="ac3-right-count"> · {domTotal.toLocaleString()}</span>}
                  </span>
                  <button className="ac3-right-close" onClick={clearAll}>✕</button>
                </>
              ) : (
                <><span className="ac-panel-dot ac-dot-green" /><span>DOMAIN DRILL-DOWN</span></>
              )}
            </div>

            {!showDrill && (
              <div className="ac3-drill-empty">
                <div className="ac3-drill-icon">⊕</div>
                <p>Use left panel filters, click any chart element, or apply a smart query from INSIGHTS to drill into matching domains.</p>
              </div>
            )}

            {showDrill && loadingDoms && (
              <div className="ac-loading" style={{ height: 100, fontSize: '0.7rem' }}>
                <div className="ac-loading-ring" style={{ width: 18, height: 18 }} />
                <span>QUERYING DATABASE</span>
              </div>
            )}

            {showDrill && !loadingDoms && domains.length === 0 && (
              <div className="ac3-drill-empty"><p>No matching domains found.</p></div>
            )}

            {showDrill && !loadingDoms && domains.length > 0 && (
              <>
                <div className="ac3-dom-list">
                  {domains.map(d => {
                    const kx     = d.pqc_group_name || d.key_exchange || '—'
                    const isPQC  = kx.includes('MLKEM') || kx.includes('Kyber')
                    const gcolor = GRADE_COLORS[d.overall_grade] ?? '#7a96e8'
                    const scolor = d.pqc_status === 'secure' ? '#34d399' : d.pqc_status === 'insecure' ? '#ff4d4d' : '#ffcc00'
                    return (
                      <div key={d.hostname} className="ac3-dom-row">
                        <div className="ac3-dom-top">
                          <span className="ac3-dom-name">{d.hostname}</span>
                          {d.asn_country && <span className="ac3-dom-flag">{countryFlag(d.asn_country)}</span>}
                          <span className="ac3-dom-grade" style={{ color: gcolor, borderColor: gcolor }}>{d.overall_grade}</span>
                        </div>
                        <div className="ac3-dom-bottom">
                          <span className="ac3-dom-status" style={{ color: scolor }}>
                            {d.pqc_status==='secure'?'◈ HARDENED':d.pqc_status==='insecure'?'◈ FRACTURED':'◈ LEGACY'}
                          </span>
                          <span className={`ac3-dom-kx${isPQC?' ac3-kx-pqc':''}`}>
                            {kx.length > 24 ? kx.slice(0, 24) + '…' : kx}
                          </span>
                        </div>
                        <div className="ac3-dom-meta">
                          {d.tls_1_3 !== null && (
                            <span className={`ac3-dom-tls${d.tls_1_3?' ac3-tls-13':' ac3-tls-12'}`}>
                              {d.tls_1_3 ? 'TLS 1.3' : 'TLS 1.2'}
                            </span>
                          )}
                          {d.asn_country && <span className="ac3-dom-country">{d.asn_country}</span>}
                          {d.pubkey_size && <span className="ac3-dom-key">{d.pubkey_size}b</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
                {domTotal > DRILL_PAGE && (
                  <div className="ac3-pages">
                    <button className="ac3-page-btn" disabled={domPage === 0}
                      onClick={() => setDomPage(p => Math.max(0, p - 1))}>◀</button>
                    <span>{domPage + 1} / {Math.ceil(domTotal / DRILL_PAGE)}</span>
                    <button className="ac3-page-btn" disabled={(domPage + 1) * DRILL_PAGE >= domTotal}
                      onClick={() => setDomPage(p => p + 1)}>▶</button>
                  </div>
                )}
              </>
            )}
          </div>}

        </div>
      )}
    </div>
  )
}

// ── Knowledge Base ────────────────────────────────────────────────────────────

type KBSection = 'briefings' | 'threat' | 'protocols' | 'primitives' | 'software' | 'capki' | 'os' | 'laws' | 'compliance' | 'wizard'

interface ProtocolEntry {
  id: string; name: string; versions?: string; category: string
  quantumRisk: 'critical' | 'high' | 'medium' | 'safe'
  harvestRisk: 'critical' | 'high' | 'medium' | 'low'
  description: string
  vulnerability: string
  brokenBy: string[]
  affectedSystems: string[]
  migrationPath: string
  migrationStatus: string
  deployedIn: string
  urgency: string
  links: { label: string; url: string }[]
}

interface CaProvider {
  id:             number
  name:           string
  readinessTier:  'high' | 'moderate' | 'staging'
  readinessLabel: string
  pqcCapability:  string
  targetUseCase:  string
}

interface PrimPopEntry { count: number; rank: number; category: string }
interface PrimPopularity {
  lookup:      Record<string, PrimPopEntry>
  total:       number   // TLS connections scanned
  totalCerts:  number   // cert chain rows (includes root + intermediate + leaf)
  kx:  { name: string; count: number; rank: number }[]
  sig: { name: string; count: number; rank: number }[]
}

interface OsPlatform {
  id:            number
  name:          string
  version:       string
  vendor:        string
  platformType:  'desktop' | 'server' | 'mobile' | 'embedded'
  readinessTier: 'safe' | 'partial' | 'planned' | 'none'
  cryptoStack:   string
  mlKem:         'yes' | 'partial' | 'no' | 'planned'
  mlDsa:         'yes' | 'partial' | 'no' | 'planned'
  notes:         string
}

interface PrimitiveEntry {
  name: string; category: 'kx' | 'cipher' | 'sig'
  subcategory: string
  type: string; keySize: string
  quantumSafe: 'broken' | 'weakened' | 'safe'
  classicBits: number; quantumBits: number
  attackVector: string; standard: string
  recommendation: 'avoid' | 'conditional' | 'use'
  description: string
}

// TODO: remove after DB migration verified
const PROTOCOL_DATA: ProtocolEntry[] = [
  {
    id: 'ssl3', name: 'SSL 3.0', versions: 'RFC 6101 (1996, deprecated)', category: 'Transport',
    quantumRisk: 'critical', harvestRisk: 'critical',
    description: 'SSL 3.0 is the predecessor to TLS, designed by Netscape in 1996. It is completely broken by both classical and quantum attacks. The POODLE vulnerability (CVE-2014-3566) demonstrated that SSL 3.0\'s CBC padding is classically exploitable in ~256 requests. Its MAC-then-encrypt construction has known weaknesses. On top of these classical breaks, every key exchange mechanism (RSA, DHE, ECDHE) and authentication algorithm in SSL 3.0 is broken by Shor\'s algorithm. SSL 3.0 was deprecated by RFC 7568 in 2015 and should not exist in any modern deployment.',
    vulnerability: 'SSL 3.0 has multiple classical vulnerabilities that render it broken independent of quantum computing. POODLE exploits CBC padding oracle. The MAC-then-encrypt construction enables chosen-ciphertext attacks. Additionally, RSA key exchange (without forward secrecy) and DHE/ECDHE (with Shor\'s quantum break) mean every dimension of SSL 3.0 security is compromised. The RC4 cipher (historically common in SSL 3.0) was also broken classically in 2015.',
    brokenBy: ['RSA key exchange (no forward secrecy)', 'DHE / ECDHE (Shor\'s algorithm)', 'POODLE CBC padding oracle (classical)', 'RC4 stream cipher (classically broken)', 'MD5/SHA-1 MACs (classically weak)', 'SSL 3.0 fallback downgrade attacks'],
    affectedSystems: ['Ancient web servers (IIS 6.0, Apache <2.4)', 'Embedded systems with stale TLS stacks', 'Legacy payment terminals', 'Industrial SCADA with unpatched firmware', 'Old network appliances (load balancers, firewalls)'],
    migrationPath: 'Disable SSL 3.0 immediately — no migration, only removal. It is classically broken. All major browsers removed SSL 3.0 support by 2015. Verify with: openssl s_client -ssl3 -connect host:443 (should fail). In nginx: ssl_protocols TLSv1.2 TLSv1.3; In Apache: SSLProtocol all -SSLv3 -TLSv1 -TLSv1.1. Any system that still supports SSL 3.0 is running critically outdated software — full platform upgrade required.',
    migrationStatus: 'Disabled in all major browsers since 2014–2015. RFC 7568 formally prohibits SSL 3.0. No legitimate deployment should have this enabled.',
    deployedIn: 'Should not be deployed anywhere. If found: legacy embedded systems, unpatched industrial equipment, ancient mainframe TLS stacks',
    urgency: 'CRITICAL — SSL 3.0 is classically broken (POODLE) before quantum is even a concern. Disable immediately. Any system supporting SSL 3.0 is critically vulnerable right now.',
    links: [
      { label: 'RFC 7568 — Deprecating SSL 3.0', url: 'https://www.rfc-editor.org/rfc/rfc7568' },
      { label: 'CVE-2014-3566 — POODLE vulnerability', url: 'https://nvd.nist.gov/vuln/detail/CVE-2014-3566' },
      { label: 'POODLE attack paper — Möller, Duong, Kotowicz', url: 'https://www.openssl.org/~bodo/ssl-poodle.pdf' },
      { label: 'NIST SP 800-52r2 — Prohibits SSL 3.0', url: 'https://csrc.nist.gov/publications/detail/sp/800-52/rev-2/final' },
      { label: 'SSL 3.0 specification (RFC 6101 — historical)', url: 'https://www.rfc-editor.org/rfc/rfc6101' },
    ],
  },
  {
    id: 'tls10', name: 'TLS 1.0', versions: 'RFC 2246 (1999, deprecated)', category: 'Transport',
    quantumRisk: 'critical', harvestRisk: 'critical',
    description: 'TLS 1.0, defined in 1999, was the first TLS version and the direct successor to SSL 3.0. It has multiple well-documented classical vulnerabilities (BEAST, POODLE-TLS, CRIME, RC4 weaknesses) and has been formally deprecated by RFC 8996 (2021). PCI DSS 3.2 required removal of TLS 1.0 from all payment systems by June 2018. Every key exchange and authentication algorithm in TLS 1.0 is also broken by Shor\'s algorithm. Any system still running TLS 1.0 is non-compliant with PCI DSS, HIPAA, NIST guidance, and most enterprise security frameworks.',
    vulnerability: 'TLS 1.0 uses an insecure PRF construction (MD5 and SHA-1 combined) for key material derivation. It is vulnerable to BEAST (Browser Exploit Against SSL/TLS) via CBC IV chaining in block ciphers, and to CRIME/BREACH compression-based attacks. The RC4 cipher, commonly used with TLS 1.0, was classically broken in 2015. Independently of these classical breaks, TLS 1.0 key exchange (RSA, DHE, ECDHE) and certificate authentication (RSA, ECDSA) are fully broken by Shor\'s algorithm.',
    brokenBy: ['RSA key exchange (static)', 'DHE (Shor\'s DLP solver)', 'ECDHE (Shor\'s ECDLP solver)', 'BEAST CBC IV attack (classical)', 'RC4 cipher suite (classically broken)', 'MD5+SHA-1 PRF (weakened)', 'RSA/ECDSA certificate signatures'],
    affectedSystems: ['Legacy payment systems (pre-PCI DSS 3.2 migration)', 'Old enterprise web applications', 'Unpatched web servers (IIS 7.x, Apache 2.2)', 'Legacy banking middleware', 'Healthcare EMR systems (pre-2018)', 'Old network equipment (Cisco IOS <15.x)', 'Industrial SCADA with legacy TLS'],
    migrationPath: 'Disable TLS 1.0 immediately. Configure minimum TLS version to 1.2 or 1.3. In nginx: ssl_protocols TLSv1.2 TLSv1.3; In Apache: SSLProtocol TLSv1.2 TLSv1.3; In IIS: disable via registry or IIS Crypto tool. Verify via SSL Labs scan (should show F rating if TLS 1.0 is enabled). If applications break, they require modernization — TLS 1.0 support in client libraries is a red flag indicating an outdated stack. Final goal: TLS 1.3 with ML-KEM hybrid key exchange.',
    migrationStatus: 'Deprecated by RFC 8996 (2021). Disabled by default in Chrome 84+, Firefox 74+, Safari 13.1+, Edge 84+. PCI DSS 3.2 required removal from payment systems by June 2018.',
    deployedIn: 'Legacy enterprise applications, unpatched web servers, old payment terminals, healthcare EMR systems, industrial equipment with stale firmware',
    urgency: 'CRITICAL — Classically broken (BEAST) and quantum-broken. Non-compliant with PCI DSS, NIST SP 800-52, and HIPAA. Disable immediately — no acceptable migration path exists, only removal.',
    links: [
      { label: 'RFC 8996 — Deprecating TLS 1.0 and 1.1', url: 'https://www.rfc-editor.org/rfc/rfc8996' },
      { label: 'RFC 2246 — TLS 1.0 specification (historical)', url: 'https://www.rfc-editor.org/rfc/rfc2246' },
      { label: 'BEAST attack paper — Rizzo & Duong (2011)', url: 'https://packetstormsecurity.com/files/105499/Browser-Exploit-Against-SSL-TLS.html' },
      { label: 'PCI DSS 3.2 — TLS 1.0 removal requirement', url: 'https://www.pcisecuritystandards.org/documents/Migrating-from-SSL-Early-TLS-Info-Supp-v1_1.pdf' },
      { label: 'NIST SP 800-52r2 — TLS implementation guidance', url: 'https://csrc.nist.gov/publications/detail/sp/800-52/rev-2/final' },
      { label: 'SSL Labs — Server test (checks TLS version support)', url: 'https://www.ssllabs.com/ssltest/' },
    ],
  },
  {
    id: 'tls11', name: 'TLS 1.1', versions: 'RFC 4346 (2006, deprecated)', category: 'Transport',
    quantumRisk: 'critical', harvestRisk: 'critical',
    description: 'TLS 1.1 was introduced in 2006 to address the BEAST vulnerability in TLS 1.0 by using explicit CBC IVs instead of implicit chaining. However, it retains MD5 in its PRF construction, relies on RC4 cipher suites, and lacks the cipher suite hygiene of TLS 1.2. TLS 1.1 was deprecated alongside TLS 1.0 by RFC 8996 (2021) and all major browsers disabled it in 2020. Like TLS 1.0, its key exchange (RSA, DHE, ECDHE) and authentication are fully broken by Shor\'s algorithm. Any server that supports TLS 1.1 and not TLS 1.2/1.3 is running critically outdated infrastructure.',
    vulnerability: 'TLS 1.1 fixed BEAST via explicit IVs but retained significant weaknesses. The PRF uses MD5 and SHA-1 concatenation (both classically weakened). RC4 cipher suites remain available. Lucky Thirteen timing side-channel attacks apply to CBC cipher suites. CRIME/BREACH compression oracle attacks apply. And critically — RSA key transport, DHE, and ECDHE key exchange are all broken by Shor\'s algorithm, with RSA key transport additionally lacking forward secrecy.',
    brokenBy: ['RSA key exchange (no forward secrecy)', 'DHE (Shor\'s DLP solver)', 'ECDHE (Shor\'s ECDLP solver)', 'RC4 cipher suites (classically broken 2015)', 'MD5+SHA-1 PRF (weakened)', 'Lucky Thirteen CBC timing (classical)', 'RSA/ECDSA certificate signatures'],
    affectedSystems: ['Legacy enterprise applications not yet migrated from TLS 1.0/1.1', 'Old IIS 7.5/8.0 Windows Server deployments', 'Java 6/7 applications (default TLS 1.0/1.1)', 'Legacy .NET Framework <4.6 applications', 'Old Cisco ASA VPN appliances', 'Enterprise middleware (MQ, legacy ESB)', 'IoT devices with stale TLS stacks'],
    migrationPath: 'Disable TLS 1.1 and enforce TLS 1.2 minimum (TLS 1.3 preferred). All major server stacks support this via configuration. Java: set -Dhttps.protocols=TLSv1.2,TLSv1.3 or update to JDK 11+ (disables TLS 1.1 by default). .NET: set SecurityProtocol = TLS12 | TLS13. If a system cannot be configured to disable TLS 1.1, it requires a platform upgrade. Long-term: TLS 1.3 with ML-KEM hybrid key exchange is the target state for quantum safety.',
    migrationStatus: 'Deprecated by RFC 8996 (2021). Disabled by default in all major browsers (2020). Java 8u261+ disables TLS 1.0/1.1 by default. .NET 5+ defaults to TLS 1.2 minimum.',
    deployedIn: 'Legacy Java/J2EE applications, old .NET Framework apps, unpatched Windows Server instances, legacy network appliances',
    urgency: 'CRITICAL — Classically weakened (Lucky Thirteen, RC4) and quantum-broken. Deprecated by RFC 8996 and disabled in all modern browsers. Remove from all server configurations immediately.',
    links: [
      { label: 'RFC 8996 — Deprecating TLS 1.0 and 1.1', url: 'https://www.rfc-editor.org/rfc/rfc8996' },
      { label: 'RFC 4346 — TLS 1.1 specification (historical)', url: 'https://www.rfc-editor.org/rfc/rfc4346' },
      { label: 'Lucky Thirteen attack — Al Fardan & Paterson (2013)', url: 'https://www.isg.rhul.ac.uk/tls/TLStiming.pdf' },
      { label: 'NIST SP 800-52r2 — TLS implementation guidance', url: 'https://csrc.nist.gov/publications/detail/sp/800-52/rev-2/final' },
      { label: 'Microsoft — Disabling TLS 1.0/1.1 in Windows', url: 'https://learn.microsoft.com/en-us/windows/win32/secauthn/protocols-in-tls-ssl--schannel-ssp-' },
      { label: 'Java TLS version guidance', url: 'https://www.java.com/en/configure_crypto.html' },
    ],
  },
  {
    id: 'tls12', name: 'TLS 1.2', versions: 'RFC 5246 (2008)', category: 'Transport',
    quantumRisk: 'high', harvestRisk: 'critical',
    description: 'Transport Layer Security 1.2 secures the majority of internet traffic. It negotiates key exchange using RSA or ephemeral Diffie-Hellman (ECDHE/DHE), both broken by Shor\'s algorithm. TLS 1.2 remains widely deployed despite TLS 1.3 being available since 2018. Its optional RSA key transport (non-ephemeral) does not provide forward secrecy — recorded traffic is trivially decryptable once RSA is broken. ECDHE variants provide forward secrecy but the key exchange itself remains quantum-vulnerable.',
    vulnerability: 'The TLS 1.2 handshake negotiates a symmetric session key using RSA key encapsulation or ECDH/DHE key agreement. Both mechanisms are directly broken by Shor\'s algorithm. An adversary recording TLS 1.2 traffic today can decrypt it once a CRQC exists. RSA cipher suites additionally lack forward secrecy — a single server private key compromise decrypts all past sessions.',
    brokenBy: ['RSA key transport (TLS_RSA_*)', 'ECDHE (all curves)', 'DHE (all FFDHE groups)', 'RSA certificate signatures', 'ECDSA certificate signatures'],
    affectedSystems: ['HTTPS web servers', 'REST/SOAP APIs', 'SMTPS email submission', 'IMAPS/POP3S mail retrieval', 'LDAPS directory', 'Legacy banking APIs', 'Industrial SCADA over TLS', 'Payment terminals (PCI DSS)', 'Healthcare HL7/FHIR APIs'],
    migrationPath: 'Short-term: disable all RSA key exchange cipher suites (TLS_RSA_*) immediately — these lack forward secrecy. Enforce ECDHE or DHE only. Medium-term: upgrade servers to TLS 1.3 with X25519MLKEM768 hybrid key exchange. Update certificates to support hybrid or PQC signatures once CAs offer them. All major TLS libraries (OpenSSL 3.x, Boring SSL, Rustls, Go crypto/tls) support TLS 1.3; migration is a configuration change, not a code rewrite.',
    migrationStatus: 'TLS 1.3 widely available. X25519MLKEM768 deployed by Chrome, Firefox, Cloudflare, AWS. IETF RFC 8996 recommends against TLS 1.2 for new deployments.',
    deployedIn: 'Legacy HTTPS endpoints, enterprise APIs, banking systems, healthcare gateways, industrial control systems',
    urgency: 'HIGH — Disable non-PFS cipher suites immediately. Harvest-now-decrypt-later attacks capture TLS 1.2 traffic today for future decryption.',
    links: [
      { label: 'RFC 5246 — TLS 1.2 specification', url: 'https://www.rfc-editor.org/rfc/rfc5246' },
      { label: 'RFC 8996 — Deprecating TLS 1.0/1.1/1.2', url: 'https://www.rfc-editor.org/rfc/rfc8996' },
      { label: 'NIST SP 800-52r2 — TLS guidelines', url: 'https://csrc.nist.gov/publications/detail/sp/800-52/rev-2/final' },
      { label: 'Cloudflare PQC TLS deployment', url: 'https://blog.cloudflare.com/pq-2024/' },
      { label: 'BSI TR-02102-2 — TLS cipher suite guidance', url: 'https://www.bsi.bund.de/EN/Topics/Crypto/Guidelines/guidelines.html' },
    ],
  },
  {
    id: 'tls13', name: 'TLS 1.3', versions: 'RFC 8446 (2018)', category: 'Transport',
    quantumRisk: 'medium', harvestRisk: 'high',
    description: 'TLS 1.3 is the current standard for transport security. It removed all RSA key exchange and non-forward-secret cipher suites, mandating ephemeral key exchange only. This means individual session keys cannot be decrypted even if the server\'s long-term private key is later compromised. However, TLS 1.3 still defaults to X25519 or P-256 ECDH — both are fully broken by Shor\'s algorithm. Traffic recorded today using vanilla TLS 1.3 can be decrypted once a CRQC exists. A hybrid PQC extension (X25519MLKEM768) is available in production.',
    vulnerability: 'The TLS 1.3 key exchange (KeyShare extension) uses X25519 or P-256/P-384 ECDH by default. These are broken by Shor\'s ECDLP solver. While TLS 1.3 provides forward secrecy against classical private key compromise, it does not protect against a quantum adversary harvesting and later decrypting the key exchange itself. Certificate authentication (server identity) uses ECDSA or RSA — also quantum-broken.',
    brokenBy: ['X25519 key exchange (default)', 'P-256 ECDH', 'P-384 ECDH', 'ECDSA certificate signatures', 'RSA-PSS certificate signatures'],
    affectedSystems: ['Modern HTTPS websites', 'CDN edge servers', 'API gateways', 'Microservice mTLS', 'gRPC services', 'WebRTC signaling', 'HTTP/2 and HTTP/3 backends'],
    migrationPath: 'Enable X25519MLKEM768 hybrid key exchange in your TLS stack. This is a single configuration line in most libraries: OpenSSL 3.2+ (TLS_HYBRID_KEX), BoringSSL (GREASE_PQC enabled by default in Chrome), Rustls (via the pqc feature flag), Go (experimental crypto/mlkem package). Hybrid KX runs X25519 alongside ML-KEM — no regression if ML-KEM has an unforeseen issue. Certificate migration requires CA support; watch for hybrid cert profiles from major CAs.',
    migrationStatus: 'X25519MLKEM768 deployed in Chrome 131+, Firefox 132+, Cloudflare global network, AWS CloudFront. OpenSSL 3.5 includes ML-KEM by default.',
    deployedIn: 'Modern web browsers, CDNs, cloud load balancers, API-first applications, mobile apps',
    urgency: 'MEDIUM — Forward secrecy limits blast radius but traffic is still harvestable. Enable X25519MLKEM768 today — it is already available in major runtimes.',
    links: [
      { label: 'RFC 8446 — TLS 1.3 specification', url: 'https://www.rfc-editor.org/rfc/rfc8446' },
      { label: 'IETF draft-tls-hybrid-design — PQC hybrid KX', url: 'https://www.ietf.org/archive/id/draft-ietf-tls-hybrid-design-10.txt' },
      { label: 'Chrome TLS PQC announcement (Google Security Blog)', url: 'https://security.googleblog.com/2023/08/towards-tls-for-post-quantum-world.html' },
      { label: 'Cloudflare — How PQC in TLS 1.3 works', url: 'https://blog.cloudflare.com/post-quantum-for-all/' },
      { label: 'IANA TLS hybrid key exchange code points', url: 'https://www.iana.org/assignments/tls-parameters/tls-parameters.xhtml' },
      { label: 'OpenSSL 3.5 PQC migration guide', url: 'https://openssl-library.org/post/2024-10-11-oqsprovider/' },
    ],
  },
  {
    id: 'ssh2', name: 'SSH-2', versions: 'RFC 4251–4254 (2006)', category: 'Remote Access',
    quantumRisk: 'high', harvestRisk: 'high',
    description: 'SSH-2 is the protocol securing the administrative layer of virtually all server infrastructure. It uses public-key cryptography for both server authentication (host keys) and optionally client authentication. The key exchange algorithms (ECDH Curve25519, P-256, DH group14/16/18) and all standard host key types (RSA, Ed25519, ECDSA) are broken by Shor\'s algorithm. An adversary recording SSH session establishment handshakes today can, once a CRQC is available, recover session keys and forge host identities. The exposure is particularly severe for CI/CD pipelines and automated deployment systems that rely on SSH with persistent known_hosts trust chains.',
    vulnerability: 'SSH-2 key exchange negotiates a shared secret using ECDH or DH. Host authentication uses RSA or Ed25519/ECDSA signatures. Both steps are quantum-broken. The known_hosts file represents a long-lived trust database of host keys — if those private keys are later breakable by quantum, host impersonation becomes trivial without the client detecting it.',
    brokenBy: ['curve25519-sha256 KEX (default)', 'ecdh-sha2-nistp256/384/521 KEX', 'diffie-hellman-group14-sha256', 'diffie-hellman-group16-sha512', 'ssh-rsa host keys', 'ecdsa-sha2-nistp256 host keys', 'ssh-ed25519 host keys'],
    affectedSystems: ['Linux/Unix server administration', 'CI/CD pipeline deployments (GitHub Actions, GitLab CI, Jenkins)', 'Bastion hosts / jump servers', 'SFTP file transfer', 'Git over SSH (GitHub, GitLab, Bitbucket)', 'Network device management (routers, switches)', 'Cloud instance access (EC2, GCP, Azure VM)'],
    migrationPath: 'OpenSSH 9.0 (2022) added hybrid PQC: sntrup761x25519-sha512@openssh.com. Enable it by adding KexAlgorithms sntrup761x25519-sha512@openssh.com,curve25519-sha256 to sshd_config. Both client and server must support it. For host key types, IETF is standardizing PQC SSH host key formats — watch for ML-DSA host key types in OpenSSH 10+. Regenerating host keys will require updating known_hosts on all clients.',
    migrationStatus: 'OpenSSH 9.0+ supports sntrup761x25519-sha512 hybrid KEX. PQC host key types in development. LibSSH, PuTTY, and Paramiko working on PQC support.',
    deployedIn: 'All Linux servers, cloud instances, network devices, CI/CD systems, developer workstations',
    urgency: 'HIGH — Enable sntrup761x25519-sha512 KEX today in OpenSSH 9.0+. Long-lived SSH sessions and persistent host key trust chains are prime harvest targets.',
    links: [
      { label: 'OpenSSH 9.0 release notes — PQC KEX', url: 'https://www.openssh.com/txt/release-9.0' },
      { label: 'IETF draft-kampanakis-curdle-ssh-pq-ke — PQC SSH', url: 'https://www.ietf.org/archive/id/draft-kampanakis-curdle-ssh-pq-ke-03.txt' },
      { label: 'NIST NCCoE SP 1800-38A — SSH migration', url: 'https://www.nccoe.nist.gov/projects/migrating-post-quantum-cryptography' },
      { label: 'RFC 4253 — SSH Transport Layer Protocol', url: 'https://www.rfc-editor.org/rfc/rfc4253' },
      { label: 'CISA SSH configuration guidance', url: 'https://www.cisa.gov/news-events/cybersecurity-advisories' },
    ],
  },
  {
    id: 'pki', name: 'X.509 / Web PKI', versions: 'RFC 5280 / CA/Browser Forum', category: 'Identity',
    quantumRisk: 'critical', harvestRisk: 'critical',
    description: 'X.509 / Web PKI is the trust infrastructure underpinning every authenticated connection on the internet. Certificate Authorities (CAs) sign TLS certificates, code signing certificates, and email certificates using RSA or ECDSA. Every certificate ever issued, and every CA signing key, uses algorithms that are fully broken by Shor\'s algorithm. This is the most severe quantum vulnerability because it undermines authentication itself — not just confidentiality. Even if key exchange is upgraded to ML-KEM, if the server certificate is still ECDSA-signed, the server\'s identity cannot be trusted against a quantum adversary.',
    vulnerability: 'CA signing keys use RSA-2048/4096 or ECDSA P-256/P-384. All certificates signed by these CAs inherit their quantum vulnerability. The entire chain of trust — root CAs, intermediate CAs, end-entity certificates — is broken simultaneously. Certificate revocation (CRL/OCSP) also uses RSA/ECDSA signatures. A quantum adversary can forge certificates, impersonate any HTTPS site, and sign malicious code with trusted identities.',
    brokenBy: ['RSA-2048/4096 CA signing keys', 'ECDSA P-256/P-384 CA signing keys', 'RSA/ECDSA end-entity cert signatures', 'OCSP response signatures', 'CRL signatures', 'Certificate Transparency log signatures'],
    affectedSystems: ['Every HTTPS website', 'Every TLS-protected API', 'Code signing (Windows, macOS, Linux packages)', 'Email (S/MIME)', 'Document signing (PDF)', 'Software update mechanisms', 'VPN gateways', 'IoT device attestation', 'Federal identity (HSPD-12, PIV cards)'],
    migrationPath: 'This is the hardest migration. Steps: (1) CAs must generate new PQC signing keys (ML-DSA-65 or ML-DSA-87 per FIPS 204). (2) Browsers and OS trust stores must add PQC root CAs. (3) Hybrid certificates (dual RSA+ML-DSA or ECDSA+ML-DSA signatures) provide a backwards-compatible transition. (4) Certificate lifetime may need reduction to reduce exposure window. (5) ACME protocol (Let\'s Encrypt) needs PQC certificate issuance support. Timeline: CAs are piloting hybrid certificates in 2025; full PQC WebPKI is a 5–10 year migration.',
    migrationStatus: 'DigiCert and Let\'s Encrypt piloting hybrid certs. IETF RFC drafts for hybrid X.509 profiles. Chrome root store team evaluating PQC root CA submissions.',
    deployedIn: 'Every HTTPS site, browser trust store, OS certificate store, VPN, IoT, PKI infrastructure globally',
    urgency: 'CRITICAL — Every recorded HTTPS session is decryptable once PKI is broken. This undermines authentication retroactively. Start cryptographic inventory of all certificate issuance immediately.',
    links: [
      { label: 'RFC 5280 — X.509 PKI certificate profile', url: 'https://www.rfc-editor.org/rfc/rfc5280' },
      { label: 'IETF draft-ietf-lamps-dilithium-certificates — ML-DSA in X.509', url: 'https://www.ietf.org/archive/id/draft-ietf-lamps-dilithium-certificates-06.txt' },
      { label: 'NIST NCCoE — Migration to PQC project', url: 'https://www.nccoe.nist.gov/projects/migrating-post-quantum-cryptography' },
      { label: 'CA/Browser Forum — PQC working group', url: 'https://cabforum.org/working-groups/server/quantum-ready/' },
      { label: 'FIPS 204 — ML-DSA standard (PDF)', url: 'https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.204.pdf' },
      { label: 'Google — Chrome root store PQC plans', url: 'https://www.chromium.org/Home/chromium-security/root-ca-policy/' },
    ],
  },
  {
    id: 'dnssec', name: 'DNSSEC', versions: 'RFC 4033–4035 (2005)', category: 'Identity',
    quantumRisk: 'high', harvestRisk: 'medium',
    description: 'DNSSEC authenticates DNS records using digital signatures, preventing DNS cache poisoning and man-in-the-middle redirection attacks. Zone Signing Keys (ZSK) and Key Signing Keys (KSK) use RSA-1024/2048 or ECDSA P-256/P-384. A quantum adversary with a CRQC could forge DNSSEC signatures for any zone, redirect entire TLDs to malicious servers, or undermine DANE (DNS-based Authentication of Named Entities) by forging TLSA records. The root zone and all TLD zones are signed with algorithms that would be quantum-broken.',
    vulnerability: 'DNSSEC record signatures (RRSIG records) use RSA or ECDSA. If the zone signing key is broken, an adversary can create forged RRSIG records for any DNS name in that zone, effectively redirecting all traffic at the DNS layer. DANE/TLSA relies on DNSSEC trust chains for certificate pinning — quantum-broken DNSSEC undermines DANE completely.',
    brokenBy: ['RSA-1024 ZSK (algorithm 5)', 'RSA-2048 ZSK/KSK (algorithm 8)', 'ECDSA P-256 ZSK (algorithm 13)', 'ECDSA P-384 ZSK (algorithm 14)', 'Root zone KSK', 'TLD zone signing chains'],
    affectedSystems: ['Root DNS zone (IANA)', 'All TLD zones (.com, .gov, .edu, .mil, .uk, etc.)', 'Enterprise internal DNS zones', 'DANE/TLSA email validation', 'DNSSEC-validated resolvers (ISP, Google 8.8.8.8, Cloudflare 1.1.1.1)', 'RPKI (Route Origin Authorization signatures share the same PKI ecosystem)'],
    migrationPath: 'IETF is developing new DNSSEC algorithm identifiers for PQC. Draft proposals include ML-DSA (Dilithium) and SLH-DSA algorithm types. Until ratified, operational mitigations: shorten RRSIG validity periods to reduce harvest window, monitor for anomalous DNSSEC validation failures, and enable DNSSEC validation with QNAME minimization. Zone operators should follow IETF DNSOP working group for PQC algorithm deployment guidance.',
    migrationStatus: 'IETF DNSOP working group drafting PQC algorithm support. No production deployments yet. Root zone KSK rollover process (precedent from 2018) provides a migration template.',
    deployedIn: '.gov, .edu, .mil TLDs, enterprise internal DNS, ISP recursive resolvers, public resolvers (8.8.8.8, 1.1.1.1)',
    urgency: 'HIGH — Forged DNSSEC enables global traffic redirection. RRSIG records with short TTLs limit harvest exposure. Follow IETF DNSOP for PQC algorithm deployment timeline.',
    links: [
      { label: 'RFC 4033 — DNSSEC Introduction and Requirements', url: 'https://www.rfc-editor.org/rfc/rfc4033' },
      { label: 'IETF draft-ietf-dnsop-algorithm-update — PQC DNSSEC', url: 'https://www.ietf.org/archive/id/draft-ietf-dnsop-algorithm-update-01.txt' },
      { label: 'ICANN DNSSEC & Security Working Group', url: 'https://www.icann.org/groups/ssac' },
      { label: 'Cloudflare DNSSEC explained', url: 'https://www.cloudflare.com/dns/dnssec/how-dnssec-works/' },
      { label: 'NIST SP 800-81r2 — DNSSEC deployment guidance', url: 'https://csrc.nist.gov/publications/detail/sp/800-81/2/final' },
    ],
  },
  {
    id: 'ipsec', name: 'IPSec / IKEv2', versions: 'RFC 7296 (2014)', category: 'Transport',
    quantumRisk: 'high', harvestRisk: 'critical',
    description: 'IPSec is the standard for network-layer encryption, widely used for VPNs, site-to-site tunnels, and government network security. IKEv2 performs authenticated key exchange to establish IPSec Security Associations (SAs). Both the key exchange (ECDH or DH groups) and the authentication mechanism (RSA certificates or ECDSA) are broken by Shor\'s algorithm. Long-lived IPSec tunnels carry high-value traffic — government communications, financial transactions, and critical infrastructure control data — making them prime targets for harvest-now-decrypt-later.',
    vulnerability: 'IKEv2 Phase 1 exchanges use ECDH or Diffie-Hellman to derive the SKEYSEED master key. Phase 2 derives individual SA keys from SKEYSEED. Both the key derivation tree and the authentication (IKE_AUTH with RSA/ECDSA) are quantum-vulnerable. NSA SUITE-B mandated P-384 and AES-256 for classified traffic — the P-384 component is broken by Shor\'s algorithm.',
    brokenBy: ['ECDH P-256/P-384/P-521 (IKEv2 key exchange)', 'Diffie-Hellman group 14/15/16/17/18', 'RSA authentication certificates', 'ECDSA P-256/P-384 authentication', 'Pre-shared key + RSA hybrid modes'],
    affectedSystems: ['Enterprise VPN gateways (Cisco, Palo Alto, Juniper, Fortinet)', 'Government site-to-site tunnels', 'Cloud VPN connections (AWS, Azure, GCP)', 'Mobile VPN clients (IKEv2 on iOS/Android)', 'MPLS overlay networks', 'Critical infrastructure control plane (ICS/SCADA)'],
    migrationPath: 'RFC 9370 (2023) defines Multiple Key Exchanges (MKE) for IKEv2, enabling PQC hybrid key exchange. StrongSwan 6.0+ implements ML-KEM hybrid IKEv2. Configure KE payloads to include both ECDH and ML-KEM-768. Authentication migration requires CA-issued PQC certificates (ML-DSA). Cisco, Palo Alto, and Juniper are adding RFC 9370 support. For immediate mitigation: enforce PFS (ephemeral DH), reduce SA lifetimes, and use IKEv2 with Group 15+ (3072-bit DH) until PQC is available.',
    migrationStatus: 'RFC 9370 published. StrongSwan 6.0 supports ML-KEM hybrid IKEv2. Major vendor implementations in progress. NSA CNSA 2.0 mandates ML-KEM in IPSec.',
    deployedIn: 'Enterprise VPNs, government networks, site-to-site WAN encryption, mobile VPN, ICS/SCADA',
    urgency: 'HIGH — Long-lived VPN tunnels are prime harvest targets. Implement RFC 9370 hybrid KE immediately where supported. Reduce SA lifetimes as interim mitigation.',
    links: [
      { label: 'RFC 9370 — Multiple Key Exchanges in IKEv2', url: 'https://www.rfc-editor.org/rfc/rfc9370' },
      { label: 'RFC 7296 — IKEv2 specification', url: 'https://www.rfc-editor.org/rfc/rfc7296' },
      { label: 'StrongSwan 6.0.0 release — ML-KEM IKEv2', url: 'https://strongswan.org/blog/2024/12/03/strongswan-6.0.0-released.html' },
      { label: 'NSA CNSA 2.0 — IPSec migration requirements', url: 'https://media.defense.gov/2022/Sep/07/2003071836/-1/-1/0/CSA_CNSA_2.0_ALGORITHMS_.PDF' },
      { label: 'IETF IPSECME working group — PQC drafts', url: 'https://datatracker.ietf.org/wg/ipsecme/documents/' },
    ],
  },
  {
    id: 'pgp', name: 'PGP / OpenPGP', versions: 'RFC 9580 (2024)', category: 'Application',
    quantumRisk: 'critical', harvestRisk: 'critical',
    description: 'OpenPGP is the standard for end-to-end encrypted email, file encryption, and software package signing. PGP keys are typically long-lived (years to decades), making them prime harvest-now-decrypt-later targets. Every email ever encrypted to a recipient\'s PGP key — including those from journalists, lawyers, activists, and government officials — can be retroactively decrypted once the recipient\'s key is broken by a CRQC. The non-repudiation property of PGP signatures is also undermined: signed documents would be forgeable.',
    vulnerability: 'PGP key encryption uses RSA-2048/4096 or ECDH (Curve25519) to encrypt the session key. Long-term PGP keys used for years of correspondence represent a complete archive of sensitive communications that becomes retroactively decryptable. Software packages signed with GPG keys are also affected — compromised signing keys enable supply chain attacks.',
    brokenBy: ['RSA-2048/4096 encryption subkeys', 'ECDH Curve25519 encryption subkeys', 'RSA signing keys', 'ECDSA/EdDSA Ed25519 signing keys', 'Elgamal encryption (legacy)'],
    affectedSystems: ['ProtonMail / Tutanota end-to-end email', 'Linux package signing (Debian, Fedora, Arch GPG keys)', 'Git commit signing (developer identity)', 'Journalist secure communications', 'Whistleblower submission systems', 'Legal document encryption', 'Government secure email'],
    migrationPath: 'RFC 9580 (August 2024 — OpenPGP revision) adds ML-KEM-768 and ML-KEM-1024 as encryption algorithms, and ML-DSA for signatures. Users must generate new PQC-capable keypairs and communicate new key fingerprints to correspondents. Legacy keys should be treated as providing zero long-term confidentiality. GnuPG 2.5+, OpenPGP.js, and GopenPGP are implementing RFC 9580. For high-risk users: consider Signal or Matrix for real-time communications where quantum-safe key ratcheting is already implemented.',
    migrationStatus: 'RFC 9580 published August 2024. GnuPG 2.5 implementing PQC. Thunderbird/Enigmail migration roadmap in progress. High-risk users should transition immediately.',
    deployedIn: 'Linux package repos (apt, dnf, pacman), developer Git signing, encrypted email clients, secure document exchange, journalistic communications',
    urgency: 'CRITICAL — Long-lived PGP keys make this the highest harvest-now-decrypt-later risk. Every email encrypted with a current PGP key is already at risk. Generate new RFC 9580 PQC keys immediately for high-sensitivity communications.',
    links: [
      { label: 'RFC 9580 — OpenPGP (2024, includes ML-KEM)', url: 'https://www.rfc-editor.org/rfc/rfc9580' },
      { label: 'GnuPG PQC implementation status', url: 'https://lists.gnupg.org/pipermail/gnupg-devel/2024-August/035548.html' },
      { label: 'OpenPGP.js — PQC roadmap', url: 'https://github.com/openpgpjs/openpgpjs/issues/1647' },
      { label: 'EFF — Surveillance Self-Defense (key hygiene)', url: 'https://ssd.eff.org/module/key-concepts-encryption' },
      { label: 'CISA — Encrypted communications guidance', url: 'https://www.cisa.gov/secure-our-world' },
    ],
  },
  {
    id: 'smime', name: 'S/MIME', versions: 'RFC 8551 (2019)', category: 'Application',
    quantumRisk: 'critical', harvestRisk: 'critical',
    description: 'S/MIME (Secure/Multipurpose Internet Mail Extensions) secures corporate, government, and healthcare email with encryption and digital signatures. It relies on X.509 certificates for key distribution — the same PKI infrastructure that is quantum-broken. Unlike TLS where PQC hybrid extensions are already deployed, the S/MIME ecosystem (email clients, enterprise gateways, healthcare messaging) lags significantly behind in PQC readiness. Emails encrypted with S/MIME today, especially in regulated industries, represent years of sensitive communications that will become decryptable.',
    vulnerability: 'S/MIME uses CMS (Cryptographic Message Syntax). Encryption uses RSA key transport or ECDH key agreement to wrap the content encryption key. Signing uses RSA or ECDSA. Both the encryption and authentication mechanisms are fully broken by Shor\'s algorithm. Enterprise email servers that archive S/MIME encrypted messages are creating a decryptable archive for adversaries who capture network traffic.',
    brokenBy: ['RSA key transport (id-RSAES-OAEP)', 'ECDH key agreement (id-ecMQV)', 'RSA/ECDSA certificate signing', 'SHA-256 with RSA signatures'],
    affectedSystems: ['Microsoft Outlook / Exchange (S/MIME)', 'Healthcare email (HIPAA-compliant gateways)', 'Legal firm communications', 'Government classified email (where permitted)', 'Financial institution secure messaging', 'DocuSign and digital contract signing', 'SWIFT financial messaging'],
    migrationPath: 'IETF is defining PQC CMS algorithm identifiers — see draft-ietf-lamps-cms-kemri for ML-KEM in CMS, and draft-ietf-lamps-cms-sphincs-plus for SLH-DSA signatures. Certificate Authorities must begin issuing S/MIME certificates with hybrid or PQC algorithms. Microsoft and Apple mail clients need updates to support PQC certificate types. Intermediate mitigation: archive minimization (don\'t retain encrypted email beyond retention policy), switch to PQC-capable messaging platforms (Signal, Matrix) for the most sensitive communications.',
    migrationStatus: 'IETF LAMPS working group drafting PQC CMS profiles. No production S/MIME PQC deployments. Timeline 2026–2028 for CA support.',
    deployedIn: 'Corporate email (Fortune 500, financial), healthcare (hospital networks, insurers), legal firms, government agencies, EU Qualified Electronic Signatures (eIDAS)',
    urgency: 'CRITICAL — Archived S/MIME encrypted email creates a high-value harvest target. Minimize retention of encrypted archives. Migrate high-sensitivity communications to PQC-capable platforms now.',
    links: [
      { label: 'RFC 8551 — S/MIME v4.0 specification', url: 'https://www.rfc-editor.org/rfc/rfc8551' },
      { label: 'IETF draft-ietf-lamps-cms-kemri — ML-KEM in CMS', url: 'https://www.ietf.org/archive/id/draft-ietf-lamps-cms-kemri-07.txt' },
      { label: 'IETF LAMPS WG — PQC email standards', url: 'https://datatracker.ietf.org/wg/lamps/documents/' },
      { label: 'HIPAA guidance on encryption', url: 'https://www.hhs.gov/hipaa/for-professionals/security/guidance/index.html' },
      { label: 'BSI PQC migration guidance for email', url: 'https://www.bsi.bund.de/EN/Themen/Unternehmen-und-Organisationen/Informationen-und-Empfehlungen/Quantencomputer/Migration-PQK/migration-pqk_node.html' },
    ],
  },
  {
    id: 'codesign', name: 'Code Signing', versions: 'Authenticode / APK / Notarization', category: 'Identity',
    quantumRisk: 'critical', harvestRisk: 'low',
    description: 'Code signing is the mechanism by which operating systems, app stores, and package managers verify that software is authentic and untampered. A quantum adversary who can forge code signatures can distribute malware with trusted identities — bypassing every OS-level security control. This is arguably the most dangerous long-term quantum risk: unlike encrypted communications (where the threat is retroactive decryption), broken code signing enables future attacks, not just retroactive ones. A single compromised CA code signing key enables unlimited malware distribution across an entire ecosystem.',
    vulnerability: 'All major code signing infrastructures use RSA-2048/4096 or ECDSA P-256/P-384. Authenticode (Windows), Apple notarization, Android APK signing, and Linux package signing (GPG) all rely on quantum-vulnerable asymmetric cryptography. The Authenticode timestamp authority chain is also quantum-vulnerable. Software signed today with long-lived certificates will need re-signing before quantum computing becomes a threat — or existing signatures must be countersigned with PQC before that window.',
    brokenBy: ['RSA-2048/4096 Authenticode certificates', 'ECDSA P-256 Apple notarization', 'ECDSA P-256/P-384 Android signing keys', 'GPG Ed25519 Linux package signing', 'PKCS#7 / CMS countersignatures', 'Microsoft Trusted Root Program CA keys'],
    affectedSystems: ['Windows software distribution (Authenticode)', 'macOS/iOS app signing (Apple notarization)', 'Android APK signing (Google Play)', 'Linux package managers (apt, dnf, pacman, yum GPG keys)', 'npm, PyPI, RubyGems, Maven package signing', 'Firmware update signing (UEFI Secure Boot)', 'Docker image signing (cosign)', 'Kubernetes admission controllers'],
    migrationPath: 'Microsoft, Apple, and Google are evaluating PQC code signing algorithms. UEFI Forum is developing PQC Secure Boot key formats. Near-term: dual-signing (sign with both RSA/ECDSA and ML-DSA) allows forwards compatibility. Medium-term: OS vendors update trust anchors to include PQC CA roots. Package managers need PQC signature verification support. Critical path: UEFI Secure Boot key rotation to ML-DSA is the most complex migration — requires firmware updates across hundreds of hardware platforms.',
    migrationStatus: 'sigstore/cosign adding ML-DSA support. UEFI Secure Boot PQC working group active. Microsoft and Apple timelines not yet published.',
    deployedIn: 'Every OS (Windows, macOS, Linux, Android, iOS), every app store, every package manager, firmware update systems, Kubernetes clusters',
    urgency: 'CRITICAL — Broken code signing enables malware signed with trusted identities. Unlike encrypted data, this is a future attack risk (not retroactive). Begin cryptographic inventory of all signing certificates and plan dual-signing migration.',
    links: [
      { label: 'Microsoft Authenticode documentation', url: 'https://learn.microsoft.com/en-us/windows/win32/seccrypto/authenticode' },
      { label: 'sigstore — Software supply chain security', url: 'https://www.sigstore.dev/' },
      { label: 'IETF draft-ietf-lamps-cms-sphincs-plus — SLH-DSA code signing', url: 'https://www.ietf.org/archive/id/draft-ietf-lamps-cms-sphincs-plus-07.txt' },
      { label: 'CISA supply chain risk guidance', url: 'https://www.cisa.gov/supply-chain' },
      { label: 'NIST SP 800-161r1 — Cybersecurity Supply Chain Risk', url: 'https://csrc.nist.gov/publications/detail/sp/800-161/rev-1/final' },
      { label: 'UEFI Forum security resources', url: 'https://uefi.org/learning_center/presentations' },
    ],
  },
  {
    id: 'wireguard', name: 'WireGuard', versions: 'RFC 8981 / whitepaper (2017)', category: 'Transport',
    quantumRisk: 'high', harvestRisk: 'high',
    description: 'WireGuard is a modern VPN protocol designed for simplicity and performance. It uses Curve25519 (X25519) for key exchange and ChaCha20-Poly1305 for data encryption. While ChaCha20-Poly1305 is quantum-safe with 256-bit keys (Grover\'s algorithm reduces it to 128-bit security — still acceptable), the X25519 key exchange is fully broken by Shor\'s algorithm. WireGuard\'s handshake — which establishes the session keys — is harvestable today. Additionally, WireGuard\'s static key model means long-lived public keys (wg0.conf persistent keys) are particularly sensitive harvest targets.',
    vulnerability: 'WireGuard\'s Noise Protocol Framework handshake uses X25519 for key exchange (ephemeral-static and ephemeral-ephemeral DH operations). Shor\'s algorithm breaks X25519, allowing recovery of the ephemeral session key material. Unlike TLS 1.3, WireGuard does not yet have a standardized PQC hybrid extension. The static public key in wg0.conf is a persistent harvest target.',
    brokenBy: ['X25519 ephemeral-ephemeral DH', 'X25519 ephemeral-static DH', 'Static public keys (wg0.conf)'],
    affectedSystems: ['Linux kernel WireGuard (built-in since 5.6)', 'macOS/iOS WireGuard clients', 'Android WireGuard app', 'Tailscale (WireGuard-based mesh VPN)', 'Mullvad, ProtonVPN, NordVPN (WireGuard tunnels)', 'Cloudflare WARP', 'Enterprise WireGuard deployments'],
    migrationPath: 'No standardized PQC WireGuard extension exists yet. Research proposals exist (Rosenpass — post-quantum WireGuard) but are not yet production-standard. Rosenpass adds a ML-KEM-based outer handshake around WireGuard\'s existing Noise handshake. For immediate mitigation: combine WireGuard with a PQC-authenticated outer tunnel (e.g., IPSec with RFC 9370 ML-KEM) for defense in depth. Mullvad VPN is running Rosenpass in production as a beta feature.',
    migrationStatus: 'Rosenpass (post-quantum WireGuard) in beta production at Mullvad. No IETF standard yet. WireGuard maintainer evaluating PQC extensions.',
    deployedIn: 'Consumer and enterprise VPNs, Tailscale mesh networks, cloud provider VPN, Linux server tunnels',
    urgency: 'HIGH — WireGuard session establishment is harvestable. Static keys in wg0.conf are long-lived harvest targets. Evaluate Rosenpass for high-security deployments.',
    links: [
      { label: 'WireGuard whitepaper — Noise protocol details', url: 'https://www.wireguard.com/papers/wireguard.pdf' },
      { label: 'Rosenpass — Post-quantum WireGuard', url: 'https://rosenpass.eu/whitepaper.pdf' },
      { label: 'Mullvad — Quantum-resistant VPN tunnels', url: 'https://mullvad.net/en/blog/2023/4/11/we-have-implemented-post-quantum-key-exchange-in-our-wireguard-tunnels' },
      { label: 'RFC 8981 — Noise Protocol Framework', url: 'https://www.rfc-editor.org/rfc/rfc8981' },
    ],
  },
]

// TODO: remove after DB migration verified
const PRIMITIVE_DATA: PrimitiveEntry[] = [

  // ── KEY EXCHANGE: CLASSICAL ───────────────────────────────────────────
  { name: 'RSA-1024 KEM', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Integer Factorization', keySize: '1024 bit',
    quantumSafe: 'broken', classicBits: 80, quantumBits: 0,
    attackVector: "Classically broken since 2010 (768-bit factored). Shor's algorithm provides additional quantum break. No modern deployment should use this.",
    standard: 'PKCS#1 / RFC 8017', recommendation: 'avoid',
    description: 'RSA-1024 is broken by both classical and quantum computers. NIST removed it from approved algorithms in 2010. It remains in some legacy SCADA, embedded, and proprietary systems. Classically breakable with moderate compute resources.' },

  { name: 'RSA-2048 KEM', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Integer Factorization', keySize: '2048 bit',
    quantumSafe: 'broken', classicBits: 112, quantumBits: 0,
    attackVector: "Shor's algorithm factors the RSA modulus in O(log²N·log log N) quantum gate operations. A CRQC breaks RSA-2048 in hours.",
    standard: 'PKCS#1 / RFC 8017', recommendation: 'avoid',
    description: 'The most commonly deployed RSA key size today, used in the majority of TLS certificates. Quantum-safe migration is urgent for any traffic that requires multi-year confidentiality. The classical security of 112 bits is adequate today but provides zero quantum protection.' },

  { name: 'RSA-4096 KEM', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Integer Factorization', keySize: '4096 bit',
    quantumSafe: 'broken', classicBits: 140, quantumBits: 0,
    attackVector: "Shor's algorithm breaks RSA-4096 in ~8× longer than RSA-2048 — a polynomial difference, not exponential. Still broken by a CRQC.",
    standard: 'PKCS#1 / RFC 8017', recommendation: 'avoid',
    description: 'RSA-4096 is sometimes used for high-value CA keys under the mistaken assumption that larger classical keys provide quantum resistance. They do not. Shor\'s algorithm scales polynomially with key size, meaning RSA-4096 is broken roughly 8× slower than RSA-2048 — both are broken by a CRQC.' },

  { name: 'DHE / FFDHE-2048', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Finite Field Discrete Log', keySize: '2048 bit',
    quantumSafe: 'broken', classicBits: 112, quantumBits: 0,
    attackVector: "Shor's algorithm solves the Discrete Logarithm Problem (DLP) over finite fields in polynomial time.",
    standard: 'RFC 7919 (FFDHE)', recommendation: 'avoid',
    description: 'Ephemeral Diffie-Hellman over finite fields (FFDHE) provides forward secrecy. RFC 7919 standardized well-known safe prime groups. All finite field DH variants are broken by Shor\'s DLP solver. TLS 1.3 removed non-ephemeral DH entirely; DHE in TLS 1.2 is still widespread.' },

  { name: 'FFDHE-3072 / FFDHE-4096', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Finite Field Discrete Log', keySize: '3072–4096 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's DLP solver scales polynomially — larger groups delay but do not prevent the quantum attack.",
    standard: 'RFC 7919', recommendation: 'avoid',
    description: 'Larger FFDHE groups (3072/4096/6144/8192 bit) from RFC 7919 provide stronger classical security but identical quantum vulnerability. All Diffie-Hellman over finite fields is broken by Shor\'s algorithm regardless of group size.' },

  { name: 'ECDH P-256 (secp256r1)', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '256 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's algorithm solves ECDLP in O(n³) quantum gate complexity. Private key recoverable from public key.",
    standard: 'NIST FIPS 186-5', recommendation: 'avoid',
    description: 'The most widely used key exchange in TLS 1.3 alongside X25519. NIST P-256 (secp256r1) is a prime-order NIST curve standardized in FIPS 186. Quantum-broken — Shor\'s ECDLP solver yields the private key from any public key. Used in billions of active TLS connections.' },

  { name: 'ECDH P-384 (secp384r1)', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '384 bit',
    quantumSafe: 'broken', classicBits: 192, quantumBits: 0,
    attackVector: "Shor's ECDLP solver breaks P-384 as readily as P-256. Larger curves provide no quantum resistance.",
    standard: 'NIST FIPS 186-5', recommendation: 'avoid',
    description: 'P-384 is the default for Suite B NSA cryptography and is used in US government TLS deployments. It offers stronger classical security than P-256 but is equally broken by Shor\'s algorithm. All elliptic curves over prime fields share the same quantum vulnerability.' },

  { name: 'ECDH P-521 (secp521r1)', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '521 bit',
    quantumSafe: 'broken', classicBits: 260, quantumBits: 0,
    attackVector: "Shor's algorithm is curve-agnostic — P-521 broken in slightly more quantum gates than P-256 but still polynomial time.",
    standard: 'NIST FIPS 186-5', recommendation: 'avoid',
    description: 'The largest NIST standardized curve, used for the highest-classification NSA Suite B cryptography. Despite its formidable classical security (260 bits), P-521 is fully broken by Shor\'s ECDLP algorithm. Rarely deployed in TLS due to performance overhead; irrelevant from a quantum perspective.' },

  { name: 'X25519 (Curve25519)', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '255 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's algorithm solves the ECDLP on Curve25519 equally to any other elliptic curve. The curve's classical benefits (speed, side-channel resistance) do not help against quantum attacks.",
    standard: 'RFC 7748', recommendation: 'avoid',
    description: 'X25519 is the modern best-practice ECDH key exchange — constant-time, fast, resistant to classical implementation attacks. It is the default in TLS 1.3, SSH, WireGuard, and Signal. Despite its modern design, X25519 offers zero quantum resistance. A site advertising X25519 without ML-KEM hybrid is quantum-vulnerable.' },

  { name: 'X448 (Curve448)', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '448 bit',
    quantumSafe: 'broken', classicBits: 224, quantumBits: 0,
    attackVector: "Shor's ECDLP algorithm breaks Curve448 as readily as any other curve. 448-bit key is broken in O(n³) quantum operations.",
    standard: 'RFC 7748', recommendation: 'avoid',
    description: 'Curve448 (also called "Goldilocks") is a high-security alternative to Curve25519 standardized in RFC 7748. It targets 224-bit classical security. Recommended for higher-security TLS deployments and long-lived keys, but provides no quantum resistance. Rarely deployed in TLS.' },

  { name: 'secp256k1', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '256 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's ECDLP attack applies equally. This curve is used in Bitcoin and Ethereum — its quantum vulnerability threatens the entire blockchain ecosystem.",
    standard: 'SEC 2 / Bitcoin/Ethereum', recommendation: 'avoid',
    description: 'secp256k1 is a Koblitz curve used by Bitcoin, Ethereum, and most cryptocurrency systems for both key exchange and signing. Not commonly used in TLS but critical to understand — a CRQC would allow derivation of private keys from all public keys in blockchain transaction history, enabling theft of any dormant wallet.' },

  { name: 'brainpoolP256r1 / brainpoolP384r1', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '256–384 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's ECDLP solver applies to all prime-order elliptic curves regardless of generation method.",
    standard: 'BSI / RFC 5639', recommendation: 'avoid',
    description: 'Brainpool curves are deterministically generated curves defined by BSI (German Federal Office for Information Security). They are mandated in some EU government TLS deployments (BSI TR-02102). Despite conservative design philosophy, they are equally broken by Shor\'s algorithm. Used in German government systems and some banking PKI.' },

  { name: 'SM2 Key Exchange', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '256 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's ECDLP attack breaks SM2 identically to other 256-bit curves. China's CFCA and OSCCA have recognized this and initiated PQC standardization.",
    standard: 'GM/T 0003 / ISO 14888-3', recommendation: 'avoid',
    description: 'SM2 is the Chinese national standard elliptic curve algorithm (replacing RSA/ECDH in Chinese government and banking systems). While a well-designed curve with 256-bit security, it is fully broken by Shor\'s algorithm. China\'s OSCCA (Office of State Commercial Cryptography Administration) is actively standardizing post-quantum replacements.' },

  { name: 'GOST R 34.10-2012', category: 'kx', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '256–512 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's ECDLP algorithm breaks GOST curves as readily as any other elliptic curve DLP instance.",
    standard: 'GOST R 34.10-2012 / RFC 7091', recommendation: 'avoid',
    description: 'Russian national digital signature and key exchange standard. Used in Russian government, financial, and e-government systems. The 512-bit variant ("Streebog" family) provides higher classical security but identical quantum vulnerability. Russia is developing quantum-resistant versions under GOST protocols.' },

  // ── KEY EXCHANGE: HYBRID PQC ─────────────────────────────────────────
  { name: 'X25519Kyber768Draft00', category: 'kx', subcategory: 'Hybrid PQC — Transitional',
    type: 'Hybrid / Pre-standard Kyber + X25519', keySize: '255 bit + Kyber768',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 164,
    attackVector: 'Pre-standard hybrid using draft Kyber before NIST finalization. Deployed by Chrome 116–123 and Cloudflare. Superseded by X25519MLKEM768.',
    standard: 'IETF draft (expired) / Chrome legacy', recommendation: 'conditional',
    description: 'The original hybrid PQC key exchange deployed by Chrome and Cloudflare starting in 2023, using a pre-standardization draft of Kyber. It has been superseded by X25519MLKEM768 (using final NIST FIPS 203 ML-KEM). Systems still seeing this in traffic are running Chrome 116–123 or older Cloudflare configurations. Data seen in our scan as "X25519Kyber768Draft00" is from clients that have not yet updated.' },

  { name: 'X25519MLKEM768', category: 'kx', subcategory: 'Hybrid PQC — Transitional',
    type: 'Hybrid PQC / ML-KEM-768 + X25519', keySize: '255 bit + 1184 byte',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 192,
    attackVector: 'Attacker must simultaneously break X25519 (Shor) AND ML-KEM-768 (no known quantum algorithm). Combined security is as strong as the stronger component.',
    standard: 'IETF RFC 9539 / deployed Chrome 124+, Firefox 128+', recommendation: 'use',
    description: 'The current IETF-standardized hybrid key exchange for TLS 1.3 (RFC 9539). Combines X25519 ECDH with ML-KEM-768 KEM — the combined shared secret requires breaking both. This is the PQC key exchange you want to see in production today. Deployed by Chrome, Firefox, Cloudflare, AWS, and major cloud providers.' },

  { name: 'SecP256r1MLKEM768', category: 'kx', subcategory: 'Hybrid PQC — Transitional',
    type: 'Hybrid PQC / ML-KEM-768 + P-256', keySize: '256 bit + 1184 byte',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 192,
    attackVector: 'Requires breaking both ECDH P-256 and ML-KEM-768. Classical component weaker than X25519MLKEM768 in implementation resistance but equivalent mathematical security.',
    standard: 'IETF RFC 9539', recommendation: 'use',
    description: 'Alternative hybrid using NIST P-256 instead of X25519 for the classical component. Useful in deployments where P-256 is mandated by compliance requirements (FIPS 140-3, Suite B). Available in some TLS libraries as an alternative to X25519MLKEM768. RFC 9539 standardizes both variants.' },

  { name: 'X448MLKEM1024', category: 'kx', subcategory: 'Hybrid PQC — Transitional',
    type: 'Hybrid PQC / ML-KEM-1024 + X448', keySize: '448 bit + 1568 byte',
    quantumSafe: 'safe', classicBits: 224, quantumBits: 232,
    attackVector: 'Highest-security hybrid — requires breaking both X448 (224-bit classical) and ML-KEM-1024 (232-bit quantum). Suitable for TOP SECRET / government traffic.',
    standard: 'IETF RFC 9539', recommendation: 'use',
    description: 'The highest-security hybrid key exchange in RFC 9539. Intended for government and intelligence community use where multi-decade confidentiality is required. Significantly larger key material than X25519MLKEM768. Rarely deployed in consumer-facing HTTPS due to overhead, but mandated in some classified system contexts.' },

  // ── KEY EXCHANGE: PQC STANDARDIZED ──────────────────────────────────
  { name: 'ML-KEM-512 (Kyber512)', category: 'kx', subcategory: 'PQC Standardized — NIST FIPS 203',
    type: 'PQC / Module Lattice (CRYSTALS)', keySize: '800 byte public key',
    quantumSafe: 'safe', classicBits: 178, quantumBits: 100,
    attackVector: 'MLWE problem. Best known attack requires ~2^100 quantum operations. Slightly below NIST Level 1 preference but acceptable for constrained environments.',
    standard: 'NIST FIPS 203 (2024)', recommendation: 'conditional',
    description: 'The smallest ML-KEM variant, targeting NIST security level 1 (~AES-128 equivalent). Suitable for IoT devices, embedded systems, and constrained environments where key/ciphertext sizes must be minimized. Marginally below recommended quantum security threshold; prefer ML-KEM-768 when bandwidth permits.' },

  { name: 'ML-KEM-768 (Kyber768)', category: 'kx', subcategory: 'PQC Standardized — NIST FIPS 203',
    type: 'PQC / Module Lattice (CRYSTALS)', keySize: '1184 byte public key',
    quantumSafe: 'safe', classicBits: 178, quantumBits: 164,
    attackVector: 'MLWE security level 3. Best known quantum attack is BKZ lattice sieving at approximately 2^164 operations. No polynomial-time quantum algorithm known.',
    standard: 'NIST FIPS 203 (2024)', recommendation: 'use',
    description: 'The primary NIST recommended security level for most applications. ML-KEM-768 is the backbone of deployed PQC today — it is used in X25519MLKEM768, the default hybrid in TLS 1.3. Based on CRYSTALS-Kyber with 7 years of public cryptanalysis. The goto choice for web servers, APIs, and enterprise TLS.' },

  { name: 'ML-KEM-1024 (Kyber1024)', category: 'kx', subcategory: 'PQC Standardized — NIST FIPS 203',
    type: 'PQC / Module Lattice (CRYSTALS)', keySize: '1568 byte public key',
    quantumSafe: 'safe', classicBits: 257, quantumBits: 232,
    attackVector: 'MLWE security level 5. Equivalent to ~AES-256. Requires ~2^232 quantum operations with best known attack. Margin of safety exceeds any foreseeable quantum hardware.',
    standard: 'NIST FIPS 203 (2024)', recommendation: 'use',
    description: 'The highest ML-KEM security level, targeting NIST level 5 (~AES-256 equivalent quantum security). Recommended for data requiring 30+ year confidentiality: government secrets, financial records, medical data, and critical infrastructure. Slightly larger ciphertexts but negligible performance impact at scale.' },

  // ── KEY EXCHANGE: PQC CANDIDATES / ALTERNATES ────────────────────────
  { name: 'NTRU-HPS (ntruhrss701)', category: 'kx', subcategory: 'PQC Candidates — NIST Alternates',
    type: 'PQC / NTRU Lattice', keySize: '1138 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'Based on the NTRU hard problem (Ring-LWE variant). Known since 1998 — extensive cryptanalysis. Best attacks require superpolynomial quantum time.',
    standard: 'NIST Round 3 Alternate (not standardized)', recommendation: 'conditional',
    description: 'NTRU is one of the oldest post-quantum candidates, published in 1996. NTRU-HPS (Hoffstein-Pipher-Silverman) was a NIST Round 3 alternate finalist. Despite not being standardized, it has the most extensive cryptanalysis history of any PQC system. Used as a conservative fallback in implementations where ML-KEM trust is uncertain.' },

  { name: 'NTRU Prime (sntrup761)', category: 'kx', subcategory: 'PQC Candidates — NIST Alternates',
    type: 'PQC / Streamlined NTRU Prime', keySize: '1158 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'NTRU over small primes. Avoids algebraic structure exploitable in Ring-LWE. sntrup761 deployed by OpenSSH 9.0 as hybrid with X25519.',
    standard: 'OpenSSH 9.0 / IETF draft', recommendation: 'conditional',
    description: 'Streamlined NTRU Prime was developed by Daniel Bernstein et al. as a conservative NTRU variant. The sntrup761 parameter set is deployed in OpenSSH 9.0 as part of "sntrup761x25519-sha512" — the default key exchange for SSH when available. Avoids number-theoretic transform (NTT) for additional algebraic-attack resistance.' },

  { name: 'FrodoKEM-640 / FrodoKEM-976', category: 'kx', subcategory: 'PQC Candidates — NIST Alternates',
    type: 'PQC / Plain LWE (Conservative Lattice)', keySize: '9616 byte public key (640)',
    quantumSafe: 'safe', classicBits: 103, quantumBits: 103,
    attackVector: 'Based on plain Learning With Errors (LWE) — the most well-studied hard lattice problem. No module structure to attack. Slower and larger than ML-KEM but maximally conservative.',
    standard: 'Microsoft Research / NIST Round 3 Alternate', recommendation: 'conditional',
    description: 'FrodoKEM avoids the module and ring structure of ML-KEM, basing security directly on unstructured LWE. This conservative choice eliminates potential algebraic attack surfaces that module lattices might have. Used internally by Microsoft. The tradeoff: public keys are ~9KB vs 1.2KB for ML-KEM-768, making it impractical for TLS but suitable for high-assurance key establishment.' },

  { name: 'BIKE (Round 4)', category: 'kx', subcategory: 'PQC Candidates — NIST Round 4',
    type: 'PQC / Code-Based (QC-MDPC)', keySize: '1541 byte public key (L1)',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'Based on hardness of decoding quasi-cyclic moderate-density parity-check (QC-MDPC) codes. Different mathematical foundation from lattice-based systems.',
    standard: 'NIST Round 4 (in evaluation)', recommendation: 'conditional',
    description: 'BIKE (Bit Flipping Key Encapsulation) is a code-based KEM advancing through NIST Round 4. It provides diversification from lattice-based schemes — if a breakthrough attack on ML-KEM were found, BIKE would remain secure. Compact keys and ciphertexts. Ongoing cryptanalysis of QC-MDPC decoding; no breaks found. Being considered for NIST standardization as a backup algorithm.' },

  { name: 'HQC (Hamming Quasi-Cyclic)', category: 'kx', subcategory: 'PQC Candidates — NIST Round 4',
    type: 'PQC / Code-Based (QC-LRPC)', keySize: '2249 byte public key (L1)',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'Based on the hardness of the Hamming metric quasi-cyclic code decoding problem. Different from BIKE; uses LRPC codes with randomized decoding.',
    standard: 'NIST Round 4 (in evaluation)', recommendation: 'conditional',
    description: 'HQC (Hamming Quasi-Cyclic) is a code-based KEM competing alongside BIKE in NIST Round 4. Based on the learning parity with noise (LPN) problem in the quasi-cyclic framework. Slightly larger keys than BIKE but with different security assumptions, providing complementary protection in mixed deployments. Strong candidate for eventual standardization.' },

  { name: 'Classic McEliece', category: 'kx', subcategory: 'PQC Candidates — NIST Round 4',
    type: 'PQC / Code-Based (Goppa Codes)', keySize: '261120 byte public key (348864)',
    quantumSafe: 'safe', classicBits: 255, quantumBits: 255,
    attackVector: 'Based on the hardness of Goppa code decoding — an NP-complete problem studied since 1978. No known quantum speedup beyond generic search. Most conservative security assumptions of all PQC KEMs.',
    standard: 'NIST Round 4 (in evaluation)', recommendation: 'conditional',
    description: 'Classic McEliece is the most mature post-quantum KEM (proposed by McEliece in 1978). Its security is exceptionally well-studied with no meaningful breaks in 45+ years. The fatal tradeoff: public keys are 261KB–1MB, making it impractical for most network protocols. Ideal for long-lived key pairs where bandwidth is not constrained — offline key agreement, firmware signing, or pre-positioning keys for later use.' },

  { name: 'SABER (Round 3 — Withdrawn)', category: 'kx', subcategory: 'PQC Historical — NIST Candidates',
    type: 'PQC / Module Lattice (LWR)', keySize: '992 byte public key',
    quantumSafe: 'safe', classicBits: 180, quantumBits: 172,
    attackVector: 'Based on Module Learning With Rounding (MLWR). Security similar to ML-KEM. Withdrawn as team conceded to CRYSTALS-Kyber\'s selection.',
    standard: 'NIST Round 3 Finalist (withdrew)', recommendation: 'conditional',
    description: 'SABER was a NIST Round 3 finalist alongside CRYSTALS-Kyber. It uses Learning With Rounding (LWR) instead of Learning With Errors (LWE), replacing probabilistic noise with deterministic rounding. The SABER team withdrew in favor of Kyber after NIST\'s selection. Available in some implementations; shares similar security properties to ML-KEM.' },

  { name: 'NewHope (Round 1 — Withdrawn)', category: 'kx', subcategory: 'PQC Historical — NIST Candidates',
    type: 'PQC / Ring Lattice (RLWE)', keySize: '1824 byte public key',
    quantumSafe: 'safe', classicBits: 255, quantumBits: 233,
    attackVector: 'Based on Ring Learning With Errors (RLWE). Stronger algebraic structure than module lattices — potential for structured attacks not present in ML-KEM.',
    standard: 'NIST Round 1 (not advanced)', recommendation: 'conditional',
    description: 'NewHope was an early and influential RLWE-based KEM deployed experimentally by Google in Chrome in 2016 — the first real-world PQC deployment in TLS. It was not advanced in NIST Round 2 due to concerns about its ring structure. The experience informed the development of CRYSTALS-Kyber and its transition to module lattices.' },

  // ── KEY EXCHANGE: EXPERIMENTAL / BROKEN ──────────────────────────────
  { name: 'SIDH / SIKE ⚠ BROKEN', category: 'kx', subcategory: 'Experimental — Broken / Withdrawn',
    type: 'PQC / Supersingular Isogeny (BROKEN)', keySize: '564 byte public key (L1)',
    quantumSafe: 'broken', classicBits: 0, quantumBits: 0,
    attackVector: 'COMPLETELY BROKEN classically (not even quantum required). Castryck-Decru attack (July 2022) recovers private keys in 1 hour on a laptop using auxiliary point information. NIST immediately withdrew SIKE.',
    standard: 'NIST Round 4 — WITHDRAWN (July 2022)', recommendation: 'avoid',
    description: 'SIDH (Supersingular Isogeny Diffie-Hellman) and its KEM variant SIKE were considered among the most promising PQC candidates due to exceptionally small key sizes. In July 2022, Castryck and Decru published an attack that completely breaks SIDH using classical computing — no quantum computer required. The attack exploits the auxiliary point information exposed in the SIDH handshake. SIKE was withdrawn from NIST Round 4 within days. A cautionary tale about premature deployment.' },

  { name: 'CSIDH (Experimental)', category: 'kx', subcategory: 'Experimental — Broken / Withdrawn',
    type: 'PQC / Commutative Isogeny (Experimental)', keySize: '64 byte public key',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Subexponential quantum attack (Kuperberg's algorithm) reduces security significantly. CSIDH-512 may offer only ~64-bit quantum security, below recommended levels.",
    standard: 'Academic / Not standardized', recommendation: 'conditional',
    description: 'CSIDH uses commutative supersingular isogenies, avoiding the auxiliary-point structure that broke SIDH. It was NOT broken by the Castryck-Decru attack. Key sizes are extremely compact (64 bytes), which is attractive. However, Kuperberg\'s quantum algorithm provides a subexponential attack — CSIDH-512 likely offers less quantum security than originally claimed. Under active academic research; not recommended for production until parameter sets are re-evaluated.' },

  { name: 'CRYSTALS-Kyber (Pre-standard)', category: 'kx', subcategory: 'Experimental — Broken / Withdrawn',
    type: 'PQC / Module Lattice (pre-FIPS)', keySize: 'Various',
    quantumSafe: 'safe', classicBits: 178, quantumBits: 164,
    attackVector: 'Pre-standardization Kyber used slightly different parameters than FIPS 203 ML-KEM. Implementations using old Kyber APIs are NOT interoperable with ML-KEM.',
    standard: 'Deprecated — use NIST FIPS 203 ML-KEM', recommendation: 'conditional',
    description: 'CRYSTALS-Kyber was the name used before NIST finalization as FIPS 203 ML-KEM. Code and libraries using "kyber512/768/1024" (old API) are NOT the same as ML-KEM-512/768/1024 — minor parameter changes were made during standardization. Library versions should be audited to ensure they use the FIPS 203-compliant implementation, not the pre-standard draft.' },

  // ── CIPHER SUITES: RECOMMENDED AEAD ─────────────────────────────────
  { name: 'AES-256-GCM', category: 'cipher', subcategory: 'Recommended — AEAD',
    type: 'Symmetric AEAD / Galois Counter Mode', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "Grover's algorithm reduces effective security to ~128 bits. NIST-recommended post-quantum threshold. AES-GCM nonce reuse catastrophically breaks authentication — use only with 96-bit random nonces.",
    standard: 'NIST FIPS 197 + SP 800-38D', recommendation: 'use',
    description: 'The primary recommended symmetric cipher for post-quantum TLS. AES-256 with Galois Counter Mode provides both confidentiality and authentication in a single pass. 128-bit quantum security meets NIST post-quantum requirements. The dominant cipher in TLS 1.3. Hardware acceleration via AES-NI ensures performance parity with weaker alternatives.' },

  { name: 'ChaCha20-Poly1305', category: 'cipher', subcategory: 'Recommended — AEAD',
    type: 'Symmetric AEAD / Stream + Poly1305 MAC', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "Grover's speedup reduces to ~128-bit quantum security. Constant-time design prevents timing attacks. No known structural weaknesses.",
    standard: 'RFC 8439', recommendation: 'use',
    description: 'ChaCha20-Poly1305 is the preferred cipher on platforms without AES hardware acceleration (mobile, IoT, ARM). Constant-time by design — immune to cache-timing attacks that affect AES implementations. 256-bit key provides 128-bit post-quantum security. Equal recommendation alongside AES-256-GCM in TLS 1.3.' },

  { name: 'AES-256-CCM', category: 'cipher', subcategory: 'Recommended — AEAD',
    type: 'Symmetric AEAD / Counter with CBC-MAC', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "Grover's reduces to ~128-bit quantum security. CCM is serial (not parallelizable like GCM), limiting throughput.",
    standard: 'NIST SP 800-38C / RFC 6655', recommendation: 'use',
    description: 'AES-256-CCM is an AEAD mode combining CTR encryption with CBC-MAC authentication. Used in TLS 1.3 cipher suite TLS_AES_256_CCM_SHA384 and in IEEE 802.15.4 (Zigbee/6LoWPAN) for IoT. Less common than GCM; CCM is not parallelizable which limits throughput. Suitable for constrained environments where AES-GCM is unavailable.' },

  { name: 'AEGIS-256', category: 'cipher', subcategory: 'Recommended — AEAD',
    type: 'Symmetric AEAD / AES-based (Experimental)', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "Grover's reduces to ~128-bit quantum security. Higher security margin than AES-GCM due to non-linear AES-round based mixing. Requires AES-NI for competitive performance.",
    standard: 'IETF draft-irtf-cfrg-aegis-aead / RFC candidate', recommendation: 'use',
    description: 'AEGIS-256 is a high-performance AEAD cipher designed for maximum throughput on AES-NI-enabled hardware. It achieves significantly higher throughput than AES-256-GCM and provides better security margins against multi-key attacks. The IETF CFRG is standardizing AEGIS. Deployed experimentally by Cloudflare. Becoming available in OpenSSL 3.x and libsodium.' },

  { name: 'AEGIS-128L', category: 'cipher', subcategory: 'Recommended — AEAD',
    type: 'Symmetric AEAD / AES-based (Experimental)', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Grover's reduces effective security to ~64-bit quantum. Very high performance but insufficient quantum security for long-lived data.",
    standard: 'IETF draft-irtf-cfrg-aegis-aead', recommendation: 'conditional',
    description: 'AEGIS-128L is the 128-bit key variant of AEGIS, optimized for maximum throughput even above AEGIS-256. Excellent for short-lived session keys where performance is critical. Below NIST post-quantum security recommendations due to Grover\'s speedup — prefer AEGIS-256 for any data requiring multi-year confidentiality.' },

  { name: 'XChaCha20-Poly1305', category: 'cipher', subcategory: 'Recommended — AEAD',
    type: 'Symmetric AEAD / Extended-Nonce Stream', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "Grover's reduces to ~128-bit. Extended 192-bit nonce eliminates nonce-reuse risk from short-nonce variants. No structural weaknesses found.",
    standard: 'libsodium / WireGuard / Not IETF standardized', recommendation: 'use',
    description: 'XChaCha20-Poly1305 extends the ChaCha20 nonce from 96 bits to 192 bits using the HChaCha20 construction. This makes random nonce generation safe (no birthday-bound collision risk even at high message volumes). Used by WireGuard, libsodium, and age (file encryption). Not yet an IETF RFC but widely deployed. Eliminates a common implementation pitfall of nonce reuse.' },

  { name: 'Deoxys-II-256', category: 'cipher', subcategory: 'Recommended — AEAD',
    type: 'Symmetric AEAD / Tweakable AES (Nonce-Misuse Resistant)', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "Grover's gives ~128-bit quantum security. Nonce-misuse resistant: even if the same nonce is used twice, only reveals that two plaintexts are identical — catastrophic GCM failure does not apply.",
    standard: 'CAESAR Competition Winner (Niche Track)', recommendation: 'conditional',
    description: 'Deoxys-II won the CAESAR cryptographic competition for nonce-misuse resistance. Unlike AES-GCM (where nonce reuse completely breaks security), Deoxys-II degrades gracefully. Based on tweakable AES. Useful for systems where nonce management is unreliable. Not yet widely deployed in TLS but present in some VPN and file encryption systems.' },

  // ── CIPHER SUITES: ACCEPTABLE AEAD ──────────────────────────────────
  { name: 'AES-128-GCM', category: 'cipher', subcategory: 'Acceptable — Below Post-Quantum Threshold',
    type: 'Symmetric AEAD / Galois Counter Mode', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Grover's algorithm reduces effective quantum security to ~64 bits — below NIST post-quantum recommendations. Acceptable for short-lived sessions; avoid for data requiring multi-year confidentiality.",
    standard: 'NIST FIPS 197 + SP 800-38D', recommendation: 'conditional',
    description: 'AES-128-GCM is the most widely deployed AEAD cipher in TLS 1.3. While quantum-weakened (Grover\'s halves key strength to ~64-bit quantum security), the practical risk for ephemeral TLS sessions is low. The main concern is traffic being harvested today for future quantum decryption. NIST recommends transitioning to AES-256-GCM for post-quantum compliance.' },

  { name: 'AES-128-CCM', category: 'cipher', subcategory: 'Acceptable — Below Post-Quantum Threshold',
    type: 'Symmetric AEAD / Counter with CBC-MAC', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Grover's reduces to ~64-bit quantum security. CBC-MAC serial authentication limits throughput. Same concerns as AES-128-GCM.",
    standard: 'NIST SP 800-38C / RFC 6655', recommendation: 'conditional',
    description: 'AES-128-CCM is the TLS cipher suite TLS_AES_128_CCM_SHA256. Used in constrained IoT environments where 256-bit key performance is unacceptable. The 128-bit key is quantum-weakened; sensor data captured today could be decrypted later. Prefer AES-256-CCM when device constraints allow.' },

  { name: 'AES-128-CCM-8', category: 'cipher', subcategory: 'Acceptable — Below Post-Quantum Threshold',
    type: 'Symmetric AEAD / CCM with 8-byte MAC', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 96, quantumBits: 48,
    attackVector: "Shortened 8-byte authentication tag reduces integrity protection to 64-bit classically. Grover's reduces confidentiality to ~64-bit quantum. Truncated MAC creates forgery opportunities.",
    standard: 'RFC 6655 / TLS_AES_128_CCM_8_SHA256', recommendation: 'conditional',
    description: 'AES-128-CCM-8 uses a truncated 8-byte (64-bit) authentication tag for bandwidth efficiency in IoT (IEEE 802.15.4, Thread, Zigbee). The shortened MAC reduces authentication strength to 64-bit — adversaries have a 2^-64 forgery probability per message. Only appropriate for low-value sensor data and strictly constrained devices.' },

  // ── CIPHER SUITES: REGIONAL STANDARDS ────────────────────────────────
  { name: 'Camellia-256-GCM', category: 'cipher', subcategory: 'Regional Standards',
    type: 'Symmetric AEAD / Camellia + GCM', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "Grover's reduces to ~128-bit quantum security. Camellia is cryptographically equivalent to AES. No known weaknesses beyond generic symmetric attacks.",
    standard: 'RFC 6367 / NTT / Mitsubishi Electric', recommendation: 'use',
    description: 'Camellia is a symmetric cipher co-developed by NTT and Mitsubishi, standardized by ISO/IEC 18033-3, NESSIE, and the Japanese government. Camellia-256-GCM is supported in TLS 1.2/1.3 cipher suites. Cryptographically equivalent to AES-256-GCM — same security level, same block size. Provides algorithm diversity: if AES were broken, Camellia deployments would be unaffected.' },

  { name: 'Camellia-128-GCM', category: 'cipher', subcategory: 'Regional Standards',
    type: 'Symmetric AEAD / Camellia + GCM', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Grover's halves to ~64-bit quantum security. Same quantum weakness as AES-128-GCM. Acceptable for short-lived sessions.",
    standard: 'RFC 6367', recommendation: 'conditional',
    description: 'The 128-bit key variant of Camellia-GCM. Used in some Japanese government and enterprise systems. Quantum-weakened by Grover\'s algorithm identically to AES-128-GCM. Prefer Camellia-256-GCM for post-quantum environments.' },

  { name: 'ARIA-256-GCM', category: 'cipher', subcategory: 'Regional Standards',
    type: 'Symmetric AEAD / ARIA + GCM', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "Grover's reduces to ~128-bit quantum security. ARIA has similar structure to AES; no known breaks beyond generic attacks.",
    standard: 'Korean KISA standard / RFC 6209', recommendation: 'use',
    description: 'ARIA is the South Korean national encryption standard (KISA, 2004), designed with AES-like structure. Mandated in Korean government and financial systems. ARIA-256-GCM in TLS provides equivalent security to AES-256-GCM. South Korea\'s KISA is actively developing PQC-compliant cipher profiles.' },

  { name: 'ARIA-128-GCM', category: 'cipher', subcategory: 'Regional Standards',
    type: 'Symmetric AEAD / ARIA + GCM', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Grover's reduces to ~64-bit quantum security. Equivalent weakness to AES-128-GCM.",
    standard: 'RFC 6209', recommendation: 'conditional',
    description: 'The 128-bit ARIA variant. Quantum-weakened to ~64-bit security by Grover\'s algorithm. Common in Korean systems where 256-bit performance is constrained. Upgrade path is ARIA-256-GCM.' },

  { name: 'SM4-GCM', category: 'cipher', subcategory: 'Regional Standards',
    type: 'Symmetric AEAD / SM4 + GCM', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Grover's reduces SM4-128 to ~64-bit quantum security. SM4 supports only 128-bit keys — no 256-bit variant exists, creating a fundamental post-quantum limitation.",
    standard: 'GM/T 0001 / ISO 18033-3 / RFC 8998', recommendation: 'conditional',
    description: 'SM4 is the Chinese national block cipher standard, mandated in WAPI (Chinese WiFi) and GB protocols. Used in TLS cipher suites for Chinese government systems (RFC 8998). SM4 only supports 128-bit keys — no 256-bit variant exists, meaning it cannot achieve NIST post-quantum security recommendations. Chinese OSCCA is working on SM4 successor algorithms.' },

  { name: 'SM4-CCM', category: 'cipher', subcategory: 'Regional Standards',
    type: 'Symmetric AEAD / SM4 + CCM', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Grover's reduces to ~64-bit quantum security. Same fundamental key-length limitation as SM4-GCM.",
    standard: 'RFC 8998 / GM/T protocols', recommendation: 'conditional',
    description: 'SM4-CCM is the CCM mode variant of the Chinese SM4 cipher. Used in Chinese IoT and embedded devices. Shares the 128-bit key-length limitation of all SM4 modes. For systems requiring Chinese national cryptographic compliance, OSCCA PQC standardization efforts should be monitored.' },

  { name: 'SEED-CBC', category: 'cipher', subcategory: 'Regional Standards',
    type: 'Symmetric Block / SEED + CBC', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "Grover's reduces to ~64-bit quantum security. CBC mode adds classical vulnerabilities (BEAST, padding oracle). SEED has no AEAD mode, limiting its viability.",
    standard: 'RFC 4162 / Korean KISA (legacy)', recommendation: 'avoid',
    description: 'SEED is a legacy Korean block cipher (1998) standardized for use in Korean financial and government systems. It uses CBC mode in TLS — no AEAD variant exists. Korea has largely transitioned to ARIA. SEED in CBC mode inherits all CBC vulnerabilities in addition to quantum weakening. Systems still running SEED-CBC should migrate to ARIA-256-GCM.' },

  // ── CIPHER SUITES: LEGACY / AVOID ────────────────────────────────────
  { name: 'AES-256-CBC', category: 'cipher', subcategory: 'Legacy — Avoid (Classical Weaknesses)',
    type: 'Symmetric Block / CBC Mode', keySize: '256 bit',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: "While 256-bit key provides ~128-bit quantum security, CBC mode is vulnerable to padding oracle attacks (LUCKY13), BEAST (TLS 1.0), and lacks authentication without separate MAC. Not a quantum concern — a classical implementation concern.",
    standard: 'NIST FIPS 197', recommendation: 'avoid',
    description: 'AES-256-CBC has sufficient key length for post-quantum environments but fails on implementation security. CBC mode in TLS has been the source of BEAST (TLS 1.0), POODLE (SSL 3.0), and LUCKY13 attacks. Without AEAD, confidentiality and integrity are handled separately, creating MAC-then-encrypt vulnerabilities. Disabled in TLS 1.3. All TLS 1.2 servers should disable CBC cipher suites.' },

  { name: 'AES-128-CBC', category: 'cipher', subcategory: 'Legacy — Avoid (Classical Weaknesses)',
    type: 'Symmetric Block / CBC Mode', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "CBC mode classical vulnerabilities (BEAST, LUCKY13, padding oracle) PLUS Grover's quantum weakening to ~64-bit security. Doubly problematic.",
    standard: 'NIST FIPS 197', recommendation: 'avoid',
    description: 'AES-128-CBC combines the weaknesses of 128-bit key quantum vulnerability with CBC mode\'s classical attack surface. Found in TLS 1.2 cipher suites like TLS_ECDHE_RSA_WITH_AES_128_CBC_SHA256. Should be disabled immediately in any TLS 1.2 configuration. TLS 1.3 removed CBC mode entirely.' },

  { name: 'RC4-128', category: 'cipher', subcategory: 'Broken — Prohibit Immediately',
    type: 'Symmetric Stream / RC4', keySize: '128 bit',
    quantumSafe: 'broken', classicBits: 0, quantumBits: 0,
    attackVector: "RC4 is classically broken: statistical biases in the keystream enable practical plaintext recovery (RC4NOMORE, 2015). Breaking a session takes ~75 hours of monitoring. Prohibited by RFC 7465 (2015). Quantum irrelevant.",
    standard: 'RFC 7465 (PROHIBITED)', recommendation: 'avoid',
    description: 'RC4 (Rivest Cipher 4) was the dominant stream cipher in SSL/TLS for decades. It is classically broken — multiple attacks (Fluhrer-Mantin-Shamir, RC4NOMORE) exploit biases in the RC4 keystream. RFC 7465 explicitly prohibits RC4 in TLS. Any system still negotiating RC4 has a critical vulnerability entirely independent of quantum computing.' },

  { name: '3DES / TDEA', category: 'cipher', subcategory: 'Broken — Prohibit Immediately',
    type: 'Symmetric Block / Triple-DES', keySize: '112 bit effective',
    quantumSafe: 'broken', classicBits: 56, quantumBits: 0,
    attackVector: "SWEET32 birthday attack (2016): 4GB of ciphertext enables session decryption. Meet-in-the-middle limits effective security to ~112 bits classically. Grover's reduces to ~56 bits — equivalent to DES.",
    standard: 'NIST SP 800-67 (withdrawn 2017) / RFC 7465', recommendation: 'avoid',
    description: 'Triple-DES applies DES three times to each block. Officially deprecated by NIST in 2017 and prohibited in new protocols. The SWEET32 birthday attack (2016) exploits the 64-bit block size to recover session data after observing ~4GB of ciphertext — achievable in a long HTTPS session. Found in legacy payment systems, banking protocols, and some enterprise network appliances.' },

  { name: 'DES (56-bit)', category: 'cipher', subcategory: 'Broken — Prohibit Immediately',
    type: 'Symmetric Block / DES', keySize: '56 bit',
    quantumSafe: 'broken', classicBits: 0, quantumBits: 0,
    attackVector: "Classically broken since 1998 (EFF DES Cracker, 22 hours). Grover's reduces 56-bit effective key to ~28-bit quantum security — trivially broken.",
    standard: 'Withdrawn / Prohibited', recommendation: 'avoid',
    description: 'DES was cracked classically in 1998 using the EFF\'s "Deep Crack" machine in 22 hours at a cost of $250K. A 56-bit key is trivially broken today with commodity hardware. Any system using DES has a critical classical vulnerability. Still found in some legacy mainframe, SCADA, and telecom systems.' },

  { name: 'IDEA-CBC', category: 'cipher', subcategory: 'Broken — Prohibit Immediately',
    type: 'Symmetric Block / IDEA', keySize: '128 bit',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: "IDEA patent expired. No critical classical breaks but its 64-bit block size creates birthday-bound issues. Grover's weakens to ~64-bit quantum security. CBC mode adds padding oracle risks.",
    standard: 'RFC 5469 (deprecated from TLS)', recommendation: 'avoid',
    description: 'IDEA (International Data Encryption Algorithm) was used in early SSL and PGP 2.x. Removed from TLS in RFC 5469 (2009). No major breaks, but the 64-bit block size causes birthday-bound concerns at high data volumes (SWEET32-class attacks). Found in legacy PGP implementations. Should not appear in any modern deployment.' },

  { name: 'RC2-40-CBC (Export)', category: 'cipher', subcategory: 'Broken — Prohibit Immediately',
    type: 'Symmetric Block / Export-Grade', keySize: '40 bit (export restricted)',
    quantumSafe: 'broken', classicBits: 0, quantumBits: 0,
    attackVector: "40-bit key exhaustively breakable in <1 second on modern hardware. FREAK attack (2015) allows downgrading TLS to export-grade ciphers. Purely classical break.",
    standard: 'Historic / US Export Control Era', recommendation: 'avoid',
    description: 'RC2-40-CBC is a relic of the 1990s US export cryptography restrictions that limited exported encryption to 40-bit keys to allow government eavesdropping. The FREAK attack (2015) demonstrated that millions of servers still supported export-grade cipher negotiation, enabling active downgrade attacks. 40-bit keys are broken in milliseconds.' },

  { name: 'DES40-CBC (Export)', category: 'cipher', subcategory: 'Broken — Prohibit Immediately',
    type: 'Symmetric Block / Export-Grade DES', keySize: '40 bit (export restricted)',
    quantumSafe: 'broken', classicBits: 0, quantumBits: 0,
    attackVector: "40-bit effective key broken in <1 second. Logjam attack (2015) downgraded DHE connections to export-grade DH. Purely classical.",
    standard: 'Historic / US Export Control Era', recommendation: 'avoid',
    description: 'Export-grade DES with 40-bit effective key length. Part of the same broken 1990s export-control framework as RC2-40. The Logjam attack (2015) exploited export-grade DHE — millions of servers were vulnerable. Pure classical vulnerability; quantum computing is irrelevant at this level of weakness.' },

  { name: 'NULL Cipher (TLS_NULL_*)', category: 'cipher', subcategory: 'Broken — Prohibit Immediately',
    type: 'No Encryption / Authentication Only', keySize: 'None',
    quantumSafe: 'broken', classicBits: 0, quantumBits: 0,
    attackVector: "No encryption — all traffic visible in plaintext to any observer. Only provides authentication (MAC). Completely defeats the purpose of TLS confidentiality.",
    standard: 'RFC 5246 (historically defined, never appropriate)', recommendation: 'avoid',
    description: 'NULL cipher suites provide authentication but no encryption — the TLS connection is established but traffic flows in plaintext. Occasionally used for debugging or monitoring purposes. Should never appear in production. Any traffic analysis tool can read NULL-cipher TLS sessions without decryption.' },

  // ── SIGNATURES ───────────────────────────────────────────────────────
  { name: 'RSA-1024 Signature', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Integer Factorization', keySize: '1024 bit',
    quantumSafe: 'broken', classicBits: 0, quantumBits: 0,
    attackVector: "Classically broken — 768-bit RSA was factored in 2010. RSA-1024 is within reach of well-funded adversaries classically. Shor's algorithm provides additional quantum break.",
    standard: 'PKCS#1 (deprecated)', recommendation: 'avoid',
    description: 'RSA-1024 is no longer considered secure even against classical computers. NIST withdrew approval in 2010. Still found in some legacy embedded devices, older code signing certificates, and proprietary protocols. Both classically and quantum-vulnerable.' },

  { name: 'RSA-2048 / RSA-3072 Signature', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Integer Factorization', keySize: '2048–3072 bit',
    quantumSafe: 'broken', classicBits: 112, quantumBits: 0,
    attackVector: "Shor's algorithm factors RSA moduli in polynomial time. All currently-issued RSA certificates are forgeable by a CRQC.",
    standard: 'PKCS#1 / RFC 8017', recommendation: 'avoid',
    description: 'RSA-2048 dominates the TLS certificate ecosystem. The entire web PKI chain of trust — root CAs, intermediate CAs, and leaf certificates — primarily uses RSA-2048 or RSA-3072. All are broken by Shor\'s algorithm. Migration requires CA infrastructure upgrades, browser trust store updates, and certificate reissuance worldwide.' },

  { name: 'RSA-PSS (RSASSA-PSS)', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Integer Factorization (Probabilistic)', keySize: '2048–4096 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's algorithm breaks RSA-PSS through integer factorization, same as PKCS#1. The PSS padding scheme provides improved security over PKCS#1v1.5 but does not affect quantum vulnerability.",
    standard: 'RFC 8017 / PKCS#1 v2.2', recommendation: 'avoid',
    description: 'RSA-PSS (Probabilistic Signature Scheme) is the recommended RSA padding mode, providing tighter security proofs than PKCS#1v1.5. Mandated by TLS 1.3 for RSA signatures. The probabilistic padding prevents some classical attacks. However, quantum vulnerability is identical to all RSA variants — the padding scheme is irrelevant when Shor\'s algorithm factors the modulus.' },

  { name: 'DSA-1024 / DSA-2048', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Finite Field DLP', keySize: '1024–2048 bit',
    quantumSafe: 'broken', classicBits: 80, quantumBits: 0,
    attackVector: "Shor's DLP solver breaks DSA identically to DHE key exchange. DSA-1024 is additionally broken classically.",
    standard: 'NIST FIPS 186-4 (deprecated in FIPS 186-5)', recommendation: 'avoid',
    description: 'DSA (Digital Signature Algorithm) is based on the finite field discrete logarithm problem. DSA-1024 was deprecated by NIST; DSA-2048 extended its use. FIPS 186-5 (2023) deprecated DSA entirely — NIST no longer approves new DSA key generation. Used in legacy SSH host keys, older code signing, and DNSSEC ZSKs.' },

  { name: 'ECDSA P-256 (secp256r1)', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '256 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's ECDLP solver recovers the private signing key from the public key. All previously issued ECDSA P-256 certificates are forgeable.",
    standard: 'NIST FIPS 186-5 / RFC 8422', recommendation: 'avoid',
    description: 'ECDSA P-256 is the most common TLS certificate signature algorithm, having largely replaced RSA-2048 for new certificates. It provides better performance and equivalent classical security to RSA-3072. Broken by Shor\'s ECDLP algorithm — the entire modern certificate ecosystem based on ECDSA P-256 must be migrated.' },

  { name: 'ECDSA P-384 / P-521', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '384–521 bit',
    quantumSafe: 'broken', classicBits: 192, quantumBits: 0,
    attackVector: "Shor's ECDLP algorithm scales polynomially — P-384 and P-521 are broken in more quantum gates but still polynomial time.",
    standard: 'NIST FIPS 186-5', recommendation: 'avoid',
    description: 'P-384 is used in US government Suite B cryptography and some high-security TLS deployments. P-521 is rarely deployed due to performance. Both are broken by Shor\'s ECDLP solver. Higher classical security levels do not translate to quantum resistance.' },

  { name: 'Ed25519 (EdDSA)', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Edwards Curve DLP', keySize: '256 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's algorithm solves the discrete log on twisted Edwards curves (Curve25519). Private key derivable from public key despite EdDSA's excellent classical properties.",
    standard: 'RFC 8032', recommendation: 'avoid',
    description: 'Ed25519 is the modern best-practice digital signature algorithm — fast, constant-time, short signatures, and deterministic. Used for SSH keys, TLS certificates, and software signing. Despite its superior classical properties, Ed25519 provides zero quantum resistance. Shor\'s algorithm breaks Curve25519\'s discrete log as readily as P-256.' },

  { name: 'Ed448 (Goldilocks EdDSA)', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Edwards Curve DLP', keySize: '448 bit (57-byte sig)',
    quantumSafe: 'broken', classicBits: 224, quantumBits: 0,
    attackVector: "Shor's ECDLP algorithm breaks Ed448 as readily as Ed25519. Larger curve parameters add quantum gate counts polynomially but the attack remains feasible.",
    standard: 'RFC 8032', recommendation: 'avoid',
    description: 'Ed448 targets 224-bit classical security on Curve448 (Goldilocks). Used for the highest-assurance SSH and TLS keys. Broken by Shor\'s algorithm — the 448-bit curve provides more quantum gate operations but the same ultimate vulnerability. No elliptic curve signature scheme survives a CRQC.' },

  { name: 'SM2-DSA', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '256 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's ECDLP algorithm breaks SM2 identically to P-256.",
    standard: 'GM/T 0003 / ISO 14888-3', recommendation: 'avoid',
    description: 'SM2-DSA is the Chinese national digital signature standard. Mandated in Chinese government TLS (GB/T 38636). Identical quantum vulnerability to ECDSA P-256.' },

  { name: 'GOST R 34.10-2012 Signature', category: 'sig', subcategory: 'Classical — Broken by Shor\'s Algorithm',
    type: 'Asymmetric / Elliptic Curve DLP', keySize: '256–512 bit',
    quantumSafe: 'broken', classicBits: 128, quantumBits: 0,
    attackVector: "Shor's ECDLP solver breaks GOST curves. Russian FSB is developing PQC variants.",
    standard: 'GOST R 34.10-2012 / RFC 7091', recommendation: 'avoid',
    description: 'Russian national digital signature standard. Mandated in Russian government, financial, and legal document signing systems. The 512-bit variant offers higher classical security but identical quantum vulnerability.' },

  { name: 'ML-DSA-44 (Dilithium2)', category: 'sig', subcategory: 'PQC Standardized — NIST FIPS 204',
    type: 'PQC / Module Lattice (CRYSTALS-Dilithium)', keySize: '1312 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'MLWE/MSIS security level 2. Best known attack: BKZ lattice reduction at ~2^128 operations. No polynomial quantum algorithm known.',
    standard: 'NIST FIPS 204 (2024)', recommendation: 'use',
    description: 'The smallest ML-DSA parameter set. Security level 2 (~AES-128 equivalent). Suitable for short-lived certificates and IoT devices where bandwidth matters. Signature size: 2420 bytes — manageable for TLS handshakes.' },

  { name: 'ML-DSA-65 (Dilithium3)', category: 'sig', subcategory: 'PQC Standardized — NIST FIPS 204',
    type: 'PQC / Module Lattice (CRYSTALS-Dilithium)', keySize: '1952 byte public key',
    quantumSafe: 'safe', classicBits: 178, quantumBits: 128,
    attackVector: 'MLWE/MSIS security level 3. NIST primary recommendation. Best known attack: ~2^178 operations classically / ~2^128 quantum.',
    standard: 'NIST FIPS 204 (2024)', recommendation: 'use',
    description: 'The NIST-recommended security level for most applications. ML-DSA-65 is the default replacement for ECDSA in certificates and software signing. Signature size: 3293 bytes. Integrated in OpenSSL 3.x, BoringSSL, and AWS Key Management Service.' },

  { name: 'ML-DSA-87 (Dilithium5)', category: 'sig', subcategory: 'PQC Standardized — NIST FIPS 204',
    type: 'PQC / Module Lattice (CRYSTALS-Dilithium)', keySize: '2592 byte public key',
    quantumSafe: 'safe', classicBits: 257, quantumBits: 192,
    attackVector: 'MLWE/MSIS security level 5. ~AES-256 equivalent quantum security. Highest assurance with largest key/signature sizes.',
    standard: 'NIST FIPS 204 (2024)', recommendation: 'use',
    description: 'Highest security ML-DSA variant. Signature size: 4595 bytes. Used for root CA certificates, long-lived code signing keys, and TOP SECRET government signing. The signature size overhead is justified for keys with multi-decade lifetimes.' },

  { name: 'FN-DSA-512 (FALCON-512)', category: 'sig', subcategory: 'PQC Standardized — NIST FIPS 206',
    type: 'PQC / NTRU Lattice (Fast Fourier)', keySize: '897 byte public key',
    quantumSafe: 'safe', classicBits: 103, quantumBits: 103,
    attackVector: 'NTRU/SIS hardness. Compact signatures (666 bytes) but implementation requires IEEE 754 floating-point constant-time arithmetic — non-trivial.',
    standard: 'NIST FIPS 206 (2024)', recommendation: 'use',
    description: 'FALCON-512 produces significantly smaller signatures than ML-DSA-44 (666 bytes vs 2420 bytes), making it attractive for bandwidth-constrained applications. Used where TLS handshake size matters. The implementation complexity (constant-time FFT over complex numbers) requires careful library support.' },

  { name: 'FN-DSA-1024 (FALCON-1024)', category: 'sig', subcategory: 'PQC Standardized — NIST FIPS 206',
    type: 'PQC / NTRU Lattice (Fast Fourier)', keySize: '1793 byte public key',
    quantumSafe: 'safe', classicBits: 230, quantumBits: 230,
    attackVector: 'NTRU security level 5. Compact signatures (1280 bytes) at highest security level. Best option where bandwidth and high security are both required.',
    standard: 'NIST FIPS 206 (2024)', recommendation: 'use',
    description: 'The higher-security FALCON variant. Still produces compact signatures (1280 bytes) despite the higher security level — roughly half the size of ML-DSA-87 at equivalent security. Preferred for TLS certificates where handshake size is a concern and high security is required.' },

  { name: 'SLH-DSA-128s / 128f (SPHINCS+)', category: 'sig', subcategory: 'PQC Standardized — NIST FIPS 205',
    type: 'PQC / Hash-Based Stateless', keySize: '32 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 64,
    attackVector: 'Security reduces entirely to hash function collision resistance (SHA-256 or SHAKE-128). No lattice or number-theoretic assumptions. Quantum security limited to ~64-bit by Grover\'s on underlying hash.',
    standard: 'NIST FIPS 205 (2024)', recommendation: 'conditional',
    description: 'The "small" (-s) and "fast" (-f) variants at security level 1. Small variant: 7856-byte signatures, slower. Fast variant: 17088-byte signatures, faster. Ultra-conservative security — zero risk from lattice cryptanalysis. Tiny public keys (32 bytes). Best for root CA certificates where signature size is irrelevant.' },

  { name: 'SLH-DSA-256s / 256f (SPHINCS+)', category: 'sig', subcategory: 'PQC Standardized — NIST FIPS 205',
    type: 'PQC / Hash-Based Stateless', keySize: '64 byte public key',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: 'Based on SHA-256/SHAKE-256 resistance. Grover\'s reduces underlying hash security to ~128-bit quantum — sufficient for post-quantum requirements at this key size.',
    standard: 'NIST FIPS 205 (2024)', recommendation: 'conditional',
    description: 'Highest-security SPHINCS+ parameters. Signature sizes: 29792 bytes (slow) to 49856 bytes (fast). Impractical for TLS leaf certificates but appropriate for offline root CA key signing ceremonies. "If we are wrong about everything else, SPHINCS+ still holds" — the absolute last line of defense against unknown lattice attacks.' },

  { name: 'XMSS / XMSSMT (Stateful)', category: 'sig', subcategory: 'PQC Experimental — Stateful Hash-Based',
    type: 'PQC / Hash-Based Stateful', keySize: '32–67 byte public key',
    quantumSafe: 'safe', classicBits: 256, quantumBits: 128,
    attackVector: 'Hash function security only. State management is critical — signing with a reused state produces existentially forgeable signatures. Incorrect state management completely breaks security.',
    standard: 'NIST SP 800-208 / RFC 8391', recommendation: 'conditional',
    description: 'XMSS (eXtended Merkle Signature Scheme) and its multi-tree variant XMSSMT are stateful hash-based signatures — NIST-approved for specific use cases in SP 800-208. Security depends only on hash functions. The critical constraint: each state (leaf) must be used exactly once. Hardware Security Modules (HSMs) can manage state safely. Used by Germany\'s BSI for firmware signing.' },

  { name: 'LMS / HSS (Stateful)', category: 'sig', subcategory: 'PQC Experimental — Stateful Hash-Based',
    type: 'PQC / Hash-Based Stateful (Leighton-Micali)', keySize: '24–60 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'Hash function security only. State reuse catastrophically breaks signature security — identical to XMSS. Requires reliable HSM or dedicated signing infrastructure.',
    standard: 'NIST SP 800-208 / RFC 8554', recommendation: 'conditional',
    description: 'LMS (Leighton-Micali Signatures) and HSS (Hierarchical Signature System) are NIST SP 800-208 approved stateful hash-based signatures. Simpler than XMSS. Used by Cisco for firmware signing and by some PKI vendors for offline root CA operations. The state limitation makes them unsuitable for high-frequency online signing but excellent for firmware/software release signing.' },

  { name: 'Rainbow (BROKEN 2022)', category: 'sig', subcategory: 'Experimental — Broken / Withdrawn',
    type: 'PQC / Multivariate Polynomial (BROKEN)', keySize: '58 KB public key (1)',
    quantumSafe: 'broken', classicBits: 0, quantumBits: 0,
    attackVector: 'COMPLETELY BROKEN classically (2022). Ward Beullens found a rectangular MinRank attack that recovers the private key in ~53 hours on a laptop. Withdrawn from NIST Round 3 finalists immediately.',
    standard: 'NIST Round 3 Finalist — WITHDRAWN (February 2022)', recommendation: 'avoid',
    description: 'Rainbow was a NIST Round 3 finalist alongside ML-DSA and FALCON. In February 2022, Ward Beullens published an attack breaking Rainbow in ~53 hours on a standard laptop. NIST immediately withdrew it. Rainbow\'s break, alongside SIKE\'s break the same year, demonstrated that PQC competition algorithms can be broken even late in the standardization process. A stark reminder that cryptanalysis is ongoing.' },

  { name: 'GeMSS (NIST Alternate)', category: 'sig', subcategory: 'Experimental — Broken / Withdrawn',
    type: 'PQC / Multivariate (HFEv-)', keySize: '352 KB public key',
    quantumSafe: 'weakened', classicBits: 128, quantumBits: 64,
    attackVector: 'Improved algebraic attacks have significantly reduced the security of GeMSS parameters. Not completely broken but parameters require upward revision. Enormous public keys limit practical use.',
    standard: 'NIST Round 3 Alternate (not advanced)', recommendation: 'avoid',
    description: 'GeMSS (Great Multivariate Short Signatures) was a NIST Round 3 alternate candidate based on Hidden Field Equations with minus and vinegar modifications (HFEv-). Subsequent algebraic attacks reduced its effective security. Combined with enormous public keys (352KB+), GeMSS was not advanced to Round 4. Multivariate signature schemes generally face significant cryptanalytic pressure.' },

  { name: 'PICNIC (ZK-based Signatures)', category: 'sig', subcategory: 'Experimental — Academic',
    type: 'PQC / Zero-Knowledge Proof Based', keySize: '32–64 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'Security based on one-way functions (AES or LowMC). No number-theoretic or lattice assumptions — only symmetric cryptography. Quantum safety equals AES-256 security (~128-bit quantum).',
    standard: 'NIST Round 3 Alternate (not standardized)', recommendation: 'conditional',
    description: 'PICNIC uses zero-knowledge proofs of knowledge of an AES (or LowMC) key to produce digital signatures. The security reduces to AES one-wayness — if AES is secure, PICNIC is secure. Tiny public/private keys. Large signatures (12–34KB). Not standardized; primarily academic interest. Excellent diversification if lattice and code-based schemes were all broken.' },

  { name: 'MAYO (Multivariate)', category: 'sig', subcategory: 'Experimental — NIST Onramp',
    type: 'PQC / Multivariate (Oil-and-Vinegar)', keySize: '1168–5488 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'Based on the Oil-and-Vinegar (UOV) multivariate problem. No known quantum speedup beyond generic search. Smaller parameters than classic UOV through map whipping.',
    standard: 'NIST PQC Onramp Competition (Round 1, 2023)', recommendation: 'conditional',
    description: 'MAYO (Multivariable pOlYnomials that Are Oil-and-vinegar) is a signature scheme in NIST\'s 2023 Onramp competition for additional digital signatures. It uses a "whipped" UOV map to dramatically reduce public key sizes compared to classic Oil-and-Vinegar. Compact signatures (~320–838 bytes). If standardized, it would provide multivariate-based diversification alongside lattice standards.' },

  { name: 'CROSS (Code-based)', category: 'sig', subcategory: 'Experimental — NIST Onramp',
    type: 'PQC / Code-Based (Rank Metric)', keySize: '76–310 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'Based on hardness of decoding random linear codes in the Hamming metric. Symmetric cryptographic assumptions only — no number theory.',
    standard: 'NIST PQC Onramp Competition (Round 1, 2023)', recommendation: 'conditional',
    description: 'CROSS (Codes and Restricted Objects Signature Scheme) is a code-based signature scheme in NIST\'s Onramp competition. Uses cut-and-choose zero-knowledge proofs over code-based problems. Compact public keys. Provides code-based diversification for signatures (complements BIKE/HQC in the KEM space). Under active evaluation.' },

  { name: 'SQIsign (Isogeny-based)', category: 'sig', subcategory: 'Experimental — NIST Onramp',
    type: 'PQC / Supersingular Isogeny (SQI)', keySize: '64 byte public key',
    quantumSafe: 'safe', classicBits: 128, quantumBits: 128,
    attackVector: 'Based on the Supersingular Isogeny Problem (not the same structure as SIDH/SIKE). Uses special quaternion algebra (SQI). No known quantum speedup beyond generic.',
    standard: 'NIST PQC Onramp Competition (Round 1, 2023)', recommendation: 'conditional',
    description: 'SQIsign (Short Quaternion and Isogeny Signature) uses isogeny-based cryptography but from a fundamentally different construction than the broken SIDH/SIKE. SQIsign is NOT broken by the Castryck-Decru attack. It features remarkably compact keys and signatures (64 bytes public key, 177 bytes signature at L1). Very slow signing (~1 second) is the current limitation. Under active cryptanalysis in the NIST Onramp.' },
]

const KB_SECTION_PATHS: Record<KBSection, string> = {
  briefings:  '/knowledge',
  threat:     '/knowledge/threat',
  protocols:  '/knowledge/protocols',
  primitives: '/knowledge/primitives',
  software:   '/knowledge/software',
  capki:      '/knowledge/ca-pki',
  os:         '/knowledge/os',
  laws:       '/knowledge/laws',
  compliance: '/knowledge/compliance',
  wizard:     '/knowledge/wizard',
}
function pathToKBSection(p: string): KBSection {
  if (p === '/knowledge/threat')     return 'threat'
  if (p === '/knowledge/protocols')  return 'protocols'
  if (p === '/knowledge/primitives') return 'primitives'
  if (p === '/knowledge/software')   return 'software'
  if (p === '/knowledge/ca-pki')     return 'capki'
  if (p === '/knowledge/os')         return 'os'
  if (p === '/knowledge/laws')       return 'laws'
  if (p === '/knowledge/compliance') return 'compliance'
  if (p === '/knowledge/wizard')     return 'wizard'
  return 'briefings'
}

// ── Software Registry Data ────────────────────────────────────────────────────

interface PlatformRow {
  platform: string
  version:  string
  status:   'safe' | 'unsafe' | 'planned'
  notes?:   string
}

interface SwEntry {
  name: string; version: string; cat: string; catLabel: string
  status: 'safe' | 'unsafe' | 'conditional'
  risk: 'safe' | 'high' | 'medium' | 'conditional'
  detail: string; since: string
  cpe: string
  links: { label: string; url: string }[]
  platforms?: PlatformRow[]
  osDep?: boolean
}

// TODO: remove after DB migration verified — SW_CATS and SOFTWARE_DATA are now served via /v1/software API
const SW_CATS = [
  { id: 'browser',   label: 'Browser' },
  { id: 'vpn',       label: 'VPN' },
  { id: 'messaging', label: 'Messaging' },
  { id: 'cryptolib', label: 'Crypto Library' },
  { id: 'network',   label: 'Network / CDN' },
  { id: 'remote',    label: 'Remote Access' },
  { id: 'cloud',     label: 'Cloud / Infra' },
  { id: 'webserver', label: 'Web Server' },
  { id: 'runtime',   label: 'Runtime / Language' },
  { id: 'database',  label: 'Database' },
  { id: 'siem',      label: 'SIEM / Analytics' },
  { id: 'grc',       label: 'GRC / Risk' },
]

const SOFTWARE_DATA: SwEntry[] = [
  // ── QUANTUM SAFE ──────────────────────────────────────────────────────────
  { name: 'Google Chrome', version: '131+', cat: 'browser', catLabel: 'Browser', status: 'safe', risk: 'safe',
    since: 'Chrome 131 (Nov 2024)',
    cpe: 'cpe:2.3:a:google:chrome:131.0.0.0:*:*:*:*:*:*:*',
    detail: 'Deploys X25519MLKEM768 hybrid TLS 1.3 key exchange by default. Chrome 124 used the Kyber draft (0x6399); Chrome 131 switched to finalized ML-KEM (0x11EC). KX is quantum-safe; certificate auth still uses ECDSA.',
    links: [
      { label: 'Google Security Blog: ML-KEM on the web', url: 'https://security.googleblog.com/2024/09/a-new-path-for-kyber-on-web.html' },
      { label: 'Chromium Blog: Advancing post-quantum TLS', url: 'https://blog.chromium.org/2024/05/advancing-our-amazing-bet-on-asymmetric.html' },
    ] },
  { name: 'Microsoft Edge', version: '124+', cat: 'browser', catLabel: 'Browser', status: 'safe', risk: 'safe',
    since: 'Edge 124 (May 2024)',
    cpe: 'cpe:2.3:a:microsoft:edge:124.0.0.0:*:*:*:chromium:*:*:*',
    detail: 'Chromium-based. Enabled X25519Kyber768 by default from Edge 124; switched to X25519MLKEM768 in Edge 131. Edge 147+ permanently enables PQC — policy to disable was removed.',
    links: [
      { label: 'MS Edge PostQuantumKeyAgreementEnabled policy', url: 'https://learn.microsoft.com/en-us/deployedge/microsoft-edge-browser-policies/postquantumkeyagreementenabled' },
      { label: 'WebRTC post-quantum key agreement policy', url: 'https://learn.microsoft.com/en-us/deployedge/microsoft-edge-browser-policies/webrtcpostquantumkeyagreement' },
    ] },
  { name: 'Mozilla Firefox', version: '132+', cat: 'browser', catLabel: 'Browser', status: 'safe', risk: 'safe',
    since: 'Firefox 132 (Oct 2024)',
    cpe: 'cpe:2.3:a:mozilla:firefox:132.0:*:*:*:*:*:*:*',
    detail: 'Enables mlkem768x25519 hybrid TLS 1.3 key exchange by default. Firefox 146 extended ML-KEM to WebRTC/DTLS 1.3. Provides harvest-now-decrypt-later protection equivalent to Chrome/Edge.',
    links: [
      { label: 'Firefox 132.0 release notes', url: 'https://www.mozilla.org/en-US/firefox/132.0/releasenotes/' },
      { label: 'Mozilla Security Blog: hybrid PQ key agreement', url: 'https://blog.mozilla.org/security/2024/05/08/improving-security-of-firefox-connections-with-hybrid-post-quantum-key-agreement/' },
    ] },
  { name: 'Opera', version: '110+', cat: 'browser', catLabel: 'Browser', status: 'safe', risk: 'safe',
    since: 'Opera 110 (May 2024)',
    cpe: 'cpe:2.3:a:opera:opera_browser:110.0.0.0:*:*:*:*:*:*:*',
    detail: 'Chromium-based browser. Opera 110 (Chromium 124) deployed the Kyber draft (X25519Kyber768); Opera 117+ (Chromium 131) switched to finalized X25519MLKEM768. Uses BoringSSL via the Chromium network stack — PQC key exchange is on by default.',
    links: [
      { label: 'Opera release blog', url: 'https://blogs.opera.com/desktop/' },
      { label: 'Chromium Blog: ML-KEM in TLS', url: 'https://blog.chromium.org/2024/05/advancing-our-amazing-bet-on-asymmetric.html' },
    ] },
  { name: 'Brave Browser', version: '1.73+', cat: 'browser', catLabel: 'Browser', status: 'safe', risk: 'safe',
    since: 'Brave 1.73 (Nov 2024)',
    cpe: 'cpe:2.3:a:brave:brave:1.73.0:*:*:*:*:*:*:*',
    detail: 'Chromium-based browser. Brave 1.73 (Chromium 131) deployed finalized X25519MLKEM768 hybrid TLS 1.3 key exchange. Earlier versions 1.67–1.72 used the Kyber draft. Privacy-focused defaults do not disable PQC key exchange.',
    links: [
      { label: 'Brave release notes', url: 'https://brave.com/latest/' },
      { label: 'Chromium Blog: ML-KEM in TLS', url: 'https://blog.chromium.org/2024/05/advancing-our-amazing-bet-on-asymmetric.html' },
    ] },
  { name: 'Cloudflare', version: 'Global CDN', cat: 'network', catLabel: 'Network / CDN', status: 'safe', risk: 'safe',
    since: 'Oct 2022 (all zones)',
    cpe: 'cpe:2.3:a:cloudflare:cloudflare:-:*:*:*:*:*:*:*',
    detail: 'Serves X25519MLKEM768 on all edge servers. Over 60% of human-generated TLS traffic to Cloudflare uses hybrid ML-KEM as of 2025. WARP VPN also includes PQC tunneling.',
    links: [
      { label: 'Cloudflare SSL post-quantum docs', url: 'https://developers.cloudflare.com/ssl/post-quantum-cryptography/' },
      { label: 'State of the post-quantum Internet 2025', url: 'https://blog.cloudflare.com/pq-2025/' },
    ] },
  { name: 'OpenSSH', version: '9.0+', cat: 'remote', catLabel: 'Remote Access', status: 'safe', risk: 'safe',
    since: 'OpenSSH 9.0 (Apr 2022)',
    cpe: 'cpe:2.3:a:openbsd:openssh:9.0:p1:*:*:*:*:*:*',
    detail: 'Made sntrup761x25519-sha512 the default KEX in 9.0. OpenSSH 9.9 added mlkem768x25519-sha256. OpenSSH 10.0 made ML-KEM the new default; 10.1 warns when non-PQC algorithms are negotiated.',
    links: [
      { label: 'OpenSSH post-quantum page', url: 'https://www.openssh.org/pq.html' },
      { label: 'OpenSSH 9.0 release notes', url: 'https://www.openssh.org/txt/release-9.0' },
    ] },
  { name: 'Signal', version: '6.43+', cat: 'messaging', catLabel: 'Messaging', status: 'safe', risk: 'safe',
    since: 'Signal 6.43 (Sep 2023)',
    cpe: 'cpe:2.3:a:signal:signal:6.43.0:*:*:*:*:*:*:*',
    detail: 'Deployed PQXDH (Post-Quantum Extended Diffie-Hellman) combining X25519 with ML-KEM-1024 for initial key agreement. Also deploying SPQR (Sparse Post-Quantum Ratchet) for ongoing session protection.',
    links: [
      { label: 'Signal PQXDH announcement', url: 'https://signal.org/blog/pqxdh/' },
      { label: 'PQXDH specification', url: 'https://signal.org/docs/specifications/pqxdh/' },
      { label: 'Signal SPQR blog post', url: 'https://signal.org/blog/spqr/' },
    ] },
  { name: 'AWS KMS', version: 'GA', cat: 'cloud', catLabel: 'Cloud / Infra', status: 'safe', risk: 'safe',
    since: 'Nov 2019 (Kyber); ML-KEM May 2025',
    cpe: 'cpe:2.3:a:amazon:key_management_service:-:*:*:*:*:*:*:*',
    detail: 'Hybrid post-quantum TLS available via AWS SDK since 2019. ML-KEM (FIPS 203) added for non-FIPS endpoints in May 2025. Also supported in ACM and Secrets Manager.',
    links: [
      { label: 'AWS KMS hybrid PQ TLS developer guide', url: 'https://docs.aws.amazon.com/kms/latest/developerguide/pqtls.html' },
      { label: 'AWS Security Blog: ML-KEM in KMS/ACM', url: 'https://aws.amazon.com/blogs/security/ml-kem-post-quantum-tls-now-supported-in-aws-kms-acm-and-secrets-manager/' },
    ] },
  { name: 'OpenSSL', version: '3.5+', cat: 'cryptolib', catLabel: 'Crypto Library', status: 'safe', risk: 'safe',
    since: 'OpenSSL 3.5.0 (Apr 2025)',
    cpe: 'cpe:2.3:a:openssl:openssl:3.5.0:*:*:*:*:*:*:*',
    detail: 'First OpenSSL release with built-in NIST-final PQC — no plugin required. Includes ML-KEM (FIPS 203), ML-DSA (FIPS 204), SLH-DSA (FIPS 205). Default TLS keyshares include X25519MLKEM768. Earlier 3.3/3.4 needed the oqs-provider.',
    links: [
      { label: 'OpenSSL 3.5 release announcement', url: 'https://openssl-library.org/post/2025-04-08-openssl-35-final-release/' },
      { label: 'OpenSSL Foundation: PQC in 3.5', url: 'https://openssl-foundation.org/post/2025-04-22-pqc/' },
    ] },
  { name: 'BoringSSL', version: 'Current', cat: 'cryptolib', catLabel: 'Crypto Library', status: 'safe', risk: 'safe',
    since: '2023 (draft Kyber); ML-KEM 2024',
    cpe: 'cpe:2.3:a:google:boringssl:*:*:*:*:*:*:*:*',
    detail: "Google's TLS library used in Chrome, Android, and Google Cloud. Ships X25519MLKEM768 (0x11EC) as current hybrid. Powers Chrome 131+ ML-KEM deployment. Open source.",
    links: [
      { label: 'BoringSSL repository', url: 'https://boringssl.googlesource.com/boringssl/' },
      { label: 'Open Quantum Safe BoringSSL fork', url: 'https://github.com/open-quantum-safe/boringssl' },
    ] },
  { name: 'Mullvad VPN', version: 'App 2023.3+', cat: 'vpn', catLabel: 'VPN', status: 'safe', risk: 'safe',
    since: 'App 2023.3 (Apr 2023)',
    cpe: 'cpe:2.3:a:mullvad:mullvad_vpn:2023.3:*:*:*:*:*:*:*',
    detail: 'Deployed quantum-resistant WireGuard tunnels for all users in app 2023.3. Uses Classic McEliece + Kyber combined as a WireGuard pre-shared key, layered on top of classical WireGuard cryptography.',
    links: [
      { label: 'Mullvad: stable quantum-resistant tunnels', url: 'https://mullvad.net/en/blog/stable-quantum-resistant-tunnels-in-the-app' },
    ] },
  { name: 'StrongSwan', version: '6.0.0+', cat: 'vpn', catLabel: 'VPN', status: 'safe', risk: 'safe',
    since: 'StrongSwan 6.0.0 (Dec 2024)',
    cpe: 'cpe:2.3:a:strongswan:strongswan:6.0.0:*:*:*:*:*:*:*',
    detail: 'Version 6.0.0 added native ML-KEM without needing liboqs. Implements RFC 9370 multiple IKEv2 key exchanges for hybrid classical + PQ key agreement. Supports ML-KEM-512, ML-KEM-768, ML-KEM-1024.',
    links: [
      { label: 'StrongSwan 6.0.0 release blog', url: 'https://strongswan.org/blog/2024/12/03/strongswan-6.0.0-released.html' },
      { label: 'StrongSwan algorithm proposals (ML-KEM)', url: 'https://docs.strongswan.org/docs/latest/config/proposals.html' },
    ] },
  { name: 'liboqs / Open Quantum Safe', version: 'Current', cat: 'cryptolib', catLabel: 'Crypto Library', status: 'safe', risk: 'safe',
    since: '2016 (ongoing)',
    cpe: 'cpe:2.3:a:open_quantum_safe:liboqs:0.9.0:*:*:*:*:*:*:*',
    detail: 'Reference C library implementing all NIST PQC standards: ML-KEM, ML-DSA, SLH-DSA, Falcon, BIKE, HQC, Classic McEliece, and more. Powers PQC in OpenSSL (oqs-provider), BoringSSL, and other TLS stacks.',
    links: [
      { label: 'liboqs official site', url: 'https://openquantumsafe.org/liboqs/' },
      { label: 'liboqs GitHub repository', url: 'https://github.com/open-quantum-safe/liboqs' },
    ] },
  { name: 'Bouncy Castle', version: '1.79+', cat: 'cryptolib', catLabel: 'Crypto Library', status: 'safe', risk: 'safe',
    since: 'BC 1.79 (Dec 2024)',
    cpe: 'cpe:2.3:a:bouncycastle:bouncy_castle_for_java:1.79:*:*:*:*:*:*:*',
    detail: 'Java/Kotlin crypto library with complete NIST-final PQC: ML-KEM, ML-DSA, SLH-DSA, FN-DSA (Falcon), XWing, and Composite signatures. Version 1.79 was first release with FIPS 203/204/205 final specs.',
    links: [
      { label: 'Bouncy Castle 1.79 PQC announcement', url: 'https://www.bouncycastle.org/resources/latest-nist-pqc-standards-and-more-bouncy-castle-java-1-79/' },
      { label: 'Bouncy Castle Java releases', url: 'https://www.bouncycastle.org/download/bouncy-castle-java/' },
    ] },
  { name: 'WireGuard + Rosenpass', version: 'Current', cat: 'vpn', catLabel: 'VPN', status: 'safe', risk: 'safe',
    since: '2023',
    cpe: 'cpe:2.3:a:rosenpass:rosenpass:0.2.0:*:*:*:*:*:*:*',
    detail: 'Rosenpass adds a post-quantum outer handshake (ML-KEM) to WireGuard, providing hybrid PQC protection. Not in mainline WireGuard — requires separate Rosenpass daemon. Used in production by Mullvad VPN.',
    links: [
      { label: 'Rosenpass whitepaper', url: 'https://rosenpass.eu/whitepaper.pdf' },
      { label: 'Rosenpass GitHub', url: 'https://github.com/rosenpass/rosenpass' },
    ] },
  // ── NOT QUANTUM SAFE ──────────────────────────────────────────────────────
  { name: 'Apple Safari', version: 'iOS/macOS 26+', cat: 'browser', catLabel: 'Browser', status: 'safe', risk: 'safe',
    since: 'iOS 26 / macOS Tahoe 26 (shipped fall 2025)',
    cpe: 'cpe:2.3:a:apple:safari:*:*:*:*:*:*:*:*',
    detail: 'Safari on iOS 26 and macOS Tahoe 26 (shipped fall 2025) includes X25519MLKEM768 hybrid TLS 1.3. Older OS versions have no ML-KEM. Status depends entirely on the OS version in use.',
    links: [
      { label: 'Apple: Prepare your network for quantum-secure TLS', url: 'https://support.apple.com/en-us/122756' },
    ],
    platforms: [
      { platform: 'iOS / iPadOS 26+',     version: 'Shipped fall 2025',    status: 'safe',   notes: 'X25519MLKEM768 hybrid TLS 1.3 enabled' },
      { platform: 'macOS Tahoe 26+',      version: 'Shipped fall 2025',    status: 'safe',   notes: 'X25519MLKEM768 hybrid TLS 1.3 enabled' },
      { platform: 'iOS / iPadOS ≤ 18',   version: 'All prior releases',    status: 'unsafe', notes: 'No ML-KEM; classical ECDH only' },
      { platform: 'macOS ≤ Sequoia 15',  version: 'All prior releases',    status: 'unsafe', notes: 'No ML-KEM; classical ECDH only' },
      { platform: 'Safari on Windows',    version: 'Discontinued',         status: 'unsafe', notes: 'No longer available; not on PQC roadmap' },
    ],
    osDep: true },
  { name: 'Microsoft Windows TLS (Schannel)', version: 'Win 11 recent / Server 2025', cat: 'runtime', catLabel: 'Runtime / Language', status: 'safe', risk: 'safe',
    since: 'Windows 11 (recent builds) / Server 2025 (recent updates)',
    cpe: 'cpe:2.3:a:microsoft:schannel:*:*:*:*:*:*:*:*',
    detail: 'Schannel is used by IIS, .NET, and most Windows apps. Recent production builds of Windows 11 and Windows Server 2025 include ML-KEM support in Schannel via CNG. Windows 10 and older Windows 11 builds remain quantum-vulnerable with no PQC roadmap.',
    links: [
      { label: 'Microsoft: PQC comes to Windows Insiders', url: 'https://techcommunity.microsoft.com/blog/microsoft-security-blog/post-quantum-cryptography-comes-to-windows-insiders-and-linux/4413803' },
    ],
    platforms: [
      { platform: 'Windows 11 (recent builds)',  version: 'Current production',      status: 'safe',   notes: 'ML-KEM in Schannel; available via CNG' },
      { platform: 'Windows 11 (older builds)',   version: 'Pre-PQC update',           status: 'unsafe', notes: 'Update required to enable ML-KEM Schannel' },
      { platform: 'Windows 10 (all)',            version: 'End-of-support Oct 2025',  status: 'unsafe', notes: 'No PQC planned for Win10 Schannel' },
      { platform: 'Windows Server 2025',        version: 'Current updates',          status: 'safe',   notes: 'ML-KEM in Schannel with recent cumulative updates' },
      { platform: 'Windows Server 2022 and earlier', version: 'All',                 status: 'unsafe', notes: 'No PQC Schannel; no roadmap announced' },
    ],
    osDep: true },
  { name: 'OpenVPN', version: '2.7.0+', cat: 'vpn', catLabel: 'VPN', status: 'safe', risk: 'safe',
    since: 'OpenVPN 2.7.0 (Feb 11, 2026)',
    cpe: 'cpe:2.3:a:openvpn:openvpn:2.7.0:*:*:*:*:*:*:*',
    detail: 'OpenVPN 2.7.0 (released February 11, 2026) introduced native post-quantum key agreement using ML-KEM (FIPS 203) in the TLS control channel. First official OpenVPN release with true PQC hybrid key exchange — no external plugins or custom builds required.',
    links: [
      { label: 'OpenVPN community forum', url: 'https://community.openvpn.net/openvpn' },
    ] },
  { name: 'Cisco AnyConnect / Secure Client', version: 'All current', cat: 'vpn', catLabel: 'VPN', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:cisco:anyconnect_secure_mobility_client:*:*:*:*:*:*:*:*',
    detail: 'PQC roadmap targeting 2025–2026 for ML-KEM in networking products. No production hybrid KX in AnyConnect/Secure Client as of Q1 2026.',
    links: [
      { label: 'Cisco post-quantum cryptography trust center', url: 'https://www.cisco.com/site/us/en/about/trust-center/post-quantum-cryptography.html' },
    ] },
  { name: 'Palo Alto GlobalProtect', version: 'PAN-OS 11.1+ with RFC 9370', cat: 'vpn', catLabel: 'VPN', status: 'safe', risk: 'safe',
    since: 'PAN-OS 11.1 (RFC 9370 ML-KEM support)',
    cpe: 'cpe:2.3:a:paloaltonetworks:globalprotect:11.1.0:*:*:*:*:*:*:*',
    detail: 'Palo Alto Networks extended GlobalProtect with quantum-safe VPN via RFC 9370 (Multiple Key Exchanges in IKEv2), enabling hybrid classical + ML-KEM key exchange. PQC is not enabled by default — requires a qualifying PAN-OS version and explicit IKEv2 crypto profile configuration.',
    links: [
      { label: 'Palo Alto: Quantum-safe VPN with RFC 9370', url: 'https://live.paloaltonetworks.com/t5/community-blogs/palo-alto-networks-extends-support-for-quantum-safe-vpn-with-rfc/ba-p/585492' },
      { label: 'Palo Alto PQC guidance', url: 'https://www.paloaltonetworks.com/cyberpedia/post-quantum-cryptography' },
    ],
    platforms: [
      { platform: 'PAN-OS 11.1+ (configured)',            version: '11.1+',             status: 'safe',   notes: 'Hybrid ML-KEM IKEv2 via RFC 9370 MKE; must configure crypto profile' },
      { platform: 'PAN-OS 11.1+ (default config)',       version: '11.1+',             status: 'unsafe', notes: 'RFC 9370 not enabled by default; requires explicit configuration' },
      { platform: 'PAN-OS < 11.1',                       version: '< 11.1',            status: 'unsafe', notes: 'Classical IKEv2 only; no ML-KEM' },
      { platform: 'GlobalProtect SSL/TLS mode',          version: 'All',               status: 'unsafe', notes: 'SSL-based tunnel mode does not use RFC 9370; classical TLS only' },
    ] },
  { name: 'HashiCorp Vault', version: 'Enterprise 1.19+', cat: 'cloud', catLabel: 'Cloud / Infra', status: 'safe', risk: 'safe',
    since: 'Vault Enterprise 1.19 (2025)',
    cpe: 'cpe:2.3:a:hashicorp:vault:1.19.0:*:*:*:*:enterprise:*:*',
    detail: 'Vault Enterprise 1.19 added ML-KEM, ML-DSA, and SLH-DSA to the transit secrets engine, enabling PQC key generation, signing, and encryption operations. Community edition has no PQC. TLS transport to the Vault API itself remains classical in all versions.',
    links: [
      { label: 'Vault Enterprise 1.19: encryption updates & automated root rotation', url: 'https://www.hashicorp.com/en/blog/vault-enterprise-1-19-reduces-risk-encryption-updates-automated-root-rotation' },
      { label: 'HashiCorp: NIST PQC standards plans', url: 'https://www.hashicorp.com/en/blog/nist-s-post-quantum-cryptography-standards-our-plans' },
    ],
    platforms: [
      { platform: 'Vault Enterprise 1.19+',    version: '1.19+',      status: 'safe',   notes: 'ML-KEM, ML-DSA, SLH-DSA in transit secrets engine' },
      { platform: 'Vault Enterprise < 1.19',   version: '< 1.19',     status: 'unsafe', notes: 'No PQC algorithm support in transit engine' },
      { platform: 'Vault Community Edition',   version: 'All',        status: 'unsafe', notes: 'PQC transit engine is Enterprise-only' },
      { platform: 'Vault API TLS transport',   version: 'All',        status: 'unsafe', notes: 'API connections use classical TLS; no ML-KEM transport' },
    ] },
  { name: 'Kubernetes (k8s)', version: '1.33+', cat: 'cloud', catLabel: 'Cloud / Infra', status: 'safe', risk: 'safe',
    since: 'Kubernetes 1.33 (mid-2025)',
    cpe: 'cpe:2.3:a:kubernetes:kubernetes:1.33.0:*:*:*:*:*:*:*',
    detail: 'Kubernetes 1.33 (released mid-2025) achieved the first major PQC milestone, introducing post-quantum safe cryptography for cluster communications. API server TLS and control plane connections gained ML-KEM hybrid key exchange. Earlier versions relied entirely on classical ECDSA/RSA via Go crypto/tls.',
    links: [
      { label: 'Kubernetes security documentation', url: 'https://kubernetes.io/docs/concepts/security/' },
      { label: 'Kubernetes 1.33 release notes', url: 'https://kubernetes.io/blog/2025/04/23/kubernetes-v1-33-release/' },
    ] },
  { name: 'Nginx', version: 'Custom OpenSSL 3.5+ build', cat: 'webserver', catLabel: 'Web Server', status: 'safe', risk: 'safe',
    since: 'Custom builds with OpenSSL 3.5 (Apr 2025)',
    cpe: 'cpe:2.3:a:nginx:nginx:*:*:*:*:*:*:*:*',
    detail: 'Delegates TLS entirely to the linked OpenSSL build. With a custom-compiled Nginx linked against OpenSSL 3.5+, X25519MLKEM768 is available. Standard OS distribution packages (Ubuntu, RHEL, Debian, Docker Hub nginx) use older OpenSSL and have no PQC.',
    links: [
      { label: 'Nginx Community Blog: PQC support in NGINX', url: 'https://blog.nginx.org/blog/pqc-nginx' },
      { label: 'OpenSSL 3.5 release announcement', url: 'https://openssl-library.org/post/2025-04-08-openssl-35-final-release/' },
    ],
    platforms: [
      { platform: 'Custom build + OpenSSL 3.5+',   version: 'Any Nginx version', status: 'safe',   notes: 'X25519MLKEM768 available; must configure ssl_ecdh_curve' },
      { platform: 'Ubuntu 22.04 / 24.04 (apt)',    version: 'nginx 1.24/1.26',   status: 'unsafe', notes: 'Ships with OpenSSL 3.0/3.3; no ML-KEM' },
      { platform: 'RHEL 10 (yum/dnf)',              version: 'nginx 1.26+',        status: 'safe',   notes: 'OpenSSL 3.5 bundled; ML-KEM available' },
      { platform: 'RHEL 9 / CentOS Stream 9',      version: 'nginx 1.20–1.24',    status: 'unsafe', notes: 'OpenSSL 3.0.x; no ML-KEM' },
      { platform: 'Debian 12 Bookworm (apt)',      version: 'nginx 1.22',         status: 'unsafe', notes: 'OpenSSL 3.0; no ML-KEM' },
      { platform: 'Docker Hub nginx official',     version: 'Any tag',            status: 'unsafe', notes: 'Based on Debian/Alpine; no OpenSSL 3.5 yet' },
    ],
    osDep: true },
  { name: 'Apache httpd', version: 'Custom OpenSSL 3.5+ build', cat: 'webserver', catLabel: 'Web Server', status: 'safe', risk: 'safe',
    since: 'Custom builds with OpenSSL 3.5 (Apr 2025)',
    cpe: 'cpe:2.3:a:apache:http_server:*:*:*:*:*:*:*:*',
    detail: 'Apache httpd delegates TLS to mod_ssl, which wraps the linked OpenSSL. With a custom build against OpenSSL 3.5+, X25519MLKEM768 can be configured. All major OS distribution packages use OpenSSL < 3.5 and have no ML-KEM.',
    links: [
      { label: 'Open Quantum Safe: TLS applications demo', url: 'https://openquantumsafe.org/applications/tls.html' },
      { label: 'Apache httpd mod_ssl documentation', url: 'https://httpd.apache.org/docs/current/mod/mod_ssl.html' },
    ],
    platforms: [
      { platform: 'Custom build + OpenSSL 3.5+',  version: 'httpd 2.4+',         status: 'safe',   notes: 'X25519MLKEM768 available via SSLOpenSSLConfCmd' },
      { platform: 'Ubuntu 22.04 / 24.04 (apt)',   version: 'apache2 2.4.52–2.4.58', status: 'unsafe', notes: 'OpenSSL 3.0/3.3; no ML-KEM' },
      { platform: 'RHEL 10 (yum/dnf)',            version: 'httpd 2.4.62+',        status: 'safe',   notes: 'OpenSSL 3.5 bundled; ML-KEM available' },
      { platform: 'RHEL 8/9 (yum/dnf)',           version: 'httpd 2.4.37–2.4.57',  status: 'unsafe', notes: 'OpenSSL 1.1/3.0; no ML-KEM' },
      { platform: 'Debian 12 Bookworm (apt)',     version: 'apache2 2.4.57',        status: 'unsafe', notes: 'OpenSSL 3.0; no ML-KEM' },
    ],
    osDep: true },
  { name: 'Java JDK (SunJSSE)', version: '24+ (primitives) / 27+ (TLS)', cat: 'runtime', catLabel: 'Runtime / Language', status: 'safe', risk: 'safe',
    since: 'JDK 24 (March 2025) — crypto primitives; JDK 27 — TLS',
    cpe: 'cpe:2.3:a:oracle:jdk:24.0.0:*:*:*:*:*:*:*',
    detail: 'Java 24 crossed the post-quantum threshold with JEP 496 (ML-KEM / FIPS 203) and JEP 497 (ML-DSA / FIPS 204) — native PQC via standard java.security APIs, no external C libraries required. TLS transport (SunJSSE) is not yet PQC: JEP 527 (hybrid X25519MLKEM768 in TLS 1.3) is in JDK 27 early access builds. Legacy LTS apps on JDK 17/21 can achieve PQC via Bouncy Castle 1.79+.',
    links: [
      { label: 'JEP 496: ML-KEM Key Encapsulation (JDK 24)', url: 'https://openjdk.org/jeps/496' },
      { label: 'JEP 497: ML-DSA Digital Signatures (JDK 24)', url: 'https://openjdk.org/jeps/497' },
      { label: 'JEP 527: Post-Quantum Hybrid TLS 1.3 (JDK 27 EA)', url: 'https://openjdk.org/jeps/527' },
    ],
    platforms: [
      { platform: 'JDK 27+ (JEP 527)',                version: 'Early access',   status: 'safe',   notes: 'Hybrid X25519MLKEM768 in TLS 1.3 via javax.net.ssl — no code changes needed' },
      { platform: 'JDK 24+ (JEP 496/497)',            version: 'GA Mar 2025',    status: 'safe',   notes: 'ML-KEM & ML-DSA crypto primitives native; TLS transport still classical' },
      { platform: 'JDK 17/21 LTS + Bouncy Castle 1.79+', version: 'BC 1.79+',   status: 'safe',   notes: 'Full ML-KEM/ML-DSA via BC provider; pluggable into legacy apps' },
      { platform: 'JDK < 24 (no BC)',                 version: '< 24',           status: 'unsafe', notes: 'No native PQC; classical ECDSA/RSA only' },
    ] },
  { name: 'Python (ssl / cryptography)', version: 'OpenSSL 3.5+ backend', cat: 'runtime', catLabel: 'Runtime / Language', status: 'safe', risk: 'safe',
    since: 'Available when linked to OpenSSL 3.5 (Apr 2025)',
    cpe: 'cpe:2.3:a:python:python:*:*:*:*:*:*:*:*',
    detail: "Python's ssl module wraps the system OpenSSL. With OpenSSL 3.5+ as the backend, X25519MLKEM768 is available in TLS 1.3 key exchange. No major Linux distribution ships Python with OpenSSL 3.5+ yet, but macOS Homebrew Python and AWS custom builds qualify.",
    links: [
      { label: 'AWS: Post-quantum TLS in Python', url: 'https://aws.amazon.com/blogs/security/post-quantum-tls-in-python/' },
      { label: 'OpenSSL 3.5 release announcement', url: 'https://openssl-library.org/post/2025-04-08-openssl-35-final-release/' },
    ],
    platforms: [
      { platform: 'Python + OpenSSL 3.5+ (custom)',   version: 'CPython 3.10+', status: 'safe',   notes: 'X25519MLKEM768 available via ssl.SSLContext' },
      { platform: 'Ubuntu 22.04 (system Python)',     version: 'Python 3.10',   status: 'unsafe', notes: 'Linked to OpenSSL 3.0; no ML-KEM' },
      { platform: 'Ubuntu 24.04 (system Python)',     version: 'Python 3.12',   status: 'unsafe', notes: 'Linked to OpenSSL 3.3; no ML-KEM' },
      { platform: 'macOS Homebrew python@3.13',       version: 'Python 3.13',   status: 'safe',   notes: 'Homebrew builds link against OpenSSL 3.5' },
      { platform: 'Windows CPython (python.org)',     version: 'CPython 3.x',   status: 'unsafe', notes: 'Bundles older OpenSSL; no ML-KEM' },
      { platform: 'AWS Lambda Python runtime',        version: 'Python 3.11/3.12', status: 'unsafe', notes: 'System OpenSSL < 3.5; no ML-KEM by default' },
    ],
    osDep: true },
  { name: 'ProtonMail / Proton', version: 'Current', cat: 'messaging', catLabel: 'Messaging', status: 'unsafe', risk: 'medium',
    since: '',
    cpe: 'cpe:2.3:a:proton:mail:*:*:*:*:*:*:*:*',
    detail: 'Uses OpenPGP for email encryption. PGP RFC 9580 (2024) adds ML-KEM to the spec, but Proton has not deployed PQC-capable keys for general users. Currently uses RSA-4096 and Curve25519.',
    links: [
      { label: 'Proton: building quantum-safe PGP encryption', url: 'https://proton.me/blog/post-quantum-encryption' },
    ] },
  { name: 'Zoom', version: 'v6.0.10+ with E2EE', cat: 'messaging', catLabel: 'Messaging', status: 'safe', risk: 'safe',
    since: 'Zoom 6.0.10 (May 2024) — E2EE only',
    cpe: 'cpe:2.3:a:zoom:zoom:6.0.10:*:*:*:*:*:*:*',
    detail: 'E2EE meetings with all participants on v6.0.10+ use Kyber-768 for key agreement (safe). TLS signaling and non-E2EE sessions are not PQC-protected. Quantum-safe only when E2EE is explicitly enabled by the host and all participants use a qualifying client.',
    links: [
      { label: 'Zoom: Post-quantum E2EE announcement', url: 'https://news.zoom.com/post-quantum-e2ee/' },
    ],
    platforms: [
      { platform: 'Desktop/Mobile ≥ 6.0.10 (E2EE enabled)', version: '6.0.10+', status: 'safe',   notes: 'Kyber-768 key agreement when all participants qualify' },
      { platform: 'Desktop/Mobile ≥ 6.0.10 (E2EE disabled)', version: '6.0.10+', status: 'unsafe', notes: 'Classical TLS; PQC only active with E2EE' },
      { platform: 'Desktop/Mobile < 6.0.10',                 version: '< 6.0.10', status: 'unsafe', notes: 'No PQC support; blocks E2EE PQC for whole session' },
      { platform: 'Zoom Web Client',                          version: 'Any',     status: 'unsafe', notes: 'E2EE (and therefore PQC) not supported in browser' },
    ] },
  { name: 'WhatsApp', version: 'Current', cat: 'messaging', catLabel: 'Messaging', status: 'safe', risk: 'safe',
    since: 'Signal Protocol PQXDH (late 2023)',
    cpe: 'cpe:2.3:a:meta:whatsapp:*:*:*:*:*:*:*:*',
    detail: 'WhatsApp uses the Signal Protocol, which was upgraded to PQXDH (Post-Quantum Extended Diffie-Hellman) in late 2023. PQXDH blends classical X25519 with ML-KEM-1024 for initial key agreement, defending against harvest-now-decrypt-later attacks. Desktop apps act as linked clients and inherit the same E2EE PQC protections.',
    links: [
      { label: 'WhatsApp security whitepaper', url: 'https://www.whatsapp.com/security/WhatsApp-Security-Whitepaper.pdf' },
      { label: 'Signal PQXDH specification', url: 'https://signal.org/docs/specifications/pqxdh/' },
    ] },
  { name: 'Facebook Messenger', version: 'Current', cat: 'messaging', catLabel: 'Messaging', status: 'safe', risk: 'safe',
    since: 'Signal Protocol PQXDH (late 2023)',
    cpe: 'cpe:2.3:a:meta:messenger:*:*:*:*:*:*:*:*',
    detail: 'Facebook Messenger uses the Signal Protocol for E2EE chats, which was upgraded to PQXDH (Post-Quantum Extended Diffie-Hellman) in late 2023. Like WhatsApp, Messenger inherits the hybrid ML-KEM-1024 + X25519 key agreement from the shared Signal Protocol architecture. Desktop and linked device clients carry the same PQC protections.',
    links: [
      { label: 'Signal PQXDH specification', url: 'https://signal.org/docs/specifications/pqxdh/' },
      { label: 'Meta Messenger security overview', url: 'https://www.messenger.com/privacy' },
    ] },
  { name: 'Instagram DMs', version: 'Current (E2EE removed May 2026)', cat: 'messaging', catLabel: 'Messaging', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:meta:instagram:*:*:*:*:*:*:*:*',
    detail: 'Meta reversed course on Instagram E2EE: as of May 8, 2026, end-to-end encryption is being removed from Instagram DMs following pressure from law enforcement and child safety organizations. With standard E2EE abandoned entirely, post-quantum user-to-user encryption is off the table. Meta has full visibility into DM content.',
    links: [
      { label: 'Instagram privacy policy', url: 'https://privacycenter.instagram.com/policy' },
    ] },
  { name: 'X (Twitter)', version: 'Current', cat: 'messaging', catLabel: 'Messaging', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:twitter:twitter:*:*:*:*:*:*:*:*',
    detail: 'X has made no public commitments or announcements regarding post-quantum cryptography. E2EE Direct Messages are not enabled by default, restricted to verified paying users or specific organizations, and use a limited cryptographic implementation far behind modern messaging protocols. No PQC roadmap exists.',
    links: [
      { label: 'X privacy policy', url: 'https://x.com/privacy' },
    ] },
  { name: 'Microsoft Teams', version: 'Win 11 recent / Server 2025', cat: 'messaging', catLabel: 'Messaging', status: 'safe', risk: 'safe',
    since: 'Depends on OS Schannel — Windows 11 recent builds',
    cpe: 'cpe:2.3:a:microsoft:teams:*:*:*:*:*:*:*:*',
    detail: 'Teams uses the OS TLS stack (Schannel on Windows) for all signaling and media transport. On recent Windows 11 builds and Windows Server 2025 where Schannel includes ML-KEM, Teams connections inherit PQC key exchange automatically. macOS, iOS, Android, and Linux clients depend on their respective platform TLS stacks, none of which have confirmed ML-KEM for Teams traffic.',
    links: [
      { label: 'Microsoft: PQC comes to Windows Insiders', url: 'https://techcommunity.microsoft.com/blog/microsoft-security-blog/post-quantum-cryptography-comes-to-windows-insiders-and-linux/4413803' },
      { label: 'Microsoft Teams security overview', url: 'https://learn.microsoft.com/en-us/microsoftteams/teams-security-guide' },
    ],
    platforms: [
      { platform: 'Windows 11 Teams (recent builds)',   version: 'Current',  status: 'safe',   notes: 'Inherits ML-KEM from Schannel on recent Win 11' },
      { platform: 'Windows 11 Teams (older builds)',    version: 'Pre-PQC',  status: 'unsafe', notes: 'Schannel without ML-KEM update' },
      { platform: 'Windows Server 2025',               version: 'Current',  status: 'safe',   notes: 'Inherits ML-KEM from updated Schannel' },
      { platform: 'Teams on macOS Tahoe 26+',           version: 'Current',  status: 'safe',   notes: 'macOS 26 system TLS includes ML-KEM; Teams inherits it' },
      { platform: 'Teams on macOS ≤ Sequoia 15',       version: 'Any',      status: 'unsafe', notes: 'Apple TLS has no ML-KEM on macOS 15 and earlier' },
      { platform: 'Teams on iOS / Android',            version: 'Any',      status: 'unsafe', notes: 'Platform TLS; no confirmed ML-KEM' },
      { platform: 'Teams Web (browser)',               version: 'Any',      status: 'safe',   notes: 'PQC depends on the browser used — Chrome/Edge/Firefox are safe' },
    ],
    osDep: true },
  { name: 'Microsoft Outlook', version: 'Win 11 recent / Server 2025', cat: 'messaging', catLabel: 'Messaging', status: 'safe', risk: 'safe',
    since: 'Depends on OS Schannel — Windows 11 recent builds',
    cpe: 'cpe:2.3:a:microsoft:outlook:*:*:*:*:*:*:*:*',
    detail: 'Outlook desktop uses the OS TLS stack (Schannel on Windows) for connections to Exchange Online. On recent Windows 11 and Server 2025 builds with ML-KEM Schannel, Outlook-to-Exchange connections use PQC key exchange. Email transport protocols (SMTP/IMAP between mail servers) are not PQC-protected in any deployment. Outlook Web App (OWA) inherits PQC from the browser.',
    links: [
      { label: 'Microsoft: PQC comes to Windows Insiders', url: 'https://techcommunity.microsoft.com/blog/microsoft-security-blog/post-quantum-cryptography-comes-to-windows-insiders-and-linux/4413803' },
      { label: 'Exchange Online TLS overview', url: 'https://learn.microsoft.com/en-us/exchange/exchange-tls-certificates' },
    ],
    platforms: [
      { platform: 'Outlook on Windows 11 (recent)',    version: 'Current',  status: 'safe',   notes: 'Client-to-Exchange TLS inherits ML-KEM from Schannel' },
      { platform: 'Outlook on Windows 11 (older)',     version: 'Pre-PQC',  status: 'unsafe', notes: 'Classical TLS only' },
      { platform: 'Outlook on macOS Tahoe 26+',         version: 'Current',  status: 'safe',   notes: 'macOS 26 system TLS includes ML-KEM; Outlook inherits it' },
      { platform: 'Outlook on macOS ≤ Sequoia 15',     version: 'Any',      status: 'unsafe', notes: 'Apple TLS has no ML-KEM on macOS 15 and earlier' },
      { platform: 'Outlook Web App (browser)',         version: 'Any',      status: 'safe',   notes: 'PQC depends on browser — Chrome/Edge/Firefox are safe' },
      { platform: 'SMTP server-to-server transport',   version: 'All',      status: 'unsafe', notes: 'Email relay protocols have no PQC standard yet' },
    ],
    osDep: true },
  { name: 'Red Hat Enterprise Linux (OpenSSL)', version: 'RHEL 9+ / RHEL 10', cat: 'runtime', catLabel: 'Runtime / Language', status: 'safe', risk: 'safe',
    since: 'RHEL 10 (May 2025)',
    cpe: 'cpe:2.3:o:redhat:enterprise_linux:10.0:*:*:*:*:*:*:*',
    detail: 'RHEL 10 ships OpenSSL 3.5.0 natively, which includes ML-KEM (FIPS 203), ML-DSA (FIPS 204), and SLH-DSA (FIPS 205) — fully quantum-safe TLS by default. RHEL 9 ships OpenSSL 3.0.x but Red Hat backported PQC support via the oqs-provider and crypto-policies; enablement requires configuration. RHEL 8 and earlier have no PQC path.',
    links: [
      { label: 'Red Hat: Post-quantum cryptography in RHEL', url: 'https://www.redhat.com/en/blog/post-quantum-cryptography-rhel' },
      { label: 'OpenSSL 3.5 release announcement', url: 'https://openssl-library.org/post/2025-04-08-openssl-35-final-release/' },
    ],
    platforms: [
      { platform: 'RHEL 10',                    version: 'GA (May 2025)',        status: 'safe',   notes: 'Ships OpenSSL 3.5.0; ML-KEM enabled by default' },
      { platform: 'RHEL 9 (with oqs-provider)', version: '9.x + configuration',  status: 'safe', notes: 'PQC available via oqs-provider and crypto-policy update; requires configuration' },
      { platform: 'RHEL 9 (default config)',    version: '9.x stock',            status: 'unsafe', notes: 'OpenSSL 3.0.x default; no ML-KEM without additional setup' },
      { platform: 'RHEL 8 (all)',               version: 'All',                  status: 'unsafe', notes: 'OpenSSL 1.1.x; no PQC path' },
      { platform: 'CentOS Stream 10',           version: 'Current',              status: 'safe',   notes: 'Based on RHEL 10 userspace; OpenSSL 3.5' },
      { platform: 'CentOS Stream 9',            version: 'Current',              status: 'unsafe', notes: 'OpenSSL 3.0.x; same as RHEL 9 default' },
    ],
    osDep: true },

  // ── DATABASES ─────────────────────────────────────────────────────────────
  { name: 'Oracle Database 26ai', version: '23.26.1+', cat: 'database', catLabel: 'Database', status: 'safe', risk: 'safe',
    since: 'Oracle AI Database 26ai 23.26.1 (January 2026)',
    cpe: 'cpe:2.3:a:oracle:database:23.26.1:*:*:*:*:*:*:*',
    detail: 'Oracle AI Database 26ai (release 23.26.1, January 2026) is the first major RDBMS to ship production-grade PQC TLS. It supports pure ML-KEM TLS 1.3 key exchange (introduced October 2025 RU) and hybrid ECDHE+ML-KEM (added January 2026 RU). Hybrid mode is recommended during the transition period. GA on Linux x86-64 on-premises; Exadata and cloud delivery on separate schedule. Older Oracle Database versions (19c, 21c, 23c prior to 23.26.1) have no PQC TLS.',
    links: [
      { label: 'Oracle AI Database 26ai: Quantum Era security', url: 'https://blogs.oracle.com/database/oracle-ai-database-26ai-pqc' },
      { label: 'Oracle: Hybrid-mode quantum-resistant TLS', url: 'https://blogs.oracle.com/database/hybrid-pqc' },
    ],
    platforms: [
      { platform: 'Oracle AI Database 26ai 23.26.1+ (Linux x86-64)', version: '23.26.1+', status: 'safe',   notes: 'Hybrid ECDHE+ML-KEM TLS 1.3 GA; pure ML-KEM mode also available' },
      { platform: 'Oracle Database 23c (< 23.26.1)',                  version: '< 23.26.1',status: 'unsafe', notes: 'No PQC TLS; classical ECDHE only' },
      { platform: 'Oracle Database 21c / 19c',                        version: 'All',      status: 'unsafe', notes: 'No PQC path; classical TLS only' },
    ] },
  { name: 'Microsoft SQL Server', version: 'Windows Server 2025 / Win 11 recent', cat: 'database', catLabel: 'Database', status: 'safe', risk: 'safe',
    since: 'Windows Server 2025 / Windows 11 (recent builds) with ML-KEM Schannel',
    cpe: 'cpe:2.3:a:microsoft:sql_server:*:*:*:*:*:*:*:*',
    detail: 'SQL Server relies on Windows Schannel for TLS. On Windows Server 2025 and recent Windows 11 builds, Schannel includes ML-KEM (FIPS 203) support via CNG, meaning SQL Server connections on those platforms inherit PQC TLS. SQL Server running on Windows Server 2022 or earlier has no PQC TLS path.',
    links: [
      { label: 'Microsoft: PQC comes to Windows (Schannel)', url: 'https://techcommunity.microsoft.com/blog/microsoft-security-blog/post-quantum-cryptography-comes-to-windows-insiders-and-linux/4413803' },
      { label: 'Microsoft Quantum Safe Program timeline', url: 'https://www.microsoft.com/en-us/security/blog/2025/08/20/quantum-safe-security-progress-towards-next-generation-cryptography/' },
    ],
    platforms: [
      { platform: 'SQL Server on Windows Server 2025 (current updates)', version: 'Any version', status: 'safe',   notes: 'Schannel with ML-KEM enabled; TLS 1.3 PQC inherited from OS' },
      { platform: 'SQL Server on Windows 11 (recent builds)',            version: 'Any version', status: 'safe',   notes: 'Same ML-KEM Schannel benefit via CNG update' },
      { platform: 'SQL Server on Windows Server 2022 and earlier',       version: 'All',         status: 'unsafe', notes: 'Schannel has no PQC on these OS versions; no roadmap' },
      { platform: 'SQL Server on Linux (SSL/TLS via OpenSSL)',           version: 'All',         status: 'unsafe', notes: 'Not a documented or supported PQC configuration' },
    ],
    osDep: true },
  { name: 'PostgreSQL', version: '15+ on OpenSSL 3.5+ host', cat: 'database', catLabel: 'Database', status: 'safe', risk: 'safe',
    since: 'PostgreSQL 15+ + OpenSSL 3.5 host (inherited)',
    cpe: 'cpe:2.3:a:postgresql:postgresql:15.0:*:*:*:*:*:*:*',
    detail: 'PostgreSQL does not implement its own PQC algorithms — quantum safety is outsourced to the host OS cryptographic libraries across three layers. (1) Network layer: PostgreSQL server TLS uses the system OpenSSL; on a host with OpenSSL 3.5+, it can negotiate X25519MLKEM768 hybrid TLS 1.3. PostgreSQL 15+ has reliable OpenSSL 3.x support; older releases may have compatibility issues. (2) Client drivers: libpq and drivers built against OpenSSL 3.5+ (psycopg3, node-postgres, etc.) also inherit PQC on the client side. (3) Storage layer: the pgcrypto extension uses OpenSSL primitives; on OpenSSL 3.5+ it gains access to ML-KEM and ML-DSA APIs for at-rest operations, though this requires explicit application-level use.',
    links: [
      { label: 'PostgreSQL TLS documentation', url: 'https://www.postgresql.org/docs/current/ssl-tcp.html' },
      { label: 'OpenSSL 3.5 PQC feature announcement', url: 'https://openssl-foundation.org/post/2025-04-22-pqc/' },
    ],
    platforms: [
      { platform: 'PostgreSQL 15+ on RHEL 10 / Ubuntu 24.04+ (OpenSSL 3.5+) — network', version: '15+', status: 'safe',   notes: 'Server TLS inherits X25519MLKEM768 hybrid key exchange from host OpenSSL 3.5' },
      { platform: 'libpq / client drivers compiled against OpenSSL 3.5+',               version: '15+', status: 'safe',   notes: 'Client-side TLS also inherits PQC from OpenSSL 3.5 at build time' },
      { platform: 'pgcrypto extension on OpenSSL 3.5+',                                 version: '15+', status: 'safe',   notes: 'ML-KEM / ML-DSA APIs available for at-rest operations via pgcrypto; requires explicit app use' },
      { platform: 'PostgreSQL 15+ on RHEL 9 / Ubuntu 22.04 (OpenSSL 3.0.x default)',   version: '15+', status: 'unsafe', notes: 'OpenSSL 3.0.x; no ML-KEM without oqs-provider configuration' },
      { platform: 'PostgreSQL 14 and earlier',                                           version: '< 15',status: 'unsafe', notes: 'OpenSSL 3.x compatibility issues; no validated PQC path' },
    ],
    osDep: true },
  { name: 'MySQL', version: 'No official PQC', cat: 'database', catLabel: 'Database', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:oracle:mysql:*:*:*:*:*:*:*:*',
    detail: 'MySQL 9.x uses OpenSSL for TLS and has no PQC-specific TLS announcements as of March 2026. Oracle\'s PQC database investment is concentrated in Oracle AI Database 26ai, not MySQL. Passive PQC inheritance via a host OpenSSL 3.5 build is theoretically possible but not documented, tested, or officially supported.',
    links: [
      { label: 'MySQL TLS documentation', url: 'https://dev.mysql.com/doc/refman/9.0/en/encrypted-connections.html' },
    ] },
  { name: 'MariaDB', version: 'No PQC', cat: 'database', catLabel: 'Database', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:mariadb:mariadb:*:*:*:*:*:*:*:*',
    detail: 'MariaDB supports OpenSSL or wolfSSL as its TLS backend. wolfSSL v5.8+ includes ML-KEM support and non-standard PQC-enabled builds are theoretically possible, but the MariaDB Foundation has published no PQC roadmap or validated configuration. No MariaDB version provides production PQC TLS.',
    links: [
      { label: 'MariaDB SSL/TLS documentation', url: 'https://mariadb.com/kb/en/securing-connections-for-client-and-server/' },
    ] },
  { name: 'MongoDB', version: '7.0+ on OpenSSL 3.5+ host', cat: 'database', catLabel: 'Database', status: 'safe', risk: 'safe',
    since: 'MongoDB 7.0 + OpenSSL 3.5 host (inherited)',
    cpe: 'cpe:2.3:a:mongodb:mongodb:7.0.0:*:*:*:*:*:*:*',
    detail: 'MongoDB does not ship native PQC algorithms — quantum safety is entirely inherited from the host OS TLS library. MongoDB 7.0 was the first major release to officially support OpenSSL 3.x. When running MongoDB 7.0+ on a host with OpenSSL 3.5+ (e.g. RHEL 10, Ubuntu 24.04 with updated OpenSSL), the server can negotiate X25519MLKEM768 hybrid TLS 1.3 key exchange. Older versions (6.0, 5.0) are pinned to OpenSSL 1.1.1 branches and cannot negotiate PQC TLS handshakes even if the connecting client supports them.',
    links: [
      { label: 'MongoDB TLS/SSL documentation', url: 'https://www.mongodb.com/docs/manual/core/security-transport-encryption/' },
      { label: 'OpenSSL 3.5 PQC feature announcement', url: 'https://openssl-foundation.org/post/2025-04-22-pqc/' },
    ],
    platforms: [
      { platform: 'MongoDB 7.0+ on RHEL 10 / Ubuntu 24.04+ (OpenSSL 3.5+)', version: '7.0+', status: 'safe',   notes: 'Inherits X25519MLKEM768 hybrid TLS 1.3 from host OpenSSL 3.5' },
      { platform: 'MongoDB 7.0+ on RHEL 9 (OpenSSL 3.0.x default)',          version: '7.0+', status: 'unsafe', notes: 'OpenSSL 3.0.x default; no ML-KEM without oqs-provider configuration' },
      { platform: 'MongoDB 6.0 / 5.0 (pinned to OpenSSL 1.1.1)',             version: '< 7.0',status: 'unsafe', notes: 'Pinned to OpenSSL 1.1.x; cannot negotiate PQC TLS regardless of OS' },
    ],
    osDep: true },
  { name: 'Elasticsearch', version: 'No PQC (blocked on JDK 27)', cat: 'database', catLabel: 'Database', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:elastic:elasticsearch:*:*:*:*:*:*:*:*',
    detail: 'Elasticsearch (by Elastic) uses the JDK\'s built-in TLS stack (JSSE/SunJSSE) for all inter-node and client communications. PQC hybrid TLS for the JDK is tracked under JEP 527 (X25519MLKEM768, SecP256r1MLKEM768), targeted to JDK 27 in February 2026. JDK 27 is a non-LTS release expected in September 2027. JDK 24 (March 2025) added ML-KEM APIs (JEP 496) but not TLS integration. Elastic has published no PQC roadmap.',
    links: [
      { label: 'JEP 527: Post-Quantum Hybrid Key Exchange for TLS 1.3', url: 'https://openjdk.org/jeps/527' },
      { label: 'Elastic: Supported TLS versions by JDK version', url: 'https://www.elastic.co/docs/deploy-manage/security/supported-ssltls-versions-by-jdk-version' },
    ] },
  { name: 'OpenSearch', version: 'No PQC (blocked on JDK 27)', cat: 'database', catLabel: 'Database', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:amazon:opensearch:*:*:*:*:*:*:*:*',
    detail: 'OpenSearch (open-source, stewarded by Amazon/AWS; forked from Elasticsearch 7.10 in 2021) also uses JSSE/SunJSSE for TLS. It shares the same JVM blocker as Elasticsearch: PQC TLS requires JEP 527, targeted to JDK 27 (expected September 2027). Amazon OpenSearch Service (managed cloud) has not announced PQC TLS at the cluster layer, though AWS infrastructure-level TLS may terminate connections with PQC separately. No OpenSearch project PQC roadmap as of March 2026.',
    links: [
      { label: 'JEP 527: Post-Quantum Hybrid Key Exchange for TLS 1.3', url: 'https://openjdk.org/jeps/527' },
      { label: 'OpenSearch security documentation', url: 'https://opensearch.org/docs/latest/security/' },
    ] },
  { name: 'Redis', version: 'No official PQC', cat: 'database', catLabel: 'Database', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:redis:redis:*:*:*:*:*:*:*:*',
    detail: 'Redis requires OpenSSL at compile time for TLS (--tls-port). If compiled against OpenSSL 3.5, passive PQC hybrid key exchange is theoretically possible, but Redis Ltd. has not announced, validated, or documented PQC TLS as a feature. Redis 8.x has no PQC-specific TLS guidance. Redis Enterprise and Redis Cloud may benefit from PQC-capable infrastructure-layer TLS termination, but not at the Redis protocol layer.',
    links: [
      { label: 'Redis TLS support documentation', url: 'https://redis.io/docs/latest/operate/oss_and_stack/management/security/tls/' },
    ] },

  // ── SIEM / ANALYTICS ──────────────────────────────────────────────────────
  { name: 'Splunk Enterprise / Cloud', version: 'No PQC', cat: 'siem', catLabel: 'SIEM / Analytics', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:splunk:splunk:*:*:*:*:*:*:*:*',
    detail: 'Splunk Enterprise (now Cisco Splunk) uses OpenSSL for TLS. Versions 9.3 and 9.4 document TLS 1.3 support via server.conf and inputs.conf but make no reference to ML-KEM or post-quantum cipher suite groups. Splunk Cloud Platform has not announced PQC at the TLS termination layer. This is a notable compliance gap for US federal deployments under CNSA 2.0. No public PQC roadmap from Cisco/Splunk as of March 2026.',
    links: [
      { label: 'Splunk: TLS encryption and cipher suites', url: 'https://help.splunk.com/en/splunk-enterprise/administer/manage-users-and-security/10.2/install-splunk-enterprise-securely/about-tls-encryption-and-cipher-suites' },
    ] },
  { name: 'IBM QRadar SIEM', version: 'No PQC', cat: 'siem', catLabel: 'SIEM / Analytics', status: 'unsafe', risk: 'high',
    since: '',
    cpe: 'cpe:2.3:a:ibm:qradar_siem:*:*:*:*:*:*:*:*',
    detail: 'IBM QRadar runs on commodity Linux (RHEL) and has no confirmed PQC TLS support as of March 2026. IBM\'s quantum-safe capabilities are concentrated in its z16 mainframe (Crypto Express 8S HSM) and IBM z/OS — not the QRadar appliance. QRadar on RHEL 10 could inherit PQC via the OS OpenSSL 3.5 stack, but IBM has not validated this for QRadar. IBM and Palo Alto announced a joint Quantum-Safe Readiness solution in early 2026, but this is a consulting offering, not a QRadar product feature.',
    links: [
      { label: 'IBM Quantum Safe Roadmap overview', url: 'https://research.ibm.com/blog/z16-quantum-safe-migration' },
    ] },
  { name: 'CrowdStrike Falcon', version: 'Detection only (no PQC channel)', cat: 'siem', catLabel: 'SIEM / Analytics', status: 'unsafe', risk: 'medium',
    since: '',
    cpe: 'cpe:2.3:a:crowdstrike:falcon_sensor:*:*:*:*:*:*:*:*',
    detail: 'CrowdStrike Falcon sensor-to-cloud communications use standard TLS with no confirmed PQC hybrid key exchange as of March 2026. Falcon does offer quantum-vulnerable cryptography detection — identifying weak classical crypto across the endpoint estate — but this is a visibility feature, not protection of Falcon\'s own communications channel. Certificate rotation in March 2026 was a standard update, not a PQC migration. No public roadmap for PQC-secured sensor communications.',
    links: [
      { label: 'CrowdStrike cybersecurity resources', url: 'https://www.crowdstrike.com/en-us/resources/' },
      { label: 'InfoSec Global AgileSec Analytics (PQC monitoring) on CrowdStrike Marketplace', url: 'https://marketplace.crowdstrike.com/listings/infosec-global-agilesec-analytics/' },
    ] },
  { name: 'Microsoft Sentinel', version: 'No PQC (follows Azure infrastructure)', cat: 'siem', catLabel: 'SIEM / Analytics', status: 'unsafe', risk: 'medium',
    since: '',
    cpe: 'cpe:2.3:a:microsoft:azure_sentinel:*:*:*:*:*:*:*:*',
    detail: 'Microsoft Sentinel is a cloud-native SaaS SIEM running entirely in Azure. Its TLS posture is determined by Azure infrastructure, not Sentinel itself. Microsoft\'s Quantum Safe Program targets Azure networking services in the 2026 phase and data/analytics platforms in the 2027 phase. Sentinel\'s Log Analytics endpoints would gain PQC TLS as part of that Azure-wide rollout, but no Sentinel-specific PQC commitment or date exists as of March 2026.',
    links: [
      { label: 'Microsoft Quantum Safe Program (2033 target)', url: 'https://www.microsoft.com/en-us/security/blog/2025/08/20/quantum-safe-security-progress-towards-next-generation-cryptography/' },
    ] },

  // ── GRC / RISK ────────────────────────────────────────────────────────────
  { name: 'RSA Archer', version: 'Windows Server 2025 / Win 11 recent', cat: 'grc', catLabel: 'GRC / Risk', status: 'safe', risk: 'safe',
    since: 'Windows Server 2025 / Windows 11 (recent builds) with ML-KEM Schannel',
    cpe: 'cpe:2.3:a:rsa:archer:*:*:*:*:*:*:*:*',
    detail: 'RSA Archer is a Windows Server / IIS / .NET-based GRC platform — all HTTPS is served through IIS via Windows Schannel. On Windows Server 2025 and recent Windows 11 builds, Schannel includes ML-KEM (FIPS 203) support, so Archer\'s HTTPS connections to client browsers and API consumers inherit PQC TLS. Deployments on Windows Server 2022 and earlier remain quantum-vulnerable as Schannel has no PQC on those OS versions.',
    links: [
      { label: 'Microsoft: PQC comes to Windows (Schannel + IIS)', url: 'https://techcommunity.microsoft.com/blog/microsoft-security-blog/post-quantum-cryptography-comes-to-windows-insiders-and-linux/4413803' },
      { label: 'RSA Archer platform overview', url: 'https://www.archerirm.com/products/archer-platform' },
    ],
    platforms: [
      { platform: 'Archer on Windows Server 2025 (current updates)', version: 'Any Archer version', status: 'safe',   notes: 'IIS + Schannel with ML-KEM; PQC TLS inherited from OS' },
      { platform: 'Archer on Windows 11 (recent builds)',             version: 'Any Archer version', status: 'safe',   notes: 'Same ML-KEM Schannel benefit' },
      { platform: 'Archer on Windows Server 2022 and earlier',        version: 'All',                status: 'unsafe', notes: 'Schannel no PQC on these OS versions' },
    ],
    osDep: true },
]

// ─── Compliance Profiles ──────────────────────────────────────────────────────

type AlgoStatus = 'required' | 'approved' | 'recommended' | 'conditional' | 'forbidden' | 'na'
interface AlgoReq { algo: string; status: AlgoStatus; note?: string }
interface ComplianceProfile {
  id: string; name: string; shortName: string; issuer: string; jurisdiction: string
  year: number; audience: string; level: 'mandatory' | 'recommended' | 'guidance'
  hybrid: 'required' | 'recommended' | 'optional' | 'not-specified'
  color: string
  kem: AlgoReq[]; sig: AlgoReq[]; hash: AlgoReq[]; symmetric: AlgoReq[]
  description: string; timelines?: string
}

const STATUS_COLOR: Record<AlgoStatus, string> = {
  required:    '#00ff88',
  approved:    '#22d3ee',
  recommended: '#60a5fa',
  conditional: '#fbbf24',
  forbidden:   '#ff4d4d',
  na:          '#1e293b',
}
const STATUS_LABEL: Record<AlgoStatus, string> = {
  required:    'REQUIRED',
  approved:    'APPROVED',
  recommended: 'RECOMMENDED',
  conditional: 'CONDITIONAL',
  forbidden:   'FORBIDDEN',
  na:          'N/A',
}

const COMPLIANCE_PROFILES: ComplianceProfile[] = [
  {
    id: 'cnsa2', name: 'CNSA 2.0', shortName: 'CNSA 2.0',
    issuer: 'NSA', jurisdiction: 'US Federal', year: 2022,
    audience: 'National Security Systems, cleared defense contractors',
    level: 'mandatory', hybrid: 'recommended', color: '#ef4444',
    kem: [
      { algo: 'ML-KEM-1024', status: 'required',    note: 'Required for all NSS — level 5 security minimum' },
      { algo: 'ML-KEM-768',  status: 'forbidden',   note: 'Insufficient security level for NSS' },
      { algo: 'ML-KEM-512',  status: 'forbidden',   note: 'Insufficient security level for NSS' },
      { algo: 'RSA-3072',    status: 'conditional', note: 'Classical hybrid component only; deprecated standalone after 2030' },
      { algo: 'ECDH P-384',  status: 'conditional', note: 'Classical hybrid component during transition period' },
    ],
    sig: [
      { algo: 'ML-DSA-87',    status: 'required',    note: 'Primary signature for NSS — level 5 required' },
      { algo: 'SLH-DSA-256s', status: 'approved',    note: 'Backup if lattice cryptography is broken' },
      { algo: 'SLH-DSA-256f', status: 'approved',    note: 'Faster hash-based backup option' },
      { algo: 'ML-DSA-65',    status: 'forbidden',   note: 'Insufficient for NSS' },
      { algo: 'ML-DSA-44',    status: 'forbidden',   note: 'Insufficient for NSS' },
      { algo: 'ECDSA P-384',  status: 'conditional', note: 'Classical hybrid co-signature during transition only' },
    ],
    hash: [
      { algo: 'SHA-384', status: 'required',   note: 'Minimum for NSS' },
      { algo: 'SHA-512', status: 'approved' },
      { algo: 'SHA-256', status: 'forbidden',  note: 'Below 128-bit post-quantum security — not acceptable for NSS' },
    ],
    symmetric: [
      { algo: 'AES-256', status: 'required', note: 'Mandatory for all NSS encrypted data' },
      { algo: 'AES-128', status: 'forbidden', note: 'Below quantum security threshold for NSS' },
    ],
    description: 'Most stringent US government PQC suite. Mandates specific high-security parameter sets only. Classical algorithms still allowed as hybrid components during transition but have hard cutoff dates. Designed for systems protecting classified national security information against nation-state quantum adversaries.',
    timelines: 'SW/FW: 2025  ·  Networking: 2026  ·  OS: 2027  ·  General HW: 2030  ·  Custom HW: 2033',
  },
  {
    id: 'nist-fips', name: 'NIST FIPS 203/204/205', shortName: 'NIST FIPS',
    issuer: 'NIST', jurisdiction: 'US Federal', year: 2024,
    audience: 'All federal agencies, general public use',
    level: 'mandatory', hybrid: 'optional', color: '#3b82f6',
    kem: [
      { algo: 'ML-KEM-512',  status: 'approved',     note: 'Level 1 — AES-128 equivalent quantum security; lightweight/IoT use' },
      { algo: 'ML-KEM-768',  status: 'recommended',  note: 'Level 3 — AES-192 equivalent; general purpose recommended choice' },
      { algo: 'ML-KEM-1024', status: 'approved',     note: 'Level 5 — AES-256 equivalent; high-value long-lived data' },
    ],
    sig: [
      { algo: 'ML-DSA-44',    status: 'approved',    note: 'Level 2 — smallest/fastest; general use' },
      { algo: 'ML-DSA-65',    status: 'approved',    note: 'Level 3 — balanced security/performance' },
      { algo: 'ML-DSA-87',    status: 'approved',    note: 'Level 5 — high security applications' },
      { algo: 'SLH-DSA-128f', status: 'approved',    note: 'Hash-based; fast variant, larger signatures' },
      { algo: 'SLH-DSA-128s', status: 'approved',    note: 'Hash-based; small signatures, slower' },
      { algo: 'SLH-DSA-256s', status: 'approved',    note: 'Hash-based; smallest high-security signature' },
      { algo: 'SLH-DSA-256f', status: 'approved',    note: 'Hash-based; fast high-security variant' },
    ],
    hash: [
      { algo: 'SHA-256', status: 'approved' },
      { algo: 'SHA-384', status: 'approved' },
      { algo: 'SHA-512', status: 'approved' },
      { algo: 'SHA3-256', status: 'approved' },
    ],
    symmetric: [
      { algo: 'AES-128', status: 'approved',     note: 'Adequate for most applications; 64-bit quantum security' },
      { algo: 'AES-256', status: 'recommended',  note: 'Preferred for sensitive or long-lived data' },
    ],
    description: 'The foundational PQC standards — no specific combination mandated. All security parameter sets approved. Choose based on your threat model, performance requirements, and data sensitivity. NIST encourages algorithm diversity: pair ML-DSA with SLH-DSA as backup to protect against lattice-specific breakthroughs.',
  },
  {
    id: 'bsi', name: 'BSI TR-02102-1', shortName: 'BSI TR-02102',
    issuer: 'BSI (Germany)', jurisdiction: 'Germany', year: 2024,
    audience: 'German federal agencies, regulated industry',
    level: 'mandatory', hybrid: 'recommended', color: '#f59e0b',
    kem: [
      { algo: 'ML-KEM-768',  status: 'required',    note: 'Minimum for Cat 1 protection (normal, ~2035 security horizon)' },
      { algo: 'ML-KEM-1024', status: 'recommended', note: 'For Cat 2 / long-lived or high-value data' },
      { algo: 'ML-KEM-512',  status: 'conditional', note: 'Only for short-lived low-risk data — not recommended' },
      { algo: 'ECDH P-384',  status: 'recommended', note: 'Classical hybrid component; X448 also acceptable' },
      { algo: 'X25519',      status: 'conditional', note: 'Hybrid component — P-384 preferred for higher security' },
    ],
    sig: [
      { algo: 'ML-DSA-65',    status: 'required',    note: 'Minimum for Cat 1 security' },
      { algo: 'ML-DSA-87',    status: 'recommended', note: 'Preferred for new deployments and high-security systems' },
      { algo: 'SLH-DSA-256s', status: 'approved',    note: 'For applications requiring hash-based assurance or algorithm diversity' },
      { algo: 'ML-DSA-44',    status: 'conditional', note: 'Only for low-risk, short-lived, performance-constrained use' },
      { algo: 'ECDSA P-384',  status: 'recommended', note: 'Classical hybrid co-signature component' },
    ],
    hash: [
      { algo: 'SHA-256', status: 'required',    note: 'Minimum acceptable' },
      { algo: 'SHA-384', status: 'recommended', note: 'Preferred for Cat 2 / high-security applications' },
      { algo: 'SHA-512', status: 'approved' },
    ],
    symmetric: [
      { algo: 'AES-128', status: 'conditional', note: 'Only for low-risk, short-duration, performance-constrained contexts' },
      { algo: 'AES-256', status: 'required',    note: 'Required for Cat 1 and Cat 2' },
    ],
    description: 'German federal technical guideline. Strongly recommends hybrid classical+PQC during transition. Two-category security model: Cat 1 (normal protection, ~2035 horizon) and Cat 2 (high protection, long-lived data). Updated annually with deprecation timelines. Hybrid recommended but not required — BSI accepts pure PQC at ML-KEM-768 + ML-DSA-65 minimum.',
  },
  {
    id: 'anssi', name: 'ANSSI PQC Guidance', shortName: 'ANSSI',
    issuer: 'ANSSI (France)', jurisdiction: 'France', year: 2022,
    audience: 'French government, critical operators (OIV/OSE)',
    level: 'guidance', hybrid: 'required', color: '#8b5cf6',
    kem: [
      { algo: 'ML-KEM-768',  status: 'conditional', note: 'MUST be used in hybrid — pure ML-KEM not yet approved by ANSSI' },
      { algo: 'ML-KEM-512',  status: 'conditional', note: 'Hybrid only with ECDH P-256 or X25519' },
      { algo: 'ML-KEM-1024', status: 'conditional', note: 'Hybrid only — transitional use' },
      { algo: 'X25519',      status: 'required',    note: 'Classical component of mandatory hybrid key exchange' },
      { algo: 'ECDH P-256',  status: 'required',    note: 'Primary classical hybrid component' },
      { algo: 'ECDH P-384',  status: 'approved',    note: 'Higher-security classical hybrid option' },
    ],
    sig: [
      { algo: 'ML-DSA-44',    status: 'conditional', note: 'Hybrid use only — pair with ECDSA P-256' },
      { algo: 'ML-DSA-65',    status: 'conditional', note: 'Hybrid only — preferred over ML-DSA-44 when feasible' },
      { algo: 'ML-DSA-87',    status: 'na',          note: 'Not specifically addressed — ANSSI recommends caution on newer parameter sets' },
      { algo: 'ECDSA P-256',  status: 'required',    note: 'Required as classical co-signature in all hybrid signature schemes' },
      { algo: 'ECDSA P-384',  status: 'approved',    note: 'Higher-security classical co-signature' },
      { algo: 'SLH-DSA-256s', status: 'na',          note: 'Not evaluated — ANSSI is conservative on new schemes' },
    ],
    hash: [
      { algo: 'SHA-256', status: 'approved' },
      { algo: 'SHA-384', status: 'recommended' },
      { algo: 'SHA-512', status: 'approved' },
    ],
    symmetric: [
      { algo: 'AES-128', status: 'approved' },
      { algo: 'AES-256', status: 'recommended' },
    ],
    description: 'Most conservative Western agency stance — hybrid is MANDATORY, pure PQC is not yet approved. Rationale: if the PQC algorithm is broken, the classical component preserves security. ANSSI argues insufficient cryptanalysis time has elapsed. Designed for French operators of vital importance (OIV) and critical security operators (OSE). Likely to relax hybrid requirement as NIST standards mature.',
  },
  {
    id: 'hybrid-tls', name: 'ETSI / IETF Hybrid TLS', shortName: 'Hybrid TLS',
    issuer: 'ETSI / IETF', jurisdiction: 'International', year: 2024,
    audience: 'Web/TLS implementers, browser vendors, CDN operators',
    level: 'recommended', hybrid: 'required', color: '#22d3ee',
    kem: [
      { algo: 'X25519+ML-KEM-768',  status: 'required',    note: 'X25519MLKEM768 — primary hybrid group; deployed in Chrome 131+, Cloudflare, AWS' },
      { algo: 'P-256+ML-KEM-768',   status: 'approved',    note: 'SecP256r1MLKEM768 — enterprise/corporate PKI environments' },
      { algo: 'P-384+ML-KEM-1024',  status: 'approved',    note: 'High-security variant for government/defense TLS' },
      { algo: 'ML-KEM-768',         status: 'conditional', note: 'Pure PQC TLS groups in draft — not yet widely deployed' },
      { algo: 'ML-KEM-1024',        status: 'conditional', note: 'Pure PQC high-security TLS — experimental' },
    ],
    sig: [
      { algo: 'ECDSA P-256',  status: 'required',    note: 'Standard X.509 cert auth — PQC TLS still uses classical certificates today' },
      { algo: 'ECDSA P-384',  status: 'approved',    note: 'Higher-security classical cert signature' },
      { algo: 'RSA-2048',     status: 'conditional', note: 'Legacy acceptable; EC preferred for new deployments' },
      { algo: 'ML-DSA-65',    status: 'conditional', note: 'Draft PQC X.509 certs (draft-ietf-lamps-dilithium-certificates) — not yet widely trusted' },
      { algo: 'ML-DSA-87',    status: 'conditional', note: 'Draft PQC X.509 certs — not yet in browser trust stores' },
    ],
    hash: [
      { algo: 'SHA-256', status: 'required',    note: 'Standard for TLS_AES_128_GCM_SHA256 — primary cipher suite' },
      { algo: 'SHA-384', status: 'approved',    note: 'Used with P-384 and TLS_AES_256_GCM_SHA384' },
    ],
    symmetric: [
      { algo: 'AES-128',   status: 'required',  note: 'TLS_AES_128_GCM_SHA256 — primary TLS 1.3 cipher suite' },
      { algo: 'AES-256',   status: 'approved',  note: 'TLS_AES_256_GCM_SHA384 — high security option' },
    ],
    description: 'The practical internet deployment profile. TLS 1.3 key exchange uses hybrid classical ECDH + ML-KEM (X25519MLKEM768). Authentication still uses classical X.509 — PQC certificate standards are in draft. This is what Chrome 131+, Firefox, Cloudflare, and AWS deploy today. Provides quantum-resistant key exchange while certificate authentication catches up.',
    timelines: 'X25519MLKEM768 in Chrome 131+ (Nov 2024)  ·  Firefox 132+ (Oct 2024)  ·  Cloudflare all edges',
  },
  {
    id: 'nato', name: 'NATO Quantum Tech Strategy', shortName: 'NATO',
    issuer: 'NATO', jurisdiction: 'International', year: 2023,
    audience: 'NATO member nations, alliance military C2 systems, defense contractors',
    level: 'mandatory', hybrid: 'recommended', color: '#60a5fa',
    kem: [
      { algo: 'ML-KEM-1024', status: 'required',    note: 'Required for SECRET and above NATO communications' },
      { algo: 'ML-KEM-768',  status: 'approved',    note: 'Acceptable for UNCLASSIFIED/lower classification systems' },
      { algo: 'ML-KEM-512',  status: 'na',          note: 'Not addressed — assumed insufficient for NATO classified systems' },
      { algo: 'ECDH P-384',  status: 'recommended', note: 'Classical hybrid component — aligned with CNSA 2.0 for US/NATO interoperability' },
    ],
    sig: [
      { algo: 'ML-DSA-87',    status: 'required',    note: 'Required for authentication of classified NATO communications' },
      { algo: 'ML-DSA-65',    status: 'approved',    note: 'Acceptable for lower-classification or administrative systems' },
      { algo: 'SLH-DSA-256s', status: 'approved',    note: 'Backup algorithm for algorithm diversity / contingency' },
      { algo: 'ECDSA P-384',  status: 'recommended', note: 'Classical hybrid co-signature during transition — CNSA 2.0 aligned' },
      { algo: 'ML-DSA-44',    status: 'na',          note: 'Not addressed for NATO classified systems' },
    ],
    hash: [
      { algo: 'SHA-384', status: 'required',  note: 'Minimum for classified NATO systems' },
      { algo: 'SHA-512', status: 'approved' },
      { algo: 'SHA-256', status: 'na',        note: 'Below minimum for NATO classified — may be used for non-sensitive admin' },
    ],
    symmetric: [
      { algo: 'AES-256', status: 'required', note: 'Required for all NATO classified encrypted data' },
      { algo: 'AES-128', status: 'na',       note: 'Below minimum for classified NATO' },
    ],
    description: 'NATO quantum strategy closely aligned with US CNSA 2.0 to ensure cryptographic interoperability on classified networks. Member nations expected to align national migration timelines. Critical for protecting key management infrastructure, strategic communications, and command-and-control systems against adversary quantum capabilities.',
    timelines: 'Most systems: 2025–2030 transition window aligned with CNSA 2.0',
  },
]

// Matrix algorithm columns for comparison view
const MATRIX_COLS: { label: string; category: 'KEM' | 'SIG' | 'HASH' | 'SYM'; algo: string }[] = [
  { category: 'KEM',  label: 'ML-KEM-512',         algo: 'ML-KEM-512' },
  { category: 'KEM',  label: 'ML-KEM-768',         algo: 'ML-KEM-768' },
  { category: 'KEM',  label: 'ML-KEM-1024',        algo: 'ML-KEM-1024' },
  { category: 'KEM',  label: 'X25519+KEM-768',     algo: 'X25519+ML-KEM-768' },
  { category: 'SIG',  label: 'ML-DSA-44',          algo: 'ML-DSA-44' },
  { category: 'SIG',  label: 'ML-DSA-65',          algo: 'ML-DSA-65' },
  { category: 'SIG',  label: 'ML-DSA-87',          algo: 'ML-DSA-87' },
  { category: 'SIG',  label: 'SLH-DSA-256s',       algo: 'SLH-DSA-256s' },
  { category: 'SIG',  label: 'ECDSA P-384',        algo: 'ECDSA P-384' },
  { category: 'HASH', label: 'SHA-256',             algo: 'SHA-256' },
  { category: 'HASH', label: 'SHA-384',             algo: 'SHA-384' },
  { category: 'SYM',  label: 'AES-128',            algo: 'AES-128' },
  { category: 'SYM',  label: 'AES-256',            algo: 'AES-256' },
]

function lookupStatus(profile: ComplianceProfile, algoName: string): { status: AlgoStatus; note?: string } {
  const all = [...profile.kem, ...profile.sig, ...profile.hash, ...profile.symmetric]
  const match = all.find(r => r.algo === algoName)
  return { status: match?.status ?? 'na', note: match?.note }
}

const CAT_COLORS: Record<string, string> = { KEM: '#22d3ee', SIG: '#a78bfa', HASH: '#fbbf24', SYM: '#4ade80' }

// ─── Security Setup Wizard ────────────────────────────────────────────────────

type WizardScenario    = 'website' | 'government' | 'defense' | 'financial' | 'healthcare' | 'infra' | 'developer' | 'iot'
type WizardSensitivity = 'basic' | 'sensitive' | 'critical' | 'classified'
type WizardLongevity   = 'short' | 'medium' | 'long'

const WIZARD_SCENARIOS: { id: WizardScenario; icon: string; label: string; desc: string }[] = [
  { id: 'website',    icon: '🌐', label: 'Website or App',            desc: 'You run a public website, web app, or API' },
  { id: 'government', icon: '🏛', label: 'Government Agency',         desc: 'You work for a federal, state, or local government' },
  { id: 'defense',    icon: '🛡', label: 'Defense / Intelligence',    desc: 'You work with classified systems or defense contractors' },
  { id: 'financial',  icon: '💰', label: 'Finance / Banking',         desc: 'You protect financial transactions or account records' },
  { id: 'healthcare', icon: '🏥', label: 'Healthcare',                desc: 'You handle patient records or medical devices' },
  { id: 'infra',      icon: '⚡', label: 'Critical Infrastructure',   desc: 'You operate power, water, transport, or telecom systems' },
  { id: 'developer',  icon: '💻', label: 'Software Developer',        desc: 'You build software, sign code, or ship software packages' },
  { id: 'iot',        icon: '📡', label: 'Connected Devices / IoT',   desc: 'You build hardware with limited memory or compute power' },
]

const WIZARD_SENSITIVITIES: { id: WizardSensitivity; label: string; desc: string; color: string }[] = [
  { id: 'basic',      color: '#22d3ee', label: 'General business',    desc: 'Mildly sensitive — inconvenient but not catastrophic if leaked' },
  { id: 'sensitive',  color: '#60a5fa', label: 'Personal or private', desc: 'Personal data, financial records, or business secrets' },
  { id: 'critical',   color: '#fbbf24', label: 'High value target',   desc: 'Exposure could harm people, cost millions, or hand adversaries an advantage' },
  { id: 'classified', color: '#ef4444', label: 'Classified / Secret', desc: 'National security information — exposure could threaten lives or state interests' },
]

const WIZARD_LONGEVITIES: { id: WizardLongevity; label: string; years: string; desc: string }[] = [
  { id: 'short',  label: 'Short-lived',  years: 'Under 5 years', desc: 'Session tokens, daily transactions, short-lived certs' },
  { id: 'medium', label: 'Medium-term',  years: '5–20 years',    desc: 'Medical records, contracts, financial history' },
  { id: 'long',   label: 'Long-term',    years: '20+ years',     desc: 'Infrastructure keys, government archives, strategic plans' },
]

interface WizardAction { title: string; detail: string; algo?: string }
interface WizardResult {
  profileName: string; profileId: string; color: string
  headline: string; subhead: string
  urgency: 'now' | 'soon' | 'plan'
  harvestWarning: boolean
  actions: WizardAction[]
  frameworks: string[]
}

function getWizardResult(s: WizardScenario, sens: WizardSensitivity, lon: WizardLongevity): WizardResult {
  // Defense / classified — always CNSA 2.0
  if (s === 'defense' || sens === 'classified') return {
    profileName: 'CNSA 2.0', profileId: 'cnsa2', color: '#ef4444',
    headline: 'You need the strongest available protection — and it\'s already required by law.',
    subhead: 'NSA CNSA 2.0 applies to all national security systems. This isn\'t optional.',
    urgency: 'now', harvestWarning: true, frameworks: ['cnsa2', 'nato'],
    actions: [
      { title: 'Use ML-KEM-1024 for secure connections', detail: 'Think of this as the "quantum-proof lock" for your communications. The NSA requires the 1024-bit version specifically — smaller versions are not acceptable for classified work.', algo: 'ML-KEM-1024' },
      { title: 'Replace all signatures with ML-DSA-87', detail: 'Anywhere you currently use RSA or ECDSA to prove a document or message is authentic, you need to switch to ML-DSA-87. The -87 variant is mandatory for national security systems.', algo: 'ML-DSA-87' },
      { title: 'All stored data must use AES-256', detail: 'AES-128 is no longer acceptable. A quantum computer cuts AES-128\'s effective strength in half, making it too weak for classified data. AES-256 remains secure.' },
      { title: 'You have a migration deadline — it\'s now', detail: 'CNSA 2.0 sets firm dates: software updated by 2025, network equipment by 2026, operating systems by 2027. Missing these is a legal and compliance risk, not just a technical one.' },
    ],
  }

  // IoT / constrained devices
  if (s === 'iot') return {
    profileName: 'NIST FIPS (Lightweight)', profileId: 'nist-fips', color: '#22d3ee',
    headline: 'Tiny devices have purpose-built options — don\'t try to squeeze full-size algorithms onto small hardware.',
    subhead: 'NIST has lightweight-friendly options that work on constrained hardware.',
    urgency: 'plan', harvestWarning: lon !== 'short', frameworks: ['nist-fips'],
    actions: [
      { title: 'Use ML-KEM-512 for key exchange on constrained devices', detail: 'The smallest NIST-approved quantum-safe option. Designed for devices with limited memory where the larger variants won\'t fit. Still genuinely secure.', algo: 'ML-KEM-512' },
      { title: 'Sign firmware updates with SLH-DSA', detail: 'Hash-based signatures are ideal for devices — they are extremely conservative and don\'t rely on the same math as most other PQC algorithms. Great choice when you can\'t easily update a device later.', algo: 'SLH-DSA-128f' },
      { title: 'Build in the ability to swap algorithms', detail: 'The most important thing for IoT is designing your update mechanism so you can change the algorithm later without replacing the hardware. This is called "crypto-agility".' },
      { title: 'NIST is still finalizing IoT-specific profiles', detail: 'The Lightweight Cryptography program continues to develop guidance for highly constrained devices. Keep an eye on NIST IR 8413 updates.' },
    ],
  }

  // Website
  if (s === 'website') {
    const urgent = lon === 'long' || sens === 'critical'
    return {
      profileName: 'Hybrid TLS', profileId: 'hybrid-tls', color: '#22d3ee',
      headline: urgent
        ? 'Sensitive traffic on your site could be harvested today and decrypted once quantum computers arrive.'
        : 'The good news: your users may already be protected if you\'re on a modern CDN. Let\'s verify.',
      subhead: 'Hybrid TLS is the standard for websites today — it combines classical and quantum-safe protection.',
      urgency: urgent ? 'now' : 'soon', harvestWarning: lon !== 'short', frameworks: ['hybrid-tls', 'nist-fips'],
      actions: [
        { title: 'Enable hybrid TLS in your web server or CDN', detail: 'One configuration change protects everyone who visits your site. Cloudflare, AWS, and most CDNs already support this. It\'s called X25519MLKEM768 — ask your hosting provider if it\'s enabled.', algo: 'X25519+ML-KEM-768' },
        { title: 'Make sure you\'re on TLS 1.3', detail: 'Quantum-safe key exchange only works with TLS 1.3. If you\'re still running TLS 1.2 for some connections, those aren\'t protected. Disable TLS 1.2 where you can.' },
        { title: 'Check your CDN\'s settings — you may already be done', detail: 'Cloudflare turned on hybrid TLS by default. If you\'re behind Cloudflare or AWS CloudFront, check your dashboard. Your users may already be quantum-safe.' },
        { title: 'Certificate upgrades are coming but not urgent yet', detail: 'The padlock in your browser (your certificate) still uses classical cryptography. PQC certificate standards are being finalized — expect upgrades in 2026-2027.' },
      ],
    }
  }

  // Government
  if (s === 'government') return {
    profileName: 'NIST FIPS + M-23-02', profileId: 'nist-fips', color: '#3b82f6',
    headline: 'Federal agencies are legally required to migrate. OMB Memo M-23-02 isn\'t a suggestion.',
    subhead: 'The White House mandate requires agencies to inventory and migrate all vulnerable cryptographic systems.',
    urgency: 'now', harvestWarning: true, frameworks: ['nist-fips', 'cnsa2'],
    actions: [
      { title: 'Step 1: Find all systems using old-style cryptography', detail: 'OMB M-23-02 requires every agency to submit an inventory of systems using RSA, ECDH, and other quantum-vulnerable algorithms. You can\'t fix what you haven\'t found.' },
      { title: 'Start with internet-facing systems using ML-KEM-768', detail: 'Public-facing government websites and APIs are the highest-risk targets because attackers can harvest encrypted traffic right now. Upgrade these first.', algo: 'ML-KEM-768' },
      { title: 'Switch document and communication signatures to ML-DSA-65', detail: 'Any system that digitally signs documents, emails, or code needs to move away from RSA/ECDSA. ML-DSA-65 is the general-purpose federal recommendation.', algo: 'ML-DSA-65' },
      { title: 'File your CISA progress report', detail: 'M-23-02 requires annual migration progress reports to OMB and CISA. Your agency\'s compliance standing depends on documented progress, not just technical work.' },
    ],
  }

  // Developer
  if (s === 'developer') return {
    profileName: 'NIST FIPS', profileId: 'nist-fips', color: '#a78bfa',
    headline: 'Code signing is an overlooked attack surface — a forged signature could let attackers ship malware as a legitimate update.',
    subhead: 'Protecting the software supply chain means protecting your signing keys and build pipelines.',
    urgency: lon === 'short' ? 'plan' : 'soon', harvestWarning: lon === 'long', frameworks: ['nist-fips'],
    actions: [
      { title: 'Sign releases with SLH-DSA for maximum durability', detail: 'Hash-based signatures are the most conservative PQC option for code signing. They don\'t rely on the same mathematical problems as other algorithms, so even if other PQC schemes are later broken, SLH-DSA signatures remain valid.', algo: 'SLH-DSA-128s' },
      { title: 'Or use ML-DSA-65 for fast CI/CD pipelines', detail: 'If your build pipeline signs hundreds of artifacts per minute, ML-DSA-65 is much faster than hash-based options and still quantum-safe. Already supported in OpenSSL 3.x and BouncyCastle.', algo: 'ML-DSA-65' },
      { title: 'Dual-sign during the transition period', detail: 'Sign with both your old key (RSA/ECDSA) and a new PQC key simultaneously. Users who haven\'t upgraded yet still validate the classical signature, while forward-looking clients can validate the PQC one.' },
      { title: 'Upgrade download URLs and package feeds to hybrid TLS', detail: 'If your CI/CD downloads dependencies, those connections should use hybrid TLS too. Supply chain attacks start in transit before code is ever signed.' },
    ],
  }

  // Financial
  if (s === 'financial') return {
    profileName: 'NIST FIPS + Hybrid TLS', profileId: 'nist-fips', color: '#4ade80',
    headline: 'Financial data is one of the top harvest-now-decrypt-later targets. Attackers are collecting encrypted transactions today.',
    subhead: 'The good news: the first step is an easy server-side change that protects all transactions in transit.',
    urgency: 'now', harvestWarning: true, frameworks: ['nist-fips', 'hybrid-tls'],
    actions: [
      { title: 'Enable hybrid TLS on all customer-facing systems right now', detail: 'Transaction data being sent today could be decrypted in 5-10 years once quantum computers arrive. Enabling X25519MLKEM768 on your web servers and APIs is a single config change that protects all future traffic.', algo: 'X25519+ML-KEM-768' },
      { title: 'Use AES-256 for all stored financial records', detail: 'A quantum computer cuts AES-128 down to roughly 64-bit equivalent strength — too weak for financial records that may need protection for decades. AES-256 stays secure against quantum attacks.' },
      { title: 'Plan ML-KEM-768 for inter-system and API connections', detail: 'Internal connections between systems — payment processors, core banking APIs, settlement networks — need the same upgrade. ML-KEM-768 is the recommended starting point.', algo: 'ML-KEM-768' },
      { title: 'Track FFIEC and PCI DSS guidance', detail: 'PCI DSS v4 addresses cryptographic agility. FFIEC is actively developing quantum guidance. Your compliance deadlines will arrive faster than most tech teams expect.' },
    ],
  }

  // Healthcare
  if (s === 'healthcare') return {
    profileName: 'NIST FIPS', profileId: 'nist-fips', color: '#34d399',
    headline: 'Patient records have a 75+ year sensitivity window. A record harvested today could be decrypted well within your patient\'s lifetime.',
    subhead: 'Healthcare data has some of the longest effective confidentiality requirements of any industry.',
    urgency: 'soon', harvestWarning: lon !== 'short', frameworks: ['nist-fips'],
    actions: [
      { title: 'Protect EHR systems in transit with hybrid TLS first', detail: 'Electronic health records being transmitted are the highest-priority target. Hybrid TLS can be deployed at your load balancer or CDN without touching your medical software.', algo: 'X25519+ML-KEM-768' },
      { title: 'Re-encrypt long-term patient archives with AES-256', detail: 'Any patient record stored for decades needs to be re-encrypted under AES-256. Records encrypted with AES-128 today may be at risk before they\'re eligible for deletion.' },
      { title: 'Medical device firmware signing with SLH-DSA', detail: 'Implantable and networked devices that can\'t easily be replaced need especially durable cryptography. SLH-DSA\'s conservative design is ideal for firmware that will live in a device for 10+ years.', algo: 'SLH-DSA-128s' },
      { title: 'Watch for HIPAA Security Rule PQC updates', detail: 'HHS has flagged quantum computing as an emerging threat. HIPAA Security Rule updates mandating PQC timelines are expected. Getting ahead of the mandate protects both patients and your compliance standing.' },
    ],
  }

  // Critical infrastructure (default)
  return {
    profileName: 'NIST FIPS + CISA Advisory', profileId: 'nist-fips', color: '#fb923c',
    headline: 'Critical infrastructure is specifically named in joint CISA/NSA/NIST guidance as a top harvest-now-decrypt-later target.',
    subhead: 'Adversaries are actively probing and collecting encrypted OT/IT traffic from infrastructure operators right now.',
    urgency: 'now', harvestWarning: true, frameworks: ['nist-fips', 'cnsa2'],
    actions: [
      { title: 'Read CISA Advisory AA23-245A — it\'s written for you', detail: 'The joint CISA/NSA/NIST advisory is specifically about critical infrastructure. It is readable and actionable, not just a technical spec. Start there.' },
      { title: 'Protect OT/IT boundaries with ML-KEM-768 first', detail: 'The connections between your operational technology (industrial control systems) and your IT networks are the highest-priority upgrade target. ML-KEM-768 is the starting recommendation for these connections.', algo: 'ML-KEM-768' },
      { title: 'Build crypto-agility into every system you update', detail: 'Industrial systems that are hard to patch are the biggest risk. Every time you update a system, add the ability to swap cryptographic algorithms later without hardware replacement.' },
      { title: 'Contact your sector ISAC', detail: 'Every critical infrastructure sector (energy, water, transportation) has an Information Sharing and Analysis Center with sector-specific PQC guidance. They have resources specific to your operational environment.' },
    ],
  }
}

function KnowledgeBase() {
  const navigate   = useNavigate()
  const location   = useLocation()
  const kbSection  = pathToKBSection(location.pathname)
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null)
  const [selectedProto, setSelectedProto] = useState<string | null>(null)
  const [selectedMilestone, setSelectedMilestone] = useState<number | null>(null)
  const [hoveredMilestone, setHoveredMilestone] = useState<number | null>(null)
  const [primFilter,   setPrimFilter]   = useState<'all'|'kx'|'cipher'|'sig'>('all')
  const [primQs,       setPrimQs]       = useState<'all'|'broken'|'weakened'|'safe'>('all')
  const [primRec,      setPrimRec]      = useState<'all'|'avoid'|'conditional'|'use'>('all')
  const [primSearch,   setPrimSearch]   = useState('')
  const [primSortCol,  setPrimSortCol]  = useState<string>('')
  const [primSortDir,  setPrimSortDir]  = useState<'asc'|'desc'>('asc')
  const [swSearch,     setSwSearch]     = useState('')
  const [swCat,        setSwCat]        = useState('all')
  const [swStatus,     setSwStatus]     = useState<'all'|'safe'|'unsafe'>('all')
  const [swSortCol,    setSwSortCol]    = useState<string>('status')
  const [swSortDir,    setSwSortDir]    = useState<'asc'|'desc'>('asc')
  const [swSelected,   setSwSelected]   = useState<string | null>(null)
  const [primSelected,    setPrimSelected]    = useState<string | null>(null)
  const [primPopularity,  setPrimPopularity]  = useState<PrimPopularity | null>(null)
  const [softwareData,  setSoftwareData]  = useState<SwEntry[]>([])
  const [swLoading,     setSwLoading]     = useState(false)
  const [protocolData,  setProtocolData]  = useState<ProtocolEntry[]>([])
  const [primitiveData, setPrimitiveData] = useState<PrimitiveEntry[]>([])
  const [caProviders,   setCaProviders]   = useState<CaProvider[]>([])
  const [caSelected,    setCaSelected]    = useState<number | null>(null)
  const [osPlatforms,      setOsPlatforms]      = useState<OsPlatform[]>([])
  const [osSelected,       setOsSelected]       = useState<number | null>(null)
  const [osTypeFilter,     setOsTypeFilter]     = useState<'all'|'desktop'|'server'|'mobile'|'embedded'>('all')
  const [osVendorFilter,   setOsVendorFilter]   = useState<string>('all')
  const [collapsedOsGroups,setCollapsedOsGroups]= useState<Set<string>>(new Set())
  const selectedPost  = POSTS.find(p => p.slug === selectedSlug) ?? null

  useEffect(() => {
    if (kbSection !== 'software') return
    if (softwareData.length > 0) return  // already loaded
    setSwLoading(true)
    fetch(`${API_BASE}/v1/software`)
      .then(r => r.json())
      .then((data: SwEntry[]) => { setSoftwareData(data); setSwLoading(false) })
      .catch(() => setSwLoading(false))
  }, [kbSection])

  // Protocols: fetch when protocols section is active
  useEffect(() => {
    if (kbSection !== 'protocols') return
    if (protocolData.length > 0) return
    fetch(`${API_BASE}/v1/protocols`)
      .then(r => r.json())
      .then((data: ProtocolEntry[]) => setProtocolData(data))
      .catch(() => {})
  }, [kbSection])

  // Primitives: fetch when primitives section is active
  useEffect(() => {
    if (kbSection !== 'primitives') return
    if (primitiveData.length > 0) return
    fetch(`${API_BASE}/v1/primitives`)
      .then(r => r.json())
      .then((data: PrimitiveEntry[]) => setPrimitiveData(data))
      .catch(() => {})
  }, [kbSection])

  // Primitives popularity: fetch live scan stats
  useEffect(() => {
    if (kbSection !== 'primitives') return
    if (primPopularity) return
    fetch(`${API_BASE}/v1/primitives/popularity`)
      .then(r => r.json())
      .then((data: PrimPopularity) => setPrimPopularity(data))
      .catch(() => {})
  }, [kbSection])

  // CA / PKI: fetch when capki section is active
  useEffect(() => {
    if (kbSection !== 'capki') return
    if (caProviders.length > 0) return
    fetch(`${API_BASE}/v1/ca-providers`)
      .then(r => r.json())
      .then((data: CaProvider[]) => setCaProviders(data))
      .catch(() => {})
  }, [kbSection])

  // OS Platforms: fetch when os section is active
  useEffect(() => {
    if (kbSection !== 'os') return
    if (osPlatforms.length > 0) return
    fetch(`${API_BASE}/v1/os-platforms`)
      .then(r => r.json())
      .then((data: OsPlatform[]) => setOsPlatforms(data))
      .catch(() => {})
  }, [kbSection])

  // Laws & Mandates: fetch when laws section is active
  const [mandates,       setMandates]       = useState<Mandate[]>([])
  const [mandatesLoading,setMandatesLoading]= useState(false)
  const [lawsJurFilter,  setLawsJurFilter]  = useState('All')
  const [lawsTypeFilter, setLawsTypeFilter] = useState('All')
  useEffect(() => {
    if (kbSection !== 'laws') return
    if (mandates.length > 0) return
    setMandatesLoading(true)
    fetch(`${API_BASE}/v1/mandates`)
      .then(r => r.json())
      .then((data: Mandate[]) => { setMandates(data); setMandatesLoading(false) })
      .catch(() => setMandatesLoading(false))
  }, [kbSection]) // eslint-disable-line react-hooks/exhaustive-deps

  // Compliance profiles: pure static data, no fetch needed
  const [complianceSelected, setComplianceSelected] = useState<string>(COMPLIANCE_PROFILES[0].id)
  const [complianceView, setComplianceView]         = useState<'profile' | 'matrix'>('profile')
  const [matrixHover, setMatrixHover]               = useState<{ row: string; col: string } | null>(null)

  // Setup wizard
  const [wizardStep,        setWizardStep]        = useState<0|1|2>(0)
  const [wizardScenario,    setWizardScenario]    = useState<WizardScenario | null>(null)
  const [wizardSensitivity, setWizardSensitivity] = useState<WizardSensitivity | null>(null)
  const [wizardLongevity,   setWizardLongevity]   = useState<WizardLongevity | null>(null)
  function wizardReset() { setWizardStep(0); setWizardScenario(null); setWizardSensitivity(null); setWizardLongevity(null) }

  const activeSoftware   = softwareData.length  > 0 ? softwareData  : SOFTWARE_DATA   // TODO: remove SOFTWARE_DATA fallback after DB migration verified
  const activeProtocols  = protocolData.length  > 0 ? protocolData  : PROTOCOL_DATA   // TODO: remove after DB migration verified
  const activePrimitives = primitiveData.length > 0 ? primitiveData : PRIMITIVE_DATA  // TODO: remove after DB migration verified
  const selectedProtoData = activeProtocols.find(p => p.id === selectedProto) ?? null
  const selectedPrimEntry = activePrimitives.find(p => p.name === primSelected) ?? null

  const KB_SECTIONS = [
    { id: 'briefings'  as KBSection, label: 'INTELLIGENCE', icon: '📡' },
    { id: 'threat'     as KBSection, label: 'QUANTUM THREAT', icon: '⚠' },
    { id: 'protocols'  as KBSection, label: 'PROTOCOLS', icon: '◈' },
    { id: 'primitives' as KBSection, label: 'CRYPTO PRIMITIVES', icon: '⬡' },
    { id: 'software'   as KBSection, label: 'SOFTWARE REGISTRY', icon: '⊙' },
    { id: 'capki'      as KBSection, label: 'CA / PKI', icon: '⎔' },
    { id: 'os'         as KBSection, label: 'OS PLATFORMS', icon: '⊞' },
    { id: 'laws'       as KBSection, label: 'LAWS & MANDATES',        icon: '⚖' },
    { id: 'compliance' as KBSection, label: 'COMPLIANCE PROFILES',    icon: '◉' },
    { id: 'wizard'     as KBSection, label: 'SETUP WIZARD',           icon: '◈' },
  ]

  const riskColor = (r: string) =>
    r === 'critical' ? '#ff4d4d' : r === 'high' ? '#ff8c42' : r === 'medium' ? '#ffcc00' : '#34d399'
  const safeColor = (s: string) =>
    s === 'broken' ? '#ff4d4d' : s === 'weakened' ? '#ffcc00' : '#34d399'
  const safeLabel = (s: string) =>
    s === 'broken' ? '✕ BROKEN' : s === 'weakened' ? '⚠ WEAKENED' : '◈ SAFE'
  const recColor = (r: string) =>
    r === 'avoid' ? '#ff4d4d' : r === 'conditional' ? '#ffcc00' : '#34d399'
  const recLabel = (r: string) =>
    r === 'avoid' ? 'AVOID' : r === 'conditional' ? 'CONDITIONAL' : 'RECOMMENDED'

  const primSearchLc = primSearch.toLowerCase()
  const filteredPrimitives = (() => {
    const QS_ORDER: Record<string,number> = { broken: 0, weakened: 1, safe: 2 }
    const REC_ORDER: Record<string,number> = { avoid: 0, conditional: 1, use: 2 }
    const CAT_ORDER: Record<string,number> = { kx: 0, cipher: 1, sig: 2 }
    const base = activePrimitives.filter(p => {
      if (primFilter !== 'all' && p.category !== primFilter) return false
      if (primQs     !== 'all' && p.quantumSafe !== primQs)  return false
      if (primRec    !== 'all' && p.recommendation !== primRec) return false
      if (primSearchLc && !p.name.toLowerCase().includes(primSearchLc) &&
          !p.type.toLowerCase().includes(primSearchLc) &&
          !p.subcategory.toLowerCase().includes(primSearchLc) &&
          !p.standard.toLowerCase().includes(primSearchLc)) return false
      return true
    })
    if (!primSortCol) return base
    const dir = primSortDir === 'asc' ? 1 : -1
    return [...base].sort((a, b) => {
      switch (primSortCol) {
        case 'name':        return dir * a.name.localeCompare(b.name)
        case 'type':        return dir * a.type.localeCompare(b.type)
        case 'category':    return dir * (CAT_ORDER[a.category] - CAT_ORDER[b.category])
        case 'keySize':     return dir * a.keySize.localeCompare(b.keySize)
        case 'classicBits': return dir * (a.classicBits - b.classicBits)
        case 'quantumBits': return dir * (a.quantumBits - b.quantumBits)
        case 'quantumSafe': return dir * (QS_ORDER[a.quantumSafe] - QS_ORDER[b.quantumSafe])
        case 'attackVector':return dir * a.attackVector.localeCompare(b.attackVector)
        case 'recommendation': return dir * (REC_ORDER[a.recommendation] - REC_ORDER[b.recommendation])
        case 'standard':    return dir * a.standard.localeCompare(b.standard)
        default: return 0
      }
    })
  })()

  function togglePrimSort(col: string) {
    if (primSortCol === col) {
      if (primSortDir === 'asc') setPrimSortDir('desc')
      else { setPrimSortCol(''); setPrimSortDir('asc') }
    } else {
      setPrimSortCol(col)
      setPrimSortDir('asc')
    }
  }

  const swSearchLc = swSearch.toLowerCase()
  const filteredSoftware = (() => {
    const STATUS_ORDER: Record<string,number> = { safe: 0, unsafe: 1 }
    const base = activeSoftware.filter(s => {
      if (swStatus !== 'all' && s.status !== swStatus) return false
      if (swCat !== 'all' && s.cat !== swCat) return false
      if (swSearchLc && !s.name.toLowerCase().includes(swSearchLc) &&
          !s.catLabel.toLowerCase().includes(swSearchLc) &&
          !s.detail.toLowerCase().includes(swSearchLc)) return false
      return true
    })
    const dir = swSortDir === 'asc' ? 1 : -1
    return [...base].sort((a, b) => {
      switch (swSortCol) {
        case 'name':    return dir * a.name.localeCompare(b.name)
        case 'cat':     return dir * a.catLabel.localeCompare(b.catLabel) || a.name.localeCompare(b.name)
        case 'version': return dir * a.version.localeCompare(b.version)
        case 'since':   return dir * (a.since || 'zzz').localeCompare(b.since || 'zzz')
        case 'status':  return dir * (STATUS_ORDER[a.status] - STATUS_ORDER[b.status]) || a.name.localeCompare(b.name)
        default: return 0
      }
    })
  })()

  const swSelectedEntry = activeSoftware.find(s => s.name === swSelected) ?? null

  function toggleSwSort(col: string) {
    if (swSortCol === col) {
      if (swSortDir === 'asc') setSwSortDir('desc')
      else { setSwSortCol('status'); setSwSortDir('asc') }
    } else {
      setSwSortCol(col); setSwSortDir('asc')
    }
  }

  return (
    <div className="kb-shell">
      <div className="ac-scanlines" />

      {/* ── Top bar ── */}
      <div className="ac-topbar">
        <span className="ac-topbar-logo">QS // QUANTUM INTELLIGENCE</span>
        <div className="kb-section-tabs">
          {KB_SECTIONS.map(s => (
            <button key={s.id}
              className={`kb-section-tab${kbSection === s.id ? ' kb-section-tab-on' : ''}`}
              onClick={() => { navigate(KB_SECTION_PATHS[s.id]); setSelectedSlug(null); setSelectedProto(null) }}>
              <span className="kb-section-tab-icon">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <div className="kb-body">

        {/* ══════════════════════════════════════════
            BRIEFINGS section
        ══════════════════════════════════════════ */}
        {kbSection === 'briefings' && (
          <div className="kb-sw-cockpit">
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">📡</span>
                <span className="kb-sw-cmd-label">INTELLIGENCE</span>
                <span className="kb-sw-cmd-count-badge">{POSTS.length}<span className="kb-sw-cmd-count-total"> briefings</span></span>
              </div>
            </div>
            <div className="kb-sw-panels">
              <div className="kb-sw-panel-left">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>BRIEFING INDEX<span className="kb-sw-panel-hdr-count">{POSTS.length}</span></div>
                <div className="kb-sw-list">
                  {POSTS.map(post => (
                    <div key={post.slug}
                      className={`kb-sw-list-row kb-sw-list-safe${selectedSlug === post.slug ? ' kb-sw-list-sel' : ''}`}
                      onClick={() => setSelectedSlug(selectedSlug === post.slug ? null : post.slug)}>
                      <span className="kb-sw-list-dot kb-sw-list-dot-safe" />
                      <div className="kb-sw-list-text">
                        <span className="kb-sw-list-name">{post.title}</span>
                        <div className="kb-sw-list-meta">
                          <span className="kb-sw-list-cat" style={{ color: post.tagColor, borderColor: post.tagColor }}>{post.tag}</span>
                          <span className="kb-sw-list-ver">{post.date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="kb-sw-panel-center">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>{selectedPost ? 'BRIEFING' : 'SELECT A BRIEFING'}</div>
                {selectedPost ? (
                  <div className="kb-sw-detail-content">
                    <div className="kb-sw-detail-name">{selectedPost.title}</div>
                    <div className="kb-sw-detail-meta-row">
                      <span className="kb-sw-cat-pill" style={{ color: selectedPost.tagColor, borderColor: selectedPost.tagColor }}>{selectedPost.tag}</span>
                      <span className="kb-sw-detail-ver">{selectedPost.date}</span>
                    </div>
                    <div className="kb-sw-detail-body" style={{ fontStyle: 'italic', marginBottom: 12 }}>{selectedPost.summary}</div>
                    <div className="kb-sw-detail-links-label" style={{ marginBottom: 10 }}>FULL BRIEF</div>
                    {selectedPost.body.map((section: { heading: string; text: string }, i: number) => (
                      <div key={i} style={{ marginBottom: 14 }}>
                        <div className="kb-sw-detail-since-label" style={{ marginBottom: 5 }}>{section.heading}</div>
                        <div className="kb-sw-detail-body">{section.text}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="kb-sw-detail-empty">
                    <div className="kb-sw-detail-empty-icon">📡</div>
                    <div className="kb-sw-detail-empty-text">Select a briefing from the index to read the full intelligence report.</div>
                  </div>
                )}
              </div>

              <div className="kb-sw-panel-right">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>NIST STANDARDS</div>
                <div className="kb-sw-stats-counts">
                  {([
                    { label: 'FIPS 203 ML-KEM',  color: '#34d399' },
                    { label: 'FIPS 204 ML-DSA',  color: '#34d399' },
                    { label: 'FIPS 205 SLH-DSA', color: '#34d399' },
                    { label: 'FIPS 206 FN-DSA',  color: '#34d399' },
                  ] as const).map(s => (
                    <div key={s.label} className="kb-sw-stats-count-row">
                      <span className="kb-sw-stats-count-label" style={{ color: s.color }}>◈ {s.label}</span>
                      <span className="kb-sw-stats-count-val" style={{ color: s.color, fontSize: '0.58rem' }}>ACTIVE</span>
                    </div>
                  ))}
                </div>
                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-cats-label">BRIEFINGS</div>
                <div className="kb-sw-stats-cats">
                  {POSTS.map(post => (
                    <div key={post.slug} className="kb-sw-stats-cat-row" style={{ cursor: 'pointer' }} onClick={() => setSelectedSlug(post.slug)}>
                      <div className="kb-sw-stats-cat-header">
                        <span className="kb-sw-stats-cat-name" style={{ color: post.tagColor }}>{post.tag}</span>
                      </div>
                      <div style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.62rem', color: 'rgba(160,200,255,0.65)', lineHeight: 1.3, marginTop: 1 }}>{post.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            QUANTUM THREAT section
        ══════════════════════════════════════════ */}
        {kbSection === 'threat' && (
          <div className="kb-sw-cockpit">
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">⚠</span>
                <span className="kb-sw-cmd-label">THREAT MATRIX</span>
                <span className="kb-sw-cmd-count-badge">2<span className="kb-sw-cmd-count-total"> quantum algorithms</span></span>
              </div>
              <div className="kb-sw-cmd-sep" />
              <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'rgba(248,113,113,0.7)', letterSpacing: '0.08em' }}>HARVEST-NOW-DECRYPT-LATER RISK ACTIVE</span>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
            <div className="kb-threat-hero">
              <div className="kb-threat-hero-label">THREAT ASSESSMENT</div>
              <h1 className="kb-threat-hero-title">The Quantum Cryptographic Crisis</h1>
              <p className="kb-threat-hero-sub">Two quantum algorithms — Shor's and Grover's — will render the cryptographic foundations of the modern internet obsolete. Understanding the attack surface is the first step toward defense.</p>
            </div>

            <div className="kb-threat-grid">
              {/* Shor's Algorithm */}
              <div className="kb-threat-card kb-threat-critical">
                <div className="kb-threat-card-header">
                  <span className="kb-threat-severity kb-tsev-critical">CRITICAL</span>
                  <span className="kb-threat-algo-name">Shor's Algorithm</span>
                  <span className="kb-threat-year">Peter Shor, 1994</span>
                </div>
                <div className="kb-threat-card-body">
                  <p>Runs in <strong>polynomial time</strong> O(log²N · log log N) on a quantum computer. Solves integer factorization (breaking RSA) and the discrete logarithm problem (breaking ECDH, DSA, ECDSA, Ed25519, X25519). A sufficiently powerful quantum computer running Shor's algorithm would decrypt <strong>all recorded internet traffic retroactively</strong>.</p>
                  <div className="kb-threat-impact">
                    <div className="kb-threat-impact-label">ASYMMETRIC SYSTEMS BROKEN:</div>
                    <div className="kb-threat-impact-tags">
                      {['RSA-1024','RSA-2048','RSA-4096','ECDH / ECDHE','ECDSA','Ed25519','Ed448','DHE','FFDHE','X25519','X448','All elliptic curves','NTRU (pre-PQC)'].map(t => (
                        <span key={t} className="kb-threat-tag kb-ttag-red">{t}</span>
                      ))}
                    </div>
                    <div className="kb-threat-impact-label" style={{ marginTop: 10 }}>REAL-WORLD IMPACT:</div>
                    <div className="kb-threat-impact-tags">
                      {['TLS certificates','HTTPS sessions','SSH host keys','Code signatures','JWT/SAML tokens','PGP email keys','VPN tunnels','PKI root CAs'].map(t => (
                        <span key={t} className="kb-threat-tag kb-ttag-blue">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="kb-threat-math">
                    <span className="kb-threat-math-label">CLASSICAL COMPLEXITY</span>
                    <span className="kb-threat-math-val">O(e^((64/9·n)^1/3 · (log n)^2/3)) — GNFS sub-exponential</span>
                    <span className="kb-threat-math-label">QUANTUM COMPLEXITY</span>
                    <span className="kb-threat-math-val kb-math-red">O(log²N · log log N) — POLYNOMIAL ← security collapses</span>
                    <span className="kb-threat-math-label">HARVEST NOW / DECRYPT LATER</span>
                    <span className="kb-threat-math-val" style={{ color: '#ff8c42' }}>Nation-states are archiving encrypted traffic today to decrypt once a CRQC exists</span>
                  </div>
                  <div className="kb-threat-links">
                    <div className="kb-threat-impact-label">FURTHER READING:</div>
                    <div className="kb-tl-link-list">
                      <a className="kb-tl-link" href="https://arxiv.org/abs/quant-ph/9508027" target="_blank" rel="noreferrer">Shor (1995) — Original paper: "Polynomial-Time Algorithms for Prime Factorization" ↗</a>
                      <a className="kb-tl-link" href="https://csrc.nist.gov/projects/post-quantum-cryptography" target="_blank" rel="noreferrer">NIST Post-Quantum Cryptography Project ↗</a>
                      <a className="kb-tl-link" href="https://cisa.gov/quantum" target="_blank" rel="noreferrer">CISA Post-Quantum Cryptography Initiative ↗</a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Grover's Algorithm */}
              <div className="kb-threat-card kb-threat-medium">
                <div className="kb-threat-card-header">
                  <span className="kb-threat-severity kb-tsev-medium">MODERATE</span>
                  <span className="kb-threat-algo-name">Grover's Algorithm</span>
                  <span className="kb-threat-year">Lov Grover, 1996</span>
                </div>
                <div className="kb-threat-card-body">
                  <p>Provides a <strong>quadratic speedup</strong> for unstructured search across N items, requiring O(√N) evaluations instead of O(N). Effectively halves the bit security of all symmetric ciphers and hash functions. AES-128 becomes AES-64 equivalent. <strong>Mitigation is straightforward: double key sizes.</strong></p>
                  <div className="kb-threat-impact">
                    <div className="kb-threat-impact-label">SYSTEMS WEAKENED (key strength halved):</div>
                    <div className="kb-threat-impact-tags">
                      {['AES-128 → 64-bit equiv.','AES-192 → 96-bit equiv.','SHA-256 → 128-bit equiv.','SHA-1 → broken','MD5 → broken','HMAC-SHA-256','3DES → broken','Argon2 (lower security)'].map(t => (
                        <span key={t} className="kb-threat-tag kb-ttag-yellow">{t}</span>
                      ))}
                    </div>
                    <div className="kb-threat-impact-label" style={{ marginTop: 10 }}>SYSTEMS UNAFFECTED (≥256-bit symmetric):</div>
                    <div className="kb-threat-impact-tags">
                      {['AES-256 (→128-bit equiv.)','ChaCha20-256','SHA-384 (→192-bit)','SHA-512 (→256-bit)','SHA-3-256 (→128-bit)','BLAKE3-256'].map(t => (
                        <span key={t} className="kb-threat-tag kb-ttag-green">{t}</span>
                      ))}
                    </div>
                    <div className="kb-threat-impact-label" style={{ marginTop: 10 }}>REAL-WORLD IMPACT:</div>
                    <div className="kb-threat-impact-tags">
                      {['TLS session keys','Password hashing','HMAC tokens','Disk encryption','Secure channels'].map(t => (
                        <span key={t} className="kb-threat-tag kb-ttag-blue">{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="kb-threat-math">
                    <span className="kb-threat-math-label">CLASSICAL COMPLEXITY</span>
                    <span className="kb-threat-math-val">O(N) — exhaustive brute-force search</span>
                    <span className="kb-threat-math-label">QUANTUM COMPLEXITY</span>
                    <span className="kb-threat-math-val kb-math-yellow">O(√N) — quadratic speedup via amplitude amplification</span>
                    <span className="kb-threat-math-label">MITIGATION</span>
                    <span className="kb-threat-math-val" style={{ color: '#34d399' }}>Use 256-bit symmetric keys (AES-256, SHA-512). No algorithm replacement needed.</span>
                  </div>
                  <div className="kb-threat-links">
                    <div className="kb-threat-impact-label">FURTHER READING:</div>
                    <div className="kb-tl-link-list">
                      <a className="kb-tl-link" href="https://arxiv.org/abs/quant-ph/9605043" target="_blank" rel="noreferrer">Grover (1996) — Original paper: "A Fast Quantum Mechanical Algorithm for Database Search" ↗</a>
                      <a className="kb-tl-link" href="https://nvlpubs.nist.gov/nistpubs/ir/2016/NIST.IR.8105.pdf" target="_blank" rel="noreferrer">NIST IR 8105 — Report on Post-Quantum Cryptography ↗</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline */}
            {(() => {
              const MILESTONES = [
                {
                  year: '1994', icon: '⚛', color: '#ff4d4d', tag: 'THREAT FOUNDATION',
                  label: "Shor's Algorithm Published",
                  detail: "Peter Shor proves quantum computers can factor large integers in polynomial time, instantly breaking RSA and all elliptic-curve cryptography.",
                  full: "Published in 1994 by Peter Shor at Bell Labs, this algorithm demonstrated that a quantum computer can solve integer factorization and the discrete logarithm problem in O(log²N) time — polynomial rather than the classical sub-exponential. This directly breaks every asymmetric cryptosystem in use on the internet. The paper was the seminal event that made post-quantum cryptography an urgent engineering problem.",
                  significance: 'Theoretical basis for the entire PQC migration effort. Every TLS certificate, SSH key, and VPN tunnel is vulnerable.',
                  links: [
                    { label: 'Original paper (arXiv 1994)', url: 'https://arxiv.org/abs/quant-ph/9508027' },
                    { label: 'NIST PQC Background', url: 'https://csrc.nist.gov/projects/post-quantum-cryptography' },
                    { label: 'NSA FAQ on Quantum Computing', url: 'https://media.defense.gov/2021/Aug/04/2002821837/-1/-1/1/Quantum_FAQs_20210804.PDF' },
                  ],
                },
                {
                  year: '1996', icon: '⊗', color: '#ff8c42', tag: 'THREAT FOUNDATION',
                  label: "Grover's Search Algorithm",
                  detail: "Grover's algorithm provides O(√N) quantum search, halving the effective bit-security of all symmetric ciphers and hash functions.",
                  full: "Lov Grover's 1996 algorithm provides a general-purpose quadratic speedup for searching unsorted databases. Applied to cryptography, this means any brute-force attack that classically requires 2^n operations requires only 2^(n/2) on a quantum computer. AES-128 drops to ~64-bit security. The mitigation is straightforward — double key sizes — making this a manageable but mandatory upgrade.",
                  significance: 'Weakens all symmetric cryptography. Mitigation: use AES-256, SHA-384+, ChaCha20-256. No algorithm replacement needed.',
                  links: [
                    { label: 'Grover (1996) — original paper', url: 'https://arxiv.org/abs/quant-ph/9605043' },
                    { label: 'NIST IR 8105 — PQC Report', url: 'https://nvlpubs.nist.gov/nistpubs/ir/2016/NIST.IR.8105.pdf' },
                  ],
                },
                {
                  year: '2016', icon: '◈', color: '#60a5fa', tag: 'STANDARDS',
                  label: 'NIST PQC Competition Opens',
                  detail: 'NIST launches a global competition to standardize quantum-resistant algorithms. 82 candidates submitted by research teams worldwide.',
                  full: "In December 2016, NIST issued a call for proposals for post-quantum cryptographic standards. 82 complete algorithm submissions were received from cryptographers across 25 countries. The competition was structured in rounds: 69 candidates advanced to Round 1 (2017), 26 to Round 2 (2019), 7 finalists + 8 alternates in Round 3 (2020). This was the largest coordinated cryptographic standardization effort in history.",
                  significance: 'The formal response to Shor/Grover. Kicked off a decade-long migration effort affecting every networked device on earth.',
                  links: [
                    { label: 'NIST PQC Competition official page', url: 'https://csrc.nist.gov/projects/post-quantum-cryptography/post-quantum-cryptography-standardization' },
                    { label: 'Round 1 candidate submissions', url: 'https://csrc.nist.gov/projects/post-quantum-cryptography/round-1-submissions' },
                    { label: 'NIST IR 8413 — Status Report Round 3', url: 'https://nvlpubs.nist.gov/nistpubs/ir/2022/NIST.IR.8413.pdf' },
                  ],
                },
                {
                  year: '2019', icon: '⬡', color: '#a78bfa', tag: 'HARDWARE MILESTONE',
                  label: 'Google Quantum Supremacy',
                  detail: "Google's 53-qubit Sycamore processor completes a specific computation in 200 seconds that would take Summit supercomputer ~10,000 years.",
                  full: "Published in Nature in October 2019, Google's Sycamore chip became the first quantum processor to perform a task verifiably beyond classical supercomputer capability. The task — random circuit sampling — was not cryptographically relevant, but it demonstrated that quantum hardware could surpass classical computation in principle. IBM disputed the 10,000-year estimate, suggesting 2.5 days with optimized classical algorithms. Either way, the era of quantum advantage had demonstrably begun.",
                  significance: 'First experimental proof that quantum computers can exceed classical supercomputers. Accelerated global PQC migration urgency.',
                  links: [
                    { label: 'Nature paper — Arute et al. (2019)', url: 'https://www.nature.com/articles/s41586-019-1666-5' },
                    { label: 'Google AI Blog announcement', url: 'https://ai.googleblog.com/2019/10/quantum-supremacy-using-programmable.html' },
                    { label: 'IBM response to Google claim', url: 'https://www.ibm.com/blogs/research/2019/10/on-quantum-supremacy/' },
                  ],
                },
                {
                  year: '2022', icon: '◈', color: '#34d399', tag: 'STANDARDS',
                  label: 'NIST PQC Finalists Selected',
                  detail: 'CRYSTALS-Kyber, CRYSTALS-Dilithium, FALCON, and SPHINCS+ selected. SIKE/SIDH broken by classical attack in 1 hour on a single CPU core.',
                  full: "In July 2022, NIST announced initial selections: CRYSTALS-Kyber for key encapsulation, CRYSTALS-Dilithium and FALCON for digital signatures, and SPHINCS+ as a conservative hash-based alternative. In the same month, researchers Castryck and Decru broke SIKE/SIDH — a supposedly quantum-safe candidate — using a classical polynomial-time attack. This was a stark reminder that novel constructions require years of analysis before deployment.",
                  significance: 'Defined the PQC algorithm landscape. SIKE break proved that "quantum-safe" does not mean "classically safe."',
                  links: [
                    { label: 'NIST selection announcement', url: 'https://www.nist.gov/news-events/news/2022/07/nist-announces-first-four-quantum-resistant-cryptographic-algorithms' },
                    { label: 'SIKE break paper — Castryck & Decru', url: 'https://eprint.iacr.org/2022/975' },
                    { label: 'NIST IR 8413-upd1', url: 'https://nvlpubs.nist.gov/nistpubs/ir/2022/NIST.IR.8413-upd1.pdf' },
                  ],
                },
                {
                  year: '2023', icon: '⬡', color: '#a78bfa', tag: 'HARDWARE MILESTONE',
                  label: 'IBM Condor: 1,121 Qubits',
                  detail: "IBM crosses 1,000 physical qubits. Error correction overhead still too high for cryptographically relevant computation, but hardware scaling is confirmed.",
                  full: "IBM's Condor processor (1,121 qubits, unveiled December 2023) and the Heron processor (133 qubits, record low error rates) demonstrated two complementary scaling strategies. Condor showed raw qubit count; Heron showed quality. IBM's roadmap targets 100,000+ qubits with error correction by 2033. The qubit count itself is not the threat — a fault-tolerant machine breaking RSA-2048 would require ~4,000 logical qubits and millions of physical qubits — but the trajectory is confirmed.",
                  significance: 'Confirmed quantum hardware is scaling on track with projections. Physical qubit count alone does not break crypto — logical qubits with error correction do.',
                  links: [
                    { label: 'IBM Quantum development roadmap', url: 'https://www.ibm.com/quantum/roadmap' },
                    { label: 'IBM Condor announcement', url: 'https://newsroom.ibm.com/2023-12-04-IBM-Debuts-Next-Generation-Quantum-Processor-IBM-Quantum-System-Two' },
                    { label: 'Physical vs logical qubit gap explained', url: 'https://quantumcomputingreport.com/understanding-the-qubit-gap/' },
                  ],
                },
                {
                  year: '2024', icon: '◈', color: '#34d399', tag: 'STANDARDIZED ✓', current: true,
                  label: 'NIST Publishes FIPS 203–206',
                  detail: 'ML-KEM, ML-DSA, FN-DSA, and SLH-DSA officially standardized. Chrome and Cloudflare deploy X25519MLKEM768 in TLS 1.3.',
                  full: "August 2024: NIST published the final FIPS standards — FIPS 203 (ML-KEM), FIPS 204 (ML-DSA), FIPS 205 (SLH-DSA), FIPS 206 (FN-DSA). These are the first federally mandated post-quantum algorithms. Chrome 124 shipped X25519Kyber768 hybrid KEM for TLS 1.3; Cloudflare reported deploying hybrid PQC on over 20% of TLS connections by end of 2024. The TLS migration is ahead of schedule.",
                  significance: 'The transition from research to mandatory deployment. Every new TLS stack must now plan for ML-KEM integration.',
                  links: [
                    { label: 'FIPS 203 — ML-KEM standard (PDF)', url: 'https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.203.pdf' },
                    { label: 'FIPS 204 — ML-DSA standard (PDF)', url: 'https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.204.pdf' },
                    { label: 'Cloudflare PQC deployment report', url: 'https://blog.cloudflare.com/pq-2024/' },
                    { label: 'Chrome PQC TLS announcement', url: 'https://security.googleblog.com/2023/08/towards-tls-for-post-quantum-world.html' },
                  ],
                },
                {
                  year: '2024', icon: '⬡', color: '#fbbf24', tag: 'HARDWARE MILESTONE', current: true,
                  label: 'Google Willow Chip',
                  detail: "Google's Willow demonstrates exponential error suppression below the error-correction threshold — the key unsolved problem in quantum computing for 30 years.",
                  full: "Published in Nature in December 2024, Google's Willow chip (105 qubits) demonstrated that adding more qubits actively reduces error rates — the opposite of what happens in noisy classical scaling. This 'below threshold' operation is the fundamental requirement for fault-tolerant quantum computing. Willow solved a benchmark problem in 5 minutes that would take the fastest supercomputer 10^25 years. Still not cryptographically relevant, but the engineering barrier to fault tolerance has been crossed.",
                  significance: 'Proof that fault-tolerant quantum computing is physically achievable, not just theoretical. Tightens the timeline for CRQC risk.',
                  links: [
                    { label: 'Nature paper — Google Willow (2024)', url: 'https://www.nature.com/articles/s41586-024-08449-y' },
                    { label: 'Google blog — Willow announcement', url: 'https://blog.google/technology/research/google-willow-quantum-chip/' },
                    { label: 'Scott Aaronson analysis', url: 'https://scottaaronson.blog/?p=8329' },
                  ],
                },
                {
                  year: '2025–26', icon: '⚑', color: '#ff8c42', tag: 'MANDATE',
                  label: 'NSA CNSA 2.0 Transition Deadline',
                  detail: 'NSA mandates exclusive use of CNSA 2.0 algorithms for all national security systems. New systems must not use legacy crypto.',
                  full: "The NSA's Commercial National Security Algorithm Suite 2.0 (CNSA 2.0), published September 2022, mandates migration timelines: new NSS software must support CNSA 2.0 by 2025, existing systems must complete migration by 2030–2033. Algorithms required: ML-KEM (key exchange), ML-DSA or LMS/XMSS (signatures), AES-256 (encryption), SHA-384/512 (hashing). RSA and ECDSA are explicitly prohibited for new implementations after 2025.",
                  significance: 'Legal mandate affecting all US defense contractors, intelligence community, and national security infrastructure.',
                  links: [
                    { label: 'NSA CNSA 2.0 fact sheet', url: 'https://media.defense.gov/2022/Sep/07/2003071836/-1/-1/0/CSA_CNSA_2.0_ALGORITHMS_.PDF' },
                    { label: 'NSA PQC FAQ', url: 'https://media.defense.gov/2021/Aug/04/2002821837/-1/-1/1/Quantum_FAQs_20210804.PDF' },
                    { label: 'NIST NCCoE Migration Guide', url: 'https://www.nccoe.nist.gov/crypto-agility-considerations-migrating-post-quantum-cryptographic-standards' },
                  ],
                },
                {
                  year: '2030', icon: '⚑', color: '#ff8c42', tag: 'MANDATE',
                  label: 'CISA Federal Civilian Migration Deadline',
                  detail: 'US CISA mandates PQC migration for all federal civilian agencies. Financial regulators and critical infrastructure expected to follow.',
                  full: "CISA's post-quantum roadmap, aligned with OMB M-23-02, requires federal civilian agencies to complete cryptographic inventory by 2025, prioritize high-risk systems by 2027, and complete full PQC migration by 2030. Financial regulators (OCC, FDIC, SEC) and critical infrastructure sectors (energy, water, healthcare) are publishing parallel guidance. The EU's ENISA has set a 2030 target for European government systems.",
                  significance: 'Extends the mandate to all civilian government and regulated industries. Affects every bank, hospital, utility, and cloud provider operating under US/EU regulation.',
                  links: [
                    { label: 'CISA PQC Initiative', url: 'https://www.cisa.gov/quantum' },
                    { label: 'OMB M-23-02 memo', url: 'https://www.whitehouse.gov/wp-content/uploads/2022/11/M-23-02-M-Memo-on-Migrating-to-Post-Quantum-Cryptography.pdf' },
                    { label: 'ENISA PQC recommendations', url: 'https://www.enisa.europa.eu/publications/post-quantum-cryptography-current-state-and-quantum-mitigation' },
                  ],
                },
                {
                  year: '2035+', icon: '☢', color: '#ff4d4d', tag: 'CRITICAL THREAT',
                  label: 'CRQC Threat Window Opens',
                  detail: "Intelligence estimates place a cryptographically relevant quantum computer within this window. Harvest-now-decrypt-later attacks are active today.",
                  full: "A cryptographically relevant quantum computer (CRQC) capable of breaking RSA-2048 in hours would require approximately 4,000 logical qubits and 4 million physical qubits with error correction. Current best hardware: ~1,000 physical qubits, no logical qubit operation at scale. ODNI, CISA, and UK NCSC consensus estimate: 10–15 years from 2024. The more acute threat is 'harvest now, decrypt later' (HNDL) — nation-state actors archiving today's encrypted traffic to decrypt once a CRQC exists. Long-lived secrets (government, financial, healthcare records) are already compromised in this model.",
                  significance: 'The endpoint of this entire timeline. Every unmitigated RSA/ECDH key in use today is a liability. The cost of inaction compounds annually.',
                  links: [
                    { label: 'ODNI — Annual Threat Assessment 2024', url: 'https://www.odni.gov/files/ODNI/documents/assessments/ATA-2024-Unclassified-Report.pdf' },
                    { label: 'CISA — Known Exploited Vulnerabilities', url: 'https://www.cisa.gov/known-exploited-vulnerabilities-catalog' },
                    { label: 'Harvest-now-decrypt-later: academic survey', url: 'https://arxiv.org/abs/2306.04544' },
                    { label: 'NCSC UK — PQC migration guidance', url: 'https://www.ncsc.gov.uk/whitepaper/preparing-for-quantum-safe-cryptography' },
                  ],
                },
              ]

              const activeMilestone = selectedMilestone !== null ? MILESTONES[selectedMilestone] : null

              return (
                <div className="kb-timeline-section">
                  <div className="kb-threat-section-hdr">⟳ QUANTUM COMPUTING MILESTONES — click any to expand</div>

                  {/* Flyout detail panel */}
                  {activeMilestone && (
                    <div className="kb-mf-backdrop" onClick={() => setSelectedMilestone(null)}>
                      <div className="kb-mf-panel" onClick={e => e.stopPropagation()}>
                        <button className="kb-mf-close" onClick={() => setSelectedMilestone(null)}>✕ CLOSE</button>
                        <div className="kb-mf-year" style={{ color: activeMilestone.color }}>{activeMilestone.year}</div>
                        <div className="kb-mf-tag" style={{ color: activeMilestone.color, borderColor: `${activeMilestone.color}44`, background: `${activeMilestone.color}11` }}>{activeMilestone.tag}</div>
                        <h3 className="kb-mf-title">{activeMilestone.label}</h3>
                        <p className="kb-mf-full">{activeMilestone.full}</p>
                        <div className="kb-mf-sig-label">SIGNIFICANCE</div>
                        <p className="kb-mf-sig">{activeMilestone.significance}</p>
                        <div className="kb-mf-links-label">EVIDENCE & FURTHER READING</div>
                        <div className="kb-mf-links">
                          {activeMilestone.links.map((lk, li) => (
                            <a key={li} className="kb-mf-link" href={lk.url} target="_blank" rel="noreferrer">
                              <span className="kb-mf-link-icon">↗</span>{lk.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="kb-tl2-wrap">
                    <div className="kb-tl2-track" />
                    {MILESTONES.map((m, i) => (
                      <div
                        key={i}
                        className={`kb-tl2-item${(m as any).current ? ' kb-tl2-current' : ''}${hoveredMilestone === i ? ' kb-tl2-hovered' : ''}${selectedMilestone === i ? ' kb-tl2-selected' : ''}`}
                        onClick={() => setSelectedMilestone(selectedMilestone === i ? null : i)}
                        onMouseEnter={() => setHoveredMilestone(i)}
                        onMouseLeave={() => setHoveredMilestone(null)}
                      >
                        <div className="kb-tl2-node" style={{ borderColor: m.color, background: `${m.color}22`, boxShadow: `0 0 16px ${m.color}44` }}>
                          <span className="kb-tl2-icon">{m.icon}</span>
                        </div>
                        <div className="kb-tl2-stem" style={{ background: m.color }} />
                        <div className="kb-tl2-card" style={{ borderColor: `${m.color}44` }}>
                          <div className="kb-tl2-year" style={{ color: m.color }}>{m.year}</div>
                          <div className="kb-tl2-tag" style={{ color: m.color, borderColor: `${m.color}44`, background: `${m.color}11` }}>{m.tag}</div>
                          <div className="kb-tl2-label">{m.label}</div>
                          <div className="kb-tl2-detail">{m.detail}</div>
                          <div className="kb-tl2-expand-hint">CLICK FOR DETAILS ↗</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {/* NIST Standards */}
            <div className="kb-threat-section-hdr">◈ NIST POST-QUANTUM STANDARDS (2024)</div>
            <div className="kb-nist-grid">
              {[
                {
                  fips: 'FIPS 203', name: 'ML-KEM', full: 'Module Lattice Key Encapsulation Mechanism',
                  purpose: 'Key Exchange / KEM', basis: 'MLWE (Module Learning With Errors)',
                  variants: 'ML-KEM-512 (128-bit), ML-KEM-768 (192-bit), ML-KEM-1024 (256-bit)',
                  status: 'FINAL', color: '#34d399',
                  desc: 'Primary standard for post-quantum key exchange. Replaces ECDH and RSA key encapsulation in TLS, SSH, and encrypted messaging. Chrome and Cloudflare deploy X25519MLKEM768 hybrid in TLS 1.3.',
                  deploy: 'Chrome 124+, Cloudflare, AWS KMS, Signal (beta)',
                  link: 'https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.203.pdf',
                },
                {
                  fips: 'FIPS 204', name: 'ML-DSA', full: 'Module Lattice Digital Signature Algorithm',
                  purpose: 'Digital Signatures', basis: 'MLWE + MSIS (Module Short Integer Solution)',
                  variants: 'ML-DSA-44 (128-bit), ML-DSA-65 (192-bit), ML-DSA-87 (256-bit)',
                  status: 'FINAL', color: '#60a5fa',
                  desc: 'Primary standard for post-quantum digital signatures. Replaces ECDSA and RSA signatures in X.509 certificates, code signing, JWTs, and SAML tokens.',
                  deploy: 'OpenSSL 3.5+, AWS, Google Cloud (planned)',
                  link: 'https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.204.pdf',
                },
                {
                  fips: 'FIPS 205', name: 'SLH-DSA', full: 'Stateless Hash-Based Digital Signature Algorithm',
                  purpose: 'Digital Signatures (conservative)', basis: 'SHA-256 / SHAKE-256 hash functions only',
                  variants: 'SLH-DSA-128s/f, SLH-DSA-192s/f, SLH-DSA-256s/f',
                  status: 'FINAL', color: '#a78bfa',
                  desc: 'Conservative hash-based signatures. Security relies solely on hash function assumptions — no lattice math. Large signatures (~50KB) but maximum long-term assurance if lattice assumptions are ever broken.',
                  deploy: 'Bouncy Castle, PQClean library',
                  link: 'https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.205.pdf',
                },
                {
                  fips: 'FIPS 206', name: 'FN-DSA', full: 'Fast Fourier Lattice-based Compact Signatures (FALCON)',
                  purpose: 'Digital Signatures (compact)', basis: 'NTRU Lattice over Z[x]/(φ)',
                  variants: 'FN-DSA-512 (128-bit), FN-DSA-1024 (256-bit)',
                  status: 'FINAL', color: '#fb923c',
                  desc: 'Compact lattice signatures for bandwidth-constrained environments. Signatures are 5–10× smaller than ML-DSA. Complex Gaussian sampling makes side-channel resistance harder to implement correctly.',
                  deploy: 'PQClean, liboqs (Open Quantum Safe)',
                  link: 'https://nvlpubs.nist.gov/nistpubs/FIPS/NIST.FIPS.206.pdf',
                },
              ].map(s => (
                <div key={s.fips} className="kb-nist-card" style={{ '--nc': s.color } as React.CSSProperties}>
                  <div className="kb-nist-top">
                    <div className="kb-nist-fips" style={{ color: s.color }}>{s.fips}</div>
                    <div className="kb-nist-name">{s.name}</div>
                    <div className="kb-nist-full">{s.full}</div>
                    <div className="kb-nist-purpose">{s.purpose}</div>
                    <div className="kb-nist-basis"><em>Basis:</em> {s.basis}</div>
                    <div className="kb-nist-variants"><em>Variants:</em> {s.variants}</div>
                  </div>
                  <div className="kb-nist-desc">{s.desc}</div>
                  <div className="kb-nist-deploy"><em>Deployed in:</em> {s.deploy}</div>
                  <div className="kb-nist-footer">
                    <div className="kb-nist-status" style={{ color: s.color }}>● {s.status}</div>
                    <a className="kb-nist-link" href={s.link} target="_blank" rel="noreferrer">READ FIPS ↗</a>
                  </div>
                </div>
              ))}
            </div>
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════
            PROTOCOLS section
        ══════════════════════════════════════════ */}
        {kbSection === 'protocols' && (() => {
          const criticalCount = activeProtocols.filter(p => p.quantumRisk === 'critical').length
          const highCount     = activeProtocols.filter(p => p.quantumRisk === 'high').length
          const mediumCount   = activeProtocols.filter(p => p.quantumRisk === 'medium').length
          const safeCount     = activeProtocols.filter(p => p.quantumRisk === 'safe').length
          return (
          <div className="kb-sw-cockpit">
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">◈</span>
                <span className="kb-sw-cmd-label">PROTOCOLS</span>
                <span className="kb-sw-cmd-count-badge">{activeProtocols.length}<span className="kb-sw-cmd-count-total"> protocols</span></span>
              </div>
            </div>
            <div className="kb-sw-panels">
              <div className="kb-sw-panel-left">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>PROTOCOL REGISTRY<span className="kb-sw-panel-hdr-count">{activeProtocols.length}</span></div>
                <div className="kb-sw-list">
                  {activeProtocols.map(p => (
                    <div key={p.id}
                      className={`kb-sw-list-row${p.quantumRisk === 'safe' ? ' kb-sw-list-safe' : ' kb-sw-list-unsafe'}${selectedProto === p.id ? ' kb-sw-list-sel' : ''}`}
                      onClick={() => setSelectedProto(selectedProto === p.id ? null : p.id)}>
                      <span className="kb-sw-list-dot" style={{ background: riskColor(p.quantumRisk), flexShrink: 0, boxShadow: `0 0 5px ${riskColor(p.quantumRisk)}88` }} />
                      <div className="kb-sw-list-text">
                        <span className="kb-sw-list-name">{p.name}</span>
                        <div className="kb-sw-list-meta">
                          <span className="kb-sw-list-cat">{p.category}</span>
                          <span className="kb-sw-list-ver" style={{ color: riskColor(p.quantumRisk) }}>{p.quantumRisk.toUpperCase()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="kb-sw-panel-center">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>{selectedProtoData ? selectedProtoData.name : 'SELECT A PROTOCOL'}</div>
                {selectedProtoData ? (
                  <div className="kb-sw-detail-content">
                    <div className="kb-sw-detail-name">{selectedProtoData.name}</div>
                    <div className="kb-sw-detail-meta-row">
                      <span className="kb-sw-cat-pill">{selectedProtoData.category}</span>
                      <span className="kb-sw-detail-status-badge" style={{ color: riskColor(selectedProtoData.quantumRisk), borderColor: `${riskColor(selectedProtoData.quantumRisk)}55`, background: `${riskColor(selectedProtoData.quantumRisk)}11` }}>{selectedProtoData.quantumRisk.toUpperCase()} RISK</span>
                    </div>
                    <div className="kb-sw-detail-body">{selectedProtoData.description}</div>
                    {selectedProtoData.vulnerability && (
                      <>
                        <div className="kb-sw-detail-since-label" style={{ marginTop: 10 }}>QUANTUM VULNERABILITY</div>
                        <div className="kb-sw-detail-body">{selectedProtoData.vulnerability}</div>
                      </>
                    )}
                    {selectedProtoData.brokenBy && selectedProtoData.brokenBy.length > 0 && (
                      <>
                        <div className="kb-sw-detail-since-label" style={{ marginTop: 10 }}>BROKEN BY</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                          {selectedProtoData.brokenBy.map((t: string) => (
                            <span key={t} style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', padding: '2px 7px', borderRadius: 3, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', color: '#f87171' }}>{t}</span>
                          ))}
                        </div>
                      </>
                    )}
                    {selectedProtoData.affectedSystems && selectedProtoData.affectedSystems.length > 0 && (
                      <>
                        <div className="kb-sw-detail-since-label" style={{ marginTop: 10 }}>AFFECTED SYSTEMS</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                          {selectedProtoData.affectedSystems.map((t: string) => (
                            <span key={t} style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.58rem', padding: '2px 7px', borderRadius: 3, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.3)', color: '#60a5fa' }}>{t}</span>
                          ))}
                        </div>
                      </>
                    )}
                    {selectedProtoData.migrationPath && (
                      <>
                        <div className="kb-sw-detail-since-label" style={{ marginTop: 10 }}>MIGRATION PATH</div>
                        <div className="kb-sw-detail-body">{selectedProtoData.migrationPath}</div>
                      </>
                    )}
                    {selectedProtoData.urgency && (
                      <>
                        <div className="kb-sw-detail-since-label" style={{ marginTop: 10, color: riskColor(selectedProtoData.quantumRisk) }}>URGENCY</div>
                        <div className="kb-sw-detail-body" style={{ color: riskColor(selectedProtoData.quantumRisk) }}>{selectedProtoData.urgency}</div>
                      </>
                    )}
                    {selectedProtoData.links && selectedProtoData.links.length > 0 && (
                      <>
                        <div className="kb-sw-detail-links-label">REFERENCES</div>
                        <div className="kb-sw-detail-links">
                          {selectedProtoData.links.map((lk: { label: string; url: string }, li: number) => (
                            <a key={li} className="kb-proto-link" href={lk.url} target="_blank" rel="noreferrer">
                              <span className="kb-proto-link-icon">↗</span>{lk.label}
                            </a>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="kb-sw-detail-empty">
                    <div className="kb-sw-detail-empty-icon">◈</div>
                    <div className="kb-sw-detail-empty-text">Select a protocol to view its quantum vulnerability analysis, affected systems, migration path, and references.</div>
                  </div>
                )}
              </div>

              <div className="kb-sw-panel-right">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>RISK BREAKDOWN</div>
                <div className="kb-sw-stats-donut-wrap">
                  <div className="kb-sw-stats-donut" style={{ background: `conic-gradient(#ff4d4d 0% ${Math.round(criticalCount/activeProtocols.length*100)}%, #ff8c42 ${Math.round(criticalCount/activeProtocols.length*100)}% ${Math.round((criticalCount+highCount)/activeProtocols.length*100)}%, #ffcc00 ${Math.round((criticalCount+highCount)/activeProtocols.length*100)}% ${Math.round((criticalCount+highCount+mediumCount)/activeProtocols.length*100)}%, #34d399 ${Math.round((criticalCount+highCount+mediumCount)/activeProtocols.length*100)}% 100%)` }}>
                    <div className="kb-sw-stats-donut-hole">
                      <span className="kb-sw-stats-donut-pct">{activeProtocols.length}</span>
                      <span className="kb-sw-stats-donut-sub">TOTAL</span>
                    </div>
                  </div>
                </div>
                <div className="kb-sw-stats-counts">
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#ff4d4d' }}>✕ CRITICAL</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#ff4d4d' }}>{criticalCount}</span>
                  </div>
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#ff8c42' }}>⚠ HIGH</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#ff8c42' }}>{highCount}</span>
                  </div>
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#ffcc00' }}>◦ MEDIUM</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#ffcc00' }}>{mediumCount}</span>
                  </div>
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#34d399' }}>◈ SAFE</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#34d399' }}>{safeCount}</span>
                  </div>
                </div>
                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-cats-label">BY CATEGORY</div>
                <div className="kb-sw-stats-cats">
                  {Array.from(new Set(activeProtocols.map(p => p.category))).map(cat => {
                    const catProtos = activeProtocols.filter(p => p.category === cat)
                    const catSafe   = catProtos.filter(p => p.quantumRisk === 'safe').length
                    return (
                      <div key={cat} className="kb-sw-stats-cat-row">
                        <div className="kb-sw-stats-cat-header">
                          <span className="kb-sw-stats-cat-name">{cat}</span>
                          <span className="kb-sw-stats-cat-n">{catSafe}<span className="kb-sw-stats-cat-n-total">/{catProtos.length}</span></span>
                        </div>
                        <div className="kb-sw-stats-cat-bar-wrap">
                          <div className="kb-sw-stats-cat-bar-safe" style={{ width: `${(catSafe/catProtos.length)*100}%` }} />
                          <div className="kb-sw-stats-cat-bar-unsafe" style={{ width: `${((catProtos.length-catSafe)/catProtos.length)*100}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
          )
        })()}

        {/* ══════════════════════════════════════════
            CRYPTO PRIMITIVES section
        ══════════════════════════════════════════ */}
        {kbSection === 'primitives' && (() => {
          const safeCount     = filteredPrimitives.filter(p => p.quantumSafe === 'safe').length
          const weakenedCount = filteredPrimitives.filter(p => p.quantumSafe === 'weakened').length
          const brokenCount   = filteredPrimitives.filter(p => p.quantumSafe === 'broken').length
          const safePercent     = Math.round(safeCount / (filteredPrimitives.length || 1) * 100)
          const weakenedPercent = Math.round(weakenedCount / (filteredPrimitives.length || 1) * 100)
          return (
          <div className="kb-sw-cockpit">

            {/* Command bar */}
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">⬡</span>
                <span className="kb-sw-cmd-label">PRIMITIVES</span>
                <span className="kb-sw-cmd-count-badge">{filteredPrimitives.length}<span className="kb-sw-cmd-count-total">/{activePrimitives.length}</span></span>
              </div>
              <div className="kb-sw-cmd-sep" />
              <input className="kb-sw-cockpit-search" type="text" placeholder="Search..." value={primSearch} onChange={e => setPrimSearch(e.target.value)} />
              <div className="kb-sw-cmd-sep" />
              {([
                { id: 'all', label: 'ALL' }, { id: 'kx', label: 'KEY EX.' }, { id: 'cipher', label: 'CIPHER' }, { id: 'sig', label: 'SIG' },
              ] as const).map(f => (
                <button key={f.id} className={`kb-sw-cockpit-btn ${primFilter === f.id ? 'kb-sw-cockpit-btn-on' : ''}`} onClick={() => setPrimFilter(f.id)}>{f.label}</button>
              ))}
              <div className="kb-sw-cmd-sep" />
              {([
                { id: 'all', label: 'ALL' }, { id: 'safe', label: '◈ SAFE' }, { id: 'weakened', label: '⚠ WEAK' }, { id: 'broken', label: '✕ BROKEN' },
              ] as const).map(f => (
                <button key={f.id} className={`kb-sw-cockpit-btn ${primQs === f.id ? 'kb-sw-cockpit-btn-on' : ''}`} onClick={() => setPrimQs(f.id)}>{f.label}</button>
              ))}
              <div className="kb-sw-cmd-sep" />
              {([
                { id: 'all', label: 'ALL' }, { id: 'use', label: 'USE' }, { id: 'conditional', label: 'COND.' }, { id: 'avoid', label: 'AVOID' },
              ] as const).map(f => (
                <button key={f.id} className={`kb-sw-cockpit-btn ${primRec === f.id ? 'kb-sw-cockpit-btn-on' : ''}`} onClick={() => setPrimRec(f.id)}>{f.label}</button>
              ))}
              {(primSearch || primFilter !== 'all' || primQs !== 'all' || primRec !== 'all') && (
                <>
                  <div className="kb-sw-cmd-sep" />
                  <button className="kb-prim-clear-btn" onClick={() => { setPrimSearch(''); setPrimFilter('all'); setPrimQs('all'); setPrimRec('all') }}>CLEAR</button>
                </>
              )}
              <div className="kb-sw-cmd-sep" />
              <button className="kb-prim-export-btn" onClick={() => {
                const headers = ['Name','Category','Subcategory','Type','Key Size','Classic Bits','Quantum Bits','Quantum Safe','Attack Vector','Recommendation','Standard','Description']
                const rows = filteredPrimitives.map(p => [p.name, p.category, p.subcategory, p.type, p.keySize, p.classicBits, p.quantumBits, p.quantumSafe, p.attackVector, p.recommendation, p.standard, `"${p.description.replace(/"/g,'""')}"`])
                const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n')
                const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv'})); a.download = `pqc-primitives-${new Date().toISOString().slice(0,10)}.csv`; a.click()
              }}>⬇ CSV</button>
            </div>

            {/* Three panels */}
            <div className="kb-sw-panels">

              {/* LEFT — algorithm list */}
              <div className="kb-sw-panel-left">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>ALGORITHMS<span className="kb-sw-panel-hdr-count">{filteredPrimitives.length}</span></div>
                <div className="kb-sw-list">
                  {filteredPrimitives.length === 0 && <div className="kb-sw-list-empty">No matches</div>}
                  {filteredPrimitives.map(p => {
                    const dotStatus = p.quantumSafe === 'safe' ? 'safe' : 'unsafe'
                    const dotColor  = p.quantumSafe === 'safe' ? '#34d399' : p.quantumSafe === 'weakened' ? '#fbbf24' : '#f87171'
                    const pop       = primPopularity?.lookup[p.name]
                    const rankColor = pop?.rank === 1 ? '#f59e0b' : pop?.rank === 2 ? '#94a3b8' : pop?.rank === 3 ? '#b45309' : '#475569'
                    return (
                      <div key={p.name}
                        className={`kb-sw-list-row ${dotStatus === 'safe' ? 'kb-sw-list-safe' : 'kb-sw-list-unsafe'}${primSelected === p.name ? ' kb-sw-list-sel' : ''}`}
                        onClick={() => setPrimSelected(primSelected === p.name ? null : p.name)}>
                        <span className="kb-sw-list-dot" style={{ background: dotColor, boxShadow: `0 0 5px ${dotColor}88`, flexShrink: 0 }} />
                        <div className="kb-sw-list-text">
                          <span className="kb-sw-list-name">{p.name}</span>
                          <div className="kb-sw-list-meta">
                            <span className="kb-sw-list-cat">{p.type}</span>
                            <span className="kb-sw-list-ver">{p.keySize}</span>
                            {pop && (
                              <span style={{
                                fontSize: '8px', padding: '1px 5px', borderRadius: '2px', marginLeft: '4px',
                                fontFamily: 'Space Mono, monospace', letterSpacing: '0.05em',
                                background: `${rankColor}18`, color: rankColor, border: `1px solid ${rankColor}40`,
                              }}>
                                {pop.rank === 1 ? '★ #1' : `#${pop.rank}`}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* CENTER — detail */}
              <div className="kb-sw-panel-center">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>{selectedPrimEntry ? 'ALGORITHM DETAIL' : 'SELECT AN ALGORITHM'}</div>
                {selectedPrimEntry ? (() => {
                  const qc = selectedPrimEntry.quantumSafe === 'safe' ? '#34d399' : selectedPrimEntry.quantumSafe === 'weakened' ? '#fbbf24' : '#f87171'
                  const ql = selectedPrimEntry.quantumSafe === 'safe' ? '◈ QUANTUM SAFE' : selectedPrimEntry.quantumSafe === 'weakened' ? '⚠ WEAKENED' : '✕ BROKEN'
                  const rc = selectedPrimEntry.recommendation === 'use' ? '#34d399' : selectedPrimEntry.recommendation === 'conditional' ? '#fbbf24' : '#f87171'
                  const rl = selectedPrimEntry.recommendation === 'use' ? 'RECOMMENDED' : selectedPrimEntry.recommendation === 'conditional' ? 'CONDITIONAL' : 'AVOID'
                  return (
                  <div className="kb-sw-detail-content">
                    <div className="kb-sw-detail-name">{selectedPrimEntry.name}</div>
                    <div className="kb-sw-detail-meta-row">
                      <span className="kb-sw-cat-pill">{selectedPrimEntry.type}</span>
                      <span className="kb-sw-detail-status-badge" style={{ color: qc, borderColor: `${qc}55`, background: `${qc}11` }}>{ql}</span>
                      <span className="kb-sw-detail-status-badge" style={{ color: rc, borderColor: `${rc}55`, background: `${rc}11` }}>{rl}</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', margin: '12px 0' }}>
                      {[
                        { label: 'KEY SIZE', val: selectedPrimEntry.keySize },
                        { label: 'STANDARD', val: selectedPrimEntry.standard || '—' },
                        { label: 'CLASSIC SECURITY', val: selectedPrimEntry.classicBits > 0 ? `${selectedPrimEntry.classicBits}-bit` : '—' },
                        { label: 'QUANTUM SECURITY', val: selectedPrimEntry.quantumBits > 0 ? `${selectedPrimEntry.quantumBits}-bit` : <strong style={{color:'#f87171'}}>0-bit (broken)</strong> },
                        { label: 'ATTACK VECTOR', val: selectedPrimEntry.attackVector },
                        { label: 'SUBCATEGORY', val: selectedPrimEntry.subcategory },
                      ].map(({ label, val }) => (
                        <div key={label}>
                          <div className="kb-sw-detail-since-label">{label}</div>
                          <div className="kb-sw-detail-since-val">{val}</div>
                        </div>
                      ))}
                    </div>
                    <div className="kb-sw-detail-links-label">DESCRIPTION</div>
                    <div className="kb-sw-detail-body">{selectedPrimEntry.description}</div>

                    {/* Worldwide observations from live scan data */}
                    {(() => {
                      const pop = primPopularity?.lookup[selectedPrimEntry.name]
                      if (!pop) return null
                      const isKx       = pop.category === 'kx'
                      const denominator = isKx ? primPopularity!.total : primPopularity!.totalCerts
                      const pct         = denominator > 0 ? (pop.count / denominator * 100) : 0
                      const rankColor   = pop.rank === 1 ? '#f59e0b' : pop.rank === 2 ? '#94a3b8' : pop.rank === 3 ? '#b45309' : '#60a5fa'
                      const rankLabel   = pop.rank === 1 ? '★ MOST OBSERVED GLOBALLY' : pop.rank === 2 ? '▲ 2ND MOST OBSERVED' : pop.rank === 3 ? '▲ 3RD MOST OBSERVED' : `RANK #${pop.rank} OBSERVED`
                      const catPeers    = isKx ? primPopularity!.kx : primPopularity!.sig
                      const catLabel    = isKx ? 'KEY EXCHANGE RANKING' : 'CERT SIGNATURE RANKING'
                      const unitLabel   = isKx ? 'TLS connections' : 'cert chain entries'
                      const totalLabel  = isKx ? primPopularity!.total : primPopularity!.totalCerts
                      return (
                        <div style={{ marginTop: '8px', padding: '12px 14px', borderRadius: '4px', background: `${rankColor}08`, border: `1px solid ${rankColor}30` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                            <span style={{ fontSize: '9px', fontFamily: 'Space Mono, monospace', letterSpacing: '0.1em', color: rankColor, fontWeight: 700 }}>{rankLabel}</span>
                            <span style={{ marginLeft: 'auto', fontSize: '9px', fontFamily: 'Space Mono, monospace', color: '#475569', letterSpacing: '0.06em' }}>LIVE SCAN DATA</span>
                          </div>
                          <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '9px', color: '#475569', fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em', marginBottom: '3px' }}>OBSERVED IN</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: rankColor, fontFamily: 'Space Mono, monospace', lineHeight: 1 }}>{pop.count.toLocaleString()}</div>
                              <div style={{ fontSize: '9px', color: '#475569', fontFamily: 'Space Mono, monospace' }}>{unitLabel}</div>
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '9px', color: '#475569', fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em', marginBottom: '3px' }}>SHARE</div>
                              <div style={{ fontSize: '18px', fontWeight: 700, color: rankColor, fontFamily: 'Space Mono, monospace', lineHeight: 1 }}>{pct.toFixed(1)}%</div>
                              <div style={{ fontSize: '9px', color: '#475569', fontFamily: 'Space Mono, monospace' }}>of {totalLabel.toLocaleString()} observed</div>
                            </div>
                          </div>
                          {/* Mini bar chart of category peers */}
                          <div style={{ fontSize: '9px', color: '#475569', fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em', marginBottom: '5px' }}>{catLabel}</div>
                          {catPeers.slice(0, 4).map(peer => (
                            <div key={peer.name} style={{ marginBottom: '4px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                <span style={{ fontSize: '8px', color: peer.name === selectedPrimEntry.name ? rankColor : '#64748b', fontFamily: 'Space Mono, monospace', maxWidth: '70%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{peer.name}</span>
                                <span style={{ fontSize: '8px', color: '#475569', fontFamily: 'Space Mono, monospace' }}>{peer.count.toLocaleString()}</span>
                              </div>
                              <div style={{ height: '3px', borderRadius: '2px', background: 'rgba(100,116,139,0.12)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${catPeers[0].count > 0 ? (peer.count / catPeers[0].count * 100) : 0}%`, background: peer.name === selectedPrimEntry.name ? rankColor : 'rgba(100,116,139,0.35)', borderRadius: '2px' }} />
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    })()}
                  </div>
                  )
                })() : (
                  <div className="kb-sw-detail-empty">
                    <div className="kb-sw-detail-empty-icon">⬡</div>
                    <div className="kb-sw-detail-empty-text">Select an algorithm to view its key size, security bit strength, quantum attack vector, and recommendation.</div>
                  </div>
                )}
              </div>

              {/* RIGHT — stats */}
              <div className="kb-sw-panel-right">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>QUANTUM STATUS</div>
                <div className="kb-sw-stats-donut-wrap">
                  <div className="kb-sw-stats-donut" style={{ background: `conic-gradient(#34d399 0% ${safePercent}%, #fbbf24 ${safePercent}% ${safePercent+weakenedPercent}%, #f87171 ${safePercent+weakenedPercent}% 100%)` }}>
                    <div className="kb-sw-stats-donut-hole">
                      <span className="kb-sw-stats-donut-pct">{safePercent}%</span>
                      <span className="kb-sw-stats-donut-sub">SAFE</span>
                    </div>
                  </div>
                </div>
                <div className="kb-sw-stats-counts">
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label">TOTAL</span>
                    <span className="kb-sw-stats-count-val" style={{ color: 'rgba(200,220,255,0.8)' }}>{filteredPrimitives.length}</span>
                  </div>
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#34d399' }}>◈ SAFE</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#34d399' }}>{safeCount}</span>
                  </div>
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#fbbf24' }}>⚠ WEAKENED</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#fbbf24' }}>{weakenedCount}</span>
                  </div>
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#f87171' }}>✕ BROKEN</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#f87171' }}>{brokenCount}</span>
                  </div>
                </div>
                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-cats-label">BY CATEGORY</div>
                <div className="kb-sw-stats-cats">
                  {([
                    { id: 'kx', label: 'Key Exchange' }, { id: 'cipher', label: 'Ciphers' }, { id: 'sig', label: 'Signatures' },
                  ] as const).map(c => {
                    const all  = activePrimitives.filter(p => p.category === c.id).length
                    const safe = activePrimitives.filter(p => p.category === c.id && p.quantumSafe === 'safe').length
                    return (
                      <div key={c.id} className="kb-sw-stats-cat-row">
                        <div className="kb-sw-stats-cat-header">
                          <span className="kb-sw-stats-cat-name">{c.label}</span>
                          <span className="kb-sw-stats-cat-n">{safe}<span className="kb-sw-stats-cat-n-total">/{all}</span></span>
                        </div>
                        <div className="kb-sw-stats-cat-bar-wrap">
                          <div className="kb-sw-stats-cat-bar-safe" style={{ width: `${(safe/all)*100}%` }} />
                          <div className="kb-sw-stats-cat-bar-unsafe" style={{ width: `${((all-safe)/all)*100}%` }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-footer-note" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.55rem', color: 'rgba(100,140,200,0.5)', padding: '0 16px 12px', lineHeight: 1.5 }}>
                  ◈ Safe — no known quantum attack<br/>
                  ⚠ Weakened — Grover halves key strength<br/>
                  ✕ Broken — Shor's algorithm defeats
                </div>
              </div>
            </div>
          </div>
          )
        })()}

        {/* ══════════════════════════════════════════
            SOFTWARE REGISTRY section
        ══════════════════════════════════════════ */}
        {kbSection === 'software' && (() => {
          const safeCount   = activeSoftware.filter(s => s.status === 'safe').length
          const unsafeCount = activeSoftware.filter(s => s.status === 'unsafe').length
          const safePercent = activeSoftware.length > 0 ? Math.round((safeCount / activeSoftware.length) * 100) : 0
          const catCounts   = SW_CATS.map(c => ({
            ...c,
            total: activeSoftware.filter(s => s.cat === c.id).length,
            safe:  activeSoftware.filter(s => s.cat === c.id && s.status === 'safe').length,
          })).filter(c => c.total > 0)
          return (
          <div className="kb-sw-cockpit">

            {/* ── Cockpit Command Bar ── */}
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">⊙</span>
                <span className="kb-sw-cmd-label">REGISTRY</span>
                <span className="kb-sw-cmd-count-badge">{filteredSoftware.length}<span className="kb-sw-cmd-count-total">/{activeSoftware.length}</span></span>
              </div>
              <div className="kb-sw-cmd-sep" />
              <input
                className="kb-sw-cockpit-search"
                type="text"
                placeholder="Search..."
                value={swSearch}
                onChange={e => setSwSearch(e.target.value)}
              />
              <div className="kb-sw-cmd-sep" />
              <div className="kb-sw-cockpit-filter-group">
                {(['all','safe','unsafe'] as const).map(v => (
                  <button key={v} className={`kb-sw-cockpit-btn ${swStatus===v?'kb-sw-cockpit-btn-on':''}`}
                    onClick={() => setSwStatus(v)}>
                    {v === 'all' ? 'ALL' : v === 'safe' ? '◈ SAFE' : '✕ UNSAFE'}
                  </button>
                ))}
              </div>
              <div className="kb-sw-cmd-sep" />
              <span className="kb-sw-cockpit-label">SORT</span>
              {([['name','NAME'],['status','STATUS'],['cat','CATEGORY']] as [string,string][]).map(([k,l]) => (
                <button key={k} className={`kb-sw-cockpit-btn ${swSortCol===k?'kb-sw-cockpit-btn-on':''}`}
                  onClick={() => toggleSwSort(k)}>
                  {l}{swSortCol===k ? (swSortDir==='asc' ? ' ↑' : ' ↓') : ''}
                </button>
              ))}
              {(swSearch || swCat !== 'all' || swStatus !== 'all') && (
                <>
                  <div className="kb-sw-cmd-sep" />
                  <button className="kb-prim-clear-btn" onClick={() => { setSwSearch(''); setSwCat('all'); setSwStatus('all' as const) }}>CLEAR</button>
                </>
              )}
            </div>

            {/* ── Category filter strip ── */}
            <div className="kb-sw-cockpit-cats">
              <button className={`kb-sw-cockpit-cat ${swCat==='all'?'kb-sw-cockpit-cat-on':''}`} onClick={() => setSwCat('all')}>ALL</button>
              {SW_CATS.map(c => (
                <button key={c.id} className={`kb-sw-cockpit-cat ${swCat===c.id?'kb-sw-cockpit-cat-on':''}`} onClick={() => setSwCat(c.id)}>
                  {c.label.toUpperCase()}
                </button>
              ))}
            </div>

            {/* ── Three Panels ── */}
            <div className="kb-sw-panels">

              {/* LEFT — Software list */}
              <div className="kb-sw-panel-left">
                <div className="kb-sw-panel-hdr">
                  <span className="kb-sw-panel-hdr-bracket">⌐</span>
                  SOFTWARE LIST
                  <span className="kb-sw-panel-hdr-count">{filteredSoftware.length}</span>
                </div>
                <div className="kb-sw-list">
                  {filteredSoftware.length === 0 && (
                    <div className="kb-sw-list-empty">No matches</div>
                  )}
                  {filteredSoftware.map(s => (
                    <div key={s.name}
                      className={`kb-sw-list-row${s.status === 'safe' ? ' kb-sw-list-safe' : ' kb-sw-list-unsafe'}${swSelected === s.name ? ' kb-sw-list-sel' : ''}`}
                      onClick={() => setSwSelected(swSelected === s.name ? null : s.name)}>
                      <span className={`kb-sw-list-dot kb-sw-list-dot-${s.status}`} />
                      <div className="kb-sw-list-text">
                        <span className="kb-sw-list-name">{s.name}</span>
                        <div className="kb-sw-list-meta">
                          <span className="kb-sw-list-cat">{s.catLabel}</span>
                          {s.osDep
                            ? <span className="kb-sw-list-ver kb-sw-list-ver-osdep">OS DEP.</span>
                            : !['current','all current','ga'].includes(s.version.toLowerCase()) && (
                                <span className="kb-sw-list-ver">{s.version}</span>
                              )
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CENTER — Detail */}
              <div className="kb-sw-panel-center">
                <div className="kb-sw-panel-hdr">
                  <span className="kb-sw-panel-hdr-bracket">⌐</span>
                  {swSelectedEntry ? 'ENTRY DETAIL' : 'SELECT AN ENTRY'}
                </div>
                {swSelectedEntry ? (
                  <div className="kb-sw-detail-content">
                    <div className="kb-sw-detail-name">{swSelectedEntry.name}</div>
                    <div className="kb-sw-detail-meta-row">
                      <span className="kb-sw-cat-pill">{swSelectedEntry.catLabel}</span>
                      <span className="kb-sw-detail-ver">v{swSelectedEntry.version}</span>
                      {(() => {
                        const sc = swSelectedEntry.status === 'safe' ? '#34d399' : swSelectedEntry.risk === 'high' ? '#f87171' : '#fbbf24'
                        const sl = swSelectedEntry.status === 'safe' ? '◈ QUANTUM SAFE' : '✕ NOT QUANTUM SAFE'
                        return <span className="kb-sw-detail-status-badge" style={{ color: sc, borderColor: `${sc}55`, background: `${sc}11` }}>{sl}</span>
                      })()}
                    </div>
                    {swSelectedEntry.since && (
                      <div className="kb-sw-detail-since">
                        <span className="kb-sw-detail-since-label">PQC SINCE</span>
                        <span className="kb-sw-detail-since-val">{swSelectedEntry.since}</span>
                      </div>
                    )}
                    <div className="kb-sw-detail-cpe">
                      <span className="kb-sw-detail-cpe-label">CPE 2.3</span>
                      <code className="kb-sw-detail-cpe-val">{swSelectedEntry.cpe}</code>
                      <button className="kb-sw-detail-cpe-copy"
                        onClick={() => navigator.clipboard.writeText(swSelectedEntry.cpe)}
                        title="Copy CPE">⎘</button>
                    </div>
                    <div className="kb-sw-detail-body">{swSelectedEntry.detail}</div>
                    {swSelectedEntry.platforms && (
                      <div className="kb-sw-platform-matrix">
                        <div className="kb-sw-platform-matrix-label">OS / PLATFORM SUPPORT MATRIX</div>
                        <table className="kb-sw-platform-table">
                          <thead>
                            <tr>
                              <th className="kb-sw-platform-th">PLATFORM</th>
                              <th className="kb-sw-platform-th">VERSION / CONFIG</th>
                              <th className="kb-sw-platform-th">PQC STATUS</th>
                              <th className="kb-sw-platform-th">NOTES</th>
                            </tr>
                          </thead>
                          <tbody>
                            {swSelectedEntry.platforms.map((row, i) => {
                              const statusColor = row.status === 'safe' ? '#34d399' : row.status === 'planned' ? '#fbbf24' : '#f87171'
                              const statusIcon  = row.status === 'safe' ? '◈' : row.status === 'planned' ? '⟳' : '✕'
                              const statusLabel = row.status === 'safe' ? 'SAFE' : row.status === 'planned' ? 'PLANNED' : 'UNSAFE'
                              return (
                                <tr key={i} className={`kb-sw-platform-row kb-sw-platform-row-${row.status}`}>
                                  <td className="kb-sw-platform-td kb-sw-platform-td-platform">{row.platform}</td>
                                  <td className="kb-sw-platform-td kb-sw-platform-td-version">{row.version}</td>
                                  <td className="kb-sw-platform-td kb-sw-platform-td-status">
                                    <span className="kb-sw-platform-badge"
                                      style={{ color: statusColor, borderColor: `${statusColor}44`, background: `${statusColor}12` }}>
                                      {statusIcon} {statusLabel}
                                    </span>
                                  </td>
                                  <td className="kb-sw-platform-td kb-sw-platform-td-notes">{row.notes ?? '—'}</td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="kb-sw-detail-links-label">REFERENCES</div>
                    <div className="kb-sw-detail-links">
                      {swSelectedEntry.links.map((lk, li) => (
                        <a key={li} className="kb-proto-link" href={lk.url} target="_blank" rel="noreferrer">
                          <span className="kb-proto-link-icon">↗</span>{lk.label}
                        </a>
                      ))}
                    </div>
                    <a className="kb-sw-challenge kb-sw-challenge-inline" href="mailto:info@tychon.io?subject=PQC%20Software%20Registry%20Challenge">
                      <span>⚑</span> Challenge this data — info@tychon.io
                    </a>
                  </div>
                ) : (
                  <div className="kb-sw-detail-empty">
                    <div className="kb-sw-detail-empty-icon">◈</div>
                    <div className="kb-sw-detail-empty-text">Select a software entry from the list to view full detail, version history, and evidence references.</div>
                  </div>
                )}
              </div>

              {/* RIGHT — Stats */}
              <div className="kb-sw-panel-right">
                <div className="kb-sw-panel-hdr">
                  <span className="kb-sw-panel-hdr-bracket">⌐</span>
                  REGISTRY STATS
                </div>

                {/* Donut */}
                <div className="kb-sw-stats-donut-wrap">
                  <div className="kb-sw-stats-donut" style={{
                    background: `conic-gradient(#34d399 0% ${safePercent}%, #f87171 ${safePercent}% 100%)`
                  }}>
                    <div className="kb-sw-stats-donut-hole">
                      <span className="kb-sw-stats-donut-pct">{safePercent}%</span>
                      <span className="kb-sw-stats-donut-sub">SAFE</span>
                    </div>
                  </div>
                </div>

                {/* Totals */}
                <div className="kb-sw-stats-counts">
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label">TOTAL</span>
                    <span className="kb-sw-stats-count-val" style={{ color: 'rgba(200,220,255,0.8)' }}>{activeSoftware.length}</span>
                  </div>
                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#34d399' }}>◈ SAFE</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#34d399' }}>{safeCount}</span>
                  </div>

                  <div className="kb-sw-stats-count-row">
                    <span className="kb-sw-stats-count-label" style={{ color: '#f87171' }}>✕ UNSAFE</span>
                    <span className="kb-sw-stats-count-val" style={{ color: '#f87171' }}>{unsafeCount}</span>
                  </div>
                </div>

                <div className="kb-sw-stats-divider" />

                {/* Category breakdown */}
                <div className="kb-sw-stats-cats-label">BY CATEGORY</div>
                <div className="kb-sw-stats-cats">
                  {catCounts.map(c => (
                    <div key={c.id} className="kb-sw-stats-cat-row">
                      <div className="kb-sw-stats-cat-header">
                        <span className="kb-sw-stats-cat-name">{c.label}</span>
                        <span className="kb-sw-stats-cat-n">{c.safe}<span className="kb-sw-stats-cat-n-total">/{c.total}</span></span>
                      </div>
                      <div className="kb-sw-stats-cat-bar-wrap">
                        <div className="kb-sw-stats-cat-bar-safe"   style={{ width: `${(c.safe / c.total) * 100}%` }} />
                        <div className="kb-sw-stats-cat-bar-unsafe" style={{ width: `${((c.total - c.safe) / c.total) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>

                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-footer-note">
                  Data reflects default end-user deployment, not custom configurations.
                </div>
                <a className="kb-sw-stats-challenge" href="mailto:info@tychon.io?subject=PQC%20Software%20Registry%20Data%20Challenge">
                  ⚑ Challenge data
                </a>
              </div>
            </div>

            {/* No more footer — challenge link moved into panels */}
          </div>
          )
        })()}

        {kbSection === 'capki' && (() => {
          const selectedCa = caProviders.find(c => c.id === caSelected) ?? null
          const tierColor  = (t: string) => t === 'high' ? '#34d399' : t === 'moderate' ? '#fbbf24' : '#60a5fa'
          const tierLabel  = (t: string) => t === 'high' ? 'HIGH' : t === 'moderate' ? 'MODERATE' : 'STAGING'
          const highCount     = caProviders.filter(c => c.readinessTier === 'high').length
          const moderateCount = caProviders.filter(c => c.readinessTier === 'moderate').length
          const stagingCount  = caProviders.filter(c => c.readinessTier === 'staging').length
          return (
          <div className="kb-sw-cockpit">
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">⎔</span>
                <span className="kb-sw-cmd-label">CA / PKI PROVIDERS</span>
                <span className="kb-sw-cmd-count-badge">{caProviders.length}<span className="kb-sw-cmd-count-total"> providers</span></span>
              </div>
              <div className="kb-sw-cmd-sep" />
              <span style={{ fontSize: '10px', color: '#64748b', letterSpacing: '0.06em', fontFamily: 'Space Mono, monospace' }}>ML-DSA / PQC READINESS MATRIX · MARCH 2026</span>
            </div>
            <div className="kb-sw-panels">

              {/* LEFT — provider list */}
              <div className="kb-sw-panel-left">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>PROVIDER REGISTRY<span className="kb-sw-panel-hdr-count">{caProviders.length}</span></div>
                <div className="kb-sw-list">
                  {caProviders.map(ca => (
                    <div key={ca.id}
                      className={`kb-sw-list-row${caSelected === ca.id ? ' kb-sw-list-row-on' : ''}`}
                      onClick={() => setCaSelected(ca.id)}>
                      <span className="kb-sw-list-dot" style={{ background: tierColor(ca.readinessTier) }} />
                      <div className="kb-sw-list-text">
                        <span className="kb-sw-list-name">{ca.name}</span>
                        <div className="kb-sw-list-meta">
                          <span className="kb-sw-list-cat">{ca.targetUseCase.split(',')[0].trim()}</span>
                          <span className="kb-sw-list-ver" style={{ color: tierColor(ca.readinessTier) }}>{tierLabel(ca.readinessTier)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CENTER — detail */}
              <div className="kb-sw-panel-center">
                {!selectedCa ? (
                  <div className="kb-sw-detail-empty">
                    <div className="kb-sw-detail-empty-icon">⎔</div>
                    <div className="kb-sw-detail-empty-text">Select a provider to view PQC readiness detail</div>
                  </div>
                ) : (
                  <div className="kb-sw-detail-content">
                    {/* Header */}
                    <div className="kb-sw-detail-name">{selectedCa.name}</div>
                    <div className="kb-sw-detail-meta-row">
                      <span className="kb-sw-cat-pill" style={{ background: `${tierColor(selectedCa.readinessTier)}18`, color: tierColor(selectedCa.readinessTier), border: `1px solid ${tierColor(selectedCa.readinessTier)}44` }}>
                        {selectedCa.readinessLabel}
                      </span>
                      <span className="kb-sw-detail-ver">ML-DSA / PQC READINESS · MARCH 2026</span>
                    </div>

                    {/* Tier status bar */}
                    <div className="kb-sw-detail-since" style={{ borderColor: `${tierColor(selectedCa.readinessTier)}33`, background: `${tierColor(selectedCa.readinessTier)}08` }}>
                      <span className="kb-sw-detail-since-label">READINESS TIER</span>
                      <span className="kb-sw-detail-since-val" style={{ color: tierColor(selectedCa.readinessTier) }}>{tierLabel(selectedCa.readinessTier)}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '9px', color: 'rgba(100,116,139,0.7)', fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em' }}>
                        {selectedCa.readinessTier === 'high' ? 'PRODUCTION CAPABLE' : selectedCa.readinessTier === 'moderate' ? 'IN DEVELOPMENT' : 'STAGING / TESTING'}
                      </span>
                    </div>

                    {/* Capability */}
                    <div className="kb-sw-detail-since-label">PQC CAPABILITY &amp; STRATEGY</div>
                    <div className="kb-sw-detail-body" style={{ borderTop: 'none', paddingTop: 0 }}>{selectedCa.pqcCapability}</div>

                    {/* Target use cases */}
                    <div className="kb-sw-detail-since-label">TARGET USE CASES</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {selectedCa.targetUseCase.split(',').map((uc, i) => (
                        <span key={i} className="kb-sw-cat-pill">{uc.trim()}</span>
                      ))}
                    </div>

                    {/* Tier definition */}
                    <div className="kb-sw-detail-since-label" style={{ marginTop: '4px' }}>TIER DEFINITION</div>
                    <div className="kb-sw-detail-body" style={{ borderTop: 'none', paddingTop: 0, color: 'rgba(148,163,184,0.75)' }}>
                      {selectedCa.readinessTier === 'high'
                        ? 'Provider actively issues or manages ML-DSA certificates in production or private PKI environments. Has published technical PQC capability and is driving CA/B Forum and IETF standards work.'
                        : selectedCa.readinessTier === 'moderate'
                        ? 'Provider has PQC capability in development or limited availability. Focused on discovery tooling, automation infrastructure, or HSM integration as prerequisites to full PQC issuance.'
                        : 'Provider operates a PQC staging or testing environment. Not yet issuing production PQC certificates but investing in ACME automation and future-ready certificate infrastructure.'}
                    </div>

                    {/* Standards note */}
                    <div style={{ marginTop: 'auto', padding: '12px 16px', background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: '4px' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(0,212,255,0.45)', fontFamily: 'Space Mono, monospace', letterSpacing: '0.1em', marginBottom: '5px' }}>NOTE</div>
                      <div style={{ fontSize: '0.78rem', color: 'rgba(148,163,184,0.65)', lineHeight: 1.6 }}>
                        Public TLS certificate issuance with ML-DSA is pending CA/B Forum ballot. All providers above support ML-DSA in private PKI environments today. Hybrid (Classical + ML-DSA) X.509 certificates are available in select environments.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT — stats */}
              <div className="kb-sw-panel-right">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>READINESS BREAKDOWN</div>

                <div className="kb-sw-stats-donut-wrap">
                  <div className="kb-sw-stats-donut" style={{ background: `conic-gradient(#34d399 0% ${Math.round(highCount/caProviders.length*100)}%, #fbbf24 ${Math.round(highCount/caProviders.length*100)}% ${Math.round((highCount+moderateCount)/caProviders.length*100)}%, #60a5fa ${Math.round((highCount+moderateCount)/caProviders.length*100)}% 100%)` }}>
                    <div className="kb-sw-stats-donut-hole">
                      <span className="kb-sw-stats-donut-pct">{caProviders.length}</span>
                      <span className="kb-sw-stats-donut-label">PROVIDERS</span>
                    </div>
                  </div>
                </div>

                <div className="kb-sw-stats-legend">
                  <div className="kb-sw-stats-legend-row"><span className="kb-sw-stats-legend-dot" style={{ background: '#34d399' }} /><span className="kb-sw-stats-legend-label">High</span><span className="kb-sw-stats-legend-val">{highCount}</span></div>
                  <div className="kb-sw-stats-legend-row"><span className="kb-sw-stats-legend-dot" style={{ background: '#fbbf24' }} /><span className="kb-sw-stats-legend-label">Moderate</span><span className="kb-sw-stats-legend-val">{moderateCount}</span></div>
                  <div className="kb-sw-stats-legend-row"><span className="kb-sw-stats-legend-dot" style={{ background: '#60a5fa' }} /><span className="kb-sw-stats-legend-label">Staging / Dev</span><span className="kb-sw-stats-legend-val">{stagingCount}</span></div>
                </div>

                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-section-label">KEY CAPABILITIES</div>
                {[
                  { label: 'Pure ML-DSA Issuance',        providers: ['DigiCert', 'AWS (Private CA)'] },
                  { label: 'X.509 Hybrid Certs',          providers: ['Sectigo', 'Entrust'] },
                  { label: 'HSM Integration',             providers: ['Entrust', 'IdenTrust (Thales)'] },
                  { label: 'IETF Standards Authorship',   providers: ['Entrust', 'DigiCert'] },
                  { label: 'Automated Lifecycle (ML-DSA)',providers: ['Keyfactor', 'GlobalSign'] },
                  { label: 'PQC Staging Environment',     providers: ['Let\'s Encrypt'] },
                ].map((cap, i) => (
                  <div key={i} style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '9px', color: '#64748b', letterSpacing: '0.06em', fontFamily: 'Space Mono, monospace', marginBottom: '3px' }}>{cap.label}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                      {cap.providers.map(p => (
                        <span key={p} style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '2px', background: 'rgba(52,211,153,0.1)', color: '#34d399', border: '1px solid rgba(52,211,153,0.2)', fontFamily: 'Space Mono, monospace' }}>{p}</span>
                      ))}
                    </div>
                  </div>
                ))}

                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-footer-note">
                  PQC issuance for publicly-trusted TLS certs pending CA/B Forum ballot. Private PKI issuance is available today.
                </div>
              </div>

            </div>
          </div>
          )
        })()}

        {kbSection === 'os' && (() => {
          const tierColor = (t: string) =>
            t === 'safe' ? '#34d399' : t === 'partial' ? '#fbbf24' : t === 'planned' ? '#60a5fa' : '#ff4d4d'
          const tierLabel = (t: string) =>
            t === 'safe' ? 'SAFE' : t === 'partial' ? 'PARTIAL' : t === 'planned' ? 'PLANNED' : 'NONE'
          const supportIcon = (v: string) =>
            v === 'yes' ? '✓' : v === 'partial' ? '◑' : v === 'planned' ? '⋯' : '✕'
          const supportColor = (v: string) =>
            v === 'yes' ? '#34d399' : v === 'partial' ? '#fbbf24' : v === 'planned' ? '#60a5fa' : '#64748b'
          const typeLabel = (t: string) =>
            t === 'desktop' ? 'DESKTOP' : t === 'server' ? 'SERVER' : t === 'mobile' ? 'MOBILE' : 'EMBEDDED'
          const typeIcon = (t: string) =>
            t === 'desktop' ? '🖥' : t === 'server' ? '⬡' : t === 'mobile' ? '◻' : '⊕'

          const toggleGroup = (key: string) => {
            setCollapsedOsGroups(prev => {
              const next = new Set(prev)
              next.has(key) ? next.delete(key) : next.add(key)
              return next
            })
          }

          // Apply both filters
          const filtered = osPlatforms.filter(o =>
            (osTypeFilter === 'all' || o.platformType === osTypeFilter) &&
            (osVendorFilter === 'all' || o.vendor === osVendorFilter)
          )

          // Sorted unique vendors from fully-unfiltered list for the filter chips
          const allVendors = Array.from(new Set(osPlatforms.map(o => o.vendor))).sort()

          // Groups: vendor is primary, platform_type is sub-label on each row
          const groupedByVendor = allVendors
            .map(vendor => ({
              vendor,
              entries: filtered.filter(o => o.vendor === vendor),
            }))
            .filter(g => g.entries.length > 0)

          const selectedOs = osPlatforms.find(o => o.id === osSelected) ?? null

          const safeCount    = osPlatforms.filter(o => o.readinessTier === 'safe').length
          const partialCount = osPlatforms.filter(o => o.readinessTier === 'partial').length
          const plannedCount = osPlatforms.filter(o => o.readinessTier === 'planned').length
          const noneCount    = osPlatforms.filter(o => o.readinessTier === 'none').length
          const total        = osPlatforms.length || 1

          const byType = (['desktop','server','mobile','embedded'] as const).map(t => ({
            type: t,
            label: typeLabel(t),
            count: osPlatforms.filter(o => o.platformType === t).length,
            safe:  osPlatforms.filter(o => o.platformType === t && o.readinessTier === 'safe').length,
          }))

          const TYPE_FILTERS: Array<{ id: 'all'|'desktop'|'server'|'mobile'|'embedded'; label: string }> = [
            { id: 'all',      label: 'ALL' },
            { id: 'desktop',  label: 'DESKTOP' },
            { id: 'server',   label: 'SERVER' },
            { id: 'mobile',   label: 'MOBILE' },
            { id: 'embedded', label: 'EMBEDDED' },
          ]

          const filterBtnStyle = (active: boolean): React.CSSProperties => ({
            fontSize: '9px', padding: '3px 8px', borderRadius: '2px', cursor: 'pointer',
            fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em',
            background: active ? 'rgba(0,212,255,0.15)' : 'transparent',
            color: active ? '#00d4ff' : '#64748b',
            border: active ? '1px solid rgba(0,212,255,0.35)' : '1px solid rgba(100,116,139,0.2)',
            whiteSpace: 'nowrap' as const,
          })

          return (
          <div className="kb-sw-cockpit">
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">⊞</span>
                <span className="kb-sw-cmd-label">OS PLATFORMS</span>
                <span className="kb-sw-cmd-count-badge">{osPlatforms.length}<span className="kb-sw-cmd-count-total"> platforms</span></span>
              </div>
              <div className="kb-sw-cmd-sep" />
              {/* Platform type filter */}
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={{ fontSize: '9px', color: '#475569', fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>TYPE</span>
                {TYPE_FILTERS.map(f => (
                  <button key={f.id} onClick={() => setOsTypeFilter(f.id)} style={filterBtnStyle(osTypeFilter === f.id)}>{f.label}</button>
                ))}
              </div>
              <div className="kb-sw-cmd-sep" />
              {/* Manufacturer filter — compact dropdown */}
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={{ fontSize: '9px', color: '#475569', fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>MFR</span>
                <select
                  value={osVendorFilter}
                  onChange={e => setOsVendorFilter(e.target.value)}
                  style={{
                    fontSize: '9px', padding: '3px 6px', borderRadius: '2px', cursor: 'pointer',
                    fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em',
                    background: osVendorFilter !== 'all' ? 'rgba(0,212,255,0.12)' : 'rgba(15,23,42,0.8)',
                    color: osVendorFilter !== 'all' ? '#00d4ff' : '#64748b',
                    border: osVendorFilter !== 'all' ? '1px solid rgba(0,212,255,0.35)' : '1px solid rgba(100,116,139,0.2)',
                    outline: 'none', appearance: 'none', WebkitAppearance: 'none',
                    paddingRight: '18px',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' viewBox='0 0 8 5'%3E%3Cpath fill='%2364748b' d='M0 0l4 5 4-5z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 5px center',
                  }}>
                  <option value="all">ALL</option>
                  {allVendors.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="kb-sw-cmd-sep" />
              <span style={{ fontSize: '10px', color: '#64748b', letterSpacing: '0.06em', fontFamily: 'Space Mono, monospace', whiteSpace: 'nowrap' }}>NIST PQC · MARCH 2026</span>
            </div>
            <div className="kb-sw-panels">

              {/* LEFT — OS list grouped by vendor */}
              <div className="kb-sw-panel-left">
                <div className="kb-sw-panel-hdr">
                  <span className="kb-sw-panel-hdr-bracket">⌐</span>PLATFORM REGISTRY
                  <span className="kb-sw-panel-hdr-count">{filtered.length}</span>
                </div>

                <div className="kb-sw-list">
                  {osPlatforms.length === 0 ? (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: '#64748b', fontSize: '11px', fontFamily: 'Space Mono, monospace' }}>Loading…</div>
                  ) : groupedByVendor.length === 0 ? (
                    <div style={{ padding: '24px 16px', textAlign: 'center', color: '#64748b', fontSize: '11px', fontFamily: 'Space Mono, monospace' }}>No entries match filters</div>
                  ) : groupedByVendor.map(({ vendor, entries }) => {
                    const isCollapsed = collapsedOsGroups.has(vendor)
                    const groupSafe   = entries.filter(e => e.readinessTier === 'safe').length
                    return (
                      <div key={vendor}>
                        {/* Vendor group header — clickable to collapse */}
                        <div
                          onClick={() => toggleGroup(vendor)}
                          style={{
                            padding: '5px 10px 4px', display: 'flex', alignItems: 'center', gap: '6px',
                            fontSize: '9px', color: '#94a3b8', letterSpacing: '0.08em',
                            fontFamily: 'Space Mono, monospace', textTransform: 'uppercase',
                            borderBottom: '1px solid rgba(71,85,105,0.2)',
                            background: 'rgba(15,23,42,0.7)', cursor: 'pointer',
                            userSelect: 'none',
                          }}>
                          <span style={{ color: '#475569', fontSize: '8px', minWidth: '8px' }}>{isCollapsed ? '▶' : '▼'}</span>
                          <span style={{ flex: 1 }}>{vendor}</span>
                          <span style={{ color: '#334155', fontSize: '9px' }}>{entries.length}</span>
                          {groupSafe > 0 && (
                            <span style={{ fontSize: '8px', padding: '1px 5px', borderRadius: '2px', background: 'rgba(52,211,153,0.12)', color: '#34d399', border: '1px solid rgba(52,211,153,0.2)' }}>
                              {groupSafe} SAFE
                            </span>
                          )}
                        </div>
                        {/* Entries — hidden when collapsed */}
                        {!isCollapsed && entries.map(os => (
                          <div key={os.id}
                            className={`kb-sw-list-row${osSelected === os.id ? ' kb-sw-list-row-on' : ''}`}
                            onClick={() => setOsSelected(os.id)}>
                            <span className="kb-sw-list-dot" style={{ background: tierColor(os.readinessTier) }} />
                            <div className="kb-sw-list-text">
                              <span className="kb-sw-list-name">{os.name}</span>
                              <div className="kb-sw-list-meta">
                                <span className="kb-sw-list-cat" style={{ color: '#475569' }}>{typeIcon(os.platformType)}</span>
                                <span className="kb-sw-list-cat">{os.version}</span>
                                <span className="kb-sw-list-ver" style={{ color: tierColor(os.readinessTier) }}>{tierLabel(os.readinessTier)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* CENTER — detail */}
              <div className="kb-sw-panel-center">
                {!selectedOs ? (
                  <div className="kb-sw-detail-empty">
                    <div className="kb-sw-detail-empty-icon">⊞</div>
                    <div className="kb-sw-detail-empty-text">Select an OS to view PQC readiness detail</div>
                  </div>
                ) : (
                  <div className="kb-sw-detail-content">
                    <div className="kb-sw-detail-name">{selectedOs.name}</div>
                    <div className="kb-sw-detail-meta-row">
                      <span className="kb-sw-cat-pill" style={{
                        background: `${tierColor(selectedOs.readinessTier)}18`,
                        color: tierColor(selectedOs.readinessTier),
                        border: `1px solid ${tierColor(selectedOs.readinessTier)}44`,
                      }}>
                        {tierLabel(selectedOs.readinessTier)}
                      </span>
                      <span className="kb-sw-cat-pill" style={{ background: 'rgba(100,116,139,0.1)', color: '#94a3b8', border: '1px solid rgba(100,116,139,0.2)' }}>
                        {typeIcon(selectedOs.platformType)} {typeLabel(selectedOs.platformType)}
                      </span>
                      <span className="kb-sw-detail-ver">{selectedOs.vendor}</span>
                    </div>

                    <div className="kb-sw-detail-since" style={{ borderColor: `${tierColor(selectedOs.readinessTier)}33`, background: `${tierColor(selectedOs.readinessTier)}08` }}>
                      <span className="kb-sw-detail-since-label">VERSION</span>
                      <span className="kb-sw-detail-since-val">{selectedOs.version || '—'}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '9px', color: 'rgba(100,116,139,0.7)', fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em' }}>
                        {selectedOs.readinessTier === 'safe' ? 'QUANTUM READY' : selectedOs.readinessTier === 'partial' ? 'CONDITIONAL PQC' : selectedOs.readinessTier === 'planned' ? 'ROADMAPPED' : 'NO PQC SUPPORT'}
                      </span>
                    </div>

                    <div className="kb-sw-detail-since-label">CRYPTO STACK</div>
                    <div className="kb-sw-detail-body" style={{ borderTop: 'none', paddingTop: 0 }}>{selectedOs.cryptoStack}</div>

                    <div className="kb-sw-detail-since-label">ALGORITHM SUPPORT</div>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                      {[
                        { alg: 'ML-KEM', val: selectedOs.mlKem, desc: 'Key Encapsulation (FIPS 203)' },
                        { alg: 'ML-DSA', val: selectedOs.mlDsa, desc: 'Digital Signature (FIPS 204)' },
                      ].map(({ alg, val, desc }) => (
                        <div key={alg} style={{
                          flex: 1, padding: '10px 12px', borderRadius: '4px',
                          background: `${supportColor(val)}0c`,
                          border: `1px solid ${supportColor(val)}30`,
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                            <span style={{ fontSize: '14px', color: supportColor(val), fontFamily: 'Space Mono, monospace' }}>{supportIcon(val)}</span>
                            <span style={{ fontSize: '10px', fontWeight: 700, color: supportColor(val), fontFamily: 'Space Mono, monospace', letterSpacing: '0.06em' }}>{alg}</span>
                          </div>
                          <div style={{ fontSize: '9px', color: 'rgba(100,116,139,0.7)', fontFamily: 'Space Mono, monospace', marginBottom: '4px' }}>{desc}</div>
                          <div style={{ fontSize: '10px', color: supportColor(val), fontFamily: 'Space Mono, monospace', fontWeight: 600 }}>
                            {val === 'yes' ? 'SUPPORTED' : val === 'partial' ? 'PARTIAL' : val === 'planned' ? 'PLANNED' : 'NOT SUPPORTED'}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="kb-sw-detail-since-label">NOTES</div>
                    <div className="kb-sw-detail-body" style={{ borderTop: 'none', paddingTop: 0, lineHeight: 1.7 }}>{selectedOs.notes}</div>

                    <div style={{ marginTop: 'auto', padding: '10px 14px', background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.1)', borderRadius: '4px' }}>
                      <div style={{ fontSize: '9px', color: 'rgba(0,212,255,0.45)', fontFamily: 'Space Mono, monospace', letterSpacing: '0.1em', marginBottom: '4px' }}>ASSESSMENT BASIS</div>
                      <div style={{ fontSize: '0.78rem', color: 'rgba(148,163,184,0.65)', lineHeight: 1.6 }}>
                        SAFE = ML-KEM hybrid TLS enabled by default in system crypto stack. PARTIAL = PQC available but requires configuration or limited to specific subsystems. PLANNED = publicly roadmapped, not yet shipped.
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT — stats */}
              <div className="kb-sw-panel-right">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>READINESS BREAKDOWN</div>

                <div className="kb-sw-stats-donut-wrap">
                  <div className="kb-sw-stats-donut" style={{ background: `conic-gradient(#34d399 0% ${Math.round(safeCount/total*100)}%, #fbbf24 ${Math.round(safeCount/total*100)}% ${Math.round((safeCount+partialCount)/total*100)}%, #60a5fa ${Math.round((safeCount+partialCount)/total*100)}% ${Math.round((safeCount+partialCount+plannedCount)/total*100)}%, #ff4d4d ${Math.round((safeCount+partialCount+plannedCount)/total*100)}% 100%)` }}>
                    <div className="kb-sw-stats-donut-hole">
                      <span className="kb-sw-stats-donut-pct">{osPlatforms.length}</span>
                      <span className="kb-sw-stats-donut-label">PLATFORMS</span>
                    </div>
                  </div>
                </div>

                <div className="kb-sw-stats-legend">
                  <div className="kb-sw-stats-legend-row"><span className="kb-sw-stats-legend-dot" style={{ background: '#34d399' }} /><span className="kb-sw-stats-legend-label">Safe</span><span className="kb-sw-stats-legend-val">{safeCount}</span></div>
                  <div className="kb-sw-stats-legend-row"><span className="kb-sw-stats-legend-dot" style={{ background: '#fbbf24' }} /><span className="kb-sw-stats-legend-label">Partial</span><span className="kb-sw-stats-legend-val">{partialCount}</span></div>
                  <div className="kb-sw-stats-legend-row"><span className="kb-sw-stats-legend-dot" style={{ background: '#60a5fa' }} /><span className="kb-sw-stats-legend-label">Planned</span><span className="kb-sw-stats-legend-val">{plannedCount}</span></div>
                  <div className="kb-sw-stats-legend-row"><span className="kb-sw-stats-legend-dot" style={{ background: '#ff4d4d' }} /><span className="kb-sw-stats-legend-label">None</span><span className="kb-sw-stats-legend-val">{noneCount}</span></div>
                </div>

                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-section-label">BY PLATFORM TYPE</div>
                {byType.map(bt => (
                  <div key={bt.type} style={{ marginBottom: '9px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                      <span style={{ fontSize: '9px', color: '#94a3b8', fontFamily: 'Space Mono, monospace', letterSpacing: '0.04em' }}>{bt.label}</span>
                      <span style={{ fontSize: '9px', color: '#64748b', fontFamily: 'Space Mono, monospace' }}>{bt.safe}/{bt.count} SAFE</span>
                    </div>
                    <div style={{ height: '4px', borderRadius: '2px', background: 'rgba(100,116,139,0.15)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${bt.count ? Math.round(bt.safe/bt.count*100) : 0}%`, background: '#34d399', borderRadius: '2px', transition: 'width 0.4s ease' }} />
                    </div>
                  </div>
                ))}

                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-section-label">ML-KEM ADOPTION</div>
                {(['yes','partial','planned','no'] as const).map(v => {
                  const count = osPlatforms.filter(o => o.mlKem === v).length
                  const color = v === 'yes' ? '#34d399' : v === 'partial' ? '#fbbf24' : v === 'planned' ? '#60a5fa' : '#64748b'
                  const lbl   = v === 'yes' ? 'Full support' : v === 'partial' ? 'Partial' : v === 'planned' ? 'Planned' : 'None'
                  return (
                    <div key={v} className="kb-sw-stats-legend-row">
                      <span className="kb-sw-stats-legend-dot" style={{ background: color }} />
                      <span className="kb-sw-stats-legend-label">{lbl}</span>
                      <span className="kb-sw-stats-legend-val">{count}</span>
                    </div>
                  )
                })}

                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-footer-note">
                  SAFE = ML-KEM hybrid TLS on by default. Data sourced from vendor release notes and NIST PQC adoption tracking. March 2026.
                </div>
              </div>

            </div>
          </div>
          )
        })()}

        {/* ══════════════════════════════════════════
            LAWS & MANDATES section
        ══════════════════════════════════════════ */}
        {kbSection === 'laws' && (() => {
          const allJurisdictions = ['All', ...Array.from(new Set(mandates.map(m => m.jurisdiction))).sort()]
          const allTypes         = ['All', ...Array.from(new Set(mandates.map(m => m.type))).sort()]
          const filtered = mandates.filter(m => {
            if (lawsJurFilter  !== 'All' && m.jurisdiction !== lawsJurFilter)  return false
            if (lawsTypeFilter !== 'All' && m.type         !== lawsTypeFilter) return false
            return true
          })
          return (
          <div className="kb-sw-cockpit">
            {/* Command bar */}
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">⚖</span>
                <span className="kb-sw-cmd-label">LAWS &amp; MANDATES</span>
                <span className="kb-sw-cmd-count-badge">{filtered.length}<span className="kb-sw-cmd-count-total">/{mandates.length}</span></span>
              </div>
              {/* Jurisdiction filter chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginLeft: '0.75rem' }}>
                {allJurisdictions.map(j => (
                  <button key={j} onClick={() => setLawsJurFilter(j)} style={{
                    background: lawsJurFilter === j ? 'rgba(0,255,255,0.15)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${lawsJurFilter === j ? 'rgba(0,255,255,0.5)' : 'rgba(255,255,255,0.1)'}`,
                    color: lawsJurFilter === j ? '#22d3ee' : '#64748b',
                    padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
                    fontSize: '9px', letterSpacing: '0.06em', fontFamily: 'inherit',
                  }}>{j === 'All' ? 'ALL JURISDICTIONS' : j.toUpperCase()}</button>
                ))}
              </div>
              {/* Type filter chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginLeft: 'auto' }}>
                {allTypes.map(t => (
                  <button key={t} onClick={() => setLawsTypeFilter(t)} style={{
                    background: lawsTypeFilter === t ? `${TYPE_COLORS[t] ?? '#888'}20` : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${lawsTypeFilter === t ? (TYPE_COLORS[t] ?? '#888') + '60' : 'rgba(255,255,255,0.07)'}`,
                    color: lawsTypeFilter === t ? (TYPE_COLORS[t] ?? '#e2e8f0') : '#64748b',
                    padding: '2px 8px', borderRadius: 3, cursor: 'pointer',
                    fontSize: '9px', letterSpacing: '0.07em', fontFamily: 'inherit',
                    textTransform: 'uppercase' as const,
                  }}>{t === 'All' ? 'ALL TYPES' : t.replace('_', ' ')}</button>
                ))}
              </div>
            </div>

            <div className="kb-sw-panels">
              <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem' }}>
                {mandatesLoading && (
                  <div style={{ color: '#64748b', fontSize: '11px', padding: '2rem', textAlign: 'center' }}>
                    Loading knowledge base…
                  </div>
                )}
                {!mandatesLoading && filtered.length === 0 && (
                  <div style={{ color: '#64748b', fontSize: '11px', padding: '2rem', textAlign: 'center' }}>
                    No documents match the selected filters.
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: '0.6rem' }}>
                  {filtered.map(m => {
                    let tags: string[] = []
                    try { tags = JSON.parse(m.tags) } catch { /* ignore */ }
                    return (
                      <div key={m.id} style={{
                        background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 6, padding: '0.75rem 0.9rem',
                        display: 'flex', flexDirection: 'column', gap: '0.45rem',
                        transition: 'border-color 0.15s',
                      }}
                        onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(0,255,255,0.2)')}
                        onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)')}
                      >
                        {/* Badges row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                          <MandateTypeBadge type={m.type} />
                          <MandateStatusBadge status={m.status} />
                          <span style={{
                            fontSize: '9px', color: '#94a3b8',
                            border: '1px solid rgba(255,255,255,0.1)',
                            padding: '2px 6px', borderRadius: 3, letterSpacing: '0.04em',
                          }}>{m.jurisdiction.toUpperCase()}</span>
                        </div>
                        {/* Title */}
                        <div style={{ fontSize: '11px', fontWeight: 600, lineHeight: 1.4, color: '#e2e8f0' }}>
                          {m.title}
                        </div>
                        {/* Meta */}
                        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '9px', color: '#64748b' }}>
                          <span>{m.issuing_body}</span>
                          {m.issued_date && <span>{m.issued_date}</span>}
                        </div>
                        {/* Summary */}
                        <div style={{ fontSize: '10px', color: '#94a3b8', lineHeight: 1.55 }}>
                          {m.summary}
                        </div>
                        {/* Applies to */}
                        {m.applies_to && (
                          <div style={{ fontSize: '9px', color: '#4b5563' }}>
                            <span style={{ color: '#64748b', fontWeight: 600 }}>Applies to: </span>{m.applies_to}
                          </div>
                        )}
                        {/* Tags */}
                        {tags.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                            {tags.map(tag => (
                              <span key={tag} style={{
                                fontSize: '8px', padding: '1px 5px',
                                background: 'rgba(0,255,255,0.05)',
                                border: '1px solid rgba(0,255,255,0.12)',
                                color: '#67e8f9', borderRadius: 2,
                              }}>#{tag}</span>
                            ))}
                          </div>
                        )}
                        {/* Link */}
                        {m.url && (
                          <a href={m.url} target="_blank" rel="noopener noreferrer" style={{
                            fontSize: '9px', color: '#22d3ee',
                            textDecoration: 'none', marginTop: 2,
                            letterSpacing: '0.05em',
                          }}>VIEW DOCUMENT →</a>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              {/* RIGHT — stats sidebar */}
              <div className="kb-sw-panel-right">
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>BY JURISDICTION</div>
                {Array.from(new Set(mandates.map(m => m.jurisdiction))).sort().map(j => {
                  const count = mandates.filter(m => m.jurisdiction === j).length
                  return (
                    <div key={j} style={{ marginBottom: 8, cursor: 'pointer' }} onClick={() => setLawsJurFilter(j === lawsJurFilter ? 'All' : j)}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: '9px', color: '#94a3b8', fontFamily: 'Space Mono, monospace', letterSpacing: '0.04em' }}>{j}</span>
                        <span style={{ fontSize: '9px', color: '#64748b', fontFamily: 'Space Mono, monospace' }}>{count}</span>
                      </div>
                      <div style={{ height: 4, borderRadius: 2, background: 'rgba(100,116,139,0.15)', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${Math.round(count/mandates.length*100)}%`, background: '#22d3ee', borderRadius: 2, transition: 'width 0.4s ease' }} />
                      </div>
                    </div>
                  )
                })}

                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-section-label">BY TYPE</div>
                <div className="kb-sw-stats-legend">
                {Array.from(new Set(mandates.map(m => m.type))).sort().map(t => {
                  const count = mandates.filter(m => m.type === t).length
                  const color = TYPE_COLORS[t] ?? '#888'
                  return (
                    <div key={t} className="kb-sw-stats-legend-row" style={{ cursor: 'pointer' }} onClick={() => setLawsTypeFilter(t === lawsTypeFilter ? 'All' : t)}>
                      <span className="kb-sw-stats-legend-dot" style={{ background: color }} />
                      <span className="kb-sw-stats-legend-label" style={{ textTransform: 'capitalize' }}>{t.replace('_',' ')}</span>
                      <span className="kb-sw-stats-legend-val">{count}</span>
                    </div>
                  )
                })}
                </div>

                <div className="kb-sw-stats-divider" />
                <div className="kb-sw-stats-footer-note">
                  {mandates.length} documents tracked. Laws, mandates, standards and guidance related to post-quantum cryptography migration. Updated {new Date().getFullYear()}.
                </div>
              </div>
            </div>
          </div>
          )
        })()}

        {/* ══════════════════════════════════════════
            COMPLIANCE PROFILES section
        ══════════════════════════════════════════ */}
        {kbSection === 'compliance' && (() => {
          const selectedProfile = COMPLIANCE_PROFILES.find(p => p.id === complianceSelected) ?? COMPLIANCE_PROFILES[0]

          const hybridColor = (h: string) =>
            h === 'required' ? '#ef4444' : h === 'recommended' ? '#fbbf24' : h === 'optional' ? '#22d3ee' : '#94a3b8'
          const levelColor = (l: string) =>
            l === 'mandatory' ? '#00ff88' : l === 'recommended' ? '#60a5fa' : '#fbbf24'

          // ── Algorithm chip ─────────────────────────────────────────────────
          function AlgoChip({ req, showNote }: { req: AlgoReq; showNote?: boolean }) {
            const color = STATUS_COLOR[req.status]
            const [hov, setHov] = useState(false)
            return (
              <div style={{ position: 'relative', display: 'inline-block' }}
                onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  padding: '4px 8px', borderRadius: 4,
                  background: `${color}12`,
                  border: `1px solid ${color}50`,
                  cursor: req.note ? 'help' : 'default',
                  transition: 'all 0.15s',
                  ...(hov && req.note ? { background: `${color}22`, borderColor: `${color}90` } : {}),
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  <span style={{ fontSize: '10px', fontWeight: 600, color: '#e2e8f0', fontFamily: 'Space Mono, monospace', letterSpacing: '0.03em' }}>
                    {req.algo}
                  </span>
                  <span style={{ fontSize: '8px', color, letterSpacing: '0.06em', opacity: 0.85 }}>
                    {STATUS_LABEL[req.status]}
                  </span>
                </div>
                {hov && req.note && (
                  <div style={{
                    position: 'absolute', bottom: '110%', left: 0, zIndex: 99,
                    background: '#0f172a', border: '1px solid rgba(0,255,255,0.25)',
                    borderRadius: 5, padding: '7px 10px', width: 260,
                    fontSize: '10px', color: '#94a3b8', lineHeight: 1.55,
                    pointerEvents: 'none',
                  }}>{req.note}</div>
                )}
              </div>
            )
          }

          // ── Profile detail view ────────────────────────────────────────────
          const ProfileDetail = () => (
            <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1rem' }}>
              {/* Profile header */}
              <div style={{
                background: `${selectedProfile.color}0f`,
                border: `1px solid ${selectedProfile.color}40`,
                borderRadius: 8, padding: '1rem 1.1rem', marginBottom: '1rem',
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>{selectedProfile.name}</span>
                      <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: 3, letterSpacing: '0.08em',
                        background: `${levelColor(selectedProfile.level)}18`, border: `1px solid ${levelColor(selectedProfile.level)}50`,
                        color: levelColor(selectedProfile.level), textTransform: 'uppercase' }}>
                        {selectedProfile.level}
                      </span>
                      <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: 3, letterSpacing: '0.08em',
                        background: `${hybridColor(selectedProfile.hybrid)}18`, border: `1px solid ${hybridColor(selectedProfile.hybrid)}50`,
                        color: hybridColor(selectedProfile.hybrid), textTransform: 'uppercase' }}>
                        HYBRID: {selectedProfile.hybrid.toUpperCase()}
                      </span>
                    </div>
                    <div style={{ fontSize: '10px', color: '#94a3b8', marginBottom: 4 }}>
                      <span style={{ color: '#64748b' }}>{selectedProfile.issuer}</span>
                      <span style={{ margin: '0 6px', color: '#334155' }}>·</span>
                      <span>{selectedProfile.jurisdiction}</span>
                      <span style={{ margin: '0 6px', color: '#334155' }}>·</span>
                      <span>{selectedProfile.year}</span>
                    </div>
                    <div style={{ fontSize: '9px', color: '#64748b' }}>Audience: {selectedProfile.audience}</div>
                  </div>
                </div>
                <div style={{ fontSize: '10px', color: '#94a3b8', lineHeight: 1.6, marginTop: '0.6rem', borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '0.6rem' }}>
                  {selectedProfile.description}
                </div>
                {selectedProfile.timelines && (
                  <div style={{ marginTop: 8, fontSize: '9px', color: '#fbbf24', fontFamily: 'Space Mono, monospace', letterSpacing: '0.04em' }}>
                    ⏱ {selectedProfile.timelines}
                  </div>
                )}
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1rem', padding: '0.4rem 0' }}>
                {(['required','approved','recommended','conditional','forbidden','na'] as AlgoStatus[]).map(s => (
                  <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: STATUS_COLOR[s] }} />
                    <span style={{ fontSize: '8px', color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s}</span>
                  </div>
                ))}
              </div>

              {/* Algorithm sections */}
              {([
                { label: 'KEY ENCAPSULATION (KEM)', items: selectedProfile.kem,       catColor: CAT_COLORS.KEM },
                { label: 'SIGNATURE ALGORITHMS',     items: selectedProfile.sig,       catColor: CAT_COLORS.SIG },
                { label: 'HASH FUNCTIONS',            items: selectedProfile.hash,      catColor: CAT_COLORS.HASH },
                { label: 'SYMMETRIC ENCRYPTION',      items: selectedProfile.symmetric, catColor: CAT_COLORS.SYM },
              ] as { label: string; items: AlgoReq[]; catColor: string }[]).map(section => (
                <div key={section.label} style={{ marginBottom: '1rem' }}>
                  <div style={{
                    fontSize: '8px', fontWeight: 700, letterSpacing: '0.14em',
                    color: section.catColor, marginBottom: 8,
                    borderLeft: `2px solid ${section.catColor}`,
                    paddingLeft: 8,
                  }}>{section.label}</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                    {section.items.map(req => <AlgoChip key={req.algo} req={req} />)}
                  </div>
                </div>
              ))}
            </div>
          )

          // ── Matrix view ────────────────────────────────────────────────────
          const MatrixView = () => {
            const catGroups = ['KEM', 'SIG', 'HASH', 'SYM'] as const
            return (
              <div style={{ flex: 1, overflowX: 'auto', overflowY: 'auto', padding: '0.75rem 1rem' }}>
                <div style={{ fontSize: '9px', color: '#64748b', marginBottom: '0.75rem', letterSpacing: '0.05em' }}>
                  Hover cells for notes · Click a row to view full profile
                </div>
                <table style={{ borderCollapse: 'collapse', minWidth: '100%' }}>
                  <thead>
                    {/* Category header row */}
                    <tr>
                      <th style={{ width: 130, minWidth: 130 }} />
                      {catGroups.map(cat => {
                        const cols = MATRIX_COLS.filter(c => c.category === cat)
                        return (
                          <th key={cat} colSpan={cols.length} style={{
                            fontSize: '8px', fontWeight: 700, letterSpacing: '0.12em',
                            color: CAT_COLORS[cat], textAlign: 'center',
                            borderBottom: `2px solid ${CAT_COLORS[cat]}60`,
                            padding: '0 4px 6px', whiteSpace: 'nowrap',
                          }}>{cat}</th>
                        )
                      })}
                    </tr>
                    {/* Algorithm name row */}
                    <tr>
                      <th style={{
                        textAlign: 'left', fontSize: '8px', color: '#94a3b8',
                        padding: '0 8px 8px 0', letterSpacing: '0.06em',
                      }}>FRAMEWORK</th>
                      {MATRIX_COLS.map(col => (
                        <th key={col.algo} style={{
                          fontSize: '8px', color: CAT_COLORS[col.category],
                          padding: '0 6px 8px', fontWeight: 400,
                          letterSpacing: '0.04em', textAlign: 'center',
                          writingMode: 'vertical-rl' as const,
                          transform: 'rotate(180deg)',
                          whiteSpace: 'nowrap', height: 80,
                        }}>{col.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {COMPLIANCE_PROFILES.map(profile => (
                      <tr key={profile.id}
                        onClick={() => { setComplianceSelected(profile.id); setComplianceView('profile') }}
                        style={{
                          cursor: 'pointer',
                          background: complianceSelected === profile.id ? `${profile.color}0c` : 'transparent',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { if (complianceSelected !== profile.id) (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.03)' }}
                        onMouseLeave={e => { if (complianceSelected !== profile.id) (e.currentTarget as HTMLElement).style.background = 'transparent' }}
                      >
                        {/* Row label */}
                        <td style={{
                          padding: '6px 10px 6px 0', whiteSpace: 'nowrap',
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: profile.color, flexShrink: 0 }} />
                            <span style={{ fontSize: '10px', fontWeight: 600, color: '#e2e8f0' }}>{profile.shortName}</span>
                          </div>
                          <div style={{ fontSize: '8px', color: '#4b5563', paddingLeft: 14 }}>{profile.issuer}</div>
                        </td>
                        {/* Status cells */}
                        {MATRIX_COLS.map(col => {
                          const { status, note } = lookupStatus(profile, col.algo)
                          const color = STATUS_COLOR[status]
                          const isHov = matrixHover?.row === profile.id && matrixHover?.col === col.algo
                          return (
                            <td key={col.algo}
                              onMouseEnter={() => setMatrixHover({ row: profile.id, col: col.algo })}
                              onMouseLeave={() => setMatrixHover(null)}
                              style={{
                                padding: '4px 6px',
                                textAlign: 'center',
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                position: 'relative',
                              }}
                            >
                              <div style={{
                                width: 18, height: 18, borderRadius: 3, margin: '0 auto',
                                background: status === 'na' ? 'rgba(255,255,255,0.04)' : `${color}25`,
                                border: `1px solid ${status === 'na' ? 'rgba(255,255,255,0.06)' : color + '60'}`,
                                transition: 'all 0.1s',
                                ...(isHov ? { transform: 'scale(1.4)', zIndex: 2, background: `${color}50` } : {}),
                              }} />
                              {isHov && note && (
                                <div style={{
                                  position: 'absolute', bottom: '110%', left: '50%',
                                  transform: 'translateX(-50%)',
                                  background: '#0f172a', border: `1px solid ${color}60`,
                                  borderRadius: 5, padding: '6px 10px',
                                  width: 220, fontSize: '9px', color: '#94a3b8',
                                  lineHeight: 1.5, zIndex: 100, pointerEvents: 'none',
                                  whiteSpace: 'normal', textAlign: 'left',
                                }}>
                                  <div style={{ color, fontWeight: 700, marginBottom: 3, fontSize: '8px', letterSpacing: '0.06em' }}>
                                    {STATUS_LABEL[status]}
                                  </div>
                                  {note}
                                </div>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Legend */}
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1.25rem', padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.06)' }}>
                  {(['required','approved','recommended','conditional','forbidden','na'] as AlgoStatus[]).map(s => (
                    <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <div style={{ width: 12, height: 12, borderRadius: 2, background: STATUS_COLOR[s] === '#1e293b' ? 'rgba(255,255,255,0.04)' : `${STATUS_COLOR[s]}30`, border: `1px solid ${STATUS_COLOR[s]}60` }} />
                      <span style={{ fontSize: '9px', color: '#64748b', letterSpacing: '0.05em' }}>{STATUS_LABEL[s]}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          }

          return (
          <div className="kb-sw-cockpit">
            <div className="kb-sw-cmd-bar">
              <div className="kb-sw-cmd-identity">
                <span className="kb-sw-cmd-icon">◉</span>
                <span className="kb-sw-cmd-label">COMPLIANCE PROFILES</span>
                <span className="kb-sw-cmd-count-badge">{COMPLIANCE_PROFILES.length}<span className="kb-sw-cmd-count-total"> suites</span></span>
              </div>
              {/* View toggle */}
              <div style={{ display: 'flex', gap: '0.4rem', marginLeft: '0.75rem' }}>
                {(['profile', 'matrix'] as const).map(v => (
                  <button key={v} onClick={() => setComplianceView(v)} style={{
                    background: complianceView === v ? 'rgba(0,255,255,0.15)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${complianceView === v ? 'rgba(0,255,255,0.5)' : 'rgba(255,255,255,0.1)'}`,
                    color: complianceView === v ? '#22d3ee' : '#64748b',
                    padding: '2px 10px', borderRadius: 3, cursor: 'pointer',
                    fontSize: '9px', letterSpacing: '0.08em', fontFamily: 'inherit',
                  }}>{v === 'profile' ? '▷ PROFILE DETAIL' : '⊞ COMPARISON MATRIX'}</button>
                ))}
              </div>
              <div style={{ marginLeft: 'auto', fontSize: '9px', color: '#4b5563', letterSpacing: '0.05em' }}>
                Hover algorithm cells for notes · Click matrix row to view profile
              </div>
            </div>

            <div className="kb-sw-panels">
              {/* LEFT — profile list */}
              <div className="kb-sw-panel-left" style={{ minWidth: 160, maxWidth: 180 }}>
                <div className="kb-sw-panel-hdr"><span className="kb-sw-panel-hdr-bracket">⌐</span>SUITES</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', padding: '0.4rem' }}>
                  {COMPLIANCE_PROFILES.map(p => (
                    <div key={p.id}
                      onClick={() => { setComplianceSelected(p.id); setComplianceView('profile') }}
                      style={{
                        padding: '0.5rem 0.6rem', borderRadius: 5, cursor: 'pointer',
                        background: complianceSelected === p.id ? `${p.color}18` : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${complianceSelected === p.id ? p.color + '60' : 'rgba(255,255,255,0.07)'}`,
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                        <span style={{ fontSize: '10px', fontWeight: 600, color: '#e2e8f0' }}>{p.shortName}</span>
                      </div>
                      <div style={{ fontSize: '8px', color: '#64748b', paddingLeft: 12 }}>{p.issuer} · {p.year}</div>
                      <div style={{ paddingLeft: 12, marginTop: 3, display: 'flex', gap: 3 }}>
                        <span style={{ fontSize: '7px', padding: '1px 4px', borderRadius: 2, background: `${hybridColor(p.hybrid)}18`, border: `1px solid ${hybridColor(p.hybrid)}40`, color: hybridColor(p.hybrid), letterSpacing: '0.05em' }}>
                          HYBRID {p.hybrid.toUpperCase()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* MAIN — profile detail or matrix */}
              {complianceView === 'profile' ? <ProfileDetail /> : <MatrixView />}
            </div>
          </div>
          )
        })()}

        {/* ══════════════════════════════════════════
            SETUP WIZARD section
        ══════════════════════════════════════════ */}
        {kbSection === 'wizard' && (() => {
          const urgencyColor = (u: string) => u === 'now' ? '#ef4444' : u === 'soon' ? '#fbbf24' : '#22d3ee'
          const urgencyLabel = (u: string) => u === 'now' ? 'ACT NOW' : u === 'soon' ? 'PLAN SOON' : 'PLAN AHEAD'

          const StepBar = () => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '1.5rem' }}>
              {['Your situation', 'Sensitivity & lifespan', 'Your recommendation'].map((label, i) => {
                const done   = i < wizardStep
                const active = i === wizardStep
                const color  = done || active ? '#22d3ee' : '#334155'
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', flex: i < 2 ? 1 : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <div style={{
                        width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                        background: active ? '#22d3ee' : done ? '#22d3ee22' : '#1e293b',
                        border: `2px solid ${color}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '10px', fontWeight: 700, color: active ? '#0a0f1e' : color,
                        transition: 'all 0.3s',
                      }}>{done ? '✓' : i + 1}</div>
                      <span style={{ fontSize: '10px', color: active ? '#e2e8f0' : done ? '#22d3ee' : '#4b5563', whiteSpace: 'nowrap', fontWeight: active ? 600 : 400 }}>{label}</span>
                    </div>
                    {i < 2 && <div style={{ flex: 1, height: 1, background: done ? '#22d3ee40' : '#1e293b', margin: '0 10px', minWidth: 20 }} />}
                  </div>
                )
              })}
            </div>
          )

          // ── Step 0: Scenario ────────────────────────────────────────────────
          if (wizardStep === 0) return (
            <div style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
              <StepBar />
              <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ margin: '0 0 6px', fontSize: '1.1rem', fontWeight: 700, color: '#e2e8f0' }}>What are you trying to protect?</h2>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Pick the option that best describes your situation — don't overthink it.</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
                {WIZARD_SCENARIOS.map(sc => (
                  <div key={sc.id}
                    onClick={() => { setWizardScenario(sc.id); setWizardStep(1) }}
                    style={{
                      padding: '1rem 1.1rem', borderRadius: 8, cursor: 'pointer',
                      background: wizardScenario === sc.id ? 'rgba(0,255,255,0.1)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${wizardScenario === sc.id ? 'rgba(0,255,255,0.5)' : 'rgba(255,255,255,0.08)'}`,
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,255,255,0.35)'; (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,255,0.06)' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = wizardScenario === sc.id ? 'rgba(0,255,255,0.5)' : 'rgba(255,255,255,0.08)'; (e.currentTarget as HTMLElement).style.background = wizardScenario === sc.id ? 'rgba(0,255,255,0.1)' : 'rgba(255,255,255,0.03)' }}
                  >
                    <div style={{ fontSize: '1.5rem', marginBottom: '0.4rem' }}>{sc.icon}</div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>{sc.label}</div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.5 }}>{sc.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          )

          // ── Step 1: Sensitivity + Longevity ────────────────────────────────
          if (wizardStep === 1) return (
            <div style={{ flex: 1, overflowY: 'auto', padding: '2rem', maxWidth: 760 }}>
              <StepBar />
              <button onClick={() => setWizardStep(0)} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', marginBottom: '1.25rem', padding: 0, fontFamily: 'inherit' }}>← Back</button>

              {/* Sensitivity */}
              <div style={{ marginBottom: '1.75rem' }}>
                <h2 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>How sensitive is this data?</h2>
                <p style={{ margin: '0 0 1rem', fontSize: '0.78rem', color: '#64748b' }}>Think about what would happen if it were exposed.</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {WIZARD_SENSITIVITIES.map(opt => (
                    <div key={opt.id}
                      onClick={() => setWizardSensitivity(opt.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '1rem',
                        padding: '0.75rem 1rem', borderRadius: 7, cursor: 'pointer',
                        background: wizardSensitivity === opt.id ? `${opt.color}12` : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${wizardSensitivity === opt.id ? opt.color + '60' : 'rgba(255,255,255,0.07)'}`,
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{
                        width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
                        background: wizardSensitivity === opt.id ? opt.color : 'transparent',
                        border: `2px solid ${opt.color}`,
                        transition: 'all 0.15s',
                      }} />
                      <div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#e2e8f0' }}>{opt.label}</div>
                        <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{opt.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Longevity */}
              <div style={{ marginBottom: '1.75rem' }}>
                <h2 style={{ margin: '0 0 4px', fontSize: '1rem', fontWeight: 700, color: '#e2e8f0' }}>How long does this data need to stay private?</h2>
                <p style={{ margin: '0 0 1rem', fontSize: '0.78rem', color: '#64748b' }}>Longer-lived data is more at risk — it may still be secret when quantum computers arrive.</p>
                <div style={{ display: 'flex', gap: '0.6rem' }}>
                  {WIZARD_LONGEVITIES.map(opt => (
                    <div key={opt.id}
                      onClick={() => setWizardLongevity(opt.id)}
                      style={{
                        flex: 1, padding: '0.85rem', borderRadius: 7, cursor: 'pointer', textAlign: 'center',
                        background: wizardLongevity === opt.id ? 'rgba(0,255,255,0.12)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${wizardLongevity === opt.id ? 'rgba(0,255,255,0.5)' : 'rgba(255,255,255,0.07)'}`,
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: wizardLongevity === opt.id ? '#22d3ee' : '#e2e8f0', marginBottom: 3 }}>{opt.label}</div>
                      <div style={{ fontSize: '0.7rem', color: '#22d3ee', fontFamily: 'Space Mono, monospace', marginBottom: 5 }}>{opt.years}</div>
                      <div style={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.4 }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                disabled={!wizardSensitivity || !wizardLongevity}
                onClick={() => setWizardStep(2)}
                style={{
                  padding: '0.6rem 2rem', borderRadius: 6, cursor: !wizardSensitivity || !wizardLongevity ? 'default' : 'pointer',
                  background: !wizardSensitivity || !wizardLongevity ? 'rgba(0,255,136,0.05)' : 'rgba(0,255,136,0.15)',
                  border: `1px solid ${!wizardSensitivity || !wizardLongevity ? 'rgba(0,255,136,0.15)' : 'rgba(0,255,136,0.5)'}`,
                  color: !wizardSensitivity || !wizardLongevity ? '#334155' : '#00ff88',
                  fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', fontFamily: 'inherit',
                  transition: 'all 0.2s',
                }}
              >GET MY RECOMMENDATION →</button>
            </div>
          )

          // ── Step 2: Result ──────────────────────────────────────────────────
          const result = getWizardResult(wizardScenario!, wizardSensitivity!, wizardLongevity!)
          const uc     = urgencyColor(result.urgency)
          return (
            <div style={{ flex: 1, overflowY: 'auto', padding: '2rem', maxWidth: 820 }}>
              <StepBar />
              <button onClick={wizardReset} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '0.75rem', marginBottom: '1.25rem', padding: 0, fontFamily: 'inherit' }}>← Start over</button>

              {/* Result card */}
              <div style={{ background: `${result.color}0a`, border: `1px solid ${result.color}50`, borderRadius: 10, padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '0.75rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginBottom: 6 }}>
                      <span style={{ fontSize: '1rem', fontWeight: 700, color: result.color }}>{result.profileName}</span>
                      <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 8px', borderRadius: 3, background: `${uc}20`, border: `1px solid ${uc}60`, color: uc, letterSpacing: '0.1em' }}>
                        {urgencyLabel(result.urgency)}
                      </span>
                      {result.harvestWarning && (
                        <span style={{ fontSize: '9px', fontWeight: 700, padding: '2px 8px', borderRadius: 3, background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.4)', color: '#fbbf24', letterSpacing: '0.08em' }}>
                          ⚠ HARVEST-NOW RISK
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '0 0 4px', fontSize: '0.95rem', fontWeight: 600, color: '#e2e8f0', lineHeight: 1.4 }}>{result.headline}</p>
                    <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.5 }}>{result.subhead}</p>
                  </div>
                </div>
                {result.harvestWarning && (
                  <div style={{ background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 6, padding: '0.6rem 0.85rem', marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.72rem', color: '#fbbf24', fontWeight: 600, marginBottom: 2 }}>What is "harvest now, decrypt later"?</div>
                    <div style={{ fontSize: '0.7rem', color: '#92400e', lineHeight: 1.55 }}>
                      Adversaries are recording your encrypted traffic <em>today</em> with the plan to decrypt it once a powerful enough quantum computer exists — likely within 10–15 years. Data that needs to stay secret for years is already at risk.
                    </div>
                  </div>
                )}
              </div>

              {/* Action items */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', color: '#64748b', marginBottom: '0.75rem' }}>WHAT TO DO</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                  {result.actions.map((action, i) => (
                    <div key={i} style={{ display: 'flex', gap: '1rem', padding: '0.9rem 1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 7 }}>
                      <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${result.color}20`, border: `1px solid ${result.color}50`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 700, color: result.color }}>{i + 1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#e2e8f0', marginBottom: 4 }}>{action.title}</div>
                        <div style={{ fontSize: '0.74rem', color: '#94a3b8', lineHeight: 1.6 }}>{action.detail}</div>
                        {action.algo && (
                          <div style={{ marginTop: 6 }}>
                            <span style={{ fontSize: '8px', padding: '2px 7px', borderRadius: 3, background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.25)', color: '#00ff88', letterSpacing: '0.06em', fontFamily: 'Space Mono, monospace' }}>
                              {action.algo}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Compliance links */}
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.12em', color: '#64748b', marginBottom: '0.6rem' }}>RELEVANT FRAMEWORKS</div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {result.frameworks.map(fwId => {
                    const fw = COMPLIANCE_PROFILES.find(p => p.id === fwId)
                    if (!fw) return null
                    return (
                      <button key={fwId}
                        onClick={() => { setComplianceSelected(fwId); navigate(KB_SECTION_PATHS.compliance) }}
                        style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0.4rem 0.85rem', borderRadius: 5, cursor: 'pointer', background: `${fw.color}10`, border: `1px solid ${fw.color}40`, color: fw.color, fontSize: '0.75rem', fontWeight: 600, fontFamily: 'inherit', letterSpacing: '0.04em' }}>
                        <span style={{ width: 7, height: 7, borderRadius: '50%', background: fw.color }} />
                        {fw.shortName}
                        <span style={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 400 }}>→ view profile</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        })()}

      </div>
    </div>
  )
}

// ── Console Shell ─────────────────────────────────────────────────────────────

const AREA_PATH: Record<ConsoleArea, string> = {
  overview: '/',
  globe:     '/globe',
  me:        '/me',
  analytics: '/analytics',
  knowledge: '/knowledge',
}
function pathToArea(p: string): ConsoleArea {
  if (p === '/globe')                  return 'globe'
  if (p === '/analytics')             return 'analytics'
  if (p.startsWith('/knowledge'))     return 'knowledge'
  return 'overview'
}

function ConsoleShell() {
  const navigate          = useNavigate()
  const location          = useLocation()
  const area              = pathToArea(location.pathname)
  const [areaClass, setAreaClass]     = useState('console-area-entering')
  const [showSignIn, setShowSignIn]   = useState(false)
  const prevArea  = useRef<ConsoleArea>(area)
  const { isSignedIn } = useUser()

  // Initial enter animation
  useEffect(() => {
    const t = setTimeout(() => setAreaClass(''), 750)
    return () => clearTimeout(t)
  }, [])

  function navigateTo(target: ConsoleArea) {
    // Home Planet requires auth
    if (target === 'me') {
      if (isSignedIn) { navigate('/me') } else { setShowSignIn(true) }
      return
    }
    if (target === prevArea.current) return
    prevArea.current = target
    setAreaClass('console-area-exiting')
    setTimeout(() => {
      navigate(AREA_PATH[target])
      setAreaClass('console-area-entering')
      setTimeout(() => setAreaClass(''), 750)
    }, 580)
  }

  function goHome() { navigateTo('overview') }

  return (
    <div className="console-shell">
      {area !== 'overview' && (
        <button className="console-orbit-btn" onClick={goHome}>
          <span className="console-orbit-icon">⊙</span>
          <span>SOLAR SYSTEM</span>
        </button>
      )}
      <div className={`console-area-wrapper ${areaClass}`}>
        {area === 'overview'  && <SolarSystem3D onSelect={navigateTo} />}
        {area === 'globe'     && <GlobalView onConsoleHome={goHome} />}
        {area === 'analytics' && <AnalyticsConsole />}
        {area === 'knowledge' && <KnowledgeBase />}
      </div>

      {/* Sign-in prompt when unauthenticated user clicks Home Planet */}
      {showSignIn && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setShowSignIn(false) }}>
          <div style={{ background: '#0d1117', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 16, padding: '2.5rem 2rem', textAlign: 'center', maxWidth: 360, boxShadow: '0 0 60px rgba(245,158,11,0.15)' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
              <svg width={64} height={64} viewBox="0 0 32 32" style={{ display: 'block', margin: '0 auto' }}>
                <polygon points="16,3 29,27 3,27" fill="none" stroke="#f59e0b" strokeWidth={2} />
              </svg>
            </div>
            <div style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.5rem', letterSpacing: '0.05em' }}>ACCESS YOUR HOME PLANET</div>
            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '1.8rem', lineHeight: 1.6 }}>
              Sign in to track your rank, score points for discovering quantum-vulnerable domains, and claim your place in the Quantum Force.
            </div>
            <SignInButton mode="modal">
              <button
                className="gd-nav-btn"
                onClick={() => setShowSignIn(false)}
                style={{ width: '100%', background: 'rgba(245,158,11,0.1)', borderColor: 'rgba(245,158,11,0.4)', color: '#f59e0b', fontSize: '0.85rem', padding: '10px' }}
              >
                SIGN IN / CREATE ACCOUNT
              </button>
            </SignInButton>
            <button onClick={() => setShowSignIn(false)} style={{ marginTop: '1rem', background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: '0.75rem' }}>
              maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Shareable Company Report Page ────────────────────────────────────────────

const CR_STARS = (() => {
  const s: string[] = []
  for (let i = 0; i < 180; i++) {
    const x = Math.random() * 100, y = Math.random() * 100
    const size = Math.random() < 0.85 ? 1 : 2
    const op = (0.15 + Math.random() * 0.5).toFixed(2)
    s.push(`radial-gradient(${size}px ${size}px at ${x.toFixed(1)}% ${y.toFixed(1)}%, rgba(255,255,255,${op}), transparent)`)
  }
  return s.join(',')
})()

function CRCard({ title, accent = 'rgba(0,209,255,0.18)', action, children }: { title: string; accent?: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ background:'rgba(6,10,22,0.85)', border:`1px solid ${accent}`, borderRadius:10, overflow:'hidden', backdropFilter:'blur(12px)' }}>
      <div style={{ padding:'10px 18px', borderBottom:`1px solid ${accent}`, display:'flex', alignItems:'center', gap:8 }}>
        <div style={{ width:6, height:6, borderRadius:'50%', background: accent.replace('0.18','1').replace('rgba','rgb').replace(/,\s*[\d.]+\)$/,')'), boxShadow:`0 0 6px ${accent.replace('0.18','0.8')}` }} />
        <span style={{ fontSize:'0.55rem', letterSpacing:'2.5px', color:'rgba(255,255,255,0.45)', fontWeight:400 }}>{title}</span>
        {action && <div style={{ marginLeft:'auto' }}>{action}</div>}
      </div>
      <div style={{ padding:18 }}>{children}</div>
    </div>
  )
}

function CRBar({ label, count, max, color, glow }: { label: string; count: number; max: number; color: string; glow?: boolean }) {
  const w = Math.round((count / max) * 100)
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:4 }}>
        <span style={{ fontSize:'0.68rem', color: glow ? color : 'rgba(220,230,255,0.75)' }}>{label}</span>
        <span style={{ fontSize:'0.62rem', color:'rgba(255,255,255,0.35)', fontVariantNumeric:'tabular-nums' }}>{count}</span>
      </div>
      <div style={{ height:5, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
        <div style={{ height:'100%', width:`${w}%`, borderRadius:3, background:color, boxShadow: glow ? `0 0 8px ${color}88` : 'none', transition:'width 1s cubic-bezier(0.4,0,0.2,1)' }} />
      </div>
    </div>
  )
}

type CRSortCol = 'hostname' | 'grade' | 'status' | 'kx' | 'tls' | 'seen'
type CRVisFilterType = 'grade' | 'tls' | 'kx' | 'status'
type CRVisFilter = { type: CRVisFilterType; value: string } | null

function CompanyReportPage() {
  const location = useLocation()
  const id = location.pathname.replace(/^\/cr\//, '')
  const [data, setData]       = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr]         = useState<string | null>(null)
  const [copied, setCopied]   = useState(false)
  const [filter, setFilter]   = useState('')
  const [sortCol, setSortCol] = useState<CRSortCol>('grade')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [visFilter, setVisFilter] = useState<CRVisFilter>(null)
  const [addOpen, setAddOpen]   = useState(false)
  const [addInput, setAddInput] = useState('')
  const [addPhase, setAddPhase] = useState<'idle' | 'working' | 'probing' | 'done' | 'error'>('idle')
  const [addMsg, setAddMsg]     = useState('')
  const [addProgress, setAddProgress] = useState({ done: 0, total: 0 })

  useEffect(() => {
    if (!id) return
    fetch(`${API_BASE}/v1/company/report/${id}`)
      .then(r => r.json())
      .then((d: any) => { if (d.error) setErr(d.error); else setData(d) })
      .catch(() => setErr('Failed to load report'))
      .finally(() => setLoading(false))
  }, [id])

  function copyLink() {
    navigator.clipboard.writeText(window.location.href)
    setCopied(true); setTimeout(() => setCopied(false), 2500)
  }

  function toggleSort(col: CRSortCol) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir('asc') }
  }

  function toggleVisFilter(type: CRVisFilterType, value: string) {
    setVisFilter(v => (v?.type === type && v?.value === value) ? null : { type, value })
  }

  async function addDomain() {
    const h = addInput.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/^\*\./, '').split('/')[0]
    if (!h) return
    setAddPhase('working')
    setAddMsg('Contacting API…')
    setAddProgress({ done: 0, total: 0 })
    try {
      const res = await fetch(`${API_BASE}/v1/company/report/${id}/add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hostname: h }),
      })
      const result: any = await res.json()
      if (result.error) { setAddPhase('error'); setAddMsg(result.error); return }

      if (result.type === 'subdomain') {
        setAddPhase('done')
        setAddMsg(`✓ Probed ${h} — ${result.grade || '?'} · ${result.status || 'unknown'}`)
        setAddInput('')
        // Refresh report data
        const fresh = await fetch(`${API_BASE}/v1/company/report/${id}`).then(r => r.json()) as any
        if (!fresh.error) setData(fresh)
      } else {
        // Root domain — probe each discovered hostname
        const hostnames: string[] = result.hostnames || []
        setAddPhase('probing')
        setAddProgress({ done: 0, total: hostnames.length })
        setAddMsg(`Discovered ${hostnames.length} hosts — probing…`)
        const PROBE = 'https://pqc-probe-worker.tychon-endpoint.workers.dev'
        const SEM = 20
        let active = 0, done = 0
        const queue = [...hostnames]
        await new Promise<void>(resolve => {
          function next() {
            while (active < SEM && queue.length > 0) {
              const host = queue.shift()!
              active++
              fetch(`${PROBE}/v1/check/${host}`, { signal: AbortSignal.timeout(18000) })
                .catch(() => {})
                .finally(() => {
                  done++; active--
                  setAddProgress({ done, total: hostnames.length })
                  if (queue.length > 0) next()
                  else if (active === 0) resolve()
                })
            }
            if (queue.length === 0 && active === 0) resolve()
          }
          next()
        })
        setAddPhase('done')
        setAddMsg(`✓ Added ${result.root} — ${hostnames.length} hosts probed`)
        setAddInput('')
        const fresh = await fetch(`${API_BASE}/v1/company/report/${id}`).then(r => r.json()) as any
        if (!fresh.error) setData(fresh)
      }
    } catch (e: any) {
      setAddPhase('error')
      setAddMsg(e.message || 'Request failed')
    }
  }

  const gradeColor  = (g: string) => !g || g === '?' ? '#4a5568' : g.startsWith('A') ? '#00ff88' : g.startsWith('B') ? '#a3e635' : g.startsWith('C') ? '#ffcc00' : '#ff4d4d'
  const statusColor = (s: string) => s === 'secure' ? '#00ff88' : s === 'legacy' ? '#ffcc00' : s === 'insecure' ? '#ff4d4d' : '#4a5568'
  const tlsColor    = (v: string) => v === 'TLSv1.3' ? '#00ff88' : v === 'TLSv1.2' ? '#ffcc00' : '#ff4d4d'
  const isPQC       = (g: string) => (g||'').includes('MLKEM') || (g||'').includes('Kyber')

  const spinnerStyle: React.CSSProperties = {
    minHeight:'100vh', background:'#06070d', backgroundImage: CR_STARS,
    display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
    fontFamily:'Space Mono, monospace', gap:16,
  }

  if (loading) return (
    <div style={spinnerStyle}>
      <div style={{ fontSize:'0.6rem', letterSpacing:'3px', color:'rgba(0,209,255,0.7)', animation:'pulse 1.5s ease-in-out infinite' }}>⬡ QUANTUM SENTRY</div>
      <div style={{ fontSize:'0.55rem', letterSpacing:'2px', color:'rgba(255,255,255,0.3)' }}>LOADING ASSESSMENT…</div>
      <div style={{ width:200, height:1, background:'linear-gradient(90deg, transparent, rgba(0,209,255,0.6), transparent)', animation:'scan 1.8s ease-in-out infinite' }} />
    </div>
  )
  if (err || !data) return (
    <div style={{ ...spinnerStyle, gap:8 }}>
      <div style={{ fontSize:'0.65rem', letterSpacing:'2px', color:'#ff4d4d' }}>⚠ REPORT UNAVAILABLE</div>
      <div style={{ fontSize:'0.55rem', color:'rgba(255,255,255,0.3)' }}>{err || 'Not found'}</div>
      <a href="/" style={{ marginTop:12, fontSize:'0.6rem', color:'rgba(0,209,255,0.6)', textDecoration:'none' }}>← Return to Globe</a>
    </div>
  )

  const domains: any[] = data.domains || []
  const pct: number    = data.pct_secure || 0
  const pctColor       = pct >= 50 ? '#00ff88' : pct >= 20 ? '#ffcc00' : '#ff4d4d'
  const threatLevel    = pct >= 50 ? 'LOW' : pct >= 20 ? 'ELEVATED' : 'CRITICAL'
  const threatAccent   = pct >= 50 ? 'rgba(0,255,136,0.22)' : pct >= 20 ? 'rgba(255,204,0,0.22)' : 'rgba(255,77,77,0.22)'
  const gradeOrder: Record<string,number> = { 'A+':0,'A':1,'B':2,'C':3,'F':4,'?':5 }
  const maxKx   = Math.max(1, ...(data.kx_breakdown||[]).map((r:any) => r.count))
  const maxTls  = Math.max(1, ...(data.tls_breakdown||[]).map((r:any) => r.count))
  const pqcCount = domains.filter(d => isPQC(d.pqc_group_name)).length
  const date = new Date(data.created_at)

  // Apply visual filter then text filter, then sort
  const afterVisFilter = domains.filter((d: any) => {
    if (!visFilter) return true
    if (visFilter.type === 'grade')  return (d.overall_grade || '?') === visFilter.value
    if (visFilter.type === 'tls')    return (d.tls_version   || '—') === visFilter.value
    if (visFilter.type === 'kx')     return (d.pqc_group_name || 'Unknown') === visFilter.value
    if (visFilter.type === 'status') return (d.pqc_status    || 'unknown') === visFilter.value
    return true
  })
  const afterTextFilter = filter
    ? afterVisFilter.filter((d: any) => d.hostname.includes(filter.toLowerCase()))
    : afterVisFilter

  const sortFn = (a: any, b: any): number => {
    let av: any, bv: any
    if (sortCol === 'hostname') { av = a.hostname; bv = b.hostname }
    else if (sortCol === 'grade')  { av = gradeOrder[a.overall_grade??'?']??5; bv = gradeOrder[b.overall_grade??'?']??5 }
    else if (sortCol === 'status') { av = a.pqc_status||''; bv = b.pqc_status||'' }
    else if (sortCol === 'kx')     { av = a.pqc_group_name||''; bv = b.pqc_group_name||'' }
    else if (sortCol === 'tls')    { av = a.tls_version||''; bv = b.tls_version||'' }
    else if (sortCol === 'seen')   { av = new Date(a.last_updated||0).getTime(); bv = new Date(b.last_updated||0).getTime() }
    else return 0
    if (av < bv) return sortDir === 'asc' ? -1 : 1
    if (av > bv) return sortDir === 'asc' ? 1 : -1
    return 0
  }
  const displayDomains = [...afterTextFilter].sort(sortFn)

  const SortHdr = ({ col, label, w }: { col: CRSortCol; label: string; w?: string }) => {
    const active = sortCol === col
    const arrow  = active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''
    return (
      <th onClick={() => toggleSort(col)} style={{ padding:'8px 12px', textAlign:'left', fontSize:'0.5rem', letterSpacing:'2px', color: active ? 'rgba(0,209,255,0.85)' : 'rgba(0,209,255,0.45)', fontWeight:400, whiteSpace:'nowrap', width:w, cursor:'pointer', userSelect:'none', transition:'color 0.15s' }}>
        {label}{arrow}
      </th>
    )
  }

  return (
    <div style={{ minHeight:'100vh', background:'#06070d', backgroundImage:CR_STARS, fontFamily:'Space Mono, monospace', color:'#c8d8f0' }}>

      {/* ── Top Nav ── */}
      <div style={{ position:'sticky', top:0, zIndex:100, background:'rgba(4,6,14,0.92)', backdropFilter:'blur(20px)', borderBottom:'1px solid rgba(0,209,255,0.12)', padding:'0 36px', height:48, display:'flex', alignItems:'center', gap:16 }}>
        <a href="/" style={{ display:'flex', alignItems:'center', gap:8, textDecoration:'none' }}>
          <img src="/qs-icon.svg" alt="QS" style={{ width:24, height:24, objectFit:'contain', filter:'drop-shadow(0 0 4px rgba(0,209,255,0.5))' }} />
          <span style={{ fontSize:'0.6rem', letterSpacing:'2.5px', color:'rgba(0,209,255,0.85)', fontWeight:700 }}>QUANTUM SENTRY</span>
        </a>
        <span style={{ color:'rgba(255,255,255,0.12)' }}>╱</span>
        <span style={{ fontSize:'0.52rem', letterSpacing:'1.5px', color:'rgba(255,255,255,0.3)' }}>QUANTUM READINESS ASSESSMENT</span>
        <div style={{ marginLeft:'auto', display:'flex', gap:8, alignItems:'center' }}>
          <div style={{ fontSize:'0.52rem', letterSpacing:'1px', color:'rgba(255,255,255,0.25)', marginRight:4 }}>
            RPT-{String(id).padStart(5,'0')} · {date.toISOString().split('T')[0]}
          </div>
          <button onClick={copyLink} style={{ background: copied ? 'rgba(0,255,136,0.12)' : 'rgba(0,209,255,0.08)', border:`1px solid ${copied ? 'rgba(0,255,136,0.4)' : 'rgba(0,209,255,0.25)'}`, color: copied ? '#00ff88' : '#00d1ff', padding:'5px 14px', borderRadius:4, cursor:'pointer', fontSize:'0.6rem', letterSpacing:'0.5px', transition:'all 0.2s' }}>
            {copied ? '✓ COPIED' : '⎘ SHARE'}
          </button>
          <a href="/" style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)', color:'rgba(255,255,255,0.4)', padding:'5px 12px', borderRadius:4, textDecoration:'none', fontSize:'0.6rem' }}>← GLOBE</a>
        </div>
      </div>

      <div style={{ maxWidth:1600, margin:'0 auto', padding:'32px 48px 80px' }}>

        {/* ── Hero Header ── */}
        <div style={{ marginBottom:28, padding:'32px 36px', background:'rgba(6,10,22,0.7)', border:'1px solid rgba(0,209,255,0.14)', borderRadius:12, backdropFilter:'blur(16px)', position:'relative', overflow:'hidden' }}>
          <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:'linear-gradient(90deg, transparent 0%, rgba(0,209,255,0.6) 30%, rgba(167,139,250,0.6) 70%, transparent 100%)' }} />
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:24 }}>
            <div>
              <div style={{ fontSize:'0.52rem', letterSpacing:'3px', color:'rgba(0,209,255,0.55)', marginBottom:10 }}>POST-QUANTUM CRYPTOGRAPHY · READINESS ASSESSMENT</div>
              <div style={{ fontSize:'2.4rem', fontWeight:900, color:'#fff', letterSpacing:'-1px', lineHeight:1.1, marginBottom:8, textShadow:'0 0 40px rgba(0,209,255,0.25)' }}>{data.name}</div>
              <div style={{ fontSize:'0.6rem', color:'rgba(255,255,255,0.35)', letterSpacing:'1px' }}>
                {(data.roots||[]).map((r:string) => <span key={r} style={{ marginRight:12, color:'rgba(0,209,255,0.5)' }}>{r}</span>)}
              </div>
            </div>
            <div style={{ display:'flex', gap:16, alignItems:'stretch' }}>
              <div style={{ background: threatAccent, border:`1px solid ${pctColor}44`, borderRadius:10, padding:'16px 24px', textAlign:'center', minWidth:110 }}>
                <div style={{ fontSize:'0.48rem', letterSpacing:'2.5px', color:'rgba(255,255,255,0.4)', marginBottom:6 }}>THREAT LEVEL</div>
                <div style={{ fontSize:'1rem', fontWeight:900, color:pctColor, letterSpacing:'1px', textShadow:`0 0 12px ${pctColor}88` }}>{threatLevel}</div>
              </div>
              <div style={{ background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'16px 24px', textAlign:'center', minWidth:110 }}>
                <div style={{ fontSize:'0.48rem', letterSpacing:'2.5px', color:'rgba(255,255,255,0.4)', marginBottom:6 }}>PQC READY</div>
                <div style={{ fontSize:'2.4rem', fontWeight:900, color:pctColor, lineHeight:1, textShadow:`0 0 20px ${pctColor}66` }}>{pct}%</div>
              </div>
            </div>
          </div>
          <div style={{ marginTop:24 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.52rem', color:'rgba(255,255,255,0.3)', marginBottom:6, letterSpacing:'1px' }}>
              <span>QUANTUM VULNERABILITY EXPOSURE</span><span>QUANTUM PROTECTED</span>
            </div>
            <div style={{ height:8, background:'rgba(255,77,77,0.2)', borderRadius:4, overflow:'hidden', border:'1px solid rgba(255,255,255,0.06)' }}>
              <div style={{ height:'100%', width:`${pct}%`, background:`linear-gradient(90deg, ${pctColor}88, ${pctColor})`, borderRadius:4, boxShadow:`0 0 12px ${pctColor}66`, transition:'width 1.2s ease' }} />
            </div>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:14, marginBottom:24 }}>
          {[
            { label:'DOMAINS PROBED', val:data.total,     color:'#00d1ff', icon:'◈', filterType:'', filterVal:'' },
            { label:'DISCOVERED',     val:data.discovered, color:'#a78bfa', icon:'⬡', filterType:'', filterVal:'' },
            { label:'PQC SECURE',     val:data.secure,     color:'#00ff88', icon:'⬢', filterType:'status', filterVal:'secure' },
            { label:'LEGACY CRYPTO',  val:data.legacy,     color:'#ffcc00', icon:'⬡', filterType:'status', filterVal:'legacy' },
            { label:'AT RISK',        val:data.insecure,   color:'#ff4d4d', icon:'⚠', filterType:'status', filterVal:'insecure' },
          ].map(s => {
            const isActive = visFilter?.type === s.filterType && visFilter?.value === s.filterVal
            return (
              <div key={s.label} onClick={() => s.filterType && toggleVisFilter(s.filterType as any, s.filterVal)}
                style={{ background:'rgba(6,10,22,0.8)', border:`1px solid ${isActive ? s.color+'88' : s.color+'22'}`, borderRadius:10, padding:'18px 14px', textAlign:'center', backdropFilter:'blur(8px)', position:'relative', overflow:'hidden', cursor: s.filterType ? 'pointer' : 'default', boxShadow: isActive ? `0 0 18px ${s.color}33` : 'none', transition:'border-color 0.2s, box-shadow 0.2s' }}>
                <div style={{ position:'absolute', inset:0, background:`radial-gradient(ellipse at 50% 0%, ${s.color}08, transparent 70%)` }} />
                <div style={{ fontSize:'0.9rem', color:`${s.color}66`, marginBottom:4 }}>{s.icon}</div>
                <div style={{ fontSize:'1.8rem', fontWeight:900, color:s.color, lineHeight:1, textShadow:`0 0 16px ${s.color}55` }}>{s.val ?? 0}</div>
                <div style={{ fontSize:'0.48rem', letterSpacing:'1.8px', color:'rgba(255,255,255,0.3)', marginTop:6 }}>{s.label}</div>
                {isActive && <div style={{ position:'absolute', top:6, right:8, fontSize:'0.45rem', color:s.color, letterSpacing:'1px' }}>FILTERED</div>}
              </div>
            )
          })}
        </div>

        {/* ── Charts Row ── */}
        <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr 1fr', gap:20, marginBottom:24 }}>

          {/* KX algorithms */}
          <CRCard title="KEY EXCHANGE ALGORITHM DISTRIBUTION  ·  click to filter" accent="rgba(0,209,255,0.18)">
            {(data.kx_breakdown||[]).slice(0,10).map((r:any) => {
              const isActive = visFilter?.type === 'kx' && visFilter?.value === r.group
              return (
                <div key={r.group} onClick={() => toggleVisFilter('kx', r.group)} style={{ cursor:'pointer', borderRadius:4, padding:'2px 4px', margin:'0 -4px', background: isActive ? 'rgba(0,209,255,0.06)' : 'transparent', transition:'background 0.15s' }}>
                  <CRBar label={r.group} count={r.count} max={maxKx}
                    color={isPQC(r.group) ? '#00ff88' : r.group === 'Unknown' ? '#4a5568' : '#00d1ff'}
                    glow={isPQC(r.group) || isActive} />
                </div>
              )
            })}
            {pqcCount > 0 && (
              <div style={{ marginTop:12, padding:'8px 12px', background:'rgba(0,255,136,0.06)', border:'1px solid rgba(0,255,136,0.2)', borderRadius:6, fontSize:'0.6rem', color:'rgba(0,255,136,0.8)', letterSpacing:'0.5px' }}>
                ✓ {pqcCount} domain{pqcCount !== 1 ? 's' : ''} using post-quantum key exchange
              </div>
            )}
          </CRCard>

          {/* TLS */}
          <CRCard title="TLS PROTOCOL  ·  click to filter" accent="rgba(167,139,250,0.18)">
            {(data.tls_breakdown||[]).map((r:any) => {
              const isActive = visFilter?.type === 'tls' && visFilter?.value === r.version
              return (
                <div key={r.version} onClick={() => toggleVisFilter('tls', r.version)} style={{ cursor:'pointer', borderRadius:4, padding:'2px 4px', margin:'0 -4px', background: isActive ? 'rgba(167,139,250,0.06)' : 'transparent', transition:'background 0.15s' }}>
                  <CRBar label={r.version} count={r.count} max={maxTls} color={tlsColor(r.version)} glow={r.version === 'TLSv1.3' || isActive} />
                </div>
              )
            })}
          </CRCard>

          {/* Grade distribution */}
          <CRCard title="READINESS GRADE  ·  click to filter" accent="rgba(255,204,0,0.18)">
            <div style={{ display:'flex', gap:10, justifyContent:'center', flexWrap:'wrap' }}>
              {(data.grade_breakdown||[]).map((r:any) => {
                const isActive = visFilter?.type === 'grade' && visFilter?.value === r.grade
                return (
                  <div key={r.grade} onClick={() => toggleVisFilter('grade', r.grade)}
                    style={{ textAlign:'center', minWidth:56, padding:'12px 10px', background: isActive ? `${gradeColor(r.grade)}12` : 'rgba(255,255,255,0.03)', borderRadius:8, border:`1px solid ${isActive ? gradeColor(r.grade)+'66' : gradeColor(r.grade)+'22'}`, cursor:'pointer', boxShadow: isActive ? `0 0 16px ${gradeColor(r.grade)}33` : 'none', transition:'all 0.2s' }}>
                    <div style={{ fontSize:'1.6rem', fontWeight:900, color:gradeColor(r.grade), textShadow:`0 0 12px ${gradeColor(r.grade)}66`, lineHeight:1 }}>{r.grade}</div>
                    <div style={{ fontSize:'0.55rem', color:'rgba(255,255,255,0.35)', marginTop:4 }}>{r.count}</div>
                  </div>
                )
              })}
            </div>
          </CRCard>
        </div>

        {/* ── Risk Assessment ── */}
        <div style={{ marginBottom:24, padding:'20px 24px', background:`linear-gradient(135deg, rgba(6,10,22,0.9), ${threatAccent.replace('0.22','0.08')})`, border:`1px solid ${pctColor}22`, borderRadius:10, backdropFilter:'blur(12px)' }}>
          <div style={{ fontSize:'0.52rem', letterSpacing:'2.5px', color:'rgba(255,255,255,0.35)', marginBottom:12 }}>CRYPTOGRAPHIC RISK ASSESSMENT</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:20 }}>
            {[
              {
                label: 'Harvest Now, Decrypt Later',
                risk: data.legacy > 0 || data.insecure > 0 ? 'ACTIVE' : 'MITIGATED',
                color: data.legacy > 0 || data.insecure > 0 ? '#ff4d4d' : '#00ff88',
                desc: `${data.legacy + (data.insecure||0)} domains using classical-only encryption are vulnerable to store-now-decrypt-later attacks by quantum adversaries.`
              },
              {
                label: 'Forward Secrecy Exposure',
                risk: data.secure > 0 ? 'PARTIAL' : 'ELEVATED',
                color: data.secure > 0 ? '#ffcc00' : '#ff4d4d',
                desc: `${data.secure || 0} of ${data.total} domains have deployed post-quantum key exchange, providing quantum-resistant forward secrecy.`
              },
              {
                label: 'Migration Urgency',
                risk: pct < 10 ? 'CRITICAL' : pct < 40 ? 'HIGH' : 'MODERATE',
                color: pct < 10 ? '#ff4d4d' : pct < 40 ? '#ffcc00' : '#a3e635',
                desc: `At current adoption of ${pct}% PQC readiness, full migration requires accelerated remediation across ${data.total - data.secure} remaining domains.`
              },
            ].map(a => (
              <div key={a.label} style={{ padding:'14px 16px', background:'rgba(0,0,0,0.25)', borderRadius:8, border:'1px solid rgba(255,255,255,0.06)' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:'0.6rem', color:'rgba(255,255,255,0.6)' }}>{a.label}</span>
                  <span style={{ fontSize:'0.55rem', fontWeight:700, color:a.color, letterSpacing:'1px' }}>{a.risk}</span>
                </div>
                <div style={{ fontSize:'0.6rem', color:'rgba(255,255,255,0.35)', lineHeight:1.6 }}>{a.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Domain Table ── */}
        <CRCard
          title={`DOMAIN INVENTORY  ·  ${displayDomains.length} / ${domains.length} SITES`}
          accent="rgba(0,209,255,0.14)"
          action={
            <button onClick={() => { setAddOpen(o => !o); setAddPhase('idle'); setAddMsg('') }}
              style={{ background: addOpen ? 'rgba(0,209,255,0.15)' : 'rgba(0,209,255,0.06)', border:'1px solid rgba(0,209,255,0.3)', color:'#00d1ff', padding:'4px 12px', borderRadius:4, cursor:'pointer', fontSize:'0.58rem', letterSpacing:'0.5px' }}>
              {addOpen ? '✕ CANCEL' : '＋ ADD HOST'}
            </button>
          }>

          {/* Add-host panel */}
          {addOpen && (
            <div style={{ marginBottom:16, padding:'14px 16px', background:'rgba(0,209,255,0.04)', border:'1px solid rgba(0,209,255,0.15)', borderRadius:8 }}>
              <div style={{ fontSize:'0.52rem', letterSpacing:'2px', color:'rgba(0,209,255,0.55)', marginBottom:10 }}>
                ADD HOST TO REPORT — enter a subdomain (www.citi.com) or root domain (citi.com) to discover all *.citi.com
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <input
                  value={addInput}
                  onChange={e => setAddInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addPhase === 'idle' && addDomain()}
                  placeholder="e.g. api.citi.com  or  citi.com"
                  disabled={addPhase === 'working' || addPhase === 'probing'}
                  style={{ flex:1, background:'rgba(0,0,0,0.4)', border:'1px solid rgba(0,209,255,0.2)', borderRadius:6, padding:'7px 12px', color:'#c8d8f0', fontFamily:'Space Mono, monospace', fontSize:'0.68rem', outline:'none' }}
                />
                <button
                  onClick={addDomain}
                  disabled={!addInput.trim() || addPhase === 'working' || addPhase === 'probing'}
                  style={{ background:'rgba(0,209,255,0.12)', border:'1px solid rgba(0,209,255,0.4)', color:'#00d1ff', padding:'7px 18px', borderRadius:6, cursor:'pointer', fontSize:'0.62rem', letterSpacing:'0.5px', opacity: (!addInput.trim() || addPhase === 'working' || addPhase === 'probing') ? 0.4 : 1 }}>
                  {addPhase === 'working' ? 'PROBING…' : addPhase === 'probing' ? `${addProgress.done}/${addProgress.total}` : 'PROBE'}
                </button>
                {addPhase === 'done' && <button onClick={() => { setAddPhase('idle'); setAddMsg(''); setAddOpen(false) }} style={{ background:'none', border:'1px solid rgba(0,255,136,0.3)', color:'#00ff88', padding:'7px 12px', borderRadius:6, cursor:'pointer', fontSize:'0.62rem' }}>DONE</button>}
              </div>
              {addPhase === 'probing' && (
                <div style={{ marginTop:10 }}>
                  <div style={{ height:3, background:'rgba(255,255,255,0.06)', borderRadius:2, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${addProgress.total > 0 ? Math.round((addProgress.done/addProgress.total)*100) : 0}%`, background:'linear-gradient(90deg,rgba(0,209,255,0.6),#00d1ff)', borderRadius:2, transition:'width 0.3s ease' }} />
                  </div>
                </div>
              )}
              {addMsg && (
                <div style={{ marginTop:8, fontSize:'0.6rem', color: addPhase === 'error' ? '#ff4d4d' : addPhase === 'done' ? '#00ff88' : 'rgba(0,209,255,0.7)', letterSpacing:'0.3px' }}>
                  {addPhase === 'working' || addPhase === 'probing' ? '⬡ ' : ''}{addMsg}
                </div>
              )}
            </div>
          )}

          <div style={{ display:'flex', gap:10, marginBottom:14, alignItems:'center' }}>
            <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="filter hostnames…"
              style={{ flex:1, background:'rgba(0,0,0,0.3)', border:'1px solid rgba(0,209,255,0.15)', borderRadius:6, padding:'7px 12px', color:'#c8d8f0', fontFamily:'Space Mono, monospace', fontSize:'0.68rem', outline:'none' }} />
            {visFilter && (
              <button onClick={() => setVisFilter(null)} style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(0,209,255,0.08)', border:'1px solid rgba(0,209,255,0.3)', color:'#00d1ff', padding:'5px 12px', borderRadius:20, cursor:'pointer', fontSize:'0.58rem', letterSpacing:'0.5px', whiteSpace:'nowrap' }}>
                ✕ {visFilter.type.toUpperCase()}: {visFilter.value}
              </button>
            )}
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.7rem' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid rgba(0,209,255,0.12)' }}>
                  <SortHdr col="hostname" label="HOSTNAME" />
                  <SortHdr col="grade"    label="GRADE"    w="70px" />
                  <SortHdr col="status"   label="STATUS"   w="96px" />
                  <SortHdr col="kx"       label="KEY EXCHANGE" />
                  <SortHdr col="tls"      label="TLS"      w="88px" />
                  <SortHdr col="seen"     label="LAST SEEN" w="110px" />
                </tr>
              </thead>
              <tbody>
                {displayDomains.map((d:any, i:number) => {
                  const isGood = isPQC(d.pqc_group_name)
                  return (
                    <tr key={d.hostname} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', background: i%2===0 ? 'transparent' : 'rgba(255,255,255,0.015)', transition:'background 0.15s' }}>
                      <td style={{ padding:'9px 12px' }}>
                        <a href={`https://${d.hostname}`} target="_blank" rel="noopener noreferrer"
                          style={{ color:'rgba(200,220,255,0.8)', textDecoration:'none', fontSize:'0.7rem', fontFamily:'monospace' }}>
                          {d.hostname}
                        </a>
                      </td>
                      <td style={{ padding:'9px 12px', fontWeight:900, fontSize:'1rem', color:gradeColor(d.overall_grade), textShadow:`0 0 8px ${gradeColor(d.overall_grade)}66` }}>{d.overall_grade || '?'}</td>
                      <td style={{ padding:'9px 12px' }}>
                        <span style={{ fontSize:'0.55rem', letterSpacing:'0.8px', color:statusColor(d.pqc_status), fontWeight:700 }}>{(d.pqc_status||'UNKNOWN').toUpperCase()}</span>
                      </td>
                      <td style={{ padding:'9px 12px', fontSize:'0.65rem', color: isGood ? '#00ff88' : 'rgba(180,200,230,0.45)' }}>
                        {isGood && <span style={{ marginRight:4, fontSize:'0.55rem' }}>✦</span>}
                        {d.pqc_group_name || '—'}
                      </td>
                      <td style={{ padding:'9px 12px', fontSize:'0.65rem', color:tlsColor(d.tls_version||'') }}>{d.tls_version || '—'}</td>
                      <td style={{ padding:'9px 12px', fontSize:'0.62rem', color:'rgba(200,220,255,0.35)', fontVariantNumeric:'tabular-nums' }}>{relTime(d.last_updated)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CRCard>

        {/* ── Footer ── */}
        <div style={{ marginTop:36, display:'flex', alignItems:'center', justifyContent:'space-between', borderTop:'1px solid rgba(0,209,255,0.08)', paddingTop:20 }}>
          <div style={{ fontSize:'0.52rem', letterSpacing:'1.5px', color:'rgba(255,255,255,0.2)' }}>
            QUANTUM SENTRY · RPT-{String(id).padStart(5,'0')} · {date.toISOString().split('T')[0]}
          </div>
          <div style={{ fontSize:'0.52rem', letterSpacing:'1px', color:'rgba(255,255,255,0.15)' }}>
            CONFIDENTIAL — POST-QUANTUM CRYPTOGRAPHY ASSESSMENT
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── AdminCorporateQueue ──────────────────────────────────────────────────────

function AdminCorporateQueue() {
  const { isSignedIn, getToken } = useAuth()
  const [items, setItems]   = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState('')

  const load = async () => {
    setLoading(true)
    try {
      const token = await getToken()
      const res = await fetch(`${API_BASE}/v1/admin/corporate/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = await res.json()
      if (!res.ok) { setError(data.error || 'Failed to load'); return }
      setItems(data.results ?? [])
    } catch (e: any) { setError(e.message) }
    setLoading(false)
  }

  useEffect(() => { if (isSignedIn) load() }, [isSignedIn]) // eslint-disable-line react-hooks/exhaustive-deps

  const approve = async (orgId: string, approved: boolean) => {
    const token = await getToken()
    await fetch(`${API_BASE}/v1/admin/corporate/approve/${orgId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ approved }),
    })
    load()
  }

  return (
    <div style={{ maxWidth: 800, margin: '4rem auto', padding: '0 2rem' }}>
      <h1 style={{ fontSize: '1rem', letterSpacing: '0.15em', marginBottom: '2rem' }}>CORPORATE APPROVAL QUEUE</h1>
      {loading && <div style={{ color: 'var(--text-dim)' }}>Loading…</div>}
      {error && <div style={{ color: '#ff4d4d' }}>{error}</div>}
      {!loading && items.length === 0 && <div style={{ color: 'var(--text-dim)' }}>No pending registrations.</div>}
      {items.map(item => (
        <div key={item.org_id} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: 8, padding: '1.2rem', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontWeight: 700, marginBottom: '0.3rem' }}>{item.org_name}</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginBottom: '0.2rem' }}>Owner: {item.email} ({item.clerk_user_id})</div>
              <div style={{ fontSize: '0.78rem', color: 'var(--accent-cyan)' }}>
                Domains: {(() => { try { return JSON.parse(item.claimed_domains).join(', ') } catch { return item.claimed_domains } })()}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '0.3rem' }}>
                {new Date(item.created_at).toLocaleString()}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="gd-nav-btn" onClick={() => approve(item.org_id, true)} style={{ color: '#00ff88', borderColor: 'rgba(0,255,136,0.4)' }}>APPROVE</button>
              <button className="gd-nav-btn" onClick={() => approve(item.org_id, false)} style={{ color: '#ff4d4d', borderColor: 'rgba(255,77,77,0.4)' }}>DENY</button>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── PQC Laws & Mandates Observatory ─────────────────────────────────────────

interface Mandate {
  id: number
  title: string
  jurisdiction: string
  type: string
  issuing_body: string
  issued_date: string | null
  effective_date: string | null
  url: string | null
  summary: string
  status: string
  applies_to: string | null
  tags: string
}

const TYPE_COLORS: Record<string, string> = {
  law:             '#f97316',
  executive_order: '#ef4444',
  memo:            '#a78bfa',
  regulation:      '#f59e0b',
  standard:        '#22d3ee',
  guidance:        '#4ade80',
  directive:       '#fb923c',
  advisory:        '#60a5fa',
}

const JURISDICTIONS = [
  'All', 'US Federal', 'EU', 'UK', 'Germany', 'France', 'Canada', 'Australia', 'International',
]

function MandateTypeBadge({ type }: { type: string }) {
  const color = TYPE_COLORS[type] ?? '#888'
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.08em',
      padding: '2px 7px', borderRadius: 3,
      border: `1px solid ${color}40`, color, background: `${color}18`,
      textTransform: 'uppercase',
    }}>{type.replace('_', ' ')}</span>
  )
}

function MandateStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: '#00ff88', enacted: '#00ff88', proposed: '#f59e0b',
    draft: '#60a5fa', superseded: '#888',
  }
  const color = map[status] ?? '#888'
  return (
    <span style={{
      fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.06em',
      padding: '2px 7px', borderRadius: 3,
      border: `1px solid ${color}40`, color, background: `${color}18`,
      textTransform: 'uppercase',
    }}>{status}</span>
  )
}

function Laws() {
  const { isSignedIn } = useUser()
  const { getToken }   = useAuth()
  const navigate             = useNavigate()

  const [mandates, setMandates]         = useState<Mandate[]>([])
  const [loading,  setLoading]          = useState(true)
  const [filter,   setFilter]           = useState('All')
  const [typeFilter, setTypeFilter]     = useState('All')
  const [showForm, setShowForm]         = useState(false)
  const [deleting, setDeleting]         = useState<number | null>(null)
  const [adminUser, setAdminUser]       = useState(false)

  // new mandate form state
  const emptyForm = {
    title: '', jurisdiction: 'US Federal', type: 'guidance', issuing_body: '',
    issued_date: '', effective_date: '', url: '', summary: '',
    status: 'active', applies_to: '', tags: '',
  }
  const [form, setForm] = useState(emptyForm)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    fetch(`${API_BASE}/v1/mandates`)
      .then(r => r.json())
      .then((d: Mandate[]) => { setMandates(d); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  // Check admin status
  useEffect(() => {
    if (!isSignedIn) return
    getToken().then((t: string | null) => {
      if (!t) return
      fetch(`${API_BASE}/v1/admin/corporate/pending`, {
        headers: { Authorization: `Bearer ${t}` },
      }).then(r => { if (r.ok || r.status === 200) setAdminUser(true) }).catch(() => {})
    })
  }, [isSignedIn, getToken])

  const filtered = mandates.filter(m => {
    if (filter !== 'All' && m.jurisdiction !== filter) return false
    if (typeFilter !== 'All' && m.type !== typeFilter) return false
    return true
  })

  const allTypes = ['All', ...Array.from(new Set(mandates.map(m => m.type))).sort()]

  async function handleDelete(id: number) {
    if (!window.confirm('Delete this mandate?')) return
    setDeleting(id)
    const t = await getToken()
    await fetch(`${API_BASE}/v1/mandates/${id}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${t}` },
    })
    setMandates(prev => prev.filter(m => m.id !== id))
    setDeleting(null)
  }

  async function handleSave() {
    setSaving(true)
    const t = await getToken()
    const body = {
      ...form,
      tags: form.tags.split(',').map(s => s.trim()).filter(Boolean),
    }
    const res = await fetch(`${API_BASE}/v1/mandates`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (res.ok) {
      const { id } = await res.json()
      setMandates(prev => [{ ...body as any, id, tags: JSON.stringify(body.tags) }, ...prev])
      setForm(emptyForm)
      setShowForm(false)
    }
    setSaving(false)
  }

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg-dark, #0a0f1e)',
      color: 'var(--text-primary, #e2e8f0)', fontFamily: "'Space Grotesk', monospace",
      padding: '0',
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(0,255,255,0.03)', borderBottom: '1px solid rgba(0,255,255,0.1)',
        padding: '1.25rem 2rem', display: 'flex', alignItems: 'center', gap: '1.5rem',
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none', border: '1px solid rgba(0,255,255,0.25)',
            color: 'var(--accent-cyan,#22d3ee)', cursor: 'pointer',
            padding: '0.35rem 0.75rem', borderRadius: 4, fontSize: '0.75rem',
            letterSpacing: '0.06em', fontFamily: 'inherit',
          }}
        >← BACK</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.2em', color: 'var(--accent-cyan,#22d3ee)', marginBottom: 2 }}>
            QUANTUM SENTRY // KNOWLEDGE BASE
          </div>
          <h1 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, letterSpacing: '0.05em' }}>
            PQC Legal &amp; Standards Observatory
          </h1>
          <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>
            {mandates.length} documents tracked across {new Set(mandates.map(m => m.jurisdiction)).size} jurisdictions
          </div>
        </div>
        {adminUser && (
          <button
            onClick={() => setShowForm(s => !s)}
            style={{
              background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.35)',
              color: '#00ff88', cursor: 'pointer', padding: '0.4rem 1rem',
              borderRadius: 4, fontSize: '0.75rem', letterSpacing: '0.08em',
              fontFamily: 'inherit', fontWeight: 700,
            }}
          >+ ADD DOCUMENT</button>
        )}
      </div>

      {/* Add form */}
      {showForm && (
        <div style={{
          margin: '1.5rem 2rem', padding: '1.25rem', background: 'rgba(0,255,136,0.04)',
          border: '1px solid rgba(0,255,136,0.2)', borderRadius: 8,
        }}>
          <div style={{ fontSize: '0.8rem', fontWeight: 700, marginBottom: '1rem', color: '#00ff88', letterSpacing: '0.1em' }}>
            NEW DOCUMENT
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            {([
              ['Title', 'title', 'text'],
              ['Issuing Body', 'issuing_body', 'text'],
              ['Jurisdiction', 'jurisdiction', 'select:US Federal,EU,UK,Germany,France,Canada,Australia,International'],
              ['Type', 'type', 'select:law,executive_order,memo,regulation,standard,guidance,directive,advisory'],
              ['Issued Date', 'issued_date', 'date'],
              ['Effective Date', 'effective_date', 'date'],
              ['Status', 'status', 'select:active,enacted,proposed,draft,superseded'],
              ['Applies To', 'applies_to', 'text'],
              ['URL', 'url', 'text'],
              ['Tags (comma-separated)', 'tags', 'text'],
            ] as [string, string, string][]).map(([label, key, kind]) => (
              <div key={key} style={key === 'title' || key === 'url' ? { gridColumn: '1/-1' } : {}}>
                <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: 3, letterSpacing: '0.08em' }}>{label.toUpperCase()}</div>
                {kind.startsWith('select:') ? (
                  <select
                    value={(form as any)[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{
                      width: '100%', background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,255,255,0.2)',
                      color: '#e2e8f0', borderRadius: 4, padding: '0.4rem 0.5rem', fontSize: '0.8rem',
                      fontFamily: 'inherit',
                    }}
                  >
                    {kind.slice(7).split(',').map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input
                    type={kind}
                    value={(form as any)[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,255,255,0.2)',
                      color: '#e2e8f0', borderRadius: 4, padding: '0.4rem 0.5rem', fontSize: '0.8rem',
                      fontFamily: 'inherit',
                    }}
                  />
                )}
              </div>
            ))}
            <div style={{ gridColumn: '1/-1' }}>
              <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginBottom: 3, letterSpacing: '0.08em' }}>SUMMARY</div>
              <textarea
                rows={4}
                value={form.summary}
                onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'vertical',
                  background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(0,255,255,0.2)',
                  color: '#e2e8f0', borderRadius: 4, padding: '0.5rem', fontSize: '0.8rem',
                  fontFamily: 'inherit',
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              onClick={handleSave} disabled={saving}
              style={{
                background: 'rgba(0,255,136,0.15)', border: '1px solid rgba(0,255,136,0.4)',
                color: '#00ff88', padding: '0.4rem 1.25rem', borderRadius: 4,
                cursor: saving ? 'default' : 'pointer', fontSize: '0.8rem', fontFamily: 'inherit',
              }}
            >{saving ? 'SAVING…' : 'SAVE'}</button>
            <button
              onClick={() => setShowForm(false)}
              style={{
                background: 'none', border: '1px solid rgba(255,255,255,0.15)',
                color: '#94a3b8', padding: '0.4rem 1rem', borderRadius: 4,
                cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'inherit',
              }}
            >CANCEL</button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={{ padding: '1rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        {/* Jurisdiction tabs */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
          {JURISDICTIONS.map(j => (
            <button
              key={j}
              onClick={() => setFilter(j)}
              style={{
                background: filter === j ? 'rgba(0,255,255,0.15)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${filter === j ? 'rgba(0,255,255,0.5)' : 'rgba(255,255,255,0.1)'}`,
                color: filter === j ? 'var(--accent-cyan,#22d3ee)' : '#94a3b8',
                padding: '0.25rem 0.75rem', borderRadius: 3, cursor: 'pointer',
                fontSize: '0.7rem', letterSpacing: '0.06em', fontFamily: 'inherit',
              }}
            >{j}</button>
          ))}
        </div>
        {/* Type filter */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {allTypes.map(t => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              style={{
                background: typeFilter === t ? `${TYPE_COLORS[t] ?? '#888'}20` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${typeFilter === t ? (TYPE_COLORS[t] ?? '#888') + '60' : 'rgba(255,255,255,0.07)'}`,
                color: typeFilter === t ? (TYPE_COLORS[t] ?? '#e2e8f0') : '#64748b',
                padding: '0.2rem 0.6rem', borderRadius: 3, cursor: 'pointer',
                fontSize: '0.65rem', letterSpacing: '0.07em', fontFamily: 'inherit',
                textTransform: 'uppercase',
              }}
            >{t === 'All' ? 'All Types' : t.replace('_', ' ')}</button>
          ))}
        </div>
      </div>

      {/* Cards */}
      <div style={{ padding: '1.5rem 2rem' }}>
        {loading && (
          <div style={{ color: '#94a3b8', textAlign: 'center', padding: '3rem' }}>
            Loading knowledge base…
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ color: '#94a3b8', textAlign: 'center', padding: '3rem' }}>
            No documents match the selected filters.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(520px, 1fr))', gap: '1rem' }}>
          {filtered.map(m => {
            let tags: string[] = []
            try { tags = JSON.parse(m.tags) } catch { /* ignore */ }
            return (
              <div
                key={m.id}
                style={{
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8, padding: '1.1rem 1.25rem',
                  display: 'flex', flexDirection: 'column', gap: '0.6rem',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(0,255,255,0.25)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)')}
              >
                {/* Top row: badges + admin actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                  <MandateTypeBadge type={m.type} />
                  <MandateStatusBadge status={m.status} />
                  <span style={{
                    fontSize: '0.65rem', color: '#94a3b8',
                    border: '1px solid rgba(255,255,255,0.1)',
                    padding: '2px 7px', borderRadius: 3, letterSpacing: '0.05em',
                  }}>{m.jurisdiction}</span>
                  {adminUser && (
                    <button
                      onClick={() => handleDelete(m.id)}
                      disabled={deleting === m.id}
                      style={{
                        marginLeft: 'auto', background: 'none',
                        border: '1px solid rgba(255,77,77,0.3)', color: '#ff4d4d',
                        cursor: 'pointer', padding: '2px 8px', borderRadius: 3,
                        fontSize: '0.65rem', letterSpacing: '0.06em', fontFamily: 'inherit',
                      }}
                    >{deleting === m.id ? '…' : 'DELETE'}</button>
                  )}
                </div>

                {/* Title */}
                <div style={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.35, color: '#e2e8f0' }}>
                  {m.title}
                </div>

                {/* Issuing body + date */}
                <div style={{ display: 'flex', gap: '1rem', fontSize: '0.72rem', color: '#94a3b8' }}>
                  <span>{m.issuing_body}</span>
                  {m.issued_date && <span>Issued: {m.issued_date}</span>}
                  {m.effective_date && m.effective_date !== m.issued_date && (
                    <span>Effective: {m.effective_date}</span>
                  )}
                </div>

                {/* Summary */}
                <div style={{ fontSize: '0.78rem', color: '#cbd5e1', lineHeight: 1.6 }}>
                  {m.summary}
                </div>

                {/* Applies to */}
                {m.applies_to && (
                  <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                    <span style={{ color: '#94a3b8', fontWeight: 600 }}>Applies to:</span> {m.applies_to}
                  </div>
                )}

                {/* Tags */}
                {tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                    {tags.map(tag => (
                      <span key={tag} style={{
                        fontSize: '0.6rem', padding: '1px 6px',
                        background: 'rgba(0,255,255,0.06)',
                        border: '1px solid rgba(0,255,255,0.15)',
                        color: '#67e8f9', borderRadius: 2,
                      }}>#{tag}</span>
                    ))}
                  </div>
                )}

                {/* Link */}
                {m.url && (
                  <a
                    href={m.url} target="_blank" rel="noopener noreferrer"
                    style={{
                      fontSize: '0.72rem', color: 'var(--accent-cyan,#22d3ee)',
                      textDecoration: 'none', marginTop: 'auto',
                      display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
                    }}
                  >VIEW DOCUMENT →</a>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// ─── App ──────────────────────────────────────────────────────────────────────

function AppInner() {
  const location = useLocation();
  // Console shell areas
  if (location.pathname === '/' ||
      location.pathname === '/globe' ||
      location.pathname === '/analytics' ||
      location.pathname.startsWith('/knowledge')) return <ConsoleShell />;
  if (location.pathname === '/registry') return <QuantumRegistry />;
  if (location.pathname === '/admin') return <AdminCorporateQueue />;
  if (location.pathname === '/me') return <HomePlanet />;
  // Any /report/:domain URL loads GlobalView with the report panel pre-opened
  if (location.pathname.startsWith('/cr/')) {
    return <CompanyReportPage />;
  }
  if (location.pathname.startsWith('/report/')) {
    const domain = location.pathname.replace('/report/', '').replace(/^www\./, '');
    return <GlobalView initialReport={domain} />;
  }
  return (
    <div className="dashboard-container">
      <nav className="navbar">
        <Link to="/" className="logo">QUANTUM SENTRY</Link>
        <div className="nav-links">
          <NavLink to="/"         label="Home"     />
          <NavLink to="/registry" label="Manifest" />
          <NavLink to="/blog"     label="Blog"     />
        </div>
      </nav>
      <Routes>
        <Route path="/company/:domain" element={<CompanyReport />} />
        <Route path="/blog"            element={<BlogIndex />} />
        <Route path="/blog/:slug"      element={<BlogPost />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

export default App;
